// DispatchDeck - Clock Module
// Time tracking with customer-first flow, ticket creation, and workforce view
// For founder account: includes Time Off tab and Workforce tab

import {
  db,
  storage,
  functions,
  httpsCallable,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  arrayUnion,
  increment,
  ref,
  uploadBytes,
  getDownloadURL,
  auth // TASK-9958 — the uploader uid on ticket photos
} from '../firebase-config.js?v=1868';

// TASK-1397 — runTransaction is not re-exported by firebase-config.js. Pull it
// straight from the SAME 10.8.0 gstatic URL firebase-config uses (identical URL
// = same module instance — the ap.js / dispatch.js / billing.js precedent). This
// is NOT the ?v= cross-import pitfall: there is no cache buster on this URL.
import { runTransaction, serverTimestamp } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';

import { processPhoto } from '../utils/imageProcessor.js?v=1955';
// TASK-9958 — file provenance (who/how/when) on clock-out ticket photos. Pure.
import { buildTicketDocUploader, fileAddedAuditPayload, fileUploadCustomMetadata } from '../shared/ticket-doc-meta.js?v=9958';
import { toDrawablePhoto, detectHeic, photoErrorMessage, createPhotoPrepTracker, settlePhotoPrepBeforeSave } from '../utils/heicConvert.js?v=3';
import { capturePingIfEnabled } from '../utils/gpsPing.js?v=2';
// TASK-1558 — effective-dated pay-rate math (pure module → window.PayRates).
// clock.js is the ONE `?v=` importer (equipmentNotes/dqMatrix precedent);
// every other consumer reads window.PayRates at call time — never a second
// `?v=` import (Pitfall #1: two busters = two instances).
import '../payRates.js?v=1520700'; // TASK-1507 company-zone workweek
// TASK-1481 — window.PersonComp: compensation accessor (peopleComp-first with
// legacy person-doc fallback). Side-effect import ONCE from here (same pattern
// as payRates.js above — clock.js is in app.js's static graph, so it evaluates
// at startup); hr.js / bi.js / billing.js read window.PersonComp at call time,
// NEVER a second ?v= import (Pitfall #1).
import '../personComp.js?v=1513900';
// TASK-4865 — window.PersonCard: fuel-card PIN accessor (peopleCard-first with
// legacy person-doc fallback). Same side-effect-import-ONCE rule as the two
// above; hr.js reads window.PersonCard at call time, NEVER a second ?v= import
// (Pitfall #1). It holds no subscription — it only does per-person getDocs on
// an explicit Reveal — so a driver session loading this module reads nothing.
import '../personCard.js?v=1520120';
// TASK-9359 — window.PersonIdentity: DOB / licence number / state / expiry /
// CDL number accessor (peopleIdentity-first with legacy person-doc fallback).
// Same side-effect-import-ONCE rule; roster.js / hr.js / compliance.js /
// dispatch.js read window.PersonIdentity at call time, NEVER a second ?v=
// import (Pitfall #1). It subscribes to nothing until a roster / HR /
// compliance / dispatch view asks, so a driver session loading it reads nothing.
import '../personIdentity.js?v=9359002';
// TASK-9387 — window.InternalTime: internal-time (Shop/Yard/…) hour buckets for
// the payroll exports. Same side-effect-import-ONCE rule as the three above;
// payroll.js reads the result off buildPayrollPrepData, NEVER a second ?v=
// import (Pitfall #1).
import '../internalTime.js?v=1520600';
// TASK-10040 — window.ShiftDayFlags: day over 24 h + Shop / no-job shift past
// midnight (pure, UMD). Same side-effect-import-ONCE rule (Pitfall #1).
import '../shiftDayFlags.js?v=10040001';
// TASK-1483 — window.VehicleReimb: personal-vehicle mileage reimbursement
// engine (pure, UMD). Same side-effect-import-ONCE rule: jobboard.js,
// payroll.js and hr.js read window.VehicleReimb at call time, NEVER a second
// ?v= import (Pitfall #1).
import '../vehicleReimb.js?v=1975452';
// TASK-9443 — window.PayrollRunLock: pay-period SOFT lock (pure, UMD). Same
// side-effect-import-ONCE rule: payroll.js reads window.PayrollRunLock at call
// time, NEVER a second ?v= import (Pitfall #1). The fetcher below is the only
// Firebase touchpoint; it fails open (a lookup error never blocks an edit).
// TASK-10121 — window.TimeOffGroup: the ONE "group time off by employee" rule
// (Clock -> Time Off here, HR -> Time Off in hr.js). Side-effect import ONCE
// from here; hr.js reads window.TimeOffGroup at call time, NEVER a second ?v=
// import (Pitfall #1).
import '../timeOffGroup.js?v=10121001';
import '../payrollRunLock.js?v=9443004'; // TASK-9443 slice 3: exportedRunForAny/exportedChipText/runsFingerprint (Hours list chip) · slice 2: confirmEditMany (billing Review) · re-review COD: unknown lock state still warns
// TASK-9646 S3b-5 — OFFICE ONLY: Timecard Exceptions + Ticket Health resolve a
// ticket's header money against the server-written ticketMoney mirror
// (_clockLoadTicketMoney). No crew / PIN / self-service path calls it. Generated
// mirror of functions/src/shared/ticketMoneyView.js; pure, SAME ?v= as billing.js.
// LAZY chunk (perf budget, _clockLoadTmv): loaded only when an office scan got at
// least one mirror. Until then these are the helper's own no-mirror answers (the
// ticket unchanged / source 'header'), which is all an empty mirror map ever asks.
let _tmvWithTicketMoney = (t) => t;
let _tmvTicketMoneyView = () => ({ source: 'header' });
let _clockTmvLoading = null;
function _clockLoadTmv() {
  if (!_clockTmvLoading) {
    _clockTmvLoading = __ddChunkWrap(import('../shared/ticket-money-view.js?v=9646310')).then((m) => {
      _tmvWithTicketMoney = m.withTicketMoney;
      _tmvTicketMoneyView = m.ticketMoneyView;
      return true;
    }, () => { _clockTmvLoading = null; return false; });
  }
  return _clockTmvLoading;
}
try {
  window.PayrollRunLock?.setFetcher?.(async () => {
    const tid = window.AppState?.tenant;
    if (!tid) return [];
    const r = await httpsCallable(functions, 'getPayrollRuns')({ tenantId: tid });
    return (r && r.data && Array.isArray(r.data.runs)) ? r.data.runs : [];
  }, () => window.AppState?.tenant || null);
} catch (_) { /* soft lock: never break clock.js boot */ }

// ============================================
// TASK-1160 — gateway idempotency keys.
// A client deadline-exceeded on mutateTicketAndClockEntry means WE gave up
// while the server may still complete; a retry then double-applies a money
// mutation. Each LOGICAL op gets a stable key: a random session token minted
// once per scope (e.g. 'jobSwitch:<entryId>') that persists across retries,
// suffixed with a hash of the exact payload — so an identical retry replays
// server-side (dedup), while a genuinely changed payload executes fresh.
// Cleared only on confirmed success. Dedup key ONLY — never an auth token.
// ============================================
const _gwOpSessions = {};
// ---------------------------------------------------------------------------
// TASK-4529 — fleet ref resolver. A jobDay/ticket ref may be a Firestore doc
// ID, a unit number, a nickname, or a legacy name — and on a live tenant one
// truck's DOC ID can equal another truck's UNIT number (TITS: doc id "15" =
// Dove, while Blue Jay's unit = "15"). A single-pass
// `find(t => t.id === ref || t.unit === ref)` returns whichever happens to
// sort first, so ID matches must be exhausted across the WHOLE list before
// any unit/nickname/name match is considered.
// KEEP IN SYNC — identical copies live in dispatch.js / jobboard.js /
// clock.js / billing.js / bi.js / jsa.js (cross-imports forbidden: Pitfall #1
// — ?v= cache busters make duplicate module instances). Twin registered in
// docs/audits/twin-audit-2026-08-19.md. A seventh SERVER copy lives in
// functions/src/services/ticketGateway.js. Byte-equality of all copies is
// asserted by tests/task4529-fleet-ref-resolver.unit.cjs.
function _resolveFleetRef(ref, list) {
  if (ref === null || ref === undefined || ref === '') return null;
  const s = String(ref);
  const arr = list || [];
  const byId = arr.find(x => x && x.id !== null && x.id !== undefined && String(x.id) === s);
  if (byId) return byId;
  const eq = (v) => v !== null && v !== undefined && v !== '' && String(v) === s;
  return arr.find(x => x && (eq(x.unit) || eq(x.nickname) || eq(x.name) || eq(x.partNumber))) || null;
}

function _gwOpKey(scope, payload) {
  if (!_gwOpSessions[scope]) {
    _gwOpSessions[scope] = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : (Date.now().toString(36) + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2));
  }
  let h = 5381;
  const s = JSON.stringify(payload || {});
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return (_gwOpSessions[scope] + '_' + h.toString(36)).replace(/[^A-Za-z0-9_-]/g, '').slice(0, 128);
}
function _gwOpDone(scope) { delete _gwOpSessions[scope]; }
function _gwIsDeadlineExceeded(err) {
  return /deadline-exceeded/i.test(String(err?.code || '')) ||
    /deadline exceeded/i.test(String(err?.message || ''));
}
// Client timeout for clock-money gateway calls: 120s (default 70s). The server
// window is 30s, but cold start + slow cellular sit ON TOP of it client-side —
// give the round trip room before we declare a timeout. (TASK-1160)
const GW_CLIENT_TIMEOUT_MS = 120000;

// TASK-9989 item 1 (Chris 2026-10-25) — the ticket gateway is the clock-entry
// edit path for EVERY company (was: TITS forced on, others only with
// settings.useTicketGateway). Left out, on purpose:
//   - the demo sandbox: anonymous auth with no members doc, so the gateway's
//     editClockEntry authz refuses it; demo keeps the direct path it has today.
//   - driver-portal / public-link sessions: those are not the office, and the
//     driver clock-out paths stay off the gateway (offline support) — same
//     exclusions the TITS early rollout always had.
// settings.useTicketGateway still forces it on (the || at each switch point).
function _clockGatewayDefaultOn(tenantId, appState) {
  if (!tenantId) return false;
  if (tenantId === 'demo' || tenantId === 'demo-tenant') return false;
  const s = appState || {};
  if (s.isPublicAccess || s.driverMode || s.driverModePin) return false;
  return true;
}
// TASK-9989 item 1 — the per-session fallback (gateway call failed for a reason
// that is not a refusal / timeout → this session uses the direct path until
// Clock is opened again) must never be silent: console + the central error log
// (debugLogs + errorLogs via app.js logError), op + error code only, no entry data.
function _clockGatewayFallback(op, err) {
  window.USE_TICKET_GATEWAY = false;
  const code = String(err?.code || err?.details?.code || 'unknown');
  console.warn(`[Clock] GATEWAY FALLBACK — ${op} failed (${code}); this session now uses the direct path:`, err?.message);
  try {
    // A fresh Error, never the server's: its message could carry a doc path or
    // entry id into errorLogs (COD r1 Secrets lane). Op + code is the signal.
    window.logError?.(new Error(`gateway fallback: ${op} (${code})`), {
      module: 'clock', operation: 'GATEWAY_FALLBACK', gatewayOp: op, errCode: code,
    });
  } catch (_) { /* logging must never change the outcome */ }
}

// ============================================
// UOM helpers (mirrored from billing.js)
// ============================================
const TIME_BASED_UNITS = new Set(['hourly', 'daily', 'monthly']);
function isTimeBasedUnit(rateUnit) {
  return TIME_BASED_UNITS.has((rateUnit || 'Hourly').toLowerCase());
}

// ============================================
// HARD-STOP COMPLIANCE GATE (TASK-258 v1.15.535)
// ============================================
// Mirrored from public/src/views/compliance.js daysUntilExpiry() — local copy
// because importing across cache busters would create two module instances per
// CLAUDE.md pitfall #1. Returns null when no date, otherwise days from today
// (negative = past, 0 = today, >0 = future).
function _hsDaysUntilExpiry(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
}

// Reads tenant.compliance.hardStopExpiredCredentials. Chris-controlled via
// Overlord per TASK-258 — default OFF. AppState.tenantData is the canonical
// path (not tenantId — see app.js logAudit comment).
function _hsIsHardStopEnabled() {
  return window.AppState?.tenantData?.compliance?.hardStopExpiredCredentials === true;
}

// Build the per-person expired-credential list. Returns array of
// { kind, label, daysOverdue } for any of CDL / MedCard with negative days.
// truck-AVI not checked from clock.js (clock-in doesn't bind to a truck).
function _hsCollectExpiredForPerson(person) {
  const out = [];
  if (!person) return out;
  const cdlDays = _hsDaysUntilExpiry(person.cdlExpiryDate);
  if (cdlDays !== null && cdlDays < 0) {
    out.push({ kind: 'cdl', label: 'CDL', daysOverdue: Math.abs(cdlDays) });
  }
  const medDays = _hsDaysUntilExpiry(person.medCardExpiryDate);
  if (medDays !== null && medDays < 0) {
    out.push({ kind: 'medcard', label: 'Medical Card', daysOverdue: Math.abs(medDays) });
  }
  return out;
}

// TASK-258 / TASK-925 Slice A — gate the override button on module access,
// not role alone. hasWorkforceAccess() (below) already covers owner /
// legacy-admin / workforce-module holder / demo / no-PINs-configured
// (single-login owner) — same override intent as the old role check, but
// module-aware so a clock-only member is NOT promoted to override power.
//
// TASK-932 — dropped a leading `isFounderAccount()` arm. It was a TENANT test that
// ran BEFORE hasWorkforceAccess()'s member-doc check, so a clock-only member on
// the founder tenant could override THEIR OWN expired-credential hard-stop.
// hasWorkforceAccess() already covers the no-member-doc owner path.
function _hsCanOverride() {
  return hasWorkforceAccess();
}

// Build & show the hard-stop modal. Returns a promise that resolves with
// { proceeded: bool, reason?: string } so the caller can branch:
// proceeded=false → stay blocked (default Close click).
// proceeded=true  → admin override; caller continues + writes HARD_STOP_OVERRIDE audit.
function _hsShowBlockingModal(personName, expired) {
  return new Promise((resolve) => {
    const lines = expired.map(e =>
      `<li style="margin:4px 0;color:var(--text-primary);font-size:14px;">
        <strong>${e.label}</strong> — expired ${e.daysOverdue} day${e.daysOverdue === 1 ? '' : 's'} ago
      </li>`
    ).join('');
    const canOverride = _hsCanOverride();
    const overrideBtn = canOverride
      ? `<button type="button" class="btn btn-warning" id="hs-hardstop-override" style="min-height:44px;margin-right:8px;">Admin Override</button>`
      : '';
    const wrap = document.createElement('div');
    wrap.className = 'modal';
    wrap.id = 'hs-hardstop-modal';
    wrap.style.cssText = 'display:flex;position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:9999;align-items:center;justify-content:center;';
    wrap.innerHTML = `
      <div class="modal-content modal-fullscreen" style="max-width:480px;background:var(--bg-card);border-radius:12px;display:flex;flex-direction:column;">
        <div class="modal-header" style="padding:20px 20px 12px 20px;border-bottom:1px solid var(--border-light);">
          <h3 style="margin:0;color:var(--text-primary);display:flex;align-items:center;gap:10px;font-size:18px;">
            <span class="material-icons" style="color:#dc2626;">block</span>
            Cannot clock in — credential expired
          </h3>
        </div>
        <div class="modal-body" style="padding:20px;flex:1;overflow-y:auto;min-height:0;">
          <p style="margin:0 0 12px 0;color:var(--text-primary);font-size:14px;">
            <strong>${(personName || 'This driver').replace(/[<>]/g,'')}</strong> has expired credential${expired.length === 1 ? '' : 's'} on file. Update the record in Roster before clocking in.
          </p>
          <ul style="margin:8px 0 16px 18px;padding:0;">${lines}</ul>
          <p style="margin:0;color:var(--text-secondary);font-size:13px;">This block is enforced per the tenant's hard-stop policy. Contact your dispatcher or compliance partner to renew.</p>
        </div>
        <div class="modal-footer" style="padding:12px 20px 20px 20px;border-top:1px solid var(--border-light);flex-shrink:0;display:flex;justify-content:flex-end;align-items:center;">
          ${overrideBtn}
          <button type="button" class="btn btn-primary" id="hs-hardstop-ok" style="min-height:44px;">Close</button>
        </div>
      </div>`;
    document.body.appendChild(wrap);
    const cleanup = (result) => { wrap.remove(); resolve(result); };
    wrap.querySelector('#hs-hardstop-ok').addEventListener('click', () => cleanup({ proceeded: false }));
    if (canOverride) {
      wrap.querySelector('#hs-hardstop-override').addEventListener('click', () => {
        const reason = (window.prompt(
          'Admin override — this incident will be audit-logged. Enter reason (min 8 chars):',
          ''
        ) || '').trim();
        if (!reason || reason.length < 8) {
          window.showToast?.('Override cancelled — reason required (min 8 chars).', 'warning');
          return; // keep modal open
        }
        cleanup({ proceeded: true, reason });
      });
    }
  });
}

// ============================================
// AUDIT LOGGING — writes to tenants/{id}/clockAuditLog
// ============================================
async function logClockAudit(tenant, action, data) {
  try {
    // TASK-916 Slice 4 — driver-PIN (anonymous) sessions have no email, so a
    // field-ticket clock-out logged here would show "unknown"/"?" in All
    // Activity. Stamp actorName = the driver's name (only when there's no email)
    // so the actor column can render "Name (driver)".
    const _email = window.AppState?.user?.email || '';
    await addDoc(collection(db, 'tenants', tenant, 'clockAuditLog'), {
      action,
      timestamp: Timestamp.now(),
      user: _email || 'unknown',
      actorName: _email ? null : (window.AppState?.driverPersonName || null),
      ...data
    });
    return true; // TASK-4616 — callers that must know the row landed (dwell retry)
  } catch (err) {
    console.warn('[Clock] Audit log failed:', err);
    return false;
  }
}

// Tracked fields for clockEntry audit (mirror of functions/src/services/clockEntryAudit.js)
const CLOCK_ENTRY_TRACKED_FIELDS = [
  'clockIn', 'clockOut', 'totalHours', 'hours', 'status', 'rate', 'role',
  'jobId', 'jobTitle', 'customerId', 'customerName', 'ticketId', 'ticketLineId',
  'notes', 'clockOutNotes', 'completionReason', 'adjustedClockIn', 'adjustedClockOut',
  'qbSynced',
  // TASK-1483 — the personal-vehicle claim is money; every edit is diffed.
  'personalVehicleUsed', 'personalVehicleMiles',
];

function _entryFieldEqual(a, b) {
  const tsToMs = v => (v?.toMillis?.() || v?._seconds * 1000 || (v instanceof Date ? v.getTime() : null));
  const aMs = tsToMs(a);
  const bMs = tsToMs(b);
  if (aMs !== null || bMs !== null) return aMs === bMs;
  return JSON.stringify(a) === JSON.stringify(b);
}

function _diffEntries(before, after) {
  const changed = {};
  for (const f of CLOCK_ENTRY_TRACKED_FIELDS) {
    if (!_entryFieldEqual(before?.[f], after?.[f])) {
      changed[f] = { from: before?.[f] ?? null, to: after?.[f] ?? null };
    }
  }
  return Object.keys(changed).length === 0 ? null : changed;
}

/**
 * Show edit history for a clockEntry in a modal. Reads tenants/{tid}/clockEntryAuditLog
 * for the given entryId, sorted newest-first.
 */
async function viewClockEntryHistory(entryId) {
  const tenant = ClockState.tenantId;
  if (!tenant || !entryId) return;
  let modal = document.getElementById('modal-clock-entry-history');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-clock-entry-history';
    modal.className = 'modal';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `
    <div class="modal-content" style="max-width:560px;">
      <div class="modal-header">
        <h3>Edit History</h3>
        <button class="btn-icon" onclick="document.getElementById('modal-clock-entry-history').classList.add('hidden')">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" id="clock-entry-history-body" style="max-height:60vh;overflow-y:auto;">
        <div style="text-align:center;color:var(--text-muted);padding:20px;">Loading...</div>
      </div>
    </div>`;
  modal.classList.remove('hidden');

  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', tenant, 'clockEntryAuditLog'),
      where('entryId', '==', entryId)
    ));
    // TASK-1655: 311 legacy rows carry timestamp {} (server sentinel was mangled
    // pre-fix) — toMillis-based sort puts them LAST instead of interleaving as 0.
    // Client-SDK Timestamps expose seconds/toMillis, not _seconds, so the old
    // `_seconds` sort key never actually sorted anything.
    const _tsMs = (t) => t?.toMillis?.() ?? ((t?._seconds ?? t?.seconds) ? (t._seconds ?? t.seconds) * 1000 : 0);
    const entries = snap.docs.map(d => d.data()).sort((a, b) => _tsMs(b.timestamp) - _tsMs(a.timestamp));
    const body = document.getElementById('clock-entry-history-body');
    if (!entries.length) {
      body.innerHTML = '<div style="text-align:center;color:var(--text-muted);padding:20px;">No edit history yet.<br><span style="font-size:0.8rem;">Edits to this entry from now on will be logged here.</span></div>';
      return;
    }
    // TASK-1655: label undated legacy rows honestly instead of '?' — they are
    // real edits whose write-time was lost (pre-fix sentinel bug), not noise.
    const fmtTs = (ts) => ts?.toDate?.()?.toLocaleString() || (ts?._seconds && new Date(ts._seconds * 1000).toLocaleString()) || 'Undated (legacy row)';
    const fmtVal = (v) => {
      if (v === null || v === undefined) return '<em style="color:var(--text-muted)">empty</em>';
      if (v?.toDate) return v.toDate().toLocaleString();
      if (v?._seconds) return new Date(v._seconds * 1000).toLocaleString();
      // TASK-917 — escape attacker-controllable snapshot values. notes/customerName/
      // jobTitle land in c.from/c.to and are writable by self-service clock +
      // driver-PIN users; without escaping, an admin opening Edit History executes
      // the payload in their authenticated session (stored XSS). Date/empty returns
      // are code-generated and safe.
      if (typeof v === 'object') return escapeHtml(JSON.stringify(v));
      return escapeHtml(String(v));
    };
    body.innerHTML = entries.map(a => `
      <div style="padding:10px 0;border-bottom:1px solid var(--border-light);">
        <div style="display:flex;justify-content:space-between;align-items:center;font-size:0.85rem;">
          <strong style="color:var(--text-primary);">${escapeHtml(a.action || '?')} via ${escapeHtml(a.source || '?')}</strong>
          <span style="color:var(--text-muted);font-size:0.78rem;">${fmtTs(a.timestamp)}</span>
        </div>
        <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:2px;">by ${escapeHtml(a.actor?.email || 'unknown')}</div>
        ${_postPayrollBadgeHtml(a)}
        ${a.changedFields ? `
          <div style="margin-top:6px;font-size:0.8rem;">
            ${Object.entries(a.changedFields).map(([f, c]) => `
              <div style="padding:2px 0;"><strong>${escapeHtml(f)}:</strong> ${fmtVal(c.from)} → ${fmtVal(c.to)}</div>
            `).join('')}
          </div>` : ''}
      </div>
    `).join('');
  } catch (e) {
    console.error('[Clock] history load failed:', e);
    document.getElementById('clock-entry-history-body').innerHTML = `<div style="color:var(--status-danger);padding:20px;">Couldn't load history: ${e.message}</div>`;
  }
}

// TASK-9443 slice 2 — "After payroll" marker on an Edit History row. The field
// is written ONLY by the server trigger onClockEntryAuditPostPayroll (it scrubs
// anything a client planted on create), so this just renders it. Escaped.
function _postPayrollBadgeHtml(a) {
  // The trigger always writes postPayroll WITH postPayrollCheckedAt; a value
  // planted on create without it (before the trigger scrubs it) is not shown.
  // Best-effort marker, not proof: Payroll Prep's snapshot diff is the record.
  if (!a || !a.postPayrollCheckedAt) return '';
  const pp = Array.isArray(a.postPayroll) ? a.postPayroll.filter(r => r && r.startYmd && r.endYmd) : [];
  if (!pp.length) return '';
  const periods = pp.map(r => `${r.startYmd} to ${r.endYmd}`).join(', ');
  return `<div style="margin-top:4px;font-size:0.76rem;font-weight:600;color:#92400E;background:#FEF3C7;border:1px solid #F59E0B;border-radius:6px;padding:2px 8px;display:inline-block;">
    After payroll: ${escapeHtml(periods)} was already exported when this change was made</div>`;
}

/**
 * Log a clockEntry edit/create/delete. Skip if no tracked field changed.
 * Phase 1 of the Clock & Billing refactor — see scripts/_cod-clock-billing-refactor-roadmap.txt
 */
async function logClockEntryChange(tenant, entryId, before, after, action, source) {
  if (!tenant || !entryId) return;
  let changedFields = null;
  if (action === 'edit') {
    changedFields = _diffEntries(before, after);
    if (!changedFields) return; // no-op
  }
  const personSource = after || before || {};
  const stripUndef = (o) => {
    if (o === null || typeof o !== 'object' || o.toMillis || o instanceof Date) return o;
    if (Array.isArray(o)) return o.map(stripUndef);
    return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined).map(([k, v]) => [k, stripUndef(v)]));
  };
  try {
    const _editorEmail = window.AppState?.user?.email || null;
    const _row = stripUndef({
      entryId,
      personId: personSource.personId || null,
      personName: personSource.personName || personSource.name || null,
      source: source || null,
      actor: { uid: window.AppState?.user?.uid || null, email: _editorEmail || 'unknown' },
      // TASK-916 Slice 4 — driver-PIN editor name when there's no email (mirrors
      // functions/src/services/clockEntryAudit.js). Null for signed-in members
      // so the renderer shows their email, not the entry's employee name.
      actorName: _editorEmail ? null : (window.AppState?.driverPersonName || personSource.personName || personSource.name || null),
      action,
      changedFields,
      beforeSnapshot: before || null,
      afterSnapshot: after || null,
    });
    // TASK-1655: stamp AFTER stripUndef so the serverTimestamp() sentinel can't
    // be recursed into (Object.entries on a sentinel yields a useless plain map
    // — the exact bug that left 311 server-written rows with timestamp {}).
    // serverTimestamp() replaces Timestamp.now(): device clocks skew, the
    // server's doesn't, and this collection exists to answer "when" in wage
    // disputes. Works offline — the SDK resolves it when the write syncs.
    _row.timestamp = serverTimestamp();
    await addDoc(collection(db, 'tenants', tenant, 'clockEntryAuditLog'), _row);
  } catch (e) {
    console.warn('[Clock] clockEntryAudit log failed:', e);
  }
}

// ============================================
// FALLBACK RATES (all zero — real rates come from
// item codes, contracts, or tenant defaultRates)
// ============================================
const FALLBACK_RATES = {
  cdl: 0,
  nonCdl: 0,
  truck: 0,
  trailer: 0,
  asset: 0
};

// ============================================
// CREW TYPE → ITEM CODE MAPPING
// ============================================
const CREW_TYPE_ITEM_CODE_MAP = {
  cdl_driver: 'CREW-CDL',
  laborer: 'CREW-LABORER',
  foreman: 'CREW-FOREMAN',
  operator: 'CREW-OPERATOR',
  swamper: 'CREW-SWAMPER',
  supervisor: 'CREW-FOREMAN',
  mechanic: 'CREW-NONCDL'
};

// ============================================
// CONTRACT RATE LOOKUP
// ============================================
async function loadContractRates(customerId) {
  if (!customerId || !shouldBuildTickets()) return null;

  // Check cache (valid for 10 minutes)
  const cached = ClockState.contractRateCache[customerId];
  if (cached && (Date.now() - cached.loadedAt) < 600000) return cached;

  const tenant = ClockState.tenantId;
  try {
    // Load active contract line items for this customer
    const contractsSnap = await getDocs(
      query(
        collection(db, 'tenants', tenant, 'customers', customerId, 'contracts'),
        where('status', '==', 'Active')
      )
    );

    let lineItems = [];
    for (const cDoc of contractsSnap.docs) {
      const linesSnap = await getDocs(
        collection(db, 'tenants', tenant, 'customers', customerId, 'contracts', cDoc.id, 'lineItems')
      );
      lineItems.push(...linesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    }

    // Load item codes with default rates
    const itemCodesSnap = await getDocs(collection(db, 'tenants', tenant, 'itemCodes'));
    const itemCodes = itemCodesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const entry = { lineItems, itemCodes, loadedAt: Date.now() };
    ClockState.contractRateCache[customerId] = entry;
    return entry;
  } catch (err) {
    console.warn('[Clock] Error loading contract rates:', err);
    return null;
  }
}

// TASK-10188 — `jobContractId` is the Price Book chosen on the shift's job day
// (jobDay.selectedContractId), or null. Field Ticket Settings come from
// window.FieldTicketSettings.resolveFieldTicketSettings: that book when it is
// one of the customer's Active books, else strictest-wins across every Active
// book — never "whichever doc sorted first".
// TASK-10190 — the "Show on field ticket" ITEMS and ClockState.contractId (the
// book stamped on each FIELD_ITEM line, which the server prices it from) follow
// the same pick: the job day's book when it is Active
// (window.FieldTicketSettings.pickLineItemsContract), else the FIRST Active
// book, exactly as before. No new pricing rule — the items' rates still come
// from the book they were listed from, server-side.
async function loadFieldTicketItems(customerId, jobContractId) {
  if (!customerId || customerId === '__shop__') {
    ClockState.fieldTicketLineItems = [];
    ClockState.fieldTicketSettings = null;
    ClockState.contractId = null;
    return;
  }

  const tenant = ClockState.tenantId;
  try {
    const contractsSnap = await getDocs(
      query(
        collection(db, 'tenants', tenant, 'customers', customerId, 'contracts'),
        where('status', '==', 'Active')
      )
    );
    if (contractsSnap.empty) {
      ClockState.fieldTicketLineItems = [];
      ClockState.fieldTicketSettings = null;
      ClockState.contractId = null;
      return;
    }

    // TASK-10190 — the job day's book when it is one of these Active books,
    // else the first Active book (the pre-10190 pick).
    const contractDoc = contractsSnap.docs[_clockPickLineItemsContractIndex(contractsSnap.docs, jobContractId)];
    ClockState.contractId = contractDoc.id;
    // TASK-10188 — settings: the job's book, else strictest across all Active books.
    ClockState.fieldTicketSettings = _clockResolveFieldTicketSettings(
      contractsSnap.docs.map(d => ({ id: d.id, ...d.data() })), jobContractId);

    const linesSnap = await getDocs(
      query(
        collection(db, 'tenants', tenant, 'customers', customerId, 'contracts', contractDoc.id, 'lineItems'),
        where('active', '==', true)
      )
    );

    ClockState.fieldTicketLineItems = linesSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(li => li.includeOnFieldTicket === true);
  } catch (err) {
    // TASK-9836 — a driver LINK member (and any login without a price-book
    // module) can no longer read the price book: rules refuse it because it
    // carries every customer rate. Ask the server for the same item list with
    // the rates stripped. Only for a signed-in member or a link session — a
    // plain PIN kiosk session never could read these and is not sent to the
    // callable (it would only add a round trip before the close-out renders).
    const _denied = /permission-denied|insufficient permissions/i.test(String(err?.code || '') + ' ' + String(err?.message || ''));
    if (_denied && (window.AppState?.accessToken || window.AppState?.memberAccess)
        && await _loadFieldTicketItemsRateFree(tenant, customerId, jobContractId)) return;
    console.warn('[Clock] Error loading field ticket items:', err);
    ClockState.fieldTicketLineItems = [];
    ClockState.fieldTicketSettings = null;
    ClockState.contractId = null; // TASK-9836 COD: never carry the previous customer's price book
  }
}

// TASK-9836 — rate-free field-ticket items from the getFieldTicketItems
// callable (functions/src/services/fieldTicketItems.js). Same pick as the
// direct read above (first Active contract, active items with Show on field
// ticket); items carry no rate, so the qty row shows the unit only. The
// FIELD_ITEM line is written at rate 0 + priceBookRatePending either way.
// Returns true when ClockState was filled.
async function _loadFieldTicketItemsRateFree(tenant, customerId, jobContractId) {
  try {
    const _req = { tenantId: tenant, customerId };
    if (jobContractId) _req.contractId = String(jobContractId); // TASK-10188 — server picks settings the same way
    const r = await httpsCallable(functions, 'getFieldTicketItems')(_req);
    const d = (r && r.data) || {};
    ClockState.contractId = d.contractId || null;
    ClockState.fieldTicketSettings = d.fieldTicketSettings || null;
    ClockState.fieldTicketLineItems = Array.isArray(d.items) ? d.items.filter(li => li && li.id) : [];
    return true;
  } catch (e) {
    console.warn('[Clock] Error loading field ticket items (rate-free):', e);
    return false;
  }
}

// TASK-10190 — index into the Active contract docs for the line items. The
// shared rule when loaded; a stale cached app.js without it falls back to the
// same rule inline (job's book if present in the list, else index 0).
function _clockPickLineItemsContractIndex(docs, jobContractId) {
  const list = (docs || []).map(d => ({ id: d.id }));
  const FTS = window.FieldTicketSettings;
  if (FTS && typeof FTS.pickLineItemsContract === 'function') {
    const i = FTS.pickLineItemsContract(list, jobContractId || null).index;
    return i >= 0 ? i : 0;
  }
  const want = jobContractId ? String(jobContractId) : '';
  const i = want ? list.findIndex(c => c.id === want) : -1;
  return i >= 0 ? i : 0;
}

// TASK-10188 — the shared rule (window.FieldTicketSettings, generated from
// functions/src/shared/fieldTicketSettings.js). If the global is missing (a
// stale cached app.js), fall back to strictest-wins over the request flags
// only, so a missing global can never drop a pad another book asked for.
function _clockResolveFieldTicketSettings(activeContracts, jobContractId) {
  const FTS = window.FieldTicketSettings;
  if (FTS && typeof FTS.resolveFieldTicketSettings === 'function') {
    return FTS.resolveFieldTicketSettings(activeContracts, jobContractId || null).settings;
  }
  const withFts = (activeContracts || []).filter(c => c && c.fieldTicketSettings && typeof c.fieldTicketSettings === 'object');
  if (!withFts.length) return null;
  const out = {};
  for (const k of ['requireDriverSignature', 'requireCustomerSignature', 'requireStamp', 'requireEmailCopy']) {
    out[k] = withFts.some(c => c.fieldTicketSettings[k] === true);
  }
  if (withFts.some(c => c.fieldTicketSettings.showCostsOnDriverTicket === false)) out.showCostsOnDriverTicket = false;
  // Recipient only when every book with one agrees (two projects / two GCs).
  const _addrs = [...new Set(withFts.map(c => String(c.fieldTicketSettings.defaultEmailRecipient || '').trim().toLowerCase()).filter(Boolean))];
  if (_addrs.length === 1) out.defaultEmailRecipient = withFts.find(c => c.fieldTicketSettings.defaultEmailRecipient).fieldTicketSettings.defaultEmailRecipient;
  return out;
}

// ===========================================================================
// TASK-1674 — EQUIPMENT rate matcher CORE. TRIPLICATED — KEEP BYTE-IDENTICAL.
// Twins: public/src/views/clock.js · public/src/views/billing.js ·
//        functions/src/services/ticketGateway.js  (search _equipRateMatchCore).
// A browser module cannot import from functions/, and cross-importing a
// `?v=`-busted client module mints a SECOND module instance (Pitfall #1), so
// three copies is the accepted shape here — same call the _resolveFleetRef
// twins make. tests/task1674-equipment-rate-match.unit.cjs extracts all three
// and fails if either the SOURCE TEXT or the fixture-table output diverges.
// Edit one → edit all three → run that test.
//
// WHY IT REPORTS HOW IT MATCHED. The three old copies returned only {rate,...}
// and threw away the reason, so a last-resort `.find()` over a description
// regex was indistinguishable from an exact price-book hit. Every TITS contract
// carries a "Winch Truck" @ $150 line, so any vehicle the price book did not
// cover — a $285 hydrovac, a semi, a rental vac — silently priced as a winch
// truck and then passed review looking fully priced, because the only existing
// badge fires on rate === 0. Read-only probe of live TITS, 2026-08-20: 166
// EQUIPMENT lines ($142,350 billed) resolve to a guess — 76 off the description
// regex, 7 off a seeded item code, 83 off an ambiguous appliesTo key.
// `guessed` is how a caller tells the two apart. Nothing here sets policy; it
// only reports what it did, and returns null rather than inventing a number.
//
// WHY candidateCount EXISTS. An appliesTo hit is not automatically trustworthy
// either: TITS binds TEN different line items to {kind:"truck",type:"trucking"}
// (Equipment $170 … SuperLoads+Jeep+Booster $235) and `.find()` returned
// whichever sorted first by Firestore doc ID — so a hand-added line with a
// random ID could silently become a customer's default truck rate. More than
// one candidate means the price book cannot answer the question. Say so.
// ===========================================================================
function _equipRateMatchCore(lineItems, itemCodes, subtype, vehicleType, assetName) {
  const lines = Array.isArray(lineItems) ? lineItems : [];
  const codes = (Array.isArray(itemCodes) ? itemCodes : []).filter((c) => c && c.billable !== false);
  const kind = subtype === "truck" ? "truck" : subtype === "trailer" ? "trailer" : "asset";
  const vt = String(vehicleType || "").trim();
  const usable = (l) => !!l && (l.active === false || l.rate != null);
  const finish = (via, line, code, count) => ({
    matchedVia: count > 1 ? via + "-ambiguous" : via,
    line: line || null,
    itemCode: code || null,
    matchedDescription: line
      ? (line.description || line.itemCode || "")
      : (code ? (code.description || code.name || code.code || "") : ""),
    candidateCount: count,
    guessed: count > 1 || via === "category-regex" || via === "item-code",
  });

  if (lines.length > 0) {
    // [1] v2 appliesTo — exact kind+type, then a kind-only line ("any truck").
    //     COUNTED, never `.find()`-ed: see the candidateCount note above.
    let cands = vt ? lines.filter((l) => l.appliesTo && l.appliesTo.kind === kind && l.appliesTo.type === vt) : [];
    let via = "appliesTo";
    if (cands.length === 0) {
      cands = lines.filter((l) => l.appliesTo && l.appliesTo.kind === kind && !l.appliesTo.type);
      via = "appliesTo-any";
    }
    if (cands.length > 0 && usable(cands[0])) return finish(via, cands[0], null, cands.length);

    // [2] The asset's own name inside a line description ("Pelican #13" → a
    //     line described "Pelican"). Specific enough to trust, so not a guess.
    const nameWords = String(assetName || "").toLowerCase().split(/[\s_/-]+/).filter((w) => w.length > 2);
    const nameCats = subtype === "truck"
      ? ["Trucks & Trailer", "Heavy Haul", "Hydrovac"]
      : (subtype === "trailer" ? [] : ["Equipment", "Other"]);
    if (nameWords.length > 0 && nameCats.length > 0) {
      const hit = lines.find((l) => nameCats.indexOf(l.category) >= 0 &&
        nameWords.some((w) => String(l.description || "").toLowerCase().includes(w)));
      if (usable(hit)) return finish("name", hit, null, 1);
    }

    // [3] Legacy (pre-appliesTo) contracts — by vehicle type, then by category.
    if (subtype === "truck") {
      if (vt) {
        const vtl = vt.toLowerCase();
        // ⚠️ THIS ARM IS THE HYDROVAC FIX. A tenant's own "hydrovac" type and
        // the built-in "vac" type are the same truck. billing.js had this
        // rescue; clock.js and the server did not — so a `vac`-typed hydrovac
        // fell to the winch-truck regex below and billed $150 instead of $285.
        let hit = /hydrovac|vac/i.test(vtl) ? lines.find((l) => l.category === "Hydrovac") : null;
        if (!hit) {
          hit = lines.find((l) => (l.category === "Trucks & Trailer" || l.category === "Heavy Haul") &&
            String(l.description || "").toLowerCase().includes(vtl));
        }
        if (usable(hit)) return finish("vehicle-type", hit, null, 1);
      }
      const guess = lines.find((l) => (l.category === "Trucks & Trailer" || l.category === "Heavy Haul") &&
        /truck|cab|sleeper|daycab|pickup|winch|bobtail|tanker|water/i.test(l.description || l.itemCode || ""));
      if (usable(guess)) return finish("category-regex", guess, null, 1);
    } else if (subtype === "trailer") {
      const guess = lines.find((l) => (l.category === "Trucks & Trailer" || l.category === "Heavy Haul") &&
        /trailer|flatbed|stepdeck|lowboy|rgn|gooseneck|drop.?deck|hot.?shot/i.test(l.description || l.itemCode || ""));
      if (usable(guess)) return finish("category-regex", guess, null, 1);
    } else {
      const guess = lines.find((l) => l.category === "Equipment" && l.rate != null);
      if (usable(guess)) return finish("category-regex", guess, null, 1);
    }
  }

  // [4] Seeded item-code defaults. TT-DAYCAB ships at $150 — the same wrong
  //     number the winch-truck regex lands on. Both roads are a guess.
  if (codes.length > 0) {
    const wanted = subtype === "truck"
      ? ["TT-DAYCAB", "TT-SLEEPER"]
      : (subtype === "trailer" ? ["TT-FLATBED", "TT-STEPDECK"] : ["EQ-MISC", "EQ-GENERATOR"]);
    const code = codes.find((c) => wanted.indexOf(c.code) >= 0);
    if (code && code.defaultRate > 0) return finish("item-code", null, code, 1);
  }

  // [5] Nothing in the price book covers this vehicle. REFUSE. The caller
  //     decides what to do with that, and every caller has to say so out loud.
  return null;
}

// TASK-1674 — twin of the same name in clock.js / billing.js / ticketGateway.js.
// KEEP BYTE-IDENTICAL (same test pins it). Turns a _equipRateMatchCore result
// into the {rate, rateUnit, ...} shape the three lookups have always returned,
// plus the provenance fields (`matchedVia` / `matchedDescription` /
// `candidateCount` / `rateGuessed`) that let a caller refuse to bill silently.
function _shapeEquipRateMatch(m) {
  if (!m || (!m.line && !m.itemCode)) return null;
  const isLine = !!m.line;
  const src = isLine ? m.line : m.itemCode;
  return {
    rate: isLine ? (src.active === false ? 0 : src.rate) : src.defaultRate,
    rateUnit: (isLine ? src.rateUnit : src.defaultRateUnit) || "Hourly",
    roundToHalfHour: src.roundToHalfHour === true,
    roundSetting: src.roundToHalfHour === true ? true : (src.roundToHalfHour === false ? false : null),
    includeOnInvoice: isLine ? src.includeOnInvoice !== false : true,
    revenueCategory: (isLine ? src.revenueCategory : "") || "",
    matchedVia: m.matchedVia,
    matchedDescription: m.matchedDescription,
    candidateCount: m.candidateCount,
    rateGuessed: m.guessed === true,
  };
}

async function lookupContractRate(customerId, type, subtype, vehicleType, assetName = '') {
  // type: 'EMPLOYEE' | 'EQUIPMENT'
  // subtype: 'CDL' | 'NON_CDL' | crew_type_slug | 'truck' | 'trailer' | 'asset'
  // vehicleType: optional truck/trailer type for v2 appliesTo matching
  // assetName: optional unit/nickname, for the name arm of the shared matcher

  const data = await loadContractRates(customerId);
  if (!data) return null; // no contract data, fall back to FALLBACK_RATES

  const { lineItems, itemCodes } = data;

  // TASK-1674 — EQUIPMENT now runs the shared matcher (_equipRateMatchCore
  // above), byte-identical to the billing.js and ticketGateway.js copies. It
  // returns null instead of inventing a number, and stamps `rateGuessed` when
  // the match was a last-resort description regex or a seeded item-code
  // default. EMPLOYEE is deliberately untouched: a man-hour line never carries
  // a customer rate on this tenant ([[feedback_man_hour_lines_never_carry_a_cost]])
  // and this task must not move that number in either direction.
  if (type === 'EQUIPMENT') {
    const _eq = _shapeEquipRateMatch(_equipRateMatchCore(lineItems, itemCodes, subtype, vehicleType, assetName));
    // TASK-9489 — Always / Never on the line wins; an unset line or item code
    // follows the company switch (off unless the company turned it on).
    if (_eq) _eq.roundToHalfHour = _clockResolveEquipRound(_eq.roundSetting);
    return _eq;
  }

  // 1. Try contract line item match
  if (lineItems.length > 0) {
    let matchedLine = null;

    // v2 appliesTo matching (priority — matches billing.js lookupReviewRate logic)
    if (type === 'EMPLOYEE' && subtype) {
      matchedLine = lineItems.find(l => l.appliesTo?.kind === 'person' && l.appliesTo?.type === subtype);
      if (!matchedLine) {
        matchedLine = lineItems.find(l => l.appliesTo?.kind === 'person' && !l.appliesTo?.type);
      }
      if (matchedLine) {
        if (matchedLine.active === false) return { rate: 0, roundToHalfHour: matchedLine.roundToHalfHour === true, rateUnit: matchedLine.rateUnit || 'Hourly', includeOnInvoice: matchedLine.includeOnInvoice !== false, revenueCategory: matchedLine.revenueCategory || '' };
        if (matchedLine.rate != null) return { rate: matchedLine.rate, roundToHalfHour: matchedLine.roundToHalfHour === true, rateUnit: matchedLine.rateUnit || 'Hourly', includeOnInvoice: matchedLine.includeOnInvoice !== false, revenueCategory: matchedLine.revenueCategory || '' };
      }
      matchedLine = null; // reset for legacy fallback
    }

    // Legacy matching (regex/category based)
    if (type === 'EMPLOYEE') {
      if (subtype === 'CDL' || ['cdl_driver', 'foreman', 'operator'].includes(subtype)) {
        matchedLine = lineItems.find(l =>
          l.category === 'Personnel' &&
          /foreman|operator|cdl|driver/i.test(l.description || l.itemCode || '')
        );
      } else {
        matchedLine = lineItems.find(l =>
          l.category === 'Personnel' &&
          /laborer|swamper|helper|non.?cdl/i.test(l.description || l.itemCode || '')
        );
      }
    }
    // TASK-1674 — the EQUIPMENT arm that stood here (and its item-code twin
    // below) is gone: it is the defect. `.find()` over
    // /truck|cab|sleeper|daycab|pickup|winch/ returned the FIRST truckish line,
    // which on TITS is "Winch Truck" @ $150, so every unmatched vehicle billed
    // as a winch truck. EQUIPMENT returns above, from _equipRateMatchCore.

    if (matchedLine) {
      if (matchedLine.active === false) return { rate: 0, roundToHalfHour: matchedLine.roundToHalfHour === true, rateUnit: matchedLine.rateUnit || 'Hourly', includeOnInvoice: matchedLine.includeOnInvoice !== false };
      if (matchedLine.rate != null) return { rate: matchedLine.rate, roundToHalfHour: matchedLine.roundToHalfHour === true, rateUnit: matchedLine.rateUnit || 'Hourly', includeOnInvoice: matchedLine.includeOnInvoice !== false };
    }
  }

  // 2. Try item code default rates
  if (itemCodes.length > 0) {
    let matchedCode = null;

    if (type === 'EMPLOYEE') {
      matchedCode = itemCodes.find(ic =>
        (subtype === 'CDL' || ['cdl_driver', 'foreman', 'operator'].includes(subtype))
          ? ic.code === 'CREW-CDL' || ic.code === 'CREW-FOREMAN'
          : ic.code === 'CREW-NONCDL' || ic.code === 'CREW-LABORER'
      );
    }
    // (EQUIPMENT item codes are handled by _equipRateMatchCore — see above.)

    if (matchedCode && matchedCode.defaultRate != null) return { rate: matchedCode.defaultRate, roundToHalfHour: matchedCode.roundToHalfHour === true, rateUnit: matchedCode.defaultRateUnit || 'Hourly', includeOnInvoice: true, revenueCategory: '' };
  }

  // 3. No match — caller should fall back to FALLBACK_RATES
  return null;
}

// Round hours to nearest half hour: :00-:14 → down, :15-:44 → .5, :45-:59 → up
function roundHalfHour(hours) {
  // TASK-1410 — ALWAYS ROUND UP to the next half hour. Standard oilfield
  // practice (Chris, 2026-08-07). This function previously rounded to the
  // NEAREST half hour, so `frac < 0.25` silently rounded DOWN: 8.2h billed 8.0
  // when it should bill 8.5, and 8.6h billed 8.5 when it should bill 9.0 — on a
  // $285/hr truck that is $142.50 given away per line, on every partial hour,
  // for as long as the feature has shipped. The name and comment said
  // "nearest", so the wrong rule was implemented faithfully rather than typo'd.
  // 549 of TITS's 808 time-based contract line items have roundToHalfHour on.
  // The 1e-9 guard keeps float noise from bumping an EXACT half (8.5 stays 8.5).
  // NOTE: this INCREASES invoice amounts. Past invoices are not retroactive.
  if (!Number.isFinite(hours) || hours <= 0) return 0;
  return Math.ceil((hours * 2) - 1e-9) / 2;
}

// TASK-9489 — the company "round equipment to the half hour" switch and the
// ONE decision (window.EquipmentRounding, generated from
// functions/src/shared/equipmentRounding.js — the server enrichment runs the
// same file). Read at CALL time (Pitfall #1). If the shared module is somehow
// missing, every helper answers exactly what the pre-9489 code did.
function _clockCompanyRoundsEquipment() {
  const ER = window.EquipmentRounding;
  return ER ? ER.companyRoundsEquipment(window.AppState?.settings) : false;
}
function _clockResolveEquipRound(lineValue) {
  const ER = window.EquipmentRounding;
  return ER ? ER.resolveEquipmentRounding(lineValue, _clockCompanyRoundsEquipment()) : lineValue === true;
}
// Nothing in the price book matched (rate falls back): a real customer follows
// the company switch; Shop / no customer never rounds.
function _clockNoMatchRounds(customerId) {
  return !!customerId && customerId !== '__shop__' && _clockCompanyRoundsEquipment();
}
// A dispatcher's jobDay pick. Its snapshot cannot tell Never from Company
// default (pre-9489 picks stored false for an unset line), so the picked line
// as it stands now decides. Company switch off = exactly `=== true`.
function _clockSelectionRounds(selected, customerId) {
  const ER = window.EquipmentRounding;
  if (!ER) return selected?.roundToHalfHour === true;
  const cached = customerId ? ClockState.contractRateCache[customerId] : null;
  const line = selected?.lineItemId && Array.isArray(cached?.lineItems)
    ? (cached.lineItems.find(l => l && l.id === selected.lineItemId) || null)
    : null;
  return ER.resolveSelectionRounding(selected?.roundToHalfHour, line, _clockCompanyRoundsEquipment());
}

// ============================================
// TASK-4576 — ONE hours rule for every close-out EQUIPMENT writer
// ============================================
// WHAT WENT WRONG. Four writers close out an equipment line. THREE of them
// rounded — confirmClockOut, confirmClockOutFromReview and saveTicketFromForm
// each asked `eq.roundToHalfHour` first. `applyReviewData` — the "Apply" button
// on the review screen, the path a ticket-building founder account actually
// clicks — did NOT. It computed `parseFloat(calcHoursBetween(...).toFixed(2))`
// flat: no rounding check, no `roundedFrom`. So the SAME physical shift billed
// DIFFERENT hours depending on which button the dispatcher pressed — 7.13h
// through Apply, 7.5h through Confirm, a $105.45 swing per line on a $285/hr
// truck — and the Apply-written line carried no evidence it had ever been
// decided, so billing.js's decideLineRounding reads it back as "never rounded"
// and every later recalc keeps it that way. A billing-accuracy /
// customer-dispute defect, not a payroll one: equipment billing is not payroll.
//
// PURE, and the purity IS the deliverable — the rule cannot be right in three
// writers and wrong in a fourth if there is only one copy of it. Exported on
// window.ClockModule per Pitfall #1 (window.*, NEVER a ?v= cross-import: two
// busters = two module instances = two rules = exactly this bug again).
//
//   rawHours          — the raw window in hours, however the caller measured it
//                       (calcHoursBetween on create, newEnd-existingFrom on the
//                       extend/re-save branches).
//   perUnit           — a per-unit item bills on a MANUAL quantity, so its hours
//                       are 0 and there is nothing to round.
//   roundToHalfHour   — the price-book decision carried on the equipment row.
//
// The rounding rule itself (roundHalfHour, TASK-1410, always UP) is NOT changed
// here — only whether it is applied. The `roundedFrom`/`roundToHalfHour`
// EVIDENCE stays an explicit inline `if` at each write site, deliberately: it
// must stay gated so it never lands on a line that carries neither field today
// (an unconditional write is the TASK-428 new-denial-surface failure mode).
function clockEquipmentHours(rawHours, perUnit, roundToHalfHour) {
  if (perUnit === true) return 0;
  if (roundToHalfHour === true) return roundHalfHour(rawHours);
  return parseFloat(Number(rawHours).toFixed(2));
}

// ============================================
// CLOCK STATE
// ============================================
const ClockState = {
  people: [],
  activeEntries: [],
  recentEntries: [],
  allJobs: [],         // all active jobs (not just today's)
  todaysJobDays: [],   // today's jobDays docs (for equipment lookup)
  entryJobDays: [],    // the clock-out shift's own day when it predates today (_clockEnsureEntryJobDays)
  customers: [],
  trucks: [],
  trailers: [],
  assets: [],
  selectedPerson: null,
  authenticatedPersonId: null,  // who clocked in with valid PIN (for edit permissions)
  codeGrant: null,     // TASK-9941 F S2 — { personId, how, reason, at }: ONE punch, ONE person
  clockOutAuth: null,  // TASK-9941 F S2 — the grant clockOut() opened the sheet under
  selectedCustomer: null,
  clockOutEntry: null, // entry being clocked out
  clockOutEquipment: [],
  clockOutDowntime: [],
  clockOutStandby: [],
  clockOutPotholes: [],
  clockOutPhotos: [],
  unsubscribers: [],
  tenantId: null,
  contractRateCache: {},  // { [customerId]: { lineItems: [...], itemCodes: [...], loadedAt } }
  // Field Ticket v2 state
  fieldTicketLineItems: [],
  fieldTicketSettings: null,
  contractId: null,
  // Tab state
  activeTab: 'clock', // 'clock', 'timeoff', or 'workforce'
  // Time Off state (for founder account)
  timeoffRequests: [],
  timeoffFilter: 'pending',
  timeoffGroup: null, // TASK-10121: 'employee' | 'none'; null = not loaded yet (see _clockTimeOffGroup)
  timeoffGroupTenant: null, // tenant the cached timeoffGroup was read for
  selectedRequest: null,
  // Workforce state
  workforceTab: 'hours',
  workforceWeekStart: null,
  weeklyCache: {},     // { 'YYYY-MM-DD': entries[] } for week navigation
  // TASK-1181 — weeklyCache keys can be SEEDED partially (showEmployeeDetail
  // pushes one person's fetched entries so edit/delete can resolve them), so
  // bare key existence does NOT mean "the full week is loaded". This flag is
  // set ONLY by a successful full-week loadWeekData() query. Wipe it wherever
  // weeklyCache is wiped.
  weeklyCacheFullyLoaded: {}, // { 'YYYY-MM-DD': true } authoritative full-week loads
  workforceSearch: '', // search filter for weekly hours table
  adjustments: [],
  adjustmentsPeriod: 'all',
  // TASK-4637 — the Adjustments tab used to render a blank panel on 85 of 86
  // tenants and never say why (the listener was founder-gated). These three
  // flags exist so the panel can always explain its own state: still loading,
  // read denied by rules, or genuinely empty. A blank panel is the one thing
  // it must never be again.
  adjustmentsLoaded: false,     // first snapshot (or denial) has landed
  adjustmentsDenied: false,     // firestore.rules refused the read
  adjustmentsTruncated: false,  // snapshot hit ADJUSTMENTS_LISTENER_LIMIT
  // TASK-1442 Slice 1 — Timecard Exceptions (the old "Health" sub-tab).
  // Point-in-time scan over a date range of CLOSED clock entries. Deliberately
  // NOT live: refreshWorkforceIfActive() skips this tab (same reason Payroll
  // Prep does) so a background snapshot can't wipe a scan mid-review.
  // NOTE: kept entirely separate from the reports* period state — the two tabs
  // are navigated independently and must never clobber each other's range.
  exceptionsPeriod: 'payperiod',     // 'payperiod'|'week'|'lastweek'|'month'|'lastmonth'|'custom'
  exceptionsPayPeriodOffset: -1,     // -1 = last COMPLETED pay period (the one being paid)
  exceptionsCustomStart: null,       // 'YYYY-MM-DD'
  exceptionsCustomEnd: null,         // 'YYYY-MM-DD'
  exceptionsRows: [],                // last scan result (array of exception objects)
  exceptionsMeta: null,              // { rangeLabel, scanned, closed, active, capped, ticketsChecked, ticketLookupSkipped, internalResolved, unattachedHours, ranAt }
  exceptionsLoading: false,
  exceptionsRan: false,              // true once a scan has completed (so "0 issues" ≠ "never ran")
  exceptionsTypeFilter: 'all',       // exception `type` key or 'all'
  exceptionsLoadSeq: 0,              // monotonic token — a stale scan discards its own result
  // TASK-916 — All Activity audit-log filter state.
  auditCurrentType: 'all',        // which log tab is showing: 'all'|'clock'|'job'|'telemetry'
  auditFilterDate: 'all',         // 'all'|'today'|'week'|'payperiod'|'custom'
  auditFilterCustomStart: null,   // 'YYYY-MM-DD'
  auditFilterCustomEnd: null,     // 'YYYY-MM-DD'
  auditFilterActor: 'all',        // actor key (email / 'name:<n>' / '__anon__') or 'all'
  auditLoadedDocs: [],            // last query result (pre actor-filter) for client re-render
  auditLoadedCapped: false,       // true when the query hit the row limit (more may exist)
  // Reports state
  reportsData: [],
  reportsLoadSeq: 0,              // TASK-1315 — monotonic token: a loadReportsData whose token is stale (a newer load/clear started) discards its result instead of clobbering the current period's data

  // TASK-949 — supplementary, WEEK-EXPANDED superset of reportsData: entries in
  // [weekStart(period.start) .. weekEnd(period.end)]. Used ONLY to compute
  // cross-period workweek OT (a workweek straddling a semimonthly/monthly
  // boundary needs its FULL-week hours visible). NEVER fold this into the
  // period-bounded sums (reg/customer/date/per-diem) — reportsData stays the
  // source of truth for those. See computeCrossPeriodOT / loadReportsData.
  reportsWeekData: [],
  reportsPeriod: 'payperiod', // default to the tenant's configured pay period (biweekly/weekly/etc.), NOT a single week — a payroll report must span the full pay period (TITS biweekly = 2 weeks). Was 'week' which under-reported payroll to one week.
  reportsCustomStart: null,
  reportsCustomEnd: null,
  reportsEmployee: 'all',
  reportsCustomer: 'all',
  reportsLoading: false,
  reportsExpanded: {},  // { personId: true } for expandable employee rows
  reportsPayPeriodOffset: -1, // -1 = last COMPLETED pay period (the one being paid). 0=current (still in progress, since offset-0 always contains today). Payroll runs the completed period, so the report opens there; ◀/▶ navigates. (-1 default + the 'payperiod' default at reportsPeriod together = "opens on the period you're paying".)
  reportsShowNotes: false, // toggle controls notes column in Detailed CSV + employee-detail print
  // TASK-1321 — customer-layout-only print toggles (Customer Hours Summary).
  reportsShowDailyDetail: false, // per-date sub-rows under each customer job row (opt-in, default OFF). No employee names.
  reportsShowLaborDetail: null,  // Labor Detail (employee → hours) on customer layout. null = inherit tenant default (AppState.settings.clockCustomerLaborDetailDefault); a boolean = explicit per-session override. Names + raw hours ONLY — never rate/OT/per-diem.
  // TASK-1182 — pay-period settings panel open/closed. Held in state (not just a
  // DOM class) because getWorkforceReportsHTML() re-renders the whole tab —
  // including after a save — and the panel must survive that so the user can see
  // the preview they just committed line up with the header label.
  payPeriodPanelOpen: false,
  detailPayPeriodOffset: 0,  // pay period offset for employee detail modal
  detailPersonId: null,      // person ID for current detail modal
  // TASK-485 — sort direction for the employee-detail timesheet Date column.
  // 'desc' = newest-on-top (default per Chris), 'asc' = oldest-on-top.
  // Persisted to localStorage so the choice sticks across reloads.
  detailSortDir: (() => { try { return localStorage.getItem('dd_timesheet_sortdir') === 'asc' ? 'asc' : 'desc'; } catch (e) { return 'desc'; } })(),
  // TASK-451 — driver "My Timesheet" read-only self-service pay-period offset.
  // SEPARATE from detailPayPeriodOffset (which the workforce-admin detail modal
  // writes) so a driver's timesheet view + an admin detail modal can't clobber
  // each other's period. 0 = current period; negative = further back.
  myTimesheetOffset: 0,
  // TASK-9195 — "What I did" lazy detail for My Hours.
  // `myTimesheetEntryIndex` maps a DOM-safe entry key -> { rawId, ticketId,
  // personId } for the entries CURRENTLY rendered, so the on-demand loader never
  // has to be handed a ticket id from an (easily replayed) inline handler.
  // `myTimesheetDetail` caches the rendered HTML per entry so re-expanding a row,
  // or paging away and back, costs ZERO extra Firestore reads.
  // 🔴 Both are rebuilt/cleared by the self-scoped render path only — nothing
  // here may ever hold another person's entry (TASK-451 hard rule).
  myTimesheetEntryIndex: {},
  myTimesheetDetail: {},
  // Review mode state (founder only)
  reviewMode: false,
  reviewData: null,
  reviewDwellInterval: null, // TASK-4616 — review-screen dwell watch
  originalShiftNotes: '',
  cleanedShiftNotes: '',
  // Live clock-out timer
  clockOutTimerInterval: null,
  clockOutTimerPaused: false,
  clockOutTypedByHand: false, // TASK-4616 — Clock Out field edited by hand (Clock In edits also pause the timer)
  // History tracking (founder only)
  historyEntries: [],       // older entries loaded via "View More"
  historyLoadedUntil: null, // Date — how far back we've fetched
  _entriesLoaded: false,
  // TASK-364 — Historical (inactive/terminated) people enrichment cache.
  // Keyed by personId. Lazy-populated during workforce reports / payroll
  // export so inactive folks who worked in the period get pay-rate, phone,
  // address, FLSA, hireDate, hrEmploymentType, etc. on their CSV row.
  // DO NOT push these into ClockState.people — that's the active-only live
  // state for dispatch picker / time-off dropdown / clock-in target.
  _historicalPeopleCache: new Map(),
  _historicalCacheFetchedFor: null  // tracks which set of pids we last fetched, for cheap re-warm skip
};

// ============================================
// TASK-364 — HISTORICAL PEOPLE ENRICHMENT (inactive folks who worked in a pay period)
// ============================================
// COD-locked Option D (2026-05-26 cod-reviews/2026-05-26T16-53*.md):
// Surgical lazy-fetch instead of dropping the global subscription filter.
// Skeptic 🔴 + Architect ⚠ + Codex ⚠ + Independent ⚠ all converged on PII
// broadening + 20-site audit blast radius of Option A. Option D fixes the
// data (payroll/print/dropdown lookups) without bloating live state.
async function fetchHistoricalPeopleForPeriod() {
  if (!ClockState.tenantId) return;
  const reportsData = ClockState.reportsData || [];
  if (reportsData.length === 0) return;

  // Compute pids that appear in this period's entries but are NOT in active people.
  const activeIds = new Set(ClockState.people.map(p => p.id));
  const missingIds = new Set();
  for (const entry of reportsData) {
    const pid = entry.personId;
    if (!pid) continue;
    if (activeIds.has(pid)) continue;
    if (ClockState._historicalPeopleCache.has(pid)) continue;
    missingIds.add(pid);
  }
  if (missingIds.size === 0) return;

  // Cheap re-warm skip: if we just fetched this exact set, don't re-hit Firestore.
  const fetchKey = Array.from(missingIds).sort().join(',');
  if (ClockState._historicalCacheFetchedFor === fetchKey) return;

  try {
    // Parallel getDoc fetches — N is typically small (1-5 inactive folks per period;
    // rarely above 20). Avoids the Firestore `in` query 30-cap chunking complexity
    // and doesn't require adding documentId() to firebase-config.js.
    // Track per-pid outcome so the fetchKey skip cannot suppress retries on partial
    // failure (COD Code Reviewer concern #2: do not stamp the skip key if any
    // missing pid failed to populate the cache).
    let anyFailed = false;
    const fetches = Array.from(missingIds).map(pid =>
      getDoc(doc(db, 'tenants', ClockState.tenantId, 'people', pid))
        .then(snap => {
          if (snap.exists()) return { id: snap.id, ...snap.data() };
          // Doc truly missing in Firestore (deleted person) — treat as final,
          // not a transient failure. Cache a null sentinel so we don't refetch
          // forever; getPersonForReport returns null which falls through to
          // entry.personName denormalized data downstream.
          return { id: pid, _missing: true };
        })
        .catch(err => {
          anyFailed = true;
          console.warn('[Clock] historical person fetch failed:', pid, err?.message || err);
          return null;
        })
    );
    const results = await Promise.all(fetches);
    for (const person of results) {
      if (!person) continue;
      if (person._missing) continue;  // don't pollute cache with sentinel
      ClockState._historicalPeopleCache.set(person.id, person);
    }
    if (!anyFailed) {
      ClockState._historicalCacheFetchedFor = fetchKey;
    }
  } catch (err) {
    console.error('[Clock] fetchHistoricalPeopleForPeriod error:', err);
    // Do not stamp fetchKey on outer-catch path — let next call retry.
  }
}

// Merged lookup — active live state first, then historical cache. Returns null
// only when the pid is truly unknown (doc deleted from Firestore).
// TASK-1481 — the return is resolved through PersonComp.effective so every
// consumer (payroll CSV, print report, manual-entry rate stamp, per-diem
// default, My-Timesheet self view, …) reads compensation comp-first with the
// legacy person-doc fallback. This is the clock module's choke point for comp
// reads; do NOT read person.hrHourlyRate off a raw ClockState.people entry.
function getPersonForReport(pid) {
  if (!pid) return null;
  const raw = ClockState.people.find(p => p.id === pid)
    || ClockState._historicalPeopleCache.get(pid) || null;
  if (!raw) return null;
  return window.PersonComp ? window.PersonComp.effective(raw) : raw;
}

// TASK-10034 — people marked No Longer Employed who are still on payroll for
// the pay period starting `startYmd` (their last day falls in it or later).
// The live people subscription is active == true only, and termination sets
// active:false, so without this a departed salaried person (no clock hours)
// vanished from Payroll Prep and the payroll CSV in the very period their final
// check is cut. Same rule as the connector (window.PayPeriodHours.onPayroll).
// Returns [] when nobody qualifies, or null when the roster could not be read
// (the caller says so; never a silent "nobody left").
// Fetched people go into the historical cache so getPersonForReport finds them.
async function loadFinalPeriodPeople(tid, startYmd) {
  if (!tid || !startYmd) return [];
  try {
    // terminated == true, plus active == false (COD: a legacy record marked only
    // by terminationDate is inactive too) — onPayroll decides who stays.
    const people = collection(db, 'tenants', tid, 'people');
    const [s1, s2] = await Promise.all([
      getDocs(query(people, where('terminated', '==', true))),
      getDocs(query(people, where('active', '==', false))),
    ]);
    const out = [];
    const seen = new Set();
    s1.docs.concat(s2.docs).forEach(d => {
      if (seen.has(d.id)) return;
      seen.add(d.id);
      const p = { id: d.id, ...d.data() };
      if (!_pph().onPayroll(p, startYmd, _pphCfg().tz)) return;
      out.push(p);
      if (tid === ClockState.tenantId) ClockState._historicalPeopleCache.set(p.id, p);
    });
    return out;
  } catch (err) {
    console.warn('[Clock] TASK-10034 final-period people lookup failed:', err?.message || err);
    return null;
  }
}

// ============================================
// INITIALIZE CLOCK VIEW
// ============================================
export function initClock(container, tenantId) {
  console.log('[Clock] Initializing for tenant:', tenantId);

  if (!tenantId) {
    console.error('[Clock] No tenant ID provided');
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">warning</div>
        <div class="empty-state-title">Account Setup Issue</div>
        <div class="empty-state-text">
          Unable to load clock. Please try logging out and back in.
        </div>
        <button class="btn btn-primary" onclick="window.handleLogout()">
          <span class="material-icons">logout</span> Log Out
        </button>
      </div>
    `;
    return () => {};
  }

  // TASK-1483 (COD round 3) — a tenant switch must never show the previous
  // tenant's claims. Dropping the cache object is enough: an in-flight load
  // holds the OLD object, so its rows land there and are never rendered.
  if (ClockState.tenantId !== tenantId) {
    ClockState.claims = null;
    // P0 1019 — the roster lists too: loadRosterEquipment keeps the old arrays
    // when its read fails, so a switch must start from empty, never from the
    // previous company's trucks / people.
    ClockState.people = [];
    ClockState.activeEntries = [];
    ClockState.recentEntries = [];
    ClockState.allJobs = [];
    ClockState.todaysJobDays = [];
    ClockState.entryJobDays = [];
    ClockState.customers = [];
    ClockState.trucks = [];
    ClockState.trailers = [];
    ClockState.assets = [];
  }
  ClockState.tenantId = tenantId;
  // TASK-804 — the ticket gateway reconciles a clock-entry edit server-side
  // (closed-ticket check, pay fork, reason code, audit row, EMPLOYEE line).
  // TASK-9989 item 1 (Chris 2026-10-25) — ON for EVERY company, not just TITS:
  // settings.useTicketGateway is no longer needed to get it. A per-session
  // gateway failure flips this back off (saveClockEntryEditViaGateway / jobSwitch,
  // logged via _clockGatewayFallback), so re-arm it on every init. Sessions the
  // rule leaves out get an explicit false so a flag left over from another
  // tenant in the same tab can't carry over.
  window.USE_TICKET_GATEWAY = _clockGatewayDefaultOn(tenantId, window.AppState);
  ClockState.activeTab = 'clock';
  ClockState.selectedPerson = null;
  ClockState.selectedCustomer = null;
  ClockState.historyEntries = [];
  ClockState.historyLoadedUntil = null;
  ClockState._entriesLoaded = false;
  ClockState._entriesFailed = false;
  // TASK-9220 — every mount (and every cleanup) bumps this, so a hand-off still
  // waiting from an EARLIER mount (double tap remounts Clock) never acts.
  ClockState._mountSeq = (ClockState._mountSeq || 0) + 1;
  // TASK-364 — reset historical-people cache on tenant init/switch so cached
  // person docs don't bleed across tenants in the same browser session
  // (COD Code Reviewer concern #1).
  ClockState._historicalPeopleCache = new Map();
  ClockState._historicalCacheFetchedFor = null;
  // TASK-4637 — same reason as the historical-people cache above, and it
  // matters more here: these are pay amounts. The listener used to run on ONE
  // tenant, so stale rows could never cross a tenant boundary; now it runs on
  // any tenant with the Workforce module, and a compliance manager switching
  // between carriers in one session must not see the previous carrier's
  // payroll rows in the gap before the new snapshot lands.
  ClockState.adjustments = [];
  ClockState.adjustmentsLoaded = false;
  ClockState.adjustmentsDenied = false;
  ClockState.adjustmentsTruncated = false;

  container.innerHTML = getClockHTML();
  setupEventHandlers();
  subscribeToData(tenantId);
  // TASK-9411 — coming back from a ticket opened on this screen? Put the user
  // back on the same employee / pay period / week / scroll.
  _applyClockReturnRestore();
  // TASK-9220 — arrived from the sidebar PERSONAL clock tile?
  _applyPersonalClockHandoff();

  return () => {
    ClockState._mountSeq = (ClockState._mountSeq || 0) + 1; // TASK-9220 — strands any waiting hand-off
    ClockState.unsubscribers.forEach(unsub => unsub());
    ClockState.unsubscribers = [];
    if (ClockState.clockUpdateInterval) {
      clearInterval(ClockState.clockUpdateInterval);
      ClockState.clockUpdateInterval = null;
    }
    ['clock-notes','clock-out-shift-notes','clock-out-ticket-notes','clock-out-notes'].forEach(id => window.SmartAssistant?.unregister(id));
  };
}

// ============================================
// CHECK IF TABS SHOULD BE SHOWN
// ============================================
function showTimeOffTab() {
  // Time Off moved to future HR module — no longer part of Clock
  return false;
}

function isFounder() {
  return window.AppState?.tenant === 'trippin-trucking' &&
    !window.AppState?.isPublicAccess &&
    !window.AppState?.driverMode &&
    !window.AppState?.driverModePin;
}

// Any logged-in admin/manager (not driver link, not public access, not driver PIN)
function isClockAdmin() {
  return !window.AppState?.isPublicAccess && !window.AppState?.driverMode && !window.AppState?.driverModePin;
}

// ============================================
// TASK-360 — Clock vs Workforce module split (2026-05-26)
// ============================================
// "Workforce is the place to manipulate clock data. Clock should be the
// person's way to clock in/out. If they're supposed to manipulate other
// clock data, they'd be granted Workforce." — Chris
//
// `hasWorkforceAccess()` is true when the current viewer is allowed to
// administer OTHER people's clock data — see the "Currently Clocked In"
// list, switch other employees' jobs, run weekly hours reports, etc.
// `clock` module alone is NOT sufficient.
//
// Returns true for:
//   - Founder (Chris on trippin-trucking)
//   - Demo mode
//   - Compliance-only tenants (preview mode — checkModuleAccess returns
//     true for everything; preserve that here too)
//   - Members with role === 'owner' (full access by definition)
//   - Legacy admins (role === 'admin' with no modules field) — pre-TASK-319
//   - Members whose memberAccess.modules[] includes 'workforce'
//   - Tenants with no PINs configured AND no members doc (new account /
//     legacy single-login owner — checkModuleAccess returns true)
// Returns false for:
//   - Driver PIN mode (drivers never get workforce; their kiosk surface
//     is jobboard, not clock)
//   - Public access / driver token mode
//   - Members whose modules[] does NOT include 'workforce'
//   - PIN-gated module-locked tenants where 'workforce' isn't unlocked
function hasWorkforceAccess() {
  const A = window.AppState || {};
  // Driver/public modes never get workforce. Defense-in-depth — these
  // contexts also can't read AppState.memberAccess.
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  // Multi-user member access (TASK-319) — members/{uid}.modules is ABSOLUTE
  // source of truth. Checked BEFORE founder/demo short-circuits so a staff
  // member signed into the founder tenant (e.g. Test Chris on trippin-trucking
  // with clock-only modules) is NOT promoted to full kiosk admin via the
  // tenant-only isFounder() check. (TASK-360 follow-up — 2026-05-26.)
  // Any active member doc is authoritative — we NEVER fall through to the
  // founder/demo path for an active member, even if their modules field is
  // malformed (otherwise a missing/non-array modules on a TITS staff doc
  // would re-open the same gate leak).
  // 2026-05-26 hotfix — legacy member docs (esp. founder/owner docs
  // created before TASK-319 added the `status` field) have
  // `status: undefined`. The original `=== 'active'` guard was bumping
  // those owners to the PIN fallback below, silently denying workforce.
  // New rule: any non-revoked/non-inactive/non-pending status counts.
  const memberAccess = A.memberAccess;
  const memberDocBlocked = memberAccess && ['revoked', 'inactive', 'pending'].includes(memberAccess.status);
  if (memberAccess && !memberDocBlocked) {
    if (memberAccess.role === 'owner') return true;
    // Legacy admin (no modules field) = full access
    if (memberAccess.role === 'admin' && (!Array.isArray(memberAccess.modules) || memberAccess.modules.length === 0)) {
      return true;
    }
    if (Array.isArray(memberAccess.modules)) {
      return memberAccess.modules.includes('workforce');
    }
    // Member doc with no usable grant rule → deny. Do NOT fall
    // through to isFounder()/PIN logic for present member docs.
    return false;
  }
  // No active member doc — legacy single-login owner / founder / demo paths.
  if (isFounder()) return true;
  if (A.isDemo || A.tenant === 'demo') return true;
  try {
    if (typeof window.isComplianceOnlyTenant === 'function' && window.isComplianceOnlyTenant()) {
      return true;
    }
  } catch (_) { /* ignore */ }
  // No member doc — fall through to PIN system / new-account default.
  // If no PINs configured, the tenant is owner-only → full access.
  const accessPins = (window.accessPinMap?.(A.settings) || A.settings?.accessPins || {});
  const hasPinsConfigured = Object.keys(accessPins).length > 0;
  if (!hasPinsConfigured) return true;
  // PIN-gated tenant: workforce requires the workforce PIN unlocked.
  try {
    if (typeof window.isModuleUnlocked === 'function') {
      return !!window.isModuleUnlocked('workforce');
    }
  } catch (_) { /* ignore */ }
  return false;
}

// ============================================
// TASK-4691 — the clock PIN override (P1 + P2)
// ============================================
// ⚠️ THIS IS A UI GATE, NOT A SECURITY BOUNDARY — do not read it as one, do not
// describe it as one in product copy. In `match /clockEntries/` (firestore.rules
// :4059), BOTH write arms — create :4127 and update :4171 — carry an
// unconditional `hasModule(tenantId, 'workforce')` clause that authorises ANY
// member holding that module to write ANY tenant clockEntry with no server-side
// PIN check; `isOwnClockEntry` constrains only the SELF arm.
// 🔴 CITE THE BLOCK, THEN THE LINE, AND RE-READ THE LINE BEFORE COPYING IT.
// This pointer has now been wrong three times running: the task brief said 3891,
// the shipping lane "corrected" it to 3867/3911, and the follow-up lane copied
// that forward. 3867 is inside `match /pretrips/`; 3911 is inside
// `match /dvirAccountability/`. Neither has anything to do with clock data.
// Line numbers drift — the arms are the `hasModule(tenantId, 'workforce')`
// clauses inside the clockEntries match block. An office user
// can still write a coworker's punch straight from the SDK today.
// What this buys is a DELIBERATE ACT plus ATTRIBUTION on the wage record —
// which is the actual goal here — and nothing more.
//
// WHAT WAS BROKEN: both call sites below used to read `!isFounder()`, and
// isFounder() tests `AppState.tenant === 'trippin-trucking'` — a TENANT test,
// not a person test. Every office session on TITS therefore skipped all 36
// people's clock PINs. The equivalent bypass in app.js isModuleUnlocked was
// already removed by TASK-748/719-A; clock.js was simply never updated. This is
// a leftover, not a design.
//
// Deliberately NOT reusing hasWorkforceAccess() directly: that helper still
// falls through to `if (isFounder()) return true` for a session with no member
// doc, which would carry the exact tenant bypass we are removing straight back
// in. This mirrors its member-doc arms line for line and then STOPS.
function _clockOverrideCapable() {
  const A = window.AppState || {};
  // Driver / public / PIN-kiosk driver modes never hold the override.
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  // TASK-319 member docs are the absolute source of truth, checked FIRST so a
  // clock-only staffer on the founder tenant is never promoted by tenant id.
  const ma = A.memberAccess;
  const maBlocked = ma && ['revoked', 'inactive', 'pending'].includes(ma.status);
  if (ma && !maBlocked) {
    if (ma.role === 'owner') return true;
    if (ma.role === 'admin' && (!Array.isArray(ma.modules) || ma.modules.length === 0)) return true;
    if (Array.isArray(ma.modules)) return ma.modules.includes('workforce');
    return false;
  }
  if (A.isDemo || A.tenant === 'demo') return true;
  try {
    if (typeof window.isComplianceOnlyTenant === 'function' && window.isComplianceOnlyTenant()) return true;
  } catch (_) { /* ignore */ }
  // Legacy single-login owner / PIN kiosk. No PINs configured = owner-only
  // tenant = full access (the platform-wide anti-lockout rule — do NOT change).
  const accessPins = (window.accessPinMap?.(A.settings) || A.settings?.accessPins || {});
  if (Object.keys(accessPins).length === 0) return true;
  try {
    if (typeof window.isModuleUnlocked === 'function') return !!window.isModuleUnlocked('workforce');
  } catch (_) { /* ignore */ }
  return false;
}

// P2 — does the DEVICE LOCK govern this viewer's override at all?
// 🔴 BINDING: only for viewers who actually HAVE a lock control. initLockFab()
// and initLockSidebarLogo() both SKIP demo/driver/public AND TASK-319
// members-with-modules — those users have no FAB and no pill, so there is no
// gesture that could ever unlock them. Gating their override on lock state
// would deny it permanently with no way to grant it: the TASK-428 stranding
// shape. For them the member grant IS the gate (they signed in with email +
// password, which is strictly stronger than a 6-char derivable clock PIN).
// Mirrors initLockFab's skip list exactly — keep the two in sync.
function _clockLockGovernsOverride() {
  const A = window.AppState || {};
  if (A.isDemo || A.tenant === 'demo' || A.driverMode || A.isPublicAccess || A.driverModePin) return false;
  // TASK-1107 — with zero access PINs the lock is unenforceable; asserting a
  // LOCKED state we cannot enforce is the hollow-badge bug. Treat as unlocked.
  const accessPins = (window.accessPinMap?.(A.settings) || A.settings?.accessPins || {});
  if (Object.keys(accessPins).length === 0) return false;
  const ma = A.memberAccess;
  if (ma && ma.status === 'active' && ma.role !== 'owner'
      && Array.isArray(ma.modules) && ma.modules.length > 0) return false;
  return true;
}

// Is the shared device currently LOCKED for a viewer the lock governs?
// Same semantics updateLockFab() paints with, so the padlock a user sees and
// the override they get can never disagree.
function _clockDeviceLocked() {
  if (!_clockLockGovernsOverride()) return false;
  const A = window.AppState || {};
  if (A.memberAccess?.role === 'owner') {
    try { return !!window.isOwnerDeviceLocked?.(); } catch (_) { return false; }
  }
  return !(A.tenant && localStorage.getItem(`dispatchdeck_pin_${A.tenant}`));
}

// The override itself: a workforce-capable office session on an UNLOCKED device.
function clockPinOverrideActive() {
  return _clockOverrideCapable() && !_clockDeviceLocked();
}

// Should the pin-entry block offer "unlock to override"? Only when the viewer
// is lock-governed AND currently locked — i.e. there is a real gesture that
// would grant the override. Note we intentionally do NOT require
// _clockOverrideCapable() here: for a legacy PIN kiosk that helper resolves
// through isModuleUnlocked('workforce'), which is false *because* the device is
// locked. Requiring it would hide the very button that unlocks it.
function clockCanOfferOverride() {
  return _clockLockGovernsOverride() && _clockDeviceLocked();
}

// Is the selected person the signed-in member themselves?
// A member clocking THEMSELVES already authenticated with email + password;
// re-demanding a clock PIN derivable from their own name + phone adds friction
// and buys nothing. Measured on trippin-trucking 2026-07-01 → 2026-09-01: of the
// 45 entries on this code path, 34 (76%) are exactly this case (Sean Wood 20,
// Steven Alandt 14 — both linked members clocking themselves). Drivers have no
// member doc, so getSelfPersonId() is null for them and their PIN flow is
// untouched. Same selfId notion already used at :9526 and :9776.
function _clockIsSelfSelection(personId) {
  const self = getSelfPersonId();
  return !!(self && personId && self === personId);
}

// The single question every punch path asks. Kept as ONE helper so the visible
// PIN box and the write-time check can never drift apart — a drift here shows a
// PIN field that clockIn() then ignores, or hides one it still enforces.
// TASK-9941 F S2 — no longer takes the person's code: the browser never knows
// it now (the server checks it, verifyClockPersonCode). A person with no
// name + phone code (no phone on file) used to need NO code at all — anyone
// could punch them. Now everyone other than the signed-in member needs their
// PIN, the office override, or an "unverified" punch the office reviews.
function clockPersonPinRequired(personId) {
  if (!personId) return false;
  if (_clockIsSelfSelection(personId)) return false;
  // A driver-shaped session clocking ITSELF (the router keeps PIN drivers off
  // this view today; harmless and right if that changes).
  const A = window.AppState || {};
  if ((A.driverModePin || A.driverMode) && A.driverPersonId && A.driverPersonId === personId) return false;
  if (clockPinOverrideActive()) return false;
  return true;
}

// TASK-1364 — the receipt-button onclick strings interpolate a ticket id into
// inline HTML. Firestore auto-ids are already [A-Za-z0-9], so this is belt not
// suspenders, but the id reaches the DOM through a clockEntry field and one
// malformed/legacy value would break out of the attribute. Same scrub
// billing.js's _reviewLoadErrorHtml() already applies for the same reason.
function _safeTicketId(id) {
  return String(id == null ? '' : id).replace(/[^A-Za-z0-9_-]/g, '');
}

// TASK-1364 — same shape as hasWorkforceAccess(), for the `billing` module.
// The Clock/Workforce receipt button routes the viewer into the Billing view
// (that's where the ticket modals live), so it must only render for people who
// are actually allowed in there — otherwise the click strands a clock-only
// member on a "no access" screen with their clock view already wiped.
// Deliberately mirrors hasWorkforceAccess() line for line rather than sharing a
// parameterized helper: keeping the two readable side by side is what makes a
// drift in either one obvious.
//
// ⚠️ THIS IS A UI GATE, NOT A SECURITY BOUNDARY — do not read it as one.
// firestore.rules ticket `update` allows content edits on any UNLOCKED ticket
// for `isWritingMember && !isViewUploadOnly`, with NO hasModule('billing')
// clause (the billing/dispatcher check exists only on the locked-ticket
// LIFECYCLE arm). A clock-only member can still edit open-ticket content
// straight from the SDK today, exactly as they could before this button worked.
// That gap is PRE-EXISTING and deliberately out of scope here (TASK-1364 board
// entry: "tightening the rules arm is a separate follow-up"), and it is NOT a
// one-liner: clock.js writes ticket + ticketLine content all over the clock-out
// path, so ANDing that arm with hasModule('billing') would break every driver
// building their own ticket on close-out. Needs its own task and its own COD.
function hasBillingAccess() {
  const A = window.AppState || {};
  // Driver/public modes never get billing.
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  // TASK-1364 (COD Code Reviewer) — DELIBERATE divergence from
  // hasWorkforceAccess(): while the member doc is still resolving, memberAccess
  // is undefined, so the checks below would fall all the way through to the
  // founder / no-PINs-configured default and flash this button at a clock-only
  // member. Hide it until we actually know. Safe to be conservative here in a
  // way it wouldn't be for the workforce gate: the clock view re-renders on
  // every snapshot, so the button appears as soon as the read lands.
  if (A.memberAccessPending) return false;
  // Multi-user member access (TASK-319) — members/{uid}.modules is ABSOLUTE and
  // is checked BEFORE the founder/demo short-circuits, so a clock-only staff
  // member signed into the founder tenant is not promoted by isFounder().
  const memberAccess = A.memberAccess;
  const memberDocBlocked = memberAccess && ['revoked', 'inactive', 'pending'].includes(memberAccess.status);
  if (memberAccess && !memberDocBlocked) {
    // NOT mirroring checkModuleAccess's isOwnerDeviceLocked() branch (COD Code
    // Reviewer raised it). An owner who device-locked this tablet still SEES
    // every sidebar nav item — the lock makes them PIN-challenge on click, it
    // doesn't hide anything. Showing the receipt button and letting
    // navigateToView('billing') run its normal PIN challenge is the consistent
    // behavior; the challenge still gates entry, and the deep link survives it.
    if (memberAccess.role === 'owner') return true;
    // Legacy admin (no modules field) = full access
    if (memberAccess.role === 'admin' && (!Array.isArray(memberAccess.modules) || memberAccess.modules.length === 0)) {
      return true;
    }
    if (Array.isArray(memberAccess.modules)) {
      return memberAccess.modules.includes('billing');
    }
    // Member doc with no usable grant rule → deny. Do NOT fall through.
    return false;
  }
  // No active member doc — legacy single-login owner / founder / demo paths.
  if (isFounder()) return true;
  if (A.isDemo || A.tenant === 'demo') return true;
  try {
    // Compliance-only tenants browse everything in preview mode (checkModuleAccess
    // returns true and navigateToView paints the upgrade overlay). Unreachable in
    // practice — setupComplianceOnlySidebar hides clock/workforce — but keeps
    // parity with the gate that actually runs on navigation.
    if (typeof window.isComplianceOnlyTenant === 'function' && window.isComplianceOnlyTenant()) {
      return true;
    }
  } catch (_) { /* ignore */ }
  // No member doc — the kiosk / legacy shared-login path, gated by access PINs.
  // TASK-1364 (COD Code Reviewer, round 3) — return TRUE here, do NOT require
  // isModuleUnlocked('billing') the way hasWorkforceAccess() does. On this
  // branch checkModuleAccess() returns either true or 'needs-pin' — never
  // false — so hiding the button would deny a legitimate user an affordance the
  // app would have PIN-challenged and then granted. navigateToView() runs that
  // challenge on click, and the deep link is built to survive it (initBilling
  // consumes hand-offs up to 30s old). Same reasoning covers settings not yet
  // loaded, where checkModuleAccess answers 'needs-pin' rather than denying.
  return true;
}

// TASK-1377(a) — Chris 2026-09-29: a clock-only worker may open the ticket on
// THEIR OWN entry (read-only, views/clockTicketView.js). Same own-entry test as
// the edit modal's _isOwnEntry (getSelfPersonId() === entry.personId; PIN
// drivers have no self id, so no button). _tkBtn is the ONE gate for the receipt
// button: billing users see it on every entry (deep link, unchanged), everyone
// else only on their own.
function _tkBtn(e) {
  const s = getSelfPersonId();
  return !!e?.ticketId && (hasBillingAccess() || (!!s && e.personId === s));
}

// Returns the personId of the currently signed-in user, if linked.
// Used by the self-clock surface (TASK-360) and Recent Activity filter.
// Returns null for owners not self-linked, founders, demo, etc.
function getSelfPersonId() {
  return window.AppState?.memberAccess?.personId || null;
}

// TASK-528 — Clock = the signed-in member's self-service hub.
// Returns the active (non-revoked/inactive/pending) member-access record for
// the signed-in user, or null. Mirrors the status guard in hasWorkforceAccess
// and jobboard.js submitExpense so the three stay consistent.
function getActiveMemberAccess() {
  const ma = window.AppState?.memberAccess;
  if (!ma) return null;
  if (['revoked', 'inactive', 'pending'].includes(ma.status)) return null;
  return ma;
}

// Does the signed-in member have a given module granted?
// Owner / legacy-admin (admin role, no modules array) → true for everything
// (they have full access by definition — matches createMemberExpense's
// isOwnerOrLegacyAdmin branch and hasWorkforceAccess's owner short-circuit).
// Returns false for PIN-driver / public modes (no member doc).
function memberHasModule(moduleName) {
  const ma = getActiveMemberAccess();
  if (!ma) return false;
  if (ma.role === 'owner') return true;
  if (ma.role === 'admin' && (!Array.isArray(ma.modules) || ma.modules.length === 0)) return true;
  return Array.isArray(ma.modules) && ma.modules.includes(moduleName);
}

// TASK-528 — Can the signed-in member submit an expense from the Clock hub?
// Mirrors the DEPLOYED createMemberExpense server gate EXACTLY (functions
// index.js ~L38704): owner/legacy-admin OR `clock` OR `billing` module. Gating
// on clock-only here is deliberate — a clock-only field employee is exactly who
// the self-service hub is for; the submission lands status:'pending' and they
// cannot self-approve, so it's low-authority (mirrors the driver-PIN flow).
// Driver-PIN / public sessions return false (no member doc) — they use the
// existing settings.driverExpenses.enabled gate instead.
function memberCanRecordExpense() {
  const ma = getActiveMemberAccess();
  if (!ma) return false;
  return memberHasModule('clock') || memberHasModule('billing');
}

// TASK-4630 — owner-ONLY gate, stricter than hasWorkforceAccess(). Mirrors the
// `isTenantOwner` arm on firestore.rules payAdjustments update/delete (:5061),
// which is owner-only for payroll integrity — these are garnishment /
// child-support deductions, crown-jewel scope, not ordinary CRUD.
//
// Straight port of hr.js `_hrIsOwner()` (TASK-1591, shipped ae3d48c2), which
// fixed the IDENTICAL defect on the HR recurring-deduction Remove button: the
// control rendered for every workforce/HR viewer, but the rules reject anyone
// but the owner, so a non-owner admin got a button that ALWAYS failed with a
// generic error that never named the boundary. Same defect on the Workforce →
// Adjustments Delete button; same fix — hide the control, and let the
// still-console-reachable handler say WHY.
//
// 🔴 The rule is NOT the thing to widen. Sweep on TASK-1591: across all 86
// tenants exactly ONE has any payAdjustments rows, and 100% were written by the
// owner. There is no non-owner workflow being protected here.
//
// Two deliberate divergences from _hrIsOwner(), because clock.js has contexts
// hr.js does not:
//   - Driver-PIN / public sessions have NO member doc, so the `!ma → true`
//     legacy-owner branch below would read them as "owner". Denied explicitly
//     first. (hasWorkforceAccess already hides the whole Workforce tab from
//     them; this is defense-in-depth for the exported window.ClockModule
//     handler, which is reachable from a console on any session.)
//   - The `!ma → true` branch stays OPTIMISTIC for the legacy single-login
//     owner (tenant with no members docs at all — same fallthrough shape
//     hasWorkforceAccess uses). Being pessimistic there would strand the one
//     person who is supposed to hold this capability; if they somehow are not
//     the owner, the rules still refuse and the handler now names the boundary
//     instead of surfacing a generic failure.
function _clockIsOwner() {
  const A = window.AppState || {};
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  // TASK-9491 — the rules now prove the owner by tenants/{tid}.ownerId (not
  // members.role == 'owner'), so mirror that first.
  const uid = A.user?.uid || null;
  const ownerId = A.tenantData?.ownerId || null;
  if (uid && ownerId) return uid === ownerId;
  const ma = A.memberAccess;
  if (!ma) return true; // owner / legacy single-login (no member doc)
  if (['revoked', 'inactive', 'pending'].includes(ma.status)) return false;
  return ma.role === 'owner';
}

// TASK-9603 — client mirror of firestore.rules timeOff READ: may this viewer
// list EVERY coworker's time off (reasons included)? Demo, the owner, or a
// member holding one of the rule's timeOffFullReader modules. members.role is
// NOT consulted (a legacy no-modules admin who is not the owner is refused by
// the rule, so must not get the unscoped query). Everyone else reads only
// their own requests.
const TIMEOFF_FULL_READ_MODULES = ['hr', 'workforce', 'dispatch', 'billing'];
function _clockCanReadAllTimeOff() {
  const A = window.AppState || {};
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  if (A.isDemo || A.tenant === 'demo' || A.tenant === 'demo-tenant') return true;
  const ma = getActiveMemberAccess();
  const mods = ma && Array.isArray(ma.modules) ? ma.modules : [];
  if (TIMEOFF_FULL_READ_MODULES.some(m => mods.includes(m))) return true;
  return _clockIsOwner();
}

// ============================================
// TASK-4637 — pay-adjustments listener: gate + bound
// ============================================
// THE DEFECT: the payAdjustments listener was `if (isFounder())`, a
// `trippin-trucking` TENANT check, so `ClockState.adjustments` was only ever
// populated on TITS. On the other 85 tenants Workforce → Adjustments rendered a
// permanently blank panel with no explanation.
//
// WHY IT WAS THERE — checked, not guessed: the `if` dates to 80c3359a
// (2026-02-17, "Add Clock In/Out + Billing module"), the commit that FIRST
// added the clock module, back when DispatchDeck was a one-tenant TITS app and
// `isFounder()` was the ambient "is this the real account" check. It is
// leftover scaffolding, not a deliberate load-shed — no commit ever narrowed it
// for cost, and nothing else in subscribeToData is founder-gated.
//
// It was never only cosmetic. A tenant gate on a DATA LOAD silently changes who
// a bug can reach: TASK-4630 was filed claiming 7 affected non-owner admins,
// and the true number was 2, because the other 5 were on a tenant where this
// listener never ran. It also blocked browser verification of the surface on a
// throwaway `_e2e_*` tenant in two separate lanes (TASK-4630, TASK-4628).
//
// THE BOUND COMES FIRST. Widening a listener from 1 tenant to 86 without a
// ceiling is how you trade a correctness bug for a cost bug. The query is now
// `orderBy(createdAt desc) + limit(500)`.
//   - Deliberately a `limit()`, NOT a rolling date window: a RECURRING
//     adjustment is a garnishment / child-support deduction that keeps paying
//     long after its createdAt, and a date window would silently hide a live
//     deduction from the one screen that lists them. A limit degrades to "the
//     newest 500", which the panel states out loud when it is hit.
//   - No composite index needed — orderBy is on the same single field the
//     query already ordered by, so this is index-free.
//   - Payroll math is NOT affected by the cap: the export/report paths
//     (exportPayrollCSV, loadAdjustmentFlags, showEmployeeDetail) each run
//     their own getDocs and never read ClockState.adjustments. The cap bounds
//     one browsing surface, not anyone's pay.
const ADJUSTMENTS_LISTENER_LIMIT = 500;

// Who may load pay adjustments. Mirrors the firestore.rules payAdjustments READ
// arm (`isTenantAdmin || hasModule('workforce') || hasModule('billing')`,
// firestore.rules ~:5067) intersected with who can actually REACH the surface:
// switchWorkforceTab() bounces anyone without hasWorkforceAccess() off the
// Adjustments sub-tab, so subscribing a billing-only member would spend a
// 500-doc read on data no screen of theirs can render. hasWorkforceAccess() is
// a subset of the rules' read arm in every real case (owner and legacy-admin →
// isTenantAdmin; workforce module → hasModule('workforce'); demo → isDemo
// inside isTenantAdmin), so this never asks for a read the rules refuse.
//
// Driver-PIN / public sessions are denied explicitly and FIRST: they have no
// member doc, the rules deny them outright, and they must never hold a listener
// on coworkers' pay (`[[lessons_people_collection_pii_leak]]` — the same shape
// of leak). hasWorkforceAccess() already refuses them; this is defense-in-depth
// for the same reason _clockIsOwner() repeats it.
function _clockCanReadPayAdjustments() {
  const A = window.AppState || {};
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  return hasWorkforceAccess();
}

// ============================================
// TASK-4642 — "Load previous week" history walk: gate + bound
// ============================================
// THE DEFECT: loadMoreHistory() opened with `if (!isFounder()) return;` — a
// hard early-return on a DATA LOAD keyed to the `trippin-trucking` TENANT. So
// Recent Activity → "Load previous week" worked on TITS and nowhere else.
// Same leftover-scaffolding shape as TASK-4637's payAdjustments listener.
//
// The perverse part was the fallback: renderHistoryMore()'s self-service
// "My Hours" arm required `!hasWorkforceAccess()`, so a non-TITS WORKFORCE
// ADMIN — the one person on that tenant who is supposed to see everyone's
// hours — got neither control. The more privileged the viewer, the less
// history they could reach.
//
// It was also actively BROKEN on TITS itself, not merely absent elsewhere.
// isFounder() is tenant-scoped, so a TITS staff member with `clock` but not
// `workforce` also got the button — and TASK-452 scoped the clockEntries READ
// rule so that viewer may read ONLY `personId == self`. The un-scoped
// tenant-wide range query below is permission-denied for them: the button
// existed and always failed with a generic "Error loading history" toast.
// Routing them to "My Hours" (which is hard-pinned to their own personId)
// removes a control that could never have worked.
//
// THE BOUND. TASK-4637 chose a `limit()` over a rolling date window because a
// recurring garnishment keeps paying long after its createdAt, so a window
// would hide live data. The reasoning inverts here, and deliberately:
//   - A clock entry is a SETTLED point-in-time record. Its `clockIn` fully
//     determines which week it belongs to and it never becomes relevant to a
//     later week. So a date window is the CORRECT primary bound for timesheet
//     history — and it is already the feature's pagination unit ("previous
//     WEEK"). Replacing the window with a pure limit would break the feature.
//   - What was genuinely unbounded is CARDINALITY INSIDE the window: the query
//     is tenant-wide across all people with no ceiling. `limit()` supplies it.
//   - Clicks are NOT the unbounded axis, which is why there is no walk-back
//     floor. This is a user-initiated getDocs behind a button that disables
//     itself in flight — paginated, one bounded read per click. TASK-4637 was
//     an onSnapshot LISTENER that runs on every view load with no user action,
//     which is why an unbounded one there was a standing cost.
//   - No composite index needed: the range and the orderBy are both on
//     `clockIn`, and `limit` does not change the index requirement.
//
// SIZING (measured 2026-08-26 against live Firestore, all 86 tenants): only 8
// tenants hold any clockEntries at all. The largest is TITS at 1,924 docs over
// 26 weeks, whose worst single 7-day window is 147 entries; the largest
// non-TITS is show-account at 135 total / 57 worst-week. 500 is ~3.4x the
// worst week that has ever existed, so it never truncates a real week today —
// it exists to cap a pathological one (a bulk import stamping many entries
// into one window). Because the query is `orderBy(clockIn desc)`, a truncated
// week would return the NEWEST 500 and silently drop the OLDEST — a missing
// shift on a wage-hour surface must never be silent, so the handler says so
// out loud when it hits the cap and points at Workforce → Hours.
const CLOCK_HISTORY_WEEK_LIMIT = 500;

// Who may walk the tenant-wide history back. Mirrors the firestore.rules
// clockEntries READ arm (~:3389 — isTenantAdmin || isDispatcherOrHigher ||
// hasModule workforce/billing/bi/hr/projects/compliance/dispatch ||
// isComplianceManager || isOwnClockEntry) intersected with who can actually
// USE this surface.
//
// hasWorkforceAccess() is the right intersection, and it is provably inside the
// rule: it is the EXACT predicate (`_isWorkforce`, ~:8273) that already decides
// whether subscribeToData fires the un-scoped tenant-wide `clockEntries`
// range query for the live 7-day feed. That query succeeds today for this
// viewer set on every tenant. This change asks for the same shape of read over
// an older window — so no principal class gains a read that is not already
// succeeding, and there is no new permission-denied surface.
//
// It is a strict SUBSET of the rules' read arm in every case: owner and legacy
// admin → isTenantAdmin; `workforce` module → hasModule('workforce'); demo →
// isDemo inside isTenantAdmin; compliance-only → isComplianceManager. The
// broader arms (billing/bi/hr/projects/dispatch-only holders) are deliberately
// NOT admitted: renderRecentList() force-filters them to their own personId
// anyway, so a tenant-wide fetch would spend reads on rows no screen of theirs
// renders — and they keep the self-scoped "My Hours" instead.
//
// Driver-PIN / public sessions are denied explicitly and FIRST. hasWorkforce-
// Access() already refuses them, but loadMoreHistory is exported on
// window.ClockModule and is therefore console-reachable from any session; the
// rules would refuse the read, and a PIN driver must never hold coworkers'
// hours in memory (`[[lessons_people_collection_pii_leak]]` — same shape).
function _clockCanLoadMoreHistory() {
  const A = window.AppState || {};
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  return hasWorkforceAccess();
}

// TASK-4637 (COD Code Reviewer finding) — the SAME defect TASK-4630 fixed on
// the Delete control, one row up on the Add control, and widening the read gate
// is what made it reachable on 85 more tenants.
//
// firestore.rules payAdjustments CREATE (~:5068) is
// `(isTenantAdmin || hasModule('billing')) && !isComplianceOnly` — note there
// is NO workforce arm. But "Add Adjustment" rendered for every viewer with
// hasWorkforceAccess(), so a workforce-module member who is not an admin and
// has no billing module could open the form, fill it in, hit Save, and get a
// generic failure that never names the boundary — the exact "reads as a broken
// product" failure mode of TASK-1591 / TASK-4630. Hide the button, and keep the
// handlers refusing out loud since both are exported on window.ClockModule.
//
// The `!ma → true` branch stays optimistic for the legacy single-login owner
// (tenant with no members docs), same reasoning as _clockIsOwner(): being
// pessimistic there would strand the one person who is supposed to hold this.
function _clockCanCreatePayAdjustment() {
  const A = window.AppState || {};
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  // `&& !isComplianceOnly(tenantId)` — the ops modules are behind the paywall
  // for a compliance-only carrier; the rule refuses the write regardless.
  try {
    if (typeof window.isComplianceOnlyTenant === 'function' && window.isComplianceOnlyTenant()) return false;
  } catch (_) { /* ignore */ }
  // TASK-9491 — firestore.rules payAdjustments create is now canEditMoney:
  // owner via ownerId, or `billing` and not view-only. The admin ROLE no longer
  // passes on its own. window.canEditMoney (app.js) is the one client mirror.
  if (typeof window.canEditMoney === 'function') return window.canEditMoney() === true;
  const ma = getActiveMemberAccess();
  if (!ma) return true; // owner / legacy single-login (no member doc)
  if (ma.viewUploadOnly === true) return false;
  return memberHasModule('billing');
}

// TASK-360 — defense-in-depth runtime gate for workforce admin actions.
// The UI hides these surfaces from clock-only viewers, but window.ClockModule
// exposes the functions globally. A clock-only user typing
// `window.ClockModule.exportPayrollCSV()` in DevTools would otherwise bypass
// the UI gate. Each workforce admin function calls this guard first; on
// deny it surfaces a toast and bails. Returns true when allowed.
//
// (Firestore rules still block any actual write — but failing at the UI
// layer with a clear toast is much better UX than a generic rules denial.)
function _assertWorkforceAccess(actionLabel) {
  if (hasWorkforceAccess()) return true;
  console.warn(`[Clock] workforce action blocked — viewer lacks workforce module:`, actionLabel || '(unspecified)');
  try {
    window.showToast?.('This action is in the Workforce module. Ask an admin to grant you Workforce access.', 'error');
  } catch (_) { /* ignore */ }
  return false;
}

// Max instances for a repeatable custom section (TASK-747-C — Chris-locked).
const CS_MAX_INSTANCES = 25;

// TASK-9337 -- settings.customTicketSections is now writable by billing-module
// STAFF, not just admins. Section and field ids are interpolated RAW into HTML
// attributes and onclick JS strings in this file, so a crafted id would run
// script in whoever opens the form (an admin, a driver). The Template Builder
// only ever mints 'cts_' / 'f_' + base36 ids, so drop any section or field whose
// id is not a plain token instead of escaping at every sink.
function _csDefs() {
  const raw = window.AppState?.settings?.customTicketSections;
  if (!Array.isArray(raw)) return [];
  const ok = (v) => /^[A-Za-z0-9_-]{1,64}$/.test(String(v ?? ''));
  return raw
    .filter(cs => cs && ok(cs.id))
    .map(cs => (Array.isArray(cs.fields) ? { ...cs, fields: cs.fields.filter(f => f && ok(f.id)) } : cs));
}
const _csEscH = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Render one field group for a custom section. `idx` is the instance index;
// repeatable sections use indexed ids (...-custom-csId-IDX-fieldId), non-repeatable
// keep the legacy flat ids (...-custom-csId-fieldId) for back-compat.
function _csFieldGroupHtml(prefix, cs, idx, repeatable) {
  const escH = _csEscH;
  const idFor = (f) => repeatable ? `${prefix}-custom-${cs.id}-${idx}-${f.id}` : `${prefix}-custom-${cs.id}-${f.id}`;
  return cs.fields.map(f => {
    const fieldId = idFor(f);
    if (f.type === 'checkbox') {
      return `<label style="display:flex;align-items:center;gap:8px;min-width:120px;flex:1;cursor:pointer;">
        <input type="checkbox" id="${fieldId}"> <span style="font-size:0.85rem;">${escH(f.label)}</span>
      </label>`;
    }
    if (f.type === 'waypoint') {
      return `<div class="form-group" style="flex:1;min-width:200px;margin:0;">
        <label style="font-size:0.8rem;display:flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:14px;">place</span>${escH(f.label)}</label>
        <input type="text" id="${fieldId}" value="" placeholder="Address, map link, or lat/long..." style="width:100%;padding:6px;font-size:0.9rem;" data-cs-waypoint="true">
      </div>`;
    }
    if (f.type === 'gps') {
      return `<div class="form-group" style="flex:1;min-width:200px;margin:0;">
        <label style="font-size:0.8rem;display:flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:14px;">my_location</span>${escH(f.label)}</label>
        <div style="display:flex;gap:6px;">
          <button type="button" class="btn btn-secondary btn-sm" onclick="window.ClockModule._csGrabGPS('${fieldId}')" style="white-space:nowrap;">
            <span class="material-icons" style="font-size:16px;">my_location</span> Grab
          </button>
          <input type="text" id="${fieldId}" value="" placeholder="46.8772, -103.9849" style="flex:1;padding:6px;font-size:0.9rem;" readonly>
        </div>
      </div>`;
    }
    // TASK-4607 — a file field's value is a Storage PATH. Falling through to the
    // generic text input below would put that path in an editable box on the
    // clock-out form, where the only possible edit is one that breaks the
    // pointer and orphans the driver's upload. Attaching happens on Build Ticket
    // (which has the ticket id an upload has to bind to); here we carry the
    // value in a hidden input so clock-out saves it back untouched, and just say
    // whether something is on it.
    if (f.type === 'file') {
      return `<div class="form-group" style="flex:1;min-width:160px;margin:0;">
        <label style="font-size:0.8rem;display:flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:14px;">attach_file</span>${escH(f.label)}</label>
        <input type="hidden" id="${fieldId}" value="">
        <div class="cs-file-state" data-cs-file-state-for="${fieldId}" style="font-size:0.8rem;color:var(--text-secondary);padding:6px 0;">Attach on Build Ticket</div>
      </div>`;
    }
    return `<div class="form-group" style="flex:1;min-width:120px;margin:0;">
      <label style="font-size:0.8rem;">${escH(f.label)}${f.lineItemBinding ? ' <span style="font-size:0.65rem;color:var(--brand-blue);">⟶ ' + escH(f.lineItemBinding.itemCode) + '</span>' : ''}</label>
      <input type="${f.type === 'number' ? 'number' : 'text'}" id="${fieldId}" ${f.type === 'number' ? 'min="0" value="0"' : 'value=""'} style="width:100%;padding:6px;font-size:1rem;">
    </div>`;
  }).join('');
}

// One repeatable instance wrapper (with a per-instance remove ×).
function _csInstanceHtml(prefix, cs, idx) {
  return `<div class="cs-instance" data-cs-instance="${idx}" style="position:relative;border:1px solid var(--border-light);border-radius:var(--radius-sm);padding:10px 12px;margin-bottom:8px;">
    <button type="button" class="cs-instance-remove" title="Remove this entry" onclick="window.ClockModule._csRemoveInstance('${prefix}','${cs.id}',${idx})" style="position:absolute;top:6px;right:6px;background:none;border:none;color:var(--text-secondary);cursor:pointer;line-height:1;padding:2px;">
      <span class="material-icons" style="font-size:18px;">close</span>
    </button>
    <div style="display:flex;gap:var(--spacing-md);flex-wrap:wrap;">${_csFieldGroupHtml(prefix, cs, idx, true)}</div>
  </div>`;
}

// Build HTML for custom ticket sections defined in Template Builder
function buildCustomSectionsHtml(prefix) {
  const customSections = _csDefs();
  if (customSections.length === 0) return '';
  let html = '';
  for (const cs of customSections) {
    const csKey = 'custom_' + cs.id;
    if (!isCOFSectionEnabled(csKey)) continue;
    const escH = _csEscH;
    if (cs.repeatable) {
      html += `
        <div class="clock-out-section" style="margin-bottom:var(--spacing-lg);" data-custom-section="${cs.id}" data-cs-repeatable="1">
          <h4 style="margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
            <span class="material-icons" style="font-size:18px;">${escH(cs.icon || 'tune')}</span> ${escH(cs.name)}
          </h4>
          <div class="cs-instances" data-cs-instances="${cs.id}">
            ${_csInstanceHtml(prefix, cs, 0)}
          </div>
          <button type="button" class="btn btn-secondary btn-sm cs-add-another" data-cs-add="${cs.id}" onclick="window.ClockModule._csAddInstance('${prefix}','${cs.id}')" style="margin-top:4px;">
            <span class="material-icons" style="font-size:16px;">add</span> Add another
          </button>
        </div>
      `;
    } else {
      html += `
        <div class="clock-out-section" style="margin-bottom:var(--spacing-lg);" data-custom-section="${cs.id}">
          <h4 style="margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
            <span class="material-icons" style="font-size:18px;">${escH(cs.icon || 'tune')}</span> ${escH(cs.name)}
          </h4>
          <div style="display:flex;gap:var(--spacing-md);flex-wrap:wrap;">
            ${_csFieldGroupHtml(prefix, cs, 0, false)}
          </div>
        </div>
      `;
    }
  }
  return html;
}

// Add another instance to a repeatable section (debounced; capped at CS_MAX_INSTANCES).
let _csAddBusy = false;
function _csAddInstance(prefix, csId) {
  if (_csAddBusy) return;
  _csAddBusy = true;
  setTimeout(() => { _csAddBusy = false; }, 250);
  const cs = _csDefs().find(s => s.id === csId);
  if (!cs) return;
  const wrap = document.querySelector(`[data-cs-instances="${csId}"]`);
  if (!wrap) return;
  const existing = wrap.querySelectorAll('.cs-instance').length;
  if (existing >= CS_MAX_INSTANCES) {
    window.showToast?.(`Up to ${CS_MAX_INSTANCES} entries per section.`, 'error');
    return;
  }
  // Next index = max current index + 1 (indices are stable even after removals).
  let maxIdx = -1;
  wrap.querySelectorAll('.cs-instance').forEach(el => { maxIdx = Math.max(maxIdx, parseInt(el.dataset.csInstance, 10) || 0); });
  const idx = maxIdx + 1;
  wrap.insertAdjacentHTML('beforeend', _csInstanceHtml(prefix, cs, idx));
  _csInitWaypoints(prefix);
  if (existing + 1 >= CS_MAX_INSTANCES) {
    const addBtn = document.querySelector(`[data-cs-add="${csId}"]`);
    if (addBtn) addBtn.style.display = 'none';
  }
}

// Remove a repeatable instance (the × IS the undo for an accidental "+").
function _csRemoveInstance(prefix, csId, idx) {
  const wrap = document.querySelector(`[data-cs-instances="${csId}"]`);
  if (!wrap) return;
  const instances = wrap.querySelectorAll('.cs-instance');
  if (instances.length <= 1) {
    // Never drop the last group — just clear its inputs.
    const only = wrap.querySelector('.cs-instance');
    if (only) only.querySelectorAll('input').forEach(el => { if (el.type === 'checkbox') el.checked = false; else el.value = (el.type === 'number') ? '0' : ''; });
    return;
  }
  const target = wrap.querySelector(`.cs-instance[data-cs-instance="${idx}"]`);
  if (target) target.remove();
  const addBtn = document.querySelector(`[data-cs-add="${csId}"]`);
  if (addBtn && wrap.querySelectorAll('.cs-instance').length < CS_MAX_INSTANCES) addBtn.style.display = '';
}

// Initialize waypoint autocomplete on custom section waypoint fields
function _csInitWaypoints(prefix) {
  setTimeout(() => {
    document.querySelectorAll(`[id^="${prefix}-custom-"][data-cs-waypoint="true"]`).forEach(el => {
      if (window.attachAddressAutocomplete) window.attachAddressAutocomplete(el);
    });
  }, 100);
}

// GPS grab for custom section GPS fields
function _csGrabGPS(fieldId) {
  const el = document.getElementById(fieldId);
  if (!el) return;
  if (!navigator.geolocation) { window.showToast?.('GPS not available', 'error'); return; }
  el.value = 'Getting location...';
  navigator.geolocation.getCurrentPosition(
    (pos) => { el.value = `${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`; },
    (err) => { el.value = ''; window.showToast?.('Could not get location: ' + err.message, 'error'); },
    { enableHighAccuracy: true, timeout: 10000 }
  );
}

// Read one field-group instance from the DOM. `idForField(f)` resolves the element id.
// Returns { data, hasData }.
function _csReadInstance(cs, idForField) {
  const sectionData = {};
  let hasData = false;
  for (const f of cs.fields) {
    const el = document.getElementById(idForField(f));
    if (!el) continue;
    if (f.type === 'checkbox') {
      sectionData[f.id] = el.checked;
      if (el.checked) hasData = true;
    } else if (f.type === 'number') {
      sectionData[f.id] = parseFloat(el.value) || 0;
      if (sectionData[f.id] !== 0) hasData = true;
    } else {
      sectionData[f.id] = el.value?.trim() || '';
      if (sectionData[f.id]) hasData = true;
    }
  }
  return { data: sectionData, hasData };
}

// Collect all custom-section data from the DOM into the persisted shape.
// Non-repeatable: customSectionData[csId] = { fieldId: value } (legacy shape, unchanged).
// Repeatable:     customSectionData[csId] = { _repeatable:true, instances:[{ fieldId:value }, ...] }.
// `prefix` = 'clock' (clock-out form). Only sections with real data are included.
function collectCustomSectionData(prefix) {
  const customSections = _csDefs();
  const customSectionData = {};
  for (const cs of customSections) {
    if (!isCOFSectionEnabled('custom_' + cs.id)) continue;
    if (cs.repeatable) {
      const wrap = document.querySelector(`[data-cs-instances="${cs.id}"]`);
      if (!wrap) continue;
      const instances = [];
      wrap.querySelectorAll('.cs-instance').forEach(el => {
        const idx = parseInt(el.dataset.csInstance, 10) || 0;
        const { data, hasData } = _csReadInstance(cs, (f) => `${prefix}-custom-${cs.id}-${idx}-${f.id}`);
        if (hasData) instances.push(data);
      });
      if (instances.length > 0) customSectionData[cs.id] = { _repeatable: true, instances };
    } else {
      const { data, hasData } = _csReadInstance(cs, (f) => `${prefix}-custom-${cs.id}-${f.id}`);
      if (hasData) customSectionData[cs.id] = data;
    }
  }
  return customSectionData;
}

// ============================================
// TASK-4596 — merge-save custom ticket sections (never silently clobber a
// crew-mate's saved row).
// ============================================
// customSectionData is written WHOLE onto a ticket that's SHARED with the
// rest of the crew — jobboard.js hit and fixed this exact clobber in TASK-1204
// (_jbMergeSaveCustomSectionData). That fix is NOT reused via
// window.JobBoardModule.* here: neither it nor its helpers (_jbCsdInstEq,
// _jbCsdInstancesOf) are exported on window.JobBoardModule, and jobboard.js is
// out of bounds for this lane (see TASK-4596 report) — so the invariant is
// reimplemented below rather than copied wholesale.
//
// One thing makes clock.js's case SIMPLER than jobboard's, not harder:
// clock.js's clock-out form never restores existing server-side
// customSectionData into its inputs (buildCustomSectionsHtml always renders
// blank fields — grep confirms no restore-into-DOM path exists in this file).
// So a clock.js driver never SEES what's already saved, which means every
// server-side row is always "not shown in this form" — jobboard's
// restore-degraded branch, unconditionally. There is no restore-succeeded
// case to special-case here.
function _clockCsdInstancesOf(val) {
  if (!val || typeof val !== 'object') return [];
  if (val._repeatable && Array.isArray(val.instances)) return val.instances;
  return [val];
}
// Loose field compare (string-coerced) so number/string drift between a
// freshly-typed value and a previously-saved one doesn't defeat de-dup.
function _clockCsdInstEq(a, b, fieldIds) {
  for (const fId of fieldIds) {
    const va = a?.[fId], vb = b?.[fId];
    if (va === vb) continue;
    if (String(va ?? '') === String(vb ?? '')) continue;
    return false;
  }
  return true;
}

// Merge `collected` (this form/session's data) against a FRESH server read —
// never against a stale open-time copy — so a crew-mate's saved row is never
// dropped. Rule per repeatable section: final = preservedServerInstances +
// collected (preserved = every server instance not already among the
// collected ones — see the comment above on why "not shown" is unconditional
// here). Non-repeatable sections merge field-by-field, driver's non-empty
// values winning. Never writes an all-empty map (matches legacy no-op
// behavior), and skips the write entirely if nothing actually changed.
//
// Throws on a failed read/write — callers decide what the driver sees (every
// call site here is already inside a best-effort ticket-phase try/catch that
// never blocks the clock-out itself; see call-site comments for exactly
// where a failure surfaces).
async function _clockMergeSaveCustomSectionData(tenant, ticketId, collected) {
  if (!collected || Object.keys(collected).length === 0) return;
  const sections = _csDefs();
  if (sections.length === 0) return;

  const snap = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId));
  const server = snap.exists() ? (snap.data().customSectionData || null) : null;

  const final = {};
  const keys = new Set([...Object.keys(server || {}), ...Object.keys(collected)]);
  for (const csId of keys) {
    const cs = sections.find(s => s.id === csId);
    if (!cs) {
      // Section deleted from settings since this data was collected — keep
      // whatever the server already has verbatim (nothing to merge against).
      if (server?.[csId]) final[csId] = server[csId];
      continue;
    }
    const fieldIds = (cs.fields || []).map(f => f.id);
    if (cs.repeatable) {
      const colArr = (collected[csId]?.instances) || [];
      const srvArr = _clockCsdInstancesOf(server?.[csId]);
      const preserved = srvArr.filter(s => !colArr.some(c => _clockCsdInstEq(s, c, fieldIds)));
      const finalArr = [...preserved, ...colArr];
      if (finalArr.length > 0) final[csId] = { _repeatable: true, instances: finalArr };
    } else {
      const colVal = collected[csId] || null;
      const srvVal = server?.[csId];
      if (!srvVal) {
        if (colVal) final[csId] = colVal;
      } else {
        const merged = (typeof srvVal === 'object') ? { ...srvVal } : {};
        for (const f of (cs.fields || [])) {
          const v = colVal ? colVal[f.id] : undefined;
          const nonEmpty = f.type === 'checkbox' ? v === true
            : f.type === 'number' ? (typeof v === 'number' && v !== 0)
            : !!(v && String(v).trim());
          if (nonEmpty) merged[f.id] = v;
        }
        if (Object.keys(merged).length > 0) final[csId] = merged;
      }
    }
  }

  if (Object.keys(final).length === 0) return; // legacy: never write an all-empty map
  if (JSON.stringify(final) === JSON.stringify(server || {})) return; // no-op

  await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
    customSectionData: final, updatedAt: Timestamp.now(),
    // TASK-4699 — custom ticket sections ARE the ticket body on a tenant that
    // uses them (permit numbers, load counts, well data). Guarded by the
    // no-op early-return above, so a save that changed nothing stamps nothing.
    ticketEmailDirtyAt: Timestamp.now()
  });
}

function isPerAssetNotesEnabled() {
  return window.AppState?.settings?.perAssetNotes === true;
}

function assetTypeGetsNotes(assetType) {
  if (!isPerAssetNotesEnabled()) return false;
  const types = window.AppState?.settings?.perAssetNotesTypes || {};
  if (assetType === 'truck') return types.truck !== false;
  if (assetType === 'trailer') return types.trailer !== false;
  if (assetType === 'asset') return types.asset !== false;
  return false;
}

// Whether this tenant should build billing tickets from clock entries
// TASK-9553 — every caller here works on the person picked on this screen
// (ClockState.selectedPerson), so that person's own clock mode decides. A
// person with no override gets the company answer exactly as before.
function shouldBuildTickets() {
  if (window.AppState?.isPublicAccess || window.AppState?.driverMode) return false;
  return window.shouldBuildTickets?.(ClockState.selectedPerson || null) || false;
}

// TASK-9553 — the Clock Out button and the close-out sheet's title + footer are
// painted once with the page (for the company's mode); repaint them for the
// person now selected. The sheet is only rebuilt when it was painted for the
// OTHER answer, so a company with no per-person overrides never sees it move.
function _clockSyncModeLabels(opts) {
  const builds = shouldBuildTickets();
  const btn = document.getElementById('btn-clock-out');
  if (btn) btn.innerHTML = `<span class="material-icons">${builds ? 'receipt_long' : 'logout'}</span> ${builds ? 'Build Ticket' : 'Close Out'}`;
  if (!opts || !opts.sheet) return;
  const footer = document.querySelector('#modal-clock-out .modal-footer');
  if (!footer || footer.dataset.builds === String(builds)) return;
  const h3 = document.querySelector('#modal-clock-out .modal-header h3');
  if (h3) h3.textContent = builds ? 'Build Ticket' : 'Close Out';
  footer.innerHTML = `
          <button class="btn btn-secondary" onclick="window.ClockModule.closeModal('clock-out')">Cancel</button>
          ${builds ? '<button class="btn btn-primary" id="btn-save-ticket-form" onclick="window.ClockModule.saveTicketFromForm()"><span class="material-icons">save</span> Save</button>' : ''}
          ${builds ? `<button class="btn btn-secondary" onclick="window.ClockModule.switchTicketFromClockOut()" title="Switch to a different customer/job without clocking out"><span class="material-icons">swap_horiz</span> Switch Ticket</button>` : ''}
          <button class="btn btn-danger" id="btn-confirm-clock-out" onclick="window.ClockModule.confirmClockOut()">
            <span class="material-icons">logout</span> ${builds ? 'Save &amp; Clock Out' : 'Close Out'}
          </button>`;
  footer.dataset.builds = String(builds);
}

// Check if a clock-out form section is enabled in the template
// If no template saved, all sections are enabled (safe default)
function isCOFSectionEnabled(key) {
  const saved = window.AppState?.settings?.clockOutFormSections;
  if (!saved || !Array.isArray(saved)) return true; // No template = show everything
  const section = saved.find(s => s.key === key);
  return section ? section.enabled : true; // Unknown section = show
}

// ============================================
// TASK-1483 — PERSONAL VEHICLE (mileage reimbursement) clock-out section.
// ONE builder + ONE reader shared by every clock-out surface: the Clock tile
// (buildClockOutModalContent), the founder review path, and the driver-PIN Job
// Board (jobboard.js reaches these through window.ClockModule — Pitfall #1).
// Shown only when (a) the tenant turned the feature on (settings.personalVehicle
// .enabled, written by the setPersonalVehicleSettings callable), (b) this person
// is eligible (people.hrVehicleMileageRate > 0 — Chris 2026-06-04: don't show it
// to people who don't get it), and (c) the Clock-Out Form template hasn't turned
// the 'personalVehicle' section off. NOT gated on buildTickets: it is payroll,
// not billing.
// 🔴 This is money paid TO THE EMPLOYEE — deliberately a separate section from
// Piece Rate, whose "Per Mile" unit BILLS THE CUSTOMER. Distinct title, icon,
// colour and copy so a driver can't log personal miles as a billable line.
// 🔴 The driver writes personalVehicleUsed + personalVehicleMiles ONLY. No
// amount or rate is ever written here (firestore.rules rejects it); payroll
// computes the dollars from the tenant policy in effect on the work date.
// Toggle debounce (COD Edge Case): the checkbox only shows/hides the miles box
// locally — nothing is written on toggle, and toggling off does NOT clear the
// typed miles, so a double-tap can't lose them. Both keys land in the SAME
// updateDoc that completes the entry (one atomic write).
// ============================================
function pvPolicy() {
  const VR = window.VehicleReimb;
  return VR ? VR.policyOf(window.AppState?.settings || {}) : null;
}

// TASK-1483 (COD) — enrolment check used where the money is computed: a
// claim from someone NOT enrolled never pays unless an admin entered it.
// Unknown person → undefined (never silently zeroes pay; see computeClaims).
function pvEligibleFn(lookup) {
  const VR = window.VehicleReimb;
  return (pid) => {
    if (!VR || !pid) return undefined;
    const p = lookup(pid);
    return p ? VR.isEligible(p) : undefined;
  };
}

// TASK-939 — THE per-diem day-hours rule, in one place: an entry's hours go to
// its clock-IN LOCAL calendar date (start-date wins; v1 never splits a shift
// across midnight). exportPayrollCSV, buildPayrollPrepData and the Claims tab
// all accumulate through this, so the per-diem ≥8h count and the TASK-1483
// vehicle "≥ N hours" trigger can never disagree about a day's hours.
function addDateHours(dateHours, clockIn, hrs) {
  // TASK-9878 — the shared rule (payPeriodHours.js pphAddDateHours), on THIS
  // device's calendar as before (the per-diem day key is not company-zone yet —
  // TASK-1507 "still open"); the connector passes the company zone.
  _pph().addDateHours(dateHours, clockIn, hrs, _clockLocalZone());
}

// TASK-1483 — the day inputs the vehicle engine needs for its "which days
// qualify" trigger, built from dateHours maps the caller ALREADY aggregated
// through addDateHours (never a second hours calculator).
//   byPid: { [pid]: { name, dateHours: { ymd: hours } } }
function pvDayOpts(byPid) {
  const src = byPid || {};
  const workDays = [];
  Object.keys(src).forEach(pid => {
    const dh = (src[pid] && src[pid].dateHours) || {};
    Object.keys(dh).forEach(ymd => workDays.push({ personId: pid, personName: src[pid].name || '', ymd }));
  });
  return {
    workDays,
    // undefined (not 0) when no completed hours are on file for that date —
    // the engine then says "hours not known" instead of a confident 0h.
    dayHours: (pid, ymd) => {
      const dh = src[pid] && src[pid].dateHours;
      return dh && Object.prototype.hasOwnProperty.call(dh, ymd) ? dh[ymd] : undefined;
    },
  };
}

function pvSectionOn(person) {
  const VR = window.VehicleReimb;
  if (!VR || !person) return false;
  const pol = pvPolicy();
  if (!pol || !pol.enabled) return false;
  if (!VR.isEligible(person)) return false;
  return isCOFSectionEnabled('personalVehicle');
}

// TASK-1483 (Chris) — the tenant's own name for this pay type. A LABEL only.
// Raw text: every caller ESCAPES it for its context (escapeHtml for HTML text
// and attributes — it escapes quotes; escapeCSV for the payroll CSV).
function pvName() {
  const VR = window.VehicleReimb;
  return VR ? VR.payTypeNameOf(window.AppState?.settings || {}) : 'Vehicle Reimbursement';
}

function pvRateLine() {
  const VR = window.VehicleReimb;
  const cur = VR ? VR.currentPolicy(pvPolicy()) : null;
  if (!cur) return '';
  const cents = (cur.ratePerMile * 100).toFixed(cur.ratePerMile * 100 % 1 ? 1 : 0);
  return cur.basis === 'flat_per_day'
    ? `Company pays $${cur.flatPerDay.toFixed(2)} for a qualifying day you use your own vehicle (miles are recorded at ${cents}¢/mile).`
    : `Company pays ${cents}¢ per mile.`;
}

// prefix: DOM id prefix ('clock' | 'jb' | 'adm'). variant: 'light' | 'dark'.
// existing: a clock entry whose claim should pre-fill (admin edit), or null.
function pvSectionHTML(prefix, variant, existing) {
  const p = String(prefix || 'clock').replace(/[^a-z0-9_-]/gi, '');
  const dark = variant === 'dark';
  const used = !!(existing && existing.personalVehicleUsed === true);
  const miles = existing && existing.personalVehicleMiles != null ? String(existing.personalVehicleMiles) : '';
  const box = dark
    ? 'background:rgba(15,118,110,0.12);border:2px solid #0F766E;border-radius:14px;padding:14px;margin-bottom:16px;color:#e2e8f0;'
    : 'background:rgba(15,118,110,0.06);border:2px solid #0F766E;border-radius:10px;padding:12px;margin-bottom:var(--spacing-lg);';
  const sub = dark ? 'color:#94a3b8;' : 'color:var(--text-secondary);';
  const input = dark
    ? 'width:100%;box-sizing:border-box;padding:12px;font-size:1.05rem;border:1px solid #334155;border-radius:10px;background:#1e293b;color:#e2e8f0;'
    : 'width:100%;max-width:220px;font-size:1rem;padding:8px;';
  const rate = pvRateLine();
  return `
    <div class="clock-out-section pv-section" id="${p}-pv-section" data-trailguide="personal-vehicle" style="${box}">
      <h4 style="margin:0 0 4px;display:flex;align-items:center;gap:6px;${dark ? 'color:#5eead4;' : 'color:#0F766E;'}">
        <span class="material-icons" style="font-size:18px;">directions_car</span> ${escapeHtml(pvName())}
        <span style="font-size:0.72rem;font-weight:600;${sub}">— paid back to you</span>
      </h4>
      <div style="font-size:0.78rem;${sub}margin-bottom:8px;line-height:1.35;">
        Did you drive your <strong>own</strong> vehicle for work this shift? This is reimbursed to you on payroll — it is <strong>not</strong> billed to the customer.${rate ? ' ' + escapeHtml(rate) : ''}
      </div>
      <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-weight:600;margin:0;">
        <input type="checkbox" id="${p}-pv-used" ${used ? 'checked' : ''} onchange="window.ClockModule.pvToggle('${p}')" style="width:20px;height:20px;">
        I used my own vehicle
      </label>
      <div id="${p}-pv-fields" style="margin-top:10px;${used ? '' : 'display:none;'}">
        <label for="${p}-pv-miles" style="display:block;font-size:0.75rem;font-weight:700;margin-bottom:4px;${sub}">Miles driven (required)</label>
        <input type="number" id="${p}-pv-miles" inputmode="decimal" min="0" max="${window.VehicleReimb?.MAX_MILES || 1000}" step="0.1" placeholder="e.g. 42" value="${escapeHtml(miles)}" style="${input}">
      </div>
    </div>`;
}

function pvToggle(prefix) {
  const p = String(prefix || 'clock').replace(/[^a-z0-9_-]/gi, '');
  const on = document.getElementById(`${p}-pv-used`)?.checked === true;
  const fields = document.getElementById(`${p}-pv-fields`);
  if (fields) fields.style.display = on ? '' : 'none';
  if (on) setTimeout(() => document.getElementById(`${p}-pv-miles`)?.focus(), 0);
}

// Reads the section IF it is rendered. Returns:
//   null                      → section not on screen: write nothing
//   { ok:false, error }       → toggle on, miles missing/invalid: refuse BEFORE
//                               any write (never a half-completed clock-out)
//   { ok:true, fields }       → exactly { personalVehicleUsed, personalVehicleMiles }
function pvReadForm(prefix) {
  const p = String(prefix || 'clock').replace(/[^a-z0-9_-]/gi, '');
  const cb = document.getElementById(`${p}-pv-used`);
  if (!cb || !window.VehicleReimb) return null;
  return window.VehicleReimb.validateClaim(cb.checked === true, document.getElementById(`${p}-pv-miles`)?.value);
}

// The clock-out write with the claim riding along. NEVER TRAP A DRIVER
// (TASK-428): client validation mirrors the rules bound exactly, but if the
// write is still refused (a stale bundle vs. newer rules, a completed-entry
// freeze), retry ONCE without the vehicle keys so the clock-out lands, and say
// plainly that the miles did not save. Any other error propagates unchanged.
async function pvSafeUpdate(ref, updateData, pvFields, source) {
  if (!pvFields) return updateDoc(ref, updateData);
  try {
    return await updateDoc(ref, { ...updateData, ...pvFields });
  } catch (err) {
    if (err?.code !== 'permission-denied') throw err;
    console.warn('[Clock] vehicle claim refused — clocking out without it', err?.code);
    await updateDoc(ref, updateData);
    try {
      logClockAudit(ClockState.tenantId, 'PERSONAL_VEHICLE_CLAIM_DROPPED', {
        source: source || 'clock-module', reason: err?.code || 'unknown',
        miles: pvFields.personalVehicleMiles ?? null,
      });
    } catch (_) { /* audit is best-effort */ }
    window.showToast?.(`Clocked out — but your ${pvName()} miles did not save. Tell the office so they can add them.`, 'warning');
    return undefined;
  }
}

// TASK-1483 — ADMIN-side claim entry (spec: people who never punch — work gets
// ticketed with no clock entry, so clock-out-only capture leaves gaps in the
// mileage log). Used by the Add Hours form (saveManualClockEntry) and the edit
// modal (saveClockEntryEdit). Workforce/admin only — the self arm is frozen on
// completed entries by rules, so this is the only post-hoc path. Marked
// `personalVehicleEnteredBy.source = 'admin'` so the review list shows it as
// admin-entered, never driver-reported. A changed claim clears any prior
// approve/deny (the reviewed number is no longer the number). Audited through
// logClockEntryChange (old → new miles on the per-entry history).
// Returns true when written (or nothing changed), false on failure (toasted).
async function pvAdminSave(entryId, fields, before) {
  if (!entryId || !fields) return true;
  if (!hasWorkforceAccess()) return true; // UI never renders the section for others
  const prevUsed = before?.personalVehicleUsed === true;
  const prevMiles = before?.personalVehicleMiles ?? null;
  if (prevUsed === fields.personalVehicleUsed && (prevMiles ?? null) === (fields.personalVehicleMiles ?? null)) return true;
  const email = window.AppState?.user?.email || 'unknown';
  const upd = {
    personalVehicleUsed: fields.personalVehicleUsed,
    personalVehicleMiles: fields.personalVehicleMiles,
    personalVehicleEnteredBy: { source: 'admin', by: email, at: Timestamp.now() },
    personalVehicleReview: null,
    updatedAt: Timestamp.now(),
  };
  try {
    await updateDoc(doc(db, 'tenants', ClockState.tenantId, 'clockEntries', entryId), upd);
    logClockEntryChange(ClockState.tenantId, entryId,
      { ...(before || {}), personalVehicleUsed: prevUsed, personalVehicleMiles: prevMiles },
      { ...(before || {}), personalVehicleUsed: fields.personalVehicleUsed, personalVehicleMiles: fields.personalVehicleMiles },
      'edit', 'admin-vehicle-claim');
    // Keep local copies honest so the review list / payroll re-render sees it.
    const patch = (e) => { if (e && e.id === entryId) Object.assign(e, upd); };
    [...(ClockState.activeEntries || []), ...(ClockState.recentEntries || [])].forEach(patch);
    Object.values(ClockState.weeklyCache || {}).forEach(arr => (arr || []).forEach(patch));
    return true;
  } catch (err) {
    console.error('[Clock] personal-vehicle admin save failed:', err);
    window.showToast?.(`Hours saved, but the ${pvName()} miles did NOT save. Re-open the entry and try again.`, 'error');
    return false;
  }
}

function _pvFindEntry(entryId) {
  return [...(ClockState.activeEntries || []), ...(ClockState.recentEntries || [])].find(e => e.id === entryId)
    || Object.values(ClockState.weeklyCache || {}).flat().find(e => e && e.id === entryId) || null;
}

// Admin section is shown when the tenant has the feature on AND (the person is
// eligible OR the entry already carries a claim — so a claim can always be
// corrected even after someone is un-enrolled).
function pvAdminSectionOn(person, entry) {
  const VR = window.VehicleReimb;
  if (!VR || !hasWorkforceAccess()) return false;
  const pol = pvPolicy();
  if (!pol || !pol.enabled) return false;
  return VR.isEligible(person) || VR.hasClaim(entry);
}

function pvAdminSectionHTML(prefix, entry) {
  return `<div style="grid-column:1/-1;">${pvSectionHTML(prefix, 'light', entry)}
    <div style="font-size:0.72rem;color:var(--text-secondary);margin:-8px 0 8px;">Entered here = marked <strong>admin-entered</strong> on the review list, not driver-reported.</div></div>`;
}

function canEditEntry(entry) {
  // TASK-360 — Workforce module gates edit-anyone power. Pre-TASK-360 any
  // signed-in non-driver could edit any entry. Now: workforce/admin can
  // edit any; clock-only members can only edit their OWN entry (matching
  // memberAccess.personId). Driver PIN / driver link still gated on the
  // allowDriverTimeEdit tenant setting + entry ownership.
  if (!window.AppState?.driverMode && !window.AppState?.isPublicAccess && !window.AppState?.driverModePin) {
    if (hasWorkforceAccess()) return true;
    // Non-workforce SPA member — only own entries.
    const selfId = getSelfPersonId();
    return !!(selfId && entry.personId === selfId);
  }
  // Driver PIN / driver link — only if allowDriverTimeEdit is on AND it's their own entry
  if (window.AppState?.settings?.allowDriverTimeEdit !== true) return false;
  return entry.personId && entry.personId === (ClockState.authenticatedPersonId || window.AppState?.driverPersonId);
}

// TASK-9941 F S2 — calculateDriverPin / getPersonPin are gone from the Clock
// screen: the browser no longer works out anyone's name + phone code or
// compares a typed code. This only answers "would this person HAVE a name +
// phone code" (2+ letters of a first name and 4+ phone digits), which keeps
// the history wall in renderRecentList exactly where TASK-4642 left it.
function _clockNamePhoneCodeExists(person) {
  if (!person) return false;
  const first = String(person.name || '').trim().split(/\s+/)[0] || '';
  return first.length >= 2 && String(person.phone || '').replace(/\D/g, '').length >= 4;
}

// ===== TASK-9941 F S2 CLOCK CODE CHECK (start) =====
// The Clock screen asks the SERVER whether the typed code belongs to the person
// (verifyClockPersonCode). Their private PIN always works; the name + phone code
// works only while they have no private PIN and before the company's cutover
// date. Every punch for someone other than the signed-in member is authorised
// one of three ways, and the punch SAYS which:
//   'code'       the server said yes
//   'override'   an office session unlocked the device (TASK-4691)
//   'unverified' the tablet is offline, the check errored, this person is locked
//                on this tablet after too many tries, or the tablet's hourly
//                budget is spent. The punch is NEVER blocked (wage-and-hour):
//                it lands with needsReview:true + codeCheck:'unverified' and
//                shows in Workforce / Payroll Prep for the office.
// A grant covers ONE punch for ONE person and is cleared after the write (or
// on a person change, or after CLOCK_CODE_GRANT_MS). It is never a standing
// "authenticated" state. The stamps are client-written: a review hint, not
// evidence (COD r2 AuthZ). This is a UI gate — the rules still let a
// workforce / dispatch / billing member write any punch.
const CLOCK_CODE_GRANT_MS = 5 * 60 * 1000;
const CLOCK_CODE_CHECK_TIMEOUT_MS = 8000;

function _clockGrantFor(personId, nowMs) {
  const g = ClockState.codeGrant;
  if (!g || !personId || g.personId !== personId) return null;
  if ((nowMs || Date.now()) - g.at > CLOCK_CODE_GRANT_MS) return null;
  return g;
}

function _clockSetGrant(personId, how, reason) {
  ClockState.codeGrant = { personId, how, reason: reason || null, at: Date.now() };
  // History + edit UI keep reading authenticatedPersonId; an unverified grant
  // does not open this person's hours.
  if (how === 'code') ClockState.authenticatedPersonId = personId;
}

// After a punch is written: the next punch for this person asks again.
function _clockConsumeGrant(personId) {
  const g = ClockState.codeGrant;
  if (g && g.personId === personId) {
    ClockState.codeGrant = null;
    if (ClockState.authenticatedPersonId === personId && !_clockIsSelfSelection(personId)) {
      ClockState.authenticatedPersonId = null;
    }
  }
}

// -> { ok:true, how:'self'|'override'|'code'|'unverified', reason } | { ok:false }
function clockPunchAuthorization(personId) {
  if (!personId) return { ok: false };
  if (!clockPersonPinRequired(personId)) {
    const A = window.AppState || {};
    const self = _clockIsSelfSelection(personId)
      || !!((A.driverModePin || A.driverMode) && A.driverPersonId === personId);
    return { ok: true, how: self ? 'self' : 'override', reason: null };
  }
  const g = _clockGrantFor(personId);
  if (g) return { ok: true, how: g.how, reason: g.reason };
  return { ok: false };
}

// Fields a punch carries. Nothing for a member clocking themselves (that was
// already true before S2). phase 'in' on the entry create, 'out' on clock-out.
function clockCodeStampFields(auth, phase) {
  if (!auth || !auth.ok || auth.how === 'self') return {};
  const key = phase === 'out' ? 'codeCheckOut' : 'codeCheck';
  const out = { [key]: auth.how };
  if (auth.how === 'unverified') {
    out.needsReview = true; // the existing review mark (rules let an office arm SET it, never clear its own)
    out[phase === 'out' ? 'codeCheckOutReason' : 'codeCheckReason'] = auth.reason || 'unknown';
  }
  return out;
}

// Pure: what the screen does with a verifyClockPersonCode answer (or error).
function clockCodeOutcome(res, err, online) {
  if (online === false && !res) {
    return { state: 'offline', allowUnverified: true, message: 'This tablet is offline, so the PIN can’t be checked.' };
  }
  if (err) {
    const code = String((err && err.code) || '').replace(/^functions\//, '');
    if (code === 'permission-denied') return { state: 'denied', allowUnverified: true, message: 'This login can’t check PINs.' };
    if (code === 'invalid-argument') return { state: 'wrong', allowUnverified: false, message: 'Enter the PIN (4 to 8 letters or numbers).' };
    return { state: 'error', allowUnverified: true, message: 'Couldn’t check the PIN right now.' };
  }
  const r = res || {};
  if (r.ok === true) return { state: 'ok', via: r.via === 'standard' ? 'standard' : 'private' };
  if (r.reason === 'locked' || r.locked === true) {
    return { state: 'locked', allowUnverified: true, message: 'Too many tries for this person on this tablet. Ask the office for a manager override, or continue and the office will review the punch.' };
  }
  if (r.reason === 'budget') {
    return { state: 'budget', allowUnverified: true, message: 'This tablet has had too many wrong PINs this hour, so PINs aren’t being checked right now.' };
  }
  if (r.reason === 'use_private_pin') {
    return { state: 'use_private_pin', allowUnverified: false, message: 'Use your private PIN, not the name + phone code.' };
  }
  if (r.reason === 'standard_code_off') {
    return { state: 'standard_code_off', allowUnverified: false, showSetup: true, message: 'Name + phone codes are turned off here. Use your private PIN, or set one below.' };
  }
  const left = Number(r.triesLeft);
  return {
    state: 'wrong', allowUnverified: false,
    message: Number.isFinite(left) && left > 0 && left <= 3 ? `That PIN didn’t match. ${left} ${left === 1 ? 'try' : 'tries'} left on this tablet.` : 'That PIN didn’t match.',
  };
}

function _clockPinPanelReset() {
  ClockState._pinFallbackReason = null;
  ClockState._pinSetup = null;
  ClockState._pinChecking = false;
  const err = document.getElementById('pin-error');
  if (err) { err.textContent = ''; err.classList.add('hidden'); }
  document.getElementById('clock-pin-fallback')?.classList.add('hidden');
  const setup = document.getElementById('clock-pin-setup');
  if (setup) { setup.innerHTML = ''; setup.classList.add('hidden'); }
  document.getElementById('clock-pin-setup-link')?.classList.remove('hidden');
}

function _clockShowPinOutcome(o) {
  const err = document.getElementById('pin-error');
  if (err) { err.textContent = o.message || ''; err.classList.toggle('hidden', !o.message); }
  document.getElementById('clock-pin-fallback')?.classList.toggle('hidden', !o.allowUnverified);
  ClockState._pinFallbackReason = o.allowUnverified ? o.state : null;
  if (o.allowUnverified) {
    document.getElementById('clock-pin-override')?.classList.toggle('hidden', !clockCanOfferOverride());
  }
  if (o.showSetup) document.getElementById('clock-pin-setup-link')?.classList.remove('hidden');
}

async function submitClockPin() {
  const personId = ClockState.selectedPerson;
  const input = document.getElementById('clock-pin');
  if (!personId || !input || ClockState._pinChecking) return;
  const code = String(input.value || '').trim();
  if (!/^[A-Za-z0-9]{4,8}$/.test(code)) {
    _clockShowPinOutcome({ state: 'wrong', allowUnverified: false, message: 'Enter the PIN (4 to 8 letters or numbers).' });
    return;
  }
  const btn = document.getElementById('clock-pin-submit');
  ClockState._pinChecking = true;
  if (btn) { btn.disabled = true; btn.textContent = 'Checking…'; }
  let res = null; let err = null;
  const online = navigator.onLine !== false;
  try {
    if (online) {
      const fn = httpsCallable(functions, 'verifyClockPersonCode', { timeout: CLOCK_CODE_CHECK_TIMEOUT_MS });
      res = (await fn({ tenantId: ClockState.tenantId, personId, code })).data || {};
    }
  } catch (e) {
    err = e;
    console.warn('[Clock] PIN check failed:', e?.code || e?.message);
  } finally {
    ClockState._pinChecking = false;
    if (btn) { btn.disabled = false; btn.textContent = 'Check'; }
  }
  if (ClockState.selectedPerson !== personId) return; // picked someone else meanwhile
  const o = clockCodeOutcome(res, err, online);
  input.value = '';
  if (o.state === 'ok') {
    // updateSelectedPersonStatus() skips its repaint while #clock-pin has focus.
    try { input.blur(); } catch (_) { /* best-effort */ }
    _clockPinPanelReset();
    _clockSetGrant(personId, 'code', o.via === 'standard' ? 'name_phone_code' : null);
    updateSelectedPersonStatus();
    renderRecentList();
    return;
  }
  _clockShowPinOutcome(o);
}

// "Continue without a PIN" — only offered after a lock / offline / error /
// budget answer. Never after a plain wrong PIN.
function clockContinueUnverified() {
  const personId = ClockState.selectedPerson;
  const reason = ClockState._pinFallbackReason;
  if (!personId || !reason) return;
  _clockPinPanelReset();
  _clockSetGrant(personId, 'unverified', reason);
  logClockAudit(ClockState.tenantId, 'CLOCK_CODE_UNVERIFIED', {
    source: 'clock-module', personId,
    personName: (ClockState.people.find(p => p.id === personId) || {}).name || null,
    reason,
  });
  window.showToast?.('OK. This punch will be marked for the office to review.', 'info');
  updateSelectedPersonStatus();
}

// ---- Option C: "Set my private PIN" at the tablet (skippable) -------------
// startClockPinSetup texts a code to the person's phone on file; setDriverPin
// with mintSession:false saves the PIN and mints NO driver session (the token
// is marked as a tablet token server-side, so it never could).
function _clockPinSetupHtml(step, opts) {
  const o = opts || {};
  const first = escapeHtml(o.first || 'this person');
  const btn = 'min-height:44px;padding:6px 14px;border-radius:6px;font-weight:600;cursor:pointer;';
  const input = 'width:100%;min-height:44px;padding:8px;border:1px solid var(--border-color,#d1d5db);border-radius:6px;font-size:1rem;margin-top:4px;';
  const msg = o.message ? `<div style="color:#b91c1c;font-size:0.85rem;margin-top:6px;">${escapeHtml(o.message)}</div>` : '';
  if (step === 'send') {
    return `<div style="font-size:0.85rem;color:var(--text-primary);">We'll text a code to ${first}'s phone on file. Then pick a private PIN (4 to 8 letters or numbers).</div>
      ${msg}
      <div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap;">
        <button type="button" id="clock-pin-setup-send" class="btn btn-primary" style="${btn}" onclick="window.ClockModule.clockSendPinSetupCode()">Text me a code</button>
        <button type="button" class="btn btn-secondary" style="${btn}" onclick="window.ClockModule.clockCancelPinSetup()">Not now</button>
      </div>`;
  }
  return `<div style="font-size:0.85rem;color:var(--text-primary);">${o.needsPhoneVerify ? `Enter the code we texted to ${first}, then pick a PIN.` : 'Pick a PIN.'}</div>
    ${o.needsPhoneVerify ? `<label style="display:block;font-size:0.8rem;margin-top:8px;">Code from the text<input type="text" id="clock-pin-setup-sms" inputmode="numeric" maxlength="6" autocomplete="one-time-code" style="${input}"></label>` : ''}
    <label style="display:block;font-size:0.8rem;margin-top:8px;">New private PIN<input type="password" id="clock-pin-setup-new" maxlength="8" autocomplete="off" autocapitalize="characters" style="${input}"></label>
    <label style="display:block;font-size:0.8rem;margin-top:8px;">Type it again<input type="password" id="clock-pin-setup-again" maxlength="8" autocomplete="off" autocapitalize="characters" style="${input}"></label>
    ${msg}
    <div style="display:flex;gap:8px;margin-top:8px;flex-wrap:wrap;">
      <button type="button" id="clock-pin-setup-save" class="btn btn-primary" style="${btn}" onclick="window.ClockModule.clockFinishPinSetup()">Save PIN</button>
      <button type="button" class="btn btn-secondary" style="${btn}" onclick="window.ClockModule.clockCancelPinSetup()">Not now</button>
    </div>`;
}

function _clockPinSetupFirst() {
  const p = ClockState.people.find(x => x.id === ClockState.selectedPerson);
  return String((p && p.name) || '').trim().split(/\s+/)[0] || '';
}

function clockStartPinSetup() {
  const box = document.getElementById('clock-pin-setup');
  if (!box || !ClockState.selectedPerson) return;
  ClockState._pinSetup = { personId: ClockState.selectedPerson, step: 'send' };
  box.innerHTML = _clockPinSetupHtml('send', { first: _clockPinSetupFirst() });
  box.classList.remove('hidden');
  document.getElementById('clock-pin-setup-link')?.classList.add('hidden');
}

function clockCancelPinSetup() {
  ClockState._pinSetup = null;
  const box = document.getElementById('clock-pin-setup');
  if (box) { box.innerHTML = ''; box.classList.add('hidden'); }
  document.getElementById('clock-pin-setup-link')?.classList.remove('hidden');
}

async function clockSendPinSetupCode() {
  const st = ClockState._pinSetup;
  const box = document.getElementById('clock-pin-setup');
  if (!st || !box || st.personId !== ClockState.selectedPerson) return;
  const btn = document.getElementById('clock-pin-setup-send');
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  let res = null; let message = null;
  try {
    const fn = httpsCallable(functions, 'startClockPinSetup', { timeout: 20000 });
    res = (await fn({ tenantId: ClockState.tenantId, personId: st.personId })).data || {};
    if (!res.ok) message = res.message || 'Ask the office to send a setup code.';
  } catch (e) {
    message = navigator.onLine === false ? 'This tablet is offline. Try again when it’s back online.' : (e?.message || 'Couldn’t send the text. Try again, or ask the office.');
  }
  if (ClockState._pinSetup !== st) return;
  if (message) {
    box.innerHTML = _clockPinSetupHtml('send', { first: _clockPinSetupFirst(), message });
    return;
  }
  st.step = 'save';
  st.setupToken = res.setupToken;
  st.needsPhoneVerify = res.needsPhoneVerify === true;
  box.innerHTML = _clockPinSetupHtml('save', { first: _clockPinSetupFirst(), needsPhoneVerify: st.needsPhoneVerify });
}

async function clockFinishPinSetup() {
  const st = ClockState._pinSetup;
  const box = document.getElementById('clock-pin-setup');
  if (!st || !box || st.step !== 'save' || st.personId !== ClockState.selectedPerson) return;
  const smsCode = String(document.getElementById('clock-pin-setup-sms')?.value || '').trim();
  const newPin = String(document.getElementById('clock-pin-setup-new')?.value || '').trim().toUpperCase();
  const again = String(document.getElementById('clock-pin-setup-again')?.value || '').trim().toUpperCase();
  const redraw = (message) => { box.innerHTML = _clockPinSetupHtml('save', { first: _clockPinSetupFirst(), needsPhoneVerify: st.needsPhoneVerify, message }); };
  if (st.needsPhoneVerify && !/^\d{6}$/.test(smsCode)) return redraw('Enter the 6-digit code from the text.');
  if (!/^[A-Z0-9]{4,8}$/.test(newPin)) return redraw('A PIN is 4 to 8 letters or numbers.');
  if (newPin !== again) return redraw('The two PINs don’t match.');
  const btn = document.getElementById('clock-pin-setup-save');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    const fn = httpsCallable(functions, 'setDriverPin', { timeout: 20000 });
    const r = (await fn({ setupToken: st.setupToken, smsCode: smsCode || undefined, newPin, mintSession: false })).data || {};
    if (ClockState._pinSetup !== st) return;
    if (!r.ok) return redraw(r.message || 'That PIN can’t be used. Pick another one.');
  } catch (e) {
    if (ClockState._pinSetup !== st) return;
    return redraw(e?.message || 'Couldn’t save the PIN. Try again.');
  }
  const personId = st.personId;
  _clockPinPanelReset();
  _clockSetGrant(personId, 'code', 'new_private_pin');
  window.showToast?.('Private PIN saved. Use it here and in the driver app from now on.', 'success');
  updateSelectedPersonStatus();
  renderRecentList();
}
// The clock-out sheet is opened only by clockOut(), which records the grant it
// opened under. Confirm stamps from that (never re-asks: a slow close-out must
// not be refused at the end), then it is spent.
function _clockOutAuthFor(entry) {
  const a = ClockState.clockOutAuth;
  if (!a || !entry || a.personId !== entry.personId) return null;
  return { ok: true, how: a.how, reason: a.reason };
}
function _clockOutCodeStamp(entry) {
  return clockCodeStampFields(_clockOutAuthFor(entry), 'out');
}
function _clockOutCodeAudit(entry) {
  const a = _clockOutAuthFor(entry);
  return a ? { codeCheck: a.how, codeCheckReason: a.reason || null } : {};
}
function _clockConsumeClockOutAuth(entry) {
  if (!entry) return;
  if (ClockState.clockOutAuth && ClockState.clockOutAuth.personId === entry.personId) ClockState.clockOutAuth = null;
  _clockConsumeGrant(entry.personId);
}
// A job switch from the clock-out sheet rides the sheet's grant; from the
// Currently Clocked In list it needs its own.
function _clockSwitchAuth(entry) {
  if (!entry) return { ok: false };
  if (ClockState._switchFromClockOut) {
    const a = _clockOutAuthFor(entry);
    if (a) return a;
  }
  return clockPunchAuthorization(entry.personId);
}
// ===== TASK-9941 F S2 CLOCK CODE CHECK (end) =====

// ============================================
// HTML TEMPLATE
// ============================================
function getClockHTML() {
  const hasTimeOff = showTimeOffTab();

  return `
    <div class="clock-view">
      <!-- Header -->
      <div class="clock-header" style="justify-content:center;">
        <div class="clock-title" style="text-align:center;">
          <h1 id="clock-view-title">${ClockState.activeTab === 'workforce' ? 'Workforce' : 'Time Clock'}</h1>
          <span class="clock-time" id="current-time"></span>
        </div>
      </div>

      ${hasTimeOff ? `
      <!-- Tab Navigation -->
      <div class="clock-tabs">
        <button class="clock-tab active" data-tab="clock" onclick="window.ClockModule.switchTab('clock')">
          <span class="material-icons">schedule</span>
          Time Clock
        </button>
        <button class="clock-tab" data-tab="timeoff" onclick="window.ClockModule.switchTab('timeoff')">
          <span class="material-icons">event_busy</span>
          Time Off
        </button>
      </div>
      ` : ''}

      <!-- Tab Content -->
      <div id="clock-tab-content">
        ${getClockTabHTML()}
      </div>
    </div>

    <!-- Clock Out / Close Out Enhanced Modal -->
    <div class="modal hidden" id="modal-clock-out">
      <div class="modal-content modal-large" style="max-width:680px;">
        <div class="modal-header">
          <h3>${shouldBuildTickets() ? 'Build Ticket' : 'Close Out'}</h3>
          <button class="btn-icon" onclick="window.ClockModule.closeModal('clock-out')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="clock-out-body" style="max-height:70vh;overflow-y:auto;padding:var(--spacing-lg);">
          <!-- Filled dynamically -->
        </div>
        <div class="modal-footer" data-builds="${shouldBuildTickets()}">
          <button class="btn btn-secondary" onclick="window.ClockModule.closeModal('clock-out')">Cancel</button>
          ${shouldBuildTickets() ? '<button class="btn btn-primary" id="btn-save-ticket-form" onclick="window.ClockModule.saveTicketFromForm()"><span class="material-icons">save</span> Save</button>' : ''}
          ${shouldBuildTickets() ? `<button class="btn btn-secondary" onclick="window.ClockModule.switchTicketFromClockOut()" title="Switch to a different customer/job without clocking out"><span class="material-icons">swap_horiz</span> Switch Ticket</button>` : ''}
          <!-- 🔴 THIS BUTTON ENDS THE SHIFT — LABEL IT THAT WAY.
               It used to read "Build Ticket" whenever shouldBuildTickets() was on,
               which is the SAME words as the button the driver just pressed to get
               here AND the same as this modal's own <h3>. Sitting next to a "Save"
               button, a red "Build Ticket" reads as "save the ticket", not "end my
               shift" — so a driver hunts for a clock-out control, doesn't find one,
               and taps Save instead. Reported live: "there is no clock-out button on
               brians phone when he goes to build ticket." The control was always
               there; nothing on the screen said "Clock Out".
               The two OTHER paths that rewrite this same button already label it
               "Clock Out" (the error-recovery reset in confirmClockOut, and the
               form-restore footer in backToForm) — this initial render was the odd
               one out, so this makes the three agree rather than inventing wording.
               Clock-out is a driver-TRAPPING surface (TASK-428): a driver who can't
               find the way out is stuck on the clock. -->
          <button class="btn btn-danger" id="btn-confirm-clock-out" onclick="window.ClockModule.confirmClockOut()">
            <span class="material-icons">logout</span> ${shouldBuildTickets() ? 'Save &amp; Clock Out' : 'Close Out'}
          </button>
        </div>
      </div>
    </div>

    <!-- Switch Job Modal -->
    <div class="modal hidden" id="modal-switch-job">
      <div class="modal-content" style="max-width:500px;">
        <div class="modal-header">
          <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">swap_horiz</span>Switch Job</h3>
          <button class="btn-icon" onclick="window.ClockModule.closeModal('switch-job')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="switch-job-body" style="max-height:60vh;overflow-y:auto;padding:var(--spacing-lg);">
          <!-- Filled dynamically -->
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.ClockModule.closeModal('switch-job')">Cancel</button>
          <button class="btn btn-primary" id="btn-confirm-switch-job" onclick="window.ClockModule.confirmSwitchJob()">
            <span class="material-icons">swap_horiz</span> Switch
          </button>
        </div>
      </div>
    </div>

    ${(hasTimeOff || memberHasModule('timeoff')) ? getTimeOffModalsHTML() : ''}

    <!-- Workforce Modal (accessible from Billing too) -->
    <div class="modal hidden" id="modal-workforce">
      <div class="modal-content modal-large" style="max-width:900px;">
        <div class="modal-header">
          <h3><span class="material-icons">groups</span> Workforce</h3>
          <button class="btn-icon" onclick="window.ClockModule.closeModal('workforce')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="workforce-modal-body" style="max-height:75vh;overflow-y:auto;padding:var(--spacing-lg);">
          <!-- Filled dynamically -->
        </div>
      </div>
    </div>
  `;
}

function getClockTabHTML() {
  // TASK-360 — Clock vs Workforce module split (2026-05-26).
  // Workforce-gated surfaces (admin kiosk):
  //   • "Select Employee" dropdown (lists all people)
  //   • "Currently Clocked In" right panel (click-to-select-other-person)
  // For users with only the `clock` module we render a stripped self-clock
  // surface — pinned "You — [Name]" header, NO employee dropdown, NO active
  // list. Workforce admins (founder/owner/legacy-admin/workforce-module) see
  // the full kiosk exactly as before. See `hasWorkforceAccess()` + spec at
  // TASKBOARD TASK-360.
  const workforceAccess = hasWorkforceAccess();
  const selfPersonId = getSelfPersonId();
  const selfPerson = selfPersonId ? ClockState.people.find(p => p.id === selfPersonId) : null;
  const selfName = selfPerson?.name || window.AppState?.user?.displayName || window.AppState?.user?.email || 'You';

  // Person-selection block. Two flavors:
  //   workforceAccess === true  → full dropdown (kiosk admin)
  //   workforceAccess === false → pinned "You — [Name]" header + hidden
  //     <select id="clock-person"> seeded with selfPersonId so downstream
  //     code (clockIn, onPersonSelect, etc.) finds the same field.
  let personBlockHtml = '';
  if (workforceAccess) {
    personBlockHtml = `
          <!-- Step 1: Select Employee -->
          <div class="form-group">
            <label for="clock-person">1. Select Employee</label>
            <select id="clock-person" class="clock-select" onchange="window.ClockModule.onPersonSelect()">
              <option value="">Choose employee...</option>
            </select>
          </div>`;
  } else if (selfPersonId) {
    personBlockHtml = `
          <!-- TASK-360 self-clock surface: pinned identity, no dropdown -->
          <div class="form-group clock-self-pinned" style="display:flex;align-items:center;gap:10px;padding:10px 12px;background:var(--bg-subtle, #F8FAFC);border:1px solid var(--border-light);border-radius:8px;margin-bottom:var(--spacing-md);">
            <span class="material-icons" style="color:var(--brand-blue);">person</span>
            <div>
              <div style="font-size:0.72rem;color:var(--text-muted);text-transform:uppercase;letter-spacing:0.06em;font-weight:600;">You</div>
              <div style="font-size:1rem;font-weight:600;color:var(--text-primary);">${escapeHtml(selfName)}</div>
            </div>
          </div>
          <select id="clock-person" class="clock-select" style="display:none;" aria-hidden="true">
            <option value="${escapeHtml(selfPersonId)}" selected>${escapeHtml(selfName)}</option>
          </select>`;
  } else {
    // Edge case (TASK-348): clock module granted but member is not linked to
    // a person doc (e.g. owner who never self-linked). Show a friendly
    // empty state — they can still see Recent Activity (filtered to none)
    // but can't clock anyone in. Direct them to ask an admin to link them.
    personBlockHtml = `
          <!-- TASK-360 self-clock surface: not linked to a person doc -->
          <div class="form-group clock-self-unlinked" style="padding:14px 16px;background:#FEF3C7;border:1px solid #F59E0B;border-radius:8px;margin-bottom:var(--spacing-md);">
            <div style="display:flex;gap:10px;align-items:flex-start;">
              <span class="material-icons" style="color:#B45309;flex-shrink:0;">info</span>
              <div>
                <div style="font-weight:600;color:#7C2D12;margin-bottom:4px;">Your login isn't linked to a roster person yet.</div>
                <div style="font-size:0.85rem;color:#7C2D12;">Ask an admin to link this account to your person record so you can clock in.</div>
              </div>
            </div>
          </div>
          <select id="clock-person" class="clock-select" style="display:none;" aria-hidden="true">
            <option value="">Choose employee...</option>
          </select>`;
  }

  return `
    <div class="clock-content${workforceAccess ? '' : ' clock-content-self-only'}">
      <!-- Left Panel: Clock In/Out -->
      <div class="clock-panel clock-panel-main">
        <div class="panel-header" style="display:flex;justify-content:space-between;align-items:center;">
          <h2>${isFounder() ? 'Time Clock' : (workforceAccess ? 'Start Job / Close Out' : 'Time Clock')}</h2>
        </div>

        <div class="clock-form">
          ${personBlockHtml}

          <div class="clock-status hidden" id="clock-status-container">
            <div class="status-card" id="clock-status"></div>
          </div>

          <!-- Step 2: PIN Entry (hidden by default) -->
          <div class="pin-entry hidden" id="pin-entry">
            <label for="clock-pin">2. Enter PIN</label>
            <!-- TASK-9941 F S2 — checked on the SERVER (verifyClockPersonCode). A
                 private PIN is 4-8 letters/numbers, so there is no auto-check on
                 length any more: Check / Enter sends it. -->
            <div class="pin-input-container" style="display:flex;gap:8px;align-items:center;">
              <input type="password" id="clock-pin" maxlength="8" placeholder="PIN" autocomplete="off" autocapitalize="characters" spellcheck="false"
                oninput="window.ClockModule.onPinInput()"
                onkeydown="if(event.key==='Enter'){event.preventDefault();window.ClockModule.submitClockPin();}">
              <button type="button" id="clock-pin-submit" class="btn btn-primary" style="min-height:44px;padding:6px 16px;flex-shrink:0;" onclick="window.ClockModule.submitClockPin()">Check</button>
            </div>
            <div style="font-size:0.75rem;color:var(--text-primary);opacity:0.7;margin-top:4px;">Your private PIN, or your name + phone code if you haven't set one yet.</div>
            <div id="pin-error" class="hidden" style="color:#dc2626;font-size:0.85rem;margin-top:4px;"></div>
            <div id="clock-pin-fallback" class="hidden" style="margin-top:8px;">
              <button type="button" onclick="window.ClockModule.clockContinueUnverified()"
                style="font-size:0.85rem;padding:6px 14px;min-height:44px;border:1px solid #b45309;color:#78350f;background:rgba(245,158,11,0.10);border-radius:6px;cursor:pointer;font-weight:600;">
                Continue without a PIN
              </button>
              <div style="font-size:0.75rem;color:var(--text-primary);opacity:0.7;margin-top:4px;">The punch is saved and marked for the office to review.</div>
            </div>
            <div id="clock-pin-setup-link" style="margin-top:6px;">
              <button type="button" onclick="window.ClockModule.clockStartPinSetup()"
                style="font-size:0.8rem;padding:4px 0;min-height:36px;border:none;background:transparent;color:var(--brand-blue);cursor:pointer;text-decoration:underline;">
                No private PIN yet? Set one
              </button>
            </div>
            <div id="clock-pin-setup" class="hidden" style="margin-top:8px;padding:10px;border:1px solid var(--border-color,#e5e7eb);border-radius:8px;"></div>
            <!-- TASK-4691 P2 — the device lock IS the override. Shown only to a
                 lock-governed viewer whose device is currently LOCKED, i.e. only
                 when there is a real gesture that would grant the override.
                 The challenge fires HERE, at the moment the override is used —
                 never on a timer. A timed re-challenge was cut by COD: it would
                 fire while a driver is in the camera on a receipt photo and it
                 breaks the clock-out law in docs/modules/clock-billing.md
                 (never block the entry write). -->
            <div id="clock-pin-override" class="hidden" style="margin-top:10px;padding-top:10px;border-top:1px solid var(--border-color,#e5e7eb);">
              <button type="button" onclick="window.ClockModule.useClockPinOverride()"
                style="display:flex;align-items:center;gap:6px;font-size:0.82rem;padding:6px 14px;min-height:44px;border:1px dashed var(--brand-blue);color:var(--brand-blue);background:transparent;border-radius:6px;cursor:pointer;font-weight:500;">
                <span class="material-icons" style="font-size:16px;">lock_open</span> Manager override
              </button>
              <div style="font-size:0.75rem;color:var(--text-primary);opacity:0.7;margin-top:5px;">
                Unlock this device with your access PIN to clock someone else in. Recorded in the activity log.
              </div>
            </div>
          </div>

          <!-- Step 3: Select Customer (for clock in - required) -->
          <!-- Self-clock surface (workforceAccess=false) hides employee
               + PIN steps, so number-prefixing the customer label is
               confusing ("Step 3" appears alone). Drop numbers entirely
               when the user only sees a single step. -->
          <div class="clock-in-fields hidden" id="clock-in-fields">
            <div class="form-group">
              <label for="clock-customer">${workforceAccess ? `${isFounder() ? '3' : '2'}. ` : ''}Select Customer *</label>
              <select id="clock-customer" class="clock-select" onchange="window.ClockModule.onCustomerSelect()">
                <option value="">Choose customer...</option>
              </select>
              <div id="clock-customer-hint"></div>
            </div>

            <!-- Step 4: Select Job (filtered by customer) -->
            <div class="form-group hidden" id="job-selection">
              <label for="clock-job">${workforceAccess ? `${isFounder() ? '4' : '3'}. ` : ''}Select Job</label>
              <select id="clock-job" class="clock-select" onchange="window.ClockModule.onClockJobChange()">
                <option value="">No specific job</option>
              </select>
              <!-- TASK-9865 — "Not on today's crew for J-1247 — you're on J-1248"
                   + one-tap switch. Advisory only; populated by _clockRenderCrewWarn. -->
              <div id="clock-job-crew-warn" style="display:none;margin-top:8px;"></div>
              <!-- TASK-586 — per-operator equipment confirm rows. Populated by
                   _clockRenderInlineEquipment ONLY when equipmentTie is ON +
                   SELF clock-in + the job day has equipment; otherwise stays
                   hidden and empty (OFF tenants byte-identical). -->
              <div id="clock-in-equipment" style="display:none;margin-top:10px;"></div>
            </div>

            <!-- Notes (hidden — notes captured at clock-out instead) -->
            <div class="form-group" style="display:none;">
              <textarea id="clock-notes" rows="2" placeholder="Optional notes..."></textarea>
            </div>

            <!-- Optional: Adjust clock-in time (admins always; drivers only if tenant setting allows) -->
            ${(() => {
              const isDriverScope = !!(window.AppState?.driverMode || window.AppState?.driverModePin || window.AppState?.isPublicAccess);
              const canAdjust = !isDriverScope || window.AppState?.settings?.allowDriverTimeEdit === true;
              if (!canAdjust) return '';
              return `
            <div style="margin-top: 4px;">
              <a href="javascript:void(0)" onclick="window.ClockModule.toggleClockInTime()" style="font-size: 0.85rem; color: var(--brand-blue); text-decoration: none;">Adjust time?</a>
            </div>
            <div class="form-group" id="clock-time-adjust-group" style="display:none; margin-top: 8px;">
              <label>Clock-In Time</label>
              ${renderSplitDateTime('clock-in-time', '', '', { className: 'clock-select' })}
            </div>`;
            })()}
          </div>

          <!-- Action Buttons -->
          <div class="clock-actions hidden" id="clock-actions">
            <button class="btn btn-success btn-large" id="btn-clock-in" onclick="window.ClockModule.clockIn()">
              <span class="material-icons">login</span> ${isClockAdmin() ? 'Clock In' : 'Start Job'}
            </button>
            <button class="btn btn-danger btn-large hidden" id="btn-clock-out" onclick="window.ClockModule.clockOut()">
              <span class="material-icons">${shouldBuildTickets() ? 'receipt_long' : 'logout'}</span> ${shouldBuildTickets() ? 'Build Ticket' : 'Close Out'}
            </button>
          </div>

        </div>
      </div>

      <!-- Right Panel: Active & Recent -->
      <div class="clock-panel clock-panel-side">
        ${workforceAccess ? `
        <div class="panel-section">
          <h3>Currently Clocked In</h3>
          <div class="active-list" id="active-list">
            <div class="empty-state-small">No one clocked in</div>
          </div>
        </div>
        ` : ''}

        <!-- Record Expense entry — promoted from buried "Outside this shift"
             footer (was inside the Clock In/Out form). Lives at the top of
             the right column so it reads as a peer to clock actions, not a
             trailing afterthought. Visibility toggled by updateUI() based on
             settings.driverExpenses.enabled. See COD review 2026-05-26
             (.cod-record-expense-placement.txt) — chose A+ over building a
             new Expenses module because firestore.rules:782 blocks driver
             read of own costEntries, so a My Expenses portal needs rules
             work first. Personal Actions menu also exposes this entry. -->
        <div class="clock-record-expense-wrap panel-section hidden" id="clock-record-expense-wrap">
          <div class="record-expense-card" onclick="window.ClockModule.openRecordExpense()" id="btn-record-expense" role="button" tabindex="0">
            <div class="record-expense-icon">
              <span class="material-icons">receipt_long</span>
            </div>
            <div class="record-expense-text">
              <div class="record-expense-title">Record an Expense</div>
              <div class="record-expense-desc">Meals, tolls, parts, fuel — no shift needed</div>
            </div>
            <span class="material-icons record-expense-arrow">chevron_right</span>
          </div>
        </div>

        <!-- TASK-528 — Request Time Off shortcut. Clock is the signed-in
             member's self-service hub: clock in/out, record an expense, and
             request time off without leaving the screen. Shown when the member
             has the 'timeoff' module (owner/admin see it regardless — see
             memberHasModule). Opens the EXISTING TimeOffModule modal, which
             self-scopes + locks the person picker for non-workforce viewers
             (TASK-528 timeoff.js). Reuses the .record-expense-card markup for a
             cohesive self-service menu (green accent vs the expense card's
             amber, applied via .clock-timeoff-card). -->
        ${memberHasModule('timeoff') ? `
        <div class="clock-timeoff-card-wrap panel-section" id="clock-timeoff-card-wrap">
          <div class="record-expense-card clock-timeoff-card" onclick="window.ClockModule.openRequestTimeOff()" id="btn-request-timeoff" role="button" tabindex="0">
            <div class="record-expense-icon clock-timeoff-icon">
              <span class="material-icons">event_busy</span>
            </div>
            <div class="record-expense-text">
              <div class="record-expense-title">Request Time Off</div>
              <div class="record-expense-desc">Vacation, sick, personal — submit a request</div>
            </div>
            <span class="material-icons record-expense-arrow">chevron_right</span>
          </div>
        </div>
        ` : ''}

        <!-- Stats strip — own section so it reads as a header band, not part
             of the recent-activity card. Header renders "Your Hours" in the
             self-clock view, "{firstName}'s Hours" for admin-selected person,
             or "Hours" when no one is selected. See renderClockSummary. -->
        <div class="panel-section clock-stats-section">
          <div class="clock-stats-header" id="clock-stats-header">
            <h3>${workforceAccess ? 'Hours' : 'Your Hours'}</h3>
          </div>
          <div class="clock-summary" id="clock-summary"></div>
        </div>

        <!-- Recent Activity — flex:1 so it fills remaining height of the
             right column (matched to the left form panel via align-items:
             stretch on .clock-content). Richer empty state replaces the
             single-line "No recent activity". -->
        <div class="panel-section clock-activity-section">
          <div class="clock-history-header" id="clock-history-header">
            <h3>${workforceAccess ? 'Recent Activity' : 'Your Recent Activity'}</h3>
          </div>
          <div class="recent-list" id="recent-list">
            <div class="empty-state-recent">
              <span class="material-icons">schedule</span>
              <div class="empty-state-recent-title">No recent shifts</div>
              <div class="empty-state-recent-desc">Once you clock in or out, your activity will show up here.</div>
            </div>
          </div>
          <div id="clock-history-more"></div>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// TIME OFF TAB HTML (unchanged from original)
// ============================================
function getTimeOffTabHTML() {
  return `
    <div class="timeoff-view">
      <div class="view-header" style="padding: 0 0 var(--spacing-md) 0;">
        <div class="view-title">
          <span class="request-count" id="timeoff-requests-count">0 requests</span>
        </div>
        <div class="view-actions">
          <button class="btn btn-primary" onclick="window.ClockModule.showNewTimeOffRequest()">
            <span class="material-icons">add</span>
            Request Time Off
          </button>
        </div>
      </div>

      <div class="timeoff-filters">
        <button class="filter-btn ${ClockState.timeoffFilter === 'pending' ? 'active' : ''}" data-filter="pending" onclick="window.ClockModule.switchTimeOffFilter('pending')">
          <span class="material-icons">pending</span> Pending
        </button>
        <button class="filter-btn ${ClockState.timeoffFilter === 'approved' ? 'active' : ''}" data-filter="approved" onclick="window.ClockModule.switchTimeOffFilter('approved')">
          <span class="material-icons">check_circle</span> Approved
        </button>
        <button class="filter-btn ${ClockState.timeoffFilter === 'denied' ? 'active' : ''}" data-filter="denied" onclick="window.ClockModule.switchTimeOffFilter('denied')">
          <span class="material-icons">cancel</span> Denied
        </button>
        <button class="filter-btn ${ClockState.timeoffFilter === 'all' ? 'active' : ''}" data-filter="all" onclick="window.ClockModule.switchTimeOffFilter('all')">
          <span class="material-icons">list</span> All
        </button>
      </div>
      ${_clockCanReadAllTimeOff() ? `
      <div class="timeoff-filters timeoff-group-filters" role="group" aria-label="Group requests by">
        <span style="align-self:center;padding:0 var(--spacing-xs);font-size:0.75rem;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;color:var(--text-secondary);">Group</span>
        <button class="filter-btn ${_clockTimeOffGroup() === 'employee' ? 'active' : ''}" data-group="employee" aria-pressed="${_clockTimeOffGroup() === 'employee'}" onclick="window.ClockModule.setTimeOffGroup('employee')">
          <span class="material-icons">people</span> By employee
        </button>
        <button class="filter-btn ${_clockTimeOffGroup() === 'none' ? 'active' : ''}" data-group="none" aria-pressed="${_clockTimeOffGroup() === 'none'}" onclick="window.ClockModule.setTimeOffGroup('none')">
          <span class="material-icons">view_list</span> None
        </button>
      </div>` : ''}

      <div class="timeoff-content">
        <div class="timeoff-list" id="timeoff-list">
          <div class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading requests...</p>
          </div>
        </div>
      </div>
    </div>
  `;
}

function getTimeOffModalsHTML() {
  return `
    <div class="modal hidden" id="modal-timeoff-request">
      <div class="modal-content">
        <div class="modal-header">
          <h3 id="modal-timeoff-title">Request Time Off</h3>
          <button class="btn-icon" onclick="window.ClockModule.closeModal('timeoff-request')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-timeoff-request" novalidate onsubmit="window.ClockModule.saveTimeOffRequest(event)">
          <input type="hidden" id="timeoff-request-id">
          <div class="modal-body">
            <div class="form-group">
              <label for="timeoff-person">Employee *</label>
              <select id="timeoff-person" required>
                <option value="">Select employee...</option>
              </select>
            </div>
            <div class="form-row" style="display: flex; gap: var(--spacing-md);">
              <div class="form-group" style="flex: 1;">
                <label for="timeoff-start">Start Date *</label>
                <input type="date" id="timeoff-start" required onchange="var _e=document.getElementById('timeoff-end');if(_e&&this.value&&_e.value<this.value)_e.value=this.value;">
              </div>
              <div class="form-group" style="flex: 1;">
                <label for="timeoff-end">End Date *</label>
                <input type="date" id="timeoff-end" required>
              </div>
            </div>
            <div class="form-group">
              <label for="timeoff-type">Type *</label>
              <select id="timeoff-type" required>
                <option value="vacation">Vacation</option>
                <option value="sick">Sick Leave</option>
                <option value="personal">Personal Day</option>
                <option value="bereavement">Bereavement</option>
                <option value="jury_duty">Jury Duty</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="form-group">
              <label for="timeoff-reason">Reason/Notes</label>
              <textarea id="timeoff-reason" rows="3" placeholder="Optional notes..."></textarea>
            </div>

            <!-- My Time Off — upcoming + past history for the selected person
                 (Chris 2026-07-13: users should see historic + future accepted
                 time off wherever they request it; ported from timeoff.js).
                 Sits BELOW the form fields per Chris's layout call. -->
            <div class="collapsible-section timeoff-history-section" id="clock-timeoff-history-section" style="margin-top:var(--spacing-md);">
              <div class="collapsible-header" onclick="window.ClockModule.toggleTimeOffHistory()">
                <span class="material-icons collapsible-arrow">expand_more</span>
                <span class="collapsible-title">My Time Off</span>
              </div>
              <div class="collapsible-body" id="clock-timeoff-history-body" style="max-height:none;">
                <div style="text-align:center;padding:var(--spacing-sm);color:var(--text-secondary);font-size:0.85rem;">
                  Loading...
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" onclick="window.ClockModule.closeModal('timeoff-request')">Cancel</button>
            <button type="submit" class="btn btn-primary">Submit Request</button>
          </div>
        </form>
      </div>
    </div>

    <div class="modal hidden" id="modal-timeoff-review">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Review Request</h3>
          <button class="btn-icon" onclick="window.ClockModule.closeModal('timeoff-review')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div id="timeoff-review-content" class="review-detail modal-body"></div>
        <div class="modal-footer" id="timeoff-review-actions"></div>
      </div>
    </div>

    <div class="modal hidden" id="modal-timeoff-delete">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Withdraw Request</h3>
        </div>
        <div class="modal-body">
          <p>Withdraw this time-off request? It is deleted, and you can send a new one.</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.ClockModule.closeModal('timeoff-delete')">Keep</button>
          <button class="btn btn-danger" id="btn-confirm-delete-timeoff">Withdraw</button>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// WORKFORCE TAB HTML
// ============================================
function getWorkforceTabHTML() {
  return `
    <div class="workforce-view">
      <div class="workforce-pills" id="workforce-view-toggle">
        <button class="filter-btn ${ClockState.workforceTab === 'hours' ? 'active' : ''}" data-tab="hours" onclick="window.ClockModule.switchWorkforceTab('hours')">
          <span class="material-icons">schedule</span>
          Hours
        </button>
        <button class="filter-btn ${ClockState.workforceTab === 'reports' ? 'active' : ''}" data-tab="reports" onclick="window.ClockModule.switchWorkforceTab('reports')">
          <span class="material-icons">assessment</span>
          Reports
        </button>
        ${/* TASK-1442 — gate tightened from isClockAdmin() (any non-driver login) to
             hasWorkforceAccess(). Consistency, not a hole: switchTab() already bounces
             non-workforce viewers off the whole Workforce tab, so this block was
             already unreachable for them. hasWorkforceAccess() is a strict subset of
             isClockAdmin() (both are false in driver/public/PIN modes), so nobody who
             could see these tabs before loses them. */ ''}
        ${hasWorkforceAccess() ? `
        <button class="filter-btn ${ClockState.workforceTab === 'adjustments' ? 'active' : ''}" data-tab="adjustments" onclick="window.ClockModule.switchWorkforceTab('adjustments')">
          <span class="material-icons">tune</span>
          Adjustments
        </button>
        <button class="filter-btn ${ClockState.workforceTab === 'exceptions' ? 'active' : ''}" data-tab="exceptions" onclick="window.ClockModule.switchWorkforceTab('exceptions')">
          <span class="material-icons">rule</span>
          Exceptions
        </button>
        ${/* TASK-1483 — driver-claimed amounts review (vehicle miles today) */''}
        <button class="filter-btn ${ClockState.workforceTab === 'claims' ? 'active' : ''}" data-tab="claims" data-trailguide="workforce-claims" onclick="window.ClockModule.switchWorkforceTab('claims')">
          <span class="material-icons">fact_check</span>
          Claims
        </button>
        ` : ''}
        ${(window.PayrollModule?.isPayrollAdmin?.()) ? `
        <button class="filter-btn ${ClockState.workforceTab === 'payroll' ? 'active' : ''}" data-tab="payroll" onclick="window.ClockModule.switchWorkforceTab('payroll')">
          <span class="material-icons">payments</span>
          Payroll Prep
        </button>
        ` : ''}
      </div>
      <div id="workforce-content">
        ${getWorkforceContentHTML()}
      </div>
    </div>
  `;
}

function getWorkforceContentHTML() {
  if (ClockState.workforceTab === 'hours') return getWorkforceHoursHTML();
  if (ClockState.workforceTab === 'reports') return getWorkforceReportsHTML();
  if (ClockState.workforceTab === 'adjustments') return getWorkforceAdjustmentsHTML();
  // TASK-1442 — 'health' kept as an alias so a stale in-memory tab value (or an
  // old deep link) still lands somewhere real instead of rendering blank.
  if (ClockState.workforceTab === 'exceptions' || ClockState.workforceTab === 'health') return getExceptionsTabHTML();
  // TASK-1483 — driver-claimed amounts (workforce-class; guarded in switchWorkforceTab too)
  if (ClockState.workforceTab === 'claims') return hasWorkforceAccess() ? getClaimsTabHTML() : '';
  // TASK-1066 — Payroll mounts the payroll.js view into this container after the
  // tab switch (initPayroll is async — see switchWorkforceTab). isPayrollAdmin
  // is re-checked here so a non-admin who forces the tab gets an empty mount,
  // not the payroll UI (the view + every callable also enforce admin server-side).
  if (ClockState.workforceTab === 'payroll') {
    return window.PayrollModule?.isPayrollAdmin?.()
      ? '<div id="workforce-payroll-mount"></div>'
      : '<div style="padding:24px;color:var(--text-secondary);">Payroll needs the Workforce module (or the account owner login).</div>';
  }
  return '';
}

function getWorkforceHoursHTML() {
  const active = ClockState.activeEntries;
  const todayStart = new Date(); todayStart.setHours(0,0,0,0);

  // Completed today
  const completedToday = ClockState.recentEntries.filter(e => {
    const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
    return cin >= todayStart;
  });

  // Today's hours (active elapsed + completed)
  let todayHours = 0;
  active.forEach(e => {
    const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
    if (cin >= todayStart) todayHours += (Date.now() - cin.getTime()) / 3600000;
  });
  completedToday.forEach(e => { todayHours += e.totalHours || 0; });

  // Weekly OT calculation
  const _wkNow = getWeekRange(Date.now()); // TASK-1507 — company workweek
  const allEntries = [...ClockState.recentEntries, ...ClockState.activeEntries];
  const weeklyByPerson = {};
  allEntries.forEach(entry => {
    const cin = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    const _t = cin.getTime();
    if (_wkNow && _t >= _wkNow.startMs && _t <= _wkNow.endMs) {
      const pid = entry.personId;
      if (!weeklyByPerson[pid]) weeklyByPerson[pid] = 0;
      const hrs = entry.status === 'completed' ? (entry.totalHours || 0) : ((Date.now() - cin.getTime()) / 3600000);
      weeklyByPerson[pid] += hrs;
    }
  });
  let totalOT = 0;
  const _otThresh = getOTThreshold();
  Object.values(weeklyByPerson).forEach(h => { if (h > _otThresh) totalOT += h - _otThresh; });

  // Stat cards
  const statCards = `
    <div class="wf-stat-grid">
      <div class="wf-stat-card" style="border-left-color:var(--status-success);">
        <div class="wf-stat-label">Active Now</div>
        <div class="wf-stat-value">${active.length}</div>
      </div>
      <div class="wf-stat-card" style="border-left-color:var(--brand-blue);">
        <div class="wf-stat-label">Completed Today</div>
        <div class="wf-stat-value">${completedToday.length}</div>
      </div>
      <div class="wf-stat-card" style="border-left-color:#8B5CF6;">
        <div class="wf-stat-label">Today's Hours</div>
        <div class="wf-stat-value">${todayHours.toFixed(1)}</div>
      </div>
      <div class="wf-stat-card" style="border-left-color:${totalOT > 0 ? 'var(--status-danger)' : 'var(--status-success)'};">
        <div class="wf-stat-label">Weekly OT</div>
        <div class="wf-stat-value" style="color:${totalOT > 0 ? 'var(--status-danger)' : ''}">${totalOT.toFixed(1)}</div>
      </div>
    </div>
  `;

  // Active clocks with elapsed coloring
  // TASK-9579 — this is the list the office opens Workforce to: a person with
  // more than one open shift is flagged here, with the older one closable.
  const _wfMultiOpen = _clockMultiOpenCounts(active);
  const activeHTML = active.length === 0
    ? '<div class="empty-state-small">No one clocked in</div>'
    : _clockMultiOpenBannerHtml(_wfMultiOpen) + '<div class="wf-card-list">' + active.map(entry => {
        // TASK-364 — fall back to _historicalPeopleCache so a person who was
        // deactivated mid-shift (open clock entry survives) still gets avatar
        // color + canonical name on the Active Now card. Falls back to
        // entry.personName denormalized data when neither has the record.
        const person = getPersonForReport(entry.personId);
        const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
        const elapsedMs = Date.now() - clockIn.getTime();
        const elapsedHrs = elapsedMs / 3600000;
        const duration = formatDuration(elapsedMs);
        const elapsedColor = elapsedHrs > 10 ? 'var(--status-danger)' : elapsedHrs >= 8 ? 'var(--status-warning)' : 'var(--status-success-emphasis)';
        const avatarColor = person?.color || '#3B82F6';
        const initials = getInitials(person?.name || entry.personName);
        const metaParts = [entry.customerName, entry.jobTitle].filter(Boolean).map(s => escapeHtml(s));
        const geoPill = buildGeofencePillHTML(entry); // TASK-191 Phase 3 — out-of-zone flag
        return `
          <div class="wf-employee-card">
            <div class="wf-employee-avatar" style="background:${avatarColor};">${initials}</div>
            <div class="wf-employee-info">
              <div class="wf-employee-name">${escapeHtml(person?.name || entry.personName)}</div>
              ${metaParts.length ? `<div class="wf-employee-meta">${metaParts.join(' — ')}</div>` : ''}
              ${geoPill}
              ${_clockMultiOpenRowHtml(entry, _wfMultiOpen)}
            </div>
            <div class="wf-time-block">
              <div class="wf-time-duration" style="color:${elapsedColor};">${duration}</div>
              <div class="wf-time-detail">In: ${clockIn.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
            </div>
            <div class="wf-card-actions">
              ${(entry.clockInLat && entry.clockInLng) ? `<a class="btn-icon" href="https://www.google.com/maps?q=${+entry.clockInLat},${+entry.clockInLng}" target="_blank" rel="noopener" title="Clock-in location (±${Math.round(entry.clockInAccuracy || 0)}m)" style="color:var(--status-success);padding:2px;text-decoration:none;"><span class="material-icons" style="font-size:16px;">location_on</span></a>` : ''}
              ${_tkBtn(entry) ? `<button class="btn-icon" onclick="window.ClockModule.openTicketFromClock('${_safeTicketId(entry.ticketId)}','${_safeTicketId(entry.id)}')" title="View/Edit Ticket" style="color:var(--brand-blue);padding:2px;"><span class="material-icons" style="font-size:16px;">receipt_long</span></button>` : ''}
              ${canEditEntry(entry) ? `<button class="btn-icon" onclick="window.ClockModule.editClockEntry('${entry.id}')" title="Edit clock-in time" style="color:var(--text-secondary);padding:2px;"><span class="material-icons" style="font-size:16px;">edit</span></button>` : ''}
            </div>
          </div>
        `;
      }).join('') + '</div>';

  // Completed today
  const completedHTML = completedToday.length === 0
    ? '<div class="empty-state-small">No completed shifts today</div>'
    : '<div class="wf-card-list">' + completedToday.map(entry => {
        // TASK-364 — fall back to _historicalPeopleCache for inactive folks.
        const person = getPersonForReport(entry.personId);
        const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
        const clockOut = entry.clockOut?.toDate?.() || new Date(entry.clockOut);
        const avatarColor = person?.color || '#3B82F6';
        const initials = getInitials(person?.name || entry.personName);
        const geoPill = buildGeofencePillHTML(entry); // TASK-191 Phase 3 — out-of-zone flag
        return `
          <div class="wf-employee-card">
            <div class="wf-employee-avatar" style="background:${avatarColor};">${initials}</div>
            <div class="wf-employee-info">
              <div class="wf-employee-name">${escapeHtml(person?.name || entry.personName)}</div>
              ${entry.customerName ? `<div class="wf-employee-meta">${escapeHtml(entry.customerName)}</div>` : ''}
              ${geoPill}
            </div>
            <div class="wf-time-block">
              <div class="wf-time-duration">${entry.totalHours?.toFixed(2) || '0.00'} hrs</div>
              <div class="wf-time-detail">
                ${window.Fmt.shiftSpan(clockIn, clockOut).rangeHtml}
              </div>
            </div>
            <div class="wf-card-actions">
              ${(entry.clockInLat && entry.clockInLng) ? `<a class="btn-icon" href="https://www.google.com/maps?q=${+entry.clockInLat},${+entry.clockInLng}" target="_blank" rel="noopener" title="Clock-in location (±${Math.round(entry.clockInAccuracy || 0)}m)" style="color:var(--status-success);padding:2px;text-decoration:none;"><span class="material-icons" style="font-size:16px;">location_on</span></a>` : ''}
              ${(entry.clockOutLat && entry.clockOutLng) ? `<a class="btn-icon" href="https://www.google.com/maps?q=${+entry.clockOutLat},${+entry.clockOutLng}" target="_blank" rel="noopener" title="Clock-out location (±${Math.round(entry.clockOutAccuracy || 0)}m)" style="color:var(--status-danger);padding:2px;text-decoration:none;"><span class="material-icons" style="font-size:16px;">location_off</span></a>` : ''}
              ${_tkBtn(entry) ? `<button class="btn-icon" onclick="window.ClockModule.openTicketFromClock('${_safeTicketId(entry.ticketId)}','${_safeTicketId(entry.id)}')" title="View/Edit Ticket" style="color:var(--brand-blue);padding:2px;"><span class="material-icons" style="font-size:16px;">receipt_long</span></button>` : ''}
              ${canEditEntry(entry) ? `<button class="btn-icon" onclick="window.ClockModule.editClockEntry('${entry.id}')" title="Edit times" style="color:var(--text-secondary);padding:2px;"><span class="material-icons" style="font-size:16px;">edit</span></button>` : ''}
              ${hasWorkforceAccess() ? `<button class="btn-icon" onclick="window.ClockModule.deleteClockEntry('${entry.id}')" title="Delete entry" style="color:var(--status-danger);padding:2px;"><span class="material-icons" style="font-size:16px;">delete</span></button>` : ''}
            </div>
          </div>
        `;
      }).join('') + '</div>';

  return `
    ${statCards}
    ${_clockClosedLateHtml(ClockState.recentEntries)}
    <div style="margin-bottom:var(--spacing-lg);">
      <div class="wf-section-header">
        <span class="material-icons" style="color:var(--status-success);font-size:20px;">radio_button_checked</span>
        Currently Clocked In
        <span class="wf-section-count" style="background:var(--status-success);">${active.length}</span>
      </div>
      ${activeHTML}
    </div>
    <div style="margin-bottom:var(--spacing-lg);">
      <div class="wf-section-header">
        <span class="material-icons" style="color:var(--brand-blue);font-size:20px;">check_circle</span>
        Completed Today
        <span class="wf-section-count" style="background:var(--brand-blue);">${completedToday.length}</span>
      </div>
      ${completedHTML}
    </div>
  `;
}

// TASK-1182 — every pay-period/work-week getter below takes an OPTIONAL
// `overrides` object `{ payPeriodType, payPeriodAnchor, workWeekStartDay }` so the
// settings panel can preview UNSAVED boundaries through the SAME math the app
// runs on (single source of truth — never fork the boundary logic).
// 🔴 Omitted/undefined keys fall back to the live saved settings, so every
// existing caller (all pass no overrides) is behavior-identical.
// 🔴 Preview NEVER mutates window.AppState.settings — the override object is
// read-only input, threaded by parameter.
function getWorkWeekStartDay(overrides) {
  const o = overrides?.workWeekStartDay;
  if (Number.isInteger(o) && o >= 0 && o <= 6) return o;
  return window.AppState?.settings?.workWeekStartDay ?? 1; // 0=Sun, 1=Mon(default), 2=Tue...6=Sat
}

function getOTThreshold() {
  return window.AppState?.settings?.overtimeThreshold ?? 40;
}

// TASK-619 (Lacey) — when a tenant turns this on, a shift note is mandatory
// before a driver can clock out or switch jobs. Opt-in, default off. Enforced
// client-side at every clock-out/switch path; the server still accepts a blank
// note (this is a workflow nudge, not a rules constraint — a stuck driver must
// never be wedged out of clocking out by a rules denial, per the TASK-395 /
// clock-out-trap lessons).
function shiftNotesRequired() {
  return window.AppState?.settings?.requireShiftNotes === true;
}

// TASK-441 — single-shift "needs review" threshold. A single clock entry whose
// hours exceed this is almost certainly a forgotten clock-out (the clockOut got
// stamped to "now" hours/days later), which silently inflates the Workforce
// weekly report + payroll CSVs. We FLAG these (badge in the table, marker in the
// report/CSV) so a human reviews the punch — we NEVER auto-cap or truncate the
// hours (🔴 COD unanimous: capping a legit long shift = wage-and-hour liability).
// Default 16h ~ FMCSA on-duty cap; bounded 1–168 (legit overnight/breakdown
// hauls exist, so the badge is advisory, not a hard limit). Configurable per
// tenant via the Workforce → Reports → Pay Settings panel (TASK-922 moved it
// out of the old standalone OT Settings gear).
function getOversizedShiftThreshold() {
  const v = window.AppState?.settings?.oversizedShiftHours;
  const n = Number(v);
  if (Number.isFinite(n) && n >= 1 && n <= 168) return n;
  return 16;
}

// TASK-9794 — ONE review decision for every surface (weekly table, Reports,
// payroll CSVs, Payroll Prep). An entry needs review when it is completed and
// carries any review mark (needsReview / driverAdjustedClockOut, stamped by the
// TASK-1180 shrink flow, the stuck-shift late close, or the server's
// backdated/overlap/future flags) or exceeds the oversized threshold — UNLESS
// the office already accepted it (reviewAccepted:true, one-click Accept in
// Payroll Prep). Accept is what lets a legit 17h breakdown haul or a confirmed
// backdated entry stop shouting; hours are never changed by any of this.
function entryReviewAccepted(entry) {
  return entry?.reviewAccepted === true;
}
function entryReviewMarked(entry) {
  return !entryReviewAccepted(entry) && (entry?.needsReview === true || entry?.driverAdjustedClockOut === true);
}
function entryNeedsReview(entry, oversizedTh) {
  if (!entry || entry.status !== 'completed' || entryReviewAccepted(entry)) return false;
  const hrs = Number(entry.totalHours) || 0;
  const th = Number.isFinite(oversizedTh) ? oversizedTh : getOversizedShiftThreshold();
  return entryReviewMarked(entry) || hrs > th || entryMidnightOffJob(entry);
}
// TASK-10040 — company calendar day of an instant (falls back to the device
// day if the company zone is not loaded yet, same as TenantLocalTime itself).
function _clockCompanyDayKey(ms) {
  try {
    const k = window.TenantLocalTime?.dateKey?.(ms);
    if (k) return k;
  } catch (_) { /* fall through */ }
  return _fmtLocalYmd(new Date(ms));
}
// TASK-10040 — a completed Shop / no-job shift that ran past midnight (Willy's
// forgotten Shop clock-out, 10/15 4:26 PM → 10/16 3:05 PM). A review reason
// like the others: computed from the entry alone, so Accept's snapshot check
// sees the same reason it was shown.
function entryMidnightOffJob(entry) {
  const F = window.ShiftDayFlags;
  return !!(F && F.entryCrossesMidnightOffJob(entry, _clockCompanyDayKey));
}
// TASK-9941 F S2 — a punch written at the tablet with no PIN check (offline,
// check failed, person locked on that tablet, tablet budget spent). Carries
// needsReview:true like the other client marks; these say WHY.
function clockEntryCodeUnverified(entry) {
  const out = [];
  if (entry?.codeCheck === 'unverified') out.push('clocked in at the tablet without a PIN check');
  if (entry?.codeCheckOut === 'unverified') out.push('clocked out at the tablet without a PIN check');
  return out;
}
// The review mark on this entry came only from an unchecked tablet punch (so it
// must not be labelled "driver corrected the clock-out").
function _clockReviewIsCodeOnly(entry) {
  return clockEntryCodeUnverified(entry).length > 0 && entry?.driverAdjustedClockOut !== true;
}

// Plain-English reasons for the Needs-review list. Server flags first, then
// the client-stamped marks, then the threshold.
function entryReviewReasons(entry, oversizedTh) {
  const out = [];
  const flags = Array.isArray(entry?.reviewFlags) ? entry.reviewFlags : [];
  const d = entry?.reviewFlagDetail || {};
  if (flags.includes('backdated')) out.push(`entered ${d.backdated?.hoursLate ? Math.round(d.backdated.hoursLate / 24 * 10) / 10 + ' days' : 'long'} after the shift`);
  if (flags.includes('overlap')) out.push('overlaps another shift');
  if (flags.includes('future')) out.push(`${d.future?.field === 'clockIn' ? 'clock-in' : 'clock-out'} was in the future when saved`);
  if (entry?.closedLate === true) out.push('never clocked out — closed at the next clock-in');
  else if (entry?.driverAdjustedClockOut === true) out.push('driver corrected the clock-out');
  else if (entry?.needsReview === true && !flags.length && !clockEntryCodeUnverified(entry).length) out.push('marked for review');
  out.push(...clockEntryCodeUnverified(entry)); // TASK-9941 F S2
  const hrs = Number(entry?.totalHours) || 0;
  const th = Number.isFinite(oversizedTh) ? oversizedTh : getOversizedShiftThreshold();
  if (entry?.status === 'completed' && hrs > th) out.push(`${hrs.toFixed(1)}h shift, over the ${th}h limit`);
  if (entryMidnightOffJob(entry)) out.push('Shop / no-job shift ran past midnight — check the clock-out'); // TASK-10040
  return out;
}

// TASK-9794 (COD r2 Code Reviewer) — what the office SAW when it clicked
// Accept: the plain-English reasons, the server's raw flags, and the times /
// hours, and whose shift it is. Accept refuses if the stored entry no longer matches this, so a flag
// the server adds after the list was drawn is never accepted unseen.
// `x` is either a Payroll Prep review item ({reasons, reviewFlags, inMs,
// outMs, hours, pid}) or, via entryReviewSnapshot(), a clockEntries doc.
function clockReviewSnapshotKey(x) {
  const ms = (v) => (v == null ? null : Number(v));
  return JSON.stringify({
    reasons: (Array.isArray(x?.reasons) ? x.reasons : []).map(String).sort(),
    flags: (Array.isArray(x?.reviewFlags) ? x.reviewFlags : []).map(String).sort(),
    pid: String(x?.pid || ''), // COD r3: the row's PERSON too (a re-point between load and click)
    inMs: ms(x?.inMs), outMs: ms(x?.outMs),
    hours: Math.round((Number(x?.hours) || 0) * 100) / 100,
  });
}
function entryReviewSnapshot(entry, oversizedTh) {
  const toMs = (t) => (t == null ? null : (t.toMillis ? t.toMillis() : (t.toDate ? t.toDate().getTime() : new Date(t).getTime())));
  return {
    pid: entry?.personId || '',
    reasons: entryReviewReasons(entry, oversizedTh),
    reviewFlags: Array.isArray(entry?.reviewFlags) ? entry.reviewFlags : [],
    inMs: toMs(entry?.clockIn), outMs: toMs(entry?.clockOut),
    hours: Number(entry?.totalHours) || 0,
  };
}

// TASK-9794 — one-click Accept from Payroll Prep. Office-only by rules
// (reviewAccepted/reviewedBy/reviewedAt are frozen on the driver self arm, and
// the office arms may not accept their OWN entry — t1483OfficeReviewOk).
// ONE transaction: read the entry, check it still matches what the office was
// shown (`expected`, a Prep review item — COD r2: read-then-write let a flag the
// server added in between be accepted unseen), then write the acceptance
// (needsReview cleared, reviewAccepted stamped). A mismatch throws
// code 'review-changed' and writes nothing. Edit History + auditLog rows
// (old -> new) follow, best-effort. Hours untouched.
async function acceptClockEntryReview(tenantId, entryId, expected) {
  const tid = tenantId || window.AppState?.tenant;
  if (!tid || !entryId) throw new Error('Missing tenant or entry');
  const ref = doc(db, 'tenants', tid, 'clockEntries', entryId);
  // Separation of duties (COD Domain 2026-10-12): a Workforce holder may not
  // accept their OWN flagged shift — only the owner can (TASK-9450: not admins).
  // Enforced by firestore.rules too; this guard just gives the plain-English message.
  const _selfPid = getSelfPersonId?.();
  // TASK-9450 — the OWNER only (tenants.ownerId; Chris 2026-09-29: owner, not
  // admins). firestore.rules enforces the same for the admin role.
  const _isOwnerOrAdmin = _clockIsOwner();
  const user = window.AppState?.user || {};
  const reviewedBy = { uid: user.uid || null, email: user.email || null, name: window.AppState?.memberAccess?.personName || user.displayName || null };
  const result = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Entry not found');
    const before = { id: snap.id, ...snap.data() };
    if (before.reviewAccepted === true) return { alreadyAccepted: true, before };
    if (_selfPid && before.personId === _selfPid && !_isOwnerOrAdmin) {
      throw new Error('You cannot accept your own flagged shift — ask the account owner or another manager to review it.');
    }
    if (expected && clockReviewSnapshotKey(expected) !== clockReviewSnapshotKey(entryReviewSnapshot(before))) {
      const err = new Error('The flags on this shift changed since the list loaded — reload and review it again.');
      err.code = 'review-changed';
      throw err;
    }
    const reasons = entryReviewReasons(before);
    const patch = {
      needsReview: false,
      reviewAccepted: true,
      reviewedBy,
      reviewedAt: serverTimestamp(),
      reviewAcceptedReasons: reasons,
      updatedAt: Timestamp.now(),
    };
    tx.update(ref, patch);
    return { accepted: true, reasons, before, patch };
  });
  if (!result.accepted) return { alreadyAccepted: true };
  const { before, patch, reasons } = result;
  const after = { ...before, ...patch, reviewedAt: Timestamp.now() };
  try { await logClockEntryChange(tid, entryId, before, after, 'review-accept', 'payroll-prep'); } catch (_) { /* best-effort */ }
  try {
    await window.logAudit?.('clock', 'CLOCK_REVIEW_ACCEPT', {
      entryId, personId: before.personId || null, personName: before.personName || null,
      reasons,
      old: { needsReview: before.needsReview === true, reviewAccepted: false, reviewFlags: before.reviewFlags || [] },
      new: { needsReview: false, reviewAccepted: true, reviewFlags: before.reviewFlags || [] },
    });
  } catch (_) { /* best-effort */ }
  return { accepted: true, reasons };
}

// ── TASK-1507 / TASK-9979 — the COMPANY workweek ───────────────────────────
// The overtime week starts at 12:00 AM on the company's workweek start day
// (settings.workWeekStartDay, TITS = Monday) in the COMPANY zone
// (settings.timezone) — never the zone of the phone looking at it. Before this,
// a Pacific phone on a Central company put Monday 12:00–1:59 AM clock-ins in
// LAST week. The math lives in ONE place, window.TenantLocalTime (weekKey /
// weekRange / weekDayIndex / periodBoundsMs); payroll (payRates.js) and BI read
// the same functions, so the three cannot disagree.
//
// Two kinds of Date travel through this file — do not mix them:
//   • an INSTANT (a clock-in, Date.now()): a real moment. Week membership is
//     decided from it with getWeekKey / getWeekRange.
//   • a CALENDAR DAY (pay-period start/end, the week cursor, getWeekStart's
//     return value): midnight on the DEVICE of the company date it names, so
//     the views can format it and step it with setDate. Compare instants with
//     _companyPeriodMs(...) / getWeekRange(...).startMs, never with it.
function getWeekKey(instant, overrides) {
  return window.TenantLocalTime.weekKey(instant, getWorkWeekStartDay(overrides));
}
// { key, startMs, endMs, lastYmd } — company-zone instants (DST-safe: the
// fall-back week is 169 h, the spring-forward week 167 h).
function getWeekRange(instant, overrides) {
  return window.TenantLocalTime.weekRange(instant, getWorkWeekStartDay(overrides));
}
// 0..6 — the day of its workweek an instant falls on, on the company calendar.
function getWeekDayIndex(instant, overrides) {
  return window.TenantLocalTime.weekDayIndex(instant, getWorkWeekStartDay(overrides));
}
// Calendar day of the workweek holding INSTANT 'date' (Invalid Date if unreadable).
function getWeekStart(date, overrides) {
  const key = getWeekKey(date, overrides);
  return key ? window.TenantLocalTime.ymdCarrier(key) : new Date(NaN);
}
// Calendar day of the workweek holding CALENDAR DAY 'day' (a pay-period bound).
function getWeekStartOfDay(day, overrides) {
  const T = window.TenantLocalTime;
  const ymd = T.carrierYmd(day);
  if (!ymd) return new Date(NaN);
  // TASK-9878 — the shared rule (payPeriodHours.js pphWeekKeyOfYmd): same
  // start-day sanitising as the week buckets (a stray "abc" → 0), so the fetch
  // window and the week buckets can never disagree.
  return T.ymdCarrier(_pph().weekKeyOfYmd(ymd, getWorkWeekStartDay(overrides)));
}
// Week key of a CALENDAR DAY returned by getWeekStart / getWeekStartOfDay.
function _weekCacheKey(day) {
  return window.TenantLocalTime.carrierYmd(day);
}
// A pay period (calendar days) → company-zone instants { startMs, endMs }.
function _companyPeriodMs(periodStart, periodEnd) {
  return window.TenantLocalTime.periodBoundsMs(periodStart, periodEnd);
}
// The clock-in window covering every full workweek a period touches (the
// TASK-949 week-expanded fetch), as company-zone instants → Dates for queries.
// TASK-9878 — the window itself is the shared rule (payPeriodHours.js
// pphQueryWindow), the same one the connector's get_payroll_hours reads with.
function _companyWeekWindow(periodStart, periodEnd) {
  const T = window.TenantLocalTime;
  const w = _pph().queryWindow(T.carrierYmd(periodStart), T.carrierYmd(periodEnd), _pphCfg());
  return { start: new Date(w.startMs), end: new Date(w.endMs) };
}

// Legacy alias — some callers still use this name
function getWeekMonday(date) {
  return getWeekStart(date);
}

// TASK-335 (Lacey 2026-05-20) — local-time YYYY-MM-DD formatter. `toISOString()`
// converts to UTC, so end-of-day Central (23:59:59.999 CDT) flips to the NEXT
// calendar day UTC. That leaked into the payroll download filename (visible
// off-by-one Lacey reported) AND silently into the payAdjustments query bounds
// — adjustments dated the day AFTER the report's selected end were being
// pulled in (wider bug than the filename symptom).
//
// Use this everywhere user-facing date strings are derived from local-time-bounded
// Date objects. Cache-key sites (e.g. `getWeekMonday(...).toISOString()` for
// week-id stability) keep `toISOString()` because they only need stable identity
// across calls, not human-readable correctness.
function _fmtLocalYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// Single source of truth for payroll OT-exempt status (CSV + printable report).
// TITS policy (Chris 2026-06-03): salaried employees are EXEMPT — no overtime.
// Plus any explicit per-person FLSA flag. Used by both exportPayrollCSV and the
// printable payroll report so the two outputs never diverge on a salaried person.
function isPayrollExempt(person) {
  if (!person) return false;
  // TASK-1481 — flsaStatus / hrOvertimeExempt live in peopleComp now; resolve
  // comp-first with legacy person-doc fallback HERE so every exempt-gate
  // caller (CSV, prep, print, Employee Breakdown, driver My-Timesheet) stays
  // on one predicate. hrEmploymentType deliberately stays on the person doc
  // (readable by the person's own driver session) so a salaried person's own
  // My-Timesheet still shows exempt after the comp fields are stripped.
  const p = window.PersonComp ? window.PersonComp.effective(person) : person;
  // TASK-9394 — mca_exempt (Motor Carrier Act, FLSA §13(b)(1)) is exempt from
  // OVERTIME only: treated exactly like ot_exempt here — hours still tracked
  // and exported at the regular rate, no OT premium.
  // TASK-9878 — the predicate itself lives in functions/src/shared/payPeriodHours.js
  // (window.PayPeriodHours), shared with the Claude connector's get_payroll_hours.
  return _pph().isPayrollExempt(p);
}

// TASK-9878 — window.PayPeriodHours (GENERATED from
// functions/src/shared/payPeriodHours.js, side-effect-imported ONCE by app.js;
// read at call time, never a ?v= import — Pitfall #1). The company workweek
// (TASK-1507, also behind window.TenantLocalTime's week helpers), the
// pay-period grid, the workweek OT rule (TASK-949), the exempt predicate and
// the per-diem day rule live there ONCE, so Payroll Prep and the connector
// tool cannot disagree. A missing mirror refuses loudly (no local fallback —
// a fallback is a 2nd copy).
function _pph() {
  const P = window.PayPeriodHours;
  if (!P || typeof P.crossPeriodOT !== 'function') {
    throw new Error('Pay-period rule not loaded — reload the page.');
  }
  return P;
}
// The zone of THIS device's calendar — Payroll Prep's per-diem day key and the
// pay-period grid's "today" have always read it (the workweek does not: that is
// the company zone, _pphCfg().tz).
function _clockLocalZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch (_) { return undefined; }
}
// Settings -> the shared config. Same getters (and TASK-1182 preview overrides)
// as before; the anchor keeps getPayPeriodAnchor's "override ?? saved" rule;
// tz = the company workweek zone (TenantLocalTime.weekZone, TASK-1507).
function _pphCfg(overrides) {
  return _pph().config({
    payPeriodType: getPayPeriodType(overrides),
    payPeriodAnchor: overrides?.payPeriodAnchor ?? window.AppState?.settings?.payPeriodAnchor,
    workWeekStartDay: getWorkWeekStartDay(overrides),
    overtimeThreshold: getOTThreshold(),
  }, window.TenantLocalTime.weekZone());
}

// TASK-9394 — WHICH exemption applies, for LABELS only ('salary' | 'mca' |
// 'ot' | null). Never an OT gate — that is isPayrollExempt above. Precedence:
// salaried wins, then MCA, then the generic hourly no-OT (incl. legacy rows
// that only carry hrOvertimeExempt).
function payrollExemptKind(person) {
  if (!person) return null;
  const p = window.PersonComp ? window.PersonComp.effective(person) : person;
  if (p.hrEmploymentType === 'salary' || p.flsaStatus === 'exempt') return 'salary';
  if (p.flsaStatus === 'mca_exempt') return 'mca';
  if (p.flsaStatus === 'ot_exempt' || p.hrOvertimeExempt === true) return 'ot';
  return null;
}

// ── TASK-4628 — per-diem precedence, made VISIBLE ──────────────────────────
// TASK-939 made the HR rate authoritative: when a person has a per-diem rate
// (hrPerDiem > 0), per diem = rate x distinct local dates >= 8h, and any MANUAL
// per-diem payAdjustment for that person is DISCARDED. The manual amount is a
// fallback used ONLY when no rate is configured; the two never stack.
//
// That precedence rule is CORRECT and TASK-4628 does not touch it — summing
// would instantly raise the per diem of every affected person (measured: a
// recurring manual row is re-counted in EVERY pay period by
// loadPayAdjustmentTotals, including periods the person barely worked, so
// summing would have added $35/period each to two TITS people who are already
// paid per diem in full by the rate). What was wrong is that the discard was
// SILENT: two live TITS people carried a recurring $35 per-diem row, created
// 2026-06-08, that had never paid a cent, and no screen or export said so.
//
// This predicate is the ONE place that decides "a manual per-diem row exists
// and the HR rate is superseding it". Every surface reads it — the payroll CSV
// REVIEW column, the Payroll Prep row payload, the Adjustments tab, and the
// Add Adjustment form — so they can never disagree about which side pays.
//
// 🔴 It reports only. It changes NO dollar figure that anyone is paid.
// Returns null when there is no conflict, else { rate, manual }.
function perDiemManualSuperseded(person, manualDollars) {
  if (!person) return null;
  const p = window.PersonComp ? window.PersonComp.effective(person) : person;
  const rate = parseFloat(p && p.hrPerDiem) || 0;
  const manual = parseFloat(manualDollars) || 0;
  if (!(rate > 0) || !(manual > 0)) return null;
  return { rate, manual };
}

// ── Per-week FLSA OT — SINGLE SOURCE OF TRUTH ──────────────────────────────
// FLSA OT is computed PER WORKWEEK, never on a multi-week period total (a
// bi-weekly period of 38h + 45h = 83h would falsely yield 3h OT on the 83h
// total; the correct answer is 0 + 5 = 5h). Both the payroll CSV
// (exportPayrollCSV ~:3517) AND the driver self-view (My Timesheet ~:5827)
// MUST run their OT through THIS helper so a driver can never see different OT
// than payroll pays (= wage dispute). Do NOT re-derive OT anywhere else.
//
// Input:
//   weekTotals — { [weekKey]: totalHoursThatWeek }  (weekKey = getWeekKey(cin),
//                 the company-zone week-start date 'YYYY-MM-DD')
//   person     — for the exempt gate (isPayrollExempt: salary/flsaStatus/flag)
// Output: { exempt, periodTotal, periodReg, periodOT,
//           weeks: [{ key, total, reg, ot }] (chronological) }
// Exempt people: periodOT = 0, every week's ot = 0 (caller decides display).
function computePerWeekOT(weekTotals, person) {
  const threshold = getOTThreshold();
  const exempt = isPayrollExempt(person);
  const keys = Object.keys(weekTotals || {}).sort(); // ISO date keys sort chronologically
  let periodTotal = 0, periodReg = 0, periodOT = 0;
  const weeks = keys.map(key => {
    const total = weekTotals[key] || 0;
    const ot = exempt ? 0 : Math.max(0, total - threshold);
    const reg = total - ot;
    periodTotal += total;
    periodReg += reg;
    periodOT += ot;
    return { key, total, reg, ot };
  });
  return { exempt, periodTotal, periodReg, periodOT, weeks };
}

// Bucket raw clock entries into per-workweek total hours (week key = local
// week-start ISO date, honoring workWeekStartDay). Only COMPLETED hours count
// toward OT (matches payroll — active/open shifts have no settled totalHours).
// Returns { [weekKey]: hours }. Pairs with computePerWeekOT above.
function bucketEntriesByWeek(entries) {
  const weekTotals = {};
  (entries || []).forEach(entry => {
    if (entry.status !== 'completed') return;
    const hrs = entry.totalHours || 0;
    if (!hrs) return;
    const cin = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    const wsKey = getWeekKey(cin);
    if (!wsKey) throw new Error('Clock entry ' + (entry.id || '?') + ' has an unreadable clock-in time — fix it before running payroll.'); // TASK-1507 COD r2: loud, as before
    weekTotals[wsKey] = (weekTotals[wsKey] || 0) + hrs;
  });
  return weekTotals;
}

// ── Cross-period workweek OT — SINGLE SOURCE OF TRUTH (TASK-949) ────────────
// FLSA OT is a per-WORKWEEK property, but a workweek can straddle a pay-period
// boundary (semimonthly 1st–15th/16th–EOM, monthly). The period-naive per-week
// helper above scores a split week as two partial weeks → 0 OT. Chris's policy
// (exact):
//   1. A straddling week's FULL-week OT is paid in the pay period in which that
//      workweek ENDS (by its last calendar day).
//   2. Regular hours = the week's first-`threshold` hours, allocated
//      CHRONOLOGICALLY by day — a period's regular = the share of the week's
//      first-`threshold` that falls on THAT period's days.
//   3. So a week straddling the period START (began prior period, ENDS here) →
//      its full-week OT lands HERE; a week straddling the period END (begins
//      here, ends NEXT period) → this period pays straight-time regular only,
//      OT deferred to next period.
//   4. Cap case: if the pre-boundary portion alone exceeds `threshold`, the
//      ending period's OT can EXCEED the hours physically worked in that period
//      (the extra OT was earned on prior-period days). Regular is chronologically
//      capped at `threshold` for the week; all OT lands in the ending period; no
//      negative columns. The pay-stub note explains it.
//
// Reg allocation math (per week, relative to the CURRENT period):
//   Order the week's hours chronologically. Before-period days occupy the
//   cumulative window [0, before); in-period days occupy [before, before+inH).
//   The week's regular window is [0, threshold). The in-period regular hours =
//   overlap of [before, before+inH) with [0, threshold)
//                = max(0, min(inH, threshold - before)).
//   OT (paid only where the week ENDS) = max(0, fullTotal - threshold).
//
// Inputs:
//   entries      — ONE person's clock entries spanning the boundary weeks
//                  (period entries + the outside-period days of straddling
//                  weeks — i.e. ClockState.reportsWeekData filtered to the
//                  person). Only COMPLETED hours count toward OT (matches
//                  payroll — active/open shifts have no settled totalHours).
//   person       — exempt gate (isPayrollExempt: salary/FLSA-exempt → OT 0).
//   periodStart  — Date, inclusive start of the current pay period.
//   periodEnd    — Date, inclusive end (end-of-day) of the current pay period.
// Output: { exempt, periodTotal, periodReg, periodOT, straddleNoteApplies,
//           weeks: [{ key, fullTotal, reg, ot, endsInPeriod, beforeHours,
//                     straddle }] (chronological) }
//   periodTotal = periodReg + periodOT (PAID hours — for a straddle week this
//   can differ from the hours physically worked in-period; see the cap case).
//   straddleNoteApplies = a week ENDING in this period had prior-period hours
//   that contributed to its OT (i.e. the OT premium "includes hours earned
//   during the prior pay period"). FALSE for weekly/aligned tenants (no week
//   ever has before-period hours), which is exactly when the note must NOT show.
function computeCrossPeriodOT(entries, person, periodStart, periodEnd) {
  // TASK-9878 — the body moved (TASK-1507 semantics, unchanged) to
  // functions/src/shared/payPeriodHours.js pphCrossPeriodOT; this wrapper only
  // resolves comp (PersonComp.effective, as isPayrollExempt always did), the
  // period's company-zone instants and the tenant settings. Same output shape;
  // an unreadable completed clock-in still THROWS.
  const p = person && window.PersonComp ? window.PersonComp.effective(person) : person;
  const { startMs: pStart, endMs: pEnd } = _companyPeriodMs(periodStart, periodEnd);
  return _pph().crossPeriodOT(entries, p, pStart, pEnd, _pphCfg());
}

// ── TASK-4612 — workweek ↔ pay-period boundary derivation (PURE, DISPLAY ONLY) ──
// Brian Reid (TITS, 2026-08-25) texted that his Sunday hours "disappeared." They
// had not — they were paid. The shift-detail table's week divider clamped its
// LABEL to the pay-period end (`labelEnd = wEnd > periodEnd ? periodEnd : wEnd`),
// so the workweek that OPENS on the period's last day (Sun Aug 23, period Aug
// 10–23) printed as `Week of Aug 23 – Aug 23`. A one-day "week" reads as a bug,
// and the hours were not under Aug 16–22 either, so they looked lost.
//
// 🔴 NOTHING HERE COMPUTES OVERTIME. Every OT number below is READ OUT of a week
// row produced by computeCrossPeriodOT() — the single owner of paid REG/OT, the
// same figure behind the payroll CSV, the Employee Breakdown and My Timesheet.
// Re-deriving OT beside it is exactly what caused root causes 2 and 3: the
// divider and the per-row badge each thresholded a PERIOD-TRUNCATED running
// total, so a straddling week could show no badge when the full Sun–Sat week
// really did cross 40, and the inverse for a week whose head fell in the prior
// period. If you ever find yourself adding a `> threshold` here against hours
// you summed yourself, stop — that is the bug, not the fix.
//
// Pure + factory-shaped on purpose so tests/task4612-week-divider.unit.cjs can
// extract it from source and run it in node:vm (the _biLaborRateCoreFactory
// pattern).
function _clockWeekBoundaryCoreFactory() {
  'use strict';
  const EPS = 1e-9;

  // Describe one displayed workweek's relationship to the pay period on screen.
  //   weekStart  — Date, midnight local, from getWeekStart(entry.clockIn)
  //   otWeek     — the matching row from computeCrossPeriodOT().weeks, or null
  //                when that workweek has NO completed hours (an all-active
  //                week: the helper counts completed entries only, like payroll)
  //   exempt     — otSummary.exempt (salaried / FLSA-exempt: never any OT)
  function describeWeek({ weekStart, periodStart, periodEnd, otWeek, exempt }) {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    // computeCrossPeriodOT decides endsInPeriod on END-OF-DAY of the week's last
    // calendar day. Mirror that exactly, or a week ending ON the period end (or
    // on the next period's start date — the Edge Case Hunter's case) classifies
    // differently here than it does in payroll. Prefer the helper's own answer
    // whenever it produced a row for this week.
    const weekEndEod = new Date(weekEnd);
    weekEndEod.setHours(23, 59, 59, 999);
    const endsInPeriod = otWeek
      ? !!otWeek.endsInPeriod
      : weekEndEod.getTime() <= periodEnd.getTime();
    const startsBeforePeriod = weekStart.getTime() < periodStart.getTime();
    // Hours this workweek already banked in the PRIOR pay period. They are not
    // shown in this table (the shift rows stay period-bounded on purpose) but
    // they DO count toward this week's 40 — FLSA is per workweek, not per pay
    // period — which is why the running column has to start from them.
    const priorHrs = (otWeek && otWeek.beforeHours) || 0;
    const fullTotal = (otWeek && otWeek.fullTotal) || 0;
    // Paid OT for THIS pay period, straight from the helper. A week that has not
    // ended yet carries ot === 0 there by construction: its overtime is paid in
    // the period the week ENDS in, so claiming it here would double-count it.
    const ot = (!exempt && otWeek && endsInPeriod) ? (otWeek.ot || 0) : 0;
    return {
      weekEnd,
      endsInPeriod,
      startsBeforePeriod,
      priorHrs,
      fullTotal,
      ot,
      // "Some of this workweek is not on screen" — drives the qualifier so a
      // partial week never masquerades as a whole one.
      partial: startsBeforePeriod || !endsInPeriod,
    };
  }

  // The divider's visible text, as PARTS so a test can assert on the words
  // without parsing HTML. `fmt` formats a Date as e.g. "Aug 23".
  function dividerParts(desc, weekStart, shownHrs, fmt, threshold) {
    // 🔴 `threshold` is TENANT DATA (settings.overtimeThreshold, returned raw by
    // getOTThreshold()) and it is the ONLY non-numeric thing that reaches the
    // divider's markup and the Running cell's title ATTRIBUTE. Coerce it here,
    // at the source, so nothing downstream has to remember to escape it — every
    // other value below is a formatted Date or a toFixed() number.
    const thr = Number.isFinite(Number(threshold)) ? Number(threshold) : 40;
    const parts = {
      range: `Week of ${fmt(weekStart)} – ${fmt(desc.weekEnd)}`,
      hours: desc.partial
        ? `${shownHrs.toFixed(1)} hrs in this pay period`
        : `${shownHrs.toFixed(1)} hrs`,
      ot: desc.ot > EPS ? `${desc.ot.toFixed(1)} OT` : '',
      notes: [],
    };
    if (desc.priorHrs > EPS) {
      parts.notes.push(
        `+${desc.priorHrs.toFixed(1)} hrs earlier this week were paid last period and still count toward the ${thr}-hour week`
      );
    }
    if (!desc.endsInPeriod) {
      parts.notes.push(
        `this workweek runs through ${fmt(desc.weekEnd)} — the rest of it, and any overtime, pays next period`
      );
    }
    return parts;
  }

  // Per-row "Running" column. The running total is a WORKWEEK total, so it has
  // to start from the hours this week already banked in the prior period —
  // otherwise the badge disagrees with the OT payroll actually pays (root cause
  // 3). Exempt people never get an OT marker anywhere.
  function rowRunning(desc, inPeriodRunning, threshold, exempt) {
    const thr = Number.isFinite(Number(threshold)) ? Number(threshold) : 40;
    const total = desc.priorHrs + inPeriodRunning;
    return { total, threshold: thr, isOT: !exempt && total > thr + EPS };
  }

  return { describeWeek, dividerParts, rowRunning, EPS };
}
const _ClockWeekBoundary = _clockWeekBoundaryCoreFactory();

// Per-person cross-period OT for the CURRENT reports period. Pulls the person's
// week-expanded entries from ClockState.reportsWeekData (superset of the
// period-bounded reportsData; see loadReportsData) so straddling weeks are
// scored on their FULL-week hours. Falls back to reportsData (period-bounded)
// when the supplementary fetch is unavailable — degrades to the old
// period-naive numbers rather than throwing.
function computePersonPeriodOT(pid, person, dates) {
  if (!dates || !dates.start || !dates.end) {
    return { exempt: isPayrollExempt(person), periodTotal: 0, periodReg: 0, periodOT: 0, straddleNoteApplies: false, weeks: [] };
  }
  const src = (ClockState.reportsWeekData && ClockState.reportsWeekData.length)
    ? ClockState.reportsWeekData
    : (ClockState.reportsData || []);
  let personEntries = src.filter(e => e.personId === pid);
  // Honor the active customer filter so on-screen OT matches the customer-scoped
  // display/CSV (the employee filter is already applied via pid).
  if (ClockState.reportsCustomer && ClockState.reportsCustomer !== 'all') {
    personEntries = personEntries.filter(e => e.customerId === ClockState.reportsCustomer);
  }
  return computeCrossPeriodOT(personEntries, person, dates.start, dates.end);
}

function getWeekDayLabels() {
  const fullLabels = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const start = getWorkWeekStartDay();
  const labels = [];
  for (let i = 0; i < 7; i++) labels.push(fullLabels[(start + i) % 7]);
  return labels;
}

function formatWeekRange(weekStart) {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  return `${weekStart.toLocaleDateString('en-US', opts)} - ${weekEnd.toLocaleDateString('en-US', opts)}, ${weekEnd.getFullYear()}`;
}

function isCurrentWeek(weekStart) {
  const currentStart = getWeekStart(new Date());
  return weekStart.getTime() === currentStart.getTime();
}

// ── Pay Period helpers ─────────────────────────────
function getPayPeriodType(overrides) {
  return overrides?.payPeriodType || window.AppState?.settings?.payPeriodType || 'biweekly'; // weekly | biweekly | semimonthly | monthly
}

function getPayPeriodAnchor(overrides) {
  // Anchor date: a known pay period start date (used to align biweekly periods)
  // TASK-1182 — an override with NO anchor key falls through to the saved anchor.
  // That deliberately mirrors savePayPeriodSettings, which only writes
  // payPeriodAnchor `if (ppAnchor)` — so a blank anchor field previews exactly
  // what a save would actually produce (the stored anchor, unchanged).
  const raw = overrides?.payPeriodAnchor ?? window.AppState?.settings?.payPeriodAnchor;
  if (raw) return new Date(raw + 'T00:00:00');
  // Default: most recent Monday as anchor
  return getWeekStart(new Date(), overrides);
}

// TASK-1182 — `overrides` (optional) previews UNSAVED panel settings; omit it and
// behavior is byte-identical to before (every existing caller does).
function getPayPeriodDates(offset = 0, overrides = null) {
  // TASK-9878 — the grid (weekly from the company workweek / biweekly from the
  // anchor by CALENDAR days, TASK-1193 / semimonthly / monthly) lives in
  // functions/src/shared/payPeriodHours.js pphPayPeriod, shared with the
  // connector. "Today" is THIS device's calendar day, as before; the weekly grid
  // and the default anchor are the company workweek (TASK-1507). Same
  // calendar-day {start, end} Dates as before; an unknown period type still
  // yields { start: undefined, end: undefined }, an unreadable anchor Invalid Dates.
  const T = window.TenantLocalTime;
  const r = _pph().payPeriod(offset, _pphCfg(overrides), Date.now(), T.carrierYmd(new Date()));
  if (!r) return { start: undefined, end: undefined };
  if (r.invalid) return { start: new Date(NaN), end: new Date(NaN) };
  const start = T.ymdCarrier(r.startYmd);
  const end = T.ymdCarrier(r.endYmd);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function formatPayPeriodRange(start, end) {
  const opts = { month: 'short', day: 'numeric' };
  const yearOpts = { month: 'short', day: 'numeric', year: 'numeric' };
  if (start.getFullYear() !== end.getFullYear()) {
    return `${start.toLocaleDateString('en-US', yearOpts)} – ${end.toLocaleDateString('en-US', yearOpts)}`;
  }
  return `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', yearOpts)}`;
}

function buildWeeklyHoursTable() {
  const currentMonday = getWeekMonday(new Date());
  const monday = ClockState.workforceWeekStart || currentMonday;
  const showingCurrentWeek = isCurrentWeek(monday);

  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push(d);
  }

  // Use cached data for past weeks, live data for current week
  let entries;
  if (showingCurrentWeek) {
    entries = [...ClockState.recentEntries, ...ClockState.activeEntries];
  } else {
    const cacheKey = _weekCacheKey(monday);
    entries = ClockState.weeklyCache[cacheKey] || [];
  }
  const _gridWeekKey = _weekCacheKey(monday);

  // TASK-441 — flag (never cap) oversized single shifts that inflate the report.
  const _oversizedTh = getOversizedShiftThreshold();
  const personHours = {};
  entries.forEach(entry => {
    const pid = entry.personId;
    if (!personHours[pid]) {
      const person = ClockState.people.find(p => p.id === pid);
      // TASK-9394 (COD) — exempt gate from the SAME predicate the CSV / Gusto /
      // print use, so the grid never shows OT a payroll export pays as 0.
      // Unresolved person -> not exempt (unchanged behaviour).
      personHours[pid] = { id: pid, name: entry.personName, color: person?.color || '#3B82F6', customers: new Set(), days: [0,0,0,0,0,0,0], total: 0, oversized: false, driverAdjusted: false, maxShiftHrs: 0, customerHours: {}, exempt: isPayrollExempt(person), exemptKind: payrollExemptKind(person) };
    }
    if (entry.customerName) personHours[pid].customers.add(entry.customerName.toLowerCase());
    const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    // TASK-1507 — company workweek + company calendar day, not device midnights.
    const dayIdx = getWeekKey(clockIn) === _gridWeekKey ? getWeekDayIndex(clockIn) : -1;
    if (dayIdx >= 0) {
      const hrs = entry.status === 'completed'
        ? (entry.totalHours || 0)
        : ((Date.now() - clockIn.getTime()) / 3600000);
      personHours[pid].days[dayIdx] += hrs;
      personHours[pid].total += hrs;
      // TASK-10040 — what the office accepted vs not, per day (see
      // ShiftDayFlags.dayReviewed: accepted shifts only clear a day while the
      // rest of it is a normal day's hours).
      { const _dr = personHours[pid].dayReview || (personHours[pid].dayReview = {});
        const _d = _dr[dayIdx] || (_dr[dayIdx] = { accepted: false, openHours: 0 });
        if (entryReviewAccepted(entry)) _d.accepted = true; else _d.openHours += hrs; }
      // TASK-938 — track per-customer hours (per day + total) so the Reports tab's
      // customer filter can scope the weekly grid to just that customer's hours.
      if (entry.customerId) {
        if (!personHours[pid].customerHours[entry.customerId])
          personHours[pid].customerHours[entry.customerId] = { days: [0,0,0,0,0,0,0], total: 0 };
        personHours[pid].customerHours[entry.customerId].days[dayIdx] += hrs;
        personHours[pid].customerHours[entry.customerId].total += hrs;
      }
      // TASK-441 — single COMPLETED shift over threshold = likely forgotten
      // clock-out. FLAG it (do NOT subtract from the total — the hours still sum
      // so the report is honest; a human decides whether to correct the punch).
      // Only completed entries are flagged: an ACTIVE entry over the threshold is
      // someone currently working a long shift, not a stale punch — matches the
      // report/CSV gate (entry.status === 'completed') so all surfaces agree.
      // TASK-1180 — ALSO flag entries carrying needsReview/driverAdjustedClockOut
      // (the driver shrunk their own forgotten clock-out in the jobboard flow):
      // a shrunk-to-normal entry no longer exceeds the threshold, so without
      // this it would VANISH from review the moment the driver corrected it.
      const _entryDriverAdjusted = entryReviewMarked(entry); // TASK-9794 — false once the office accepted it
      if (entryNeedsReview(entry, _oversizedTh)) {
        personHours[pid].oversized = true;
        if (hrs > personHours[pid].maxShiftHrs) personHours[pid].maxShiftHrs = hrs;
        // stuck-shift-morning — a shift closed late at the next clock-in
        // (closedLate) carries needsReview too; say WHICH it was, not "driver
        // corrected their clock-out".
        if (entry.closedLate === true) personHours[pid].closedLate = (personHours[pid].closedLate || 0) + 1;
        else if (_entryDriverAdjusted && _clockReviewIsCodeOnly(entry)) personHours[pid].codeUnchecked = (personHours[pid].codeUnchecked || 0) + 1; // TASK-9941 F S2
        else if (_entryDriverAdjusted) personHours[pid].driverAdjusted = true;
        if (entryMidnightOffJob(entry)) personHours[pid].midnightOffJob = (personHours[pid].midnightOffJob || 0) + 1; // TASK-10040
      }
    }
  });

  // TASK-10040 — any single DAY over 24 h. The grid books a whole shift on its
  // clock-in day, so a forgotten clock-out piles onto that day (Willy's Thu read
  // 30.7). Flag only; the hours stay summed exactly as shown.
  if (window.ShiftDayFlags) {
    Object.values(personHours).forEach(p => {
      const over = window.ShiftDayFlags.daysOverLimit(
        Object.assign({}, p.days), window.ShiftDayFlags.DAY_LIMIT_HOURS,
        (k) => window.ShiftDayFlags.dayReviewed(p.dayReview && p.dayReview[k]));
      if (over.length) {
        p.daysOver24 = {};
        over.forEach(d => { p.daysOver24[d.key] = d.hours; });
        p.oversized = true;
      }
    });
  }

  // TASK-9579 — more than one open shift at once: the older one's running hours
  // are already in this week's total above, so payroll must see it before it
  // pays them. Flag only (the hours stay summed, exactly like TASK-441).
  {
    // COD Domain — also on a past week: an older open shift that started last
    // week inflates THAT week's total, which is the one payroll is closing.
    const _multi = _clockMultiOpenCounts(ClockState.activeEntries);
    Object.keys(_multi).forEach(pid => {
      if (personHours[pid]) { personHours[pid].multiOpen = _multi[pid]; personHours[pid].oversized = true; }
    });
  }

  const allPeople = Object.values(personHours).sort((a, b) => a.name.localeCompare(b.name));

  // Apply search filter (matches employee name OR customer name)
  const searchTerm = (ClockState.workforceSearch || '').toLowerCase().trim();
  let people = searchTerm
    ? allPeople.filter(p => p.name.toLowerCase().includes(searchTerm) || [...p.customers].some(c => c.includes(searchTerm)))
    : allPeople;

  // TASK-938 — on the REPORTS tab, also honor the employee/customer filter so the
  // weekly grid matches the breakdown cards. Gated to the reports tab so the
  // standalone 'hours' tab can't silently inherit a stale Reports filter.
  if (ClockState.workforceTab === 'reports') {
    if (ClockState.reportsEmployee && ClockState.reportsEmployee !== 'all')
      people = people.filter(p => p.id === ClockState.reportsEmployee);
    if (ClockState.reportsCustomer && ClockState.reportsCustomer !== 'all')
      people = people.filter(p => !!(p.customerHours && p.customerHours[ClockState.reportsCustomer]));
  }

  // TASK-938 — when a customer filter is active, day cells / Reg-OT / totals / stats
  // MUST use THAT customer's hours, not the person's all-customer total (else the
  // Reg+OT split wouldn't sum to the displayed Total). Employee-only filter still
  // shows the person's full hours (no customer scoping).
  const _reportsCustId = (ClockState.workforceTab === 'reports' && ClockState.reportsCustomer && ClockState.reportsCustomer !== 'all')
    ? ClockState.reportsCustomer : null;
  const getDisplayHours = (p) => _reportsCustId
    ? (p.customerHours[_reportsCustId] || { days: [0, 0, 0, 0, 0, 0, 0], total: 0 })
    : { days: p.days, total: p.total };

  // Detect today's day index (0=Mon..6=Sun) relative to displayed week
  const today = new Date();
  const todayIdx = days.findIndex(d => d.toDateString() === today.toDateString());

  const dayLabels = getWeekDayLabels();
  // Chris 10/23 (TASK-10034 lane) — the date in small text above each day label.
  // `days` are company calendar-day carriers, so carrierYmd is the company date.
  const _whMd = (dt) => (window.TenantLocalTime?.carrierYmd?.(dt) || '').slice(5).replace(/^0?(\d+)-0?(\d+)$/, '$1/$2');
  const headerCells = dayLabels.map((d, i) =>
    `<th class="${i === todayIdx ? 'wh-today' : ''}"><small style="display:block;font-weight:400;opacity:.75">${_whMd(days[i])}</small>${d}</th>`
  ).join('');

  // Search input
  const searchInput = `
    <div class="wh-search-wrap">
      <span class="material-icons wh-search-icon">search</span>
      <input type="search" class="wh-search-input" placeholder="Search by name or customer..."
        value="${escapeHtml(ClockState.workforceSearch || '')}" autocomplete="off"
        oninput="window.ClockModule.filterWorkforceSearch(this.value)" />
    </div>
  `;

  // Week nav — Prev (left) | date range (center) | Next (right) per Chris 2026-05-07
  const weekNav = `
    <div class="weekly-hours-nav" style="display:flex;align-items:center;justify-content:center;gap:var(--spacing-md);">
      <button class="btn btn-secondary btn-sm" onclick="window.ClockModule.navigateWorkforceWeek(-1)" style="display:flex;align-items:center;gap:2px;flex-shrink:0;">
        <span class="material-icons" style="font-size:16px;">chevron_left</span> Prev
      </button>
      <div class="weekly-hours-nav-label" style="text-align:center;flex:0 0 auto;">
        ${formatWeekRange(monday)}${showingCurrentWeek ? ' <span style="color:var(--text-secondary);font-weight:400;">(Current)</span>' : ''}
      </div>
      <button class="btn btn-secondary btn-sm" onclick="window.ClockModule.navigateWorkforceWeek(1)" ${showingCurrentWeek ? 'disabled' : ''} style="display:flex;align-items:center;gap:2px;flex-shrink:0;${showingCurrentWeek ? 'opacity:0.4;' : ''}">
        Next <span class="material-icons" style="font-size:16px;">chevron_right</span>
      </button>
    </div>
  `;

  // Summary stats (based on filtered results)
  const _otTh = getOTThreshold();
  const totalHours = people.reduce((s, p) => s + getDisplayHours(p).total, 0);
  const withOT = people.filter(p => !p.exempt && getDisplayHours(p).total > _otTh).length;
  const statsBar = `
    <div class="wh-stats-bar">
      <span class="wh-stat-pill"><strong>${people.length}</strong> Employee${people.length !== 1 ? 's' : ''}</span>
      <span class="wh-stat-pill"><strong>${totalHours.toFixed(1)}</strong> Total Hrs</span>
      <span class="wh-stat-pill ${withOT > 0 ? 'wh-stat-ot' : ''}"><strong>${withOT}</strong> with OT</span>
    </div>
  `;

  if (allPeople.length === 0) {
    return searchInput + weekNav + statsBar + `
      <div style="overflow-x:auto;">
        <table class="weekly-hours-table">
          <thead><tr><th style="text-align:left;">Employee</th>${headerCells}<th>Reg</th><th>OT</th><th>Total</th></tr></thead>
          <tbody></tbody>
        </table>
      </div>`;
  }

  const rows = people.map(p => {
    const _dh = getDisplayHours(p);
    const ot = p.exempt ? 0 : Math.max(0, _dh.total - _otTh);
    const reg = _dh.total - ot;
    // TASK-9394 (COD) — exempt marker so "OT -" on a 60h row reads as a
    // classification, not a missing number.
    const _exemptTitle = p.exemptKind === 'mca' ? 'Motor Carrier Act exempt — hours tracked, no OT premium'
      : p.exemptKind === 'salary' ? 'Salaried — overtime exempt'
      : 'Hourly — overtime exempt (hours tracked, no OT premium)';
    const exemptBadge = p.exempt
      ? `<span class="wh-exempt-badge" title="${_exemptTitle}" style="margin-left:6px;font-size:0.68rem;font-weight:600;color:var(--text-secondary);border:1px solid var(--border-light);border-radius:4px;padding:0 4px;white-space:nowrap;">${p.exemptKind === 'mca' ? 'OT exempt (MCA)' : 'OT exempt'}</span>`
      : '';
    // TASK-10040 — a day over 24 h is marked on its own cell (red, with why).
    // Whole-day hours, so not shown on a customer-filtered grid (a filtered
    // 8 h cell must never read "over 24").
    const _over24 = (!_reportsCustId && p.daysOver24) || {};
    const dayCells = _dh.days.map((h, i) => {
      const _o = _over24[i] != null;
      const _cls = [i === todayIdx ? 'wh-today' : '', _o ? 'wh-day-over24' : ''].filter(Boolean).join(' ');
      const _tt = _o ? ` title="${_over24[i].toFixed(1)} hours on one day — a shift counts on the day it started, so a forgotten clock-out piles up here. Check the clock-out before payroll."` : '';
      const _st = _o ? ' style="color:#B91C1C;font-weight:700;background:rgba(239,68,68,0.10);"' : '';
      return `<td class="${_cls}"${_tt}${_st}>${h > 0 ? h.toFixed(1) : '-'}${_o ? ' ⚠️' : ''}</td>`;
    }).join('');
    // TASK-441 — bold red "Needs Review" badge when any single shift this week
    // exceeds the oversized threshold (likely a forgotten clock-out). Hours are
    // still summed below — the badge prompts a human to correct the punch, it
    // does NOT alter the total.
    const _whyFlagged = [];
    if (p.maxShiftHrs > _oversizedTh) _whyFlagged.push(`A single shift this week is ${p.maxShiftHrs.toFixed(1)}h (over the ${_oversizedTh}h review threshold) — likely a forgotten clock-out.`);
    if (p.driverAdjusted) _whyFlagged.push('A driver corrected their own clock-out on a shift this week (forgotten-clock-out fix) — check the entry\'s adjusted time and reason.');
    if (p.codeUnchecked) _whyFlagged.push(`${p.codeUnchecked === 1 ? 'A punch' : p.codeUnchecked + ' punches'} this week went through at the shared tablet without a PIN check (offline, too many tries, or the check failed). Confirm it was really this person.`);
    if (p.closedLate) _whyFlagged.push(`${p.closedLate === 1 ? 'A shift' : p.closedLate + ' shifts'} this week never clocked out and ${p.closedLate === 1 ? 'was' : 'were'} closed late — the finish time was entered at the next clock-in. Verify it before payroll; the job's ticket may still need its hours.`);
    { const _dk = Object.keys(_over24); if (_dk.length) _whyFlagged.push(`${_dk.length === 1 ? 'A day' : _dk.length + ' days'} this week ${_dk.length === 1 ? 'has' : 'have'} more than 24 hours on ${_dk.length === 1 ? 'it' : 'them'} (${_dk.map(k => dayLabels[k] + ' ' + _over24[k].toFixed(1) + 'h').join(', ')}) — a shift counts on the day it started, so this is almost always a forgotten clock-out.`); } // TASK-10040
    if (p.midnightOffJob) _whyFlagged.push(`${p.midnightOffJob === 1 ? 'A Shop / no-job shift' : p.midnightOffJob + ' Shop / no-job shifts'} this week ran past midnight — likely a forgotten clock-out.`); // TASK-10040
    if (p.multiOpen) _whyFlagged.push(`${p.multiOpen} open shifts at once — only one can be real, and both are still adding hours. Close the wrong one from Workforce → Hours → Currently Clocked In (Close older shift / Close newest shift).`);
    const reviewBadge = p.oversized
      ? `<span class="wh-needs-review-badge" title="${_whyFlagged.join(' ')} Open the employee, find the flagged entry, and verify the clock-out time before running payroll.">⚠️ Needs Review</span>`
      : '';
    return `
      <tr${p.oversized ? ' class="wh-row-needs-review"' : ''}>
        <td class="wh-name-cell"><span class="wf-name-cell-inner" onclick="window.ClockModule.showEmployeeDetail('${p.id}')"><span class="wf-table-avatar" style="background:${p.color};">${getInitials(p.name)}</span>${escapeHtml(p.name)}${exemptBadge}${reviewBadge}</span></td>
        ${dayCells}
        <td class="wh-reg-cell">${reg > 0 ? reg.toFixed(1) : '-'}</td>
        <td class="${ot > 0 ? 'ot-cell wh-ot-bg' : 'wh-zero-ot'}">${ot > 0 ? ot.toFixed(1) : '-'}</td>
        <td class="wh-total-cell${p.oversized ? ' wh-total-needs-review' : ''}" style="font-weight:600;">${_dh.total > 0 ? _dh.total.toFixed(1) : '-'}</td>
      </tr>
    `;
  }).join('');

  // Totals row
  const dayTotals = [0,0,0,0,0,0,0];
  let totalReg = 0, totalOT = 0;
  people.forEach(p => {
    const _dh = getDisplayHours(p);
    const ot = p.exempt ? 0 : Math.max(0, _dh.total - _otTh);
    const reg = _dh.total - ot;
    totalReg += reg;
    totalOT += ot;
    _dh.days.forEach((h, i) => { dayTotals[i] += h; });
  });
  const totalDayCells = dayTotals.map((h, i) =>
    `<td class="wh-totals-cell ${i === todayIdx ? 'wh-today' : ''}">${h > 0 ? h.toFixed(1) : '-'}</td>`
  ).join('');

  // TASK-938 — a reports-only filter (employee/customer set, no search text) also
  // yields zero rows; show the empty message for it too, not just a text search.
  const _anyFilter = searchTerm || (ClockState.workforceTab === 'reports' &&
    ((ClockState.reportsEmployee && ClockState.reportsEmployee !== 'all') || (ClockState.reportsCustomer && ClockState.reportsCustomer !== 'all')));
  const noResults = people.length === 0 && _anyFilter
    ? `<tr><td colspan="11" style="text-align:center;padding:16px;color:var(--text-secondary);">${searchTerm ? `No employees match "${escapeHtml(searchTerm)}"` : 'No hours match the current filter'}</td></tr>`
    : '';

  // TASK-922 — OT settings moved into the Reports tab's Pay Period settings
  // panel (one place, explicit Save). Only the Add Hours button remains here.
  const addHoursBtn = `
    <div style="display:flex;justify-content:flex-end;gap:8px;margin-bottom:var(--spacing-sm);align-items:center;">
      ${isClockAdmin() ? `<button onclick="window.ClockModule.showAddHoursPersonPicker()" style="display:flex;align-items:center;gap:4px;font-size:0.8rem;padding:5px 12px;border:1px dashed var(--brand-blue);color:var(--brand-blue);background:transparent;border-radius:6px;cursor:pointer;font-weight:500;" onmouseenter="this.style.background='rgba(59,130,246,0.06)'" onmouseleave="this.style.background='transparent'">
        <span class="material-icons" style="font-size:16px;">person_add</span> Add Hours
      </button>` : ''}
    </div>`;

  return `
    ${searchInput}
    ${weekNav}
    ${statsBar}
    ${addHoursBtn}
    <div style="overflow-x:auto;">
      <table class="weekly-hours-table">
        <thead>
          <tr>
            <th style="text-align:left;">Employee</th>
            ${headerCells}
            <th>Reg</th>
            <th>OT</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>${rows || noResults}</tbody>
        <tfoot>
          <tr class="wh-totals-row">
            <td class="wh-totals-cell" style="text-align:left;">Total</td>
            ${totalDayCells}
            <td class="wh-totals-cell">${totalReg > 0 ? totalReg.toFixed(1) : '-'}</td>
            <td class="wh-totals-cell ${totalOT > 0 ? 'wh-ot-bg' : ''}">${totalOT > 0 ? totalOT.toFixed(1) : '-'}</td>
            <td class="wh-totals-cell" style="font-weight:700;">${(totalReg + totalOT) > 0 ? (totalReg + totalOT).toFixed(1) : '-'}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  `;
}

// Load pay period adjustments and inject flags into employee name cells
async function loadAdjustmentFlags() {
  if (!ClockState.tenantId) return;
  try {
    const ppDates = getPayPeriodDates(0); // current pay period
    const startStr = _fmtLocalYmd(ppDates.start);
    const endStr = _fmtLocalYmd(ppDates.end);

    // Load payAdjustments (in-period + recurring from any period)
    const adjSnap = await getDocs(collection(db, 'tenants', ClockState.tenantId, 'payAdjustments'));
    const byPerson = {}; // { personId: { additions: true, deductions: true } }
    adjSnap.docs.forEach(d => {
      const data = d.data();
      const isRecurring = data.recurring === true;
      if (!isRecurring) {
        const dt = data.date?.toDate?.() || (data.date ? new Date(data.date) : null);
        if (!dt) return;
        const dtStr = _fmtLocalYmd(dt);
        if (dtStr < startStr || dtStr > endStr) return;
      }
      const pid = data.personId;
      if (!byPerson[pid]) byPerson[pid] = { additions: false, deductions: false };
      const t = (data.type || '').toLowerCase();
      if (t === 'deduction') byPerson[pid].deductions = true;
      else byPerson[pid].additions = true;
    });

    // Also check legacy perDiemEntries
    const pdSnap = await getDocs(
      query(
        collection(db, 'tenants', ClockState.tenantId, 'perDiemEntries'),
        where('date', '>=', startStr),
        where('date', '<=', endStr)
      )
    );
    pdSnap.docs.forEach(d => {
      const pid = d.data().personId;
      if (!byPerson[pid]) byPerson[pid] = { additions: false, deductions: false };
      byPerson[pid].additions = true;
    });

    // Inject badges into name cells
    document.querySelectorAll('.wf-name-cell-inner').forEach(cell => {
      const onclick = cell.getAttribute('onclick') || '';
      const match = onclick.match(/showEmployeeDetail\('([^']+)'\)/);
      if (!match) return;
      const pid = match[1];
      const flags = byPerson[pid];
      if (!flags) return;
      // Remove existing flags
      cell.querySelectorAll('.adj-flag').forEach(f => f.remove());
      if (flags.additions) {
        cell.insertAdjacentHTML('beforeend', '<span class="adj-flag" style="display:inline-flex;align-items:center;margin-left:4px;font-size:0.6rem;font-weight:700;color:#059669;background:#D1FAE5;padding:1px 4px;border-radius:3px;vertical-align:middle;">$+</span>');
      }
      if (flags.deductions) {
        cell.insertAdjacentHTML('beforeend', '<span class="adj-flag" style="display:inline-flex;align-items:center;margin-left:4px;font-size:0.6rem;font-weight:700;color:#dc2626;background:#FEE2E2;padding:1px 4px;border-radius:3px;vertical-align:middle;">$−</span>');
      }
    });
  } catch (err) {
    console.error('[Clock] Error loading adjustment flags:', err);
  }
}

async function navigateWorkforceWeek(direction) {
  const currentMonday = getWeekMonday(new Date());
  const current = ClockState.workforceWeekStart || currentMonday;
  const newMonday = new Date(current);
  newMonday.setDate(current.getDate() + (direction * 7));

  // Don't go past current week
  if (newMonday > currentMonday) return;

  ClockState.workforceWeekStart = newMonday;

  // Load data if not current week and not cached
  if (!isCurrentWeek(newMonday)) {
    const cacheKey = _weekCacheKey(newMonday);
    // TASK-1181 — test the fully-loaded flag, NOT bare key existence. A partial
    // cache seeded by showEmployeeDetail (one person's entries) used to satisfy
    // `!weeklyCache[cacheKey]` and skip this authoritative full-week query, so
    // the grid rendered a single employee for the week.
    if (!ClockState.weeklyCacheFullyLoaded[cacheKey]) {
      await loadWeekData(newMonday);
    }
  }

  // TASK-1314 — REMOVED the reports-period stomp. This used to set
  // reportsPeriod='custom' + reportsCustomStart/End to the single viewed week
  // (Chris 2026-05-07 "export should follow the week i'm viewing"), but that
  // silently overwrote a period the user explicitly chose on the SEPARATE
  // Reports tab — so a payroll/customer-billing export taken after any Weekly
  // Hours ◀/▶ click shipped ~one week instead of the intended range (live TITS:
  // a July report collapsed to Jul 26–Aug 1). The intent is now vestigial: the
  // Reports period defaults to 'payperiod' (a payroll report must span the full
  // pay period, see reportsPeriod default), and Export/Print live only on the
  // Reports tab. Weekly Hours nav now moves ONLY its own workforceWeekStart
  // cursor + weeklyCache grid; the Reports period stays user-controlled. (COD
  // 2026-08-04: Option 1, unanimous — a viewed-week export, if ever wanted, is a
  // separate "Export This Week" button on the Hours tab, not a shared-state sync.)

  // Re-render workforce content
  const content = document.getElementById('workforce-content');
  if (content) content.innerHTML = getWorkforceContentHTML();
  loadAdjustmentFlags();
}

function filterWorkforceSearch(value) {
  ClockState.workforceSearch = value;
  // Re-render just the weekly hours section
  const content = document.getElementById('workforce-content');
  if (content) content.innerHTML = getWorkforceContentHTML();
  loadAdjustmentFlags();
  // Restore focus to search input and cursor position
  const input = document.querySelector('.wh-search-input');
  if (input) {
    input.focus();
    input.setSelectionRange(value.length, value.length);
  }
}

// TASK-922 — saveOTSettings removed: the Workforce Hours "OT Settings" gear
// panel moved into the Reports tab's Pay Period settings block, which persists
// all four values (incl. oversizedShiftHours) via savePayPeriodSettings().

async function loadWeekData(weekStart) {
  const cacheKey = _weekCacheKey(weekStart);
  // TASK-1507 — the company workweek's instants (was device midnight → +7 days, inclusive).
  const _wr = window.TenantLocalTime.weekRangeOfKey(cacheKey);

  try {
    const q = query(
      collection(db, 'tenants', ClockState.tenantId, 'clockEntries'),
      where('clockIn', '>=', Timestamp.fromDate(new Date(_wr.startMs))),
      where('clockIn', '<=', Timestamp.fromDate(new Date(_wr.endMs))),
      orderBy('clockIn', 'desc')
    );
    const snap = await getDocs(q);
    // TASK-1181 — this query IS the authoritative week (same tenant, same
    // clockIn range as the grid renders), so its result REPLACES the cache.
    //
    // An earlier cut merged the fetched docs over the seeded ones to preserve
    // entry refs, but COD caught that preserving "seeded-only" ids is exactly
    // how stale rows become immortal: once fullyLoaded is set nothing re-queries,
    // so an entry deleted by another session — or the optimistic `temp-<ts>` row
    // that saveManualClockEntry pushes (whose id can NEVER match a real Firestore
    // id) — would render forever as a phantom/duplicate. Overwriting lets both
    // resolve themselves on the next load: mutators look entries up by id, and
    // the ids that matter all come back in this query.
    ClockState.weeklyCache[cacheKey] = snap.docs.map(d => ({ id: d.id, ...d.data() }));

    // Success arm ONLY — see the error arm below.
    ClockState.weeklyCacheFullyLoaded[cacheKey] = true;
  } catch (err) {
    console.error('[Clock] Error loading week data:', err);
    // TASK-1181 — do NOT mark fullyLoaded here: a failed fetch must stay
    // retryable, otherwise the next navigation skips the query forever and the
    // grid is empty until a settings save (the empty-grid-forever variant of
    // the bug this task fixes). Also do NOT clobber a seeded cache with [] —
    // that would destroy the entry refs edit/delete depend on. Only initialise
    // the key when nothing is there, so the grid renders empty rather than
    // throwing.
    if (!ClockState.weeklyCache[cacheKey]) ClockState.weeklyCache[cacheKey] = [];
  }
}

async function showEmployeeDetail(personId, resetOffset = true) {
  // TASK-360 — workforce admin surface. Viewing arbitrary employee details
  // (weekly hours, adjustments, etc.) is workforce-only. Clock-only users
  // see their own activity in the Clock tab Recent Activity feed.
  if (!_assertWorkforceAccess('showEmployeeDetail')) return;
  let person = ClockState.people.find(p => p.id === personId);
  if (!person) {
    // Fallback: fetch from Firestore (terminated/inactive people).
    // TASK-364 — previously pushed into ClockState.people, which polluted
    // global active-only state (dispatch picker / time-off dropdown /
    // clock-in target started showing inactive folks after any admin
    // opened the detail modal). Cache in _historicalPeopleCache instead
    // so live views stay clean. Child functions (Add Entry, Per-Diem)
    // should resolve via getPersonForReport() if they need the inactive
    // person's profile fields.
    person = ClockState._historicalPeopleCache.get(personId) || null;
    if (!person) {
      try {
        const snap = await getDoc(doc(db, 'tenants', ClockState.tenantId, 'people', personId));
        if (!snap.exists()) return;
        person = { id: snap.id, ...snap.data() };
        ClockState._historicalPeopleCache.set(person.id, person);
      } catch (e) { console.error('[Clock] Failed to fetch person:', e); return; }
    }
  }

  if (resetOffset) ClockState.detailPayPeriodOffset = 0;
  ClockState.detailPersonId = personId;

  const ppDates = getPayPeriodDates(ClockState.detailPayPeriodOffset);
  const periodStart = ppDates.start;
  const periodEnd = ppDates.end;
  const periodEndExclusive = new Date(periodEnd); periodEndExclusive.setDate(periodEnd.getDate() + 1);
  const isCurrent = ClockState.detailPayPeriodOffset === 0;
  const ppType = getPayPeriodType();
  const ppTypeLabel = ppType === 'biweekly' ? 'Bi-Weekly' : ppType === 'semimonthly' ? 'Semi-Monthly' : ppType.charAt(0).toUpperCase() + ppType.slice(1);

  // Fetch entries directly from Firestore. TASK-956 — widen the window to the
  // boundary weeks [weekStart(periodStart) .. weekEnd(periodEnd)] (same shape as
  // loadReportsData / the driver My-Timesheet) so a workweek straddling a pay-
  // period boundary is scored on its FULL-week hours. `personEntries` (the shift
  // table + live indicator) stays period-bounded; `weekEntries` (the wider set)
  // feeds the cross-period OT helper only, so this modal's paid REG/OT can never
  // disagree with the payroll CSV.
  let personEntries = [];
  let weekEntries = [];
  try {
    // TASK-1507 — company-zone workweeks around the period.
    const { start: winStart, end: winEnd } = _companyWeekWindow(periodStart, periodEnd);
    const q = query(
      collection(db, 'tenants', ClockState.tenantId, 'clockEntries'),
      where('personId', '==', personId),
      where('clockIn', '>=', Timestamp.fromDate(winStart)),
      where('clockIn', '<=', Timestamp.fromDate(winEnd)),
      orderBy('clockIn', 'desc')
    );
    const snap = await getDocs(q);
    weekEntries = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // Period-bounded display set (identical to the old direct [start,end] query).
    const { startMs: _sMs, endMs: _eMs } = _companyPeriodMs(periodStart, periodEnd); // TASK-1507
    personEntries = weekEntries
      .filter(e => {
        const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
        const t = cin.getTime();
        return t >= _sMs && t <= _eMs;
      })
      .sort((a, b) => {
        const aIn = a.clockIn?.toDate?.() || new Date(a.clockIn);
        const bIn = b.clockIn?.toDate?.() || new Date(b.clockIn);
        return aIn - bIn; // ASC for the shift table
      });
    // Merge any active entries for current period into BOTH sets. The OT helper
    // ignores non-completed entries, so active hours never inflate paid OT — they
    // only surface in the period-bounded live indicator below.
    if (isCurrent) {
      const activeForPerson = ClockState.activeEntries.filter(e => e.personId === personId);
      activeForPerson.forEach(ae => {
        if (!personEntries.find(pe => pe.id === ae.id)) personEntries.push(ae);
        if (!weekEntries.find(pe => pe.id === ae.id)) weekEntries.push(ae);
      });
      personEntries.sort((a, b) => {
        const aIn = a.clockIn?.toDate?.() || new Date(a.clockIn);
        const bIn = b.clockIn?.toDate?.() || new Date(b.clockIn);
        return aIn - bIn;
      });
    }
  } catch (err) {
    console.error('[Clock] Error loading pay period entries:', err);
  }

  // Store fetched entries so edit/delete can find them
  personEntries.forEach(e => {
    if (!ClockState.recentEntries.find(r => r.id === e.id) && !ClockState.activeEntries.find(a => a.id === e.id)) {
      // Add to weekly cache so editClockEntry/deleteClockEntry can find them
      const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
      const cacheKey = getWeekKey(cin);
      if (!cacheKey) return;
      if (!ClockState.weeklyCache[cacheKey]) ClockState.weeklyCache[cacheKey] = [];
      if (!ClockState.weeklyCache[cacheKey].find(c => c.id === e.id)) {
        ClockState.weeklyCache[cacheKey].push(e);
      }
    }
  });

  // OT threshold retained for the per-shift Running column display below.
  const _otThreshold = getOTThreshold();

  // TASK-956 — paid REG/OT via the SHARED cross-period helper on the WEEK-EXPANDED
  // COMPLETED entries (matches the payroll CSV, on-screen Employee Breakdown, and
  // driver My-Timesheet). Active/in-progress shifts are NEVER folded into the FLSA
  // OT number — the helper counts completed hours only, exactly like payroll — so
  // they surface as a separate live indicator instead. A straddling workweek's full
  // OT lands in the period it ENDS.
  const otSummary = computeCrossPeriodOT(weekEntries, person, periodStart, periodEnd);
  const totalReg = otSummary.periodReg;
  const totalOT = otSummary.periodOT;
  // TASK-4612 — index the helper's per-week rows by the SAME key the shift table
  // groups on (`getWeekKey(clockIn)`, identical on
  // both sides) so the week divider and the per-row Running badge can READ the
  // authoritative OT instead of re-thresholding a period-truncated sum. A week
  // with only ACTIVE entries has no row here — the helper is completed-only, like
  // payroll — and correctly falls back to "no paid OT yet".
  const _otWeekByKey = {};
  (otSummary.weeks || []).forEach(w => { _otWeekByKey[w.key] = w; });
  const _payExempt = !!otSummary.exempt;

  // Live/in-progress hours in the CURRENT period — a separate running indicator,
  // deliberately EXCLUDED from the paid REG/OT/TOTAL above (which is completed-only,
  // like the CSV). Never changes the FLSA OT figure.
  let liveActiveHrs = 0;
  personEntries.forEach(entry => {
    if (entry.status === 'completed') return;
    const cin = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    liveActiveHrs += (Date.now() - cin.getTime()) / 3600000;
  });

  // Build shift rows with per-week running total.
  // TASK-485 — ALWAYS build the per-entry strings CHRONOLOGICALLY so each row's
  // running total + OT flag accumulate in the correct order, then flip ONLY the
  // finished per-entry strings for display when newest-on-top is selected.
  const weekRunningAccum = {}; // track running total within each week
  let shiftRows;
  if (personEntries.length === 0) {
    shiftRows = '<tr><td colspan="6" style="text-align:center;padding:var(--spacing-md);color:var(--text-secondary);">No shifts this period</td></tr>';
  } else {
    const entryRowData = personEntries.map(entry => {
        const cin = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
        const cout = entry.clockOut?.toDate?.() ? entry.clockOut.toDate() : (entry.status === 'active' ? new Date() : null);
        const hrs = entry.status === 'completed' ? (entry.totalHours || 0) : ((Date.now() - cin.getTime()) / 3600000);
        const ws = getWeekStart(cin);
        const weekKey = getWeekKey(cin);
        weekRunningAccum[weekKey] = (weekRunningAccum[weekKey] || 0) + hrs;
        // TASK-4612 root cause 3 — the Running column is a WORKWEEK total, but it
        // used to start at 0 on the first row VISIBLE in this pay period. For a
        // week whose head fell in the previous period (period Aug 10–23 opens on
        // a Monday, so Sun Aug 9 belongs to the prior period) that silently
        // dropped real hours off the count and the badge disagreed with the OT
        // payroll actually paid. Seed from the helper's own `beforeHours`; the
        // divider above the rows says so in words, so the number is never a
        // mystery. Exempt people never get an OT marker (otSummary.exempt is the
        // one predicate the CSV, the print report and My Timesheet all use).
        const _wkDesc = _ClockWeekBoundary.describeWeek({
          weekStart: ws, periodStart, periodEnd, otWeek: _otWeekByKey[weekKey] || null, exempt: _payExempt,
        });
        const _run = _ClockWeekBoundary.rowRunning(_wkDesc, weekRunningAccum[weekKey], _otThreshold, _payExempt);
        const weekRunTotal = _run.total;
        const isOT = _run.isOT;
        // This goes in an ATTRIBUTE, and escapeHtml does NOT escape quotes — use
        // window.escAttr. `_run.threshold` is already coerced to a finite number
        // by rowRunning (settings.overtimeThreshold is tenant-controlled); the
        // escape is the second layer, not the only one.
        const _escA = window.escAttr || escapeHtml;
        const _runTitle = _escA(_wkDesc.priorHrs > 0
          ? `Workweek running total. Includes ${_wkDesc.priorHrs.toFixed(1)} hrs earlier this workweek that were paid in the previous pay period and still count toward the ${_run.threshold}-hour week.`
          : 'Workweek running total (resets each workweek).');
        const dateStr = cin.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        // TASK-9189 — this table is the payroll-REVIEW panel: the screen a human
        // reads to approve hours BEFORE a pay run. The In/Out cells used to be
        // time-only, so every shift that crossed midnight printed a range that
        // reads backwards next to a total it cannot possibly produce — Chris's
        // own entry showed "9:18 AM – 10:39 AM" beside "25.35 hrs". Both cells
        // now come from the shared window.Fmt.shiftSpan (sharedFormat.js), which
        // carries the +1d marker. `cout` is `new Date()` for an ACTIVE row (see
        // above), so a still-running shift that began yesterday now says so too.
        const _span = window.Fmt.shiftSpan(cin, cout);
        const cinStr = _span.inText;
        const coutStr = cout ? _span.outHtml : '<em class="ss-text">Active</em>';

        // TASK-9189 — and the TOTAL has to agree with the RANGE, or the range has
        // only been made to look right. `hrs` is entry.totalHours for a completed
        // row; totalHours is always round((out-in)/3600000, 2) with no break
        // deduction anywhere in the product, so a disagreement is a data defect.
        // Flag it where it can be acted on; never auto-correct it. Active rows are
        // excluded — their `hrs` is live elapsed time, not a stored total.
        const _drift = entry.status === 'completed'
          ? window.Fmt.shiftHoursDrift(_span.spanHours, entry.totalHours)
          : null;
        const _driftHtml = _drift
          ? `<span title="${_escA(_drift.text)}" style="margin-left:4px;color:var(--status-danger);font-weight:700;cursor:help;">&#9888;</span>`
          : '';

        const hasNotes = !!(entry.clockOutNotes || entry.notes);
        const notesText = entry.clockOutNotes || entry.notes || '';
        // TASK-1364 — ONE predicate for "does this row get an actions cell", used
        // by both the <td> below and the notes-row colspan. They disagreed before
        // (colCount ignored entry.ticketId entirely), and gating the receipt
        // button on billing access would otherwise add a second way to drift —
        // a ticketId row for a non-billing viewer rendering an empty 8th cell
        // over a colspan of 7.
        const _showTicketBtn = _tkBtn(entry);
        const _hasRowActions = canEditEntry(entry) || hasWorkforceAccess() || _showTicketBtn;
        const colCount = _hasRowActions ? 8 : 7;
        // TASK-9500b — the out-of-zone pill (display only, same as the Active-now /
        // Completed-today cards), so a punch outside the job geofence shows on the
        // screen the office approves hours from, before the pay run.
        // Own line under the date, never wrapping mid-distance (COD r1 UX).
        const _geoPill = ((p) => (p ? `<div style="white-space:nowrap;">${p}</div>` : ''))(buildGeofencePillHTML(entry));

        const rowHtml = `
          <tr style="border-bottom:${hasNotes ? 'none' : '1px solid var(--border-light)'};">
            <td style="padding:6px 8px;color:var(--text-primary);">${dateStr}${_geoPill}</td>
            <td style="padding:6px 8px;color:var(--text-primary);">${escapeHtml(entry.customerName || '-')}</td>
            <td style="padding:6px 8px;color:var(--text-primary);">${escapeHtml(entry.jobTitle || '-')}</td>
            <td style="padding:6px 8px;color:var(--text-secondary);">${cinStr}</td>
            <td style="padding:6px 8px;color:var(--text-secondary);">${coutStr}</td>
            <td style="padding:6px 8px;text-align:right;font-weight:600;color:var(--text-primary);white-space:nowrap;">${hrs.toFixed(2)}${_driftHtml}</td>
            <td title="${_runTitle}" style="padding:6px 8px;text-align:right;font-weight:600;color:${isOT ? 'var(--status-danger)' : 'var(--text-primary)'};">${weekRunTotal.toFixed(1)}${isOT ? ' (OT)' : ''}</td>
            ${_hasRowActions ? `<td style="padding:6px 4px;white-space:nowrap;">
              ${_showTicketBtn ? `<button class="btn-icon" onclick="window.ClockModule.openTicketFromClock('${_safeTicketId(entry.ticketId)}','${_safeTicketId(entry.id)}')" title="View/Edit Ticket" style="color:var(--brand-blue);padding:2px;"><span class="material-icons" style="font-size:16px;">receipt_long</span></button>` : ''}
              ${canEditEntry(entry) ? `<button class="btn-icon" onclick="window.ClockModule.editClockEntry('${entry.id}')" title="Edit" style="color:var(--text-secondary);padding:2px;"><span class="material-icons" style="font-size:16px;">edit</span></button>` : ''}
              ${hasWorkforceAccess() ? `<button class="btn-icon" onclick="window.ClockModule.deleteClockEntry('${entry.id}', '${personId}')" title="Delete" style="color:var(--status-danger);padding:2px;"><span class="material-icons" style="font-size:16px;">delete</span></button>` : ''}
            </td>` : ''}
          </tr>
          ${hasNotes ? `<tr style="border-bottom:1px solid var(--border-light);">
            <td colspan="${colCount}" style="padding:2px 8px 8px;"><div style="display:flex;align-items:flex-start;gap:6px;background:#F8FAFC;border-radius:6px;padding:6px 10px;font-size:0.78rem;color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;color:var(--brand-blue);margin-top:1px;">speaker_notes</span><span style="white-space:pre-wrap;">${escapeHtml(notesText)}</span></div></td>
          </tr>` : ''}
        `;
        return { weekKey, weekStart: ws, html: rowHtml };
      });
    // TASK-1179 — group the chronologically-built rows into workweek groups so a
    // divider row can head each week (the Running column resets per week). The
    // computation above stays CHRONOLOGICAL (TASK-485 invariant); we only group
    // the finished strings for display. weekRunningAccum holds each week's final
    // total after the map, so the divider can show it.
    const _weekOrder = [];
    const _weekGroups = {};
    entryRowData.forEach(r => {
      if (!_weekGroups[r.weekKey]) { _weekGroups[r.weekKey] = { weekStart: r.weekStart, rows: [] }; _weekOrder.push(r.weekKey); }
      _weekGroups[r.weekKey].rows.push(r.html);
    });
    const _dividerColspan = (hasWorkforceAccess() || ClockState.authenticatedPersonId) ? 8 : 7;
    // TASK-4612 — root causes 1 and 2, together.
    //  (1) The label used to be CLAMPED to the pay-period end
    //      (`labelEnd = wEnd > periodEnd ? periodEnd : wEnd`), so the workweek
    //      that opens on the period's last day printed as "Week of Aug 23 – Aug
    //      23". A one-day week reads as an error and Brian Reid reported his
    //      Sunday hours as missing. The range is now the TRUE Sun–Sat week, with
    //      an explicit note when part of it sits outside this pay period.
    //  (2) The (OT) badge used to be `weekRunningAccum[weekKey] > _otThreshold` —
    //      a threshold test on PERIOD-TRUNCATED hours. It now reads the paid OT
    //      out of computeCrossPeriodOT, the same number as the payroll CSV, the
    //      Employee Breakdown and My Timesheet. Suppressing the badge instead
    //      would have traded a wrong badge for a missing one.
    const _fmtWkDate = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const _buildWeekDivider = (weekKey, weekStart) => {
      const desc = _ClockWeekBoundary.describeWeek({
        weekStart, periodStart, periodEnd, otWeek: _otWeekByKey[weekKey] || null, exempt: _payExempt,
      });
      const parts = _ClockWeekBoundary.dividerParts(
        desc, weekStart, weekRunningAccum[weekKey] || 0, _fmtWkDate, _otThreshold
      );
      const otHtml = parts.ot ? ` <span style="color:var(--status-danger);">(${parts.ot})</span>` : '';
      const noteHtml = parts.notes.length
        // --text-secondary, never --text-muted: this row sits on the light
        // --bg-secondary band and muted-on-light is the standing contrast trap.
        ? `<div style="font-variant:normal;font-weight:500;letter-spacing:0;text-transform:none;color:var(--text-secondary);font-size:0.66rem;margin-top:1px;line-height:1.35;">${escapeHtml(parts.notes.join(' · '))}</div>`
        : '';
      return `<tr style="background:var(--bg-secondary);"><td colspan="${_dividerColspan}" style="padding:4px 10px;font-size:0.68rem;font-variant:small-caps;letter-spacing:0.04em;font-weight:600;color:var(--text-secondary);border-bottom:1px solid var(--border-light);">${parts.range} · ${parts.hours}${otHtml}${noteHtml}</td></tr>`;
    };
    // Display newest-on-top by reversing GROUP order AND the entries within each
    // group when detailSortDir === 'desc' — the divider must stay ABOVE its
    // week's rows in BOTH directions. Each row string bundles the shift <tr> +
    // its optional notes <tr>, so reversing per-entry keeps notes with their shift.
    const _orderedKeys = ClockState.detailSortDir === 'desc' ? _weekOrder.slice().reverse() : _weekOrder;
    shiftRows = _orderedKeys.map(wk => {
      const g = _weekGroups[wk];
      const rows = ClockState.detailSortDir === 'desc' ? g.rows.slice().reverse() : g.rows;
      return _buildWeekDivider(wk, g.weekStart) + rows.join('');
    }).join('');
  }

  const totalHrs = totalReg + totalOT;
  const ot = totalOT;
  const reg = totalReg;

  const modalBody = document.getElementById('workforce-modal-body');
  modalBody.innerHTML = `
    <div style="margin-bottom:var(--spacing-md);">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;">
        <div>
          <h3 style="margin:0 0 4px 0;color:var(--text-primary);">${escapeHtml(person.name)}</h3>
        </div>
        ${isClockAdmin() ? `<div style="display:flex;gap:6px;">
          <button class="btn btn-sm" onclick="window.ClockModule.showAddClockEntryForm('${personId}')" style="font-size:0.78rem;padding:4px 10px;border:1px solid var(--brand-blue);color:var(--brand-blue);background:transparent;border-radius:6px;cursor:pointer;">+ Add Entry</button>
        </div>` : ''}
      </div>
      <div style="display:flex;align-items:center;gap:var(--spacing-sm);margin-top:8px;">
        <button class="btn btn-secondary btn-sm" onclick="window.ClockModule.navigateDetailPayPeriod(-1)" style="display:flex;align-items:center;padding:4px 8px;">
          <span class="material-icons" style="font-size:18px;">chevron_left</span>
        </button>
        <div style="text-align:center;flex:1;">
          <div style="font-weight:600;color:var(--text-primary);font-size:0.9rem;">${formatPayPeriodRange(periodStart, periodEnd)}</div>
          <div style="font-size:0.7rem;color:var(--text-secondary);">${ppTypeLabel} Pay Period${isCurrent ? ' (Current)' : ''}</div>
        </div>
        <button class="btn btn-secondary btn-sm" onclick="window.ClockModule.navigateDetailPayPeriod(1)" ${isCurrent ? 'disabled style="opacity:0.4;display:flex;align-items:center;padding:4px 8px;"' : 'style="display:flex;align-items:center;padding:4px 8px;"'}>
          <span class="material-icons" style="font-size:18px;">chevron_right</span>
        </button>
      </div>
    </div>
    <div style="display:flex;gap:var(--spacing-md);margin-bottom:var(--spacing-lg);flex-wrap:wrap;">
      <div style="background:var(--bg-secondary);border-radius:var(--radius-sm);padding:var(--spacing-sm) var(--spacing-md);">
        <span style="font-size:0.75rem;color:var(--text-secondary);">Regular</span>
        <div style="font-weight:700;color:var(--text-primary);">${reg.toFixed(1)} hrs</div>
      </div>
      <div style="background:var(--bg-secondary);border-radius:var(--radius-sm);padding:var(--spacing-sm) var(--spacing-md);">
        <span style="font-size:0.75rem;color:var(--text-secondary);">Overtime</span>
        <div style="font-weight:700;color:${ot > 0 ? 'var(--status-danger)' : 'var(--text-primary)'};">${ot.toFixed(1)} hrs</div>
      </div>
      <div style="background:var(--bg-secondary);border-radius:var(--radius-sm);padding:var(--spacing-sm) var(--spacing-md);">
        <span style="font-size:0.75rem;color:var(--text-secondary);">Total</span>
        <div style="font-weight:700;color:var(--text-primary);">${totalHrs.toFixed(1)} hrs</div>
      </div>
      ${liveActiveHrs > 0.001 ? `<div title="Hours from a shift still in progress — NOT included in paid Regular/Overtime/Total (payroll pays completed hours only)." style="background:rgba(16,185,129,0.08);border:1px solid rgba(16,185,129,0.35);border-radius:var(--radius-sm);padding:var(--spacing-sm) var(--spacing-md);">
        <span class="ss-text" style="font-size:0.75rem;">In Progress</span>
        <div class="ss-text" style="font-weight:700;">+${liveActiveHrs.toFixed(1)} hrs</div>
      </div>` : ''}
    </div>
    ${otSummary.straddleNoteApplies ? `<div style="display:flex;align-items:flex-start;gap:6px;margin:calc(-1 * var(--spacing-sm)) 0 var(--spacing-md);font-size:0.72rem;color:var(--text-secondary);line-height:1.35;">
      <span class="material-icons" style="font-size:14px;color:var(--brand-blue);margin-top:1px;flex-shrink:0;">info</span>
      <span>Overtime premium includes hours earned during the prior pay period due to a workweek spanning multiple pay periods.</span>
    </div>` : ''}
    <div style="overflow-x:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:0.85rem;">
        <thead>
          <tr style="border-bottom:2px solid var(--border-medium);">
            <th style="padding:6px 8px;text-align:left;color:var(--text-primary);">
              <button type="button" onclick="window.ClockModule.toggleTimesheetSort()" title="Sort by date (${ClockState.detailSortDir === 'desc' ? 'newest first' : 'oldest first'})" aria-label="Sort by date, currently ${ClockState.detailSortDir === 'desc' ? 'newest first' : 'oldest first'}" style="display:inline-flex;align-items:center;gap:2px;cursor:pointer;user-select:none;background:none;border:none;padding:0;margin:0;font:inherit;font-weight:inherit;color:inherit;">
                Date
                <span class="material-icons" style="font-size:15px;color:var(--text-secondary);line-height:1;">${ClockState.detailSortDir === 'desc' ? 'arrow_downward' : 'arrow_upward'}</span>
              </button>
            </th>
            <th style="padding:6px 8px;text-align:left;color:var(--text-primary);">Customer</th>
            <th style="padding:6px 8px;text-align:left;color:var(--text-primary);">Job</th>
            <th style="padding:6px 8px;text-align:left;color:var(--text-primary);">In</th>
            <th style="padding:6px 8px;text-align:left;color:var(--text-primary);">Out</th>
            <th style="padding:6px 8px;text-align:right;color:var(--text-primary);">Hours</th>
            <th style="padding:6px 8px;text-align:right;color:var(--text-primary);">Running</th>
            ${(hasWorkforceAccess() || ClockState.authenticatedPersonId) ? '<th style="padding:6px 4px;"></th>' : ''}
          </tr>
        </thead>
        <tbody>${shiftRows}</tbody>
        <tfoot>
          <tr style="border-top:2px solid var(--border-medium);">
            <td colspan="5" style="padding:6px 8px;font-weight:700;color:var(--text-primary);">Totals</td>
            <td style="padding:6px 8px;text-align:right;font-weight:700;color:var(--text-primary);">${totalHrs.toFixed(2)}</td>
            <td></td>
            ${(hasWorkforceAccess() || ClockState.authenticatedPersonId) ? '<td></td>' : ''}
          </tr>
        </tfoot>
      </table>
    </div>
    <div id="employee-adjustments-section" data-person-id="${personId}"></div>
  `;
  document.getElementById('modal-workforce')?.classList.remove('hidden');

  // Load adjustments (payAdjustments + legacy perDiemEntries) for this pay period
  loadDetailAdjustments(personId, periodStart, periodEndExclusive).then(items => {
    const section = document.getElementById('employee-adjustments-section');
    if (!section || section.dataset.personId !== personId) return;

    if (items.length === 0) return;

    const total = items.reduce((sum, e) => sum + (e.isDeduction ? -e.amount : e.amount), 0);
    const rows = items.map(e => {
      const dateStr = e.dateStr || '—';
      const color = e.isDeduction ? '#EF4444' : e.type === 'Per-Diem' ? '#06B6D4' : '#22C55E';
      return `
      <div style="display:flex;justify-content:space-between;align-items:center;padding:5px 0;border-bottom:1px solid var(--border-light);">
        <div style="display:flex;align-items:center;gap:8px;">
          <span style="font-size:0.7rem;font-weight:600;color:${color};background:${color}15;padding:2px 6px;border-radius:4px;text-transform:uppercase;">${e.type}</span>
          ${e.recurring ? '<span class="material-icons" style="font-size:12px;color:var(--brand-blue);" title="Recurring">repeat</span>' : ''}
          <span style="font-size:0.82rem;color:var(--text-secondary);">${dateStr}</span>
          ${e.notes ? `<span style="font-size:0.78rem;color:var(--text-secondary);">&mdash; ${escapeHtml(e.notes)}</span>` : ''}
        </div>
        <span style="font-weight:600;color:${e.isDeduction ? 'var(--status-danger)' : 'var(--text-primary)'};">${e.isDeduction ? '-' : ''}$${e.amount.toFixed(2)}</span>
      </div>`;
    }).join('');

    section.innerHTML = `
      <div style="margin-top:var(--spacing-md);">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
          <strong style="font-size:0.85rem;color:var(--text-primary);">Adjustments</strong>
          <span style="font-size:0.85rem;font-weight:600;color:${total >= 0 ? 'var(--text-primary)' : 'var(--status-danger)'};">Net: ${total < 0 ? '-' : ''}$${Math.abs(total).toFixed(2)}</span>
        </div>
        ${rows}
      </div>
    `;
  });
}

async function loadDetailAdjustments(personId, periodStart, periodEnd) {
  const items = [];
  const startStr = _fmtLocalYmd(periodStart);
  const endStr = _fmtLocalYmd(new Date(periodEnd.getTime() - 1)); // inclusive end
  const tenant = ClockState.tenantId;
  try {
    // Load from payAdjustments (new system)
    const adjSnap = await getDocs(
      query(
        collection(db, 'tenants', tenant, 'payAdjustments'),
        where('personId', '==', personId)
      )
    );
    adjSnap.docs.forEach(d => {
      const data = d.data();
      const isRecurring = data.recurring === true;
      const dt = data.date?.toDate?.() || (data.date ? new Date(data.date) : null);
      if (!dt) return;
      const dtStr = _fmtLocalYmd(dt);
      // Include if: in period OR recurring (always applies)
      if (dtStr >= startStr && dtStr <= endStr || isRecurring) {
        const typeLabel = data.category || ((data.type || 'other').charAt(0).toUpperCase() + (data.type || 'other').slice(1));
        const isDeduction = (data.type || '').toLowerCase() === 'deduction';
        items.push({
          id: d.id, source: 'payAdjustments', type: typeLabel, isDeduction, recurring: isRecurring,
          amount: data.amount || 0, notes: data.notes || '',
          dateStr: isRecurring ? 'Every period' : dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          sortDate: isRecurring ? new Date(0) : dt
        });
      }
    });
    // Load from legacy perDiemEntries (old system)
    const pdSnap = await getDocs(
      query(
        collection(db, 'tenants', tenant, 'perDiemEntries'),
        where('personId', '==', personId),
        where('date', '>=', startStr),
        where('date', '<=', endStr)
      )
    );
    pdSnap.docs.forEach(d => {
      const data = d.data();
      // Skip if already covered by a payAdjustments entry (avoid duplicates)
      items.push({
        id: d.id, source: 'perDiemEntries', type: 'Per-Diem', isDeduction: false,
        amount: data.amount || 0, notes: data.notes || '',
        dateStr: data.date ? new Date(data.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—',
        sortDate: data.date ? new Date(data.date + 'T12:00:00') : new Date(0)
      });
    });
  } catch (err) {
    console.error('[Clock] Error loading detail adjustments:', err);
  }
  items.sort((a, b) => a.sortDate - b.sortDate);
  return items;
}

async function navigateDetailPayPeriod(direction) {
  if (direction > 0 && ClockState.detailPayPeriodOffset >= 0) return;
  ClockState.detailPayPeriodOffset += direction;
  if (ClockState.detailPayPeriodOffset > 0) ClockState.detailPayPeriodOffset = 0;
  if (ClockState.detailPersonId) {
    await showEmployeeDetail(ClockState.detailPersonId, false);
  }
}

// TASK-485 — flip the employee-detail timesheet Date sort direction, persist it,
// and re-render the modal at the current pay-period offset.
async function toggleTimesheetSort() {
  ClockState.detailSortDir = ClockState.detailSortDir === 'desc' ? 'asc' : 'desc';
  try { localStorage.setItem('dd_timesheet_sortdir', ClockState.detailSortDir); } catch (e) { /* ignore storage errors */ }
  if (ClockState.detailPersonId) {
    await showEmployeeDetail(ClockState.detailPersonId, false);
  }
}

// ============================================
// TASK-1182 — PAY PERIOD SETTINGS PANEL
// ============================================
// This panel defines PAYROLL boundaries, so it has to show its work. Chris was
// burned twice in one sitting: (1) the gear only rendered inside the 'Pay Period'
// chip branch, so it was invisible on every other period mode; (2) flipping
// "Week Starts On" to Sunday while the anchor stayed a Monday silently produced
// an internally inconsistent config (Monday-anchored periods + Sun–Sat OT weeks)
// that hid a live driver's hours (the TASK-1193 outage).
const PP_WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const PP_TYPE_LABELS = { weekly: 'Weekly', biweekly: 'Bi-Weekly', semimonthly: 'Semi-Monthly', monthly: 'Monthly' };

// Reads the panel's CURRENT (unsaved) control values into an overrides object.
// PURE — 🔴 never writes window.AppState.settings; the object is threaded to
// getPayPeriodDates() by parameter. Returns null when the panel isn't mounted, so
// callers transparently fall back to saved-settings math.
function readPayPeriodFormOverrides() {
  const typeEl = document.getElementById('pp-type-select');
  const anchorEl = document.getElementById('pp-anchor-date');
  const weekEl = document.getElementById('pp-week-start');
  if (!typeEl && !anchorEl && !weekEl) return null;
  const o = {};
  const t = typeEl?.value;
  if (t && ['weekly', 'biweekly', 'semimonthly', 'monthly'].includes(t)) o.payPeriodType = t;
  const a = anchorEl?.value;
  // Only a well-formed date becomes an override; a half-typed date falls back to
  // the saved anchor rather than previewing an Invalid Date.
  if (a && /^\d{4}-\d{2}-\d{2}$/.test(a)) o.payPeriodAnchor = a;
  const w = parseInt(weekEl?.value, 10);
  if (Number.isInteger(w) && w >= 0 && w <= 6) o.workWeekStartDay = w;
  return o;
}

function formatPayPeriodPreviewRange(start, end) {
  const opts = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', opts)}`;
}

// Non-blocking amber warning — legit configs exist, so this never gates Save.
function buildPayPeriodMismatchHTML(overrides, type) {
  if (type !== 'weekly' && type !== 'biweekly') return ''; // semi-monthly/monthly don't use the anchor weekday
  const weekStartDay = getWorkWeekStartDay(overrides);
  const anchor = getPayPeriodAnchor(overrides);
  if (!anchor || isNaN(anchor.getTime())) return '';
  const anchorDay = anchor.getDay();
  if (anchorDay === weekStartDay) return '';
  const anchorName = PP_WEEKDAY_NAMES[anchorDay];
  const weekName = PP_WEEKDAY_NAMES[weekStartDay];
  // Worded per type so the warning is never itself a lie: WEEKLY periods derive
  // from the work-week start and ignore the anchor entirely (getPayPeriodDates'
  // weekly branch calls getWeekStart, not the anchor), whereas BIWEEKLY periods
  // really are anchor-gridded — that's the config that hid Javaun's hours.
  const body = type === 'biweekly'
    ? `Your pay periods start on <strong>${anchorName}</strong> but your work week starts <strong>${weekName}</strong> — these usually match. Overtime is bucketed by the work week, so it will split across pay periods.`
    : `Weekly pay periods always start on <strong>${weekName}</strong> (your “Week Starts On”). The Period Start Reference (a ${anchorName}) isn’t used for weekly pay — set it to a ${weekName} to avoid confusion.`;
  return `
    <div class="pp-mismatch-warning">
      <span class="material-icons">warning_amber</span>
      <div>${body}</div>
    </div>`;
}

// Live preview of the boundaries the CURRENT panel values would produce, PRE-save.
// Built off the same getPayPeriodDates() the whole app runs on — never a fork.
function buildPayPeriodPreviewHTML(overrides) {
  const type = getPayPeriodType(overrides);
  const chips = [];
  for (let off = -2; off <= 1; off++) {
    let d = null;
    try { d = getPayPeriodDates(off, overrides); } catch (e) { d = null; }
    if (!d?.start || !d?.end || isNaN(d.start.getTime()) || isNaN(d.end.getTime())) {
      return '<div class="pp-preview-invalid">Enter a valid Period Start Reference date to preview your pay periods.</div>';
    }
    const cur = off === 0;
    chips.push(
      `<span class="pp-preview-chip${cur ? ' pp-preview-chip-current' : ''}">` +
      `${cur ? '<span class="material-icons">play_arrow</span>' : ''}` +
      `${escapeHtml(formatPayPeriodPreviewRange(d.start, d.end))}` +
      `${cur ? '<em>current</em>' : ''}</span>`
    );
  }
  return `
    <div class="pp-preview-label">${escapeHtml(PP_TYPE_LABELS[type] || type)} periods around today</div>
    <div class="pp-preview-chips">${chips.join('')}</div>
    ${buildPayPeriodMismatchHTML(overrides, type)}`;
}

// Re-renders the preview in place as the user edits. Wired to the type/anchor/
// week-start controls; reads the live form, mutates nothing but #pp-preview.
function updatePayPeriodPreview() {
  const el = document.getElementById('pp-preview');
  if (!el) return;
  el.innerHTML = buildPayPeriodPreviewHTML(readPayPeriodFormOverrides());
}

function togglePayPeriodSettings() {
  ClockState.payPeriodPanelOpen = !ClockState.payPeriodPanelOpen;
  const panel = document.getElementById('pp-settings-panel');
  const gear = document.getElementById('pp-gear-btn');
  if (panel) panel.classList.toggle('hidden', !ClockState.payPeriodPanelOpen);
  if (gear) gear.setAttribute('aria-expanded', String(ClockState.payPeriodPanelOpen));
  // Repaint the preview from saved settings on open so it always opens truthful.
  if (ClockState.payPeriodPanelOpen) {
    _loadInternalPaySection(); // TASK-9388 — always a fresh read on open
    updatePayPeriodPreview();
    panel?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

// TASK-9527 follow-up (2026-10-19) — who may SAVE Pay Settings = the server
// gate on setOvertimeThreshold (_requireWorkforceModuleOrOwner): the OWNER via
// tenants.ownerId, or an active member holding `workforce` who is not read-only
// or view-and-upload-only. members.role is never read (same shape as app.js
// canEditMoney, module swapped). Everyone who reaches Workforce → Reports still
// SEES the settings; the Save is disabled with one plain line when refused.
function canEditPaySettings() {
  const st = window.AppState || {};
  if (st.isDemo || st.tenant === 'demo') return true;
  if (st.driverMode || st.isPublicAccess || st.driverModePin) return false;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  if (ma.readOnly === true || ma.viewUploadOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('workforce');
}
const PAY_SETTINGS_VIEW_ONLY_MSG = 'View only — the owner or a login with Workforce access can change pay settings.';

// The panel itself — rendered on EVERY period-chip mode (burn #1), grouped into
// Pay Schedule / Overtime sections. Hidden state comes from ClockState so a
// re-render (including savePayPeriodSettings') can't slam it shut.
function buildPayPeriodSettingsPanelHTML() {
  const canSavePay = canEditPaySettings();
  const ppType = getPayPeriodType();
  const savedAnchor = window.AppState?.settings?.payPeriodAnchor || '';
  const weekStartDay = getWorkWeekStartDay();
  const open = ClockState.payPeriodPanelOpen === true;
  return `
    <div id="pp-settings-panel" class="pp-settings-panel${open ? '' : ' hidden'}">
      <div class="pp-section">
        <div class="pp-section-title">Pay Schedule</div>
        <div class="pp-field-row">
          <div class="pp-field">
            <label for="pp-type-select">Pay Period Type</label>
            <select id="pp-type-select" class="form-control" onchange="window.ClockModule.updatePayPeriodPreview()">
              <option value="weekly" ${ppType === 'weekly' ? 'selected' : ''}>Weekly</option>
              <option value="biweekly" ${ppType === 'biweekly' ? 'selected' : ''}>Bi-Weekly (every 2 weeks)</option>
              <option value="semimonthly" ${ppType === 'semimonthly' ? 'selected' : ''}>Semi-Monthly (1st &amp; 16th)</option>
              <option value="monthly" ${ppType === 'monthly' ? 'selected' : ''}>Monthly</option>
            </select>
          </div>
          <div class="pp-field">
            <label for="pp-anchor-date">Period Start Reference</label>
            <input type="date" id="pp-anchor-date" class="form-control" value="${escapeHtml(savedAnchor)}" onchange="window.ClockModule.updatePayPeriodPreview()" oninput="window.ClockModule.updatePayPeriodPreview()">
            <div class="pp-field-hint">A known pay period start date</div>
          </div>
          <div class="pp-field">
            <label for="pp-week-start">Week Starts On</label>
            <select id="pp-week-start" class="form-control" onchange="window.ClockModule.updatePayPeriodPreview()">
              ${PP_WEEKDAY_NAMES.map((name, i) => `<option value="${i}" ${i === weekStartDay ? 'selected' : ''}>${name}</option>`).join('')}
            </select>
            <div class="pp-field-hint">Used for overtime weeks</div>
          </div>
        </div>
        <div id="pp-preview" class="pp-preview">${buildPayPeriodPreviewHTML(null)}</div>
      </div>

      <div class="pp-section">
        <div class="pp-section-title">Overtime</div>
        <div class="pp-field-row">
          <div class="pp-field pp-field-narrow">
            <label for="pp-ot-threshold">OT After (hrs/week)</label>
            <input type="number" id="pp-ot-threshold" class="form-control" value="${getOTThreshold()}" min="1" max="168" step="1">
          </div>
          <div class="pp-field pp-field-narrow">
            <label for="pp-oversized-threshold" title="A single shift longer than this is flagged '⚠️ Needs Review' (likely a forgotten clock-out). Hours are NEVER auto-capped.">Flag Single Shift Over (hrs)</label>
            <input type="number" id="pp-oversized-threshold" class="form-control" value="${getOversizedShiftThreshold()}" min="1" max="168" step="1" title="A single shift longer than this is flagged '⚠️ Needs Review' (likely a forgotten clock-out). Hours are NEVER auto-capped.">
          </div>
        </div>
      </div>

      <!-- TASK-619 (Lacey) — require a shift note before clock-out. Opt-in, default off. -->
      <label class="pp-toggle-row">
        <input type="checkbox" id="pp-require-shift-notes" ${window.AppState?.settings?.requireShiftNotes === true ? 'checked' : ''}>
        <span><strong>Require a shift note to clock out</strong><br><span class="pp-field-hint">Drivers must type what they did before they can clock out or switch jobs.</span></span>
      </label>

      <div class="pp-panel-footer">
        ${canSavePay ? '' : `<span class="pp-field-hint" id="pp-save-view-only">${PAY_SETTINGS_VIEW_ONLY_MSG}</span>`}
        <button id="pp-save-btn" class="btn btn-primary btn-sm" onclick="window.ClockModule.savePayPeriodSettings()" ${canSavePay ? '' : 'disabled aria-describedby="pp-save-view-only"'}>Save Pay Settings</button>
      </div>

      <!-- TASK-9388 — internal-time pay rates + OT method. Its own Save (its own
           server callable + audit); filled async by _loadInternalPaySection. -->
      <div class="pp-section" id="pp-internal-pay-section">
        <div class="pp-section-title" style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;">
          <span>Internal-Time Pay (Shop / Yard / …)</span>
          ${_hrOtQuestionLinkHTML()}
        </div>
        <div id="pp-internal-pay-body">${open ? _internalPaySectionHTML() : ''}</div>
      </div>
    </div>`;
}

// ============================================
// TASK-9388 — INTERNAL-TIME PAY SETTINGS (Workforce → Reports → Pay Settings)
// ============================================
// CRM owns "is this customer internal time"; Workforce owns what it PAYS. Per
// explicit internalTimeOnly === true customer: an optional $/hr and a list of
// people who keep their normal rate on it; plus the tenant's OT method for
// weeks that mix rates. Stored in tenants/{tid}/payConfig/internalTime (never
// the tenant root / settings / customers — all driver-readable), written ONLY
// by the setInternalTimePayConfig callable (owner or workforce module; audits
// every change old → new). Editors mirror the server gate exactly.
function _canEditInternalPay() {
  const A = window.AppState || {};
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  const uid = A.user?.uid;
  if (uid && A.tenantData?.ownerId === uid) return true;
  const ma = A.memberAccess;
  if (!ma || ['revoked', 'inactive', 'pending'].includes(ma.status)) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('workforce') && ma.viewUploadOnly !== true;
}

// TASK-9689 slice 3 — "How is overtime figured with two rates?" next to the
// Internal-Time Pay section opens the HR assistant with that question. Shown
// only to a login the assistant will answer: mirrors askHRDocuments' gate
// (owner via ownerId, or an ACTIVE member holding `hr`; a view/upload-only
// member is admitted there too). Never in driver / public sessions. hr.js is
// reached through window.HRModule (Pitfall #1 — no cross-import).
function _canAskHrAssistant() {
  const A = window.AppState || {};
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  const uid = A.user?.uid;
  if (uid && A.tenantData?.ownerId === uid) return true;
  const ma = A.memberAccess;
  if (!ma || ['revoked', 'inactive', 'pending', 'removed', 'suspended'].includes(ma.status)) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('hr');
}

function _hrOtQuestionLinkHTML() {
  if (!_canAskHrAssistant()) return '';
  return `<button type="button" class="btn-text" onclick="window.HRModule?.openDocAssistant?.({ ask: 'How is overtime figured when someone works at two different pay rates in the same week?' })"
            style="background:none;border:none;padding:2px 0;cursor:pointer;color:var(--brand-blue);font-size:0.8rem;font-weight:500;display:inline-flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:16px;" aria-hidden="true">help_outline</span>How is overtime figured with two rates?
          </button>`;
}

async function _loadInternalPaySection() {
  const tid = ClockState.tenantId;
  const st = { tid, loading: true, error: null, customers: [], cfg: null, removed: new Set() };
  ClockState._internalPay = st;
  _paintInternalPaySection();
  const [customers, cfgRes] = await Promise.all([_loadPayrollCustomers(tid), _loadInternalPayConfig(tid)]);
  if (ClockState._internalPay !== st) return; // superseded by a newer load
  st.loading = false;
  if (!Array.isArray(customers)) st.error = 'Customer list failed to load — refresh and try again.';
  else if (!cfgRes.ok) st.error = 'Internal-time pay settings could not be loaded (your login needs the Workforce module).';
  st.customers = (customers || []).filter(c => c && c.internalTimeOnly === true)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  st.allCustomers = customers || [];
  st.cfg = cfgRes.cfg;
  _paintInternalPaySection();
}

function _paintInternalPaySection() {
  const el = document.getElementById('pp-internal-pay-body');
  if (el) el.innerHTML = _internalPaySectionHTML();
}

function _internalPaySectionHTML() {
  const st = ClockState._internalPay;
  if (!st || st.tid !== ClockState.tenantId) {
    // First paint (or a different tenant's cached state): load after the HTML lands.
    setTimeout(() => {
      const cur = ClockState._internalPay;
      if (!cur || (cur.tid !== ClockState.tenantId && !cur.loading)) _loadInternalPaySection();
    }, 0);
    return '<div class="pp-field-hint">Loading…</div>';
  }
  if (st.loading) return '<div class="pp-field-hint">Loading…</div>';
  if (st.error) return `<div class="pp-field-hint" style="color:var(--status-danger);">${escapeHtml(st.error)}</div>`;
  const IT = window.InternalTime;
  const cfg = st.cfg || (IT ? IT.normalizePayConfig(null) : { otMethod: 'weighted_average', customers: {} });
  const canEdit = _canEditInternalPay();
  const dis = canEdit ? '' : 'disabled';
  const people = (ClockState.people || []).filter(p => p && p.active !== false && !p.terminated)
    .slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  const rows = st.customers.map((c, i) => {
    const cc = cfg.customers[c.id];
    const rate = cc && IT ? IT.currentRate(cc) : null;
    const excl = cc ? cc.exclude : new Set();
    const boxes = people.map((p, j) => `
          <label style="display:flex;gap:6px;align-items:center;padding:2px 0;font-size:0.85rem;">
            <input type="checkbox" class="pp-ip-excl" data-ci="${i}" data-pi="${j}" ${excl.has(p.id) ? 'checked' : ''} ${dis}>
            ${escapeHtml(p.name || 'Unnamed')}
          </label>`).join('');
    return `
      <div class="pp-ip-row" style="border:1px solid var(--border-light);border-radius:8px;padding:8px 10px;margin-bottom:8px;">
        <div class="pp-field-row" style="align-items:flex-end;">
          <div class="pp-field"><label>${escapeHtml(c.name || 'Unnamed customer')}</label>
            <div class="pp-field-hint">Internal time only (set in CRM)</div></div>
          <div class="pp-field pp-field-narrow">
            <label for="pp-ip-rate-${i}">Rate ($/hr)</label>
            <input type="number" id="pp-ip-rate-${i}" class="form-control" min="7.25" max="500" step="0.01" placeholder="Normal rate" value="${rate !== null ? rate.toFixed(2) : ''}" ${dis}>
          </div>
        </div>
        <details style="margin-top:6px;">
          <summary style="cursor:pointer;font-size:0.85rem;">Keep their normal rate on ${escapeHtml(c.name || 'this customer')} (${excl.size} ${excl.size === 1 ? 'person' : 'people'})</summary>
          <div class="pp-field-hint" style="margin:4px 0;">Checked people are paid their own rate here, and their hours stay in regular/OT instead of a separate ${escapeHtml(c.name || '')} column from the moment you save — hours already worked are not changed (use this for anyone with no ${escapeHtml(c.name || 'internal')} rate in your payroll system).</div>
          <div style="max-height:220px;overflow-y:auto;">${boxes || '<div class="pp-field-hint">No active people.</div>'}</div>
        </details>
      </div>`;
  }).join('');
  const stale = (IT ? IT.staleConfigRows(cfg, st.allCustomers) : []).filter(r => !st.removed.has(r.id));
  const staleHTML = stale.map((r, k) => `
      <div style="display:flex;gap:8px;align-items:center;padding:6px 8px;margin-bottom:6px;border:1px solid rgba(217,119,6,0.45);border-radius:8px;background:rgba(217,119,6,0.06);font-size:0.85rem;">
        <span class="material-icons" style="color:#D97706;font-size:18px;">warning</span>
        <span style="flex:1;"><strong>${escapeHtml(r.name)}</strong> is no longer internal time (or was deleted) — its ${r.rate !== null ? 'rate' : ''}${r.rate !== null && r.excludeCount ? ' and ' : ''}${r.excludeCount ? 'keep-normal-rate list' : ''} is ignored by payroll. Stale setting.</span>
        ${canEdit ? `<button class="btn btn-secondary btn-sm" onclick="window.ClockModule.removeStaleInternalPay(${k})">Remove</button>` : ''}
      </div>`).join('');
  const m = cfg.otMethod;
  return `
    <div class="pp-field-hint" style="margin-bottom:8px;">Pay time clocked to an internal customer (CRM → customer → Billing → <em>Internal time only</em>) at its own hourly rate. Blank = everyone keeps their normal rate. A rate applies to shifts from the moment you save it — never back-dated. Salaried people are always paid salary. Your payroll system still pays from its own rates: give each person a pay rate there named like the customer (e.g. "Shop").</div>
    ${rows || '<div class="pp-field-hint">No customers are marked <em>Internal time only</em> yet. Mark one in CRM to set a rate for it.</div>'}
    ${staleHTML}
    <div class="pp-section-title" style="margin-top:10px;">Overtime when a week has two rates</div>
    <label class="pp-toggle-row">
      <input type="radio" name="pp-ip-otmethod" value="weighted_average" ${m !== 'rate_of_work' ? 'checked' : ''} ${dis} onchange="window.ClockModule.onInternalOtMethodChange()">
      <span><strong>Weighted average (recommended)</strong><br><span class="pp-field-hint">OT premium = ½ × (all straight-time pay that week ÷ all hours that week) × OT hours. Always lawful (29 CFR 778.115).</span></span>
    </label>
    <label class="pp-toggle-row">
      <input type="radio" name="pp-ip-otmethod" value="rate_of_work" ${m === 'rate_of_work' ? 'checked' : ''} ${dis} onchange="window.ClockModule.onInternalOtMethodChange()">
      <span><strong>Rate of the work done during the OT hours</strong><br><span class="pp-field-hint">OT hours are paid at 1.5 × the rate of whatever work was being done after the 40th hour (29 CFR 778.419). A change applies from the NEXT workweek that starts after you save — never to a week already under way.</span></span>
    </label>
    <div id="pp-ip-agree-box" style="${m === 'rate_of_work' ? '' : 'display:none;'}margin:6px 0 4px;padding:8px 10px;border:1px solid rgba(220,38,38,0.45);border-radius:8px;background:rgba(220,38,38,0.06);font-size:0.85rem;color:var(--text-primary);">
      <strong>Only allowed with a written agreement with employees made before the work is performed.</strong>
      Without one, use the weighted average. Gusto figures overtime on multiple rates with the weighted average itself and its import file carries hours only, so with this option Gusto's run will not match DD's estimate — enter the OT at the right rate in Gusto by hand.
      <label style="display:flex;gap:6px;align-items:center;margin-top:6px;"><input type="checkbox" id="pp-ip-agree" ${m === 'rate_of_work' ? 'checked' : ''} ${dis}> We have that written agreement on file</label>
    </div>
    ${canEdit
      ? '<div class="pp-panel-footer"><button id="pp-ip-save-btn" class="btn btn-primary btn-sm" onclick="window.ClockModule.saveInternalPaySettings()">Save Internal-Time Pay</button></div>'
      : '<div class="pp-field-hint">Only the account owner or someone with the Workforce module can change these.</div>'}`;
}

function onInternalOtMethodChange() {
  const v = document.querySelector('input[name="pp-ip-otmethod"]:checked')?.value;
  const box = document.getElementById('pp-ip-agree-box');
  if (box) box.style.display = v === 'rate_of_work' ? '' : 'none';
}

function removeStaleInternalPay(k) {
  const st = ClockState._internalPay;
  if (!st || !window.InternalTime) return;
  const cfg = st.cfg || window.InternalTime.normalizePayConfig(null);
  const stale = window.InternalTime.staleConfigRows(cfg, st.allCustomers).filter(r => !st.removed.has(r.id));
  const row = stale[k];
  if (!row) return;
  st.removed.add(row.id);
  _paintInternalPaySection();
  window.showToast?.(`${row.name} will be removed when you save`, 'info');
}

// Build the callable payload from the form: only customers whose rate or
// exclude list CHANGED (so an untouched row never appends rate history),
// plus any stale rows marked for removal. Pure over its inputs so the unit
// test can drive it. Returns { payload } or { error }.
function _buildInternalPayPayload(customers, cfg, form, people, removed) {
  const IT = window.InternalTime;
  const out = {};
  for (let i = 0; i < customers.length; i++) {
    const c = customers[i];
    const raw = String(form.rates[i] ?? '').trim();
    let rate = null;
    if (raw !== '') {
      rate = Math.round(Number(raw) * 100) / 100;
      if (!Number.isFinite(rate) || rate < 7.25 || rate > 500) {
        return { error: `${c.name || 'Internal customer'}: rate must be between $7.25 and $500.00 an hour (or blank)` };
      }
    }
    const excl = [...new Set((form.excluded[i] || []).map(j => people[j] && people[j].id).filter(Boolean))].sort();
    const cc = cfg.customers[c.id];
    const curRate = cc ? IT.currentRate(cc) : null;
    const curExcl = cc ? [...cc.exclude].sort() : [];
    if (rate !== curRate || excl.join('\u0000') !== curExcl.join('\u0000')) {
      out[c.id] = { rate, excludePersonIds: excl };
    }
  }
  (removed || []).forEach(id => { out[id] = { rate: null, excludePersonIds: [] }; });
  const payload = {};
  if (Object.keys(out).length) payload.customers = out;
  if (form.otMethod && form.otMethod !== cfg.otMethod) {
    if (form.otMethod === 'rate_of_work' && form.agreement !== true) {
      return { error: 'Tick "We have that written agreement on file" to use the rate-of-work OT method' };
    }
    payload.otMethod = form.otMethod;
    if (form.otMethod === 'rate_of_work') payload.writtenAgreementConfirmed = true;
  }
  return { payload };
}

async function saveInternalPaySettings() {
  const st = ClockState._internalPay;
  if (!st || st.loading || st.error || !window.InternalTime) return;
  if (!_canEditInternalPay()) { window.showToast?.('Only the account owner or a Workforce-module login can change this', 'error'); return; }
  if (ClockState._ipSaveInflight) return;
  const cfg = st.cfg || window.InternalTime.normalizePayConfig(null);
  const people = (ClockState.people || []).filter(p => p && p.active !== false && !p.terminated)
    .slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  const form = { rates: [], excluded: [], otMethod: null, agreement: false };
  st.customers.forEach((c, i) => {
    form.rates[i] = document.getElementById(`pp-ip-rate-${i}`)?.value ?? '';
    form.excluded[i] = [...document.querySelectorAll(`input.pp-ip-excl[data-ci="${i}"]:checked`)].map(el => Number(el.getAttribute('data-pi')));
  });
  form.otMethod = document.querySelector('input[name="pp-ip-otmethod"]:checked')?.value || null;
  form.agreement = document.getElementById('pp-ip-agree')?.checked === true;
  // People who are on a list but no longer active are not rendered as boxes —
  // keep them on the list rather than silently dropping them on save.
  const shown = new Set(people.map(p => p.id));
  st.customers.forEach((c, i) => {
    const cc = cfg.customers[c.id];
    if (!cc) return;
    [...cc.exclude].filter(id => !shown.has(id)).forEach(id => {
      people.push({ id });
      form.excluded[i].push(people.length - 1);
    });
  });
  const built = _buildInternalPayPayload(st.customers, cfg, form, people, [...st.removed]);
  if (built.error) { window.showToast?.(built.error, 'error'); return; }
  if (!built.payload.customers && !built.payload.otMethod) { window.showToast?.('No changes to save', 'info'); return; }
  ClockState._ipSaveInflight = true;
  const btn = document.getElementById('pp-ip-save-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    const fn = httpsCallable(functions, 'setInternalTimePayConfig');
    await fn({ tenantId: ClockState.tenantId, ...built.payload });
    window.showToast?.('Internal-time pay saved', 'success');
    await _loadInternalPaySection();
  } catch (err) {
    console.error('[Clock] saveInternalPaySettings failed:', err);
    window.showToast?.(err?.message || 'Could not save internal-time pay', 'error');
  } finally {
    ClockState._ipSaveInflight = false;
    const b = document.getElementById('pp-ip-save-btn');
    if (b) { b.disabled = false; b.textContent = 'Save Internal-Time Pay'; }
  }
}

// ============================================
// WORKFORCE REPORTS TAB
// ============================================
function getWorkforceReportsHTML() {
  const periods = [
    { key: 'payperiod', label: 'Pay Period' },
    { key: 'week', label: 'This Week' },
    { key: 'lastweek', label: 'Last Week' },
    { key: 'month', label: 'This Month' },
    { key: 'lastmonth', label: 'Last Month' },
    { key: 'custom', label: 'Custom' }
  ];

  const periodBtns = periods.map(p =>
    `<button class="filter-btn ${ClockState.reportsPeriod === p.key ? 'active' : ''}" onclick="window.ClockModule.switchReportsPeriod('${p.key}')">${p.label}</button>`
  ).join('');

  // TASK-364 — merge active people + historical cache (inactive folks who
  // worked in the current pay period). Inactive-only entries get ` (inactive)`
  // plain-text suffix (UX Lead: native <select> strips badge styling on iOS Safari).
  // Sort alphabetically by name for predictable order.
  // COD Code Reviewer concern #3 — scope the historical-cache merge to pids
  // that actually appear in the CURRENT reportsData, not the entire cache.
  // Otherwise inactive folks from a previously-loaded period stay in the
  // dropdown after navigating to a different pay period that doesn't include
  // them. Also retain the currently-selected reportsEmployee even if it's not
  // in the cache snapshot so the selection doesn't silently snap to "all".
  const periodPersonIds = new Set();
  for (const entry of (ClockState.reportsData || [])) {
    if (entry.personId) periodPersonIds.add(entry.personId);
  }
  const activeById = new Map(ClockState.people.map(p => [p.id, { person: p, inactive: false }]));
  for (const [pid, person] of ClockState._historicalPeopleCache.entries()) {
    if (activeById.has(pid)) continue;
    if (!periodPersonIds.has(pid) && ClockState.reportsEmployee !== pid) continue;
    activeById.set(pid, { person, inactive: true });
  }
  const personOptions = Array.from(activeById.values())
    .sort((a, b) => (a.person.name || '').localeCompare(b.person.name || ''))
    .map(({ person: p, inactive }) => {
      const label = escapeHtml(p.name || 'Unnamed') + (inactive ? ' (inactive)' : '');
      const sel = ClockState.reportsEmployee === p.id ? 'selected' : '';
      return `<option value="${p.id}" ${sel}>${label}</option>`;
    }).join('');

  // TASK-9548 slice 3 — the Reports customer filter groups like every other
  // Workforce customer picker (vendors dropped, the current pick kept).
  const customerOptions = _clockCustomerOptionsHtml(ClockState.customers || [], { selectedId: ClockState.reportsCustomer, excludeInactive: false }); // TASK-10250 — a history filter keeps Inactive customers

  let periodExtraHTML = '';
  if (ClockState.reportsPeriod === 'custom') {
    periodExtraHTML = `
      <div style="display:flex;gap:var(--spacing-sm);align-items:center;margin-top:var(--spacing-sm);">
        <input type="date" id="reports-custom-start" class="form-control" style="width:auto;" value="${ClockState.reportsCustomStart || ''}" onchange="window.ClockModule.setReportsCustomDate('start', this.value)">
        <span style="color:var(--text-secondary);">to</span>
        <input type="date" id="reports-custom-end" class="form-control" style="width:auto;" value="${ClockState.reportsCustomEnd || ''}" onchange="window.ClockModule.setReportsCustomDate('end', this.value)">
        <button class="btn btn-primary btn-sm" onclick="window.ClockModule.applyReportsCustom()">Go</button>
      </div>`;
  } else if (ClockState.reportsPeriod === 'payperiod') {
    const ppDates = getPayPeriodDates(ClockState.reportsPayPeriodOffset);
    const ppLabel = formatPayPeriodRange(ppDates.start, ppDates.end);
    const ppType = getPayPeriodType();
    const ppTypeLabel = { weekly: 'Weekly', biweekly: 'Bi-Weekly', semimonthly: 'Semi-Monthly', monthly: 'Monthly' }[ppType] || ppType;
    const isCurrent = ClockState.reportsPayPeriodOffset === 0;
    periodExtraHTML = `
      <div style="display:flex;align-items:center;gap:10px;margin-top:8px;padding:8px 12px;background:var(--bg-secondary);border-radius:var(--radius-md);border:1px solid var(--border-light);">
        <button class="btn btn-secondary btn-module-header" onclick="window.ClockModule.navigatePayPeriod(-1)" style="display:flex;align-items:center;">
          <span class="material-icons">chevron_left</span>
        </button>
        <div style="flex:1;text-align:center;">
          <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);">${ppLabel}</div>
          <div style="font-size:0.75rem;color:var(--text-secondary);">${ppTypeLabel} Pay Period${isCurrent ? ' (Current)' : ''}</div>
        </div>
        <button class="btn btn-secondary btn-module-header" onclick="window.ClockModule.navigatePayPeriod(1)" ${isCurrent ? 'disabled style="opacity:0.4;display:flex;align-items:center;"' : 'style="display:flex;align-items:center;"'}>
          <span class="material-icons">chevron_right</span>
        </button>
      </div>`;
  }

  // Build results if data is loaded
  let resultsHTML = '';
  if (ClockState.reportsLoading) {
    resultsHTML = '<div style="text-align:center;padding:var(--spacing-xl);"><span class="btn-spinner" style="width:32px;height:32px;"></span></div>';
  } else if (ClockState.reportsData.length > 0) {
    resultsHTML = buildReportsResults();
  } else if (ClockState.reportsPeriod === 'custom' && !(ClockState.reportsCustomStart && ClockState.reportsCustomEnd)) {
    // TASK-1315 — Custom selected but no range yet: prompt, never show a prior
    // period's stale numbers under a "(Custom Period)" header.
    resultsHTML = '<div class="empty-state-small" style="margin-top:var(--spacing-lg);">Pick a start and end date, then hit <strong>Go</strong>.</div>';
  } else {
    resultsHTML = '<div class="empty-state-small" style="margin-top:var(--spacing-lg);">No data for this period. Select a period to load reports.</div>';
  }

  return `
    <div>
      <!-- Period pills -->
      <div class="workforce-pills">
        ${periodBtns}
      </div>
      ${periodExtraHTML}
      <!-- TASK-1182 — pay-period settings gear ALWAYS reachable (was payperiod-chip-only) -->
      <div class="pp-gear-row">
        <button id="pp-gear-btn" class="btn btn-secondary btn-neon-settings btn-module-header pp-gear-btn" onclick="window.ClockModule.togglePayPeriodSettings()" title="Pay Period &amp; Overtime Settings" aria-expanded="${ClockState.payPeriodPanelOpen ? 'true' : 'false'}" aria-controls="pp-settings-panel">
          <span class="material-icons">settings</span>
          <span class="pp-gear-label">Pay Settings</span>
        </button>
      </div>
      ${buildPayPeriodSettingsPanelHTML()}
      <!-- Filters -->
      <div style="display:flex;gap:10px;margin-top:10px;flex-wrap:wrap;">
        <div style="flex:1;min-width:140px;position:relative;">
          <span class="material-icons" style="position:absolute;left:12px;top:50%;transform:translateY(-50%);font-size:20px;color:var(--brand-blue);pointer-events:none;">person</span>
          <select class="form-control" style="width:100%;padding:12px 14px 12px 40px;border-radius:12px;border:2px solid #e2e8f0;font-size:0.9rem;font-weight:600;color:var(--text-primary);background:#fff;transition:border-color 0.15s;" onfocus="this.style.borderColor='var(--brand-blue)'" onblur="this.style.borderColor='#e2e8f0'" onchange="window.ClockModule.switchReportsEmployee(this.value)">
            <option value="all" ${ClockState.reportsEmployee === 'all' ? 'selected' : ''}>All Employees</option>
            ${personOptions}
          </select>
        </div>
        <div style="flex:1;min-width:140px;position:relative;">
          <span class="material-icons" style="position:absolute;left:12px;top:50%;transform:translateY(-50%);font-size:20px;color:var(--brand-blue);pointer-events:none;">business</span>
          <select class="form-control" style="width:100%;padding:12px 14px 12px 40px;border-radius:12px;border:2px solid #e2e8f0;font-size:0.9rem;font-weight:600;color:var(--text-primary);background:#fff;transition:border-color 0.15s;" onfocus="this.style.borderColor='var(--brand-blue)'" onblur="this.style.borderColor='#e2e8f0'" onchange="window.ClockModule.switchReportsCustomer(this.value)">
            <option value="all" ${ClockState.reportsCustomer === 'all' ? 'selected' : ''}>All Customers</option>
            ${customerOptions}
          </select>
        </div>
      </div>
      <div id="reports-results" style="margin-top:var(--spacing-lg);">
        ${resultsHTML}
      </div>
    </div>
  `;
}

function getReportsPeriodDates(period) {
  const now = new Date();
  let start, end;
  if (period === 'payperiod') {
    return getPayPeriodDates(ClockState.reportsPayPeriodOffset);
  } else if (period === 'week') {
    start = getWeekMonday(now);
    end = new Date(start); end.setDate(start.getDate() + 7); end.setMilliseconds(-1);
  } else if (period === 'lastweek') {
    const thisMonday = getWeekMonday(now);
    start = new Date(thisMonday); start.setDate(thisMonday.getDate() - 7);
    end = new Date(thisMonday); end.setMilliseconds(-1);
  } else if (period === 'month') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  } else if (period === 'lastmonth') {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  } else if (period === 'custom') {
    if (ClockState.reportsCustomStart && ClockState.reportsCustomEnd) {
      start = new Date(ClockState.reportsCustomStart + 'T00:00:00');
      end = new Date(ClockState.reportsCustomEnd + 'T23:59:59.999');
    } else {
      return null;
    }
  }
  return { start, end };
}

async function switchReportsPeriod(period) {
  ClockState.reportsPeriod = period;
  if (period === 'payperiod') ClockState.reportsPayPeriodOffset = -1; // open on the last COMPLETED period (the one being paid), not the in-progress current one
  if (period !== 'custom') {
    await loadReportsData();
  } else if (ClockState.reportsCustomStart && ClockState.reportsCustomEnd) {
    // TASK-1315 (COD ❌ fix) — entering Custom WITH a range already set (e.g. the
    // user loaded a custom range, tabbed to Pay Period, then back): RELOAD it so
    // reportsData matches the custom period, never the last non-custom period's
    // rows relabeled "(Custom Period)".
    await loadReportsData();
  } else {
    // Entering Custom with no range yet: drop the prior period's data so the
    // re-render below can't relabel it "(Custom Period)"; the pick-dates prompt
    // shows until the user picks a range + hits Go.
    ClockState.reportsLoadSeq++;   // TASK-1315 — invalidate any in-flight load so it can't repopulate over this clear
    ClockState.reportsData = [];
    ClockState.reportsWeekData = [];
    ClockState.reportsLoading = false; // else a superseded in-flight load leaves the spinner stuck (it returns early w/o resetting this)
  }
  const content = document.getElementById('workforce-content');
  if (content) content.innerHTML = getWorkforceReportsHTML();
}

async function navigatePayPeriod(direction) {
  if (direction > 0 && ClockState.reportsPayPeriodOffset >= 0) return;
  ClockState.reportsPayPeriodOffset += direction;
  if (ClockState.reportsPayPeriodOffset > 0) ClockState.reportsPayPeriodOffset = 0;
  await loadReportsData();
  const content = document.getElementById('workforce-content');
  if (content) content.innerHTML = getWorkforceReportsHTML();
}

async function savePayPeriodSettings() {
  if (!canEditPaySettings()) { window.showToast?.(PAY_SETTINGS_VIEW_ONLY_MSG, 'error'); return; }
  const ppType = document.getElementById('pp-type-select')?.value || 'biweekly';
  const ppAnchor = document.getElementById('pp-anchor-date')?.value || '';
  const weekStart = parseInt(document.getElementById('pp-week-start')?.value ?? 1, 10);
  const otThreshold = parseInt(document.getElementById('pp-ot-threshold')?.value ?? 40, 10);
  const requireShiftNotes = document.getElementById('pp-require-shift-notes')?.checked === true;
  if (isNaN(weekStart) || isNaN(otThreshold) || otThreshold < 1) return;
  // F-H3 (Tier-1 security audit): mirror server clamps to fail fast.
  if (otThreshold > 60) {
    window.showToast?.('Overtime threshold must be 1-60 hours', 'error');
    return;
  }
  if (weekStart < 0 || weekStart > 6) return;
  // TASK-441 — oversized-shift FLAG threshold (display only; never caps pay).
  // Bound 1–168 (legit overnight/breakdown hauls exist). Persisted via the same
  // owner-or-workforce-gated setOvertimeThreshold callable as a single source of truth.
  const oversizedRaw = document.getElementById('pp-oversized-threshold')?.value;
  let oversized = parseInt(oversizedRaw ?? 16, 10);
  if (isNaN(oversized)) oversized = 16;
  if (oversized < 1 || oversized > 168) {
    window.showToast?.('Single-shift review threshold must be 1-168 hours', 'error');
    return;
  }
  const ppAllowed = ['weekly', 'biweekly', 'semimonthly', 'monthly'];
  if (!ppAllowed.includes(ppType)) {
    window.showToast?.('Invalid pay period type', 'error');
    return;
  }
  if (ppAnchor && !/^\d{4}-\d{2}-\d{2}$/.test(ppAnchor)) {
    window.showToast?.('Pay period anchor must be a valid date', 'error');
    return;
  }

  // TASK-1182 slice 1 — disable-while-inflight guard against double-save (standard
  // button-debounce lesson). The save PATH below is unchanged; this only blocks a
  // second concurrent submit and re-enables in finally.
  if (ClockState._ppSaveInflight) return;
  ClockState._ppSaveInflight = true;
  const saveBtn = document.getElementById('pp-save-btn');
  if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving…'; }

  const tenant = ClockState.tenantId;
  try {
    // F-H3: route through setOvertimeThreshold callable (server clamps + owner-or-workforce gate, TASK-9527).
    // Same callable handles all four pay-period/OT settings under one transaction.
    const fn = httpsCallable(functions, 'setOvertimeThreshold');
    const payload = {
      tenantId: tenant,
      payPeriodType: ppType,
      workWeekStartDay: weekStart,
      overtimeThreshold: otThreshold,
      oversizedShiftHours: oversized,
      requireShiftNotes,
    };
    if (ppAnchor) payload.payPeriodAnchor = ppAnchor;
    await fn(payload);
    if (window.AppState?.settings) {
      window.AppState.settings.payPeriodType = ppType;
      window.AppState.settings.workWeekStartDay = weekStart;
      window.AppState.settings.overtimeThreshold = otThreshold;
      window.AppState.settings.oversizedShiftHours = oversized;
      window.AppState.settings.requireShiftNotes = requireShiftNotes;
      if (ppAnchor) window.AppState.settings.payPeriodAnchor = ppAnchor;
    }
    ClockState.weeklyCache = {};
    // TASK-1181 — must wipe in lockstep with weeklyCache. A stale
    // fullyLoaded flag pointing at a wiped cache would skip every reload and
    // render ZERO hours for everyone right after a pay-period settings save.
    ClockState.weeklyCacheFullyLoaded = {};
    ClockState.workforceWeekStart = null;
    ClockState.reportsPayPeriodOffset = 0;
    await loadReportsData();
    const content = document.getElementById('workforce-content');
    if (content) content.innerHTML = getWorkforceReportsHTML();
    window.showToast?.('Pay period settings saved', 'success');
  } catch (err) {
    console.error('[Clock] Error saving pay period settings:', err);
    const msg = err?.message || 'Error saving settings';
    window.showToast?.(msg, 'error');
  } finally {
    ClockState._ppSaveInflight = false;
    // On the error path the DOM wasn't re-rendered, so restore the live button.
    // On success getWorkforceReportsHTML() already painted a fresh enabled one.
    const btn = document.getElementById('pp-save-btn');
    if (btn) { btn.disabled = false; btn.textContent = 'Save Pay Settings'; }
  }
}

function switchReportsEmployee(id) {
  ClockState.reportsEmployee = id;
  const resultsEl = document.getElementById('reports-results');
  if (resultsEl && ClockState.reportsData.length > 0) {
    resultsEl.innerHTML = buildReportsResults();
  }
}

function switchReportsCustomer(id) {
  ClockState.reportsCustomer = id;
  const resultsEl = document.getElementById('reports-results');
  if (resultsEl && ClockState.reportsData.length > 0) {
    resultsEl.innerHTML = buildReportsResults();
  }
}

function setReportsCustomDate(which, value) {
  if (which === 'start') ClockState.reportsCustomStart = value;
  else ClockState.reportsCustomEnd = value;
}

// TASK-1315 — the Go button reads the two date inputs DIRECTLY (not the state the
// onchange was trusted to have populated — the inputs are re-rendered by innerHTML
// replacement, so a value can be on screen while state is stale/null, which made Go
// silently keep showing the prior pay period). Sync state from the DOM, then load.
async function applyReportsCustom() {
  const s = document.getElementById('reports-custom-start')?.value || '';
  const e = document.getElementById('reports-custom-end')?.value || '';
  ClockState.reportsCustomStart = s || null;
  ClockState.reportsCustomEnd = e || null;
  if (s && e && s > e) {
    window.showToast?.('Start date must be on or before the end date.', 'error');
    return;
  }
  await loadReportsData();
  // Re-render so the period header + inputs reflect the resolved range (or the
  // pick-dates prompt if the user hit Go with an incomplete range).
  const content = document.getElementById('workforce-content');
  if (content) content.innerHTML = getWorkforceReportsHTML();
}

async function loadReportsData() {
  const dates = getReportsPeriodDates(ClockState.reportsPeriod);
  if (!dates) {
    // TASK-1315 — the ONLY !dates case is Custom with no range yet. Never leave a
    // PRIOR period's data on screen behind a silent return (it renders because
    // reportsData.length > 0, mislabeled "(Custom Period)"). Clear it + prompt.
    ClockState.reportsLoadSeq++;   // invalidate any in-flight load so a slow prior fetch can't repopulate stale data over this clear
    ClockState.reportsData = [];
    ClockState.reportsWeekData = [];
    ClockState.reportsLoading = false;
    const el = document.getElementById('reports-results');
    if (el) el.innerHTML = '<div class="empty-state-small" style="margin-top:var(--spacing-lg);">Pick a start and end date, then hit <strong>Go</strong>.</div>';
    return;
  }

  // TASK-1315 — claim this load. Any newer load OR clear bumps reportsLoadSeq,
  // which makes the guards below discard our (now-stale) result instead of
  // clobbering the current period's data on rapid period/tab switching.
  const _seq = ++ClockState.reportsLoadSeq;
  ClockState.reportsLoading = true;
  // Re-query the DOM both BEFORE and AFTER the async fetch — callers like
  // navigateWorkforceWeek may replace the parent workforce-content div
  // synchronously after firing this load, which detaches whatever
  // reports-results we captured up front.
  const preEl = document.getElementById('reports-results');
  if (preEl) preEl.innerHTML = '<div style="text-align:center;padding:var(--spacing-xl);"><span class="btn-spinner" style="width:32px;height:32px;"></span></div>';

  try {
    // TASK-949 — query the WEEK-EXPANDED window [weekStart(period.start) ..
    // weekEnd(period.end)] in ONE pass, then derive the period-bounded
    // reportsData by client-side filter. The fringe (≤6 days each side) gives
    // cross-period visibility of workweeks that straddle a semimonthly/monthly
    // boundary, WITHOUT widening the period-bounded sums (reg/customer/date/
    // per-diem) that reportsData drives. reportsWeekData feeds OT only.
    // TASK-1507 — company-zone workweeks around the period.
    const { start: winStart, end: winEnd } = _companyWeekWindow(dates.start, dates.end);
    const q = query(
      collection(db, 'tenants', ClockState.tenantId, 'clockEntries'),
      where('clockIn', '>=', Timestamp.fromDate(winStart)),
      where('clockIn', '<=', Timestamp.fromDate(winEnd)),
      orderBy('clockIn', 'desc')
    );
    const snap = await getDocs(q);
    if (_seq !== ClockState.reportsLoadSeq) return; // a newer load/clear superseded us — discard (don't clobber current data)
    ClockState.reportsWeekData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // Period-bounded view (identical set to the old direct [start,end] query).
    const { startMs: sMs, endMs: eMs } = _companyPeriodMs(dates.start, dates.end); // TASK-1507
    ClockState.reportsData = ClockState.reportsWeekData.filter(e => {
      const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
      const t = cin.getTime();
      return t >= sMs && t <= eMs;
    });
  } catch (err) {
    console.error('[Clock] Error loading reports data:', err);
    // TASK-1315 — only wipe if we're STILL the current load. A superseded load's
    // fetch throwing (transient blip) must not erase the newer period's fresh data.
    if (_seq === ClockState.reportsLoadSeq) {
      ClockState.reportsData = [];
      ClockState.reportsWeekData = [];
    }
  }

  // TASK-364 — warm historical-people cache for any inactive folks who had
  // hours this period. Populates `_historicalPeopleCache` so the filter
  // dropdown (rendered next by callers via getWorkforceReportsHTML) shows
  // inactive-with-hours people, and so buildReportsResults / export functions
  // get full enrichment. Non-blocking on failure — cache helper logs + returns.
  await fetchHistoricalPeopleForPeriod();
  if (_seq !== ClockState.reportsLoadSeq) return; // superseded during the people-cache warm — the newer load owns the render

  ClockState.reportsLoading = false;
  ClockState.reportsExpanded = {};
  const postEl = document.getElementById('reports-results');
  if (postEl) postEl.innerHTML = buildReportsResults();
}

function getFilteredReportsData() {
  let data = ClockState.reportsData;
  if (ClockState.reportsEmployee !== 'all') {
    data = data.filter(e => e.personId === ClockState.reportsEmployee);
  }
  if (ClockState.reportsCustomer !== 'all') {
    data = data.filter(e => e.customerId === ClockState.reportsCustomer);
  }
  return data;
}

// TASK-915 — flagged-shift cache for the "Confirm with Employee" SMS.
// Populated on every buildReportsResults() run: pid → the LARGEST flagged
// (oversized) shift for that person this period ({ date, hours, jobTitle }).
// confirmHoursWithEmployee reads it at click time so the SMS draft can name
// the specific punch the ⚠️ Needs Review badge is about.
let _rptFlaggedShiftByPid = {};

function buildReportsResults() {
  const data = getFilteredReportsData();

  if (data.length === 0) {
    _rptFlaggedShiftByPid = {};
    return '<div class="empty-state-small">No entries found for this period and filters.</div>';
  }

  let totalHours = 0;
  const personTotals = {};
  const customerTotals = {};
  // TASK-441 — count entries that look like a forgotten clock-out so the report
  // surfaces them. We FLAG (never cap): the inflated hours still sum into the
  // totals; the badge tells the biller which punches to verify.
  const _rptOversizedTh = getOversizedShiftThreshold();
  let oversizedCount = 0;

  data.forEach(entry => {
    const hrs = entry.totalHours || 0;
    totalHours += hrs;
    // TASK-1180 — a driver-corrected forgotten clock-out (needsReview /
    // driverAdjustedClockOut stamped by the jobboard shrink flow) stays flagged
    // even though its hours are now normal — otherwise the correction makes the
    // punch VANISH from review. Same consolidation on all flag surfaces.
    const _entryDriverAdjusted = entryReviewMarked(entry); // TASK-9794 — false once the office accepted it
    const _isOversized = entryNeedsReview(entry, _rptOversizedTh);
    if (_isOversized) oversizedCount++;
    const pid = entry.personId;
    if (!personTotals[pid]) personTotals[pid] = { name: entry.personName, hours: 0, customers: {}, oversized: false, driverAdjusted: false, maxShiftHrs: 0, oversizedShifts: [] };
    if (_isOversized) {
      personTotals[pid].oversized = true;
      // stuck-shift-morning — closed late at the next clock-in: its own reason.
      if (_entryDriverAdjusted && entry.status === 'completed') {
        if (entry.closedLate === true) personTotals[pid].closedLate = true;
        else if (_clockReviewIsCodeOnly(entry)) personTotals[pid].codeUnchecked = true; // TASK-9941 F S2
        else personTotals[pid].driverAdjusted = true;
      }
      if (entryMidnightOffJob(entry)) personTotals[pid].midnightOffJob = true; // TASK-10040
      if (hrs > personTotals[pid].maxShiftHrs) personTotals[pid].maxShiftHrs = hrs;
      // Keep the actual flagged shifts so the expanded row can show WHICH
      // punches to verify, not just that one exists.
      personTotals[pid].oversizedShifts.push({ date: entry.clockIn, hours: hrs, jobTitle: entry.jobTitle || 'No Job' });
    }
    personTotals[pid].hours += hrs;
    const cName = entry.customerName || 'Unassigned';
    const cId = entry.customerId || 'none';
    if (!personTotals[pid].customers[cId]) personTotals[pid].customers[cId] = { name: cName, hours: 0 };
    personTotals[pid].customers[cId].hours += hrs;

    if (!customerTotals[cId]) customerTotals[cId] = { name: cName, hours: 0, people: new Set(), jobs: {} };
    customerTotals[cId].hours += hrs;
    customerTotals[cId].people.add(pid);
    const jId = entry.jobId || 'none';
    const jName = entry.jobTitle || 'No Job';
    if (!customerTotals[cId].jobs[jId]) customerTotals[cId].jobs[jId] = { name: jName, hours: 0, people: new Set() };
    customerTotals[cId].jobs[jId].hours += hrs;
    customerTotals[cId].jobs[jId].people.add(pid);
  });

  // TASK-915 — refresh the flagged-shift cache (largest flagged shift wins;
  // that's the punch the badge tooltip's maxShiftHrs refers to).
  _rptFlaggedShiftByPid = {};
  Object.entries(personTotals).forEach(([pid, pt]) => {
    if (pt.oversized && pt.oversizedShifts.length) {
      const top = [...pt.oversizedShifts].sort((a, b) => b.hours - a.hours)[0];
      _rptFlaggedShiftByPid[pid] = { ...top, personName: pt.name };
    }
  });

  // Overtime — per-person, CROSS-PERIOD workweek aware (TASK-949). A workweek
  // straddling a semimonthly/monthly boundary has its FULL-week OT paid in the
  // period it ENDS; regular is chronologically allocated by day. Computed via
  // the shared computeCrossPeriodOT so this on-screen display, the payroll CSV,
  // and the driver My-Timesheet view can NEVER disagree. _periodOTByPid is
  // reused by the per-person Employee Breakdown rows below.
  const dates = getReportsPeriodDates(ClockState.reportsPeriod);
  let overtimeHours = 0;
  const _periodOTByPid = {}; // pid -> computeCrossPeriodOT result
  let _periodHasStraddle = false;
  if (dates) {
    Object.keys(personTotals).forEach(pid => {
      const person = getPersonForReport(pid);
      const ot = computePersonPeriodOT(pid, person, dates);
      _periodOTByPid[pid] = ot;
      overtimeHours += ot.periodOT;
      if (ot.straddleNoteApplies) _periodHasStraddle = true;
    });
  }

  const peopleCount = Object.keys(personTotals).length;
  const avgPerPerson = peopleCount > 0 ? totalHours / peopleCount : 0;

  const statsHTML = `
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:var(--spacing-sm);margin-bottom:var(--spacing-lg);">
      <div style="background:var(--bg-elevated);border-radius:var(--radius-md);padding:var(--spacing-md);border-left:4px solid var(--brand-blue);">
        <div style="font-size:0.75rem;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;">Total Hours</div>
        <div style="font-size:1.5rem;font-weight:700;color:var(--text-primary);">${totalHours.toFixed(1)}</div>
      </div>
      <div style="background:var(--bg-elevated);border-radius:var(--radius-md);padding:var(--spacing-md);border-left:4px solid ${overtimeHours > 0 ? 'var(--status-danger)' : 'var(--status-success)'};">
        <div style="font-size:0.75rem;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;">Overtime Hours</div>
        <div style="font-size:1.5rem;font-weight:700;color:${overtimeHours > 0 ? 'var(--status-danger)' : 'var(--text-primary)'};">${overtimeHours.toFixed(1)}</div>
      </div>
      <div style="background:var(--bg-elevated);border-radius:var(--radius-md);padding:var(--spacing-md);border-left:4px solid #8B5CF6;">
        <div style="font-size:0.75rem;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;">People Worked</div>
        <div style="font-size:1.5rem;font-weight:700;color:var(--text-primary);">${peopleCount}</div>
      </div>
      <div style="background:var(--bg-elevated);border-radius:var(--radius-md);padding:var(--spacing-md);border-left:4px solid var(--accent-teal);">
        <div style="font-size:0.75rem;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;">Avg Hrs/Person</div>
        <div style="font-size:1.5rem;font-weight:700;color:var(--text-primary);">${avgPerPerson.toFixed(1)}</div>
      </div>
      ${oversizedCount > 0 ? `
      <div style="background:rgba(239,68,68,0.06);border-radius:var(--radius-md);padding:var(--spacing-md);border-left:4px solid #EF4444;" title="${oversizedCount} shift${oversizedCount !== 1 ? 's' : ''} flagged for review — over ${_rptOversizedTh}h (likely forgotten clock-out) or driver-corrected clock-out. Verify these punches before running payroll; hours are NOT auto-capped.">
        <div style="font-size:0.75rem;color:#B91C1C;text-transform:uppercase;letter-spacing:0.5px;font-weight:700;">⚠️ Needs Review</div>
        <div style="font-size:1.5rem;font-weight:700;color:#B91C1C;">${oversizedCount}</div>
      </div>` : ''}
    </div>
  `;

  // Employee breakdown
  const sortedPeople = Object.entries(personTotals).sort((a, b) => b[1].hours - a[1].hours);
  const empRows = sortedPeople.map(([pid, p]) => {
    // TASK-949 — cross-period workweek OT via the shared helper (same numbers as
    // the payroll CSV + driver view). Reg is the chronological first-40 share
    // (NOT p.hours − OT, which would go negative in the cap case); Total = Reg +
    // OT = PAID hours (can exceed hours physically worked in-period when a
    // straddling week's OT lands here — the legend below the table explains it).
    const _ot = _periodOTByPid[pid] || computePersonPeriodOT(pid, getPersonForReport(pid), dates);
    const personOT = _ot.periodOT;
    const personReg = _ot.periodReg;
    const personTotalPaid = _ot.periodTotal;
    const isExpanded = ClockState.reportsExpanded[pid];

    const customerRows = isExpanded ? Object.entries(p.customers).sort((a, b) => b[1].hours - a[1].hours).map(([, c]) =>
      `<tr style="background:var(--bg-secondary);">
        <td style="padding:4px 8px 4px 32px;color:var(--text-secondary);font-size:0.8rem;">\u2192 ${escapeHtml(c.name)}</td>
        <td style="padding:4px 8px;text-align:center;font-size:0.8rem;color:var(--text-primary);">${c.hours.toFixed(1)}</td>
        <td></td><td></td>
      </tr>`
    ).join('') : '';

    // Flagged (oversized) shifts \u2014 shown on expand ABOVE the customer subtotals
    // so the biller can see WHICH punches tripped the \u26a0\ufe0f Needs Review badge.
    const fmtShiftDate = (ts) => { const d = ts?.toDate?.() || new Date(ts); return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); };
    const flaggedShiftRows = (isExpanded && p.oversizedShifts?.length) ? `
      <tr style="background:rgba(239,68,68,0.06);">
        <td colspan="4" style="padding:4px 8px 2px 32px;font-size:0.72rem;font-weight:700;color:#B91C1C;text-transform:uppercase;letter-spacing:0.5px;">\u26a0\ufe0f Review These Shifts</td>
      </tr>
      ${p.oversizedShifts.sort((a, b) => (b.hours - a.hours)).map(s => `
        <tr style="background:rgba(239,68,68,0.04);">
          <td style="padding:2px 8px 2px 40px;color:#B91C1C;font-size:0.8rem;">\u2192 ${escapeHtml(fmtShiftDate(s.date))} \u00b7 ${s.hours.toFixed(1)}h \u00b7 ${escapeHtml(s.jobTitle)}</td>
          <td colspan="3"></td>
        </tr>
      `).join('')}` : '';

    // TASK-915 — the badge is now a clickable pill: opens a confirm modal that
    // texts the employee asking them to review the flagged hours.
    // stopPropagation is REQUIRED — the parent <tr> onclick toggles row expand.
    const _rptWhyFlagged = [];
    if (p.maxShiftHrs > _rptOversizedTh) _rptWhyFlagged.push(`A single shift is ${p.maxShiftHrs.toFixed(1)}h (over the ${_rptOversizedTh}h review threshold) — likely a forgotten clock-out.`);
    if (p.driverAdjusted) _rptWhyFlagged.push('A shift\'s clock-out was corrected by the driver (forgotten-clock-out fix) — verify the adjusted time and reason.');
    if (p.codeUnchecked) _rptWhyFlagged.push('A punch went through at the shared tablet without a PIN check. Confirm it was really this person.');
    if (p.closedLate) _rptWhyFlagged.push('A shift never clocked out and was closed late — the finish time was entered at the next clock-in. Verify it before payroll.');
    if (p.midnightOffJob) _rptWhyFlagged.push('A Shop / no-job shift ran past midnight — likely a forgotten clock-out.'); // TASK-10040
    const rptReviewBadge = p.oversized
      ? ` <span style="display:inline-block;margin-left:6px;padding:1px 7px;border-radius:10px;font-size:0.68rem;font-weight:700;color:#B91C1C;background:rgba(239,68,68,0.12);border:1px solid rgba(239,68,68,0.35);white-space:nowrap;cursor:pointer;" onclick="event.stopPropagation(); window.ClockModule.confirmHoursWithEmployee('${pid}')" title="${_rptWhyFlagged.join(' ')} Verify the punch before payroll. Click to text this employee asking them to review their hours.">⚠️ Needs Review</span>`
      : '';
    return `
      <tr style="border-bottom:1px solid var(--border-light);cursor:pointer;${p.oversized ? 'background:rgba(239,68,68,0.04);' : ''}" onclick="window.ClockModule.toggleReportsExpand('${pid}')">
        <td style="padding:6px 8px;font-weight:600;color:var(--text-primary);">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;color:var(--text-secondary);">${isExpanded ? 'expand_more' : 'chevron_right'}</span>
          ${escapeHtml(p.name)}${rptReviewBadge}
        </td>
        <td style="padding:6px 8px;text-align:center;color:var(--text-primary);">${personReg.toFixed(1)}</td>
        <td style="padding:6px 8px;text-align:center;color:${personOT > 0 ? 'var(--status-danger)' : 'var(--text-secondary)'};">${personOT > 0 ? personOT.toFixed(1) : '-'}</td>
        <td style="padding:6px 8px;text-align:center;font-weight:600;color:var(--text-primary);">${personTotalPaid.toFixed(1)}</td>
      </tr>
      ${flaggedShiftRows}
      ${customerRows}
    `;
  }).join('');

  // Employee Reg/OT/Total breakdown for the selected period (sum across weeks)
  const periodEmpBreakdown = `
    <div style="margin-bottom:var(--spacing-lg);">
      <h3 style="margin:0 0 var(--spacing-sm);color:var(--text-primary);">Employee Breakdown (${ClockState.reportsPeriod === 'custom' ? 'Custom Period' : 'Selected Period'})</h3>
      <div style="overflow-x:auto;">
        <table style="width:100%;border-collapse:collapse;font-size:0.85rem;">
          <thead>
            <tr style="border-bottom:2px solid var(--border-medium);">
              <th style="padding:6px 8px;text-align:left;color:var(--text-primary);">Employee</th>
              <th style="padding:6px 8px;text-align:center;color:var(--text-primary);">Reg</th>
              <th style="padding:6px 8px;text-align:center;color:var(--text-primary);">OT</th>
              <th style="padding:6px 8px;text-align:center;color:var(--text-primary);">Total</th>
            </tr>
          </thead>
          <tbody>${empRows}</tbody>
        </table>
      </div>
      ${_periodHasStraddle ? `
      <div style="display:flex;align-items:flex-start;gap:6px;margin-top:6px;font-size:0.72rem;color:var(--text-secondary);line-height:1.35;">
        <span class="material-icons" style="font-size:14px;color:var(--brand-blue);margin-top:1px;flex-shrink:0;">info</span>
        <span>Overtime premium includes hours earned during the prior pay period due to a workweek spanning multiple pay periods.</span>
      </div>` : ''}
    </div>
  `;

  // Top-right action bar — Print + Export dropdown + "Show notes" toggle.
  // Pulled out of the Weekly Hours section per Chris 2026-05-07 so the
  // buttons sit above the report content where they can be reached
  // without scrolling. The "Show notes" toggle controls whether entry
  // notes are included in the Detailed Entries CSV export and on the
  // employee-detail print — Lacey (TITS bookkeeper) doesn't want them
  // in the payroll-bound CSV.
  const showNotes = !!ClockState.reportsShowNotes;
  // TASK-1321 — extra print toggles show ONLY when a single customer is selected
  // (the Customer Hours Summary layout). The payroll (unfiltered) action bar is
  // byte-for-byte unchanged — the _custFiltered branch below adds nothing then.
  const _custFiltered = !!(ClockState.reportsCustomer && ClockState.reportsCustomer !== 'all');
  const _laborDetailDefault = window.AppState?.settings?.clockCustomerLaborDetailDefault === true;
  const _showLaborDetail = ClockState.reportsShowLaborDetail === null ? _laborDetailDefault : !!ClockState.reportsShowLaborDetail;
  const _showDailyDetail = !!ClockState.reportsShowDailyDetail;
  const actionsBar = `
    <div style="display:flex;justify-content:flex-end;align-items:center;gap:var(--spacing-md);margin-bottom:var(--spacing-md);flex-wrap:wrap;">
      <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:0.82rem;color:var(--text-secondary);user-select:none;">
        <input type="checkbox" ${showNotes ? 'checked' : ''} onchange="window.ClockModule.toggleReportsShowNotes(this.checked)" style="cursor:pointer;width:14px;height:14px;">
        ${_custFiltered ? 'Include work descriptions (written by drivers — review before sending)' : 'Show notes'}
      </label>
      ${_custFiltered ? `
      <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:0.82rem;color:var(--text-secondary);user-select:none;">
        <input type="checkbox" ${_showDailyDetail ? 'checked' : ''} onchange="window.ClockModule.toggleReportsShowDailyDetail(this.checked)" style="cursor:pointer;width:14px;height:14px;">
        Show daily detail
      </label>
      <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:0.82rem;color:var(--text-secondary);user-select:none;">
        <input type="checkbox" ${_showLaborDetail ? 'checked' : ''} onchange="window.ClockModule.toggleReportsShowLaborDetail(this.checked)" style="cursor:pointer;width:14px;height:14px;">
        Show labor detail (employee hours)
      </label>` : ''}
      <button class="btn btn-sm wf-action-btn" onclick="window.ClockModule.printWorkforceReport()">
        <span class="material-icons" style="font-size:16px;">print</span> Print
      </button>
      <div style="position:relative;display:inline-block;" id="export-dropdown-wrap">
        <button class="btn btn-sm wf-action-btn wf-action-btn--export" onclick="window.ClockModule.toggleExportMenu()">
          <span class="material-icons" style="font-size:16px;">download</span> Export <span class="material-icons" style="font-size:14px;margin-left:-2px;">arrow_drop_down</span>
        </button>
        <div id="export-dropdown-menu" style="display:none;position:absolute;right:0;top:100%;margin-top:4px;background:var(--bg-elevated);border:1px solid var(--border-medium);border-radius:var(--radius-md);box-shadow:var(--shadow-md);z-index:100;min-width:200px;">
          <button onclick="window.ClockModule.exportPayrollCSV()" style="display:flex;align-items:center;gap:8px;width:100%;padding:10px 14px;border:none;background:none;cursor:pointer;font-size:0.85rem;color:var(--text-primary);text-align:left;" onmouseenter="this.style.background='var(--bg-secondary)'" onmouseleave="this.style.background='none'">
            <span class="material-icons" style="font-size:16px;color:var(--brand-blue);">summarize</span> Payroll Summary (CSV)
          </button>
          <button onclick="window.ClockModule.exportDetailedCSV()" style="display:flex;align-items:center;gap:8px;width:100%;padding:10px 14px;border:none;background:none;cursor:pointer;font-size:0.85rem;color:var(--text-primary);text-align:left;border-top:1px solid var(--border-light);" onmouseenter="this.style.background='var(--bg-secondary)'" onmouseleave="this.style.background='none'">
            <span class="material-icons" style="font-size:16px;color:var(--accent-teal);">list_alt</span> Detailed Entries (CSV)
          </button>
          <button onclick="window.ClockModule.exportAdjustmentsCSV()" style="display:flex;align-items:center;gap:8px;width:100%;padding:10px 14px;border:none;background:none;cursor:pointer;font-size:0.85rem;color:var(--text-primary);text-align:left;border-top:1px solid var(--border-light);" onmouseenter="this.style.background='var(--bg-secondary)'" onmouseleave="this.style.background='none'">
            <span class="material-icons" style="font-size:16px;color:#8B5CF6;">receipt_long</span> Adjustments Report (CSV)
          </button>
        </div>
      </div>
    </div>
  `;

  // Weekly Hours grid (reuse from workforce tab)
  const weeklyGridHTML = buildWeeklyHoursTable();
  const empBreakdownHTML = `
    <div style="margin-bottom:var(--spacing-lg);">
      <h3 style="margin:0 0 var(--spacing-sm);color:var(--text-primary);">Weekly Hours</h3>
      ${weeklyGridHTML}
    </div>
  `;

  // Customer breakdown
  const sortedCustomers = Object.entries(customerTotals).sort((a, b) => b[1].hours - a[1].hours);
  const custRows = sortedCustomers.map(([, c]) => {
    const sortedJobs = Object.entries(c.jobs).sort((a, b) => b[1].hours - a[1].hours);
    const hasMultipleJobs = sortedJobs.length > 1 || (sortedJobs.length === 1 && sortedJobs[0][0] !== 'none');
    const jobRows = hasMultipleJobs ? sortedJobs.map(([, j]) => `
      <tr style="border-bottom:1px solid var(--border-light);background:var(--bg-elevated);">
        <td style="padding:4px 8px 4px 28px;font-size:0.8rem;color:var(--text-secondary);">${escapeHtml(j.name)}</td>
        <td style="padding:4px 8px;text-align:center;font-size:0.8rem;color:var(--text-secondary);">${j.hours.toFixed(1)}</td>
        <td style="padding:4px 8px;text-align:center;font-size:0.8rem;color:var(--text-secondary);">${j.people.size}</td>
      </tr>
    `).join('') : '';
    return `
      <tr style="border-bottom:1px solid var(--border-light);">
        <td style="padding:6px 8px;font-weight:600;color:var(--text-primary);">${escapeHtml(c.name)}</td>
        <td style="padding:6px 8px;text-align:center;color:var(--text-primary);">${c.hours.toFixed(1)}</td>
        <td style="padding:6px 8px;text-align:center;color:var(--text-primary);">${c.people.size}</td>
      </tr>
      ${jobRows}
    `;
  }).join('');

  const custBreakdownHTML = `
    <div>
      <h3 style="margin-bottom:var(--spacing-sm);color:var(--text-primary);">Customer Breakdown</h3>
      <div style="overflow-x:auto;">
        <table style="width:100%;border-collapse:collapse;font-size:0.85rem;">
          <thead>
            <tr style="border-bottom:2px solid var(--border-medium);">
              <th style="padding:6px 8px;text-align:left;color:var(--text-primary);">Customer</th>
              <th style="padding:6px 8px;text-align:center;color:var(--text-primary);">Hours</th>
              <th style="padding:6px 8px;text-align:center;color:var(--text-primary);">People</th>
            </tr>
          </thead>
          <tbody>${custRows}</tbody>
        </table>
      </div>
    </div>
  `;

  // Final order on page: Stats → Top-right action bar → Weekly Hours grid →
  // Employee Breakdown summary → Customer Breakdown.
  return statsHTML + actionsBar + empBreakdownHTML + periodEmpBreakdown + custBreakdownHTML;
}

function toggleReportsExpand(personId) {
  ClockState.reportsExpanded[personId] = !ClockState.reportsExpanded[personId];
  const resultsEl = document.getElementById('reports-results');
  if (resultsEl) resultsEl.innerHTML = buildReportsResults();
}

// TASK-915 — "Confirm with Employee": clicking the ⚠️ Needs Review badge in the
// Employee Breakdown texts the employee a draft asking them to review the
// flagged shift. Confirm modal first (showConfirm is textContent-based, XSS-safe);
// TASK-9826: sends through sendHoursReviewSms, which takes the personId (never
// a phone number) and builds the text server-side from the same template the
// confirm modal previews below. Owner or `workforce` module; rate limited;
// still backed by sendTenantAwareSms (TCPA STOP opt-out + credit limits).
let _confirmHoursSmsInFlight = false;

async function confirmHoursWithEmployee(pid) {
  if (!_assertWorkforceAccess('confirmHoursWithEmployee')) return;
  if (_confirmHoursSmsInFlight) return; // debounce — one send at a time

  const flagged = _rptFlaggedShiftByPid[pid];
  // getPersonForReport, NOT ClockState.people.find — historical/inactive workers
  // with hours this period live in the historical cache, not the live roster.
  const person = getPersonForReport(pid);
  const name = (person?.name || flagged?.personName || 'this employee').trim();
  const phone = (person?.phone || '').trim();
  if (!phone) {
    window.showToast?.('No phone on file for ' + name, 'error');
    return;
  }
  const firstName = name.split(/\s+/)[0] || 'there';

  // Shift date = the largest flagged shift this period (what the badge is about).
  // The server formats the dates from these YYYY-MM-DD values (local calendar
  // day) — the preview below uses the same labels.
  const _ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  let shiftDateStr = 'this period';
  let shiftDateIso = null;
  if (flagged?.date) {
    const d = flagged.date?.toDate?.() || new Date(flagged.date);
    if (!isNaN(d)) {
      shiftDateStr = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      shiftDateIso = _ymd(d);
    }
  }
  const periodDates = getReportsPeriodDates(ClockState.reportsPeriod);
  const periodEndOk = periodDates?.end && !isNaN(periodDates.end);
  const periodEndStr = periodEndOk
    ? periodDates.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const periodEndIso = periodEndOk ? _ymd(periodDates.end) : null;

  const message = `Hi ${firstName}, please review your hours for ${shiftDateStr}${periodEndStr ? ` (period ending ${periodEndStr})` : ''}. If you notice any errors, please let your supervisor know.`;

  const ok = await (window.showConfirm
    ? window.showConfirm('Text Employee to Confirm Hours', `Send this SMS to ${name} (${phone})? "${message}"`, { confirmText: 'Send SMS', cancelText: 'Cancel' })
    : Promise.resolve(window.confirm(`Send this SMS to ${name} (${phone})?\n\n"${message}"`)));
  if (!ok) return;

  _confirmHoursSmsInFlight = true;
  try {
    const sendSms = httpsCallable(functions, 'sendHoursReviewSms');
    const res = await sendSms({ tenantId: ClockState.tenantId, personId: pid, shiftDate: shiftDateIso, periodEnd: periodEndIso });
    const r = res?.data;
    if (r && r.ok === false) {
      // Soft failure from sendTenantAwareSms (opt-out, no SMS pack, credit, limits)
      const why = r.reason === 'sms_opted_out'
        ? `${name} has opted out of SMS (replied STOP)`
        : (r.error || 'SMS could not be sent');
      window.showToast?.(why, 'error');
    } else {
      window.showToast?.(`SMS sent to ${name} ✓`, 'success');
    }
  } catch (err) {
    // Log ONLY the error object — never the phone number or message body
    // (matches the hr.js doc-signing SMS pattern).
    console.error('[Clock] confirmHoursWithEmployee send failed:', err);
    // TASK-9826 — the server's refusals (no phone, rate limit, no access) are
    // plain-English; show them. Anything internal stays generic.
    const _why = err?.code && !/internal|unknown/.test(String(err.code)) && err.message ? `: ${err.message}` : '';
    window.showToast?.(`Failed to send SMS to ${name}${_why}`, 'error');
  } finally {
    _confirmHoursSmsInFlight = false;
  }
}

// ============================================
// PAYROLL EXPORT FUNCTIONS
// ============================================
function toggleExportMenu() {
  const menu = document.getElementById('export-dropdown-menu');
  if (!menu) return;
  const isOpen = menu.style.display !== 'none';
  menu.style.display = isOpen ? 'none' : 'block';
  if (!isOpen) {
    // Close on outside click
    const closeHandler = (e) => {
      if (!document.getElementById('export-dropdown-wrap')?.contains(e.target)) {
        menu.style.display = 'none';
        document.removeEventListener('click', closeHandler);
      }
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 0);
  }
}

function toggleReportsShowNotes(checked) {
  ClockState.reportsShowNotes = !!checked;
  const resultsEl = document.getElementById('reports-results');
  if (resultsEl) resultsEl.innerHTML = buildReportsResults();
}

// TASK-1321 — customer-layout-only print toggles. These affect ONLY the printed
// Customer Hours Summary (printWorkforceReport), not the on-screen results, so
// they just set state — no re-render needed.
function toggleReportsShowDailyDetail(checked) {
  ClockState.reportsShowDailyDetail = !!checked;
}
function toggleReportsShowLaborDetail(checked) {
  ClockState.reportsShowLaborDetail = !!checked;
}

function downloadCSV(filename, csvContent) {
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function getReportsPeriodLabel() {
  const dates = getReportsPeriodDates(ClockState.reportsPeriod);
  if (!dates) return 'custom';
  const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fmt(dates.start)} - ${fmt(dates.end)}`;
}

function getReportsPeriodFilename() {
  const dates = getReportsPeriodDates(ClockState.reportsPeriod);
  if (!dates) return 'custom';
  // TASK-335 — local-time formatter; toISOString flips end-of-day to next UTC day.
  return `${_fmtLocalYmd(dates.start)}_${_fmtLocalYmd(dates.end)}`;
}

function escapeCSV(val) {
  if (val == null) return '';
  let str = String(val);
  // CSV formula-injection guard (TASK-453, COD Adversary): employee-controlled
  // free-text fields (name/email/address/hrDeductions) flow into the payroll
  // CSV the admin opens in Excel/Sheets. A value starting with = + - @ (or
  // tab/CR) is interpreted as a formula. Neutralize by prefixing a single quote
  // so the cell renders as literal text. Applied before quote-wrapping.
  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str;
  }
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

// TASK-1169 — shared pay-adjustments loader (per-diem / bonus / deduction /
// reimbursement totals per person for a pay-period date range). Extracted
// VERBATIM from exportPayrollCSV so the Payroll Prep screen (payroll.js) and
// the payroll CSV can never diverge on adjustment math — single source of
// truth, same recurring-adjustment + legacy-perDiemEntries handling.
// `tenantId` param keeps it callable from the standalone 'payroll' route where
// ClockState may not be initialized. Errors are swallowed (logged) and return
// empty maps — matches the CSV's historical fail-open behavior.
async function loadPayAdjustmentTotals(dates, tenantId) {
  const tid = tenantId || ClockState.tenantId;
  const perDiemByPerson = {};
  const bonusByPerson = {};
  const deductionItemsByPerson = {};
  const deductionAmountByPerson = {}; // TASK-940 — numeric deduction total per person (for the DEDUCTIONS AMOUNT column)
  const reimbursementByPerson = {};
  if (dates && tid) {
    try {
      const startStr = _fmtLocalYmd(dates.start);
      const endStr = _fmtLocalYmd(dates.end);
      // Legacy per-diem entries
      const pdSnap = await getDocs(
        query(
          collection(db, 'tenants', tid, 'perDiemEntries'),
          where('date', '>=', startStr),
          where('date', '<=', endStr)
        )
      );
      pdSnap.docs.forEach(d => {
        const pd = d.data();
        if (!perDiemByPerson[pd.personId]) perDiemByPerson[pd.personId] = 0;
        perDiemByPerson[pd.personId] += pd.amount || 0;
      });
      // payAdjustments (new system — per-diem, bonus, deduction, reimbursement)
      const adjSnap = await getDocs(collection(db, 'tenants', tid, 'payAdjustments'));
      adjSnap.docs.forEach(d => {
        const adj = d.data();
        const isRecurring = adj.recurring === true;
        const dt = adj.date?.toDate?.() || (adj.date ? new Date(adj.date) : null);
        if (!dt) return;
        const dtStr = _fmtLocalYmd(dt);
        if (!isRecurring && (dtStr < startStr || dtStr > endStr)) return;
        const pid = adj.personId;
        const amt = adj.amount || 0;
        if (adj.type === 'per-diem' || adj.type === 'Per-Diem') {
          perDiemByPerson[pid] = (perDiemByPerson[pid] || 0) + amt;
        } else if (adj.type === 'bonus' || adj.type === 'Bonus') {
          bonusByPerson[pid] = (bonusByPerson[pid] || 0) + amt;
        } else if (adj.type === 'deduction' || adj.type === 'Deduction') {
          // Itemized for the CSV — keep each deduction's category + amount so the
          // CPA sees "Child Support: $200; Car Payment: $150" instead of a lump sum.
          (deductionItemsByPerson[pid] = deductionItemsByPerson[pid] || []).push({
            label: adj.category || 'Deduction', amount: amt
          });
          // TASK-940 — accumulate the numeric total too (DEDUCTIONS AMOUNT column).
          // NOTE (Domain mitigation): legacy free-text `hrDeductions` (e.g. Justin "305",
          // Pardon "Loan Repayment: $300") is NOT parsed into this total — only structured
          // `payAdjustments` of type deduction. Flag to Lacey at rollout.
          deductionAmountByPerson[pid] = (deductionAmountByPerson[pid] || 0) + amt;
        } else if (adj.type === 'reimbursement' || adj.type === 'Reimbursement') {
          reimbursementByPerson[pid] = (reimbursementByPerson[pid] || 0) + amt;
        }
      });
    } catch (err) { console.error('[Clock] Error loading adjustments for export:', err); }
  }
  return { perDiemByPerson, bonusByPerson, deductionItemsByPerson, deductionAmountByPerson, reimbursementByPerson };
}

// ── TASK-1558: effective-dated pay-rate helpers ─────────────────────────────
// Fetch tenants/{tid}/payRates/{personId} history docs for a set of people.
// BEST-EFFORT: a read denial or fetch failure degrades to "no history" —
// payroll then behaves byte-identically to pre-1558 (current rate for the
// whole period). Never throws, never blocks a payroll build.
async function loadPayRateHistories(tid, personIds) {
  const map = new Map();
  const ids = [...new Set((personIds || []).filter(Boolean))];
  if (!ids.length || !window.PayRates) return map;
  await Promise.all(ids.map(pid =>
    getDoc(doc(db, 'tenants', tid, 'payRates', pid))
      .then(s => {
        const h = s.exists() ? s.data()?.history : null;
        if (Array.isArray(h) && h.length) map.set(pid, h);
      })
      .catch(err => { console.warn('[Clock] payRates history read failed for', pid, err?.message || err); })
  ));
  return map;
}

// TASK-9387 — customers for the internal-time payroll breakdown. ALWAYS one
// fresh uncapped read (a payroll export is rare; a limit() could drop an
// internal customer). 🔴 NO fallback to the live subscription: it is
// orderBy('name'), which silently omits any customer doc without a `name`
// field — that customer would look hard-deleted and be classified by its
// entries' name snapshot instead of its flag (COD 2026-09-24). On failure
// return null so callers SAY the breakdown is unavailable.
// TASK-9694 S6a — every clock customer read goes through the directory
// (window.CustomerSourceCore, utils/customerSource.js; read at call time).
function _clockCustomerSource() {
  return window.CustomerSourceCore.create({ db, collection, doc, getDoc, getDocs, onSnapshot, query, orderBy });
}

async function _loadPayrollCustomers(tid) {
  try {
    // TASK-9694 S6a — the directory carries name + internalTimeOnly (all this
    // breakdown reads) and every directory doc has a `name`, so nothing drops.
    return await _clockCustomerSource().getAll(tid);
  } catch (err) {
    console.warn('[Clock] payroll customer load failed — internal-time breakdown unavailable:', err?.message || err);
    return null;
  }
}

// TASK-9387 — internal hours per person. Takes the WEEK-EXPANDED entries (the
// same set computeCrossPeriodOT scores paid REG/OT from, billable entries
// included — they count toward the OT threshold) and reports, per internal
// customer:
//   • hours WORKED in the period (total / byWeek);
//   • hours PAYABLE this period (payable / payableByWeek) — each workweek is
//     walked in clock-in order with the SAME threshold, exempt rule and
//     endsInPeriod rule as computeCrossPeriodOT, so across all customers
//     payable sums to its periodReg + periodOT. Worked ≠ payable when a week
//     ends NEXT period (hours past 40 are paid then) or started LAST period
//     (its OT is paid here);
//   • last period's hours in a week that ends here (priorByWeek) and the part
//     already PAID last period (priorPaidByWeek) — context for the weighted-
//     average rate, never hours to move (moving them pays twice).
// Workweek key = getWeekKey (company zone, the tenant's workWeekStartDay —
// TITS = 1, Monday, verified live) — never hardcoded here.
// Returns null when the breakdown can't be computed (customers failed to load,
// or the InternalTime module is missing) — callers surface that, never hide it.
// TASK-9388 — payCfg (normalized payConfig, or null) applies the per-customer
// "keep their normal rate" list: an excluded person's hours on that customer
// are not broken out (they still count toward the week's OT threshold).
function _payrollInternalBreakdown(weekEntries, customers, periodStart, periodEnd, isExempt, payCfg) {
  if (!window.InternalTime || !Array.isArray(customers)) return null;
  // TASK-1507 — company-zone period instants, same as computeCrossPeriodOT.
  const _pb = (periodStart && periodEnd) ? _companyPeriodMs(periodStart, periodEnd) : null;
  const pStart = _pb ? _pb.startMs : NaN;
  const pEnd = _pb ? _pb.endMs : NaN;
  const bounded = Number.isFinite(pStart) && Number.isFinite(pEnd);
  const cinOf = (e) => e.clockIn?.toDate?.() || new Date(e.clockIn);
  return window.InternalTime.aggregate(weekEntries, customers, {
    payConfig: payCfg || null,
    weekKeyOf: (e) => getWeekKey(cinOf(e)) || '',
    timeOf: (e) => cinOf(e).getTime(),
    otThreshold: getOTThreshold(),
    isExempt: typeof isExempt === 'function' ? isExempt : () => false,
    position: (e) => {
      if (!bounded) return 'in';
      const t = cinOf(e).getTime();
      if (t < pStart) return 'prior';
      return t <= pEnd ? 'in' : 'skip';
    },
    weekEndsInPeriod: (e) => {
      if (!bounded) return true;
      const wr = getWeekRange(cinOf(e));
      return !!wr && wr.endMs <= pEnd && wr.endMs >= pStart;
    },
  });
}

// TASK-9388 — the tenant's internal-time pay config (per-internal-customer
// $/hr + exclude-people list + OT method), tenants/{tid}/payConfig/internalTime.
// Server-written only (setInternalTimePayConfig); readable by the owner or a
// workforce-module member. A MISSING doc is ok:true with an empty config (no
// rates, no exclusions, weighted-average OT). A FAILED read is ok:false — the
// caller must SAY rates/exclusions were not applied, never silently skip them.
async function _loadInternalPayConfig(tid) {
  if (!window.InternalTime || !tid) return { ok: false, cfg: null, raw: null };
  try {
    const s = await getDoc(doc(db, 'tenants', tid, 'payConfig', 'internalTime'));
    const raw = s.exists() ? s.data() : null;
    return { ok: true, raw, cfg: window.InternalTime.normalizePayConfig(raw) };
  } catch (err) {
    console.warn('[Clock] internal-time pay config read failed:', err?.message || err);
    return { ok: false, cfg: null, raw: null };
  }
}

// Pricing context for _computePersonPay1558: the customer index (explicit-flag
// classification) + the normalized config. null when nothing can be priced.
function _internalPayCtx(customers, payCfg) {
  if (!window.InternalTime || !Array.isArray(customers) || !payCfg) return null;
  const anyRate = Object.keys(payCfg.customers || {}).some(k => (payCfg.customers[k].rateHistory || []).length);
  if (!anyRate && payCfg.otMethod !== 'rate_of_work') return null;
  return { index: window.InternalTime.buildIndex(customers), cfg: payCfg };
}

// Map raw clockEntry docs → the pure-engine entry shape. `keyOf(entry)` (TASK-9388)
// tags the entries an internal rate applies to; omitted → no tag (1558 shape).
function _payEngineEntries(rawEntries, keyOf) {
  return (rawEntries || []).map(e => {
    const out = {
      inMs: (e.clockIn?.toDate?.() || new Date(e.clockIn)).getTime(),
      outMs: e.clockOut ? (e.clockOut?.toDate?.() || new Date(e.clockOut)).getTime() : null,
      hours: e.totalHours || 0,
      completed: e.status === 'completed',
    };
    const k = typeof keyOf === 'function' ? keyOf(e) : null;
    if (k) out.internalKey = k;
    return out;
  });
}

// Run the per-segment pay engine for one person. `otResult` MUST be the
// computeCrossPeriodOT output for the same person/period — the engine never
// re-derives OT hours (83h-bug invariant), it only PRICES them. Returns null
// when there's nothing to price beyond the legacy single rate (no rate
// history AND no internal-rated hours) or on any failure.
//
// TASK-9388 — `internalCtx` (from _internalPayCtx) prices hours on an
// explicit-flag internal customer at that customer's rate in effect at the
// time, unless the person is on its exclude list. DECIDED (task row: "Skip
// salaried/exempt automatically" + "exempt get no OT under either option"):
//   • salaried (payrollExemptKind 'salary' — hrEmploymentType salary or FLSA
//     'exempt'): NO internal rate — salary pays every hour;
//   • hourly no-OT ('ot' / 'mca' — TASK-9394 mca_exempt): the internal rate
//     DOES apply to straight time; OT stays 0 (computeCrossPeriodOT already
//     zeroes it for isPayrollExempt), so neither OT method adds a premium.
function _computePersonPay1558(pid, person, weekEntriesRaw, otResult, dates, payHistories, internalCtx) {
  try {
    const hist = payHistories?.get?.(pid);
    if (!window.PayRates || !dates?.start || !dates?.end) return null;
    const useInternal = !!(internalCtx && window.InternalTime && payrollExemptKind(person) !== 'salary');
    const keyOf = useInternal
      ? (e) => window.InternalTime.rateKeyFor(e, internalCtx.index, internalCtx.cfg)
      : null;
    const entries = _payEngineEntries(weekEntriesRaw, keyOf);
    const keys = [...new Set(entries.map(e => e.internalKey).filter(Boolean))];
    if (!hist && !keys.length) return null;
    const internalRates = {};
    // TASK-9388 COD — per-PERSON rate history: the customer's rate at each
    // rate/exclusion change, null while this person is on its keep-normal-rate
    // list. Nothing before the first save (never back-dated).
    keys.forEach(k => { internalRates[k] = window.InternalTime.personRateHistory(internalCtx.cfg.customers[k], pid); });
    return window.PayRates.computePeriodPay({
      entries,
      // The ONLY source of OT hours: the engine prices exactly week.reg +
      // week.ot from computeCrossPeriodOT (no threshold of its own).
      otWeeks: otResult?.weeks || [],
      history: hist || null,
      person,
      internalRates,
      opts: {
        // TASK-1507 — company-zone instants, the same bounds computeCrossPeriodOT uses.
        periodStartMs: _companyPeriodMs(dates.start, dates.end).startMs,
        periodEndMs: _companyPeriodMs(dates.start, dates.end).endMs,
        weekStartDay: getWorkWeekStartDay(),
        periodType: getPayPeriodType(),
        // Forward-only OT method: resolved at each workweek's START.
        otMethodHistory: internalCtx?.cfg?.otMethodHistory || [],
      },
    });
  } catch (err) {
    console.warn('[Clock] per-segment pay calc failed for', pid, err?.message || err);
    return null;
  }
}

// TASK-9388 — OT-method wording shared by the CSV / Prep so it can't drift.
function _otMethodLabel(method) {
  return method === 'rate_of_work'
    ? 'OT at 1.5x the rate of the work performed during the OT hours (29 CFR 778.419 — written agreement required)'
    : 'OT at the weighted-average regular rate (29 CFR 778.115)';
}

// TASK-9388 — one CSV cell describing a person's internal-rate pay estimate:
// "Shop 14.00h @ $20.00 | est gross $1,252.70 (OT at …)". '' when no internal
// rate priced anything for this person this period.
function _describeInternalPayCell(pay, labelOf, exempt) {
  if (!pay || !pay.internalRated) return '';
  const parts = [];
  const segs = (pay.internalSegments || []).map(s => `${labelOf(s.key)} ${s.hours.toFixed(2)}h @ $${s.rate.toFixed(2)}`);
  if (segs.length) parts.push(segs.join('; '));
  if (pay.supported === false) {
    parts.push('REVIEW BY HAND: ' + (pay.notes || []).join('; '));
    return parts.join(' | ');
  }
  const otWeeks = (pay.weeks || []).filter(w => w.otHours > 1e-9);
  const how = exempt ? 'no OT — exempt'
    : !otWeeks.length ? 'no OT'
      : _otMethodLabel(otWeeks.some(w => w.method === 'rate_of_work') ? 'rate_of_work' : 'weighted_average');
  if (pay.gross !== null) parts.push(`est gross $${pay.gross.toFixed(2)} (${how})`);
  const fallbacks = (pay.notes || []).filter(n => /instead of the rate of work performed/.test(n));
  if (fallbacks.length) parts.push(fallbacks.join('; '));
  return parts.join(' | ');
}

// Shared change formatter: describeChange + the entered-after-close warning
// (a backdated append that touches an already-paid period must be LOUD).
function _describeChange1558(c) {
  return window.PayRates.describeChange(c)
    + (c.enteredAfterPeriodEnd ? ' [ENTERED AFTER PERIOD CLOSE — verify vs what was paid]' : '');
}

// One-line CSV/print description of a person's mid-period rate situation.
function _describeRateChangeCell(pay) {
  if (!pay || !pay.changes || !pay.changes.length) return '';
  const parts = [pay.changes.map(_describeChange1558).join('; ')];
  if (pay.segments && pay.segments.length > 1) {
    parts.push('split ' + pay.segments.map(s => `${s.hours.toFixed(2)}h @ $${s.rate.toFixed(2)}`).join(' + '));
  }
  if (pay.supported === false) {
    parts.push('REVIEW BY HAND: ' + (pay.notes || []).join('; '));
  } else if (pay.multiRate && pay.gross !== null) {
    parts.push((pay.weeks || []).some(w => w.method === 'rate_of_work')
      ? `est gross $${pay.gross.toFixed(2)} (OT at 1.5x rate of work performed, 29 CFR 778.419)`
      : `est gross $${pay.gross.toFixed(2)} (OT at weighted-avg regular rate, 29 CFR 778.115)`);
  }
  return parts.join(' | ');
}

// perf (appColdLoadGzip, perf/app-coldload-slim2) — the Workforce > Reports
// "Payroll CSV" export body lives in the LAZY views/clockPayrollCsv.js. Same
// name + same contract (async, no args) for window.ClockModule.exportPayrollCSV;
// deps are handed over, never imported (pitfall #1). A failed load toasts and is
// retried on the next tap. The workforce gate (_assertWorkforceAccess) is still
// the body's first line, unchanged.
// Editing clockPayrollCsv.js: bump its ?v= here AND clock.js's ?v= in app.js.
let _payrollCsvLoad = null;
async function exportPayrollCSV() {
  let m;
  try {
    m = await (_payrollCsvLoad ||= __ddChunkWrap(import('./clockPayrollCsv.js?v=1')).then((mod) => {
      mod._bind({
        ClockState, _assertWorkforceAccess, _clockReviewIsCodeOnly, _computePersonPay1558,
        _describeInternalPayCell, _describeRateChangeCell, _fmtLocalYmd, _internalPayCtx,
        _loadInternalPayConfig, _loadPayrollCustomers, _otMethodLabel, _payrollInternalBreakdown,
        _pph, _pphCfg, addDateHours, collection, computePersonPeriodOT, db, downloadCSV,
        entryNeedsReview, entryReviewMarked, escapeCSV, fetchHistoricalPeopleForPeriod, getDocs,
        getFilteredReportsData, getOversizedShiftThreshold, getPersonForReport,
        getReportsPeriodDates, getReportsPeriodFilename, getWeekDayIndex, getWeekKey,
        isPayrollExempt, loadFinalPeriodPeople, loadPayAdjustmentTotals, loadPayRateHistories,
        perDiemManualSuperseded, pvDayOpts, pvEligibleFn, pvName, pvPolicy
      });
      return mod;
    }));
  } catch (e) {
    _payrollCsvLoad = null;
    console.error('[Clock] payroll CSV module failed to load:', e);
    if (window.showToast) window.showToast('Could not start the payroll export. Check your connection and try again.', 'error');
    return;
  }
  return m.exportPayrollCSV();
}

function exportDetailedCSV() {
  // TASK-360 — detailed CSV export is workforce-only.
  if (!_assertWorkforceAccess('exportDetailedCSV')) return;
  const data = getFilteredReportsData();
  if (data.length === 0) {
    window.showToast('No data to export', 'error');
    return;
  }

  // TASK-441 — flag oversized shifts (likely forgotten clock-outs) in the
  // payroll-bound CSV so the biller can spot them. Hours are NOT altered.
  const _csvOversizedTh = getOversizedShiftThreshold();
  const includeNotes = !!ClockState.reportsShowNotes;
  const headers = ['Date', 'Employee', 'Role', 'Rate', 'Customer', 'Job', 'Clock In', 'Clock Out', 'Hours', 'Review'];
  if (includeNotes) headers.push('Notes');
  const rows = [headers.join(',')];

  // Sort by date then employee name
  const sorted = [...data].sort((a, b) => {
    const aDate = a.clockIn?.toDate?.() || new Date(a.clockIn);
    const bDate = b.clockIn?.toDate?.() || new Date(b.clockIn);
    const dateDiff = aDate - bDate;
    if (dateDiff !== 0) return dateDiff;
    return (a.personName || '').localeCompare(b.personName || '');
  });

  sorted.forEach(entry => {
    const cin = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    const cout = entry.clockOut?.toDate?.() || (entry.clockOut ? new Date(entry.clockOut) : null);
    const dateFmt = cin.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const timeFmt = d => d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '';

    // TASK-9189 — this CSV is payroll-bound and its Date column is the clock-IN
    // date, so a time-only Clock Out silently understated every overnight shift
    // to whoever keys the pay run. The shared formatter appends the "(+1d)"
    // marker; it contains no comma, so the column stays single-field.
    const _csvSpan = window.Fmt.shiftSpan(cin, cout);

    const _entryHrs = entry.totalHours || 0;
    // TASK-1180 — consolidate the driver-corrected-clock-out flag into the
    // existing _needsReview marker (never a parallel concept): a shrunk-to-
    // normal entry stays visible in the payroll-bound CSV.
    const _needsReview = entryNeedsReview(entry, _csvOversizedTh); // TASK-9794 — accepted entries drop the token
    // TASK-9189 — a stored total that disagrees with its own two timestamps is a
    // data defect (totalHours is always derived from them; there is no break
    // deduction), and payroll is the last place it can be caught. Kept INSIDE the
    // existing Review column, prefixed with the same "NEEDS REVIEW" token any
    // downstream filter already matches on, rather than adding a parallel column.
    const _csvDrift = entry.status === 'completed'
      ? window.Fmt.shiftHoursDrift(_csvSpan.spanHours, entry.totalHours)
      : null;
    const _reviewCell = (_needsReview || _csvDrift)
      ? ('NEEDS REVIEW' + (_csvDrift ? ' (hours ' + _csvDrift.short + ')' : ''))
      : '';
    const row = [
      dateFmt,
      escapeCSV(entry.personName || ''),
      entry.role || 'NON_CDL',
      (entry.rate || 0).toFixed(2),
      escapeCSV(entry.customerName || ''),
      escapeCSV(entry.jobTitle || ''),
      timeFmt(cin),
      cout ? _csvSpan.outText : timeFmt(cout),
      _entryHrs.toFixed(2),
      _reviewCell,
    ];
    if (includeNotes) row.push(escapeCSV(entry.clockOutNotes || entry.notes || ''));
    rows.push(row.join(','));
  });

  const filename = `timesheet-${getReportsPeriodFilename()}.csv`;
  downloadCSV(filename, rows.join('\n'));
  window.showToast('Detailed timesheet downloaded', 'success');

  // Close dropdown
  const menu = document.getElementById('export-dropdown-menu');
  if (menu) menu.style.display = 'none';
}

async function exportAdjustmentsCSV() {
  // TASK-360 — adjustments CSV export is workforce-only.
  if (!_assertWorkforceAccess('exportAdjustmentsCSV')) return;
  const dates = getReportsPeriodDates(ClockState.reportsPeriod);
  if (!dates) {
    window.showToast('Select a period first', 'error');
    return;
  }
  const startStr = _fmtLocalYmd(dates.start);
  const endStr = _fmtLocalYmd(dates.end);
  const tenant = ClockState.tenantId;

  try {
    // Load all adjustments
    const adjSnap = await getDocs(collection(db, 'tenants', tenant, 'payAdjustments'));
    const items = [];
    adjSnap.docs.forEach(d => {
      const data = d.data();
      const dt = data.date?.toDate?.() || (data.date ? new Date(data.date) : null);
      if (!dt) return;
      const dtStr = _fmtLocalYmd(dt);
      if (dtStr < startStr || dtStr > endStr) return;
      items.push({ ...data, id: d.id, _date: dt });
    });

    // Also load legacy perDiemEntries
    const pdSnap = await getDocs(
      query(
        collection(db, 'tenants', tenant, 'perDiemEntries'),
        where('date', '>=', startStr),
        where('date', '<=', endStr)
      )
    );
    pdSnap.docs.forEach(d => {
      const data = d.data();
      items.push({
        ...data, id: d.id, type: 'per-diem', category: 'Per-Diem',
        _date: new Date(data.date + 'T12:00:00')
      });
    });

    if (items.length === 0) {
      window.showToast('No adjustments for this period', 'error');
      const menu = document.getElementById('export-dropdown-menu');
      if (menu) menu.style.display = 'none';
      return;
    }

    items.sort((a, b) => (a.category || a.type || '').localeCompare(b.category || b.type || '') || a._date - b._date);

    const headers = ['DATE', 'EMPLOYEE', 'TYPE', 'CATEGORY', 'AMOUNT', 'CUSTOMER', 'JOB', 'NOTES'];
    const rows = [headers.join(',')];

    // Category totals
    const catTotals = {};
    items.forEach(adj => {
      const cat = adj.category || adj.type || 'Other';
      const isDeduction = (adj.type || '').toLowerCase() === 'deduction';
      const dateFmt = adj._date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      const typeLabel = (adj.type || '').charAt(0).toUpperCase() + (adj.type || '').slice(1);

      rows.push([
        dateFmt,
        escapeCSV(adj.personName || ''),
        escapeCSV(typeLabel),
        escapeCSV(cat),
        (isDeduction ? '-' : '') + (adj.amount || 0).toFixed(2),
        escapeCSV(adj.customerName || ''),
        escapeCSV(adj.jobTitle || ''),
        escapeCSV(adj.notes || '')
      ].join(','));

      if (!catTotals[cat]) catTotals[cat] = { count: 0, total: 0 };
      catTotals[cat].count++;
      catTotals[cat].total += isDeduction ? -(adj.amount || 0) : (adj.amount || 0);
    });

    // Add summary section
    rows.push('');
    rows.push('CATEGORY SUMMARY');
    rows.push('Category,Count,Total');
    Object.entries(catTotals).sort((a, b) => a[0].localeCompare(b[0])).forEach(([cat, data]) => {
      rows.push(`${escapeCSV(cat)},${data.count},${data.total.toFixed(2)}`);
    });

    const filename = `adjustments-${getReportsPeriodFilename()}.csv`;
    downloadCSV(filename, rows.join('\n'));
    window.showToast('Adjustments report downloaded', 'success');
  } catch (err) {
    console.error('[Clock] Error exporting adjustments:', err);
    window.showToast('Error exporting adjustments', 'error');
  }

  const menu = document.getElementById('export-dropdown-menu');
  if (menu) menu.style.display = 'none';
}

// ============================================
// TASK-1169 — PAYROLL PREP DATA (Workforce → Payroll Prep, payroll.js)
// ============================================
// Assembles the per-person pay-period rollup + exceptions that the Payroll
// Prep screen renders and exports to an EXTERNAL standalone Gusto (CSV /
// copy-paste — no Gusto API). Lives in clock.js so every number comes from
// the SAME single-source-of-truth helpers the payroll CSV uses:
//   • OT        → computeCrossPeriodOT / isPayrollExempt (🔴 re-derivation is
//                 FORBIDDEN — see the 83h OT bug note at computePerWeekOT)
//   • per-diem  → TASK-939 CSV-authoritative auto-calc (hrPerDiem × eligible
//                 days ≥8h); manual payAdjustments are the fallback ONLY —
//                 never stacked. Per diem is EXPORTED in the Reimbursement
//                 column (routing, not a tax ruling — the tenant decides).
//   • adjust.   → loadPayAdjustmentTotals (shared with exportPayrollCSV)
//   • period    → getPayPeriodDates off settings.payPeriodType/payPeriodAnchor
// Access: owner (ownerId) or the workforce module (PayrollModule.isPayrollAdmin, TASK-9804) — re-asserted here;
// the real boundary is Firestore rules (clockEntries / payAdjustments reads).
// Rates returned here feed a MASKED-by-default UI and are NEVER exported to
// the Gusto file (Gusto owns pay rates).
async function buildPayrollPrepData(offset = -1, tenantId = null) {
  // Defense-in-depth: the payroll tab + payroll.js already gate on
  // isPayrollAdmin; deny here too so a console call from a scoped member
  // renders nothing (their Firestore reads would also be denied).
  if (!(window.PayrollModule?.isPayrollAdmin?.())) return null;
  const tid = tenantId || ClockState.tenantId || window.AppState?.tenant;
  if (!tid) return null;

  const dates = getPayPeriodDates(offset);
  if (!dates || !dates.start || !dates.end) return null;

  // TASK-9178 — W-2 vs 1099 decides who is in the Gusto employee file. No
  // local fallback: a second copy of the rule is exactly the drift this task
  // removed, so a missing rule refuses the whole prep (payroll.js shows it).
  const _wc = window.WorkerClass;
  if (!_wc || typeof _wc.payrollRow !== 'function') {
    throw new Error('Worker classification rule not loaded — reload the page before running payroll.');
  }

  // Week-expanded query window (mirrors loadReportsData / TASK-949) so
  // straddling workweeks are scored on their FULL-week hours. Local arrays —
  // never clobbers ClockState.reports* (the Reports tab owns those).
  // TASK-1507 — company-zone workweeks around the period.
  const { start: winStart, end: winEnd } = _companyWeekWindow(dates.start, dates.end);
  const snap = await getDocs(query(
    collection(db, 'tenants', tid, 'clockEntries'),
    where('clockIn', '>=', Timestamp.fromDate(winStart)),
    where('clockIn', '<=', Timestamp.fromDate(winEnd)),
    orderBy('clockIn', 'asc')
  ));
  const weekData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  const { startMs: sMs, endMs: eMs } = _companyPeriodMs(dates.start, dates.end); // TASK-1507
  const periodData = weekData.filter(e => {
    const t = (e.clockIn?.toDate?.() || new Date(e.clockIn)).getTime();
    return t >= sMs && t <= eMs;
  });

  // People: reuse the live subscription when this is the clock view's tenant;
  // otherwise (standalone 'payroll' route) fetch actives directly.
  let activePeople;
  if (ClockState.tenantId === tid && ClockState.people.length) {
    activePeople = ClockState.people;
  } else {
    try {
      const ps = await getDocs(query(collection(db, 'tenants', tid, 'people'), where('active', '==', true), orderBy('name')));
      activePeople = ps.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.error('[Clock] PayrollPrep people fetch failed:', err);
      activePeople = [];
    }
  }
  // TASK-10034 — people marked No Longer Employed stay on payroll through the
  // pay period that contains their last day (loadFinalPeriodPeople). null =
  // the roster read failed → an exception below says so.
  const _ppStartYmd = window.TenantLocalTime.carrierYmd(dates.start);
  const _finalPeople = await loadFinalPeriodPeople(tid, _ppStartYmd);
  {
    const _seen = new Set(activePeople.map(p => p.id));
    activePeople = activePeople.concat((_finalPeople || []).filter(p => !_seen.has(p.id)));
  }
  const peopleById = new Map(activePeople.map(p => [p.id, p]));
  const activeIds = new Set(activePeople.filter(p => !(p.active === false || p.terminated)).map(p => p.id));
  // TASK-364 pattern — historical/inactive folks with hours this period get
  // full enrichment (rate, FLSA, hrPerDiem) via the shared historical cache.
  const missingIds = [...new Set(periodData.map(e => e.personId).filter(pid => pid && !peopleById.has(pid)))];
  const toFetch = [];
  for (const pid of missingIds) {
    const cached = ClockState._historicalPeopleCache.get(pid);
    if (cached) peopleById.set(pid, cached);
    else toFetch.push(pid);
  }
  if (toFetch.length) {
    const fetched = await Promise.all(toFetch.map(pid =>
      getDoc(doc(db, 'tenants', tid, 'people', pid))
        .then(s => (s.exists() ? { id: s.id, ...s.data() } : null))
        .catch(err => { console.warn('[Clock] PayrollPrep historical person fetch failed:', pid, err?.message || err); return null; })
    ));
    fetched.forEach(p => { if (p) { peopleById.set(p.id, p); ClockState._historicalPeopleCache.set(p.id, p); } });
  }

  // Shared adjustments loader (identical numbers to exportPayrollCSV).
  const adj = await loadPayAdjustmentTotals(dates, tid);

  // Per-person aggregation over the PERIOD-bounded entries (start-date wins —
  // same v1 midnight-crossing attribution as the CSV; local calendar dates via
  // _fmtLocalYmd so CT period edges never shift a day, TASK-335/453).
  const oversizedTh = getOversizedShiftThreshold();
  const perPerson = {};
  const ensure = (pid, name) => perPerson[pid] || (perPerson[pid] = {
    pid, name, hours: 0, dateHours: {}, entries: [],
    missingClockOuts: 0, flaggedEntries: 0, maxShiftHrs: 0,
    // TASK-4627 — holiday buckets, mirroring exportPayrollCSV's TASK-341 rule
    // (start-date wins; premium = hours x (multiplier - 1)). The Gusto CSV
    // CANNOT carry these (see GUSTO_CSV_HEADERS in payroll.js), so the prep
    // screen has to SAY so before anyone downloads.
    holidayHours: 0, holidayPremiumHours: 0,
  });
  // TASK-4627 — company holidays live on tenants/{tid}.settings.companyHolidays
  // and window.AppHelpers reads them off window.AppState.settings, i.e. the
  // CURRENTLY-LOADED tenant only. When the prep screen is built for some other
  // tenant we genuinely CANNOT know the holiday schedule -- say "unknown"
  // rather than report a confident 0 (a 0 here reads as "no premium owed").
  const _holidayScopeKnown = !window.AppState?.tenant || window.AppState.tenant === tid;
  periodData.forEach(e => {
    const pid = e.personId;
    if (!pid) return;
    const agg = ensure(pid, e.personName || peopleById.get(pid)?.name || 'Unknown');
    const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
    const cout = e.clockOut?.toDate?.() || (e.clockOut ? new Date(e.clockOut) : null);
    const hrs = e.totalHours || 0;
    const completed = e.status === 'completed';
    // TASK-1180 — driver-corrected clock-outs (needsReview / driverAdjustedClockOut)
    // + TASK-441 oversized shifts stay flagged; hours are NEVER capped/altered.
    const reviewFlag = entryReviewMarked(e); // TASK-9794 — false once the office accepted it
    const flagged = entryNeedsReview(e, oversizedTh);
    if (!completed) agg.missingClockOuts++; // still-open entry in the period = missing clock-out (critical)
    if (flagged) {
      agg.flaggedEntries++;
      if (hrs > agg.maxShiftHrs) agg.maxShiftHrs = hrs;
    }
    if (completed && hrs) {
      agg.hours += hrs;
      addDateHours(agg.dateHours, cin, hrs);
      // TASK-4627 — holiday match, IDENTICAL rule to exportPayrollCSV
      // (clock.js TASK-341): the entry's clock-IN local date decides, v1 never
      // splits a shift across midnight. Only COMPLETED hours count here --
      // export is hard-blocked while any clock-out is open, so at export time
      // every entry is completed and these agree with the summary CSV.
      if (_holidayScopeKnown) {
        try {
          const _hYmd = window.AppHelpers?.formatLocalYmd?.(cin);
          const _hPerson = peopleById.get(pid) || {};
          if (_hYmd && window.AppHelpers?.getHolidayForDate) {
            if (window.AppHelpers.getHolidayForDate(_hYmd, _hPerson)) {
              agg.holidayHours += hrs;
              const _mult = window.AppHelpers.holidayMultiplierForDate(_hYmd, _hPerson);
              if (_mult > 1) agg.holidayPremiumHours += hrs * (_mult - 1);
            }
          }
        } catch (err) { console.warn('[Clock] PayrollPrep holiday lookup failed:', err?.message || err); }
      }
    }
    agg.entries.push({
      id: e.id, dateKey: _fmtLocalYmd(cin),
      inMs: cin.getTime(), outMs: cout ? cout.getTime() : null,
      hours: hrs, completed, flagged, reviewFlag,
      jobTitle: e.jobTitle || '', customerName: e.customerName || '',
      // TASK-9794 — why it needs review (plain English) + the raw marks, so the
      // Prep screen can list each flagged shift with a one-click Accept.
      reviewReasons: flagged ? entryReviewReasons(e, oversizedTh) : [],
      reviewFlags: Array.isArray(e.reviewFlags) ? e.reviewFlags.slice() : [],
      reviewAccepted: e.reviewAccepted === true,
    });
  });
  // Merge active roster people so salary / zero-hour employees still appear
  // (mirrors exportPayrollCSV's roster merge).
  activePeople.forEach(p => {
    if (!_pph().onPayroll(p, _ppStartYmd, _pphCfg().tz)) return; // TASK-10034 — final-period people stay
    ensure(p.id, p.name);
  });

  // TASK-1558 — effective-dated rate histories (best-effort; empty map keeps
  // the pre-1558 numbers byte-identical).
  const payHistories = await loadPayRateHistories(tid, Object.keys(perPerson));

  // TASK-9387 — internal-time breakdown, SAME helper + SAME period-bounded
  // entries as exportPayrollCSV so the two screens can't disagree. Screen
  // only: the Gusto file (GUSTO_CSV_HEADERS) is a fixed contract and never
  // carries it. null customers = load failed → surfaced, not silent.
  const internalCustomers = await _loadPayrollCustomers(tid);
  // TASK-9388 — rates / exclude lists / OT method (same config as the CSV).
  const payCfgRes = await _loadInternalPayConfig(tid);
  const payCfg = payCfgRes.ok ? payCfgRes.cfg : null;
  const internalCtx = _internalPayCtx(internalCustomers, payCfg);
  const internal = _payrollInternalBreakdown(weekData, internalCustomers, dates.start, dates.end,
    (pid) => isPayrollExempt(peopleById.get(pid) || {}), payCfg);
  const internalColumns = internal ? internal.columns : [];
  const internalLabelOf = (key) => {
    const col = internalColumns.find(c => c.key === key);
    return col ? col.label : ((payCfg && payCfg.customers[key] && payCfg.customers[key].name) || 'Internal');
  };

  // TASK-1483 — personal-vehicle reimbursement, computed HERE from the tenant
  // policy in effect on each work date (never a stored amount). Same
  // PERIOD-bounded entries as hours. The policy lives on the tenant doc
  // (AppState.settings), i.e. the CURRENTLY-LOADED tenant only — for any other
  // tenant we cannot know it, so say so rather than print $0 (the holiday-scope
  // pattern above).
  const _pvVR = window.VehicleReimb;
  const _pvScopeKnown = !!_pvVR && (!window.AppState?.tenant || window.AppState.tenant === tid);
  const _pvPolicy = _pvScopeKnown ? pvPolicy() : null;
  const _pvRows = _pvScopeKnown ? _pvVR.computeClaims(periodData, _pvPolicy,
    Object.assign({ isEligible: pvEligibleFn((pid) => peopleById.get(pid)) }, pvDayOpts(perPerson))) : [];
  const _pvSum = _pvScopeKnown ? _pvVR.summarize(_pvRows) : {};
  // TASK-9439 — approval before payroll: what in this period still needs a
  // decision (pending — never paid until approved) and what was denied ($0).
  // blockExport (tenant setting, owner/workforce, audited) decides whether
  // payroll.js DISABLES the export while anything is pending or only warns.
  // TASK-9451 — periodData (open shifts included) lets a pending item say
  // "clock-out still open" when an open shift is what holds it up.
  const _pvApproval = _pvScopeKnown && _pvVR.approvalItems ? _pvVR.approvalItems(_pvRows, { entries: periodData }) : { pending: [], denied: [] };
  const _pvBlockExport = !!(_pvScopeKnown && _pvPolicy && _pvPolicy.approval && _pvPolicy.approval.blockExport === true);
  const _pvAnyClaims = periodData.some(e => _pvVR && _pvVR.hasClaim(e));
  // The tenant's pay-type name for Payroll Prep labels (default when unknown).
  const _pvPayTypeName = _pvScopeKnown && _pvPolicy ? _pvPolicy.payTypeName : (_pvVR ? _pvVR.DEFAULT_PAY_TYPE_NAME : 'Vehicle Reimbursement');

  // TASK-9878 — the HOURS half of every row (who is listed, reg / OT / total,
  // straddle weeks, open shifts, per-diem days) from ONE shared function, the
  // one the connector's get_payroll_hours runs. The loop above keeps only what
  // the shared rule does not own (entry list, review flags, holiday buckets,
  // the per-day map the vehicle engine reads).
  const _core = _pph().buildPeriodHours({
    weekEntries: weekData,
    activePeople,
    nameOf: (pid) => peopleById.get(pid)?.name,
    personOf: (pid) => {
      const raw = peopleById.get(pid);
      if (!raw) return null;
      return window.PersonComp ? window.PersonComp.effective(raw) : raw;
    },
    startMs: sMs,
    endMs: eMs,
    periodStartYmd: _ppStartYmd, // TASK-10034
    cfg: _pphCfg(),
    dayTz: _clockLocalZone(),
  });
  let straddleNote = false;
  const rows = Object.values(perPerson).map(agg => {
    // TASK-1481 — resolve comp through the accessor (peopleComp-first, legacy
    // person-doc fallback). peopleById holds RAW docs (live sub OR direct
    // fetch on the standalone payroll route), so wrap at the read point.
    const _rawPerson = peopleById.get(agg.pid) || {};
    const person = window.PersonComp ? window.PersonComp.effective(_rawPerson) : _rawPerson;
    const exempt = isPayrollExempt(person);
    const isSalary = person.hrEmploymentType === 'salary';
    const rate = person.hrHourlyRate || 0;
    // 🔴 OT — single source of truth ONLY (computeCrossPeriodOT on the
    // person's WEEK-EXPANDED entries). Never re-derive (83h bug).
    const personWeekEntries = weekData.filter(e => e.personId === agg.pid);
    const _coreRow = _core.byPid[agg.pid];
    // Membership is identical by construction (same entries, same roster rule);
    // the else-arm is the same shared function, never a second OT copy.
    const ot = _coreRow ? _coreRow.otDetail : computeCrossPeriodOT(personWeekEntries, person, dates.start, dates.end);
    if (ot.straddleNoteApplies) straddleNote = true;
    // TASK-1558 — per-segment pay resolution from the effective-dated history.
    // OT HOURS still come exclusively from computeCrossPeriodOT above (single
    // source of truth); the engine only prices them. null = no history =
    // legacy single-rate behavior.
    const pay = _computePersonPay1558(agg.pid, person, personWeekEntries, ot, dates, payHistories, internalCtx);
    // TASK-939 — CSV-authoritative per-diem auto-calc: hrPerDiem × eligible
    // days (distinct local dates ≥8h). Manual payAdjustments per-diem is the
    // fallback ONLY when no hrPerDiem rate is configured — never stacked.
    const hrPerDiem = parseFloat(person.hrPerDiem) || 0;
    const perDiemEligible = hrPerDiem > 0;
    const perDiemDays = perDiemEligible
      ? (_coreRow && _coreRow.perDiemDays !== null ? _coreRow.perDiemDays : _pph().perDiemDays(agg.dateHours))
      : null;
    const manualPerDiem = adj.perDiemByPerson[agg.pid] || 0;
    const perDiemDollars = perDiemEligible ? hrPerDiem * (perDiemDays || 0) : manualPerDiem;
    // TASK-4628 — carry the superseded manual amount on the row so the prep
    // screen can name it. `perDiemDollars` / `perDiemSource` are deliberately
    // UNCHANGED: this is a new, purely informational field. Zero = no conflict.
    const _pdSuperseded = perDiemManualSuperseded(person, manualPerDiem);
    const perDiemManualIgnored = _pdSuperseded ? _pdSuperseded.manual : 0;
    const inactive = !activeIds.has(agg.pid);
    // TASK-10034 — No Longer Employed: their last day, and any shift that
    // starts after it (flagged, never dropped — the office decides).
    const lastDayYmd = _pph().lastDayYmd(_rawPerson, _pphCfg().tz);
    const afterLastDay = lastDayYmd ? agg.entries.filter(x => x.dateKey > lastDayYmd) : [];
    // TASK-4633 — worker classification. DD had NO concept of a contractor on
    // the payroll path, so a 1099 contractor exported as a W-2 employee row.
    // Lives on the PERSON doc (not peopleComp): it is a classification, not a
    // dollar figure, and payroll prep + the roster both need to read it.
    // Default is 'employee' -- but we also carry whether that was an explicit
    // choice or just the default, because "nobody has decided yet" and
    // "somebody decided W-2" are different facts and only one is safe.
    // TASK-9178 — resolved by the ONE shared rule (window.WorkerClass), never
    // the raw `workerType` field: that field alone let a person marked a 1099
    // contractor on `taxClassification` export here as a W-2 employee row.
    // `workerType` on the ROW stays the Gusto export gate payroll.js reads.
    const { workerType, workerTypeExplicit, workerClass, workerClassConflict } = _wc.payrollRow(_rawPerson);
    return {
      pid: agg.pid,
      name: agg.name,
      exempt, isSalary,
      workerType, workerTypeExplicit,
      workerClass, workerClassConflict, // TASK-9178 — 'employee'|'contractor'|'owner_operator'|null · both fields set and disagreeing
      // TASK-9804 — Paychex Flex Worker ID (Paychex matches rows by it, never by name). Read only by payroll.js → PaychexExport.
      paychexWorkerId: typeof _rawPerson.paychexWorkerId === 'string' ? _rawPerson.paychexWorkerId.trim() : '',
      // TASK-4627 — holiday hours (already inside `reg`) + the PREMIUM delta,
      // which no Gusto import column can carry. null = we could not know.
      holidayHours: _holidayScopeKnown ? agg.holidayHours : null,
      holidayPremiumHours: _holidayScopeKnown ? agg.holidayPremiumHours : null,
      holidayScopeKnown: _holidayScopeKnown,
      rate, hasRate: isSalary || rate > 0,
      reg: ot.periodReg, ot: ot.periodOT, total: ot.periodTotal,
      rawHours: agg.hours, // in-period completed hours (≠ paid total on straddle weeks)
      weeks: ot.weeks, straddle: ot.straddleNoteApplies,
      perDiemEligible, perDiemRate: hrPerDiem, perDiemDays, perDiemDollars,
      perDiemSource: perDiemEligible ? 'auto' : (manualPerDiem > 0 ? 'manual' : 'none'),
      perDiemManualIgnored, // TASK-4628 — $ of manual per-diem rows the rate superseded (0 = none)
      // TASK-1483 — personal-vehicle reimbursement: its OWN columns, never
      // folded into per diem. vehicleReimb = paid as a reimbursement;
      // vehicleTaxable = paid as wages (a flat day's uncovered part, ONLY when
      // the tenant's excessTaxable switch is on); vehicleUncovered = that
      // uncovered part whatever the switch says (information). Pending/denied/no-rate claims are NOT in either figure — they
      // are counted so the prep screen can say what was left out.
      vehicleMiles: (_pvSum[agg.pid] || {}).miles || 0,
      vehicleDays: (_pvSum[agg.pid] || {}).days || 0,
      vehicleReimb: (_pvSum[agg.pid] || {}).nonTaxable || 0,
      vehicleTaxable: (_pvSum[agg.pid] || {}).taxableExcess || 0,
      vehicleUncovered: (_pvSum[agg.pid] || {}).uncovered || 0,
      vehicleNotQualified: (_pvSum[agg.pid] || {}).notQualified || 0,
      // TASK-1483 — why each flat day did or didn't earn the flat (Payroll Prep).
      vehicleDayDetail: _pvRows.filter(r => r.personId === agg.pid && r.basis === 'flat_per_day')
        .map(r => ({ ymd: r.ymd, miles: r.miles, hours: r.dayHours, qualifies: r.qualifies !== false,
                     reason: r.qualifyReason || '', amount: r.amount, status: r.status })),
      vehiclePending: (_pvSum[agg.pid] || {}).pending || 0,
      vehicleDenied: (_pvSum[agg.pid] || {}).denied || 0,
      vehicleNoRate: (_pvSum[agg.pid] || {}).noRate || 0,
      vehicleIneligible: (_pvSum[agg.pid] || {}).ineligible || 0,
      bonus: adj.bonusByPerson[agg.pid] || 0,
      reimbursement: adj.reimbursementByPerson[agg.pid] || 0,
      dateHours: agg.dateHours,
      entries: agg.entries,
      missingClockOuts: agg.missingClockOuts,
      flaggedEntries: agg.flaggedEntries,
      maxShiftHrs: agg.maxShiftHrs,
      inactive,
      lastDayYmd, // TASK-10034 — null unless No Longer Employed
      afterLastDayCount: afterLastDay.length,
      afterLastDayHours: afterLastDay.reduce((s, x) => s + (x.completed ? (x.hours || 0) : 0), 0),
      // TASK-1558 — per-segment pay result (null = no rate history). Carries
      // changes[] (old→new @ timestamp), segments[] (hours at each rate),
      // gross (weighted-avg OT) and supported/notes for the review-by-hand
      // degradation. payroll.js renders it; rates stay masked by default.
      pay,
      rateChanged: !!(pay && pay.changes && pay.changes.length),
      // TASK-9387 — { customerKey: hours } for internal time (inside reg/ot),
      // plus the per-workweek text the CPA CSV carries.
      internalHours: internal?.byPerson?.[agg.pid]?.total || {},
      // Raw per-workweek buckets ({ weekKey: { customerKey: h } }, plus the
      // prior-period part of a straddling week) — the seam TASK-9388's
      // internal pay rates feed into _computePersonPay1558. Display-only today.
      // COD 2026-09-24: hours PAYABLE this period ({ customerKey: h }) —
      // what the Gusto Regular/Overtime cells actually pay.
      internalPayable: internal?.byPerson?.[agg.pid]?.payable || {},
      internalWeeks: internal?.byPerson?.[agg.pid]
        ? {
          byWeek: internal.byPerson[agg.pid].byWeek,
          priorByWeek: internal.byPerson[agg.pid].priorByWeek,
          payableByWeek: internal.byPerson[agg.pid].payableByWeek,
          priorPaidByWeek: internal.byPerson[agg.pid].priorPaidByWeek,
        }
        : null,
      internalByWeek: internal && internal.byPerson[agg.pid]
        ? window.InternalTime.formatWeeks(internal.byPerson[agg.pid].payableByWeek, internalColumns.filter(c => c.confirmed)) : '',
      internalAlreadyPaid: internal && internal.byPerson[agg.pid]
        ? window.InternalTime.formatWeeks(internal.byPerson[agg.pid].priorPaidByWeek, internalColumns) : '',
      // TASK-9388 — internal-rate pay estimate (null = no internal rate priced
      // anything for this person). payroll.js renders it; rates stay masked.
      internalPay: pay && pay.internalRated ? {
        segments: (pay.internalSegments || []).map(s => ({ ...s, label: internalLabelOf(s.key) })),
        gross: pay.supported === false ? null : pay.gross,
        supported: pay.supported !== false,
        notes: pay.notes || [],
        otWeeks: (pay.weeks || []).filter(w => w.otHours > 1e-9).map(w => ({ key: w.key, otHours: w.otHours, method: w.method, otByRate: w.otByRate, weightedRate: w.weightedRate })),
        cell: _describeInternalPayCell(pay, internalLabelOf, exempt),
      } : null,
    };
  }).sort((a, b) => a.name.localeCompare(b.name));

  // Exceptions panel (S2). Missing clock-outs are CRITICAL — the prep screen
  // disables export until they're resolved (unsettled hours must never feed a
  // real paycheck). The rest are warnings: review-flagged entries, hours on
  // terminated/inactive people, zero-hour actives (forgot to clock?).
  const exceptions = {
    missingClockOuts: rows.filter(r => r.missingClockOuts > 0).map(r => ({ pid: r.pid, name: r.name, count: r.missingClockOuts })),
    flagged: rows.filter(r => r.flaggedEntries > 0).map(r => ({ pid: r.pid, name: r.name, count: r.flaggedEntries, maxShiftHrs: r.maxShiftHrs })),
    // TASK-9794 — the "Needs review" list itself: one row per flagged shift
    // (not per person), oldest first, with reasons. unreviewedCount is what the
    // export WARNS with (Chris q03: warn only, never refuse).
    reviewItems: rows.flatMap(r => (r.entries || []).filter(x => x.flagged).map(x => ({
      id: x.id, pid: r.pid, name: r.name, dateKey: x.dateKey, inMs: x.inMs, outMs: x.outMs,
      hours: x.hours, reasons: x.reviewReasons || [], reviewFlags: x.reviewFlags || [],
      jobTitle: x.jobTitle || '', customerName: x.customerName || '',
    }))).sort((a, b) => a.inMs - b.inMs),
    // TASK-10034 — a No Longer Employed person is listed in finalChecks instead.
    terminatedWithHours: rows.filter(r => r.inactive && !r.lastDayYmd && r.total > 0).map(r => ({ pid: r.pid, name: r.name, hours: r.total })),
    // TASK-10034 — everyone marked No Longer Employed whose last day falls in
    // this period: it is their final pay period, so run their final check.
    finalChecks: rows.filter(r => r.lastDayYmd && r.lastDayYmd >= _ppStartYmd && r.lastDayYmd <= window.TenantLocalTime.carrierYmd(dates.end)).map(r => ({ pid: r.pid, name: r.name, lastDay: r.lastDayYmd, hours: r.total, isSalary: !!r.isSalary })),
    // Shifts that start after the person's last day — flagged, still counted.
    afterLastDay: rows.filter(r => r.afterLastDayCount > 0).map(r => ({ pid: r.pid, name: r.name, lastDay: r.lastDayYmd, count: r.afterLastDayCount, hours: r.afterLastDayHours })),
    // The No Longer Employed roster read failed: say so, never "nobody left".
    finalChecksUnknown: _finalPeople === null,
    // TASK-10040 — any single day over 24 h (completed shifts, each counted on
    // its clock-in day). A warning, not a refusal: hours are never changed. A
    // day drops off once a shift on it was accepted and the rest is <= 24 h.
    dayOver24: rows.flatMap(r => {
      const F = window.ShiftDayFlags;
      if (!F) return [];
      // Company calendar day of the clock-in — the same day the weekly grid
      // books it on, so the two screens flag the same day.
      const byDay = {}, review = {};
      (r.entries || []).forEach(x => {
        if (!x.completed || !(x.hours > 0)) return;
        const k = _clockCompanyDayKey(x.inMs);
        byDay[k] = (byDay[k] || 0) + x.hours;
        const d = review[k] || (review[k] = { accepted: false, openHours: 0 });
        if (x.reviewAccepted) d.accepted = true; else d.openHours += x.hours;
      });
      return F.daysOverLimit(byDay, F.DAY_LIMIT_HOURS, (k) => F.dayReviewed(review[k])).map(d => ({ pid: r.pid, name: r.name, dateKey: d.key, hours: d.hours }));
    }),
    zeroHourActives: rows.filter(r => !r.inactive && !r.exempt && r.rawHours <= 0 && r.total <= 0).map(r => ({ pid: r.pid, name: r.name })),
    unreviewedCount: rows.reduce((n, r) => n + (r.flaggedEntries || 0), 0), // TASK-9794
    // TASK-4628 — manual per-diem rows that the HR rate superseded. Not a
    // critical (no export is wrong and no pay is short — the rate paid), but
    // dead config that LOOKS live is exactly how someone concludes "removing
    // the rate is safe" or "this person is paid per diem twice".
    perDiemManualIgnored: rows.filter(r => r.perDiemManualIgnored > 0).map(r => ({
      pid: r.pid, name: r.name, ignored: r.perDiemManualIgnored,
      rate: r.perDiemRate, paid: r.perDiemDollars,
    })),
    // TASK-1558 — pay changed inside the displayed period: old value, new
    // value, timestamp (spec part 1: the silent wrong number becomes a
    // visible decision). needsHandReview = the engine declined to price it
    // (hourly→salary conversion, double basis flip, piece rate).
    rateChanges: rows.filter(r => r.rateChanged).map(r => ({
      pid: r.pid,
      name: r.name,
      changes: r.pay.changes.map(_describeChange1558),
      needsHandReview: r.pay.supported === false,
      notes: r.pay.notes || [],
    })),
    // TASK-4627 🔴 — HOLIDAY PREMIUM CANNOT BE EXPORTED. Gusto's import maps
    // hourly columns to Regular / Overtime / Double overtime only; there is no
    // standard Holiday earning (a company can create a CUSTOM earning type in
    // Gusto, but we cannot assume one exists, and a header Gusto silently drops
    // is the exact bug this task exists to kill). Holiday BASE hours are safe —
    // they are already inside `reg`. The PREMIUM delta is not, so it must be
    // entered in Gusto by hand, which means the prep screen has to say so
    // BEFORE the download, not after.
    holidayPremium: rows.filter(r => (r.holidayPremiumHours || 0) > 0.004).map(r => ({
      pid: r.pid, name: r.name,
      premiumHours: r.holidayPremiumHours,
      holidayHours: r.holidayHours,
    })),
    // TASK-4627 — Gusto's Smart Import matches rows by NAME. We split on
    // whitespace: first token = first name, the rest = last name. A middle
    // name or a suffix therefore produces a last name Gusto will not match
    // ("Ivan O Vega lerma" -> last name "O Vega lerma"). Gusto shows "no match
    // found" on ITS side, but DD never warned that the row was likely to
    // strand — so name it here, before the download.
    nameSplitRisk: rows.filter(r => {
      const parts = String(r.name || '').trim().split(/\s+/).filter(Boolean);
      return parts.length !== 2 && (r.total > 0.004 || r.bonus > 0.004 || r.perDiemDollars > 0.004 || r.reimbursement > 0.004);
    }).map(r => {
      const parts = String(r.name || '').trim().split(/\s+/).filter(Boolean);
      return { pid: r.pid, name: r.name, first: parts[0] || '', last: parts.slice(1).join(' ') };
    }),
    // TASK-4633 🔴 — somebody worked, DD holds no rate for them, and nobody has
    // said whether they are a W-2 employee or a 1099 contractor. Today they
    // export as an EMPLOYEE row (buildGustoRows only drops rows with no hours
    // AND no money), so a contractor gets paid twice: once correctly through
    // Gusto's contractor flow, once wrongly as W-2 wages with the wrong tax
    // treatment and a 1099 that understates. Not export-blocking (blocking
    // payroll over one unclassified person traps everybody) — but loud.
    unclassifiedWorkers: rows.filter(r =>
      !r.isSalary && !r.hasRate && !r.workerTypeExplicit && (r.total > 0.004 || r.rawHours > 0.004)
    ).map(r => ({ pid: r.pid, name: r.name, hours: r.total || r.rawHours })),
    // TASK-4633 — classified contractors, EXCLUDED from the Gusto employee
    // export on purpose. Listed so "excluded" never reads as "forgotten":
    // they are paid through Gusto -> Pay -> Pay contractors.
    contractorsExcluded: rows.filter(r =>
      r.workerType === 'contractor' && (r.total > 0.004 || r.rawHours > 0.004 || r.bonus > 0.004 || r.perDiemDollars > 0.004 || r.reimbursement > 0.004
        || r.vehicleReimb > 0.004 || r.vehicleTaxable > 0.004)
    ).map(r => ({ pid: r.pid, name: r.name, hours: r.total || r.rawHours,
      // TASK-1483 (COD round 3) — vehicle dollars ride along so a contractor
      // with only mileage pay never shows as "0.00h" and gets forgotten.
      vehicle: Math.round(((r.vehicleReimb || 0) + (r.vehicleTaxable || 0)) * 100) / 100 })),
    // TASK-9178 — legacy records whose two classification fields DISAGREE
    // (one says W-2, the other 1099). The Tax-tab value wins, so the row is
    // already in or out of the file correctly by that rule — but a human
    // should confirm it, so it is listed rather than silently resolved.
    classificationConflicts: rows.filter(r =>
      r.workerClassConflict && (r.total > 0.004 || r.rawHours > 0.004 || r.bonus > 0.004 || r.perDiemDollars > 0.004 || r.reimbursement > 0.004)
    ).map(r => ({ pid: r.pid, name: r.name, workerClass: r.workerClass })),
    // TASK-1483 — vehicle claims NOT in this run: no rate on file for the
    // work date, or from someone not enrolled. Never silent. (Awaiting
    // approval moved to `approval` below — TASK-9439 — so it is listed once.)
    vehicleUnsettled: rows.filter(r => r.vehicleNoRate > 0 || r.vehicleIneligible > 0)
      .map(r => ({ pid: r.pid, name: r.name, pending: 0, noRate: r.vehicleNoRate, ineligible: r.vehicleIneligible })),
    // TASK-9439 — approval before payroll. `pending` items are NOT in the file
    // (never paid until approved); `denied` pay $0. blocksExport = the tenant
    // chose to block the export while anything is pending (else: warn).
    approval: {
      typeLabel: _pvPayTypeName,
      blockExport: _pvBlockExport,
      blocksExport: _pvBlockExport && _pvApproval.pending.length > 0,
      pending: _pvApproval.pending.map(it => ({ pid: it.personId, name: it.personName || (perPerson[it.personId] || {}).name || '', ymd: it.ymd, amount: it.amount, miles: it.miles, hoursOnly: it.hoursOnly, type: it.type, openShift: it.openShift === true, dayHeld: it.dayHeld === true })),
      denied: _pvApproval.denied.map(it => ({ pid: it.personId, name: it.personName || (perPerson[it.personId] || {}).name || '', ymd: it.ymd, amount: it.amount, miles: it.miles, hoursOnly: it.hoursOnly, type: it.type })),
    },
    // TASK-1483 🔴 MIGRATION GUARD — per diem AND vehicle pay for the same
    // person in the same period. Legit for someone who truly gets both, but it
    // is exactly the shape of a missed migration (hrPerDiem still holding
    // "truck pay" after vehicle reimbursement was turned on) = silent
    // double-pay. Listed so a human confirms it.
    vehicleAndPerDiem: rows.filter(r => r.perDiemDollars > 0.004 && (r.vehicleReimb + r.vehicleTaxable) > 0.004)
      .map(r => ({ pid: r.pid, name: r.name, perDiem: r.perDiemDollars, vehicle: r.vehicleReimb + r.vehicleTaxable })),
    // Policy unknowable for this tenant (prep built for a tenant other than the
    // loaded one) while entries DO carry claims.
    vehicleScopeUnknown: !_pvScopeKnown && _pvAnyClaims,
  };
  // TASK-9387 — internal-time hours are inside Regular/Overtime in the Gusto
  // file; if a tenant pays them at a separate rate they must be moved to that
  // rate in Gusto. Listed per person so nothing is forgotten.
  // items      = CONFIRMED internal customers, hours PAYABLE this period (the
  //              only figures the banner says may be moved); worked shown too.
  // unconfirmed = name-guess / legacy / deleted buckets — never "move", only
  //              "tick Internal time only on the customer".
  // alreadyPaid = last period's part of a week ending here — "do not move".
  exceptions.internalTime = rows
    .map(r => ({
      pid: r.pid, name: r.name,
      items: internalColumns
        .filter(c => c.confirmed && ((r.internalPayable[c.key] || 0) > 0.004 || (r.internalHours[c.key] || 0) > 0.004))
        .map(c => ({ key: c.key, label: c.label, hours: r.internalPayable[c.key] || 0, worked: r.internalHours[c.key] || 0 })),
      unconfirmed: internalColumns
        .filter(c => !c.confirmed && (r.internalHours[c.key] || 0) > 0.004)
        .map(c => ({ key: c.key, label: c.label, worked: r.internalHours[c.key] || 0, source: c.source })),
      byWeek: r.internalByWeek,
      alreadyPaid: r.internalAlreadyPaid,
      pay: r.internalPay, // TASK-9388
    }))
    .filter(x => x.items.length || x.unconfirmed.length || x.byWeek || x.alreadyPaid || x.pay);
  exceptions.internalTimeUnavailable = !internal;
  // TASK-9388 — the pay config could not be read (e.g. a login without the
  // workforce module): rates AND "keeps normal rate" exclusions are missing.
  exceptions.internalPayConfigUnavailable = !payCfgRes.ok && internalColumns.length > 0;
  exceptions.criticalCount = exceptions.missingClockOuts.reduce((s, x) => s + x.count, 0);
  // TASK-4627 — we could not read this tenant's holiday schedule (prep built
  // for a tenant other than the loaded one). Report the gap, never a 0.
  exceptions.holidayScopeUnknown = rows.some(r => r.holidayScopeKnown === false);

  // TASK-1481 — 🔴 DENIED ≠ ABSENT: surface the comp-access state on the prep
  // payload so payroll.js can show "no access to pay rates" instead of a wall
  // of $0 rows once the fields are stripped off the person docs. Blocked only
  // when the peopleComp read was permission-denied AND no person doc carries
  // legacy comp fields (pre-strip this is never blocked).
  const compAccess = window.PersonComp?.exportGuard?.(activePeople) || { blocked: false, reason: null };

  return {
    tenantId: tid,
    offset,
    periodType: getPayPeriodType(),
    otThreshold: getOTThreshold(),
    oversizedThreshold: oversizedTh,
    startMs: dates.start.getTime(),
    endMs: dates.end.getTime(),
    rows,
    exceptions,
    straddleNote,
    compAccess,
    internalColumns, // TASK-9387
    // TASK-9388 — the tenant's OT method + whether any internal rate priced pay.
    internalOtMethod: payCfg ? payCfg.otMethod : 'weighted_average',
    internalPayAny: rows.some(r => r.internalPay),
    vehiclePayTypeName: _pvPayTypeName, // TASK-1483 — tenant's label; RAW text, escape on render
  };
}

// perf (appColdLoadGzip, perf/app-coldload-slim) — the Workforce > Reports Print
// body (internal payroll sheet / customer-safe billing sheet) lives in the LAZY
// views/clockWorkforceReport.js. Same name + same contract (async, no args) for
// window.ClockModule.printWorkforceReport; deps are handed over, never imported
// (pitfall #1). A failed load is retried on the next tap.
// Editing clockWorkforceReport.js: bump its ?v= here AND clock.js's ?v= in app.js.
let _wfReportLoad = null;
async function printWorkforceReport() {
  let m;
  try {
    m = await (_wfReportLoad ||= __ddChunkWrap(import('./clockWorkforceReport.js?v=1')).then((mod) => {
      mod._bind({
        ClockState, _computePersonPay1558, _fmtLocalYmd, _weekCacheKey, collection,
        computePersonPeriodOT, db, doc, escapeHtml, fetchHistoricalPeopleForPeriod, getDoc,
        getDocs, getFilteredReportsData, getOTThreshold, getPersonForReport,
        getReportsPeriodDates, getWeekDayIndex, getWeekDayLabels, getWeekKey, isPayrollExempt,
        loadPayRateHistories, payrollExemptKind, query, where
      });
      return mod;
    }));
  } catch (e) {
    _wfReportLoad = null;
    console.error('[Clock] report print module failed to load:', e);
    if (window.showToast) window.showToast('Could not open the report. Check your connection and try again.', 'error');
    return;
  }
  return m.printWorkforceReport();
}

// ============================================
// WORKFORCE ADJUSTMENTS TAB
// ============================================
// ============================================
// TASK-1442 SLICE 1 — TIMECARD EXCEPTIONS (was: "Health" tab)
// ============================================
// A date-ranged review list over CLOSED clock entries, one tap from the fix.
// It replaces the old "System Health" screen, which only ever read ACTIVE
// entries + ~20 recent draft/ready tickets and therefore caught none of the
// exceptions below. The old ticket-health / audit-log tools are preserved
// verbatim in the collapsed "Data tools" block at the bottom.
//
// 🔴 FLSA — WE FLAG, WE NEVER FIX. Nothing in this file auto-adjusts, caps or
// truncates a punch. Every row's action opens the existing edit modal so a
// human decides. Auto-trimming a 22h punch is wage underpayment, not cleanup
// (same invariant TASK-441 locked for the oversized-shift badge).
//
// Checks (v1):
//   long-shift        — completed shift over getOversizedShiftThreshold() (default 16h,
//                       tenant-configurable via Workforce → Reports → Pay Settings). 16h not 14h:
//                       measured on 387 real TITS punches, >14h fires 21×/month and
//                       ~20 of those are legitimate bore-support days.
//   overlap           — two punches for the SAME person whose windows intersect
//                       (sequential OR fully nested). Near-zero false positives.
//   no-job-no-ticket  — no job, no ticket, and the customer is not internalTimeOnly.
//                       (This check IS the absorbed TASK-1435.)
//   job-no-ticket     — has a job but no ticket, external customer. The bucket a
//                       naive "no jobId" filter misses entirely.
//   deleted-ticket    — hours hung on a ticket whose status === 'deleted'. There is
//                       NO boolean `deleted` field on this collection (TASK-1464).
//   orphan-ticket     — ticketId points at a ticket doc that no longer exists.
//   edited-after      — updatedAt more than TCX_EDIT_GRACE_MS past clockOut.
//
// Money field is `grandTotal`. `total` is 0 on every ticket (TASK-1464).

// Read cap for one scan. A 30-day window on the busiest live tenant is ~400
// entries; 2000 is ~4 months of headroom. If we hit it the UI SAYS so rather
// than silently reporting on a truncated window.
const TCX_ENTRY_CAP = 2000;
// Max distinct tickets we'll resolve per scan. Beyond this the two ticket-state
// checks are SKIPPED and labelled — never silently downgraded to "no issues".
const TCX_TICKET_CAP = 600;
// Parallel getDoc fan-out width (mirrors fetchHistoricalPeopleForPeriod's
// rationale: avoids needing documentId() in firebase-config.js for `in` chunks).
const TCX_TICKET_FANOUT = 25;
// How long after clock-out an edit is considered "after the fact". A clock-out
// write itself stamps updatedAt a beat later, so a few minutes of slack keeps
// the normal path off the list.
const TCX_EDIT_GRACE_MS = 5 * 60 * 1000;
// Minimum intersection before two punches count as overlapping. NOT arbitrary:
// Switch Job writes the new clock-in at a minute-truncated HH:MM:00 while the
// closing punch keeps its seconds, so a clean switch leaves a few seconds of
// mathematical overlap. Measured over 387 real punches (30 days, 2026-07-12..08-11):
// 6 raw intersections, of which 4 were 16–35 SECONDS (all job switches) and 2 were
// real (29 min and 30 min). A 2-minute floor drops every artifact and keeps both
// real hits — without it this check cries wolf 4 times a month on day one.
const TCX_MIN_OVERLAP_MS = 2 * 60 * 1000;

// Per-type presentation. `sev` drives colour + sort weight only — it never
// changes any number.
const TCX_TYPES = {
  'long-shift':       { label: 'Long shift',          icon: 'hourglass_top',   sev: 'high' },
  'overlap':          { label: 'Overlapping punches', icon: 'call_merge',      sev: 'high' },
  // TASK-1548 — the ticket this punch fed carries a PRICED equipment line with 0
  // hours (job_generate birth defect; #100464 invoiced $375 of hauling at $0).
  'equipment-zero-hours': { label: 'Priced equipment, no hours', icon: 'local_shipping', sev: 'high' },
  // TASK-1405 — the mirror: the hours ARE on the equipment line, the RATE is $0,
  // and the ticket's other lines carry money so nothing else on the ticket looks
  // wrong. Detection only — this tab never blocks anybody.
  'equipment-unpriced': { label: 'Equipment ran, no rate', icon: 'money_off', sev: 'high' },
  'no-job-no-ticket': { label: 'No job, no ticket',   icon: 'help_outline',    sev: 'med'  },
  'job-no-ticket':    { label: 'Job, no ticket',      icon: 'receipt_long',    sev: 'med'  },
  'deleted-ticket':   { label: 'Deleted ticket',      icon: 'delete_forever',  sev: 'med'  },
  'orphan-ticket':    { label: 'Missing ticket',      icon: 'link_off',        sev: 'med'  },
  'edited-after':     { label: 'Edited after the fact', icon: 'edit_note',     sev: 'low'  }
};
const TCX_SEV_ORDER = { high: 0, med: 1, low: 2 };

// Resolve the exceptions date range. Deliberately a sibling of
// getReportsPeriodDates() rather than a caller of it: that one reads
// ClockState.reports*, and the two tabs must not share a period.
function _tcxPeriodDates() {
  const now = new Date();
  let start, end;
  const p = ClockState.exceptionsPeriod;
  if (p === 'payperiod') {
    return getPayPeriodDates(ClockState.exceptionsPayPeriodOffset);
  } else if (p === 'week') {
    start = getWeekStart(now);
    end = new Date(start); end.setDate(start.getDate() + 7); end.setMilliseconds(-1);
  } else if (p === 'lastweek') {
    const thisStart = getWeekStart(now);
    start = new Date(thisStart); start.setDate(thisStart.getDate() - 7);
    end = new Date(thisStart); end.setMilliseconds(-1);
  } else if (p === 'month') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  } else if (p === 'lastmonth') {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  } else if (p === 'custom') {
    if (!ClockState.exceptionsCustomStart || !ClockState.exceptionsCustomEnd) return null;
    start = new Date(ClockState.exceptionsCustomStart + 'T00:00:00');
    end = new Date(ClockState.exceptionsCustomEnd + 'T23:59:59.999');
  } else {
    return null;
  }
  if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return null;
  return { start, end };
}

function _tcxRangeLabel(range) {
  if (!range) return '';
  return `${_fmtLocalYmd(range.start)} → ${_fmtLocalYmd(range.end)}`;
}

// Which customers are "internal time" (real work, no external revenue)? Read
// the TASK-1175 flag — a name list is TITS-special and misclassifies every
// other carrier. The name fallback fires ONLY when the flag was never set on
// that customer, matching scripts/billable-hours-report.cjs exactly so the
// screen and the report can never disagree about a bucket.
const TCX_INTERNAL_NAME_FALLBACK = /^(shop|office|internal|yard)$/i;
function _tcxInternalCustomerIds(customers) {
  const s = new Set();
  // TASK-9387 — one classifier: the payroll exports bucket internal hours with
  // window.InternalTime.isInternalCustomer, so this scan must use the same one.
  const isInternal = window.InternalTime?.isInternalCustomer || ((c) => {
    const nm = c.name || c.customerName || '';
    return c.internalTimeOnly === true
      || (c.internalTimeOnly === undefined && TCX_INTERNAL_NAME_FALLBACK.test(nm));
  });
  (customers || []).forEach(c => { if (c && isInternal(c)) s.add(c.id); });
  return s;
}

function _tcxDate(v) {
  if (!v) return null;
  const d = v?.toDate?.() ? v.toDate() : new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function _tcxHours(e) {
  if (typeof e.totalHours === 'number' && isFinite(e.totalHours)) return e.totalHours;
  const a = _tcxDate(e.clockIn), b = _tcxDate(e.clockOut);
  if (!a || !b) return 0;
  return Math.max(0, (b - a) / 3600000);
}

function _tcxWhen(d) {
  if (!d) return '';
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function getExceptionsTabHTML() {
  // Belt to switchTab()'s suspenders — this sub-tab reads every person's hours.
  if (!hasWorkforceAccess()) {
    return '<div style="padding:24px;color:var(--text-secondary);">Timecard Exceptions is available to members with the Workforce module.</div>';
  }
  const p = ClockState.exceptionsPeriod;
  const pill = (key, label) =>
    `<button class="filter-btn ${p === key ? 'active' : ''}" onclick="window.ClockModule.switchExceptionsPeriod('${key}')">${label}</button>`;
  const th = getOversizedShiftThreshold();

  return `
    <div class="tcx-view">
      <div class="tcx-header">
        <div>
          <h3 class="tcx-title">
            <span class="material-icons">rule</span>
            Timecard Exceptions
          </h3>
          <div class="tcx-subtitle">
            Punches worth a second look before payroll runs.
            <strong>Nothing here is auto-corrected</strong> — every fix is a human decision.
          </div>
        </div>
        <button class="btn btn-primary" id="tcx-run-btn" onclick="window.ClockModule.runTimecardExceptions()">
          <span class="material-icons" style="font-size:18px;vertical-align:middle;">search</span>
          Run check
        </button>
      </div>

      <div class="tcx-controls">
        ${pill('payperiod', 'Pay period')}
        ${pill('week', 'This week')}
        ${pill('lastweek', 'Last week')}
        ${pill('month', 'This month')}
        ${pill('lastmonth', 'Last month')}
        ${pill('custom', 'Custom')}
        ${p === 'custom' ? `
        <span class="tcx-custom-range">
          <input type="date" class="form-control" id="tcx-custom-start" value="${escapeHtml(ClockState.exceptionsCustomStart || '')}" onchange="window.ClockModule.setExceptionsCustomRange()">
          <span class="tcx-dash">–</span>
          <input type="date" class="form-control" id="tcx-custom-end" value="${escapeHtml(ClockState.exceptionsCustomEnd || '')}" onchange="window.ClockModule.setExceptionsCustomRange()">
        </span>` : ''}
        ${p === 'payperiod' ? `
        <span class="tcx-pp-nav">
          <button class="btn-icon" title="Earlier pay period" onclick="window.ClockModule.navExceptionsPayPeriod(-1)"><span class="material-icons">chevron_left</span></button>
          <button class="btn-icon" title="Later pay period" onclick="window.ClockModule.navExceptionsPayPeriod(1)"><span class="material-icons">chevron_right</span></button>
        </span>` : ''}
      </div>

      <div class="tcx-legend">
        Reviews <strong>closed</strong> punches only — a shift still on the clock has no final
        hours yet. "Reached no ticket" means the time never landed on a customer ticket;
        internal-time customers are excluded by their <code>internalTimeOnly</code> flag, not by name.
        Long-shift threshold is <strong>${th}h</strong> (Reports → Pay Settings).
      </div>

      <div id="tcx-results">${getExceptionsResultsHTML()}</div>

      <details class="tcx-tools">
        <summary>Data tools</summary>
        <div class="tcx-tools-grid">
          <button class="btn btn-secondary" onclick="window.ClockModule.runTicketHealthCheck()">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;">fact_check</span> Ticket Health
          </button>
          <button class="btn btn-secondary" onclick="window.ClockModule.loadAuditLog('clock')">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;">schedule</span> Clock Log
          </button>
          <button class="btn btn-secondary" onclick="window.ClockModule.loadAuditLog('job')">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;">work</span> Job Log
          </button>
          <button class="btn btn-secondary" onclick="window.ClockModule.loadAuditLog('all')">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;">list_alt</span> All Activity
          </button>
          <button class="btn btn-secondary" style="grid-column:1/-1;" onclick="window.ClockModule.loadAuditLog('telemetry')" title="Client-side telemetry — the moments the app declined to do what was asked, or did something different (sms_send_*, sms_gate_blocked, ticket_heal_unpersisted, invoice_email_failed, invoice_total_drift, session_start). Start a support call here.">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;">analytics</span> Telemetry
          </button>
        </div>
        <div id="health-results" style="font-size:0.88rem;"></div>
      </details>
    </div>
  `;
}

// Renders ONLY the results block, from ClockState. Called both by the shell
// above and by runTimecardExceptions() when a scan lands, so the two can never
// drift apart.
function getExceptionsResultsHTML() {
  if (ClockState.exceptionsLoading) {
    return '<div class="tcx-empty"><span class="btn-spinner"></span> Scanning timecards…</div>';
  }
  if (!ClockState.exceptionsRan) {
    return '<div class="tcx-empty">Pick a period and hit <strong>Run check</strong>.</div>';
  }
  const meta = ClockState.exceptionsMeta || {};
  const rows = ClockState.exceptionsRows || [];

  // Type filter chips — only for types actually present.
  const counts = {};
  rows.forEach(r => { counts[r.type] = (counts[r.type] || 0) + 1; });
  const present = Object.keys(TCX_TYPES).filter(t => counts[t]);
  const active = ClockState.exceptionsTypeFilter;
  const chips = present.length ? `
    <div class="tcx-chips">
      <button class="tcx-chip ${active === 'all' ? 'active' : ''}" onclick="window.ClockModule.setExceptionsTypeFilter('all')">All <span>${rows.length}</span></button>
      ${present.map(t => `<button class="tcx-chip tcx-sev-${TCX_TYPES[t].sev} ${active === t ? 'active' : ''}" onclick="window.ClockModule.setExceptionsTypeFilter('${t}')">${escapeHtml(TCX_TYPES[t].label)} <span>${counts[t]}</span></button>`).join('')}
    </div>` : '';

  const notes = [];
  notes.push(`${meta.closed || 0} closed punch${(meta.closed === 1) ? '' : 'es'} reviewed · ${_tcxRangeLabel(meta.range)}`);
  if (meta.active) notes.push(`${meta.active} still on the clock — excluded, hours not final`);
  if (meta.unattachedHours > 0) notes.push(`${meta.unattachedHours.toFixed(2)}h reached no ticket`);
  if (meta.capped) notes.push(`⚠ hit the ${TCX_ENTRY_CAP}-entry read cap — narrow the range, results are partial`);
  if (meta.ticketLookupSkipped) notes.push(`⚠ ${meta.ticketLookupSkipped} — ticket-state checks not run`);
  if (meta.lineLookupIncomplete) notes.push(`⚠ ${meta.lineLookupIncomplete} ticket${meta.lineLookupIncomplete === 1 ? '' : 's'} unreadable for the equipment-hours check — partial`);
  if (meta.internalUnresolved) notes.push('⚠ customer list unavailable — ticketless checks skipped rather than risk flagging internal time');

  const summary = `<div class="tcx-summary">${notes.map(n => `<span>${escapeHtml(n)}</span>`).join('')}</div>`;

  if (!rows.length) {
    return `${summary}<div class="tcx-clean"><span class="material-icons">check_circle</span> No exceptions in this period.</div>`;
  }

  const shown = active === 'all' ? rows : rows.filter(r => r.type === active);
  const list = shown.map(r => {
    const t = TCX_TYPES[r.type] || { label: r.type, icon: 'help', sev: 'low' };
    const money = (typeof r.grandTotal === 'number' && r.grandTotal > 0)
      ? `<span class="tcx-money">$${r.grandTotal.toFixed(2)}</span>` : '';
    const alt = r.altEntryId
      ? `<button class="btn btn-secondary btn-sm" onclick="window.ClockModule.openExceptionEntry('${_safeTicketId(r.altEntryId)}')">Other punch</button>`
      : '';
    return `
      <div class="tcx-row tcx-sev-${t.sev}">
        <span class="material-icons tcx-row-icon">${t.icon}</span>
        <div class="tcx-row-main">
          <div class="tcx-row-top">
            <strong>${escapeHtml(r.personName || 'Unknown')}</strong>
            <span class="tcx-when">${escapeHtml(r.when || '')}</span>
            <span class="tcx-type">${escapeHtml(t.label)}</span>
            ${money}
          </div>
          <div class="tcx-row-msg">${escapeHtml(r.msg || '')}</div>
        </div>
        <div class="tcx-row-actions">
          ${alt}
          <button class="btn btn-primary btn-sm" onclick="window.ClockModule.openExceptionEntry('${_safeTicketId(r.entryId)}')">Review</button>
        </div>
      </div>`;
  }).join('');

  return `${summary}${chips}<div class="tcx-list">${list}</div>`;
}

function _tcxRenderResults() {
  const el = document.getElementById('tcx-results');
  if (el) el.innerHTML = getExceptionsResultsHTML();
}

function switchExceptionsPeriod(period) {
  ClockState.exceptionsPeriod = period;
  if (period === 'payperiod') ClockState.exceptionsPayPeriodOffset = -1;
  // A period change invalidates the on-screen result — never leave the previous
  // range's rows sitting under a new label (the TASK-1315 trap on the reports tab).
  ClockState.exceptionsLoadSeq++;
  ClockState.exceptionsRows = [];
  ClockState.exceptionsMeta = null;
  ClockState.exceptionsRan = false;
  ClockState.exceptionsLoading = false;
  ClockState.exceptionsTypeFilter = 'all';
  const content = document.getElementById('workforce-content');
  if (content) content.innerHTML = getWorkforceContentHTML();
  if (period !== 'custom') runTimecardExceptions();
}

function navExceptionsPayPeriod(delta) {
  ClockState.exceptionsPayPeriodOffset += delta;
  ClockState.exceptionsLoadSeq++;
  ClockState.exceptionsRows = [];
  ClockState.exceptionsMeta = null;
  ClockState.exceptionsRan = false;
  ClockState.exceptionsTypeFilter = 'all';
  runTimecardExceptions();
}

function setExceptionsCustomRange() {
  const s = document.getElementById('tcx-custom-start')?.value || null;
  const e = document.getElementById('tcx-custom-end')?.value || null;
  ClockState.exceptionsCustomStart = s;
  ClockState.exceptionsCustomEnd = e;
  if (s && e) runTimecardExceptions();
}

function setExceptionsTypeFilter(type) {
  ClockState.exceptionsTypeFilter = type;
  _tcxRenderResults();
}

// One tap from the fix. Opens the SAME edit modal the rest of Workforce uses —
// no bespoke editor, so every write keeps its existing audit trail, overlap
// preview and permission checks. Nothing is changed until the human saves.
function openExceptionEntry(entryId) {
  if (!hasWorkforceAccess()) return;
  editClockEntry(entryId);
}

async function runTimecardExceptions() {
  // Hard gate — this scan reads every person's hours (TASK-360: cross-person
  // clock data is `workforce`, not `clock`). The UI hides the tab; this is the
  // second layer, and the one that matters if someone calls the export directly.
  //
  // Be precise about what the server does here: `firestore.rules` clockEntries
  // `read` (~:2282) is BROADER than hasWorkforceAccess() — it also allows
  // dispatcher+, tenant admins, compliance managers, and holders of
  // billing/bi/hr/projects/compliance/dispatch. So rules deny the un-scoped
  // range query for a driver / public / PIN session and for a member holding
  // ONLY `clock`, but a member holding e.g. `bi` would be allowed through at the
  // rules layer and is stopped here in the client instead. That generosity is
  // pre-existing (COD 2026-06-03) and out of scope for this slice — do NOT read
  // this gate as "rules already prevent it" for every caller.
  if (!hasWorkforceAccess()) {
    window.showToast?.('Timecard Exceptions requires the Workforce module', 'error');
    return;
  }
  const range = _tcxPeriodDates();
  if (!range) {
    ClockState.exceptionsLoading = false;
    ClockState.exceptionsRan = false;
    ClockState.exceptionsRows = [];
    ClockState.exceptionsMeta = null;
    _tcxRenderResults();
    if (ClockState.exceptionsPeriod === 'custom') window.showToast?.('Pick a start and end date', 'info');
    return;
  }

  const seq = ++ClockState.exceptionsLoadSeq;
  ClockState.exceptionsLoading = true;
  _tcxRenderResults();

  try {
    const tenant = ClockState.tenantId;
    const snap = await getDocs(query(
      collection(db, 'tenants', tenant, 'clockEntries'),
      where('clockIn', '>=', Timestamp.fromDate(range.start)),
      where('clockIn', '<=', Timestamp.fromDate(range.end)),
      orderBy('clockIn', 'desc'),
      limit(TCX_ENTRY_CAP)
    ));
    if (seq !== ClockState.exceptionsLoadSeq) return; // superseded — discard

    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const capped = snap.size >= TCX_ENTRY_CAP;
    // CLOSED only. An entry still on the clock has no final hours, so every
    // check below would be measuring a moving number.
    const closed = all.filter(e => !!e.clockOut && e.status !== 'active');
    const activeCount = all.length - closed.length;

    // Seed the weekly cache so editClockEntry()/deleteClockEntry() can resolve
    // these ids without a second fetch. Same pattern showEmployeeDetail uses.
    // Deliberately does NOT set weeklyCacheFullyLoaded — this is a partial seed
    // and the weekly table must still run its own authoritative full-week query.
    closed.forEach(e => {
      if (ClockState.recentEntries.find(r => r.id === e.id) || ClockState.activeEntries.find(a => a.id === e.id)) return;
      const cin = _tcxDate(e.clockIn);
      if (!cin) return;
      const key = getWeekKey(cin);
      if (!key) return;
      if (!ClockState.weeklyCache[key]) ClockState.weeklyCache[key] = [];
      if (!ClockState.weeklyCache[key].find(c => c.id === e.id)) ClockState.weeklyCache[key].push(e);
    });

    // ---- internal-time customers ---------------------------------------
    let customers = ClockState.customers;
    if (!customers || customers.length === 0) {
      // The live subscription normally has these; one-shot only if it hasn't
      // landed yet. If it fails we SKIP the ticketless checks entirely rather
      // than report legitimate Shop/Office time as a leak.
      // Deliberately uncapped: subscribeToData() already reads this whole
      // collection on every session, so this is the same cost class, not a new
      // one — and a limit() here could silently drop an internalTimeOnly
      // customer, which would turn correct shop time into a false "leak" row.
      try {
        customers = await _clockCustomerSource().getAll(tenant); // TASK-9694 S6a — directory (name + flags)
      } catch (err) {
        console.warn('[TCX] customer load failed — ticketless checks skipped:', err?.message || err);
        customers = null;
      }
    }
    if (seq !== ClockState.exceptionsLoadSeq) return;
    const internalResolved = Array.isArray(customers);
    const internalIds = internalResolved ? _tcxInternalCustomerIds(customers) : new Set();

    // ---- ticket state ----------------------------------------------------
    const ticketIds = [...new Set(closed.map(e => e.ticketId).filter(Boolean))];
    const tickets = {};
    let ticketLookupSkipped = '';
    if (ticketIds.length > TCX_TICKET_CAP) {
      ticketLookupSkipped = `${ticketIds.length} tickets in range exceeds the ${TCX_TICKET_CAP} lookup cap`;
    } else {
      let anyTicketFetchFailed = false;
      for (let i = 0; i < ticketIds.length; i += TCX_TICKET_FANOUT) {
        const batch = ticketIds.slice(i, i + TCX_TICKET_FANOUT);
        // eslint-disable-next-line no-await-in-loop
        const res = await Promise.all(batch.map(tid =>
          getDoc(doc(db, 'tenants', tenant, 'tickets', tid))
            .then(s => ({ tid, exists: s.exists(), data: s.exists() ? s.data() : null }))
            .catch(err => { anyTicketFetchFailed = true; console.warn('[TCX] ticket fetch failed', tid, err?.message || err); return null; })
        ));
        if (seq !== ClockState.exceptionsLoadSeq) return;
        res.forEach(r => { if (r) tickets[r.tid] = r; });
      }
      if (anyTicketFetchFailed) ticketLookupSkipped = 'some tickets could not be read';
    }

    // ---- ticket LINES (TASK-1548 equipment-zero-hours arm) ----------------
    // One ticketLines subcollection read per live ticket in range — the same
    // fan-out and cap discipline as the ticket fetch above. Skipped tickets
    // (deleted / missing / over-cap) are already covered by their own arms.
    // A failed line read is COUNTED and surfaced, never silently treated as
    // "no lines" (this whole family of bugs is silent-open failures).
    const ticketLines = {};
    let lineLookupIncomplete = 0;
    {
      const _lineTids = Object.entries(tickets)
        .filter(([, r]) => r.exists && r.data && r.data.status !== 'deleted')
        .map(([tid]) => tid);
      for (let i = 0; i < _lineTids.length; i += TCX_TICKET_FANOUT) {
        const batch = _lineTids.slice(i, i + TCX_TICKET_FANOUT);
        // eslint-disable-next-line no-await-in-loop
        const res = await Promise.all(batch.map(tid =>
          getDocs(collection(db, 'tenants', tenant, 'tickets', tid, 'ticketLines'))
            .then(s => ({ tid, lines: s.docs.map(d => ({ id: d.id, ...d.data() })) }))
            .catch(err => { lineLookupIncomplete++; console.warn('[TCX] ticketLines fetch failed', tid, err?.message || err); return null; })
        ));
        if (seq !== ClockState.exceptionsLoadSeq) return;
        res.forEach(r => { if (r) ticketLines[r.tid] = r.lines; });
      }
    }

    // ---- ticket money (TASK-9646 S3b-5) ----------------------------------
    // Only the tickets a row shows a dollar figure for; each resolved through the
    // shared helper. Denied / failed / no mirror -> the ticket's own fields.
    {
      const _money = await _clockLoadTicketMoney(tenant, _tcxMoneyTicketIds(tickets, ticketLines));
      if (seq !== ClockState.exceptionsLoadSeq) return;
      _money.forEach((m, tid) => {
        const r = tickets[tid];
        if (r && r.data) tickets[tid] = { ...r, data: _tmvWithTicketMoney(r.data, m) };
      });
    }

    // ---- run the checks --------------------------------------------------
    const { rows, unattachedHours } = buildTimecardExceptions(closed, {
      tickets, ticketLines, internalIds, internalResolved,
      thresholdHours: getOversizedShiftThreshold()
    });

    if (seq !== ClockState.exceptionsLoadSeq) return;
    ClockState.exceptionsRows = rows;
    ClockState.exceptionsMeta = {
      range, scanned: all.length, closed: closed.length, active: activeCount,
      capped, ticketsChecked: Object.keys(tickets).length, ticketLookupSkipped,
      lineLookupIncomplete,
      internalUnresolved: !internalResolved, unattachedHours, ranAt: Date.now()
    };
    ClockState.exceptionsRan = true;
  } catch (err) {
    console.error('[TCX] exceptions scan failed:', err);
    if (seq === ClockState.exceptionsLoadSeq) {
      ClockState.exceptionsRows = [];
      ClockState.exceptionsMeta = null;
      ClockState.exceptionsRan = false;
      ClockState.exceptionsLoading = false;
      const el = document.getElementById('tcx-results');
      if (el) el.innerHTML = `<div class="tcx-error">Couldn't run the check: ${escapeHtml(err?.message || String(err))}</div>`;
      return;
    }
  } finally {
    if (seq === ClockState.exceptionsLoadSeq) ClockState.exceptionsLoading = false;
  }
  if (seq === ClockState.exceptionsLoadSeq) _tcxRenderResults();
}

// TASK-9646 S3b-5 — the ticketMoney mirror for an OFFICE scan (Timecard
// Exceptions, Ticket Health). Map(ticketId -> mirror data) for the ids that
// have one. Read-only, by id, TCX_TICKET_FANOUT at a time. Never throws, never
// toasts: no Workforce access, a login the rules plainly deny (not the owner,
// no billing / bi module), a permission-denied (stops the rest) or any other
// read error -> those tickets are absent and keep their own fields.
async function _clockLoadTicketMoney(tenant, ids) {
  const out = new Map();
  const list = [...new Set((ids || []).filter(x => typeof x === 'string' && x))];
  if (!tenant || !list.length || !hasWorkforceAccess()) return out;
  const ma = window.AppState?.memberAccess;
  if (ma && ma.role !== 'owner' && !(Array.isArray(ma.modules) && (ma.modules.includes('billing') || ma.modules.includes('bi')))) return out;
  let denied = false;
  for (let i = 0; i < list.length && !denied; i += TCX_TICKET_FANOUT) {
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(list.slice(i, i + TCX_TICKET_FANOUT).map(tid =>
      getDoc(doc(db, 'tenants', tenant, 'ticketMoney', tid))
        .then(s => { if (s.exists()) out.set(tid, s.data()); })
        .catch(err => { if (err?.code === 'permission-denied') denied = true; })
    ));
  }
  if (denied) out.clear();
  // the helper chunk, only when there is a mirror to apply; a failed load = no mirror (own fields)
  if (out.size && !(await _clockLoadTmv())) out.clear();
  return out;
}

// TASK-9646 S3b-5 — the tickets a Timecard Exceptions row prints a dollar
// figure for (buildTimecardExceptions' deleted-ticket / equipment-zero-hours /
// equipment-unpriced arms), so the scan reads a mirror only where it shows one.
function _tcxMoneyTicketIds(tickets, ticketLines) {
  const ids = [];
  for (const [tid, r] of Object.entries(tickets || {})) {
    if (!r || !r.exists || !r.data) continue;
    const lines = (ticketLines || {})[tid];
    if (r.data.status === 'deleted'
      || (Array.isArray(lines) && lines.some(l => equipmentLineNeedsHours(l) || equipmentLineUnpriced(l)))) ids.push(tid);
  }
  return ids;
}

// TASK-1548 — "priced equipment with no hours" (the job_generate birth defect).
// 🔴 TWIN of billing.js `equipmentLineNeedsHours` — the ready/invoice gate and this
// detection arm must agree, so the bodies stay BYTE-IDENTICAL (change both or they
// diverge; tests/task1548-zero-hour-equipment.test.mjs asserts it). See the
// billing.js copy for the rationale behind every exclusion.
function equipmentLineNeedsHours(line) {
  const l = line || {};
  if (l.type !== 'EQUIPMENT') return false;
  if (l.zeroHoursOk === true) return false;         // marked deliberately non-billable
  if (l.includeOnInvoice === false) return false;   // explicitly excluded from the invoice
  if (!((Number(l.rate) || 0) > 0)) return false;   // unpriced — TASK-1165's lane
  if (!isTimeBasedUnit(l.rateUnit || 'Hourly')) return false; // per-unit bills on quantity
  if ((Number(l.hours) || 0) > 0) return false;
  if ((Number(l.lineAmount) || 0) > 0) return false;
  return true;
}

// TASK-1405 — the MIRROR of the predicate above: a rate-0 EQUIPMENT line carrying
// REAL HOURS. The machine ran, the rate is zero, and the ticket's other lines carry
// money — so the $0-total gate never fires and the priced-no-hours gate skips it by
// design. It invoices the machine's whole day at nothing while the ticket reads
// complete. 🔴 TWIN of billing.js `equipmentLineUnpriced` — the ready/invoice gate
// and this detection arm must agree, so the bodies stay BYTE-IDENTICAL (change both
// or they diverge; tests/task1548-zero-hour-equipment.test.mjs asserts it). See the
// billing.js copy for the rationale behind every exclusion — including why EMPLOYEE
// lines are out of scope by TYPE (TITS bills the truck, never the person).
function equipmentLineUnpriced(line) {
  const l = line || {};
  if (l.type !== 'EQUIPMENT') return false;
  if (l.zeroHoursOk === true) return false;         // marked deliberately non-billable
  if (l.includeOnInvoice === false) return false;   // explicitly excluded from the invoice
  if ((Number(l.rate) || 0) > 0) return false;      // priced — the zero-hours gate's lane
  if (!isTimeBasedUnit(l.rateUnit || 'Hourly')) return false; // per-unit bills on quantity
  if (!((Number(l.hours) || 0) > 0)) return false;  // no hours: nothing proves the machine ran
  if ((Number(l.lineAmount) || 0) > 0) return false;
  return true;
}

// The exception rules themselves — PURE. No Firestore, no DOM, no ClockState.
// Split out from the scan so the rules can be tested directly against fixture
// entries (tests/task1442-timecard-exceptions.spec.js) instead of only through
// a live query. Returns { rows, unattachedHours }.
//
// `entries` MUST already be closed (clockOut present) and inside the range.
// `opts.tickets`         — { [ticketId]: { exists, data } } (may be {} when the lookup was skipped)
// `opts.ticketLines`     — { [ticketId]: line[] } (TASK-1548; may be {} / missing a
//                          ticket when the line lookup was skipped or failed — an
//                          ABSENT key is "unknown", never "no problem")
// `opts.internalIds`     — Set of internalTimeOnly customer ids
// `opts.internalResolved`— false when the customer list could not be read; the two
//                          ticketless checks are then SKIPPED rather than risk
//                          reporting legitimate internal time as a revenue leak.
// `opts.thresholdHours`  — long-shift threshold (tenant `settings.oversizedShiftHours`, default 16)
function buildTimecardExceptions(entries, opts) {
  const o = opts || {};
  const tickets = o.tickets || {};
  const ticketLines = o.ticketLines || {};
  const internalIds = o.internalIds || new Set();
  const flaggedZeroHourTickets = new Set(); // TASK-1548 — one row per ticket, not per punch
  const flaggedUnpricedTickets = new Set(); // TASK-1405 — same, for the hours-with-no-rate arm
  const internalResolved = o.internalResolved !== false;
  const th = Number(o.thresholdHours) > 0 ? Number(o.thresholdHours) : 16;
  const closed = entries || [];
  const rows = [];
  let unattachedHours = 0;

  for (const e of closed) {
    const cin = _tcxDate(e.clockIn);
    const cout = _tcxDate(e.clockOut);
    const hrs = _tcxHours(e);
    const when = cin ? cin.toLocaleDateString([], { month: 'short', day: 'numeric' }) : '';
    const base = { entryId: e.id, personId: e.personId || '', personName: e.personName || '', when, sortMs: cin ? cin.getTime() : 0 };

    // 1. Long shift. FLAG ONLY — the hours still count everywhere.
    if (hrs > th) {
      rows.push({ ...base, type: 'long-shift',
        msg: `${hrs.toFixed(2)}h on one punch (${_tcxWhen(cin)} → ${_tcxWhen(cout)}) — over the ${th}h review threshold. Usually a missed clock-out. Hours are NOT adjusted.` });
    }

    // 2. Edited after clock-out.
    const upd = _tcxDate(e.updatedAt);
    if (cout && upd && (upd - cout) > TCX_EDIT_GRACE_MS) {
      rows.push({ ...base, type: 'edited-after',
        msg: `Changed ${_tcxWhen(upd)}, ${((upd - cout) / 3600000).toFixed(1)}h after the clock-out. See History on the entry for who and what.` });
    }

    // 3/4. Ticket state.
    if (e.ticketId) {
      const t = tickets[e.ticketId];
      if (t && !t.exists) {
        rows.push({ ...base, type: 'orphan-ticket',
          msg: `${hrs.toFixed(2)}h points at ticket ${String(e.ticketId).slice(0, 8)}… which no longer exists.` });
      } else if (t && t.data && t.data.status === 'deleted') {
        // Deletion on this collection is status === 'deleted'. There is no
        // boolean `deleted` field (TASK-1464 — a committed script gets this wrong).
        rows.push({ ...base, type: 'deleted-ticket', grandTotal: Number(t.data.grandTotal) || 0,
          msg: `${hrs.toFixed(2)}h sits on deleted ticket #${t.data.ticketNo || '—'}${e.customerName ? ' (' + e.customerName + ')' : ''} — the time is paid but can never be invoiced.` });
      } else if (t && t.data && Array.isArray(ticketLines[e.ticketId])) {
        // The two line-level arms below are independently de-duped per ticket
        // (TASK-1405): one ticket can carry BOTH shapes, and gating the whole
        // branch on the zero-hours Set would hide the second one after the first
        // punch flags the first.
        //
        // TASK-1548 — the clock PROVES the shift ran, yet the ticket carries a
        // PRICED equipment line at 0 hours: it bills $0 for real work, and once
        // invoiced it reads exactly like a deliberate $0 (#100464, $375 lost).
        // One row per ticket (several punches can feed one ticket). An ABSENT
        // ticketLines key means the lookup failed/was skipped — unknown, not clean
        // (the meta line reports that separately). FLAG ONLY, nothing auto-fills.
        if (!flaggedZeroHourTickets.has(e.ticketId)) {
          const zh = ticketLines[e.ticketId].filter(equipmentLineNeedsHours);
          if (zh.length > 0) {
            flaggedZeroHourTickets.add(e.ticketId);
            const inv = t.data.invoiced === true || !!t.data.invoiceNumber || !!t.data.invoicedAt;
            const names = zh.map(l => `${l.assetName || l.assetId || 'equipment'} ($${Number(l.rate) || 0}/${l.rateUnit || 'Hourly'})`).join(', ');
            rows.push({ ...base, type: 'equipment-zero-hours', grandTotal: Number(t.data.grandTotal) || 0,
              msg: `Ticket #${t.data.ticketNo || '—'}${e.customerName ? ' (' + e.customerName + ')' : ''} has priced equipment with 0 hours: ${names}${inv ? ` — ALREADY INVOICED (${t.data.invoiceNumber || 'no number'})` : ''}. The punch proves the work ran. Enter the hours or mark the line non-billable in AR/AP → Review Ticket.` });
          }
        }
        // TASK-1405 — the mirror shape: the equipment line HAS the hours and no
        // rate, so it bills the machine's whole day at $0 while the ticket total
        // (its other lines) looks perfectly healthy. Same one-row-per-ticket rule,
        // same FLAG ONLY contract — nothing is auto-priced anywhere.
        if (!flaggedUnpricedTickets.has(e.ticketId)) {
          const up = ticketLines[e.ticketId].filter(equipmentLineUnpriced);
          if (up.length > 0) {
            flaggedUnpricedTickets.add(e.ticketId);
            const inv = t.data.invoiced === true || !!t.data.invoiceNumber || !!t.data.invoicedAt;
            const names = up.map(l => `${l.assetName || l.assetId || 'equipment'} (${Number(l.hours) || 0}h)`).join(', ');
            rows.push({ ...base, type: 'equipment-unpriced', grandTotal: Number(t.data.grandTotal) || 0,
              msg: `Ticket #${t.data.ticketNo || '—'}${e.customerName ? ' (' + e.customerName + ')' : ''} has equipment that ran with NO rate: ${names}${inv ? ` — ALREADY INVOICED (${t.data.invoiceNumber || 'no number'})` : ''}. Those hours bill $0. Enter the rate or mark the line non-billable in AR/AP → Review Ticket.` });
          }
        }
      }
    } else if (internalResolved) {
      // 5/6. No ticket at all. Split by whether a job was picked, because the
      // two have different causes AND different fixes. Internal-time customers
      // are correctly ticketless (TASK-1175) — excluded by flag, never by name.
      const isInternal = !!e.customerId && internalIds.has(e.customerId);
      if (!isInternal) {
        unattachedHours += hrs;
        if (e.jobId) {
          rows.push({ ...base, type: 'job-no-ticket',
            msg: `${hrs.toFixed(2)}h on ${e.jobTitle || 'a job'}${e.customerName ? ' for ' + e.customerName : ''} never reached a ticket.` });
        } else {
          rows.push({ ...base, type: 'no-job-no-ticket',
            msg: `${hrs.toFixed(2)}h with no job and no ticket${e.customerName ? ' (' + e.customerName + ')' : ''} — nothing to invoice it against.` });
        }
      }
    }
  }

  // 7. Overlapping / nested punches, per person. Sweep the person's punches in
  // clock-in order and compare each against the running max end — catches the
  // nested case (B entirely inside A) that a naive neighbour compare misses.
  const byPerson = {};
  closed.forEach(e => { if (e.personId) (byPerson[e.personId] = byPerson[e.personId] || []).push(e); });
  for (const list of Object.values(byPerson)) {
    const arr = list
      .map(e => ({ e, a: _tcxDate(e.clockIn), b: _tcxDate(e.clockOut) }))
      // Zero-length punches are excluded on purpose: closeStaleClockEntry()
      // closes a forgotten punch at clockOut === clockIn, and that neutralised
      // entry sitting inside the person's real shift is not a double-pay overlap.
      .filter(x => x.a && x.b && x.b > x.a)
      .sort((x, y) => x.a - y.a);
    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        if (arr[j].a >= arr[i].b) break; // sorted by start — no later punch can overlap arr[i] either
        const A = arr[i], B = arr[j];
        const overlapMs = Math.min(A.b.getTime(), B.b.getTime()) - B.a.getTime();
        if (overlapMs < TCX_MIN_OVERLAP_MS) continue; // job-switch second-rounding, not double pay
        const nested = B.a >= A.a && B.b <= A.b;
        const mins = Math.round(overlapMs / 60000);
        rows.push({
          entryId: B.e.id, altEntryId: A.e.id, personId: B.e.personId || '',
          personName: B.e.personName || '', when: B.a.toLocaleDateString([], { month: 'short', day: 'numeric' }),
          sortMs: B.a.getTime(), type: 'overlap',
          msg: `${nested ? 'Nested' : 'Overlapping'} punches: ${_tcxWhen(A.a)}–${_tcxWhen(A.b)} and ${_tcxWhen(B.a)}–${_tcxWhen(B.b)}. ${mins} min counted twice.`
        });
      }
    }
  }

  rows.sort((a, b) => {
    const sa = TCX_SEV_ORDER[(TCX_TYPES[a.type] || {}).sev] ?? 9;
    const sb = TCX_SEV_ORDER[(TCX_TYPES[b.type] || {}).sev] ?? 9;
    if (sa !== sb) return sa - sb;
    return b.sortMs - a.sortMs;
  });

  return { rows, unattachedHours };
}

async function runTicketHealthCheck() {
  const el = document.getElementById('health-results');
  if (!el) return;
  el.innerHTML = '<div style="padding:12px;color:var(--text-secondary);"><span class="btn-spinner"></span> Running health check...</div>';

  try {
    const tenant = ClockState.tenantId;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const weekAgo = new Date(today.getTime() - 7 * 86400000);

    // Recent tickets
    const ticketSnap = await getDocs(
      query(collection(db, 'tenants', tenant, 'tickets'), where('status', 'in', ['draft', 'ready']), orderBy('createdAt', 'desc'), limit(20))
    );

    // Active clock entries
    const activeSnap = await getDocs(
      query(collection(db, 'tenants', tenant, 'clockEntries'), where('status', '==', 'active'))
    );
    // TASK-9646 S3b-5 — the same tickets' ticketMoney mirrors (office only; empty
    // when denied / missing -> the check below runs exactly as before).
    const moneyById = await _clockLoadTicketMoney(tenant, ticketSnap.docs.map(d => d.id));

    const issues = [];
    const healthy = [];

    // Check active entries have valid tickets
    for (const d of activeSnap.docs) {
      const entry = d.data();
      if (entry.ticketId) {
        const tSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId));
        if (!tSnap.exists()) {
          issues.push({ type: 'orphan', icon: 'link_off', msg: `${entry.personName} clocked in but ticket ${entry.ticketId.slice(0,8)}… is missing`, id: d.id });
        } else if (tSnap.data().status === 'deleted') {
          issues.push({ type: 'deleted-ticket', icon: 'delete', msg: `${entry.personName} clocked in but ticket #${tSnap.data().ticketNo} is deleted`, id: d.id });
        }
      }
      if (entry.rate === 0 || entry.rate == null) {
        issues.push({ type: 'zero-rate', icon: 'attach_money', msg: `${entry.personName} has $0 rate (${entry.role || 'no role'})`, id: d.id });
      }
    }

    // Check recent tickets for issues
    for (const d of ticketSnap.docs) {
      const ticket = d.data();
      const linesSnap = await getDocs(collection(db, 'tenants', tenant, 'tickets', d.id, 'ticketLines'));
      const empLines = linesSnap.docs.filter(l => l.data().type === 'EMPLOYEE');

      // Check for orphaned ticket lines (clockEntryId points to deleted entry)
      for (const line of empLines) {
        const ld = line.data();
        if (ld.clockEntryId) {
          const entrySnap = await getDoc(doc(db, 'tenants', tenant, 'clockEntries', ld.clockEntryId));
          if (!entrySnap.exists()) {
            issues.push({ type: 'orphan-line', icon: 'broken_image', msg: `Ticket #${ticket.ticketNo} has line for ${ld.personName} but clock entry was deleted`, id: d.id });
          }
        }
        if ((ld.hours || 0) === 0 && ld.toTime === null) {
          // Active — not an issue
        } else if ((ld.hours || 0) === 0 && ld.toTime !== null) {
          issues.push({ type: 'zero-hours', icon: 'timer_off', msg: `Ticket #${ticket.ticketNo}: ${ld.personName} has 0 hours but was clocked out`, id: d.id });
        }
      }

      // Check grandTotal consistency: the stored header against the plain sum
      // of the lines just fetched (unchanged).
      let calcTotal = 0;
      linesSnap.docs.forEach(l => {
        const ld = l.data();
        calcTotal += (ld.type === 'DOWNTIME' ? -1 : 1) * (ld.lineAmount || 0);
      });
      // TASK-9646 S3b-5 — ADDITIVE only (COD r1 Code Reviewer: the mirror is not
      // proof it has caught up with these lines, so it never clears the check
      // above). When the lines agree with the header but a fresh open mirror —
      // the server's own total, tax and fuel surcharge included — does not, the
      // header is flagged against the server's figure.
      const _mv = _tmvTicketMoneyView(ticket, moneyById.get(d.id) || null);
      if (Math.abs(calcTotal - (ticket.grandTotal || 0)) <= 0.01 && _mv.source === 'mirror'
        && Math.abs((Number(_mv.grandTotal) || 0) - (ticket.grandTotal || 0)) > 0.01) {
        calcTotal = Number(_mv.grandTotal) || 0;
      }
      if (Math.abs(calcTotal - (ticket.grandTotal || 0)) > 0.01 && linesSnap.size > 0) {
        issues.push({ type: 'total-mismatch', icon: 'calculate', msg: `Ticket #${ticket.ticketNo}: stored total $${(ticket.grandTotal||0).toFixed(2)} ≠ calculated $${calcTotal.toFixed(2)}`, id: d.id });
      }

      if (empLines.length === 0 && linesSnap.size === 0) {
        issues.push({ type: 'empty-ticket', icon: 'receipt_long', msg: `Ticket #${ticket.ticketNo} (${ticket.jobName || 'no job'}) has no line items`, id: d.id });
      }
    }

    // Count healthy
    const healthyCount = ticketSnap.size - issues.filter(i => i.type !== 'zero-rate').length;

    let html = `<div style="display:flex;gap:16px;margin-bottom:16px;">
      <div style="padding:12px 16px;border-radius:8px;background:#f0fdf4;border:1px solid #bbf7d0;">
        <span style="font-size:1.5rem;font-weight:700;color:#16a34a;">${activeSnap.size}</span>
        <span style="color:#15803d;font-size:0.85rem;"> active clocks</span>
      </div>
      <div style="padding:12px 16px;border-radius:8px;background:#eff6ff;border:1px solid #bfdbfe;">
        <span style="font-size:1.5rem;font-weight:700;color:#2563eb;">${ticketSnap.size}</span>
        <span style="color:#1d4ed8;font-size:0.85rem;"> recent tickets</span>
      </div>
      <div style="padding:12px 16px;border-radius:8px;background:${issues.length ? '#fef2f2' : '#f0fdf4'};border:1px solid ${issues.length ? '#fecaca' : '#bbf7d0'};">
        <span style="font-size:1.5rem;font-weight:700;color:${issues.length ? '#dc2626' : '#16a34a'};">${issues.length}</span>
        <span style="color:${issues.length ? '#b91c1c' : '#15803d'};font-size:0.85rem;"> issues</span>
      </div>
    </div>`;

    if (issues.length === 0) {
      html += '<div style="padding:16px;background:#f0fdf4;border-radius:8px;border:1px solid #bbf7d0;color:#15803d;"><span class="material-icons" style="vertical-align:middle;">check_circle</span> All tickets healthy — no orphaned entries, no mismatched totals.</div>';
    } else {
      html += '<div style="display:flex;flex-direction:column;gap:8px;">';
      for (const issue of issues) {
        const colors = { 'orphan': '#dc2626', 'deleted-ticket': '#dc2626', 'orphan-line': '#dc2626', 'zero-rate': '#f59e0b', 'zero-hours': '#f59e0b', 'total-mismatch': '#f59e0b', 'empty-ticket': '#6b7280' };
        const color = colors[issue.type] || '#6b7280';
        html += `<div style="padding:10px 14px;border-radius:8px;background:#fff;border:1px solid #e2e8f0;display:flex;align-items:center;gap:10px;">
          <span class="material-icons" style="color:${color};font-size:18px;">${issue.icon}</span>
          <span style="flex:1;">${escapeHtml(issue.msg)}</span>
          <span style="color:var(--text-muted);font-size:0.75rem;">${issue.type}</span>
        </div>`;
      }
      html += '</div>';
    }

    el.innerHTML = html;
  } catch (err) {
    console.error('[Health] Check failed:', err);
    el.innerHTML = `<div style="padding:12px;color:#dc2626;">Health check failed: ${err.message}</div>`;
  }
}

// ============================================
// TASK-916 — All Activity filter helpers
// ============================================
// Resolve the active date filter to a {start,end} Date range, or null for "all".
// All ranges are inclusive of the day boundaries in LOCAL time.
function _auditDateRange() {
  const f = ClockState.auditFilterDate;
  if (!f || f === 'all') return null;
  let start, end = new Date(); end.setHours(23, 59, 59, 999);
  if (f === 'today') {
    start = new Date(); start.setHours(0, 0, 0, 0);
  } else if (f === 'week') {
    start = getWeekStart(new Date()); start.setHours(0, 0, 0, 0);
  } else if (f === 'payperiod') {
    const pp = getPayPeriodDates(0);
    if (!pp?.start) return null;
    start = pp.start; end = pp.end;
  } else if (f === 'custom') {
    if (!ClockState.auditFilterCustomStart) return null;
    start = new Date(ClockState.auditFilterCustomStart + 'T00:00:00');
    end = ClockState.auditFilterCustomEnd
      ? new Date(ClockState.auditFilterCustomEnd + 'T23:59:59')
      : new Date();
  } else {
    return null;
  }
  if (!start || isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  return { start, end };
}

// Derive a stable {key,label} for an audit doc's actor, used by BOTH the actor
// dropdown and the rendered actor column. Email wins; else the driver name
// (actorName, or a non-email `user` string); else an anonymous-driver bucket.
function _auditActor(data) {
  const email = data.actorEmail
    || (typeof data.user === 'string' && data.user.includes('@') ? data.user : '')
    || '';
  const name = data.actorName
    || (typeof data.user === 'string' && data.user && !data.user.includes('@') && data.user !== 'unknown' ? data.user : '')
    || '';
  if (email) return { key: email, label: email };
  if (name) return { key: 'name:' + name, label: name };
  return { key: '__anon__', label: 'Driver (anonymous)' };
}

// Filter-bar HTML (date chips + actor dropdown + custom-range inputs).
function _auditFilterBarHTML(actorOptions) {
  const dateBtns = [
    { k: 'all', l: 'All Time' },
    { k: 'today', l: 'Today' },
    { k: 'week', l: 'This Week' },
    { k: 'payperiod', l: 'Pay Period' },
    { k: 'custom', l: 'Custom' },
  ].map(b => `<button class="filter-btn ${ClockState.auditFilterDate === b.k ? 'active' : ''}" onclick="window.ClockModule.setAuditDateFilter('${b.k}')">${b.l}</button>`).join('');

  const customRow = ClockState.auditFilterDate === 'custom' ? `
    <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-top:8px;">
      <input type="date" id="audit-custom-start" class="control-auto" value="${escapeHtml(ClockState.auditFilterCustomStart || '')}" style="padding:4px 8px;border:1px solid var(--border-light);border-radius:6px;font-size:0.82rem;">
      <span style="color:var(--text-secondary);font-size:0.82rem;">to</span>
      <input type="date" id="audit-custom-end" class="control-auto" value="${escapeHtml(ClockState.auditFilterCustomEnd || '')}" style="padding:4px 8px;border:1px solid var(--border-light);border-radius:6px;font-size:0.82rem;">
      <button class="btn btn-secondary" style="font-size:0.8rem;padding:4px 12px;" onclick="window.ClockModule.applyAuditCustomRange()">Apply</button>
    </div>` : '';

  const opts = ['<option value="all">All Users</option>']
    .concat(actorOptions.map(o => `<option value="${escapeHtml(o.key)}" ${ClockState.auditFilterActor === o.key ? 'selected' : ''}>${escapeHtml(o.label)}</option>`))
    .join('');

  return `
    <div style="background:#f8fafc;border:1px solid var(--border-light);border-radius:8px;padding:10px 12px;margin-bottom:12px;">
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
        <div style="display:flex;gap:6px;flex-wrap:wrap;">${dateBtns}</div>
        <div style="flex:1;"></div>
        <select onchange="window.ClockModule.setAuditActorFilter(this.value)" style="padding:5px 8px;border:1px solid var(--border-light);border-radius:6px;font-size:0.82rem;max-width:220px;">${opts}</select>
      </div>
      ${customRow}
    </div>`;
}

async function loadAuditLog(type) {
  const el = document.getElementById('health-results');
  if (!el) return;
  // Remember the active log tab so filter changes re-query the same source.
  // An explicit type arg = a tab switch → reset the actor filter (its keys are
  // source-specific). Date filter persists across tabs (deliberate).
  if (type) { ClockState.auditCurrentType = type; ClockState.auditFilterActor = 'all'; }
  type = ClockState.auditCurrentType || 'all';
  el.innerHTML = '<div style="padding:12px;color:var(--text-secondary);"><span class="btn-spinner"></span> Loading audit log...</div>';

  try {
    const tenant = ClockState.tenantId;
    const range = _auditDateRange();
    // When a date filter is active we raise the per-collection cap to 500 for
    // realistic period coverage; otherwise keep the lean recent-feed limits.
    // NOTE: where('timestamp','>=',..)+where('timestamp','<=',..)+orderBy on the
    // SAME field needs NO composite index (single-field range) — do not add one.
    const tsRange = range
      ? [where('timestamp', '>=', Timestamp.fromDate(range.start)), where('timestamp', '<=', Timestamp.fromDate(range.end))]
      : [];
    const capFor = (base) => range ? 500 : base;

    let allDocs = [];
    if (type === 'all' || type === 'clock') {
      try {
        const s = await getDocs(query(collection(db, 'tenants', tenant, 'clockAuditLog'), ...tsRange, orderBy('timestamp', 'desc'), limit(capFor(50))));
        s.docs.forEach(d => allDocs.push({ ...d.data(), _source: 'clock' }));
      } catch (_) {}
      // TASK-916 Slice 3 — surface clockEntry punch-edits (clockIn/out/job/notes
      // changes). This collection finally has a firestore.rules block so the
      // client write path is no longer silently permission-denied. Normalize the
      // nested actor.{uid,email} + actorName into FLAT actorEmail/actorName so the
      // shared actor helpers + renderer treat it like every other source.
      try {
        const _normalizeClockEntryRow = (data, extra) => {
          const act = data.action === 'create' ? 'CLOCK_ENTRY_CREATE'
            : data.action === 'delete' ? 'CLOCK_ENTRY_DELETE' : 'CLOCK_ENTRY_EDIT';
          allDocs.push({
            ...data,
            _source: 'clockentry',
            action: act,
            actorEmail: data.actor?.email || '',
            actorName: data.actorName || '',
            ...(extra || {}),
          });
        };
        // TASK-1655: 311 legacy rows carry timestamp {} (an empty MAP, not a
        // Timestamp — the server sentinel was mangled pre-fix). Firestore sorts
        // maps ABOVE timestamps, so an unguarded orderBy desc returns the legacy
        // junk FIRST and limit() starves out every real dated row. Guard the main
        // query to timestamp-typed values (>= epoch adds no composite index —
        // same-field range + orderBy).
        const tsGuard = tsRange.length ? tsRange : [where('timestamp', '>=', Timestamp.fromMillis(0))];
        const s = await getDocs(query(collection(db, 'tenants', tenant, 'clockEntryAuditLog'), ...tsGuard, orderBy('timestamp', 'desc'), limit(capFor(50))));
        s.docs.forEach(d => _normalizeClockEntryRow(d.data()));
        // Surface the undated legacy rows too, honestly labeled, instead of
        // silently dropping them — a time-filtered view of this collection once
        // produced a false "no audit row exists" conclusion (#100511). Maps sort
        // first in desc order, so this unguarded query IS the legacy fetch; rows
        // with a real Timestamp are the signal the legacy set is exhausted.
        const legacy = await getDocs(query(collection(db, 'tenants', tenant, 'clockEntryAuditLog'), orderBy('timestamp', 'desc'), limit(50)));
        legacy.docs.forEach(d => {
          const data = d.data();
          if (typeof data.timestamp?.toDate === 'function') return; // dated — already covered above
          _normalizeClockEntryRow(data, { _undated: true });
        });
      } catch (_) {}
    }
    if (type === 'all' || type === 'job') {
      try {
        const s = await getDocs(query(collection(db, 'tenants', tenant, 'jobAuditLog'), ...tsRange, orderBy('timestamp', 'desc'), limit(capFor(50))));
        s.docs.forEach(d => allDocs.push({ ...d.data(), _source: 'job' }));
      } catch (_) {}
    }
    if (type === 'all') {
      try {
        // Default "All Activity" excludes telemetry — operators want business
        // events (clock-ins, deletes, settings saves), not session_start /
        // sms_modal_opened heartbeats. Telemetry is its own tab now.
        const s = await getDocs(query(collection(db, 'tenants', tenant, 'auditLog'), ...tsRange, orderBy('timestamp', 'desc'), limit(capFor(100))));
        s.docs.forEach(d => {
          const data = d.data();
          if (data.category === 'telemetry') return;
          allDocs.push({ ...data, _source: data.category || 'general' });
        });
      } catch (_) {}
    }
    if (type === 'telemetry') {
      try {
        const s = await getDocs(query(
          collection(db, 'tenants', tenant, 'auditLog'),
          where('category', '==', 'telemetry'),
          ...tsRange,
          orderBy('timestamp', 'desc'),
          limit(capFor(100))
        ));
        s.docs.forEach(d => allDocs.push({ ...d.data(), _source: 'telemetry' }));
      } catch (_) {}
    }
    // Sort all by timestamp desc
    allDocs.sort((a, b) => (b.timestamp?.toDate?.()?.getTime() || 0) - (a.timestamp?.toDate?.()?.getTime() || 0));
    const finalCap = range ? 500 : (type === 'all' ? 100 : 50);
    ClockState.auditLoadedCapped = allDocs.length > finalCap;
    ClockState.auditLoadedDocs = allDocs.slice(0, finalCap);
    _renderAuditLog();
  } catch (err) {
    console.error('[Health] Audit log failed:', err);
    el.innerHTML = `<div style="padding:12px;color:#dc2626;">Failed to load audit log: ${escapeHtml(err.message || '')}</div>`;
  }
}

// Render (or re-render) the loaded audit docs through the active actor filter.
// Split out from loadAuditLog so the actor dropdown re-filters client-side
// without re-querying Firestore.
function _renderAuditLog() {
  const el = document.getElementById('health-results');
  if (!el) return;
  const type = ClockState.auditCurrentType || 'all';
  const loaded = ClockState.auditLoadedDocs || [];

  // Unique actors for the dropdown (from the loaded docs).
  const actorMap = new Map();
  for (const d of loaded) {
    const a = _auditActor(d);
    if (!actorMap.has(a.key)) actorMap.set(a.key, a);
  }
  const actorOptions = Array.from(actorMap.values()).sort((x, y) => x.label.localeCompare(y.label));

  // Client-side actor filter (Slice 2 — no new query).
  const sel = ClockState.auditFilterActor;
  const docs = (sel && sel !== 'all') ? loaded.filter(d => _auditActor(d).key === sel) : loaded;

  const filterBar = _auditFilterBarHTML(actorOptions);

  if (docs.length === 0) {
    el.innerHTML = filterBar + '<div style="padding:16px;color:var(--text-secondary);">No audit entries match the current filters.</div>';
    return;
  }

  {
    const actionColors = {
      'CLOCK_IN': '#16a34a', 'CLOCK_OUT': '#2563eb', 'DELETE': '#dc2626',
      'JOB_CREATE': '#16a34a', 'JOB_UPDATE': '#f59e0b', 'JOB_ARCHIVE': '#6b7280', 'JOB_DELETE': '#dc2626',
      'MARK_PAID': '#16a34a', 'UNMARK_PAID': '#f59e0b', 'WRITE_OFF': '#dc2626',
      'ARCHIVE': '#6b7280', 'UNARCHIVE': '#3b82f6', 'RESTORE': '#3b82f6',
      'MARK_INVOICED': '#8b5cf6', 'UNMARK_INVOICED': '#f59e0b',
      'DELETE_LINE': '#dc2626', 'STATUS_CHANGE': '#f59e0b',
      'CREATE_PERSON': '#16a34a', 'UPDATE_PERSON': '#f59e0b', 'CREATE_TRUCK': '#16a34a', 'UPDATE_TRUCK': '#f59e0b',
      'CREATE_TRAILER': '#16a34a', 'UPDATE_TRAILER': '#f59e0b', // TASK-9715
      'CREATE_CUSTOMER': '#16a34a', 'UPDATE_CUSTOMER': '#f59e0b', 'DELETE_CUSTOMER': '#dc2626',
      'PIPELINE_MOVE': '#8b5cf6',
      'ASSIGNMENT_CHANGE': '#3b82f6', 'CANCEL_JOBDAY': '#dc2626', 'RESTORE_JOBDAY': '#16a34a',
      'RESCHEDULE_JOBDAY': '#f59e0b', 'DELETE_JOB': '#dc2626',
      'SAVE_SETTINGS': '#6b7280',
      'CLOCK_ENTRY_EDIT': '#f59e0b', 'CLOCK_ENTRY_CREATE': '#16a34a', 'CLOCK_ENTRY_DELETE': '#dc2626',
      'FUEL_CARD_PIN_VIEWED': '#7c3aed', 'FUEL_CARD_PIN_VIEW_RATE_LIMITED': '#dc2626', // TASK-9752
      'MAINT_TASK_STATUS': '#f59e0b', // TASK-9884 — TASK-9766 work-order status rows
      'FILE_ADDED': '#2563eb' // TASK-9958 — a file put on a job or ticket
    };
    const actionIcons = {
      'CLOCK_IN': 'login', 'CLOCK_OUT': 'logout', 'DELETE': 'delete_forever',
      'JOB_CREATE': 'add_circle', 'JOB_UPDATE': 'edit', 'JOB_ARCHIVE': 'archive', 'JOB_DELETE': 'delete',
      'MARK_PAID': 'paid', 'UNMARK_PAID': 'money_off', 'WRITE_OFF': 'money_off',
      'ARCHIVE': 'archive', 'UNARCHIVE': 'unarchive', 'RESTORE': 'restore',
      'MARK_INVOICED': 'receipt', 'UNMARK_INVOICED': 'receipt_long',
      'DELETE_LINE': 'remove_circle', 'STATUS_CHANGE': 'swap_horiz',
      'CREATE_PERSON': 'person_add', 'UPDATE_PERSON': 'person', 'CREATE_TRUCK': 'local_shipping', 'UPDATE_TRUCK': 'local_shipping',
      'CREATE_TRAILER': 'rv_hookup', 'UPDATE_TRAILER': 'rv_hookup', // TASK-9715
      'CREATE_CUSTOMER': 'business', 'UPDATE_CUSTOMER': 'business', 'DELETE_CUSTOMER': 'delete',
      'PIPELINE_MOVE': 'swap_horiz',
      'ASSIGNMENT_CHANGE': 'group', 'CANCEL_JOBDAY': 'event_busy', 'RESTORE_JOBDAY': 'event_available',
      'RESCHEDULE_JOBDAY': 'date_range', 'DELETE_JOB': 'delete',
      'SAVE_SETTINGS': 'settings',
      'CLOCK_ENTRY_EDIT': 'edit_note', 'CLOCK_ENTRY_CREATE': 'note_add', 'CLOCK_ENTRY_DELETE': 'delete',
      'FUEL_CARD_PIN_VIEWED': 'visibility', 'FUEL_CARD_PIN_VIEW_RATE_LIMITED': 'block', // TASK-9752
      'MAINT_TASK_STATUS': 'build', // TASK-9884
      'FILE_ADDED': 'attach_file' // TASK-9958
    };

    const typeLabels = { clock: 'Clock', job: 'Job', all: 'All Activity', telemetry: 'Telemetry' };
    const cappedNote = ClockState.auditLoadedCapped
      ? ` <span style="color:var(--text-secondary);font-weight:400;font-size:0.78rem;">(showing most recent ${docs.length} — narrow the date range to see more)</span>`
      : '';
    let html = filterBar;
    html += `<div style="font-weight:600;margin-bottom:8px;">${typeLabels[type] || type} Audit Log (${docs.length} entries)${cappedNote}</div>`;
    html += '<div style="display:flex;flex-direction:column;gap:6px;">';
    for (const data of docs) {
      const ts = data.timestamp?.toDate?.();
      // TASK-1655: legacy clock-entry rows whose write-time was lost are labeled,
      // not shown as '?' — they sort last (toDate() undefined → 0 in the sort).
      const timeStr = ts ? ts.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : (data._undated ? 'Undated (legacy)' : '?');
      const color = actionColors[data.action] || '#6b7280';
      const icon = actionIcons[data.action] || 'info';

      // Defense-in-depth: escape all tenant-supplied fields before innerHTML.
      // Today not exploitable (only authenticated members can write to auditLog
      // and call sites pass hardcoded action strings) — but this closes the
      // latent vulnerability and protects future paths that might log
      // user-supplied content (e.g., custom note fields, applicant telemetry).
      let detail = '';
      if (data._source === 'clock') {
        detail = `<strong>${escapeHtml(data.personName || '?')}</strong>`;
        if (data.jobTitle) detail += ` — ${escapeHtml(data.jobTitle)}`;
        if (data.source) detail += ` <span style="color:var(--text-secondary);font-size:0.75rem;">(${escapeHtml(data.source)})</span>`;
        if (data.totalHours) detail += ` | ${escapeHtml(String(data.totalHours))}h`;
        if (data.rate != null) detail += ` | $${escapeHtml(String(data.rate))}/hr`;
        if (data.action === 'DELETE' && data.entrySnapshot) {
          const es = data.entrySnapshot;
          detail += ` | was: ${escapeHtml(es.status || '?')}`;
          if (es.clockIn?.toDate) detail += ` | in: ${es.clockIn.toDate().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
        }
      } else if (data._source === 'job') {
        detail = `<strong>${escapeHtml(data.jobTitle || '?')}</strong>`;
        if (data.customerName) detail += ` — ${escapeHtml(data.customerName)}`;
        if (data.dateChanged) {
          detail += ` | <span style="color:#dc2626;">${escapeHtml(String(data.oldStartDate))}→${escapeHtml(String(data.newStartDate))}</span>`;
          if (data.oldEndDate !== data.newEndDate) detail += `, ${escapeHtml(String(data.oldEndDate))}→${escapeHtml(String(data.newEndDate))}`;
        }
        if (data.startDate && !data.dateChanged) detail += ` | ${escapeHtml(String(data.startDate))} to ${escapeHtml(String(data.endDate))}`;
        // TASK-9471 — oldPoNumber/newPoNumber are written on every job save
        // (TASK-4782) and by the Claude connector, and were shown nowhere.
        // Tenant/customer free text: escape both values.
        if (data.poChanged) {
          const _po = (v) => (v == null || String(v) === '' ? '(blank)' : escapeHtml(String(v)));
          detail += ` | PO: <span style="color:#dc2626;">${_po(data.oldPoNumber)}→${_po(data.newPoNumber)}</span>`;
          if (data.poWhitespaceOnly) detail += ' <span style="color:var(--text-secondary);font-size:0.75rem;">(spacing cleanup)</span>';
        }
        if (data.source === 'mcp') detail += ' <span style="color:var(--text-secondary);font-size:0.75rem;">(via Claude)</span>';
        // TASK-9958 — the Claude upload link's server row names the file it attached.
        if (data.permitAttached && data.permitAttached.name) detail += ` | file added: ${escapeHtml(String(data.permitAttached.name).slice(0, 120))}`;
      } else if (data._source === 'clockentry') {
        // TASK-916 Slice 3 — punch-edit: show employee + before→after on the
        // tracked fields (clockIn/out rendered as times). fmtT returns a
        // code-generated date string (safe) or an escaped primitive.
        detail = `<strong>${escapeHtml(data.personName || '?')}</strong>`;
        const cf = data.changedFields || {};
        const fmtT = (v) => {
          if (v == null) return '—';
          const d = v?.toDate?.() || (v?._seconds ? new Date(v._seconds * 1000) : (typeof v === 'number' ? new Date(v) : null));
          if (d && !isNaN(d.getTime())) return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
          return escapeHtml(String(v));
        };
        const bits = [];
        if (cf.clockIn) bits.push(`in: ${fmtT(cf.clockIn.from)} → ${fmtT(cf.clockIn.to)}`);
        if (cf.clockOut) bits.push(`out: ${fmtT(cf.clockOut.from)} → ${fmtT(cf.clockOut.to)}`);
        ['jobTitle', 'customerName', 'status', 'rate', 'notes'].forEach(f => {
          if (cf[f]) bits.push(`${escapeHtml(f)}: ${escapeHtml(String(cf[f].from ?? '—'))} → ${escapeHtml(String(cf[f].to ?? '—'))}`);
        });
        if (bits.length) detail += ` | ${bits.join(' · ')}`;
        if (data.source) detail += ` <span style="color:var(--text-secondary);font-size:0.72rem;">(${escapeHtml(data.source)})</span>`;
      } else if (data.category === 'telemetry' && data.context && typeof data.context === 'object') {
        // TASK-9251 — RENDER THE CONTEXT. `Telemetry.logUserAction` nests every
        // field under `context`, but the universal branch below only reads
        // TOP-LEVEL keys (data.ticketNo, data.amount…). So every telemetry row
        // ever written — including the six SMS events since the Rhyder
        // investigation — has rendered here as a bare action name with its
        // payload invisible. A listener whose evidence you cannot read in the
        // one UI built to read it is not earning its row.
        //
        // Flattened generically rather than per-event: this pane must not need
        // an edit every time someone wires a new listener. Values are
        // code-generated ids/counts, but they are escaped anyway — `context` is
        // free-form by design and a future call site may pass a tenant string.
        const bits = [];
        for (const [k, v] of Object.entries(data.context)) {
          if (v === null || v === undefined || v === '') continue;
          if (bits.length >= 10) { bits.push('…'); break; }
          const flat = Array.isArray(v)
            ? v.slice(0, 5).map(x => String(x)).join(',')
            : (typeof v === 'object' ? '{…}' : String(v));
          if (!flat) continue;
          bits.push(`${escapeHtml(k)}: <strong>${escapeHtml(flat.slice(0, 60))}</strong>`);
        }
        detail = `<span style="color:var(--text-secondary);font-size:0.7rem;text-transform:uppercase;">telemetry</span> ${bits.join(' · ') || escapeHtml(data.action || '')}`;
        if (data.url) detail += ` <span style="color:var(--text-secondary);font-size:0.7rem;">(${escapeHtml(String(data.url).slice(0, 40))})</span>`;
      } else {
        // Universal audit log entries
        const parts = [];
        if (data.personName) parts.push(`<strong>${escapeHtml(data.personName)}</strong>`);
        if (data.customerName) parts.push(`<strong>${escapeHtml(data.customerName)}</strong>`);
        if (data.jobTitle) parts.push(escapeHtml(data.jobTitle));
        if (data.ticketNo) parts.push(`Ticket #${escapeHtml(String(data.ticketNo))}`);
        if (data.ticketId && !data.ticketNo) parts.push(`Ticket ${escapeHtml(data.ticketId.slice(0,8))}…`);
        if (data.oppName) parts.push(escapeHtml(data.oppName));
        if (data.oldStage && data.newStage) parts.push(`${escapeHtml(data.oldStage)}→${escapeHtml(data.newStage)}`);
        if (data.amount != null) parts.push(`$${escapeHtml(String(data.amount))}`);
        if (data.oldDate) parts.push(`${escapeHtml(String(data.oldDate))}→${escapeHtml(String(data.newDate))}`);
        // TASK-9715 — truck / trailer rows name the unit (Roster saves and the Claude connector write unit + name).
        if (/^(CREATE|UPDATE)_(TRUCK|TRAILER)$/.test(data.action || '') && data.unit) {
          parts.push(`${data.action.endsWith('TRAILER') ? 'Trailer' : 'Truck'} ${escapeHtml(String(data.unit))}${data.name ? ` ${escapeHtml(String(data.name))}` : ''}`);
          if (Array.isArray(data.changedFields) && data.changedFields.length) parts.push(escapeHtml(data.changedFields.slice(0, 8).map(String).join(', ')));
          if (data.source === 'mcp') parts.push('<span style="color:var(--text-secondary);font-size:0.75rem;">(via Claude)</span>');
        }
        // TASK-9752 — server-authored fuel-card PIN view rows: say what happened and where.
        if (data.action === 'FUEL_CARD_PIN_VIEWED' || data.action === 'FUEL_CARD_PIN_VIEW_RATE_LIMITED') {
          parts.push(data.action === 'FUEL_CARD_PIN_VIEWED'
            ? (data.hadPin === false ? 'fuel-card PIN viewed (none on file)' : 'fuel-card PIN viewed')
            : 'fuel-card PIN views blocked — too many');
          if (data.screenLabel) parts.push(escapeHtml(String(data.screenLabel).slice(0, 60)));
        }
        // TASK-9884 — maintenance work-order status rows (TASK-9766): unit, the
        // task, and old → new status. Task text is tenant free text: escape it.
        if (data.action === 'MAINT_TASK_STATUS') {
          const _unit = data.truckUnit || data.trailerUnit || data.assetName;
          if (_unit) parts.push(`<strong>${escapeHtml(String(_unit))}</strong>`);
          if (data.description) parts.push(escapeHtml(String(data.description).slice(0, 80)));
          const _st = (v) => (v == null || v === '' ? '(none)' : escapeHtml(String(v)));
          parts.push(`${_st(data.fromStatus)}→${_st(data.toStatus)}`);
          if (data.change === 'dvir_deleted') parts.push('<span style="color:var(--text-secondary);font-size:0.75rem;">(DVIR deleted)</span>');
          if (data.signOffName) parts.push(`signed: ${escapeHtml(String(data.signOffName))}`);
        }
        // TASK-9958 — "MONTANA TRIP PERMIT.pdf → Load Permits · via Job form".
        // File names and paths are tenant/user text: escape all of it.
        if (data.action === 'FILE_ADDED') {
          const _tgt = { job_permits: 'Load Permits', job_photos: 'Job photos', ticket_documents: 'Ticket documents', ticket_photos: 'Ticket photos', ticket_line_photos: 'Ticket line photos' };
          const _via = { job_modal: 'Job form', ticket_edit: 'Ticket edit', driver_app: 'Driver app', clock_out: 'Clock-out', claude_link: 'Claude upload link', email_import: 'Email import' };
          parts.push(`<strong>${escapeHtml(String(data.fileName || '(unnamed file)').slice(0, 120))}</strong> → ${escapeHtml(_tgt[data.target] || 'file')}`);
          if (data.via) parts.push(`via ${escapeHtml(_via[data.via] || String(data.via))}`);
          if (data.jobId && !data.jobTitle) parts.push(`Job ${escapeHtml(String(data.jobId).slice(0, 8))}…`);
        }
        detail = parts.join(' — ') || escapeHtml(data.action || '');
        if (data.category) detail = `<span style="color:var(--text-secondary);font-size:0.7rem;text-transform:uppercase;">${escapeHtml(data.category)}</span> ${detail}`;
      }

      // TASK-916 Slice 4 — actor column. Prefer actorName, then a non-'unknown'
      // user, then email; for driver-PIN edits (blank actorEmail + actorName
      // present) tag "(driver)". XSS Sentinel (COD Adversary 🔴): actorName /
      // user originate from free-text roster names — escapeHtml ALL of them.
      const _aEmail = data.actorEmail
        || (typeof data.user === 'string' && data.user.includes('@') ? data.user : '')
        || '';
      const _aName = data.actorName || '';
      let actorCol;
      if (!_aEmail && _aName) {
        actorCol = `${escapeHtml(_aName)} <span style="opacity:0.7;">(driver)</span>`;
      } else {
        const fallback = _aName
          || (typeof data.user === 'string' && data.user && data.user !== 'unknown' ? data.user : '')
          || _aEmail || '?';
        actorCol = escapeHtml(fallback);
      }

      // TASK-924 — 2-row card so the detail text wraps on mobile instead of
      // the old single flex row squeezing five items into a phone width.
      html += `<div style="padding:8px 12px;border-radius:6px;background:#fff;border:1px solid #e2e8f0;font-size:0.85rem;">
        <div style="display:flex;align-items:flex-start;gap:6px;">
          <span class="material-icons" style="color:${color};font-size:16px;flex-shrink:0;margin-top:1px;">${icon}</span>
          <span style="font-weight:600;color:${color};font-size:0.7rem;white-space:nowrap;flex-shrink:0;margin-top:2px;">${escapeHtml(data.action || '')}</span>
          <span style="flex:1;min-width:0;word-break:break-word;">${detail}</span>
        </div>
        <div style="display:flex;justify-content:space-between;gap:8px;margin-top:3px;padding-left:22px;">
          <span style="color:var(--text-secondary);font-size:0.75rem;white-space:nowrap;">${escapeHtml(timeStr)}</span>
          <span style="color:var(--text-secondary);font-size:0.7rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:160px;text-align:right;">${actorCol}</span>
        </div>
      </div>`;
    }
    html += '</div>';
    el.innerHTML = html;
  }
}

// TASK-916 — All Activity filter handlers (exposed on window.ClockModule).
function setAuditDateFilter(val) {
  ClockState.auditFilterDate = val;
  if (val === 'custom') {
    // Just reveal the custom inputs — don't query until Apply.
    _renderAuditLog();
    return;
  }
  loadAuditLog();
}

function applyAuditCustomRange() {
  const s = document.getElementById('audit-custom-start')?.value || null;
  const e = document.getElementById('audit-custom-end')?.value || null;
  if (!s) { window.showToast?.('Pick a start date.', 'warning'); return; }
  ClockState.auditFilterCustomStart = s;
  ClockState.auditFilterCustomEnd = e;
  loadAuditLog();
}

function setAuditActorFilter(val) {
  ClockState.auditFilterActor = val || 'all';
  _renderAuditLog(); // client-side only — no re-query
}

function getWorkforceAdjustmentsHTML() {
  const periods = [
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'lastmonth', label: 'Last Month' },
    { key: 'all', label: 'All' }
  ];

  const periodBtns = periods.map(p =>
    `<button class="filter-btn ${ClockState.adjustmentsPeriod === p.key ? 'active' : ''}" onclick="window.ClockModule.switchAdjustmentsPeriod('${p.key}')">${p.label}</button>`
  ).join('');

  // Filter adjustments by period
  const filtered = getFilteredAdjustments();

  // TASK-4628 — which of these per-diem rows is the HR per-diem rate silently
  // superseding? (TASK-939 precedence: rate wins, manual is fallback-only.)
  // Decided ONCE here and reused by the row badge and the Net Total, so the
  // list and the total can never tell different stories.
  const _pdSuperseded = new Map(); // adj.id -> { rate, manual }
  let _pdSupersededTotal = 0;
  filtered.forEach(adj => {
    const ty = String(adj.type || '').toLowerCase();
    if (ty !== 'per-diem') return;
    const hit = perDiemManualSuperseded(getPersonForReport(adj.personId), adj.amount);
    if (!hit) return;
    _pdSuperseded.set(adj.id, hit);
    _pdSupersededTotal += hit.manual;
  });

  // Net total. Superseded per-diem rows are EXCLUDED — they are not money
  // anyone receives, and counting them made the total overstate payroll by
  // exactly the amount that was being dropped. The exclusion is stated below
  // the total; no paycheck changes, only this display stops lying.
  let netTotal = 0;
  filtered.forEach(adj => {
    if (_pdSuperseded.has(adj.id)) return;
    if (adj.type === 'deduction') netTotal -= (adj.amount || 0);
    else netTotal += (adj.amount || 0);
  });

  // TASK-4630 — firestore.rules payAdjustments delete is OWNER-only (payroll
  // integrity), but this Delete button rendered for every workforce viewer. A
  // non-owner admin could click it all day and only ever get "Error deleting
  // adjustment" — never silent, but it never says WHY, so it reads as a broken
  // product rather than a deliberate boundary. Same defect + same fix as
  // hr.js `_renderRecurringDeductionRows` (TASK-1591); see `_clockIsOwner()`.
  // Add is deliberately NOT gated here — create is admin/billing per the rule.
  const canDelete = _clockIsOwner();
  const escA = window.escAttr || escapeHtml;

  // TASK-4637 — the panel must always account for itself. Before this task the
  // listener was founder-gated, so on 85 of 86 tenants this rendered "No
  // adjustments for this period" forever — a sentence that was not true and
  // that pointed at the period pills, which were not the reason. Four distinct
  // states now, and only one of them is the old copy.
  const _emptyStateHTML = (() => {
    if (ClockState.adjustmentsDenied) {
      return '<div class="empty-state-small">Pay adjustments are payroll data. Ask your account owner to add the Workforce module to your account to see them.</div>';
    }
    if (!ClockState.adjustmentsLoaded) {
      return '<div class="empty-state-small">Loading pay adjustments&hellip;</div>';
    }
    if (ClockState.adjustments.length === 0) {
      return '<div class="empty-state-small">No pay adjustments yet. Use <strong>Add Adjustment</strong> above to record a per-diem, bonus, reimbursement or deduction.</div>';
    }
    // Rows exist, just not in the selected period — name the period, and say
    // where the rest are, so nobody reads this as "the feature is broken".
    const other = ClockState.adjustments.length;
    return `<div class="empty-state-small">No adjustments in this period. ${other} ${other === 1 ? 'adjustment is' : 'adjustments are'} loaded &mdash; switch to <strong>All</strong> to see ${other === 1 ? 'it' : 'them'}.</div>`;
  })();

  const listHTML = filtered.length === 0
    ? _emptyStateHTML
    : filtered.map(adj => {
        const adjDate = adj.createdAt?.toDate?.() || (adj.date?.toDate?.() || new Date(adj.createdAt || adj.date));
        const dateStr = adjDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        // TASK-4628 — this row is configured but NOT paying. Say it on the row.
        const sup = _pdSuperseded.get(adj.id) || null;
        const supBadgeHTML = sup ? `
                <span style="display:inline-flex;align-items:center;gap:3px;font-size:0.7rem;color:#B45309;background:rgba(217,119,6,0.12);border:1px solid rgba(217,119,6,0.45);padding:1px 6px;border-radius:4px;font-weight:700;">
                  <span class="material-icons" style="font-size:12px;">report_problem</span>NOT PAID
                </span>` : '';
        const supNoteHTML = sup ? `
              <div style="font-size:0.76rem;color:#B45309;margin-top:3px;line-height:1.35;">
                This row pays nothing. ${escapeHtml(adj.personName || 'This employee')} has a per-diem rate of $${sup.rate.toFixed(2)}/day in HR &rarr; Edit Employment Info, and the rate takes precedence &mdash; per diem is paid as $${sup.rate.toFixed(2)} &times; each day of 8h or more. Delete this row, or clear the HR rate if this amount is the one you want paid.
              </div>` : '';
        return `
          <div style="display:flex;justify-content:space-between;align-items:center;padding:var(--spacing-sm) 0;border-bottom:1px solid var(--border-light);${sup ? 'background:rgba(217,119,6,0.05);' : ''}">
            <div style="flex:1;">
              <div style="display:flex;align-items:center;gap:8px;">
                <strong style="color:var(--text-primary);">${escapeHtml(adj.personName)}</strong>
                <span class="badge badge-${adj.type === 'bonus' ? 'approved' : adj.type === 'deduction' ? 'denied' : 'pending'}">${escapeHtml(adj.category || capitalizeFirst(adj.type))}</span>
                ${adj.recurring ? '<span style="display:inline-flex;align-items:center;gap:2px;font-size:0.7rem;color:var(--brand-blue);background:rgba(59,130,246,0.08);padding:1px 5px;border-radius:4px;font-weight:600;"><span class="material-icons" style="font-size:12px;">repeat</span>Recurring</span>' : ''}
                ${supBadgeHTML}
                <span style="font-size:0.8rem;color:var(--text-secondary);">${dateStr}</span>
              </div>
              ${adj.customerName ? `<div style="font-size:0.78rem;color:var(--text-secondary);margin-top:2px;"><span class="material-icons" style="font-size:12px;vertical-align:middle;">business</span> ${escapeHtml(adj.customerName)}${adj.jobTitle ? ' → ' + escapeHtml(adj.jobTitle) : ''}</div>` : ''}
              ${adj.notes ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-top:2px;">${escapeHtml(adj.notes)}</div>` : ''}
              ${supNoteHTML}
            </div>
            <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
              <span style="font-weight:600;${sup ? 'color:var(--text-muted);text-decoration:line-through;' : `color:${adj.type === 'deduction' ? 'var(--status-danger)' : 'var(--status-success-emphasis)'};`}">
                ${adj.type === 'deduction' ? '-' : '+'}$${adj.amount?.toFixed(2) || '0.00'}
              </span>
              ${canDelete ? `
              <button class="btn-icon" onclick="window.ClockModule.deleteAdjustment('${escA(adj.id)}')" title="Delete" style="color:var(--text-secondary);">
                <span class="material-icons" style="font-size:18px;">delete_outline</span>
              </button>` : ''}
            </div>
          </div>
        `;
      }).join('');

  // TASK-4630 — when the Delete control is hidden, say so once for the list
  // rather than leaving a non-owner wondering where the button went. Mirrors the
  // hr.js note ("Removing a recurring deduction is owner-only.").
  const ownerNoteHTML = (!canDelete && filtered.length > 0)
    ? '<div style="font-size:0.72rem;color:var(--text-secondary);padding-top:6px;">Removing a pay adjustment is owner-only.</div>'
    : '';

  // TASK-4637 — the listener is capped at ADJUSTMENTS_LISTENER_LIMIT so it
  // cannot become an unbounded collection read on every tenant. When the cap is
  // actually reached, SAY SO: a silently truncated payroll list is the same
  // class of lie this task set out to remove. The period pills filter what is
  // already loaded, so they cannot reach past the cap — the note does not
  // pretend otherwise.
  const truncatedNoteHTML = ClockState.adjustmentsTruncated
    ? `<div style="font-size:0.72rem;color:var(--text-secondary);padding-top:6px;">Showing the ${ADJUSTMENTS_LISTENER_LIMIT} most recent adjustments. Older ones are not loaded here &mdash; payroll exports and reports still read every row.</div>`
    : '';

  // TASK-4628 — one banner above the list so the conflict is visible before
  // anyone scrolls, plus a footnote on the total explaining the exclusion.
  const supersededCount = _pdSuperseded.size;
  const supersededBannerHTML = supersededCount > 0 ? `
    <div style="display:flex;gap:10px;align-items:flex-start;background:rgba(217,119,6,0.08);border:1px solid rgba(217,119,6,0.4);border-radius:8px;padding:10px 12px;margin-bottom:var(--spacing-md);">
      <span class="material-icons" style="color:#B45309;font-size:20px;">report_problem</span>
      <div style="flex:1;min-width:0;font-size:0.82rem;color:var(--text-primary);line-height:1.4;">
        <strong>${supersededCount} per-diem ${supersededCount === 1 ? 'row is' : 'rows are'} not being paid</strong> &mdash; $${_pdSupersededTotal.toFixed(2)} total.
        These people have a per-diem <em>rate</em> set in HR &rarr; Edit Employment Info, and the rate takes precedence: per diem is paid as rate &times; each day of 8h or more, and the manual rows below are ignored. They are struck through and excluded from the Net Total.
        Delete the manual row, or clear the HR rate if the manual amount is the one that should pay.
      </div>
    </div>` : '';

  const netColor = netTotal >= 0 ? 'var(--status-success-emphasis)' : 'var(--status-danger)';
  const netTotalHTML = filtered.length > 0 ? `
    <div style="display:flex;justify-content:flex-end;padding:var(--spacing-sm) 0;margin-top:var(--spacing-sm);border-top:2px solid var(--border-medium);">
      <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
        <span style="font-weight:600;color:var(--text-primary);">Net Total:</span>
        <span style="font-weight:700;font-size:1.1rem;color:${netColor};">${netTotal >= 0 ? '+' : '-'}$${Math.abs(netTotal).toFixed(2)}</span>
      </div>
    </div>
    ${supersededCount > 0 ? `<div style="display:flex;justify-content:flex-end;font-size:0.72rem;color:var(--text-secondary);">Excludes $${_pdSupersededTotal.toFixed(2)} of per-diem rows superseded by an HR per-diem rate.</div>` : ''}
  ` : '';

  // TASK-4637 — Add is admin/billing per firestore.rules (no workforce arm).
  // See _clockCanCreatePayAdjustment(). A workforce-only member used to get a
  // button that always failed with a generic error.
  const addBtnHTML = _clockCanCreatePayAdjustment()
    ? `<button onclick="window.ClockModule.showAddAdjustment()" style="width:100%;padding:12px;font-size:0.9rem;font-weight:600;display:flex;align-items:center;justify-content:center;gap:6px;border-radius:var(--radius-md);border:2px dashed var(--brand-blue);color:var(--brand-blue);background:rgba(59,130,246,0.04);cursor:pointer;transition:all 0.15s;" onmouseenter="this.style.background='rgba(59,130,246,0.08)'" onmouseleave="this.style.background='rgba(59,130,246,0.04)'">
          <span class="material-icons" style="font-size:20px;">add_circle_outline</span> Add Adjustment
        </button>`
    // When the READ was also refused there is nothing "below" to review, so the
    // empty state carries the whole explanation and this note would contradict
    // it. Say one true thing, not two half-true ones.
    : (ClockState.adjustmentsDenied
        ? ''
        : `<div style="font-size:0.72rem;color:var(--text-secondary);text-align:center;padding:8px 0;">Adding a pay adjustment needs the Billing module (not view only) — ask the owner. You can review the ones below.</div>`);

  return `
    <div>
      <div style="margin-bottom:var(--spacing-md);">
        ${addBtnHTML}
      </div>
      <div class="workforce-pills">
        ${periodBtns}
      </div>
      ${supersededBannerHTML}
      <div id="adjustments-list">
        ${listHTML}
        ${ownerNoteHTML}
        ${truncatedNoteHTML}
      </div>
      ${netTotalHTML}
    </div>
  `;
}

function getFilteredAdjustments() {
  const all = ClockState.adjustments;
  if (ClockState.adjustmentsPeriod === 'all') return all;

  const now = new Date();
  let start;
  if (ClockState.adjustmentsPeriod === 'week') {
    start = getWeekMonday(now);
  } else if (ClockState.adjustmentsPeriod === 'month') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (ClockState.adjustmentsPeriod === 'lastmonth') {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  }
  const end = ClockState.adjustmentsPeriod === 'lastmonth'
    ? new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999)
    : now;

  return all.filter(adj => {
    const d = adj.createdAt?.toDate?.() || (adj.date?.toDate?.() || new Date(adj.createdAt || adj.date || 0));
    return d >= start && d <= end;
  });
}

function switchAdjustmentsPeriod(period) {
  ClockState.adjustmentsPeriod = period;
  const content = document.getElementById('workforce-content');
  if (content) content.innerHTML = getWorkforceAdjustmentsHTML();
}

async function deleteAdjustment(id) {
  // TASK-360 — workforce admin surface.
  if (!_assertWorkforceAccess('deleteAdjustment')) return;
  // TASK-4630 — firestore.rules payAdjustments delete is OWNER-only. The button
  // is now hidden for non-owners, but this handler is exported on
  // window.ClockModule and reachable from a console, so name the boundary here
  // too instead of letting the rule denial land as a generic failure.
  if (!_clockIsOwner()) {
    console.warn('[Clock] deleteAdjustment blocked — payAdjustments delete is owner-only (firestore.rules).');
    window.showToast('Only the account owner can remove a pay adjustment.', 'error');
    return;
  }
  if (!confirm('Delete this adjustment?')) return;
  try {
    await deleteDoc(doc(db, 'tenants', ClockState.tenantId, 'payAdjustments', id));
    // Also clean up BI cost entry if it exists
    try { await deleteDoc(doc(db, 'tenants', ClockState.tenantId, 'costEntries', `payadj_${id}`)); } catch (_) {}
    window.showToast('Adjustment deleted', 'success');
    switchWorkforceTab('adjustments');
  } catch (err) {
    console.error('[Clock] Error deleting adjustment:', err);
    // TASK-4630 — a payroll denial that reads as a generic error sends someone
    // hunting for a bug that isn't there. The legacy single-login branch of
    // _clockIsOwner() is optimistic, so this arm is still reachable.
    const denied = /permission|insufficient/i.test(String(err?.code || err?.message || ''));
    window.showToast(denied
      ? 'Only the account owner can remove a pay adjustment.'
      : 'Error deleting adjustment', 'error');
  }
}

// ============================================
// TAB SWITCHING
// ============================================
function refreshWorkforceIfActive() {
  if (ClockState.activeTab !== 'workforce') return;
  if (isClockModalOpen()) return;
  // TASK-1169 — the Payroll (Prep) sub-tab manages its own render via the
  // payroll.js mount (switchWorkforceTab). Re-rendering workforce-content on
  // every people/clock snapshot would wipe the mounted view back to an empty
  // <div id="workforce-payroll-mount"> mid-review (live tenants fire these
  // snapshots constantly). Prep data is point-in-time by design — the screen
  // has its own Refresh.
  if (ClockState.workforceTab === 'payroll') return;
  // TASK-1442 — same reason: Timecard Exceptions is a POINT-IN-TIME scan with its
  // own Run button. Re-rendering on every people/clock snapshot (live tenants fire
  // these constantly) would wipe a scan out from under whoever is working the list.
  if (ClockState.workforceTab === 'exceptions') return;
  const content = document.getElementById('workforce-content');
  if (content) {
    content.innerHTML = getWorkforceContentHTML();
  }
}

// TASK-4637 — repaint ONLY the Adjustments sub-tab when its listener resolves.
// Deliberately narrower than refreshWorkforceIfActive(): the payAdjustments
// snapshot must not repaint Payroll Prep or the Exceptions scan (both
// point-in-time — see the guards above), and it must not yank the DOM out from
// under an open Add Adjustment modal.
function _refreshAdjustmentsTabIfActive() {
  if (ClockState.activeTab !== 'workforce') return;
  if (ClockState.workforceTab !== 'adjustments') return;
  if (isClockModalOpen()) return;
  // showAddAdjustment() injects an INLINE form (#adj-form-inline) into
  // #workforce-content rather than a modal, so isClockModalOpen() does not see
  // it. Repainting here would erase a half-typed pay adjustment.
  if (document.getElementById('adj-form-inline')) return;
  const content = document.getElementById('workforce-content');
  if (content) content.innerHTML = getWorkforceAdjustmentsHTML();
}

function switchTab(tab) {
  // TASK-360 — defense in depth: if a user with only `clock` (no workforce)
  // somehow lands on the workforce tab (deep-link, URL hash race, stale
  // window.ClockModule.switchTab() call from app.js navigateToView for
  // #/workforce) — bounce them back to the clock tab. The sidebar nav
  // entry is hidden by checkModuleAccess, but this is the belt to the
  // checkModuleAccess suspenders.
  if (tab === 'workforce' && !hasWorkforceAccess()) {
    console.warn('[Clock] workforce tab blocked — viewer lacks workforce module');
    tab = 'clock';
  }
  ClockState.activeTab = tab;

  // Header reflects the active tab: Workforce admin view vs self-service clock.
  const viewTitle = document.getElementById('clock-view-title');
  if (viewTitle) viewTitle.textContent = tab === 'workforce' ? 'Workforce' : 'Time Clock';

  document.querySelectorAll('.clock-tab').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.tab === tab);
  });

  const content = document.getElementById('clock-tab-content');
  if (tab === 'clock') {
    content.innerHTML = getClockTabHTML();
    setupEventHandlers();
    populatePeopleDropdown();
    populateCustomerDropdown();
    renderActiveList();
    renderRecentList();
    startClockUpdate();
    // Self-clock surface: auto-select the linked person so the clock-in
    // fields appear without requiring a dropdown interaction.
    if (!hasWorkforceAccess()) {
      const selfId = getSelfPersonId();
      if (selfId) {
        // selectPerson() triggers onPersonSelect → status card, customer
        // step, etc. Same flow the dropdown would trigger.
        try { selectPerson(selfId); } catch (e) { console.warn('[Clock] self-select failed:', e); }
      }
    }
  } else if (tab === 'timeoff') {
    content.innerHTML = getTimeOffTabHTML();
    updateTimeOffPersonDropdown();
    renderTimeOffRequestsList();
  } else if (tab === 'workforce') {
    content.innerHTML = getWorkforceTabHTML();
  }
}

function switchWorkforceTab(tab) {
  // TASK-1442 — Adjustments + Exceptions both administer OTHER people's clock
  // data. switchTab() already bounces non-workforce viewers off the parent tab;
  // this stops a direct window.ClockModule.switchWorkforceTab() call from
  // reaching them anyway.
  if ((tab === 'adjustments' || tab === 'exceptions' || tab === 'health' || tab === 'claims') && !hasWorkforceAccess()) {
    console.warn('[Clock] workforce sub-tab blocked — viewer lacks workforce module');
    tab = 'hours';
  }
  ClockState.workforceTab = tab;
  const content = document.getElementById('workforce-content');
  if (content) {
    // Re-render workforce toggle buttons
    document.querySelectorAll('#workforce-view-toggle .filter-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tab === tab);
    });
    content.innerHTML = getWorkforceContentHTML();
    // Auto-load reports data when switching to reports tab for the first time
    if (tab === 'reports' && ClockState.reportsData.length === 0 && ClockState.reportsPeriod !== 'custom') {
      loadReportsData();
    }
    // Load adjustment flags for the weekly hours table
    if (tab === 'reports') loadAdjustmentFlags();
    // TASK-1066 — Payroll: mount the payroll.js view into the freshly-rendered
    // container. initPayroll is async (fetches Gusto provision/onboarding state)
    // and renders into the passed element itself. Guard on the admin check +
    // the mount existing (a non-admin gets the access message div instead).
    if (tab === 'payroll' && window.PayrollModule?.isPayrollAdmin?.()) {
      const mount = document.getElementById('workforce-payroll-mount');
      if (mount) window.PayrollModule.initPayroll(mount, ClockState.tenantId);
    }
    // TASK-1442 — run the exceptions scan on first entry so the tab is useful
    // without a second click. Re-entering the tab keeps the previous result
    // (point-in-time by design); "Run check" re-scans.
    if (tab === 'exceptions' && !ClockState.exceptionsRan && !ClockState.exceptionsLoading) {
      runTimecardExceptions();
    }
  }
}

// ============================================
// DATA SUBSCRIPTION
// ============================================
function subscribeToData(tenantId) {
  // TASK-1481 — comp accessor: mirror the people subscription with the
  // peopleComp one. A clock-only member (or driver-PIN session) being DENIED
  // here is expected once the rules land — the accessor records the state and
  // money surfaces refuse visibly instead of rendering $0.
  window.PersonComp?.subscribe?.(tenantId, { key: 'clock' });

  // People
  const peopleQuery = query(
    collection(db, 'tenants', tenantId, 'people'),
    where('active', '==', true),
    orderBy('name')
  );
  const unsubPeople = onSnapshot(peopleQuery, (snapshot) => {
    ClockState.people = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    window.ClockMode?.notePeople?.(ClockState.people); // TASK-9553 per-person clock mode
    populatePeopleDropdown();
    updateTimeOffPersonDropdown();
    refreshWorkforceIfActive();
  });
  ClockState.unsubscribers.push(unsubPeople);

  // TASK-452 — personId-scope the two tenant-wide clockEntries reads below
  // for NON-workforce viewers. A clock-only member can open this kiosk view to
  // clock themselves in/out (TASK-360), but under the tightened clockEntries
  // READ rule they may read ONLY their own entries — so an un-scoped
  // `where status==active` / `where clockIn>=7d` query is permission-denied for
  // them. When the viewer lacks workforce access we add `where personId==self`
  // (which they already client-filter to anyway — the "Currently Clocked In"
  // panel + tenant-wide Recent feed are workforce-only surfaces). Workforce/
  // admin/owner keep the full tenant-wide read for the kiosk + weekly reports.
  const _isWorkforce = hasWorkforceAccess();
  const _scopeSelfPid = _isWorkforce ? null : getSelfPersonId();
  const _scopeReads = !_isWorkforce && !!_scopeSelfPid;
  // Non-workforce viewer with NO linked personId can't read any clockEntries
  // under the tightened rule (no own-entry match, no full-read grant). Skip the
  // tenant-wide subs entirely rather than fire a permission-denied query.
  const _skipClockReads = !_isWorkforce && !_scopeSelfPid;

  // Active clock entries
  if (_skipClockReads) {
    ClockState.activeEntries = [];
    ClockState._entriesLoaded = true;
  } else {
    const activeQuery = _scopeReads
      ? query(
          collection(db, 'tenants', tenantId, 'clockEntries'),
          where('personId', '==', _scopeSelfPid),
          where('status', '==', 'active')
        )
      : query(
          collection(db, 'tenants', tenantId, 'clockEntries'),
          where('status', '==', 'active')
        );
    const unsubActive = onSnapshot(activeQuery, (snapshot) => {
      ClockState.activeEntries = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      ClockState._entriesLoaded = true;
      ClockState._entriesFailed = false;
      renderActiveList();
      updateSelectedPersonStatus();
      refreshWorkforceIfActive();
      // TASK-9220 — keep the sidebar PERSONAL clock tile in step with a punch
      // made here (it filters to the signed-in person itself).
      window._refreshPersonalClockLabel?.(ClockState.activeEntries);
    }, (err) => {
      console.error('[Clock] Active entries subscription error:', err);
      ClockState.activeEntries = [];
      ClockState._entriesLoaded = true;
      ClockState._entriesFailed = true; // TASK-9220 — "unknown", never "not clocked in"
    });
    ClockState.unsubscribers.push(unsubActive);
  }

  // Recent entries (last 48 hours for workforce weekly calc)
  if (_skipClockReads) {
    ClockState.recentEntries = [];
  } else {
    const twoDaysAgo = new Date();
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 7); // 7 days for weekly hours
    const recentQuery = _scopeReads
      ? query(
          collection(db, 'tenants', tenantId, 'clockEntries'),
          where('personId', '==', _scopeSelfPid),
          where('clockIn', '>=', Timestamp.fromDate(twoDaysAgo)),
          orderBy('clockIn', 'desc')
        )
      : query(
          collection(db, 'tenants', tenantId, 'clockEntries'),
          where('clockIn', '>=', Timestamp.fromDate(twoDaysAgo)),
          orderBy('clockIn', 'desc')
        );
    const unsubRecent = onSnapshot(recentQuery, (snapshot) => {
      ClockState.recentEntries = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(e => e.status === 'completed');
      renderRecentList();
      refreshWorkforceIfActive();
    }, (err) => {
      console.error('[Clock] Recent entries subscription error:', err);
      ClockState.recentEntries = [];
    });
    ClockState.unsubscribers.push(unsubRecent);
  }

  // Customers — TASK-9694 S6a: the crew-safe customerDirectory (name, company,
  // type, status, active, internalTimeOnly, requiredFields), never the full
  // `customers` docs. Every field the clock reads off a customer is in it.
  const unsubCustomers = _clockCustomerSource().subscribe(tenantId, (list) => {
    ClockState.customers = list;
    console.log('[Clock] Customers loaded:', ClockState.customers.length);
    populateCustomerDropdown();
  }, {
    label: 'Clock customers',
    orderByName: true,
    onError: (err) => console.error('[Clock] Customer subscription error:', err),
  });
  ClockState.unsubscribers.push(unsubCustomers);

  // All active jobs (not just today's)
  loadAllActiveJobs(tenantId);

  // Load today's jobDays for equipment lookup
  loadTodaysJobDays(tenantId);

  // Trucks, trailers, assets for equipment auto-fill
  loadRosterEquipment(tenantId);

  // Time-off requests. Chris 2026-07-13 — ALSO subscribe when the member can
  // open the TASK-528 "Request Time Off" card (memberHasModule('timeoff') is
  // the exact card gate), so the request modal's "My Time Off" history has
  // data. Previously gated on showTimeOffTab() alone, which is hardcoded
  // false — ClockState.timeoffRequests never populated. memberHasModule
  // requires an active members/{uid} doc; PIN-driver/public sessions (no
  // member doc) still skip it, same as before.
  // TASK-9603 — the timeOff READ rule no longer admits every member: only the
  // owner, demo, or an hr/workforce/dispatch/billing holder may list the whole
  // collection (reasons included). Anyone else — a clock+timeoff staff login —
  // may read only their OWN requests, so their listener is pinned to their
  // linked personId (index: personId ASC + startDate DESC). The unscoped query
  // would be denied outright and blank their My Time Off history.
  // If the client mirror guesses "full reader" wrong (e.g. owner metadata not
  // loaded and the role says owner), the unscoped listener is denied: fall
  // back ONCE to the self-scoped query instead of leaving the history blank.
  // Start empty every init: a skipped or denied listener must never leave an
  // earlier session's (or tenant's) list on screen (COD Code Reviewer).
  ClockState.timeoffRequests = [];
  if (showTimeOffTab() || memberHasModule('timeoff')) {
    const tenant = tenantId === 'demo-tenant' ? 'demo' : tenantId;
    const toCol = collection(db, 'tenants', tenant, 'timeOff');
    const selfQuery = () => {
      const pid = getSelfPersonId();
      return pid ? query(toCol, where('personId', '==', pid), orderBy('startDate', 'desc')) : null;
    };
    const fullRead = _clockCanReadAllTimeOff();
    const subscribeTimeOff = (requestsQuery, isFull) => {
      const unsubRequests = onSnapshot(requestsQuery, (snapshot) => {
        ClockState.timeoffRequests = snapshot.docs.map(d => ({
          id: d.id, ...d.data(),
          startDate: d.data().startDate?.toDate ? d.data().startDate.toDate() : new Date(d.data().startDate),
          endDate: d.data().endDate?.toDate ? d.data().endDate.toDate() : new Date(d.data().endDate)
        }));
        renderTimeOffRequestsList();
        // Live-refresh the My Time Off history while the request modal is open
        // (a newly submitted request shows up without re-opening — mirrors
        // timeoff.js subscribeToData).
        if (!document.getElementById('modal-timeoff-request')?.classList.contains('hidden')) {
          renderTimeOffHistory();
        }
      }, (error) => {
        ClockState.timeoffRequests = [];
        const fallback = isFull && error?.code === 'permission-denied' ? selfQuery() : null;
        if (fallback) { subscribeTimeOff(fallback, false); return; }
        console.error('[Clock] Error loading time-off requests:', error?.message);
      });
      ClockState.unsubscribers.push(unsubRequests);
    };
    const firstQuery = fullRead ? query(toCol, orderBy('startDate', 'desc')) : selfQuery();
    if (firstQuery) subscribeTimeOff(firstQuery, fullRead);
  }

  // Pay adjustments — TASK-4637. Was `if (isFounder())`, a TITS-only tenant
  // gate left over from the first clock commit; now a module gate that mirrors
  // firestore.rules, over a bounded query. See _clockCanReadPayAdjustments().
  if (_clockCanReadPayAdjustments()) {
    const adjQuery = query(
      collection(db, 'tenants', tenantId, 'payAdjustments'),
      orderBy('createdAt', 'desc'),
      limit(ADJUSTMENTS_LISTENER_LIMIT)
    );
    const unsubAdj = onSnapshot(adjQuery, (snapshot) => {
      ClockState.adjustments = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      ClockState.adjustmentsLoaded = true;
      ClockState.adjustmentsDenied = false;
      ClockState.adjustmentsTruncated = snapshot.size >= ADJUSTMENTS_LISTENER_LIMIT;
      // Live-refresh the tab if it is the one on screen. Without this the panel
      // stays on "Loading pay adjustments…" until the next tab switch, which is
      // exactly the blank-panel-with-no-explanation failure this task fixed.
      _refreshAdjustmentsTabIfActive();
    }, (error) => {
      // Two very different causes land here and they must not look the same:
      // a rules denial (the viewer genuinely may not read payroll data) vs a
      // transient/absent-collection failure. An empty array either way, but the
      // panel says WHICH.
      const denied = /permission|insufficient/i.test(String(error?.code || error?.message || ''));
      if (!denied) console.warn('[Clock] payAdjustments listener failed:', error);
      ClockState.adjustments = [];
      ClockState.adjustmentsLoaded = true;
      ClockState.adjustmentsDenied = denied;
      ClockState.adjustmentsTruncated = false;
      _refreshAdjustmentsTabIfActive();
    });
    ClockState.unsubscribers.push(unsubAdj);
  } else {
    // Not an error — this viewer cannot administer other people's pay. Mark it
    // resolved so the panel renders its access message instead of spinning.
    ClockState.adjustments = [];
    ClockState.adjustmentsLoaded = true;
    ClockState.adjustmentsDenied = true;
    ClockState.adjustmentsTruncated = false;
  }

  startClockUpdate();

  // Register fields with Smart Assistant
  if (window.SmartAssistant) {
    const SA = window.SmartAssistant;
    SA.register('clock-notes', () => [], 'Clock', { formatOnly: true });
    SA.register('clock-out-shift-notes', () => [], 'Clock', { formatOnly: true });
    SA.register('clock-out-ticket-notes', () => [], 'Clock', { formatOnly: true });
    SA.register('clock-out-notes', () => [], 'Clock', { formatOnly: true });
    SA.registerClass('.sa-asset-note', { formatOnly: true });
  }
}

async function loadAllActiveJobs(tenantId) {
  try {
    const jobsSnap = await getDocs(query(
      collection(db, 'tenants', tenantId, 'jobs'),
      where('status', '!=', 'finished')
    ));
    ClockState.allJobs = jobsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    return true;   // TASK-1417 — explicit success/failure so refreshJobCachesIfStale
  } catch (error) {  // can tell a failed read from a genuinely empty result.
    console.error('[Clock] Error loading jobs:', error);
    // Fallback: load all jobs
    try {
      const allJobsSnap = await getDocs(collection(db, 'tenants', tenantId, 'jobs'));
      ClockState.allJobs = allJobsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(j => j.status !== 'finished');
      return true;
    } catch (e) {
      console.error('[Clock] Fallback job load failed:', e);
      ClockState.allJobs = [];
      return false;
    }
  }
}

// TASK-1417 — the job caches are one-shot getDocs at module init, so the clock
// module's idea of "today" freezes at page load. Two real failures from that:
//   • A jobDay added AFTER the page loaded is invisible. Dispatch built
//     Saturday's board Friday evening; Saturday morning the Customer->Job
//     dropdown still read "No specific job" for Nelson SWD on a session that
//     had been open since Friday.
//   • An app left open overnight keeps YESTERDAY's jobDays forever, so today's
//     jobs lose their score-2 ranking and fall into the 10-item "other" tail.
// Refresh on the customer-select gesture — one small query, only when the user
// is actually about to pick a job, with a short TTL so re-selects don't hammer
// Firestore. The Job Board card path never had this bug because it reads
// jobDays live, which is why crews clocked in fine while the dropdown lied.
let _jobCacheStamp = 0;
let _jobCacheDay = '';
const JOB_CACHE_TTL_MS = 60 * 1000;

let _jobCacheInFlight = null;

async function refreshJobCachesIfStale(tenantId) {
  const tid = tenantId || ClockState.tenantId;
  if (!tid) return;
  const todayKey = _clockFormatDateISO(new Date());
  const fresh = _jobCacheDay === todayKey && (Date.now() - _jobCacheStamp) < JOB_CACHE_TTL_MS;
  if (fresh) return;
  // Coalesce concurrent callers onto one refresh (COD Edge Case: a refresh
  // slower than the TTL would otherwise stack duplicate queries).
  if (_jobCacheInFlight) return _jobCacheInFlight;

  // COD Code Reviewer ❌ — BOTH loaders swallow their own errors and assign []
  // (loadAllActiveJobs ~6656, loadTodaysJobDays ~6714). So Promise.all RESOLVES
  // on failure and an outer catch never fires: a network blip or a missing
  // index would silently EMPTY the picker, which is worse than the staleness
  // this fix exists to remove. Snapshot first, and put the old lists back if a
  // refresh came home empty when we previously had data.
  // v2 COD Code Reviewer ⚠️ — an earlier version restored whenever the new list
  // came back EMPTY, which cannot tell "the query failed" from "there genuinely
  // is no work today". On a quiet Sunday that pinned yesterday's jobDays
  // forever (auto-filling the WRONG day's equipment) and re-queried on every
  // call. The loaders now report success explicitly, so restore keys off a real
  // failure and an honest empty day is accepted as empty.
  const prevJobs = ClockState.allJobs;
  const prevDays = ClockState.todaysJobDays;
  _jobCacheInFlight = (async () => {
    try {
      const [okJobs, okDays] = await Promise.all([loadAllActiveJobs(tid), loadTodaysJobDays(tid)]);
      if (!okJobs) ClockState.allJobs = prevJobs;
      if (!okDays) ClockState.todaysJobDays = prevDays;
      if (!okJobs || !okDays) {
        console.warn('[Clock] job cache refresh failed — kept the previous lists.');
        return; // do NOT stamp: still stale, retry on the next gesture
      }
      _jobCacheStamp = Date.now();
      _jobCacheDay = todayKey;
    } catch (e) {
      ClockState.allJobs = prevJobs;
      ClockState.todaysJobDays = prevDays;
      console.warn('[Clock] job cache refresh threw, using cached lists:', e);
    } finally {
      _jobCacheInFlight = null;
    }
  })();
  return _jobCacheInFlight;
}

async function loadTodaysJobDays(tenantId) {
  // Widen query window to catch job days stored at UTC midnight (legacy migration)
  // vs local-time-based dates, then filter client-side by calendar date
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const dayAfter = new Date(today); dayAfter.setDate(dayAfter.getDate() + 2);
  const todayStr = today.toISOString().split('T')[0]; // "YYYY-MM-DD"
  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', tenantId, 'jobDays'),
      where('date', '>=', Timestamp.fromDate(yesterday)),
      where('date', '<', Timestamp.fromDate(dayAfter))
    ));
    // Filter to only job days whose date resolves to today's calendar date
    ClockState.todaysJobDays = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(jd => {
      const d = jd.date?.toDate ? jd.date.toDate() : new Date(jd.date);
      // Check both UTC date and local date to handle either storage convention
      return d.toISOString().split('T')[0] === todayStr || d.toLocaleDateString('en-CA') === todayStr;
    });
    return true;   // TASK-1417 — see loadAllActiveJobs; an empty result here is a
  } catch (error) {  // legitimate "no work today", NOT a failure to be papered over.
    console.error('[Clock] Error loading today jobDays:', error);
    ClockState.todaysJobDays = [];
    return false;
  }
}

async function loadRosterEquipment(tenantId) {
  try {
    const [trucksSnap, trailersSnap, assetsSnap] = await Promise.all([
      getDocs(query(collection(db, 'tenants', tenantId, 'trucks'), where('active', '==', true))),
      getDocs(query(collection(db, 'tenants', tenantId, 'trailers'), where('active', '==', true))),
      getDocs(query(collection(db, 'tenants', tenantId, 'assets'), where('active', '==', true)))
    ]);
    if (ClockState.tenantId !== tenantId) return; // P0 1019 COD — switched companies mid-read; never publish the old tenant's fleet
    ClockState.trucks = trucksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    ClockState.trailers = trailersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    ClockState.assets = assetsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('[Clock] Error loading equipment:', error);
  }
}

// ============================================
// CLOCK RENDER FUNCTIONS
// ============================================
function populatePeopleDropdown() {
  const select = document.getElementById('clock-person');
  if (!select) return;
  // TASK-360 — self-clock surface uses a hidden, pre-seeded <select> with
  // ONLY the viewer's own personId. Do NOT rebuild it with the full people
  // list; that would re-expose the choose-anyone surface.
  if (!hasWorkforceAccess()) {
    const selfId = getSelfPersonId();
    if (selfId) {
      const selfPerson = ClockState.people.find(p => p.id === selfId);
      const selfName = selfPerson?.name
        || window.AppState?.user?.displayName
        || window.AppState?.user?.email
        || 'You';
      select.innerHTML = `<option value="${escapeHtml(selfId)}" selected>${escapeHtml(selfName)}</option>`;
      select.value = selfId;
    } else {
      select.innerHTML = '<option value="">Choose employee...</option>';
    }
    return;
  }
  const currentValue = select.value;
  select.innerHTML = '<option value="">Choose employee...</option>';
  ClockState.people.forEach(p => {
    select.innerHTML += `<option value="${p.id}">${escapeHtml(p.name)}</option>`;
  });
  if (currentValue) select.value = currentValue;
}

// Cache of the last-rendered customer list signature. Used to short-circuit
// the innerHTML rewrite below — startClockUpdate ticks updateSelectedPersonStatus
// every second, which calls this function. On iOS, wiping options while the
// native <select> popover is open closes/reopens it and snaps the scroll back
// to the selected option, making it impossible to pick a different customer.
// Re-render only when the list actually changed. Reset to null whenever
// real semantic changes happen (subscription fires with new IDs/names).
let _customerDropdownSignature = null;
let _customerDropdownCount = -1; // TASK-10250 — options actually rendered (Inactive ones are left out)

function populateCustomerDropdown() {
  const select = document.getElementById('clock-customer');
  if (!select) return;

  // TASK-9548 — the internal flag is part of the signature: ticking "Internal
  // time only" in CRM moves the customer between optgroups, so it must re-render.
  const signature = ClockState.customers
    .map(c => `${c.id}:${c.name || ''}:${c.internalTimeOnly === true ? 1 : 0}:${window.PickerGroups.isInactiveCustomer(c) ? 1 : 0}`)
    .join('|');

  // Skip the rewrite ONLY when nothing changed AND the DOM already reflects the
  // current customer list. The signature guard protects the iOS native <select>
  // from the every-second tick collapsing its open popover, but a tab rebuild
  // (switchClockTab → content.innerHTML = getClockTabHTML) resets the select to
  // just its 1 static option while ClockState.customers is unchanged — so the
  // signature alone would early-return over a freshly-wiped select and leave it
  // empty. Compared against the option count the last render produced
  // (TASK-10250: Inactive customers are left out, so it is no longer
  // customers.length + 1). Self-heals any rebuilt DOM.
  if (signature === _customerDropdownSignature &&
      select.options.length === _customerDropdownCount) return;

  // If the user has the picker open right now, defer the rewrite until they
  // blur or commit. Otherwise even a legitimate customer add mid-interaction
  // would still snap the scroll.
  if (document.activeElement === select) {
    select.addEventListener('blur', () => {
      _customerDropdownSignature = null;
      populateCustomerDropdown();
    }, { once: true });
    return;
  }

  _customerDropdownSignature = signature;
  const currentValue = select.value;
  // TASK-1175 pinned internalTimeOnly customers (Shop/Office/yard) at the TOP so
  // yard time stays one tap; TASK-9548 labels WHY (optgroup "Internal — not
  // billed", then "Customers" A–Z) + a one-line hint under the field. Internal
  // options keep data-internal-time="1" so tooling/tests can tell no-ticket
  // customers apart. Option values are unchanged (the customer id).
  select.innerHTML = '<option value="">Choose customer...</option>' + _clockCustomerOptionsHtml(ClockState.customers, {
    optionAttrs: c => (c.internalTimeOnly === true ? ' data-internal-time="1"' : ''),
  });
  _customerDropdownCount = select.options.length;
  if (currentValue) select.value = currentValue;
  const hint = document.getElementById('clock-customer-hint');
  if (hint) hint.innerHTML = _clockCustomerHintHtml(ClockState.customers);
}

// TASK-9548 — every clock customer picker builds its options through the ONE
// shared helper (window.PickerGroups, utils/pickerGroups.js — read at CALL time,
// Pitfall #1). Replaces TASK-1175's _clockCustomersInternalFirst sort.
// TASK-10250 — every clock picker MOVES or STARTS a shift, so Inactive and
// merged-away customers are left out (a shift on one makes a ticket nobody can
// bill); the entry's own customer (selectedId / keepId) still shows. Only the
// Reports filter reads history and passes excludeInactive: false.
function _clockCustomerOptionsHtml(list, opts) {
  return window.PickerGroups.customerOptionsHtml(list, Object.assign({ nameOf: c => c.name || '', excludeInactive: true }, opts || {}));
}
function _clockCustomerHintHtml(list) {
  return window.PickerGroups.customerHintHtml(list, { nameOf: c => c.name || '' });
}

/**
 * Get relevant jobs for a customer, sorted by relevance to today.
 * All today-relevant jobs always shown. Older jobs capped at 10 most recent.
 *
 * Priority:
 *   1. Jobs with a jobDay doc for today (dispatched today) — always shown
 *   2. Jobs whose date range covers today — always shown
 *   3. Other active jobs — most recent 10 by startDate
 */
function getRelevantJobs(customerId) {
  const todaysJobIds = new Set(ClockState.todaysJobDays.map(jd => jd.jobId));
  const customerName = ClockState.customers.find(c => c.id === customerId)?.name;
  const custJobs = ClockState.allJobs.filter(j =>
    (j.customerId === customerId || j.customer === customerName) && j.status !== 'finished'
  );

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayEnd = new Date(today); todayEnd.setHours(23, 59, 59, 999);

  const todayJobs = []; // score 2: jobDay today
  const rangeJobs = []; // score 1: date range covers today
  const otherJobs = []; // score 0: other active

  for (const j of custJobs) {
    if (todaysJobIds.has(j.id)) {
      todayJobs.push(j);
    } else {
      const start = j.startDate?.toDate?.() || (j.startDate ? new Date(j.startDate) : null);
      const end = j.endDate?.toDate?.() || (j.endDate ? new Date(j.endDate) : start);
      if (start) {
        const s = new Date(start); s.setHours(0, 0, 0, 0);
        const e = new Date(end); e.setHours(23, 59, 59, 999);
        s.setDate(s.getDate() - 1); // 1-day tolerance for UTC timezone shift
        if (today <= e && todayEnd >= s) { rangeJobs.push(j); continue; }
      }
      otherJobs.push(j);
    }
  }

  // Sort "other" by most recent, cap at 10
  otherJobs.sort((a, b) => {
    const da = a.startDate?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
    const db2 = b.startDate?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
    return db2 - da;
  });

  // TASK-9865 — today's jobs in start-time order (no start → last), so the
  // 8 AM run lists before the 5 PM run. Stable: equal starts keep cache order.
  todayJobs.sort((a, b) => {
    const sa = clockWallMinutes(clockJobDayStart(a, clockTodaysDayFor(a.id, ClockState.todaysJobDays, null)));
    const sb = clockWallMinutes(clockJobDayStart(b, clockTodaysDayFor(b.id, ClockState.todaysJobDays, null)));
    return (sa == null ? 1e9 : sa) - (sb == null ? 1e9 : sb);
  });

  // TASK-9548 slice 2 — the buckets go out too, so every job picker can label
  // them ("Today" / "In progress" / "Recent") instead of silently concatenating.
  const groups = { today: todayJobs, range: rangeJobs, other: otherJobs.slice(0, 10) };
  return { jobs: [...groups.today, ...groups.range, ...groups.other], todaysJobIds, groups };
}

// TASK-9548 slice 2 — ONE builder for every clock-side job <select>. Buckets
// from getRelevantJobs / getRelevantAdminJobGroups, labeled via
// window.PickerGroups (never a ?v= import — Pitfall #1). Option VALUES are the
// job ids exactly as before; only the <optgroup> wrappers are new, so every
// `.value` reader (clockIn, confirmSwitchJob, saveClockEntryEdit, manual add,
// adjustments) is untouched.
// TASK-9865 — every option reads "8:00 AM · J-1248 · title": the day's start
// time and the job number lead, so two same-customer jobs whose titles differ
// only at the END ("…(8AM)" / "…(5PM)") stay apart after the <select> truncates.
function _clockJobOptionsHtml(groups, opts) {
  const o = opts || {};
  return window.PickerGroups.jobOptionsHtml(groups, {
    placeholders: [{ value: '', label: o.placeholderLabel === undefined ? 'No specific job' : o.placeholderLabel }],
    selectedId: o.selectedJobId || null,
    nameOf: j => clockJobPickLabel(j, clockTodaysDayFor(j.id, ClockState.todaysJobDays, null)),
  });
}

// ============================================
// TASK-9865 — WHICH JOB TO PRESELECT AT CLOCK-IN (pure helpers)
// ============================================
// ZL 10/14: two same-customer jobs the same day, titles differing only at the
// end ("…(8AM)" / "…(5PM)"). populateJobDropdown auto-picked `filtered.find(j
// => todaysJobIds.has(j.id))` — the FIRST job with a day today — and two people
// who ran the 8 AM job clocked onto the 5 PM one (one of them not on its crew
// at all). Their ticket sat on the other job; nobody saw it until payroll.
//
// Decision table (clockPickJobForPerson):
//   1. the jobs whose TODAY day carries this person (drivers OR helpers,
//      legacy dual-key: person id or name) are the "crewed" set;
//   2. one crewed job → it; several → the one whose day start is nearest NOW
//      (a job with no start sorts last; ties keep list order);
//   3. none crewed: exactly one job with a day today → it (the old single-match
//      behaviour); two or more → NOTHING preselected — the person picks. The
//      old "first job with a day today" is exactly the wrong-job write.
//   4. no person (kiosk before an employee is chosen) → rule 3 only.
// Multiple runs per day is NORMAL: being on two jobs never warns. The only
// warning (clockCrewWarning) is "the job you picked has you on NO crew today,
// and another job today does" — advisory, one tap to switch, never a block
// (plans change in the yard).
//
// jobboard.js reads these through window.ClockModule (Pitfall #1 — never a
// ?v= import), so the two clock-in surfaces + both Switch Job sheets decide
// with the SAME table. Self-contained: tests/task9865-*.unit.cjs extracts
// them by name and runs the table.
function clockPersonOnJobDay(jobDay, personId, personName) {
  if (!jobDay) return false;
  const refs = [
    ...(Array.isArray(jobDay.drivers) ? jobDay.drivers : []),
    ...(Array.isArray(jobDay.helpers) ? jobDay.helpers : []),
  ];
  const pid = personId != null && personId !== '' ? String(personId) : null;
  const pname = personName ? String(personName).trim().toLowerCase() : null;
  return refs.some(r => {
    if (r == null) return false;
    const s = String(r);
    if (pid && s === pid) return true;
    return !!pname && s.trim().toLowerCase() === pname;
  });
}

// "HH:MM" / "H:MM" / "HH:MM:SS" wall-clock → minutes since midnight, else null.
function clockWallMinutes(t) {
  if (t == null) return null;
  const m = /^\s*(\d{1,2}):(\d{2})/.exec(String(t));
  if (!m) return null;
  const h = +m[1], mi = +m[2];
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

// The day's start: per-day override → the job-level dispatcher start (the
// field the dispatch form writes) → legacy startTime. Same chain as the board
// card and billing.js. '' when nothing is set.
function clockJobDayStart(job, jobDay) {
  return (jobDay && jobDay.startTime) || (job && (job.defaultStart || job.startTime)) || '';
}

function clockFormatWallTime(t) {
  const mins = clockWallMinutes(t);
  if (mins == null) return '';
  const h = Math.floor(mins / 60), mi = mins % 60;
  return `${h % 12 || 12}:${String(mi).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

// 'J-1248' for a numbered job, '' for the legacy unnumbered ones (same rule as
// dispatch.js formatJobNumber — stored as an INT, prefix only at render).
function clockJobNumberLabel(job) {
  const n = job && job.jobNumber;
  if (Number.isFinite(n)) return `J-${n}`;
  if (typeof n === 'string' && /^J-\d+$/i.test(n.trim())) return n.trim().toUpperCase();
  return '';
}

// THE day of `jobId` among today's days: the one carrying `person` when there
// is one (a job can have two days on the same date), else the first. null when
// the job has no day today.
function clockTodaysDayFor(jobId, todaysDays, person) {
  const days = (todaysDays || []).filter(d => d && d.jobId === jobId);
  if (!days.length) return null;
  if (person) {
    const mine = days.find(d => clockPersonOnJobDay(d, person.id, person.name));
    if (mine) return mine;
  }
  return days[0];
}

// "8:00 AM · J-1248 · ZL ISO drop/swap" — only the parts that exist. Plain
// text; the caller escapes (PickerGroups does for <option>s).
function clockJobPickLabel(job, jobDay) {
  const title = (job && (job.title || job.name)) || 'Untitled Job';
  const parts = [];
  const t = clockFormatWallTime(clockJobDayStart(job, jobDay));
  if (t) parts.push(t);
  const n = clockJobNumberLabel(job);
  if (n) parts.push(n);
  parts.push(title);
  return parts.join(' · ');
}

// Short name for a sentence: the job number, else the title.
function clockJobShortLabel(job) {
  return clockJobNumberLabel(job) || (job && (job.title || job.name)) || 'this job';
}

// person: { id, name } | null. nowMinutes: minutes since local midnight.
// Returns { jobId: string|null, crewedJobIds: string[], todayJobIds: string[] }.
function clockPickJobForPerson(jobs, todaysDays, person, nowMinutes) {
  const list = (jobs || []).filter(j => j && j.id != null);
  const days = (todaysDays || []).filter(Boolean);
  const todayJobIds = list.filter(j => days.some(d => d.jobId === j.id)).map(j => j.id);
  const pid = person && person.id;
  const pname = person && person.name;
  const crewed = (pid || pname)
    ? list.filter(j => days.some(d => d.jobId === j.id && clockPersonOnJobDay(d, pid, pname)))
    : [];
  const crewedJobIds = crewed.map(j => j.id);
  if (crewed.length === 1) return { jobId: crewed[0].id, crewedJobIds, todayJobIds };
  if (crewed.length > 1) {
    const now = Number.isFinite(nowMinutes) ? nowMinutes : null;
    let best = null, bestDist = Infinity;
    for (const j of crewed) {
      const day = clockTodaysDayFor(j.id, days, person);
      const start = clockWallMinutes(clockJobDayStart(j, day));
      const dist = (start == null || now == null) ? Infinity : Math.abs(start - now);
      if (dist < bestDist) { best = j; bestDist = dist; }
    }
    return { jobId: (best || crewed[0]).id, crewedJobIds, todayJobIds };
  }
  if (todayJobIds.length === 1) return { jobId: todayJobIds[0], crewedJobIds, todayJobIds };
  return { jobId: null, crewedJobIds, todayJobIds };
}

// null when nothing to say: no pick, no person, the pick carries the person
// on some day today, or no job today has them at all (then there is nothing
// better to point at). Otherwise the sentence parts + the job to switch to.
function clockCrewWarning(pickedJobId, jobs, todaysDays, person, nowMinutes) {
  if (!pickedJobId || !person || !(person.id || person.name)) return null;
  const list = (jobs || []).filter(j => j && j.id != null);
  const picked = list.find(j => j.id === pickedJobId);
  if (!picked) return null;
  const days = (todaysDays || []).filter(Boolean);
  const onPicked = days.some(d => d.jobId === pickedJobId && clockPersonOnJobDay(d, person.id, person.name));
  if (onPicked) return null;
  const pick = clockPickJobForPerson(list.filter(j => j.id !== pickedJobId), days, person, nowMinutes);
  if (!pick.crewedJobIds.length) return null;
  const crewed = pick.crewedJobIds.map(id => list.find(j => j.id === id)).filter(Boolean);
  const target = list.find(j => j.id === pick.jobId) || crewed[0];
  const targetDay = clockTodaysDayFor(target.id, days, person);
  return {
    pickedLabel: clockJobShortLabel(picked),
    crewLabels: crewed.map(clockJobShortLabel),
    switchToJobId: target.id,
    switchToLabel: clockJobShortLabel(target),
    switchToTime: clockFormatWallTime(clockJobDayStart(target, targetDay)),
    switchToJobDayId: (targetDay && targetDay.id) || null,
  };
}

// One line, amber (DESIGN.md Warning), with the one-tap switch. `switchJs` is
// the onclick body (TRUSTED — built by the caller from an escaped id). Text is
// split into static spans so the driver i18n pass can translate the chrome and
// leave the job numbers (data) alone.
function clockCrewWarningHtml(w, switchJs, opts) {
  if (!w) return '';
  const o = opts || {};
  const esc = (typeof escapeHtml === 'function') ? escapeHtml : (s => String(s));
  const you = w.crewLabels.length > 1 ? w.crewLabels.join(', ') : w.switchToLabel;
  const when = w.switchToTime ? ` (${esc(w.switchToTime)})` : '';
  const verb = o.buttonLabel || 'Switch to';
  // COD Domain: a kiosk admin clocking SOMEONE ELSE in reads "Sean Wood is on",
  // never "you're on" (opts.subjectName = the subject when it is not the viewer).
  const who = o.subjectName
    ? `<span aria-hidden="true">—</span> <b data-no-i18n style="white-space:nowrap;">${esc(o.subjectName)}</b> <span>is on</span>`
    : `<span>— you're on</span>`;
  return `<div class="clock-crew-warn" role="status" style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;background:#FFFBEB;border:1px solid #FDE68A;color:#92400E;border-radius:8px;padding:8px 12px;font-size:0.85rem;line-height:1.35;text-align:left;">
    <span class="material-icons" style="font-size:18px;flex-shrink:0;" aria-hidden="true">info</span>
    <span style="flex:1;min-width:0;"><span>Not on today's crew for</span> <b data-no-i18n style="white-space:nowrap;">${esc(w.pickedLabel)}</b> ${who} <b data-no-i18n style="white-space:nowrap;">${esc(you)}</b><span data-no-i18n style="white-space:nowrap;">${when}</span></span>
    ${switchJs ? `<button type="button" onclick="${switchJs}" style="flex-shrink:0;min-height:44px;padding:8px 14px;border-radius:999px;border:1.5px solid #D97706;background:#fff;color:#92400E;font-weight:700;font-size:0.82rem;cursor:pointer;"><span>${esc(verb)}</span> <span data-no-i18n>${esc(w.switchToLabel)}</span></button>` : ''}
  </div>`;
}

// The person a clock-side job picker is deciding for: the kiosk's selected
// employee (the self surface pins it to the viewer), as { id, name } | null.
function _clockPickerPerson(personId) {
  const pid = personId || ClockState.selectedPerson || null;
  if (!pid) return null;
  const p = (ClockState.people || []).find(x => x.id === pid);
  return { id: pid, name: (p && p.name) || null };
}

function _clockNowMinutes() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

// Paint (or clear) the crew warning under a job <select>. `jobs` = the jobs the
// picker lists (so a pinned / out-of-window job is judged too).
function _clockRenderCrewWarn(selectId, warnId, person, jobs) {
  const el = document.getElementById(warnId);
  if (!el) return;
  const jobId = document.getElementById(selectId)?.value || '';
  let w = null;
  try { w = clockCrewWarning(jobId, jobs, ClockState.todaysJobDays, person, _clockNowMinutes()); } catch (_) { w = null; }
  if (!w) { el.style.display = 'none'; el.innerHTML = ''; return; }
  const idJs = window.escJsAttr ? window.escJsAttr(w.switchToJobId) : String(w.switchToJobId).replace(/[^A-Za-z0-9_-]/g, '');
  // Kiosk: the subject is another employee → name them, never "you".
  const subjectName = (person && person.id && !_clockIsSelfSelection(person.id)) ? (person.name || 'This person') : null;
  el.innerHTML = clockCrewWarningHtml(w, `window.ClockModule.clockPickCrewJob('${selectId}','${warnId}','${idJs}')`, { subjectName });
  el.style.display = '';
}

// The one-tap switch: set the picker to the crewed job and run the same
// follow-ups a manual change runs (equipment rows + the warning itself).
function clockPickCrewJob(selectId, warnId, jobId) {
  const sel = document.getElementById(selectId);
  if (!sel) return;
  sel.value = jobId;
  if (sel.value !== jobId) return; // not an option (list changed under us) — leave it
  if (selectId === 'switch-job') onSwitchJobChange();
  else onClockJobChange();
}

// TASK-1082 — the admin job pickers (manual-adjustment + edit-clock-entry) used to
// dump EVERY non-finished/non-archived job for a customer (Core Oilfield: ~49),
// unlike the self clock-in picker which relevance-scores via getRelevantJobs. Route
// them through the same scoring (today + all jobs whose range covers today + the 10
// most-recent others) so the list stays short. ALWAYS pin the entry's currently-
// selected job even if it's out-of-window/finished, so editing an old shift never
// loses or silently changes its job. Serves the 99% (edits are within the current
// pay period); an ancient job for a brand-new manual entry is the rare 1% (add a
// type-ahead search later if that ever bites).
// TASK-9548 slice 2 — same scoring, returned as labeled buckets. `pinned` holds
// the entry's current job when it fell out of the window (the TASK-1082 pin),
// under its own "On this entry" header instead of an unexplained top slot.
function getRelevantAdminJobGroups(customerId, selectedJobId, selectedJobTitle) {
  const { groups } = getRelevantJobs(customerId);
  // getRelevantJobs only drops status==='finished'; ClockState.allJobs still
  // carries 'archived' (soft-deleted) jobs, which the admin pickers must not list.
  const live = j => j.status !== 'archived';
  const out = { pinned: [], today: groups.today.filter(live), range: groups.range.filter(live), other: groups.other.filter(live) };
  const listed = [...out.today, ...out.range, ...out.other];
  if (selectedJobId && !listed.some(j => j.id === selectedJobId)) {
    const sel = (ClockState.allJobs || []).find(j => j.id === selectedJobId);
    // Pin the entry's current job even if out-of-window. ClockState.allJobs is
    // loaded with status != 'finished' (loadAllActiveJobs), so a FINISHED job
    // is never in it — the pin used to silently miss, the placeholder rendered
    // selected, and a Save then wrote jobId:null onto the entry (both save
    // paths send the picker's value). Fall back to a stub built from the id
    // and the title the entry itself carries (found 2026-10-02 by the slice-2
    // Playwright run, not by a report).
    out.pinned.push(sel || { id: selectedJobId, title: selectedJobTitle || 'Current job' });
  }
  return out;
}

function getRelevantAdminJobs(customerId, selectedJobId) {
  const g = getRelevantAdminJobGroups(customerId, selectedJobId);
  return [...g.pinned, ...g.today, ...g.range, ...g.other];
}

// TASK-9548 slice 2 — the title to SAVE when the picked job is not in
// ClockState.allJobs (finished jobs never are): if it is the job the entry
// already has, keep the entry's own stored title; a genuinely new, uncached
// job (shouldn't happen — the picker only lists cached jobs + the pin) saves
// null exactly as before.
function _keptJobTitle(newJobId, entry) {
  if (!newJobId || !entry || entry.jobId !== newJobId) return null;
  return entry.jobTitle || entry.jobName || null;
}

function buildRelevantJobOptions(customerId, selectedJobId, placeholderLabel = 'No specific job', selectedJobTitle = null) {
  return _clockJobOptionsHtml(getRelevantAdminJobGroups(customerId, selectedJobId, selectedJobTitle), { selectedJobId, placeholderLabel });
}

function populateJobDropdown(customerId) {
  const select = document.getElementById('clock-job');
  if (!select) return;
  select.innerHTML = '<option value="">No specific job</option>';

  if (!customerId || customerId === '__shop__') return;

  const { jobs: filtered, todaysJobIds, groups } = getRelevantJobs(customerId);

  select.innerHTML = _clockJobOptionsHtml(groups);

  // Auto-select if exactly one job; otherwise (TASK-9865) the job whose TODAY
  // day has this person on the crew — nearest start when several — and
  // NOTHING when two+ jobs have a day today and none carries them. The old
  // "first job with a day today" match is how two people landed on the 5 PM
  // job after running the 8 AM one.
  const person = _clockPickerPerson();
  if (filtered.length === 1) {
    select.value = filtered[0].id;
  } else if (filtered.length > 1) {
    const todayMatch = clockPickJobForPerson(filtered, ClockState.todaysJobDays, person, _clockNowMinutes());
    if (todayMatch.jobId && todaysJobIds.has(todayMatch.jobId)) select.value = todayMatch.jobId;
  }
  // COD Code Reviewer: remember that THIS value was automatic, so a kiosk
  // "employee A → employee B" change re-decides it instead of carrying A's
  // job to B as if B had picked it (onClockJobChange clears the mark).
  select.dataset.autoPick = select.value || '';

  // TASK-586 — the job list was (re)built and may have been auto-selected
  // WITHOUT firing onchange — sync the inline equipment confirm rows to the
  // current selection. No-ops (hides/clears) when equipmentTie is OFF.
  _clockRenderInlineEquipment(select.value || '');
  _clockRenderCrewWarn('clock-job', 'clock-job-crew-warn', person, filtered);
}

// ============================================
// TASK-586 — PER-OPERATOR EQUIPMENT CONFIRM AT SELF-CLOCK-IN (equipmentTie)
// ============================================
// When settings.equipmentTie.enabled is ON (TASK-491, default OFF) and the
// person clocking THEMSELVES in selects a job whose day has equipment, the
// same all-default-selected asset toggle rows used by the Job Board inline
// clock-in (TASK-558) render under the job dropdown, and the single Clock In
// tap writes the chosen assetIds[] ATOMICALLY on the clock entry
// (source:'clock_spa') — no second sheet, no tap-and-not-clocked-in gap.
// Without this, staff-login self clock-ins via the Clock tile fell back to
// legacy full-fleet attribution on ON tenants (the exact inflation the
// feature exists to prevent).
//
// HELPERS BELOW ARE COPIED FROM jobboard.js, NOT IMPORTED — Pitfall #1
// forbids cross-module ?v= imports (separate module instances), and jobboard
// is owned by another session right now. Each copy notes its jobboard.js twin
// — KEEP THEM IN SYNC.

// mirrors jobboard.js equipmentTieEnabled (TASK-491) — keep in sync.
// Master tenant gate. DEFAULT OFF — when OFF every path below is inert and
// pre-586 behavior is byte-identical. Reads AppState (not a module import) so
// there's no ?v= cross-import instance risk. Simple clock-out mode = OFF (no
// tickets, toggle hidden there — see the jobboard.js twin's note).
function equipmentTieEnabled() {
  const s = window.AppState?.settings;
  if (s?.equipmentTie?.enabled !== true) return false;
  // TASK-9553 — the mode of the person being clocked (their override, else the company's).
  const mode = _clockModeFor(ClockState.selectedPerson);
  return mode === 'ticket' || mode === 'ticketonly';
}

// TASK-9553 — one person's clock mode: their own override, else the company's.
// Without app.js's resolver it is the company setting, exactly as before.
function _clockModeFor(personId) {
  const cm = window.ClockMode;
  if (cm && typeof cm.effective === 'function') return cm.effective(personId || null);
  return window.AppState?.settings?.clockOutMode || 'simple';
}

// mirrors jobboard.js formatDateISO (local-timezone YYYY-MM-DD) — keep in sync.
function _clockFormatDateISO(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// mirrors jobboard.js _jbResolveJobDayForEntry / _jbPickJobDayForEntry — keep
// in sync. Resolve THE jobDay a clock entry belongs to: the day on the entry's
// clock-in TENANT calendar date (settings.timezone via window.ClockDay; a bare
// 'YYYY-MM-DD' date is that calendar day), or NOTHING. v1.15.2000 COD 🔴 (money):
// the old first-day fallback and the legacy fill's first-match lookup put a
// different day's truck on the ticket; null makes every caller fill nothing and
// leave the pick to the person. Searches today's jobDays plus the clock-in
// date's days that _clockEnsureEntryJobDays loads for a shift that started on
// an earlier day (overnight / forgot to clock out). Several days of the job on
// that date → the entry's own jobDayId wins. Self-contained (unit harnesses
// extract it by name).
function _clockResolveJobDayForEntry(entry) {
  if (!entry?.jobId) return null;
  const pool = [...(ClockState.todaysJobDays || []), ...(ClockState.entryJobDays || [])];
  const seen = new Set();
  const days = pool.filter(jd => {
    if (!jd || jd.jobId !== entry.jobId || seen.has(jd.id)) return false;
    seen.add(jd.id);
    return true;
  });
  if (!days.length) return null;
  if (entry.clockIn == null || entry.clockIn === '') return null;
  const W = (typeof window !== 'undefined' && window) || {};
  const _s = W.AppState?.settings || W.AppState?.tenantData?.settings || {};
  const _tz = _s.timezone || 'America/Chicago';
  const _CD = W.ClockDay;
  const dayKey = (v) => {
    if (typeof v === 'string') {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
      if (m) return Math.round(Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000);
    }
    const d = v?.toDate?.() || (v instanceof Date ? v : (v != null ? new Date(v) : null));
    if (!d || isNaN(d.getTime())) return null;
    if (_CD && typeof _CD.localDaySerial === 'function') {
      const k = _CD.localDaySerial(d, _tz);
      if (k != null) return k;
    }
    return Math.round(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000);
  };
  const ciKey = dayKey(entry.clockIn);
  if (ciKey == null) return null;
  const sameDay = days.filter(jd => dayKey(jd.date) === ciKey);
  if (!sameDay.length) return null;
  return (entry.jobDayId && sameDay.find(jd => jd.id === entry.jobDayId)) || sameDay[0];
}

// TASK-9579 — the shift is on a job, but that job has no day on the shift's
// clock-in TENANT date (same resolver as the fill). Returns { entryId, label }
// or null. Shop / internal-time customers bill no truck by design: null.
// mirrors jobboard.js _jbEquipDayMiss — keep in sync.
function _clockEquipDayMiss(entry) {
  try {
    if (!entry?.jobId || entry.clockIn == null || entry.clockIn === '') return null;
    if (entry.customerId === '__shop__') return null;
    if (ClockState._entryJobDaysReadFailed === true) return null; // unread ≠ missing (COD)
    const cust = entry.customerId ? (ClockState.customers || []).find(c => c.id === entry.customerId) : null;
    if (cust && (cust.internalTimeOnly === true || (cust.internalTimeOnly === undefined && /^(shop|office|internal|yard)$/i.test(cust.name || '')))) return null;
    if (_clockResolveJobDayForEntry(entry)) return null;
    const d = entry.clockIn?.toDate?.() || (entry.clockIn instanceof Date ? entry.clockIn : new Date(entry.clockIn));
    if (!d || isNaN(d.getTime())) return null;
    const tz = (window.AppState?.settings || window.AppState?.tenantData?.settings || {}).timezone || 'America/Chicago';
    let label;
    try { label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz }); }
    catch (_) { label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
    return { entryId: entry.id || null, label };
  } catch (e) {
    console.warn('[Clock] TASK-9579 day-miss check failed (no warning shown):', e?.message);
    return null;
  }
}

// v1.15.2000 COD 🔴 — ClockState only holds TODAY's jobDays, so a shift that
// started yesterday (overnight, forgot to clock out) has no day to resolve, and
// the old first-match lookup billed TODAY's truck onto it. Before a clock-out
// fill, load the entry's own job days by jobId and keep the ones that resolve
// for its clock-in date. Best-effort: a failed read leaves the fill empty
// (driver picks), never blocks the clock-out (TASK-428).
async function _clockEnsureEntryJobDays(entry) {
  ClockState.entryJobDays = [];
  ClockState._entryJobDaysReadFailed = false; // TASK-9579
  if (!entry?.jobId || !ClockState.tenantId) return;
  if (_clockResolveJobDayForEntry(entry)) return; // today's list already has it
  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', ClockState.tenantId, 'jobDays'),
      where('jobId', '==', entry.jobId)
    ));
    ClockState.entryJobDays = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // Keep only the day(s) on the clock-in date — nothing else may resolve.
    const hit = _clockResolveJobDayForEntry(entry);
    ClockState.entryJobDays = hit
      ? ClockState.entryJobDays.filter(jd => _clockResolveJobDayForEntry({ ...entry, jobDayId: jd.id }) === jd)
      : [];
  } catch (e) {
    console.warn('[Clock] entry jobDays read failed (fill stays empty, driver picks):', e?.message);
    ClockState.entryJobDays = [];
    // TASK-9579 (COD) — an unread day list is not "no job day": never claim a
    // date miss on a failed read.
    ClockState._entryJobDaysReadFailed = true;
  }
}

// mirrors jobboard.js _jbJobDayAssetIds (TASK-491) — keep in sync.
// Union of a jobDay's asset ids (trucks + trailers + assets).
function _clockJobDayAssetIds(jobDay) {
  if (!jobDay) return new Set();
  const ids = new Set();
  (jobDay.trucks || []).forEach(r => { if (r != null) ids.add(String(r)); });
  (jobDay.trailers || []).forEach(r => { if (r != null) ids.add(String(r)); });
  (jobDay.assets || []).forEach(r => { if (r != null) ids.add(String(r)); });
  return ids;
}

// mirrors jobboard.js _jbBuildAssetProvenance (TASK-1547) — keep in sync.
// Normalizes the assetProvenance map written on the clockEntry at clock-in:
// the CHOICE (rendered/selected/touched/labor-only) alongside the outcome
// (assetIds). Firestore rejects `undefined` — every field explicit. Written
// ONLY on equipmentTie ON paths (always alongside an assetIds array).
function _clockBuildAssetProvenance(source, sel, jobDayAssetIdSet) {
  return {
    source: source || 'kiosk',
    renderedAssetIds: Array.isArray(sel?.renderedAssetIds) ? sel.renderedAssetIds.map(String) : [],
    selectedAssetIds: Array.isArray(sel?.selectedAssetIds) ? sel.selectedAssetIds.map(String) : [],
    explicitLaborOnly: sel?.explicitLaborOnly === true,
    jobDayAssetIds: jobDayAssetIdSet ? Array.from(jobDayAssetIdSet).map(String) : [],
    defaulted: sel?.defaulted === true
  };
}

// mirrors jobboard.js _jbEntryCountsAsMissingAssetIds (TASK-1539 💰) — keep in
// sync (body identical; the FULL decision-table comment lives on the jobboard
// twin). Summary: field-absent = missing (unchanged); on SPLIT jobs only that
// rule applies ([] never promotes — TASK-1221 double-bill hazard); [] with
// provenance follows the driver's recorded choice (explicit labor-only /
// picked-nothing-from-a-real-menu stay empty; nothing-to-pick is eligible);
// legacy [] with no provenance is eligible only when the jobDay has equipment
// NOW (conservative reconstruction — bills the day exactly as pre-feature via
// the legacy all-assets fallback).
function _clockEntryCountsAsMissingAssetIds(entry, opts) {
  const a = entry ? entry.assetIds : null;
  if (!Array.isArray(a)) return true;                              // rule 1
  if (opts && opts.splitJob === true) return false;                // rule 2
  if (a.length > 0) return false;                                  // rule 3
  const prov = entry.assetProvenance;
  if (prov && typeof prov === 'object') {
    if (prov.explicitLaborOnly === true) return false;             // rule 4
    if (!Array.isArray(prov.renderedAssetIds)) return false;       // rule 7
    return prov.renderedAssetIds.length === 0;                     // rules 5+6
  }
  return !!(opts && opts.jobDayHasAssetsNow === true);             // rule 8
}

// ── TASK-1221 — per-job splitBy resolver (SHARED, client + server) ──────────
// resolveSplitKey MUST stay BYTE-IDENTICAL with the copy in
// functions/src/services/ticketGateway.js (and the classifier arrow-fn body
// below). Client + server both key a clock entry's ticket on this function; if
// they drift, a PIN driver's server path (repoint / switchJob) re-merges what
// the client split → duplicate tickets + double-billed equipment. Do NOT edit
// one copy without the other.
//
//   splitBy    : 'combined' | 'vehicle' | 'operator' | 'asset' (absent → combined)
//   context    : { assetIds?: (string)[], personId?: string }
//   classifier : (assetRef) => ({ id: <canonicalPowerUnitId>, isPickup: bool }) | null
//                null when the ref is NOT a power unit (trailer / gear / unknown).
//                `id` is the roster-canonical truck doc id so both sides agree
//                whether assetIds carry doc-ids, unit#s, or names.
//
// Returns the splitKey string, or null for combined (unchanged jobId behavior).
function resolveSplitKey(splitBy, context, classifier) {
  const mode = splitBy || 'combined';
  if (mode === 'combined') return null;
  const assetIds = (context && Array.isArray(context.assetIds)) ? context.assetIds : [];
  const personId = (context && context.personId != null) ? String(context.personId) : '';

  if (mode === 'operator') {
    return personId ? ('op:' + personId) : null;
  }

  if (mode === 'vehicle') {
    // Primary power unit among this entry's assets: a non-pickup
    // (hydrovac / semi / vac / etc.) outranks a pickup; ties break on the
    // assetIds order (first wins). One entry → one rig ticket (Model A).
    let primary = null;
    for (const ref of assetIds) {
      const pu = classifier ? classifier(ref) : null;
      if (!pu) continue;                       // not a power unit — skip
      if (!primary) { primary = pu; continue; }
      if (primary.isPickup && !pu.isPickup) primary = pu; // upgrade off a pickup
    }
    if (primary) return 'veh:' + primary.id;
    // No power unit on this entry (trailer-only / labor-only) → give it its own
    // ticket keyed on the operator so it never collapses onto a rig it never ran.
    return personId ? ('op:' + personId) : null;
  }

  // 'asset' fan-out is Phase 2 (one entry → multiple tickets) — NOT resolvable
  // to a single key here. Until Phase 2 ships its per-asset former, treat like
  // combined (no split).
  return null;
}

// Client-side power-unit classifier over the loaded roster (ClockState.trucks).
// The RETURNED arrow-fn body is kept identical to the server copy in
// ticketGateway.js (buildServerPowerUnitClassifier); only the `trucks` source
// differs (sync ClockState cache here vs a one-shot collection read server-side).
function _clockBuildPowerUnitClassifier() {
  const trucks = ClockState.trucks || [];
  return (ref) => {
    if (ref == null) return null;
    const t = _resolveFleetRef(ref, trucks); // TASK-4529
    if (!t) return null;
    const ty = String(t.assetType || t.type || '').toLowerCase();
    return { id: String(t.id), isPickup: ty === 'pickup' };
  };
}

// Display label for a splitKey (vehicle nickname + unit, e.g. "Pelican #13").
// Display-only — never affects matching. Resolved from the loaded roster.
function _clockSplitLabel(splitKey) {
  if (!splitKey) return null;
  if (splitKey.startsWith('veh:')) {
    const id = splitKey.slice(4);
    const t = (ClockState.trucks || []).find(x => String(x.id) === id);
    if (!t) return 'Vehicle';
    const nick = t.nickname || t.name || '';
    const unit = t.unit ? ('#' + t.unit) : '';
    const label = [nick, unit].filter(Boolean).join(' ');
    return label || (window.getUnitDisplay ? window.getUnitDisplay(t) : (t.unit || 'Vehicle'));
  }
  if (splitKey.startsWith('op:')) {
    const pid = splitKey.slice(3);
    const p = (ClockState.people || []).find(x => String(x.id) === pid);
    return p ? (p.name || 'Operator') : 'Operator';
  }
  return null;
}

// TASK-586 — is this clock-in the viewer clocking THEMSELVES in?
// Self = the signed-in member's linked personId (members/{uid}.personId)
// matches the person being clocked in. Covers both surfaces:
//   • self-clock surface (no workforce access): clock-person is pinned to
//     getSelfPersonId(), so a match is guaranteed;
//   • workforce kiosk: an admin selecting THEIR OWN linked person is self;
//     selecting anyone else is an ADMIN clock-in of another person and stays
//     legacy full-fill — the admin isn't the operator and can't speak for
//     their equipment (dispatcher-confirmed default, TASK-586).
// Kiosk PIN tenants without member docs and founder/owner accounts without a
// linked personId return false → legacy fallback intact by design.
function _clockIsSelfClockIn(personId) {
  if (!personId) return false;
  const A = window.AppState || {};
  // Driver/public contexts never get the member self surface.
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  const selfId = getSelfPersonId();
  return !!selfId && String(personId) === String(selfId);
}

// mirrors jobboard.js _jbBuildEquipRows (TASK-558) — keep in sync.
// Resolve a jobDay's asset ids to display rows {id, name, type, icon} against
// ClockState's roster lists (legacy dual lookup: id OR unit OR name).
function _clockBuildEquipRows(jobDay) {
  const ids = Array.from(_clockJobDayAssetIds(jobDay));
  return ids.map(id => {
    let asset = _resolveFleetRef(id, ClockState.trucks);
    let type = 'Truck', icon = 'local_shipping';
    if (!asset) { asset = _resolveFleetRef(id, ClockState.trailers); type = 'Trailer'; icon = 'rv_hookup'; }
    if (!asset) { asset = _resolveFleetRef(id, ClockState.assets); type = 'Equipment'; icon = 'construction'; }
    const realId = asset?.id || id;
    const name = (window.getUnitDisplay && asset ? window.getUnitDisplay(asset) : '') || asset?.name || asset?.unit || id;
    // TASK-9797 item 1 S2 — the unit's named slot ("H2-Night"), '' when none / setting off.
    let slot = '';
    try { slot = window.CrewPairing?.slot?.(jobDay, realId, window.AppState?.settings) || ''; } catch (_) { /* no slot line */ }
    return { id: realId, name, type, icon, slot };
  });
}

// mirrors jobboard.js _jbBuildEquipRowsHtml (TASK-558) — keep in sync.
// All rows default selected (data-selected="1"). Tapping toggles via
// window.ClockModule.clockToggleEquipRow (same visual states as the twin).
// TASK-9763 — `pre` (optional) = _clockCrewPairPreselect's result; absent or
// mode 'none' → every row checked, byte-identical to before.
function _clockBuildEquipRowsHtml(rows, pre) {
  const rowStyle = 'display:flex;align-items:center;gap:12px;width:100%;min-height:56px;padding:12px 14px;border:2px solid #e2e8f0;border-radius:12px;background:#fff;cursor:pointer;text-align:left;-webkit-tap-highlight-color:rgba(0,0,0,0.05);';
  const paired = !!pre && pre.mode && pre.mode !== 'none';
  const isOn = (r) => !paired || pre.checked.has(String(r.id));
  const list = paired ? rows.filter(isOn).concat(rows.filter(r => !isOn(r))) : rows;
  return list.map(r => {
    const on = isOn(r);
    const mine = on && paired && pre.mode === 'paired';
    return `
    <button type="button" class="clock-eqc-row" data-asset-id="${escapeHtml(r.id)}" data-selected="${on ? '1' : '0'}"${mine ? ' data-paired="1"' : ''}
      onclick="window.ClockModule.clockToggleEquipRow(this)"
      style="${rowStyle}${on ? 'border-color:#3b82f6;background:#eff6ff;' : ''}">
      <span class="material-icons clock-eqc-check" style="font-size:24px;color:${on ? '#3b82f6' : '#94a3b8'};">${on ? 'check_box' : 'check_box_outline_blank'}</span>
      <span class="material-icons" style="font-size:22px;color:#64748b;">${r.icon}</span>
      <span style="flex:1;min-width:0;">
        <span style="display:block;font-weight:600;font-size:0.95rem;color:#0f172a;">${escapeHtml(r.name)}</span>
        <span style="display:block;font-size:0.78rem;color:#64748b;">${r.type}${mine ? ' · <strong style="color:#1e40af;">Your assignment</strong>' : ''}${r.slot ? ' · Slot ' + escapeHtml(r.slot) : ''}</span>
      </span>
    </button>`;
  }).join('');
}

// TASK-9763 — "who's in what". mirrors jobboard.js _jbUnitKey/_jbPersonKey/
// _jbCrewPairPreselect — keep in sync. The rule itself lives ONCE in
// window.CrewPairing (functions/src/shared/crewPairing.js).
function _clockUnitKey(ref) {
  const s = String(ref);
  const a = _resolveFleetRef(s, ClockState.trucks)
    || _resolveFleetRef(s, ClockState.trailers)
    || _resolveFleetRef(s, ClockState.assets);
  return a && a.id != null ? String(a.id) : s;
}

function _clockPersonKey(ref) {
  const s = String(ref);
  const p = (ClockState.people || []).find(x => x && (String(x.id) === s || x.name === s));
  return p && p.id != null ? String(p.id) : s;
}

// Rule 5 for an id list → { mode, checked: Set<id> }. Any failure → mode 'none'
// (all checked) — a pairing can never trap anyone.
function _clockCrewPairPreselect(jobDay, ids, personId) {
  const list = (ids || []).map(String);
  const all = new Set(list);
  const CP = window.CrewPairing;
  if (!CP || !jobDay || !personId) return { mode: 'none', checked: all };
  try {
    const view = CP.view(jobDay, { unitKey: _clockUnitKey, personKey: _clockPersonKey });
    if (!view || !view.active) return { mode: 'none', checked: all };
    // The id alone (personId is always known here): a name match would hand a
    // namesake's truck to this person (COD Edge Case).
    const viewer = [String(personId)];
    const keyOf = new Map(list.map(id => [id, _clockUnitKey(id)]));
    const res = CP.preselect(view, Array.from(new Set(keyOf.values())), viewer);
    const on = new Set(res.checked || []);
    return { mode: res.mode, checked: new Set(list.filter(id => on.has(keyOf.get(id)))) };
  } catch (_) {
    return { mode: 'none', checked: all };
  }
}

// TASK-9763 S2 — "In a different truck?" (settings.driverTruckSwap, default
// OFF). mirrors jobboard.js jbDriverTruckSwapEnabled/_jbTruckSwap*/
// jbEquipSwapPicked — keep in sync (full reasoning on the jobboard twin).
function _clockTruckSwapEnabled() {
  return equipmentTieEnabled() && window.AppState?.settings?.driverTruckSwap === true;
}

function _clockIsTruckRef(ref) {
  return !!_resolveFleetRef(String(ref), ClockState.trucks);
}

function _clockTruckSwapHtml(rows, personId) {
  if (!_clockTruckSwapEnabled()) return '';
  const listed = new Set();
  (rows || []).forEach(r => {
    listed.add(String(r.id));
    const t = _resolveFleetRef(String(r.id), ClockState.trucks);
    if (t && t.id != null) listed.add(String(t.id));
  });
  let rank = new Map();
  try { rank = _clockRecentAssetRank(personId); } catch (_) { /* ordering is a nicety */ }
  const nameOf = t => (window.getUnitDisplay ? window.getUnitDisplay(t) : '') || t.nickname || t.unit || t.name || 'Truck';
  const trucks = (ClockState.trucks || [])
    .filter(t => t && t.id != null && t.active !== false && t.deleted !== true && t.archived !== true && !listed.has(String(t.id)))
    .sort((a, b) => {
      const ra = rank.get(String(a.id)) || 0;
      const rb = rank.get(String(b.id)) || 0;
      if (ra !== rb) return rb - ra;
      return nameOf(a).localeCompare(nameOf(b));
    });
  if (!trucks.length) return '';
  const escA = window.escAttr || escapeHtml;
  return `
    <label style="display:block;font-size:0.85rem;font-weight:600;color:#334155;margin:12px 0 4px;">In a different truck?</label>
    <select class="clock-eqc-swap" aria-label="Pick a different truck" onchange="window.ClockModule.clockEquipSwapPicked(this)"
      style="width:100%;min-height:48px;padding:10px 12px;font-size:16px;border:2px solid #e2e8f0;border-radius:12px;background:#fff;color:#0f172a;"><option value="">Pick from all trucks…</option>${trucks.map(t => `<option value="${escA(String(t.id))}">${escapeHtml(nameOf(t))}</option>`).join('')}</select>`;
}

function clockEquipSwapPicked(sel) {
  if (!sel || !sel.value) return;
  const id = String(sel.value);
  const host = sel.closest('#clock-in-equipment');
  const first = host ? host.querySelector('.clock-eqc-row') : null;
  const list = first ? first.parentNode : null;
  if (!host || !list) return;
  const rows = () => Array.from(list.querySelectorAll('.clock-eqc-row'));
  rows().forEach(row => {
    if (row.dataset.selected === '1' && row.dataset.assetId !== id && _clockIsTruckRef(row.dataset.assetId)) clockToggleEquipRow(row);
  });
  let row = rows().find(r => r.dataset.assetId === id);
  if (!row) {
    const t = _resolveFleetRef(id, ClockState.trucks);
    const name = (t && window.getUnitDisplay ? window.getUnitDisplay(t) : '') || t?.nickname || t?.unit || t?.name || 'Truck';
    list.insertAdjacentHTML('afterbegin', _clockBuildEquipRowsHtml(
      [{ id, name, type: 'Truck · not on this job', icon: 'local_shipping' }],
      { mode: 'unpaired', checked: new Set([id]) }));
    row = rows().find(r => r.dataset.assetId === id);
    if (row) row.dataset.swap = '1';
  } else if (row.dataset.selected !== '1') {
    clockToggleEquipRow(row);
  }
  host.dataset.eqTouched = '1';
  delete host.dataset.eqLaborOnly;
  const opt = Array.from(sel.options).find(o => o.value === id);
  if (opt) opt.remove();
  sel.value = '';
}

// mirrors jobboard.js jbToggleEquipConfirmRow (TASK-491/558) — keep in sync.
function clockToggleEquipRow(btn) {
  const selected = btn.dataset.selected === '1';
  const next = !selected;
  btn.dataset.selected = next ? '1' : '0';
  btn.style.borderColor = next ? '#3b82f6' : '#e2e8f0';
  btn.style.background = next ? '#eff6ff' : '#fff';
  const chk = btn.querySelector('.clock-eqc-check');
  if (chk) { chk.textContent = next ? 'check_box' : 'check_box_outline_blank'; chk.style.color = next ? '#3b82f6' : '#94a3b8'; }
  // TASK-1547 — any row toggle marks the selection as user-touched (provenance
  // defaulted:false).
  const host = btn.closest('#clock-in-equipment');
  if (host) host.dataset.eqTouched = '1';
}

// mirrors jobboard.js jbStandaloneLaborOnly (TASK-558) — keep in sync.
// "No Equipment (Labor Only)" deselects every row so the single Clock In tap
// writes an empty assetIds[].
function clockInLaborOnly() {
  const container = document.getElementById('clock-in-equipment');
  if (!container) return;
  // TASK-1547 — remember the labor-only tap; explicitLaborOnly is stamped at
  // clock-in only if the selection is STILL empty then (re-selecting a row
  // after this tap wins).
  container.dataset.eqLaborOnly = '1';
  container.querySelectorAll('.clock-eqc-row').forEach(row => {
    if (row.dataset.selected === '1') clockToggleEquipRow(row);
  });
}

// mirrors jobboard.js _jbReadStandaloneAssetIds (TASK-558) — keep in sync.
// Returns the selected asset ids, [] when all rows are deselected (labor
// only), or NULL when no rows are rendered at all (caller falls back to
// all-default-selected — never silently zero-bills a day with equipment).
function _clockReadInlineAssetIds() {
  const container = document.getElementById('clock-in-equipment');
  if (!container) return null;
  const rows = Array.from(container.querySelectorAll('.clock-eqc-row'));
  if (!rows.length) return null;
  return rows
    .filter(r => r.dataset.selected === '1')
    .map(r => r.dataset.assetId)
    .filter(Boolean);
}

// mirrors jobboard.js _jbRenderStandaloneEquipment (TASK-558) — keep in sync.
// Renders the toggle rows + "No Equipment (Labor Only)" inline under the job
// dropdown when ON + SELF clock-in + the job's day has equipment. Hidden and
// empty in every other case, so the Clock In tap stays one-tap and OFF
// tenants render byte-identical.
function _clockRenderInlineEquipment(jobId) {
  const container = document.getElementById('clock-in-equipment');
  if (!container) return;

  // TASK-1547 — every (re)render is a fresh menu: reset the provenance flags
  // so a stale touch/labor-only from a previous job selection can't leak.
  delete container.dataset.eqTouched;
  delete container.dataset.eqLaborOnly;

  if (!equipmentTieEnabled() || !jobId || !_clockIsSelfClockIn(ClockState.selectedPerson)) {
    container.style.display = 'none';
    container.innerHTML = '';
    return;
  }

  const jobDay = _clockResolveJobDayForEntry({ jobId, clockIn: new Date() });
  const dayAssetIds = _clockJobDayAssetIds(jobDay);
  if (dayAssetIds.size === 0) {
    // TASK-1572 Slice 2 — ON but dispatch left this day empty: ASK (fleet,
    // ordered by this person's recent use, never pre-selected) instead of hiding
    // the question. mirrors jobboard.js _jbRenderStandaloneEquipment — keep in sync.
    const _fleet = _clockBuildFleetOptions(ClockState.selectedPerson);
    if (_fleet.count > 0) {
      container.style.display = 'block';
      container.innerHTML = _clockFleetAskHtml(_fleet.html);
      return;
    }
    // ON but this job day has no equipment → plain clock-in, no rows.
    container.style.display = 'none';
    container.innerHTML = '';
    return;
  }

  const rows = _clockBuildEquipRows(jobDay);
  // TASK-9763 — only THIS person's paired units start checked (rule 5).
  const pre = _clockCrewPairPreselect(jobDay, rows.map(r => r.id), ClockState.selectedPerson);
  const rowsHtml = _clockBuildEquipRowsHtml(rows, pre);
  const labelStyle = 'display:block;font-size:14px;font-weight:600;color:#334155;margin-bottom:6px;';
  container.style.display = 'block';
  container.innerHTML = `
    <label style="${labelStyle}">Which equipment will you be in? <span style="font-weight:400;color:#64748b;">${pre.mode === 'none' ? '(tap to deselect)' : pre.mode === 'paired' ? '(checked = your assignment; tap to change)' : "(dispatch hasn't put you in a unit — tap the one you're running)"}</span></label>
    <div style="display:flex;flex-direction:column;gap:8px;">
      ${rowsHtml}
    </div>
    ${_clockTruckSwapHtml(rows, ClockState.selectedPerson)}
    <button type="button" id="clock-in-labor-only" onclick="window.ClockModule.clockInLaborOnly()"
      style="width:100%;min-height:48px;margin-top:10px;padding:12px;font-size:0.9rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:12px;background:#f8fafc;color:#334155;cursor:pointer;">
      <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:18px;">person</span>No Equipment (Labor Only)
    </button>`;
}

// ============================================
// TASK-1572 Slice 2 — STOP HIDING THE QUESTION AT CLOCK-IN (clock.js twin)
// ============================================
// mirrors jobboard.js _jbFleetAsk* (full reasoning on the jobboard twin's Slice 2
// header) — keep in sync. In short: on an equipmentTie tenant whose job day has
// no equipment, the self clock-in asks which truck instead of hiding the section;
// the answer is a CLAIM on assetProvenance (`fleetOffered` + `claimedAssetIds`),
// never assetIds (onClockEntryAssetValidate would strip it and the rule-5 read
// would switch off the TASK-1539 fill and the Slice 1 prompt); renderedAssetIds
// stays [] (rule 6); never pre-selected; every exit clocks in.
// This surface is SELF clock-in by an SPA member (non-driver scope), for whom
// jobboard's jbCanDriverEditEquipment is always true — so the gate is the one
// _clockRenderInlineEquipment already applied (tie ON + self clock-in).

// Recent-use rank from the entries this module already holds for the person —
// his own tied assetIds and earlier claims. No new query. Absent = no signal.
function _clockRecentAssetRank(personId) {
  const rank = new Map();
  if (!personId) return rank;
  const seen = new Set();
  [...(ClockState.activeEntries || []), ...(ClockState.recentEntries || [])].forEach(e => {
    if (!e || String(e.personId) !== String(personId) || seen.has(e.id)) return;
    seen.add(e.id);
    const ids = [
      ...(Array.isArray(e.assetIds) ? e.assetIds : []),
      ...(Array.isArray(e.assetProvenance?.claimedAssetIds) ? e.assetProvenance.claimedAssetIds : [])
    ];
    ids.forEach(ref => {
      const hit = _clockResolveAnyAsset(String(ref));
      const key = hit && hit.asset.id != null ? String(hit.asset.id) : String(ref);
      rank.set(key, (rank.get(key) || 0) + 1);
    });
  });
  return rank;
}

function _clockBuildFleetOptions(personId) {
  let rank = new Map();
  try { rank = _clockRecentAssetRank(personId); } catch (_) { /* ordering is a nicety */ }
  const nameOf = t => (window.getUnitDisplay ? window.getUnitDisplay(t) : '') || t.nickname || t.unit || t.name || 'Unit';
  const sortRanked = list => list.slice().sort((a, b) => {
    const ra = rank.get(String(a.id)) || 0;
    const rb = rank.get(String(b.id)) || 0;
    if (ra !== rb) return rb - ra;
    return nameOf(a).localeCompare(nameOf(b));
  });
  const trucks = sortRanked((ClockState.trucks || []).filter(t => t && t.id != null));
  const trailers = sortRanked((ClockState.trailers || []).filter(t => t && t.id != null));
  const escA = window.escAttr || escapeHtml;
  const opt = t => `<option value="${escA(String(t.id))}">${escapeHtml(nameOf(t))}</option>`;
  let html = '<option value="">Select what you\'re in…</option>';
  if (trucks.length) html += `<optgroup label="Trucks">${trucks.map(opt).join('')}</optgroup>`;
  if (trailers.length) html += `<optgroup label="Trailers">${trailers.map(opt).join('')}</optgroup>`;
  return { html, count: trucks.length + trailers.length };
}

function _clockFleetAskHtml(optionsHtml) {
  return `
    <label style="display:block;font-size:14px;font-weight:600;color:#334155;margin-bottom:4px;">Which truck are you in?</label>
    <div style="font-size:0.78rem;color:#64748b;margin-bottom:6px;line-height:1.35;">Dispatch hasn't assigned equipment to this job yet. Pick what you're running so it lands on your ticket.</div>
    <select id="clock-in-fleet-ask" class="clock-fleet-ask" onchange="window.ClockModule.clockFleetAskChanged(this)"
      style="width:100%;min-height:48px;padding:10px 12px;font-size:16px;border:2px solid #e2e8f0;border-radius:12px;background:#fff;color:#0f172a;">${optionsHtml}</select>
    <button type="button" id="clock-in-fleet-labor-only" onclick="window.ClockModule.clockFleetAskLaborOnly()"
      style="width:100%;min-height:48px;margin-top:10px;padding:12px;font-size:0.9rem;font-weight:600;border:2px dashed #cbd5e1;border-radius:12px;background:#f8fafc;color:#334155;cursor:pointer;">
      <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:18px;">person</span>No Equipment (Labor Only)
    </button>`;
}

function clockFleetAskChanged(sel) {
  const host = document.getElementById('clock-in-equipment');
  if (!host) return;
  host.dataset.eqTouched = '1';
  if (sel && sel.value) delete host.dataset.eqLaborOnly;
}

function clockFleetAskLaborOnly() {
  const host = document.getElementById('clock-in-equipment');
  if (!host) return;
  host.dataset.eqLaborOnly = '1';
  host.dataset.eqTouched = '1';
  const sel = document.getElementById('clock-in-fleet-ask');
  if (sel) sel.value = '';
  const btn = document.getElementById('clock-in-fleet-labor-only');
  if (btn) { btn.style.borderColor = '#64748b'; btn.style.background = '#e2e8f0'; }
}

// { claim, laborTap, touched } or null when no fleet ask is on screen.
function _clockReadFleetAsk() {
  const host = document.getElementById('clock-in-equipment');
  const sel = document.getElementById('clock-in-fleet-ask');
  if (!host || !sel) return null;
  const v = String(sel.value || '');
  return { claim: v ? [v] : [], laborTap: host.dataset.eqLaborOnly === '1', touched: host.dataset.eqTouched === '1' };
}

// mirrors jobboard.js _jbFleetAskProvenance — keep in sync.
function _clockFleetAskProvenance(source, ask, dayAssetIds) {
  const a = ask || { claim: [], laborTap: false, touched: false };
  const prov = _clockBuildAssetProvenance(source, {
    renderedAssetIds: [],
    selectedAssetIds: [],
    explicitLaborOnly: a.laborTap === true && a.claim.length === 0,
    defaulted: a.touched !== true
  }, dayAssetIds);
  prov.fleetOffered = true;
  prov.claimedAssetIds = (a.claim || []).map(String);
  return prov;
}

// mirrors jobboard.js _jbDriverClaimIds — keep in sync.
function _clockDriverClaimIds(entry) {
  const p = entry && entry.assetProvenance;
  if (!p || p.fleetOffered !== true || !Array.isArray(p.claimedAssetIds)) return [];
  if (!Array.isArray(entry.assetIds) || entry.assetIds.length > 0) return [];
  return p.claimedAssetIds.map(String).filter(Boolean);
}

// mirrors jobboard.js _jbFillDriverClaimedAssets — keep in sync. Off-day claims
// take the TASK-1635 flagged fill; on-day claims (dispatch listed the rig after
// clock-in) fill clean through the legacy body, skipping any the day fill
// already put on the sheet. Wrapped: runs while building the clock-out sheet.
async function _clockFillDriverClaimedAssets(entry) {
  try {
    const ids = _clockDriverClaimIds(entry);
    if (ids.length === 0) return;
    const jobDay = _clockResolveJobDayForEntry(entry);
    const expanded = jobDay ? _clockExpandedDayAssetIds(jobDay) : new Set();
    const offIds = ids.filter(id => !_clockTiedIdIsOnDay(id, expanded));
    await _clockFillTiedOffDayAssets(entry, offIds);
    if (!jobDay) return;
    const present = new Set((ClockState.clockOutEquipment || []).map(e => String(e.assetId)).filter(Boolean));
    const allowed = new Set();
    ids.filter(id => _clockTiedIdIsOnDay(id, expanded)).forEach(id => {
      const hit = _clockResolveAnyAsset(id);
      const rid = hit && hit.asset.id != null ? String(hit.asset.id) : id;
      if (present.has(rid) || present.has(id)) return;
      allowed.add(id);
      allowed.add(rid);
    });
    if (allowed.size > 0) await _clockAutoFillEquipmentAll(entry, allowed, jobDay);
  } catch (err) {
    console.warn('[Clock] TASK-1572 claimed-equipment fill failed (non-fatal):', err?.message);
  }
}

// TASK-586 — onchange handler for the #clock-job dropdown.
function onClockJobChange() {
  const sel = document.getElementById('clock-job');
  const jobId = sel?.value || '';
  if (sel) sel.dataset.autoPick = ''; // TASK-9865 — a hand pick is never re-decided
  _clockRenderInlineEquipment(jobId);
  // TASK-9865 — advisory "not on this job's crew today" line under the picker.
  try {
    const customerId = document.getElementById('clock-customer')?.value;
    const jobs = (customerId && customerId !== '__shop__') ? getRelevantJobs(customerId).jobs : [];
    _clockRenderCrewWarn('clock-job', 'clock-job-crew-warn', _clockPickerPerson(), jobs);
  } catch (e) { console.warn('[Clock] crew warning skipped:', e); }
}

// ============================================
// TASK-592 — SURFACE THE EQUIPMENT TIE (render-only reads)
// ============================================
// assetIds[] (TASK-491/558/586) was write-only: captured at clock-in and
// consumed at ticket build, but no admin surface ever showed WHO grabbed WHICH
// vehicle. The helpers below render it — chips on the Currently Clocked In
// panel and a read-only row in the edit-entry modal. Everything is gated on
// equipmentTie.enabled; kiosk/legacy entries with NO assetIds field render
// nothing (explicitly distinct from assetIds:[] labor-only). NO new listeners
// or writes: assetIds rides the existing clockEntries subscription and names
// resolve against the already-loaded loadRosterEquipment lists.

// Resolve ONE asset id to a display {name, icon} against ClockState's roster
// lists (legacy dual lookup: id OR unit OR name — Pitfall #5). Unknown ids
// (asset deleted/deactivated since clock-in) fall back to the raw id string.
function _clockResolveAssetDisplay(id) {
  const sid = String(id);
  let asset = _resolveFleetRef(sid, ClockState.trucks);
  let icon = 'local_shipping';
  if (!asset) { asset = _resolveFleetRef(sid, ClockState.trailers); if (asset) icon = 'rv_hookup'; }
  if (!asset) { asset = _resolveFleetRef(sid, ClockState.assets); if (asset) icon = 'construction'; }
  const name = (window.getUnitDisplay && asset ? window.getUnitDisplay(asset) : '') || asset?.name || asset?.unit || sid;
  return { name, icon };
}

// Map of assetId → number of OPEN (active) entries claiming it. Two OPEN
// entries on the same truck is almost always a mis-tap — both rows get a warn
// badge so the dispatcher can spot it and have the wrong driver re-clock
// (no assetIds edit path exists — see the edit-modal note below).
function _clockOpenAssetClaimCounts() {
  const counts = new Map();
  (ClockState.activeEntries || []).forEach(e => {
    if (!Array.isArray(e.assetIds)) return; // kiosk/legacy entries never claim
    e.assetIds.forEach(id => {
      const k = String(id);
      counts.set(k, (counts.get(k) || 0) + 1);
    });
  });
  return counts;
}

// Chip strip for one clock entry. Returns '' (zero chrome) when:
//   • equipmentTie is OFF (tenant gate),
//   • the entry has NO assetIds field (kiosk/legacy — pre-feature),
//   • assetIds is [] but the entry's jobDay has no equipment anyway (an empty
//     tag would be noise — the explicit "no equipment" signal is only
//     meaningful on a day that HAD equipment to pick). KNOWN LIMIT (COD
//     2026-06-12): _clockResolveJobDayForEntry only sees TODAY's jobDays, so a
//     stale/overnight [] entry shows no tag — conservative by design (the tag
//     is a hint, not an alarm; the double-claim badge doesn't depend on this).
// `claimCounts` (optional) adds the double-claim warn badge per chip.
// `opts.muted` renders flat gray chips (read-only contexts like the edit
// modal, so they don't look like tappable buttons — COD UX Lead 2026-06-12).
function _clockEquipChipsHtml(entry, claimCounts, opts) {
  if (!equipmentTieEnabled()) return '';
  if (!Array.isArray(entry?.assetIds)) return '';

  if (entry.assetIds.length === 0) {
    // Labor-only signal — subtle tag, not an alarm (legit for swampers).
    const jobDay = _clockResolveJobDayForEntry(entry);
    if (!jobDay || _clockJobDayAssetIds(jobDay).size === 0) return '';
    return `
      <div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:4px;">
        <span style="display:inline-flex;align-items:center;gap:3px;font-size:0.72rem;font-weight:500;color:var(--text-secondary);border:1px dashed var(--border-light);border-radius:999px;padding:2px 8px;">
          <span class="material-icons" style="font-size:13px;">person</span>No equipment
        </span>
      </div>`;
  }

  const muted = !!opts?.muted;
  const chips = entry.assetIds.map(id => {
    const { name, icon } = _clockResolveAssetDisplay(id);
    const dupe = !!claimCounts && (claimCounts.get(String(id)) || 0) > 1;
    if (dupe) {
      return `
        <span style="display:inline-flex;align-items:center;gap:3px;font-size:0.72rem;font-weight:600;color:#a16207;background:#fef3c7;border:1px solid #fcd34d;border-radius:999px;padding:2px 8px;" title="Two open entries claim this — likely a mis-tap">
          <span class="material-icons" style="font-size:13px;">warning</span>${escapeHtml(name)}
        </span>`;
    }
    const chipStyle = muted
      ? 'color:#475569;background:#f1f5f9;border:1px solid #e2e8f0;'
      : 'color:#1d4ed8;background:#eff6ff;border:1px solid #bfdbfe;';
    return `
      <span style="display:inline-flex;align-items:center;gap:3px;font-size:0.72rem;font-weight:600;${chipStyle}border-radius:999px;padding:2px 8px;">
        <span class="material-icons" style="font-size:13px;">${escapeHtml(icon)}</span>${escapeHtml(name)}
      </span>`;
  }).join('');

  return `<div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:4px;">${chips}</div>`;
}

function isClockModalOpen() {
  return ['modal-clock-out', 'modal-switch-job', 'modal-edit-clockentry', 'modal-workforce'
  ].some(id => { const m = document.getElementById(id); return m && !m.classList.contains('hidden'); });
}

// TASK-9579 — one person, more than one OPEN shift. Only one of them can be
// real: the older one is a missed clock-out (or a double tap on two devices) and
// keeps adding hours to the weekly grid until someone closes it. Every surface
// acts on the NEWEST (same rule as app.js togglePersonalClock and dispatch.js
// _loadSwitchJobContext) and FLAGS the rest — never closes or re-times one on
// its own (flag, don't cap: TASK-428 / TASK-1509).
function _clockShiftMs(e) {
  const v = e && e.clockIn;
  const ms = v?.toMillis ? v.toMillis() : (v?.toDate ? v.toDate().getTime() : (v ? new Date(v).getTime() : 0));
  return Number.isFinite(ms) ? ms : 0;
}
// { entry: newest open shift or null, others: the older open shifts, newest first }
function _clockOpenShiftsFor(personId, list) {
  const mine = (list || ClockState.activeEntries || [])
    .filter(e => e && e.personId === personId)
    .sort((a, b) => _clockShiftMs(b) - _clockShiftMs(a));
  return { entry: mine[0] || null, others: mine.slice(1) };
}
// personId -> open-shift count, only for people with MORE than one.
function _clockMultiOpenCounts(list) {
  const n = {};
  (list || ClockState.activeEntries || []).forEach(e => { if (e && e.personId) n[e.personId] = (n[e.personId] || 0) + 1; });
  Object.keys(n).forEach(k => { if (n[k] < 2) delete n[k]; });
  return n;
}
function _clockShiftStartLabel(e) {
  const ms = _clockShiftMs(e);
  if (!ms) return 'an unknown time';
  const tz = (window.AppState?.settings || window.AppState?.tenantData?.settings || {}).timezone || 'America/Chicago';
  try {
    return new Date(ms).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz });
  } catch (_) {
    return new Date(ms).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }
}

// stuck-shift-morning — Workforce → Hours: shifts that never clocked out and
// were closed late at the person's next clock-in (closedLate, last 7 days =
// the window recentEntries already holds). The hours were entered by the
// worker, not the office — so they are listed here until payroll checks them.
// Display only; Review opens the entry editor (same gate as the edit pencil).
function _clockClosedLateHtml(entries) {
  const list = (entries || []).filter(e => e && e.closedLate === true && e.status === 'completed')
    .sort((a, b) => (b.clockIn?.toMillis?.() || 0) - (a.clockIn?.toMillis?.() || 0));
  if (!list.length) return '';
  const tz = window.AppState?.settings?.timezone || 'America/Chicago';
  const fmt = (ts, o) => {
    const d = ts?.toDate?.() || (ts ? new Date(ts) : null);
    if (!d || isNaN(d.getTime())) return '';
    try { return d.toLocaleString('en-US', { ...o, timeZone: tz }); } catch (_) { return d.toLocaleString('en-US', o); }
  };
  const rows = list.map(e => {
    const person = getPersonForReport(e.personId);
    const by = e.lateCloseBy || {};
    const byTxt = by.self ? 'entered by them at their next clock-in' : `entered by ${escapeHtml(by.name || 'someone else')} at the next clock-in`;
    const idJs = window.escJsAttr ? window.escJsAttr(e.id) : String(e.id).replace(/[^A-Za-z0-9_-]/g, '');
    const where = [e.customerName, e.jobTitle].filter(Boolean).map(s => escapeHtml(s)).join(' — ');
    return `
        <div data-closed-late="${escapeHtml(String(e.id))}" style="display:flex;flex-wrap:wrap;align-items:center;gap:6px 10px;padding:8px 0;border-top:1px solid rgba(245,158,11,0.35);">
          <div style="flex:1 1 220px;min-width:0;">
            <div style="font-weight:700;color:var(--text-primary);">${escapeHtml(person?.name || e.personName || 'Unknown')}</div>
            <div style="font-size:0.8rem;color:var(--text-primary);">${escapeHtml(fmt(e.clockIn, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }))} – ${escapeHtml(fmt(e.clockOut, { hour: 'numeric', minute: '2-digit' }))} · ${(Number(e.totalHours) || 0).toFixed(1)}h${where ? ' · ' + where : ''}</div>
            <div style="font-size:0.75rem;color:var(--text-secondary);">Finish time ${byTxt}${e.ticketId ? ' — the ticket for this shift may still need its hours' : ''}.</div>
          </div>
          ${canEditEntry(e) ? `<button type="button" class="btn btn-secondary btn-sm" data-closed-late-review="${escapeHtml(String(e.id))}" onclick="event.stopPropagation();window.ClockModule.editClockEntry('${idJs}')" style="min-height:44px;padding:6px 12px;font-size:0.8rem;color:#92400e;border-color:#f59e0b;">Review</button>` : ''}
        </div>`;
  }).join('');
  return `
    <div class="wf-closed-late" role="region" aria-label="Shifts closed late" style="margin-bottom:var(--spacing-lg);padding:10px 12px;border-radius:10px;background:rgba(245,158,11,0.10);border:1px solid #f59e0b;">
      <div style="font-weight:700;color:#78350f;font-size:0.9rem;">
        <span class="material-icons" style="font-size:18px;vertical-align:-4px;margin-right:4px;color:#b45309;">history_toggle_off</span>
        ${list.length === 1 ? '1 shift' : list.length + ' shifts'} closed late — missed clock-out (last 7 days)
      </div>
      <div style="font-size:0.78rem;color:#78350f;margin:2px 0 4px;">The worker never clocked out and entered the finish time later. Check the hours before payroll.</div>
      ${rows}
    </div>`;
}

// Banner on top of a Currently Clocked In list ('' when nobody has two).
function _clockMultiOpenBannerHtml(multiOpen) {
  const n = Object.keys(multiOpen || {}).length;
  if (!n) return '';
  return `
      <div class="active-multi-open-banner" role="alert" style="margin-bottom:var(--spacing-sm);padding:8px 10px;border-radius:8px;background:rgba(245,158,11,0.12);border:1px solid #f59e0b;color:#78350f;font-size:0.8rem;line-height:1.35;">
        <span class="material-icons" style="font-size:16px;vertical-align:-3px;margin-right:4px;color:#b45309;">warning</span>
        <strong>${n === 1 ? '1 person has' : n + ' people have'} more than one open shift.</strong>
        Both keep adding hours until one is closed. After a double tap the <em>newest</em> is usually the accidental one — use <strong>Close older shift</strong> or <strong>Close newest shift</strong> on the one that should not be open.
      </div>`;
}
// Per-row chip, plus "Close older shift" on every row but the person's newest
// and (clock-double-tap) "Close newest shift" on the newest — after a double tap
// the newest is usually the accidental one, so the office must be able to pick
// EITHER. Each button only OPENS that entry in the editor (editClockEntry) — the
// office sets the real end time there; nothing is written from the list.
function _clockMultiOpenRowHtml(entry, multiOpen) {
  const n = multiOpen && entry ? multiOpen[entry.personId] : 0;
  if (!n) return '';
  const newest = _clockOpenShiftsFor(entry.personId).entry;
  const isNewest = !!newest && newest.id === entry.id;
  // COD AuthZ — offered only where the editor would open (same gate as the
  // row's own edit pencil); rules still decide the write.
  const canClose = canEditEntry(entry);
  const isOlder = !!newest && !isNewest && canClose;
  const idJs = window.escJsAttr ? window.escJsAttr(entry.id) : String(entry.id).replace(/[^A-Za-z0-9_-]/g, '');
  const btnStyle = 'min-height:44px;padding:6px 12px;font-size:0.8rem;color:#92400e;border-color:#f59e0b;';
  return `
        <div style="display:flex;flex-wrap:wrap;align-items:center;gap:6px;margin-top:4px;">
          <span data-multi-open="${escapeHtml(String(entry.personId))}" style="font-size:0.72rem;font-weight:700;color:#92400e;background:rgba(245,158,11,0.15);border-radius:999px;padding:2px 8px;" title="This person has ${n} open shifts at once. Only one can be real.">${n} open shifts${isNewest ? ' — newest' : ' — older one'}</span>
          ${isOlder ? `<button type="button" class="btn btn-secondary btn-sm" data-close-older="${escapeHtml(String(entry.id))}" onclick="event.stopPropagation();window.ClockModule.editClockEntry('${idJs}')" style="${btnStyle}">Close older shift</button>` : ''}
          ${(isNewest && canClose) ? `<button type="button" class="btn btn-secondary btn-sm" data-close-newest="${escapeHtml(String(entry.id))}" onclick="event.stopPropagation();window.ClockModule.editClockEntry('${idJs}')" style="${btnStyle}">Close newest shift</button>` : ''}
        </div>`;
}

function renderActiveList() {
  // TASK-360 — "Currently Clocked In" right-panel is workforce-gated. For
  // self-clock-only viewers the #active-list container isn't rendered into
  // the DOM by getClockTabHTML — early-return covers both cases (no
  // container = nothing to render).
  if (!hasWorkforceAccess()) return;
  const container = document.getElementById('active-list');
  if (!container) return;
  if (isClockModalOpen()) return;

  if (ClockState.activeEntries.length === 0) {
    if (!ClockState._entriesLoaded) {
      container.innerHTML = `
        <div style="text-align:center;padding:var(--spacing-xl);">
          <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
          <div style="color:var(--text-secondary);font-size:0.875rem;">Loading...</div>
        </div>
      `;
    } else {
      container.innerHTML = '<div class="empty-state-small">No one clocked in</div>';
    }
    return;
  }

  // TASK-592 — double-claim detection across all OPEN entries (computed once
  // per render, only when the tenant gate is on; OFF tenants skip entirely).
  const _claimCounts = equipmentTieEnabled() ? _clockOpenAssetClaimCounts() : null;

  // TASK-9579 — people with more than one open shift: a banner on top of the
  // list (this panel is where the office closes stuck shifts) plus, on each of
  // that person's rows, a chip, and on every row but the newest a button that
  // opens THAT entry in the editor to set its real end time. Display only.
  const _multiOpen = _clockMultiOpenCounts(ClockState.activeEntries);
  const _multiBanner = _clockMultiOpenBannerHtml(_multiOpen);

  container.innerHTML = _multiBanner + ClockState.activeEntries.map(entry => {
    // TASK-364 — fall back to _historicalPeopleCache for folks deactivated
    // mid-shift (their open clock entry still surfaces here for admin to resolve).
    const person = getPersonForReport(entry.personId);
    const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    // TASK-1509 — an open entry older than 24h is a forgotten clock-out, not a
    // shift. Rendering the raw elapsed number ("1,468h") reads as either a
    // glitch or a lie — on the prospect-facing demo it was both. DISPLAY-ONLY
    // (COD: flag, never cap — the TASK-428 lesson): clock-out, payroll rollup
    // and ticket build are untouched; active shifts were already excluded from
    // paid totals (bucketEntriesByWeek). Skip-safe: a corrupt/invalid clockIn
    // must not render NaN or abort the whole list — it falls into the stale
    // branch (a timestamp we can't read is by definition "needs review").
    const _elapsedMs = new Date() - clockIn;
    const _isStale = !Number.isFinite(_elapsedMs) || _elapsedMs > 24 * 60 * 60 * 1000;
    const duration = _isStale ? '' : formatDuration(_elapsedMs);
    const _staleChip = _isStale
      ? `<span class="active-entry-duration" style="color:#B45309;font-weight:600;" title="Open for more than 24 hours - probably a forgotten clock-out. Open the entry to set the real end time.">stale — needs review</span>`
      : '';
    // TASK-9579 — second open shift for this person.
    const _multiHtml = _clockMultiOpenRowHtml(entry, _multiOpen);

    return `
      <div class="active-entry" onclick="window.ClockModule.selectPerson('${entry.personId}')">
        <div style="display:flex;align-items:center;justify-content:space-between;">
          <div class="active-entry-name">${escapeHtml(person?.name || entry.personName)}</div>
          <button class="btn-icon" onclick="event.stopPropagation();window.ClockModule.showSwitchJobModal('${entry.id}')" title="Switch Job" style="color:var(--brand-blue);flex-shrink:0;">
            <span class="material-icons" style="font-size:18px;">swap_horiz</span>
          </button>
        </div>
        <div class="active-entry-meta">
          <span class="active-entry-time">${_isStale ? (Number.isFinite(clockIn.getTime()) ? clockIn.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '?') : clockIn.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
          ${_isStale ? _staleChip : `<span class="active-entry-duration">${duration}</span>`}
        </div>
        ${entry.customerName ? `<div class="active-entry-job">${escapeHtml(entry.customerName)}${entry.jobTitle ? ' - ' + escapeHtml(entry.jobTitle) : ''}</div>` : ''}
        ${_claimCounts ? _clockEquipChipsHtml(entry, _claimCounts) : ''}
        ${_multiHtml}
      </div>
    `;
  }).join('');
}

// ============================================
// CLOCK SUMMARY & HISTORY (founder-only enhancement)
// ============================================

function getWeekBounds(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  const diffToMon = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMon);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { monday, sunday };
}

function getAllCompletedEntries() {
  const seen = new Set();
  const all = [];
  for (const e of [...ClockState.recentEntries, ...ClockState.historyEntries]) {
    if (!seen.has(e.id)) {
      seen.add(e.id);
      all.push(e);
    }
  }
  return all;
}

function sumHours(entries) {
  return entries.reduce((sum, e) => sum + (e.totalHours || 0), 0);
}

function renderClockSummary() {
  const container = document.getElementById('clock-summary');
  if (!container) return;

  // TASK-618 — at-a-glance Today/Week/Month chips. Previously founder-only; now
  // also shown to the self-clock viewer (a signed-in clock member or PIN driver
  // looking at THEIR OWN hours). A non-founder WORKFORCE admin still gets no chips
  // here (they have the full Workforce hours tab) — only founder OR self-mode.
  if (!isFounder() && hasWorkforceAccess()) {
    container.innerHTML = '';
    return;
  }

  const now = new Date();
  const todayStart = new Date(now); todayStart.setHours(0, 0, 0, 0);
  const { monday, sunday } = getWeekBounds(now);

  // First day of current month
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  let entries = getAllCompletedEntries();

  // Filter by selected person if set
  if (ClockState.selectedPerson) {
    entries = entries.filter(e => e.personId === ClockState.selectedPerson);
  }

  // TASK-618 — defense in depth, mirrors renderRecentList:5584. A self-clock
  // viewer must NEVER see anyone else's totals. If `selectedPerson` raced to
  // null (dropdown reset, reload) we still force-filter to the viewer's own
  // personId; if they aren't linked to a person, the chips collapse to empty
  // rather than silently totalling the whole tenant.
  if (!hasWorkforceAccess()) {
    const selfId = getSelfPersonId() || ClockState.authenticatedPersonId || window.AppState?.driverPersonId || null;
    if (selfId) {
      entries = entries.filter(e => e.personId === selfId);
    } else {
      container.innerHTML = '';
      return;
    }
  }

  const todayHours = sumHours(entries.filter(e => {
    const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
    return cin >= todayStart;
  }));

  const weekHours = sumHours(entries.filter(e => {
    const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
    return cin >= monday && cin <= sunday;
  }));

  const periodHours = sumHours(entries.filter(e => {
    const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
    return cin >= monthStart;
  }));

  container.innerHTML = `
    <div class="clock-stat-chip">
      <span class="clock-stat-label">Today</span>
      <span class="clock-stat-value">${todayHours.toFixed(2)} hrs</span>
    </div>
    <div class="clock-stat-chip">
      <span class="clock-stat-label">This Week</span>
      <span class="clock-stat-value">${weekHours.toFixed(2)} hrs</span>
    </div>
    <div class="clock-stat-chip">
      <span class="clock-stat-label">This Month</span>
      <span class="clock-stat-value">${periodHours.toFixed(2)} hrs</span>
    </div>
    ${hasWorkforceAccess() ? '' : `<div style="flex-basis:100%;font-size:0.7rem;color:var(--text-muted);margin-top:2px;">Payroll hours only — not your FMCSA Hours-of-Service log.</div>`}
  `;
}

function renderRecentList() {
  const container = document.getElementById('recent-list');
  if (!container) return;

  // Gate history behind PIN for non-founders
  if (ClockState.selectedPerson && !isFounder()) {
    const person = ClockState.people.find(p => p.id === ClockState.selectedPerson);
    // TASK-9941 F S2 — same wall as before (people with a name + phone code),
    // without working the code out; opened by a server-checked PIN.
    if (_clockNamePhoneCodeExists(person) && ClockState.authenticatedPersonId !== ClockState.selectedPerson) {
      container.innerHTML = '';
      const summaryEl = document.getElementById('clock-summary');
      if (summaryEl) summaryEl.innerHTML = '';
      const headerEl = document.getElementById('clock-history-header');
      if (headerEl) headerEl.innerHTML = '<h3>Enter PIN to view hours</h3>';
      // TASK-4642 (COD Code Reviewer) — this early return skips
      // renderHistoryMore(), so #clock-history-more keeps whatever was painted
      // for the PREVIOUS selection. It clears its two siblings above and left
      // this one stale. Harmless while the walk-back control was founder-only;
      // now that a workforce admin can hold one, that stale button would sit
      // under "Enter PIN to view hours" offering to load a walled person's
      // history into a #recent-list that was just blanked.
      //
      // 🔴 Clear ONLY the workforce walk-back. A blanket clear here is a
      // REGRESSION, caught by the browser lane and not by reading: a self-clock
      // member's own dropdown selection is their own personId, so this PIN wall
      // fires for them too — and their "My Hours" button (painted by an earlier
      // render, self-scoped, hard-pinned to their own personId) survives
      // precisely BECAUSE of the staleness. Wiping it took away a control that
      // works. The self-service arm is left exactly as it is today; so is the
      // PIN condition itself.
      const moreEl = document.getElementById('clock-history-more');
      if (moreEl && _clockCanLoadMoreHistory()) moreEl.innerHTML = '';
      return;
    }
  }

  // Also render the summary
  renderClockSummary();

  // Header text:
  //  - Self-clock view (no workforce access): always "Your Hours" / "Your
  //    Recent Activity" — never the awkward "Test's Hours" possessive of
  //    the viewer's own first name.
  //  - Workforce admin viewing a selected person: "{first}'s Hours" /
  //    "{first}'s Recent Activity".
  //  - Workforce admin, nobody selected: "Hours" / "Recent Activity".
  const selfMode = !hasWorkforceAccess();
  const statsHeaderEl = document.getElementById('clock-stats-header');
  const historyHeaderEl = document.getElementById('clock-history-header');

  if (selfMode) {
    if (statsHeaderEl) statsHeaderEl.innerHTML = '<h3>Your Hours</h3>';
    if (historyHeaderEl) historyHeaderEl.innerHTML = '<h3>Your Recent Activity</h3>';
  } else if (ClockState.selectedPerson) {
    const person = ClockState.people.find(p => p.id === ClockState.selectedPerson);
    const firstName = (person?.name || 'Employee').split(' ')[0];
    if (statsHeaderEl) statsHeaderEl.innerHTML = `<h3>${escapeHtml(firstName)}'s Hours</h3>`;
    if (historyHeaderEl) historyHeaderEl.innerHTML = `<h3>${escapeHtml(firstName)}'s Recent Activity</h3>`;
  } else {
    if (statsHeaderEl) statsHeaderEl.innerHTML = '<h3>Hours</h3>';
    if (historyHeaderEl) historyHeaderEl.innerHTML = '<h3>Recent Activity</h3>';
  }

  let entries = getAllCompletedEntries();

  // Filter by selected person
  if (ClockState.selectedPerson) {
    entries = entries.filter(e => e.personId === ClockState.selectedPerson);
  }

  // TASK-360 — Self-clock surface must NEVER show other people's recent
  // activity. Defense in depth: if `selectedPerson` got cleared (race,
  // dropdown reset, etc.) we still force-filter to memberAccess.personId.
  // If the viewer isn't linked to a person, the list collapses to empty.
  if (!hasWorkforceAccess()) {
    const selfId = getSelfPersonId();
    if (selfId) {
      entries = entries.filter(e => e.personId === selfId);
    } else {
      entries = [];
    }
  }

  // Sort descending by clockIn
  entries.sort((a, b) => {
    const aTime = (a.clockIn?.toDate?.() || new Date(a.clockIn)).getTime();
    const bTime = (b.clockIn?.toDate?.() || new Date(b.clockIn)).getTime();
    return bTime - aTime;
  });

  if (entries.length === 0) {
    container.innerHTML = '<div class="empty-state-recent"><span class="material-icons">schedule</span><div class="empty-state-recent-title">No recent shifts</div><div class="empty-state-recent-desc">Once you clock in or out, your activity will show up here.</div></div>';
    renderHistoryMore();
    return;
  }

  // Group by day. TASK-4642 — this was `if (!isFounder())` too, and it is the
  // reason fixing loadMoreHistory() alone would have shipped a DEAD BUTTON:
  // a non-founder falls into the flat branch below, which re-filters `entries`
  // down to `clockIn >= todayStart`, so every week loadMoreHistory() appended
  // to ClockState.historyEntries was discarded before it could paint. The
  // control would have spun, succeeded, and changed nothing on screen.
  //
  // Keyed to the SAME predicate as the control itself so the two cannot
  // disagree: the grouped multi-day feed renders for exactly the viewers who
  // are allowed to load more days. Everyone else keeps the flat today-only
  // list and reaches their own history through "My Hours".
  if (!_clockCanLoadMoreHistory()) {
    // Self-clock / non-workforce: today only, flat list (original behavior)
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const todayEntries = entries.filter(e => {
      const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
      return cin >= todayStart;
    });
    if (todayEntries.length === 0) {
      container.innerHTML = '<div class="empty-state-recent"><span class="material-icons">schedule</span><div class="empty-state-recent-title">No recent shifts</div><div class="empty-state-recent-desc">Once you clock in or out, your activity will show up here.</div></div>';
      renderHistoryMore(); // TASK-451 — still surface "View Past Timesheets" when today is empty
      return;
    }
    container.innerHTML = todayEntries.slice(0, 10).map(entry => renderEntryCard(entry)).join('');
    renderHistoryMore(); // TASK-451 — append the driver's "View Past Timesheets" entry point
    return;
  }

  // Workforce-class: group by day with day headers
  const groups = new Map();
  for (const entry of entries) {
    const cin = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    const dayKey = cin.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    if (!groups.has(dayKey)) groups.set(dayKey, []);
    groups.get(dayKey).push(entry);
  }

  // TASK-9867 — drop picks that left the list (a load-more/refresh re-render
  // keeps the rest ticked), then the sticky "N selected · Move to job…" bar.
  if (_clockMoveSel.ids.size) {
    const present = new Set(entries.map(e => e.id));
    _clockMoveSel.ids = new Set([..._clockMoveSel.ids].filter(id => present.has(id)));
    if (!_clockMoveSel.ids.size) _clockMoveSel.dayKey = null;
  }
  let html = _clockMoveBarHtml();
  const today = new Date();
  const todayKey = today.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const yesterdayKey = yesterday.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });

  for (const [dayKey, dayEntries] of groups) {
    const dayDate = dayEntries[0].clockIn?.toDate?.() || new Date(dayEntries[0].clockIn);
    const dayTotal = sumHours(dayEntries);

    let dayLabel;
    if (dayKey === todayKey) {
      dayLabel = 'Today';
    } else if (dayKey === yesterdayKey) {
      dayLabel = 'Yesterday';
    } else {
      dayLabel = dayDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }

    html += `
      <div class="day-group-header">
        <span class="day-group-label">${dayLabel}${_payrollExportedDayChipHtml(dayEntries)}</span>
        <span class="day-group-total">${dayTotal.toFixed(2)} hrs</span>
      </div>
    `;

    for (const entry of dayEntries) {
      html += renderEntryCard(entry);
    }
  }

  container.innerHTML = html;
  renderHistoryMore();
  _refreshPayLockBadges();
  _clockRefreshPendingRequests(false); // TASK-9867 — "a Claude fix is waiting" chips
}

// TASK-9443 slice 3 — "Payroll exported" chip on a Workforce day header whose
// shifts sit in a period whose payroll was already exported, so the office sees
// it BEFORE opening a shift (slice 1 asks again at Save). DISPLAY ONLY, soft
// lock (Chris 2026-09-29): nothing is refused. Workforce-class list only (this
// grouped branch is gated by _clockCanLoadMoreHistory), never the driver views.
// Unknown lookup state => no chip (the edit warning still asks on unknown).
function _payrollExportedDayChipHtml(dayEntries) {
  try {
    const PL = window.PayrollRunLock;
    if (!PL || typeof PL.exportedRunForAny !== 'function') return '';
    if (typeof PL.lookupOk === 'function' && !PL.lookupOk()) return '';
    const ms = (dayEntries || []).map(e => {
      const d = e && (e.clockIn?.toDate?.() || (e.clockIn ? new Date(e.clockIn) : null));
      return d ? d.getTime() : NaN;
    });
    const run = PL.exportedRunForAny(PL.getRuns(), ms);
    if (!run) return '';
    const c = PL.exportedChipText(run);
    const escA = window.escAttr || escapeHtml;
    return ` <span class="payroll-exported-chip" data-payroll-exported="${escA(String(run.key || ''))}" title="${escA(c.title)}" `
      + `style="display:inline-flex;align-items:center;gap:3px;margin-left:8px;font-size:0.72rem;font-weight:600;color:#92400E;background:#FEF3C7;border:1px solid #F59E0B;border-radius:999px;padding:1px 8px;vertical-align:middle;">`
      + `<span class="material-icons" aria-hidden="true" style="font-size:13px;">lock_open</span>${escapeHtml(c.label)}</span>`;
  } catch (_) { return ''; }
}

// Fetch the exported periods for the chip, at most once a minute (the
// PayrollRunLock cache is 60s; a FAILING lookup is not cached there, so this
// throttle is what stops a snapshot-driven re-render from calling the callable
// on every paint). Repaints only when what the chips would show changed.
let _payLockBadgeFp = null;
let _payLockBadgeAskedAt = 0;
let _payLockBadgeTid = null;
function _refreshPayLockBadges() {
  try {
    const PL = window.PayrollRunLock;
    if (!PL || typeof PL.refresh !== 'function' || typeof PL.runsFingerprint !== 'function') return;
    if (!_clockCanLoadMoreHistory()) return;
    // The throttle belongs to ONE company: a switch inside the minute must
    // still look up the new company's periods (COD Code Reviewer).
    const tid = window.AppState?.tenant || null;
    if (tid !== _payLockBadgeTid) { _payLockBadgeTid = tid; _payLockBadgeAskedAt = 0; _payLockBadgeFp = null; }
    const now = Date.now();
    if (now - _payLockBadgeAskedAt < 60 * 1000) return;
    _payLockBadgeAskedAt = now;
    PL.refresh(false).then(() => {
      const ok = typeof PL.lookupOk === 'function' ? PL.lookupOk() : false;
      const fp = (ok ? 'ok:' : 'unknown:') + PL.runsFingerprint(PL.getRuns());
      if (fp === _payLockBadgeFp) return;
      _payLockBadgeFp = fp;
      renderRecentList();
    }).catch(() => { /* display only */ });
  } catch (_) { /* display only: never break the Hours list */ }
}

// ============================================
// TASK-9867 — a Claude request waiting on a shift (chip + edit-modal warning)
// ============================================
// TITS ZL 10/14: Chris's Claude had a move pending on Mike's shift while Jess
// was heading to Workforce to fix the same shift by hand — nothing told her.
// mcpProposals is server-only (firestore.rules get/list: false), so the
// Workforce list asks listMcpProposals({ clockPending: true }): EVERY
// requester's pending clock requests, entry id -> { proposalId, kind, to,
// requester, mine, canReject }. Throttled like the payroll chip
// (_refreshPayLockBadges); a failed read shows nothing (never a false chip).
// Workforce-class viewers only — the callable refuses anyone else.
const PENDING_REQ_REFRESH_MS = 30 * 1000;
let _clockPendingReq = { byEntry: {}, tid: null, askedAt: 0, fp: '', inflight: null };
function _clockPendingFor(entryId) {
  return (entryId && _clockPendingReq.byEntry && _clockPendingReq.byEntry[entryId]) || null;
}
// Pure. What the pending request would do to this shift, in the chip's words.
function _clockPendingKindText(r) {
  const k = r && r.kind;
  if (k === 'move_clock_time' || (k === 'clock_out_person' && r.to)) return `Move to ${r.to || 'another job'}`;
  if (k === 'clock_out_person') return 'Clock-out';
  if (k === 'edit_clock_times') return 'Time change';
  if (k === 'split_clock_entry') return 'Split';
  return 'A change';
}
function _clockPendingChipHtml(entryId) {
  const r = _clockPendingFor(entryId);
  if (!r) return '';
  const escA = window.escAttr || escapeHtml;
  const who = r.mine ? 'you' : (r.requester || 'someone');
  return `<span class="clock-pending-chip" data-pending-proposal="${escA(String(r.proposalId || ''))}" title="${escA(`A Claude request on this shift is waiting for approval in Account → Connect Claude. Asked by ${who}.`)}" `
    + `style="display:inline-flex;align-items:center;gap:4px;margin-top:4px;font-size:0.72rem;font-weight:600;color:#92400E;background:#FEF3C7;border:1px solid #F59E0B;border-radius:999px;padding:1px 8px;max-width:100%;">`
    + `<span class="material-icons icon-dd-ai" aria-hidden="true" style="font-size:13px;"></span><span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(_clockPendingKindText(r))} waiting for approval (asked by ${escapeHtml(who)})</span></span>`;
}
async function _clockRefreshPendingRequests(force) {
  try {
    if (!_clockCanLoadMoreHistory()) return;
    if (window.AppState?.driverMode || window.AppState?.driverModePin || window.AppState?.isPublicAccess) return;
    if (ClockState.tenantId === 'demo' || ClockState.tenantId === 'demo-tenant') return;
    const tid = window.AppState?.tenant || ClockState.tenantId || null;
    if (tid !== _clockPendingReq.tid) _clockPendingReq = { byEntry: {}, tid, askedAt: 0, fp: '', inflight: null };
    const now = Date.now();
    if (!force && now - _clockPendingReq.askedAt < PENDING_REQ_REFRESH_MS) return;
    if (_clockPendingReq.inflight) return _clockPendingReq.inflight;
    _clockPendingReq.askedAt = now;
    _clockPendingReq.inflight = (async () => {
      try {
        const res = await httpsCallable(functions, 'listMcpProposals')({ clockPending: true });
        if (tid !== _clockPendingReq.tid) return;
        const byEntry = (res?.data?.ok && res.data.byEntry && typeof res.data.byEntry === 'object') ? res.data.byEntry : {};
        const fp = JSON.stringify(Object.keys(byEntry).sort().map(k => [k, byEntry[k].proposalId]));
        _clockPendingReq.byEntry = byEntry;
        if (fp !== _clockPendingReq.fp) {
          _clockPendingReq.fp = fp;
          renderRecentList();
        }
      } catch (_) {
        // no module / offline / old server: nothing to show. Keep what we had.
      } finally {
        _clockPendingReq.inflight = null;
      }
    })();
    return _clockPendingReq.inflight;
  } catch (_) { /* display only */ }
}
// Edit-modal warning: the shift already has a Claude change waiting. Saving by
// hand makes that request refuse at approve (the gateway's fingerprint guard),
// so the person is told — and, when it is theirs or they own the company,
// offered to withdraw it here (decideMcpProposal reject, the same callable the
// Account card uses).
function _clockPendingWarnHtml(entryId) {
  const r = _clockPendingFor(entryId);
  if (!r) return '';
  const escJ = window.escJsAttr || escapeHtml;
  const who = r.mine ? 'you' : (r.requester || 'someone else');
  const when = r.createdAtMs ? ` at ${new Date(r.createdAtMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : '';
  return `<div id="edit-clock-pending-warn" role="status" style="display:flex;align-items:flex-start;gap:8px;background:#FFFBEB;border:1px solid #F59E0B;border-radius:8px;padding:8px 12px;font-size:0.85rem;color:var(--text-primary);line-height:1.35;">
    <span class="material-icons icon-dd-ai" aria-hidden="true" style="font-size:18px;color:#B45309;flex-shrink:0;"></span>
    <span style="flex:1;min-width:0;"><strong>${escapeHtml(_clockPendingKindText(r))} is waiting for approval</strong> on this shift — asked by ${escapeHtml(who)} through Claude${escapeHtml(when)}. Saving a change here makes that request fail when it is approved.
      ${r.canReject ? `<button type="button" class="btn btn-secondary btn-sm" style="display:inline-block;margin-top:6px;min-height:36px;padding:4px 10px;font-size:0.8rem;" onclick="window.ClockModule.rejectPendingClockRequest('${escJ(entryId)}')">Reject that request</button>` : `<span style="display:block;margin-top:4px;color:var(--text-secondary);">Only ${escapeHtml(r.requester || 'the requester')} or the owner can reject it.</span>`}
    </span></div>`;
}
async function rejectPendingClockRequest(entryId) {
  const r = _clockPendingFor(entryId);
  if (!r || !r.proposalId) return;
  const ok = typeof window.showConfirm === 'function'
    ? await window.showConfirm('Reject the pending Claude request?', `${_clockPendingKindText(r)} on this shift, asked by ${r.mine ? 'you' : (r.requester || 'someone else')}, will be rejected. Nothing on the shift changes.`, { confirmText: 'Reject request', cancelText: 'Keep it', danger: true })
    : confirm('Reject the pending Claude request on this shift?');
  if (!ok) return;
  try {
    const res = await httpsCallable(functions, 'decideMcpProposal')({ proposalId: r.proposalId, decision: 'reject' });
    if (!res?.data || (res.data.status && res.data.status !== 'rejected')) throw new Error('Could not reject it');
    delete _clockPendingReq.byEntry[entryId];
    document.getElementById('edit-clock-pending-warn')?.remove();
    window.showToast?.('Claude request rejected — this shift is yours to edit.', 'success');
    try { window.ApprovalsBadge?.refresh?.({ force: true }); } catch (_) { /* cosmetic */ }
    renderRecentList();
  } catch (e) {
    window.showToast?.(e?.message || 'Could not reject that request. Open Account → Connect Claude.', 'error');
  }
}

// ============================================
// TASK-9867 — move several finished shifts to one job in ONE action
// ============================================
// Workforce-class only (hasWorkforceAccess + canEditEntry). Pick finished
// shifts from ONE day (the crew's day — a wrong-job fix is a same-day thing),
// "Move to job…" once, read every person's old → new in the sheet, then each
// shift goes through the SAME gateway op a single Workforce edit uses
// (editClockEntry: times pinned to the stored ones, fingerprint-guarded so a
// shift edited since the list painted refuses) — one audit row per shift,
// ticket lines follow (repoint). An invoiced/locked ticket refuses that shift
// and the rest still move; every outcome is listed, never a silent skip.
let _clockMoveSel = { ids: new Set(), dayKey: null };
function _clockEntryDayKey(entry) {
  const cin = entry?.clockIn?.toDate?.() || (entry?.clockIn ? new Date(entry.clockIn) : null);
  if (!cin || isNaN(cin.getTime())) return null;
  return cin.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
}
// Pure. Finished, editable by this viewer, workforce-class — the shifts the
// checkbox is drawn on.
function _clockMoveSelectable(entry, canEdit, workforce) {
  if (!entry || !entry.id || !entry.clockOut) return false;
  if (entry.status === 'deleted' || entry.status === 'voided' || entry.deleted === true || entry.voided === true) return false;
  return !!(workforce && canEdit);
}
// Pure. Add/remove an id under the one-day rule. Returns { ids, dayKey, refused }.
function clockMoveSelectionNext(sel, entryId, dayKey, on) {
  const ids = new Set(sel && sel.ids ? sel.ids : []);
  let day = sel && sel.dayKey ? sel.dayKey : null;
  if (!on) {
    ids.delete(entryId);
    if (!ids.size) day = null;
    return { ids, dayKey: day, refused: false };
  }
  if (!dayKey) return { ids, dayKey: day, refused: true };
  if (ids.size && day && day !== dayKey) return { ids, dayKey: day, refused: true };
  ids.add(entryId);
  return { ids, dayKey: dayKey, refused: false };
}
function _clockFindEntry(entryId) {
  return getAllCompletedEntries().find(e => e.id === entryId)
    || Object.values(ClockState.weeklyCache || {}).flat().find(e => e && e.id === entryId) || null;
}
function toggleMovePick(entryId, on) {
  const entry = _clockFindEntry(entryId);
  const next = clockMoveSelectionNext(_clockMoveSel, entryId, _clockEntryDayKey(entry), !!on);
  if (next.refused) {
    window.showToast?.('Pick shifts from one day at a time.', 'warning');
    const box = document.querySelector(`.clock-move-pick[data-entry-id="${CSS.escape(String(entryId))}"]`);
    if (box) box.checked = false;
    return;
  }
  _clockMoveSel = { ids: next.ids, dayKey: next.dayKey };
  _clockRenderMoveBar();
}
function clearMovePick() {
  _clockMoveSel = { ids: new Set(), dayKey: null };
  document.querySelectorAll('.clock-move-pick').forEach(b => { b.checked = false; });
  _clockRenderMoveBar();
}
function _clockMoveBarHtml() {
  const n = _clockMoveSel.ids.size;
  const escA = window.escAttr || escapeHtml;
  return `<div id="clock-move-bar" role="toolbar" aria-label="Selected shifts" style="${n ? '' : 'display:none;'}position:sticky;top:0;z-index:2;display:${n ? 'flex' : 'none'};align-items:center;gap:10px;flex-wrap:wrap;background:var(--bg-card);border:1px solid var(--brand-blue);border-radius:8px;padding:8px 12px;margin:0 0 8px;box-shadow:0 2px 4px rgba(0,0,0,0.08);">
      <span style="font-weight:600;color:var(--text-primary);">${n} shift${n === 1 ? '' : 's'} selected${_clockMoveSel.dayKey ? ` <span style="font-weight:400;color:var(--text-secondary);">· ${escapeHtml(_clockMoveSel.dayKey)}</span>` : ''}</span>
      <button type="button" class="btn btn-primary btn-sm" style="min-height:40px;" onclick="window.ClockModule.openMoveEntriesModal()" title="${escA('Move every selected shift to one job or the shop — one save, each shift audited')}">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;">drive_file_move</span> Move to job…
      </button>
      <button type="button" class="btn btn-secondary btn-sm" style="min-height:40px;" onclick="window.ClockModule.clearMovePick()">Clear</button>
    </div>`;
}
function _clockRenderMoveBar() {
  const bar = document.getElementById('clock-move-bar');
  if (!bar) return;
  bar.outerHTML = _clockMoveBarHtml();
}
function _clockMoveTargetFromForm() {
  const custId = document.getElementById('move-entries-customer')?.value || '';
  const jobId = document.getElementById('move-entries-job')?.value || '';
  const customer = custId ? ClockState.customers.find(c => c.id === custId) : null;
  const job = jobId ? ClockState.allJobs.find(j => j.id === jobId) : null;
  return {
    customerId: custId || null,
    customerName: customer ? (customer.name || null) : null,
    internal: !!(customer && customer.internalTimeOnly === true),
    jobId: jobId || null,
    jobTitle: job ? (job.title || job.name || null) : null,
    label: job ? (typeof clockJobShortLabel === 'function' ? `${clockJobShortLabel(job)} · ${job.title || job.name || ''}` : (job.title || job.name || jobId)) : (customer ? customer.name : ''),
  };
}
// Pure. The gateway payload for ONE shift: times pinned to what is stored,
// the target, and the fingerprint the gateway re-checks inside its transaction
// (clockEntryFingerprint, mirrored field for field).
function clockMovePayloadFor(entry, target) {
  const ms = (v) => (v && typeof v.toMillis === 'function') ? v.toMillis() : (v && typeof v.toDate === 'function' ? v.toDate().getTime() : (v ? new Date(v).getTime() : null));
  const inMs = ms(entry.clockIn); const outMs = ms(entry.clockOut);
  return {
    entryId: entry.id,
    clockIn: new Date(inMs).toISOString(),
    clockOut: outMs != null ? new Date(outMs).toISOString() : null,
    customerId: target.customerId,
    customerName: target.customerName || (target.customerId ? null : 'Shop'),
    jobId: target.jobId || null,
    jobTitle: target.jobTitle || null,
    expectedEntry: {
      status: entry.status == null ? null : String(entry.status),
      clockInMs: inMs, clockOutMs: outMs,
      updatedAtMs: ms(entry.updatedAt),
      customerId: entry.customerId || null, jobId: entry.jobId || null,
      ticketId: entry.ticketId || null, personId: entry.personId || null,
      qbSynced: entry.qbSynced === true,
    },
  };
}
// Pure. Which selected shifts the move would touch, and which are already there.
function clockMovePlan(entries, target) {
  const plan = [];
  for (const e of entries || []) {
    if (!e) continue;
    const same = (e.customerId || null) === (target.customerId || null) && (e.jobId || null) === (target.jobId || null);
    plan.push({ entry: e, skip: same ? 'already there' : null });
  }
  return plan;
}
function _clockMoveRowsHtml(plan, target) {
  return plan.map(({ entry, skip }) => {
    const person = ClockState.people.find(p => p.id === entry.personId);
    const cin = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    const cout = entry.clockOut?.toDate?.() || new Date(entry.clockOut);
    const span = window.Fmt?.shiftSpan ? window.Fmt.shiftSpan(cin, cout).rangeHtml : `${cin.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}–${cout.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    const from = entry.jobTitle ? `${entry.jobTitle}${entry.customerName ? ` (${entry.customerName})` : ''}` : (entry.customerName || 'no customer or job');
    return `<div data-move-row="${(window.escAttr || escapeHtml)(entry.id)}" style="display:flex;flex-wrap:wrap;gap:4px 10px;align-items:baseline;padding:8px 0;border-top:1px solid var(--border-light);${skip ? 'opacity:0.6;' : ''}">
        <span style="flex:1 1 160px;min-width:0;font-weight:600;color:var(--text-primary);">${escapeHtml(person?.name || entry.personName || 'Unknown')}</span>
        <span style="color:var(--text-secondary);font-size:0.85rem;">${span} · ${(Number(entry.totalHours) || 0).toFixed(2)} h</span>
        <span style="flex-basis:100%;font-size:0.85rem;color:var(--text-primary);"><span style="color:var(--text-secondary);">${escapeHtml(from)}</span> <span aria-hidden="true">→</span> <strong>${escapeHtml(target.label || '—')}</strong>${skip ? ` <span style="color:#92400E;">· ${escapeHtml(skip)}, left as is</span>` : ''}</span>
        <span class="move-row-outcome" style="flex-basis:100%;font-size:0.85rem;"></span>
      </div>`;
  }).join('');
}
function openMoveEntriesModal() {
  if (!hasWorkforceAccess()) return;
  const entries = [..._clockMoveSel.ids].map(_clockFindEntry).filter(Boolean);
  if (!entries.length) { window.showToast?.('Pick the shifts first.', 'warning'); return; }
  let modal = document.getElementById('modal-move-entries');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-move-entries';
    modal.className = 'modal';
    document.body.appendChild(modal);
  }
  // Default target: the customer most of the picked shifts are on (a wrong-job
  // fix is nearly always a sister job of the same customer) — job left blank.
  const counts = new Map();
  entries.forEach(e => { if (e.customerId) counts.set(e.customerId, (counts.get(e.customerId) || 0) + 1); });
  const _topCust = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || '';
  // TASK-10250 (COD r2) — never default the move TARGET to an Inactive / merged-away
  // customer: it would stay offered for every picked shift, including active ones.
  const _topDoc = (ClockState.customers || []).find(c => c && c.id === _topCust);
  const defaultCust = _topDoc && window.PickerGroups.isInactiveCustomer(_topDoc) ? '' : _topCust;
  const custOptions = _clockCustomerOptionsHtml(ClockState.customers, {
    selectedId: defaultCust,
    optionAttrs: c => (c.internalTimeOnly === true ? ' data-internal-time="1"' : ''),
  });
  modal.innerHTML = `
    <div class="modal-content" style="max-width:520px;">
      <div class="modal-header">
        <h3>Move ${entries.length} shift${entries.length === 1 ? '' : 's'}</h3>
        <button class="btn-icon" onclick="window.ClockModule.closeMoveEntriesModal()" aria-label="Close"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="display:flex;flex-direction:column;gap:var(--spacing-md);">
        <div style="font-size:0.85rem;color:var(--text-secondary);">Times do not change. Each shift's ticket line moves with it; an invoiced or locked ticket refuses that shift and the others still move. Every shift gets its own entry in the edit history.</div>
        <div class="form-group" style="margin:0;">
          <label for="move-entries-customer">Customer</label>
          <select id="move-entries-customer" class="form-control" onchange="window.ClockModule.onMoveEntriesCustomerChange()">
            <option value="">— pick a customer —</option>
            ${custOptions}
          </select>
          ${_clockCustomerHintHtml(ClockState.customers)}
        </div>
        <div class="form-group" id="move-entries-job-group" style="margin:0;${defaultCust ? '' : 'display:none;'}">
          <label for="move-entries-job">Job</label>
          <select id="move-entries-job" class="form-control" onchange="window.ClockModule.onMoveEntriesCustomerChange(true)">
            ${defaultCust ? buildRelevantJobOptions(defaultCust, null, '— pick a job —') : '<option value="">— pick a job —</option>'}
          </select>
        </div>
        <div id="move-entries-rows"></div>
        <div id="move-entries-note" style="font-size:0.85rem;color:#92400E;"></div>
      </div>
      <div class="modal-footer" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
        <button class="btn btn-secondary btn-sm" onclick="window.ClockModule.closeMoveEntriesModal()" style="padding:6px 12px;">Cancel</button>
        <button id="btn-move-entries" class="btn btn-primary btn-sm" onclick="window.ClockModule.submitMoveEntries()" style="padding:6px 14px;min-height:44px;" disabled>Move ${entries.length} shift${entries.length === 1 ? '' : 's'}</button>
      </div>
    </div>`;
  modal.dataset.entryIds = JSON.stringify(entries.map(e => e.id));
  modal.classList.remove('hidden');
  onMoveEntriesCustomerChange(true);
}
function closeMoveEntriesModal() {
  const modal = document.getElementById('modal-move-entries');
  if (!modal || modal.dataset.busy === '1') return;
  modal.classList.add('hidden');
}
function onMoveEntriesCustomerChange(keepJob) {
  const modal = document.getElementById('modal-move-entries');
  if (!modal) return;
  const custId = document.getElementById('move-entries-customer')?.value || '';
  const jobGroup = document.getElementById('move-entries-job-group');
  const jobSel = document.getElementById('move-entries-job');
  if (!keepJob && jobSel) {
    jobSel.innerHTML = custId ? buildRelevantJobOptions(custId, null, '— pick a job —') : '<option value="">— pick a job —</option>';
  }
  if (jobGroup) jobGroup.style.display = custId ? '' : 'none';
  const target = _clockMoveTargetFromForm();
  const ids = (() => { try { return JSON.parse(modal.dataset.entryIds || '[]'); } catch (_) { return []; } })();
  const entries = ids.map(_clockFindEntry).filter(Boolean);
  const plan = clockMovePlan(entries, target);
  const rows = document.getElementById('move-entries-rows');
  if (rows) rows.innerHTML = _clockMoveRowsHtml(plan, target);
  const note = document.getElementById('move-entries-note');
  const btn = document.getElementById('btn-move-entries');
  // A job is the point of this action; the shop (internal-time customer) is
  // the one target that has no job.
  const ready = !!target.customerId && (!!target.jobId || target.internal);
  const toMove = plan.filter(p => !p.skip).length;
  if (note) note.textContent = !target.customerId ? '' : (!ready ? 'Pick the job these shifts belong on.' : (!toMove ? 'Every selected shift is already there.' : (target.internal ? 'Shop time has no ticket: each shift’s ticket line comes off the ticket it is on now.' : '')));
  if (btn) { btn.disabled = !(ready && toMove); btn.textContent = `Move ${toMove || entries.length} shift${(toMove || entries.length) === 1 ? '' : 's'}`; }
}
async function submitMoveEntries() {
  const modal = document.getElementById('modal-move-entries');
  const btn = document.getElementById('btn-move-entries');
  if (!modal || modal.dataset.busy === '1') return;
  if (!hasWorkforceAccess()) return;
  const target = _clockMoveTargetFromForm();
  if (!target.customerId || !(target.jobId || target.internal)) return;
  const ids = (() => { try { return JSON.parse(modal.dataset.entryIds || '[]'); } catch (_) { return []; } })();
  const entries = ids.map(_clockFindEntry).filter(Boolean);
  const plan = clockMovePlan(entries, target).filter(p => !p.skip);
  if (!plan.length) return;
  // TASK-9443 soft pay-period lock, once for all the shifts (confirmEditMany).
  try {
    const PL = window.PayrollRunLock;
    if (PL && typeof PL.confirmEditMany === 'function') {
      const msList = plan.map(p => (p.entry.clockIn?.toDate?.() || new Date(p.entry.clockIn)).getTime());
      if (!(await PL.confirmEditMany(msList, { what: `Moving ${plan.length} shift${plan.length === 1 ? '' : 's'}` }))) return;
    }
  } catch (_) { /* the lock informs, it never traps */ }
  modal.dataset.busy = '1';
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Moving…'; }
  modal.querySelectorAll('select').forEach(s => { s.disabled = true; });
  const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: GW_CLIENT_TIMEOUT_MS });
  const outcomes = [];
  for (const { entry } of plan) {
    const rowEl = modal.querySelector(`[data-move-row="${CSS.escape(String(entry.id))}"] .move-row-outcome`);
    const scope = 'move:' + entry.id;
    try {
      // Inside the try (COD Code Reviewer): a bad timestamp fails THIS row, never the batch.
      const payload = clockMovePayloadFor(entry, target);
      if (!payload.clockIn || payload.clockIn === 'Invalid Date' || !payload.clockOut) throw new Error('This shift has no usable clock-in / clock-out — fix it on its own first.');
      const result = await fn({ op: 'editClockEntry', tenantId: ClockState.tenantId, payload, opKey: _gwOpKey(scope, payload) });
      if (!result?.data?.ok) throw new Error('Gateway returned not-ok');
      _gwOpDone(scope);
      const u = result.data.updated || {};
      const apply = (e) => {
        if (e.id !== entry.id) return;
        e.customerId = (u.customerId !== undefined ? u.customerId : payload.customerId) || null;
        e.customerName = (u.customerName !== undefined ? u.customerName : payload.customerName) || null;
        e.jobId = (u.jobId !== undefined ? u.jobId : payload.jobId) || null;
        e.jobTitle = (u.jobTitle !== undefined ? u.jobTitle : payload.jobTitle) || null;
        if (u.ticketId !== undefined) e.ticketId = u.ticketId || null;
        if (u.ticketLineId !== undefined) e.ticketLineId = u.ticketLineId || null;
      };
      ClockState.recentEntries.forEach(apply);
      ClockState.historyEntries.forEach(apply);
      for (const list of Object.values(ClockState.weeklyCache || {})) list.forEach(apply);
      outcomes.push({ entryId: entry.id, ok: true });
      if (rowEl) rowEl.innerHTML = `<span style="color:#166534;"><span class="material-icons" style="font-size:14px;vertical-align:-2px;">check_circle</span> Moved</span>`;
    } catch (e) {
      const code = String(e?.code || e?.details?.code || '');
      const msg = /failed-precondition|permission-denied/i.test(code) || /failed-precondition|permission-denied/i.test(String(e?.message))
        ? (e?.message || 'Refused by the server')
        : (_gwIsDeadlineExceeded(e) ? 'Took too long — it may still have saved; check the entry before retrying.' : (e?.message || 'Could not move it'));
      outcomes.push({ entryId: entry.id, ok: false, reason: msg });
      if (rowEl) rowEl.innerHTML = `<span style="color:#991B1B;"><span class="material-icons" style="font-size:14px;vertical-align:-2px;">error</span> Not moved — ${escapeHtml(msg)}</span>`;
    }
  }
  modal.dataset.busy = '0';
  const moved = outcomes.filter(o => o.ok).length;
  const failed = outcomes.length - moved;
  if (btn) { btn.innerHTML = failed ? 'Done (some not moved)' : 'Done'; btn.disabled = false; btn.onclick = () => closeMoveEntriesModal(); }
  window.showToast?.(failed ? `${moved} of ${outcomes.length} moved to ${target.label}. ${failed} not moved — see the list.` : `${moved} shift${moved === 1 ? '' : 's'} moved to ${target.label}.`, failed ? 'warning' : 'success');
  _clockMoveSel = { ids: new Set(), dayKey: null };
  renderRecentList();
  const content = document.getElementById('workforce-content');
  if (content) content.innerHTML = getWorkforceContentHTML();
  renderActiveList();
  if (ClockState.detailPersonId && !document.getElementById('modal-workforce')?.classList.contains('hidden')) {
    showEmployeeDetail(ClockState.detailPersonId, false);
  }
}

function renderEntryCard(entry) {
  const person = ClockState.people.find(p => p.id === entry.personId);
  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
  const clockOut = entry.clockOut?.toDate?.() || new Date(entry.clockOut);
  const editable = canEditEntry(entry);
  const editAttr = editable ? `onclick="window.ClockModule.editClockEntry('${entry.id}')"` : '';
  const editClass = editable ? ' recent-entry-editable' : '';
  // TASK-9867 — the multi-select checkbox (workforce-class, finished shifts)
  // and the "a Claude fix is waiting" chip. The checkbox stops the card's
  // click so a tick never opens the edit modal.
  const pick = _clockMoveSelectable(entry, editable, hasWorkforceAccess() && _clockCanLoadMoreHistory());
  const escA = window.escAttr || escapeHtml;
  const pickHtml = pick
    ? `<label onclick="event.stopPropagation()" style="display:inline-flex;align-items:center;justify-content:center;min-width:44px;min-height:44px;margin:-12px 0 -12px -12px;cursor:pointer;vertical-align:middle;"><input type="checkbox" class="clock-move-pick" data-entry-id="${escA(entry.id)}" aria-label="Select this shift to move" ${_clockMoveSel.ids.has(entry.id) ? 'checked' : ''} onclick="event.stopPropagation();window.ClockModule.toggleMovePick('${(window.escJsAttr || escapeHtml)(entry.id)}', this.checked)" style="width:18px;height:18px;margin:0 8px 0 0;accent-color:var(--brand-blue);cursor:pointer;"></label>`
    : '';

  return `
    <div class="recent-entry${editClass}" ${editAttr}>
      <div class="recent-entry-name">${pickHtml}${escapeHtml(person?.name || entry.personName)}</div>
      <div class="recent-entry-time">
        ${window.Fmt.shiftSpan(clockIn, clockOut).rangeHtml}
      </div>
      <div class="recent-entry-meta-row">
        <span class="recent-entry-hours">${entry.totalHours?.toFixed(2) || '0.00'} hrs</span>
        ${entry.customerName ? `<span class="recent-entry-customer">${escapeHtml(entry.customerName)}</span>` : ''}
      </div>
      ${_clockPendingChipHtml(entry.id)}
    </div>
  `;
}

function renderHistoryMore() {
  const moreEl = document.getElementById('clock-history-more');
  if (!moreEl) return;

  // TASK-451 — Self-clock driver (no workforce access) gets a read-only
  // "View Past Timesheets" entry point here, at the bottom of the 7-day
  // Recent Activity feed. Workforce-class viewers keep the "Load previous
  // week" control instead. Anyone else gets nothing — there's nothing of
  // theirs to show.
  //
  // TASK-4642 — this arm was `if (!isFounder())` wrapping an inner
  // `!hasWorkforceAccess() && selfId`, so on the 85 non-founder tenants a
  // WORKFORCE ADMIN fell into the else and got NOTHING: not the walk-back
  // control (tenant-gated away) and not the self-service fallback (their own
  // workforce access disqualified them from it). Keying both arms off the same
  // capability closes that hole — every viewer now lands in exactly one of the
  // two branches. See _clockCanLoadMoreHistory().
  if (!_clockCanLoadMoreHistory()) {
    const selfId = getSelfPersonId() || ClockState.authenticatedPersonId || window.AppState?.driverPersonId || null;
    if (selfId) {
      moreEl.innerHTML = `
        <button class="btn-text clock-load-more" id="btn-my-timesheet" onclick="window.ClockModule.showMyTimesheet()">
          <span class="material-icons" style="font-size:16px;">schedule</span> My Hours (totals &amp; past periods)
        </button>
      `;
    } else {
      moreEl.innerHTML = '';
    }
    return;
  }

  // Workforce-class: determine the earliest entry we have
  const allEntries = getAllCompletedEntries();
  if (allEntries.length === 0) {
    moreEl.innerHTML = '';
    return;
  }

  moreEl.innerHTML = `
    <button class="btn-text clock-load-more" id="btn-load-more-history" onclick="window.ClockModule.loadMoreHistory()">
      <span class="material-icons" style="font-size:16px;">history</span> Load previous week
    </button>
  `;
}

async function loadMoreHistory() {
  // TASK-4642 — was `if (!isFounder()) return;`, a tenant check on a data load.
  // Now the capability the firestore.rules read arm actually keys on, and the
  // same predicate that already governs the un-scoped live 7-day subscription.
  // Exported on window.ClockModule, so this refuses out loud rather than
  // silently returning — a console caller should learn the boundary, not think
  // the function is broken.
  if (!_clockCanLoadMoreHistory()) {
    console.warn('[Clock] loadMoreHistory blocked — viewer lacks workforce access.');
    try {
      window.showToast?.('Past weeks are in the Workforce module. Use "My Hours" to see your own past periods.', 'error');
    } catch (_) { /* ignore */ }
    return;
  }

  const btn = document.getElementById('btn-load-more-history');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Loading...';
  }

  try {
    // Determine how far back to query
    let endDate;
    if (ClockState.historyLoadedUntil) {
      endDate = new Date(ClockState.historyLoadedUntil);
    } else {
      // Start from 7 days ago (where the subscription ends)
      endDate = new Date();
      endDate.setDate(endDate.getDate() - 7);
    }
    endDate.setHours(0, 0, 0, 0);

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 7); // load one more week

    // TASK-4642 — bounded. The 7-day window is the feature's pagination unit;
    // CLOCK_HISTORY_WEEK_LIMIT caps cardinality INSIDE it so a tenant-wide
    // week can never return an unbounded page. See the constant for sizing.
    const historyQuery = query(
      collection(db, 'tenants', ClockState.tenantId, 'clockEntries'),
      where('clockIn', '>=', Timestamp.fromDate(startDate)),
      where('clockIn', '<', Timestamp.fromDate(endDate)),
      orderBy('clockIn', 'desc'),
      // TASK-4642 (COD Code Reviewer) — LIMIT + 1, not LIMIT. Asking for exactly
      // the cap makes `size === cap` ambiguous: a week holding precisely 500
      // shifts is complete, but reads identically to one that was cut off, so a
      // plain `size >= cap` would cry truncation on a whole week. Fetching one
      // extra doc makes "was there more?" a fact rather than an inference; the
      // extra row is dropped below and never rendered.
      limit(CLOCK_HISTORY_WEEK_LIMIT + 1)
    );

    const snapshot = await getDocs(historyQuery);
    const truncated = snapshot.size > CLOCK_HISTORY_WEEK_LIMIT;
    const newEntries = snapshot.docs
      .slice(0, CLOCK_HISTORY_WEEK_LIMIT)
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(e => e.status === 'completed');

    // Append to history (deduped in getAllCompletedEntries)
    ClockState.historyEntries.push(...newEntries);
    ClockState.historyLoadedUntil = startDate;

    // A capped week drops the OLDEST rows in that window (orderBy is desc), so
    // say it rather than letting a shift vanish silently off a wage-hour
    // screen. The walk still advances — Workforce → Hours has the full week.
    if (truncated) {
      try {
        window.showToast?.(
          `That week has more than ${CLOCK_HISTORY_WEEK_LIMIT} shifts — showing the most recent ${CLOCK_HISTORY_WEEK_LIMIT}. Use Workforce → Hours for the full week.`,
          'warning'
        );
      } catch (_) { /* ignore */ }
    }

    renderRecentList();
  } catch (err) {
    console.error('[Clock] Error loading history:', err);
    window.showToast('Error loading history', 'error');
  }

  if (btn) {
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons" style="font-size:16px;">history</span> Load previous week';
  }
}

// ============================================
// TASK-451 — DRIVER "MY TIMESHEET" (read-only, self-only, lazy per-period)
// ============================================
//
// A separate, dead-simple read-only view of the SIGNED-IN person's OWN completed
// shifts, paged one pay period at a time, back ~12 months. Deliberately NOT a
// reuse of showEmployeeDetail (workforce-admin-only, edit/delete/CSV/per-diem
// chrome).
//
// AUTHZ (two layers, both real):
//  1. The query is hard-pinned `where('personId','==',SELF)`, so it can only
//     ever return THIS person's rows. SELF is resolved internally and the
//     function never accepts a personId argument.
//  2. The firestore clockEntries `read` rule (firestore.rules) allows reads to
//     any tenant member, BUT a driver-session token only satisfies the rule for
//     its own personId via isOwnClockEntry() (TASK-452 verified live) — so a
//     PIN/driver viewer physically cannot read anyone else's entries server-side.
// The self-resolve below is the UI half; the rule is the server half.

// How many periods back ≈ 12 months, by pay-period type.
function _myTimesheetMaxPeriodsBack() {
  switch (getPayPeriodType()) {
    case 'weekly':      return 52;
    case 'biweekly':    return 26;
    case 'semimonthly': return 24;
    case 'monthly':     return 12;
    default:            return 26; // safe default (biweekly)
  }
}

// Resolve the viewer's OWN personId. NEVER takes a personId argument.
function _myTimesheetSelfPersonId() {
  return getSelfPersonId()
    || ClockState.authenticatedPersonId
    || window.AppState?.driverPersonId
    || null;
}

async function showMyTimesheet() {
  const SELF = _myTimesheetSelfPersonId();
  if (!SELF) {
    try { window.showToast?.('Sign in or enter your PIN to view your timesheet.', 'error'); } catch (_) { /* ignore */ }
    return;
  }
  // TASK-562: PIN drivers reach this from the Job Board and never run initClock
  // (the ONLY place ClockState.tenantId is set), so hydrate it from the
  // token-derived AppState.tenant. Null-safe — the office path already set it.
  ClockState.tenantId = ClockState.tenantId || window.AppState?.tenant || null;
  // GUARD (COD Edge Case): if STILL unresolved, bail gracefully — never fire a
  // tenants/null/clockEntries query.
  if (!ClockState.tenantId) {
    try { window.showToast?.("Couldn't load your timesheet — please reopen the app.", 'error'); } catch (_) { /* ignore */ }
    return;
  }
  // TASK-562: PIN drivers don't run initClock's activeEntries subscription
  // (ClockState.activeEntries stays []), so a shift still running from a PRIOR
  // pay period wouldn't merge into the current-period view (_renderMyTimesheet
  // merges activeEntries only for the current period). Seed the viewer's OWN
  // active entries with a self-scoped query — same shape the Job Board uses
  // (jobboard.js clockQuery), and own-entry reads are allowed by the
  // clockEntries rule (isOwnClockEntry). Non-fatal on error.
  try {
    if (!ClockState.activeEntries || ClockState.activeEntries.length === 0) {
      const activeSnap = await getDocs(query(
        collection(db, 'tenants', ClockState.tenantId, 'clockEntries'),
        where('personId', '==', SELF),
        where('status', '==', 'active')
      ));
      if (!activeSnap.empty) ClockState.activeEntries = activeSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    }
  } catch (_) { /* live in-progress shift just won't pre-merge; same-period shifts still show via the period query */ }

  // Reset to the current period each time the view is opened fresh.
  ClockState.myTimesheetOffset = 0;
  // TASK-9195 — drop the "What I did" caches on a fresh open so a shift edited
  // since the last open re-reads. Within one open, paging periods reuses them.
  ClockState.myTimesheetDetail = {};
  ClockState.myTimesheetEntryIndex = {};

  let modal = document.getElementById('modal-my-timesheet');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-my-timesheet';
    modal.className = 'modal';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `
    <div class="modal-content modal-fullscreen" style="max-width:560px;display:flex;flex-direction:column;">
      <div class="modal-header">
        <h3>My Hours</h3>
        <button class="btn-icon" onclick="document.getElementById('modal-my-timesheet').classList.add('hidden')">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" id="my-timesheet-body" style="flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;">
        <div style="text-align:center;color:var(--text-muted);padding:24px;">Loading…</div>
      </div>
    </div>`;
  modal.classList.remove('hidden');

  await _renderMyTimesheet();
}

// (Re)render the current period for the signed-in person. Takes NO personId arg
// (TASK-451 hard rule) — always re-resolves SELF internally so it can never be
// steered to another person's id, even via an internal call path.
async function _renderMyTimesheet() {
  const SELF = _myTimesheetSelfPersonId();
  const body = document.getElementById('my-timesheet-body');
  if (!body || !SELF) return;

  body.innerHTML = '<div style="text-align:center;color:var(--text-muted);padding:24px;">Loading…</div>';

  const { start: periodStart, end: periodEnd } = getPayPeriodDates(ClockState.myTimesheetOffset);
  const isCurrent = ClockState.myTimesheetOffset === 0;
  const minOffset = -_myTimesheetMaxPeriodsBack();
  const atOldest = ClockState.myTimesheetOffset <= minOffset;

  // Same per-period query shape as showEmployeeDetail (~:2247), self-scoped, but
  // TASK-949 widens the window to the boundary weeks [weekStart(periodStart) ..
  // weekEnd(periodEnd)] so a workweek straddling a pay-period boundary is scored
  // on its FULL-week hours — the driver's OT band must match what payroll pays.
  // `entries` (display + periodTotal) stays period-bounded; `weekEntries` (the
  // wider set) feeds the cross-period OT helper only.
  let entries = [];
  let weekEntries = [];
  try {
    // TASK-1507 — company-zone workweeks around the period.
    const { start: winStart, end: winEnd } = _companyWeekWindow(periodStart, periodEnd);
    const q = query(
      collection(db, 'tenants', ClockState.tenantId, 'clockEntries'),
      where('personId', '==', SELF),
      where('clockIn', '>=', Timestamp.fromDate(winStart)),
      where('clockIn', '<=', Timestamp.fromDate(winEnd)),
      orderBy('clockIn', 'desc')
    );
    const snap = await getDocs(q);
    weekEntries = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // Period-bounded display set (identical to the old direct [start,end] query).
    const { startMs: _sMs, endMs: _eMs } = _companyPeriodMs(periodStart, periodEnd); // TASK-1507
    entries = weekEntries.filter(e => {
      const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
      const t = cin.getTime();
      return t >= _sMs && t <= _eMs;
    });
    // Merge any active entries for the current period (same as showEmployeeDetail ~:2257).
    if (isCurrent) {
      const activeForSelf = ClockState.activeEntries.filter(e => e.personId === SELF);
      activeForSelf.forEach(ae => {
        if (!entries.find(pe => pe.id === ae.id)) entries.push(ae);
        if (!weekEntries.find(pe => pe.id === ae.id)) weekEntries.push(ae);
      });
    }
    // Newest first for display.
    entries.sort((a, b) => {
      const aIn = a.clockIn?.toDate?.() || new Date(a.clockIn);
      const bIn = b.clockIn?.toDate?.() || new Date(b.clockIn);
      return bIn - aIn;
    });
  } catch (err) {
    console.error('[Clock] My Timesheet load error:', err);
    body.innerHTML = '<div style="text-align:center;color:var(--status-danger);padding:24px;">Could not load this period. Try again.</div>';
    return;
  }

  // TASK-9195 — rebuild the entry index for THIS render. Keyed by a DOM-safe id
  // so the inline `ontoggle` never carries a ticket id; the loader resolves the
  // ticket from here. Rebuilt (not merged) every render so a row that scrolled
  // out of the period can't be reached by a stale key.
  ClockState.myTimesheetEntryIndex = {};
  entries.forEach(e => {
    const k = _safeTicketId(e.id);
    if (!k) return;
    ClockState.myTimesheetEntryIndex[k] = {
      rawId: e.id,
      ticketId: e.ticketId || null,
      personId: e.personId || null
    };
  });

  let periodTotal = 0;
  const rows = entries.map(entry => {
    const cin = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    const isActive = entry.status === 'active' || !entry.clockOut;
    const cout = entry.clockOut?.toDate?.() ? entry.clockOut.toDate() : (isActive ? null : new Date(entry.clockOut));
    const hrs = entry.status === 'completed'
      ? (entry.totalHours || 0)
      : ((Date.now() - cin.getTime()) / 3600000);
    if (entry.status === 'completed') periodTotal += (entry.totalHours || 0);
    const dateStr = cin.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    // TASK-9189 — the driver's own My Hours had the same time-only range, so a
    // night shift read backwards on the phone the driver checks their pay on.
    // Shared formatter (sharedFormat.js) so this card and the admin approval
    // panel can never disagree about what a shift looks like.
    const _span = window.Fmt.shiftSpan(cin, cout);
    const cinStr = _span.inText;
    const coutStr = cout
      ? _span.outHtml
      : '<em class="ss-text">Active</em>';
    const jobLabel = [entry.customerName, entry.jobTitle].filter(Boolean).map(escapeHtml).join(' · ');
    const notesText = entry.clockOutNotes || entry.notes || '';
    // TASK-9195 — the disclosure used to hold the shift NOTE and nothing else, so
    // a thin note (or none — the case Ethan actually hits) left the row saying
    // "8.25h · Shop" with nothing to reconstruct the day from. The equipment,
    // downtime and standby he logged at clock-out persist as `ticketLines` under
    // the ticket this entry is stamped to; they are fetched ON EXPAND, never on
    // render. A pay period is N shifts and this screen does ONE query — N
    // subcollection reads on a driver's phone at paint time is not acceptable.
    const _entryKey = _safeTicketId(entry.id);
    const _hasWork = !!(_entryKey && entry.ticketId);
    const notesBlock = notesText
      ? `<div style="display:flex;align-items:flex-start;gap:6px;background:#F8FAFC;border-radius:6px;padding:6px 10px;margin-top:4px;font-size:0.78rem;color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;color:var(--brand-blue);margin-top:1px;">speaker_notes</span><span style="white-space:pre-wrap;">${escapeHtml(notesText)}</span></div>`
      : '';
    const notesDisclosure = (notesText || _hasWork)
      ? `<details style="margin-top:6px;"${_hasWork ? ` ontoggle="window.ClockModule.loadMyHoursDetail('${_entryKey}', this)"` : ''}>
          <summary style="display:flex;align-items:center;gap:6px;min-height:44px;cursor:pointer;font-size:0.8rem;color:var(--brand-blue);list-style:revert;">
            <span class="material-icons" style="font-size:14px;">${_hasWork ? 'fact_check' : 'speaker_notes'}</span>${_hasWork ? 'What I did' : 'Notes'}
          </summary>
          ${notesBlock}
          ${_hasWork ? `<div id="my-ts-detail-${_entryKey}" style="margin-top:4px;"><div style="font-size:0.75rem;color:var(--text-secondary);padding:6px 2px;">Loading…</div></div>` : ''}
        </details>`
      : '';
    return `
      <div style="padding:12px 4px;border-bottom:1px solid var(--border-light);">
        <div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px;">
          <strong style="color:var(--text-primary);">${dateStr}</strong>
          <span style="font-weight:600;color:var(--text-primary);white-space:nowrap;">${hrs.toFixed(2)} hrs</span>
        </div>
        <div style="font-size:0.85rem;color:var(--text-secondary);margin-top:2px;">${cinStr} – ${coutStr}</div>
        ${jobLabel ? `<div style="font-size:0.8rem;color:var(--text-muted);margin-top:2px;">${jobLabel}</div>` : ''}
        ${notesDisclosure}
      </div>`;
  }).join('');

  const emptyState = `
    <div class="empty-state-recent" style="padding:32px 16px;text-align:center;color:var(--text-secondary);">
      <span class="material-icons" style="font-size:32px;color:var(--text-muted);">event_busy</span>
      <div style="margin-top:6px;font-weight:600;">No shifts this period</div>
    </div>`;

  // Per-WEEK Reg/OT band — OT is an FLSA workweek property, NOT a per-shift one,
  // so it lives in its own band (never a per-line badge). Runs the SAME shared
  // helper the payroll CSV + on-screen Reports use (computeCrossPeriodOT ~:1774)
  // on the person's WEEK-EXPANDED entries, so a driver can never see a different
  // OT than payroll pays. A bi-weekly period of 38h(wk1)+45h(wk2) correctly
  // shows wk1 OT 0 / wk2 OT 5 (NOT 3 on the 83h period total). And a workweek
  // straddling a semimonthly/monthly boundary has its FULL-week OT shown in the
  // period it ENDS (TASK-949). READ-ONLY/presentational.
  const selfPerson = getPersonForReport(SELF);
  const otSummary = computeCrossPeriodOT(weekEntries, selfPerson, periodStart, periodEnd);
  const _otWeeks = (otSummary.weeks || []).filter(w => !w.skipped);
  let otBandHtml = '';
  if (!selfPerson) {
    // Person not resolved (e.g. a PIN/JobBoard driver whose roster isn't
    // hydrated in ClockState.people). We can't know their exempt status, so we
    // do NOT show an OT band — a misleading "OT 0" for an exempt driver is worse
    // than showing nothing. Hours-per-shift + period total still render below.
    otBandHtml = '';
  } else if (otSummary.exempt) {
    // Exempt (salaried / FLSA-exempt — incl. CDL Motor Carrier Act drivers):
    // OT does not apply. Show that explicitly — NEVER a misleading "OT 0" that
    // implies hourly.
    otBandHtml = `
      <div style="background:#F8FAFC;border:1px solid var(--border-light);border-radius:8px;padding:10px 12px;margin-bottom:10px;font-size:0.82rem;color:var(--text-secondary);display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="font-size:18px;color:var(--text-muted);">badge</span>
        <span>${({ mca: 'Motor Carrier Act — overtime exempt (hours still tracked)', ot: 'Hourly — overtime exempt (hours still tracked)' })[payrollExemptKind(selfPerson)] || 'Salaried — overtime exempt'}</span>
      </div>`;
  } else if (_otWeeks.length > 0) {
    const weekRows = _otWeeks.map(w => {
      const ws = new Date(w.key + 'T00:00:00');
      const we = new Date(ws); we.setDate(we.getDate() + 6);
      const opts = { month: 'short', day: 'numeric' };
      const label = `${ws.toLocaleDateString('en-US', opts)} – ${we.toLocaleDateString('en-US', opts)}`;
      const otCell = w.ot > 0
        ? `<span style="font-weight:700;color:var(--brand-blue);">${w.ot.toFixed(2)}</span>`
        : `<span style="color:var(--text-muted);">0.00</span>`;
      return `
        <div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px;padding:4px 0;font-size:0.82rem;">
          <span style="color:var(--text-secondary);">${label}</span>
          <span style="white-space:nowrap;color:var(--text-secondary);">
            Reg <strong style="color:var(--text-primary);">${w.reg.toFixed(2)}</strong>
            &nbsp;·&nbsp; OT ${otCell}
          </span>
        </div>`;
    }).join('');
    // TASK-949 — conditional straddle note (verbatim), only when a boundary-
    // straddling week moved OT into this period. Never shown for aligned periods.
    const straddleNote = otSummary.straddleNoteApplies ? `
        <div style="display:flex;align-items:flex-start;gap:6px;margin-top:6px;padding-top:6px;border-top:1px solid var(--border-light);font-size:0.72rem;color:var(--text-muted);line-height:1.35;">
          <span class="material-icons" style="font-size:13px;color:var(--brand-blue);margin-top:1px;flex-shrink:0;">info</span>
          <span>Overtime premium includes hours earned during the prior pay period due to a workweek spanning multiple pay periods.</span>
        </div>` : '';
    otBandHtml = `
      <div style="background:#F8FAFC;border:1px solid var(--border-light);border-radius:8px;padding:8px 12px;margin-bottom:10px;">
        <div style="font-size:0.72rem;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;color:var(--text-muted);margin-bottom:2px;">By workweek</div>
        ${weekRows}
        ${straddleNote}
      </div>`;
  }

  body.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:4px 4px 12px;border-bottom:1px solid var(--border-light);margin-bottom:8px;">
      <button class="btn btn-secondary btn-sm" id="my-ts-prev" ${atOldest ? 'disabled' : ''}
        onclick="window.ClockModule.navigateMyTimesheet(-1)" title="Older period" aria-label="Older pay period"
        style="min-width:44px;min-height:44px;padding:6px 12px;${atOldest ? 'opacity:0.45;cursor:not-allowed;' : ''}">
        <span class="material-icons" style="font-size:20px;">chevron_left</span>
      </button>
      <div style="text-align:center;min-width:0;">
        <div style="font-weight:600;color:var(--text-primary);font-size:0.92rem;">${formatPayPeriodRange(periodStart, periodEnd)}</div>
        <div style="font-size:0.78rem;color:var(--text-muted);">${periodTotal.toFixed(2)} hrs${isCurrent ? ' · current period' : ''}</div>
      </div>
      <button class="btn btn-secondary btn-sm" id="my-ts-next" ${isCurrent ? 'disabled' : ''}
        onclick="window.ClockModule.navigateMyTimesheet(1)" title="Newer period" aria-label="Newer pay period"
        style="min-width:44px;min-height:44px;padding:6px 12px;${isCurrent ? 'opacity:0.45;cursor:not-allowed;' : ''}">
        <span class="material-icons" style="font-size:20px;">chevron_right</span>
      </button>
    </div>
    ${entries.length === 0 ? emptyState : `${otBandHtml}${rows}`}
    ${atOldest ? '<div style="text-align:center;color:var(--text-muted);font-size:0.78rem;padding:12px 0;">Showing up to the last 12 months.</div>' : ''}
    <div style="text-align:center;font-size:0.7rem;color:var(--text-muted);padding:10px 0 2px;border-top:1px solid var(--border-light);margin-top:8px;">Payroll hours only — not your FMCSA Hours-of-Service log.</div>
  `;
}

// ◀ / ▶ period nav. dir = -1 (older) or +1 (newer). Clamped to [minOffset, 0].
async function navigateMyTimesheet(dir) {
  const minOffset = -_myTimesheetMaxPeriodsBack();
  const next = ClockState.myTimesheetOffset + (dir < 0 ? -1 : 1);
  if (next > 0) return;            // never past the current period
  if (next < minOffset) return;    // never past the ~12-month cap
  ClockState.myTimesheetOffset = next;
  await _renderMyTimesheet();
}

// ============================================
// TASK-9195 — "What I did": the persisted work behind a My Hours shift.
//
// 🔴 NOT the clock-out recap. jobboard.js's _jbRecapBuild reads IN-MEMORY
// Build-Ticket state (JobBoardState.btEquipment/btDowntime/btStandby/btPhotos)
// that exists only while the clock-out modal is open. My Hours has no
// JobBoardState — it is the other end of the pay period. What persists is
// `tenants/{tid}/tickets/{ticketId}/ticketLines`, joined by
// clockEntries/{id}.ticketId. So: a separate read path and a separate
// presenter. Only the ROW STYLING is deliberately kept in step with the recap,
// so the same work looks the same in both places.
// ============================================

// Row chrome, ported from jobboard.js _jbRecapRow. Literal hex (not tokens) for
// the same reason the recap uses them: this panel sits on the white modal body
// and must not inherit a muted-on-light contrast trap.
function _myHoursRow(icon, title, meta, body) {
  return `
    <div style="display:flex;gap:8px;padding:8px 0;border-top:1px solid #e2e8f0;">
      <span class="material-icons" style="font-size:16px;color:#94a3b8;flex-shrink:0;margin-top:2px;">${escapeHtml(icon)}</span>
      <div style="min-width:0;flex:1;">
        <div style="display:flex;gap:8px;align-items:baseline;justify-content:space-between;">
          <span style="font-size:0.8rem;font-weight:600;color:#0f172a;word-break:break-word;">${escapeHtml(title)}</span>
          ${meta ? `<span style="font-size:0.75rem;color:#64748b;white-space:nowrap;flex-shrink:0;">${escapeHtml(meta)}</span>` : ''}
        </div>
        ${body ? `<div style="font-size:0.78rem;color:#475569;white-space:pre-wrap;margin-top:2px;word-break:break-word;">${escapeHtml(body)}</div>` : ''}
      </div>
    </div>`;
}

// TASK-9252 — the DRIVER sees the raw window; only AR/AP sees the round-up.
//
// `roundHalfHour` (TASK-1410, Chris 2026-08-07) rounds equipment hours UP to the
// next half hour for the INVOICE — standard oilfield practice, on for 549 of
// TITS's 808 time-based lines. The writers persist the pre-rounding value next
// to it as `roundedFrom`, and that field was written by 12 call sites across
// clock.js / billing.js / ticketGateway.js / equipmentLineEnrichment.js and read
// by NONE. So this panel showed a driver `8.00h` for a shift he knows ran 7:42.
//
// That is exactly how Brian Reid reported it (2026-09-16): "off by 30 minutes",
// except the example he gave was 20. Both were true — the GRID is 30 minutes, the
// actual bump is 0-29 depending where he lands (7.16h → 7.50h is his 20).
//
// 🔴 Display only. AR/AP keeps reading `hours`, the invoice is untouched, no
// writer is involved. Moving where the rounding is APPLIED is TASK-9257, and is
// deliberately NOT this change.
//
// Trust `roundedFrom` ONLY when `hours` actually IS its rounded form — the same
// corroboration predicate billing.js's `decideLineRounding` settled on, and for
// the same reason: a close-out writer stamps `roundedFrom` without always
// stamping the flag, so a stale or forged one must never invent a raw number on
// a line that was never rounded. Uncorroborated → fall through to today's value.
//
// Deliberately NOT `window.BillingRounding.decideLineRounding` (which exists):
// billing.js is not guaranteed loaded in a PIN-driver session and this panel is
// driver-facing, so the check stays local to clock.js's own `roundHalfHour`.
// Narrowed after COD (Independent, 2026-09-16): EQUIPMENT only — the measured
// population and the only type any writer stamps `roundedFrom` on — and an
// explicit `roundToHalfHour: false` LOSES, because equipmentLineEnrichment
// writes that false without deleting a pre-existing `roundedFrom`. An explicit
// false is a real answer; honour it. Without those two guards a stale 7.7 under
// a genuinely-unrounded `hours: 8` would render 7.70 and be wrong by 18 minutes.
function _myHoursRawHours(line) {
  if (!line || line.type !== 'EQUIPMENT' || line.roundToHalfHour === false) return null;
  const rf = Number(line.roundedFrom);
  const h = Number(line.hours);
  if (!Number.isFinite(rf) || rf <= 0 || !Number.isFinite(h)) return null;
  return Math.abs(h - roundHalfHour(rf)) < 0.005 ? rf : null;
}

function _myHoursLineHours(line) {
  const raw = _myHoursRawHours(line);
  if (raw !== null) return raw;
  if (typeof line.hours === 'number' && isFinite(line.hours)) return Math.max(0, line.hours);
  const f = line.fromTime?.toDate?.() || (line.fromTime ? new Date(line.fromTime) : null);
  const t = line.toTime?.toDate?.() || (line.toTime ? new Date(line.toTime) : null);
  if (!f || !t || isNaN(f) || isNaN(t)) return 0;
  return Math.max(0, (t - f) / 3600000);
}

// Never render a Firestore timestamp raw — same shared formatter the shift row
// above uses, so a line that crossed midnight carries the +1d marker's wording.
function _myHoursLineSpan(line) {
  const f = line.fromTime?.toDate?.() || (line.fromTime ? new Date(line.fromTime) : null);
  const t = line.toTime?.toDate?.() || (line.toTime ? new Date(line.toTime) : null);
  if (!f || isNaN(f)) return '';
  try {
    const s = window.Fmt?.shiftSpan?.(f, t);
    if (s) return s.rangeText || '';
  } catch (_) { /* fall through to nothing — a missing span is not worth a blank panel */ }
  return '';
}

// Build the panel from PERSISTED ticketLines.
//
// ✅ Chris 2026-09-14 — a coworker's line on a shared ticket DOES show, tagged
// `· crew`, own lines first (mirrors jobboard.js:4201 / TASK-1188). 🔴 The LABEL
// is the load-bearing half, not the sort: an untagged crew line on a
// payroll-adjacent screen is the actual defect.
//
// ⚠️ `myEntryId` is the viewer's OWN clock-entry id; a line is his only when the
// line's `clockEntryId` matches it. Anything else — a coworker's session, or an
// office-added line carrying no clockEntryId at all — is tagged. Deliberately
// stricter than jobboard's jbIsCrewEquipmentLine, which treats an unstamped line
// as the current session's (true while BUILDING a ticket, false when reading one
// back weeks later).
function _myHoursDetailHtml(lines, photos, myEntryId) {
  let html = '';
  let rows = 0;
  let eqRows = 0;
  const mine = (l) => !!(myEntryId && l.clockEntryId === myEntryId);
  const meta = (l) => `${_myHoursLineHours(l).toFixed(2)}h${mine(l) ? '' : ' · crew'}`;
  const body = (l) => [_myHoursLineSpan(l), (l.notes || '').trim()].filter(Boolean).join('\n');

  if (isCOFSectionEnabled('equipment')) {
    const eq = (lines.filter(l => l.type === 'EQUIPMENT'))
      .sort((a, b) => (mine(a) ? 0 : 1) - (mine(b) ? 0 : 1));
    for (const l of eq) {
      const name = [l.assetName || 'Equipment', l.fleetNumber ? `#${l.fleetNumber}` : ''].filter(Boolean).join(' ');
      html += _myHoursRow(l.assetType === 'trailer' ? 'rv_hookup' : 'local_shipping', name, meta(l), body(l));
      rows++; eqRows++;
    }
  }
  if (isCOFSectionEnabled('downtime')) {
    for (const l of lines.filter(l => l.type === 'DOWNTIME')) {
      html += _myHoursRow('pause_circle', 'Downtime', meta(l), body(l));
      rows++;
    }
  }
  if (isCOFSectionEnabled('standby')) {
    for (const l of lines.filter(l => l.type === 'STANDBY')) {
      html += _myHoursRow('hourglass_empty', 'Standby', meta(l), body(l));
      rows++;
    }
  }
  if (isCOFSectionEnabled('photos') && photos.length > 0) {
    // Storage download URLs only — anything else is not rendered at all rather
    // than trusted into an href/src.
    const safe = photos.filter(p => typeof p?.url === 'string' && /^https:\/\//i.test(p.url)).slice(0, 8);
    html += _myHoursRow('photo_camera', `${photos.length} photo${photos.length === 1 ? '' : 's'} on this ticket`, '', '');
    if (safe.length) {
      html += `<div style="display:flex;flex-wrap:wrap;gap:6px;padding:2px 0 8px 24px;">${
        // TASK-9855 — opened/loaded through the Storage SDK by path, not the token URL.
        safe.map(p => `<a ${window.StorageFile.linkAttrs(p)} style="display:block;width:64px;height:64px;border-radius:6px;overflow:hidden;border:1px solid #e2e8f0;"><img ${window.StorageFile.imgAttrs(p)} alt="${escapeHtml(p.name || 'photo')}" loading="lazy" style="width:100%;height:100%;object-fit:cover;display:block;"></a>`).join('')
      }</div>`;
    }
    rows++;
  }

  if (rows === 0) {
    // 🔴 The empty-note case is the whole point of this task — say something true
    // rather than leaving a blank panel that reads as a broken load.
    return `<div style="font-size:0.78rem;color:#475569;padding:8px 0;border-top:1px solid #e2e8f0;">No equipment, downtime or standby was logged on this shift's ticket.</div>`;
  }
  // 💵 Equipment hours are the ASSET's time on the ticket, not a person's pay —
  // the server consolidates same-asset lines from every session to ONE line at
  // the interval-union of their windows (ticketLineDedup findMergeGroups), so a
  // team-driven truck's hours legitimately span more than one driver's shift.
  // Say so on a payroll-adjacent screen instead of letting him add them up.
  const footer = eqRows > 0
    ? `<div style="font-size:0.7rem;color:#64748b;padding:8px 0 2px;border-top:1px solid #e2e8f0;">Equipment and downtime hours are the ticket's, not your pay. Your paid hours are the number at the top of this shift.</div>`
    : '';
  return html + footer;
}

// Lazy loader for one shift's panel. Wired to the <details> `ontoggle`, so it
// runs when the driver actually opens a row — ZERO reads at render time.
// `entryKey` is a DOM-safe key into ClockState.myTimesheetEntryIndex, NOT a
// ticket id: an inline handler string is trivially replayable, so the ticket
// this reads is resolved from state the self-scoped render put there.
async function loadMyHoursDetail(entryKey, detailsEl) {
  const key = _safeTicketId(entryKey);
  const host = document.getElementById('my-ts-detail-' + key);
  if (!host) return;
  // ontoggle fires on CLOSE too.
  if (detailsEl && detailsEl.open === false) return;
  if (host.dataset.ddLoaded === '1') return;

  const idx = (ClockState.myTimesheetEntryIndex || {})[key];
  // TASK-451 hard rule, server-side half mirrored in the UI: re-resolve SELF and
  // refuse anything that is not this viewer's own entry. This function takes no
  // personId and can't be steered to one.
  const SELF = _myTimesheetSelfPersonId();
  if (!idx || !idx.ticketId || !SELF || (idx.personId && idx.personId !== SELF) || !ClockState.tenantId) {
    host.dataset.ddLoaded = '1';
    host.innerHTML = '';
    return;
  }

  const cached = (ClockState.myTimesheetDetail || {})[key];
  if (cached !== undefined) {
    host.dataset.ddLoaded = '1';
    host.innerHTML = cached;
    return;
  }
  host.dataset.ddLoaded = '1'; // claim BEFORE the await — a fast open/close/open must not double-read

  try {
    const tenant = ClockState.tenantId;
    const wantPhotos = isCOFSectionEnabled('photos');
    // TWO reads per EXPANDED row (one when photos are off), never per rendered
    // row. Parallel so the panel lands in one round trip.
    const [linesSnap, ticketSnap] = await Promise.all([
      getDocs(collection(db, 'tenants', tenant, 'tickets', idx.ticketId, 'ticketLines')),
      wantPhotos ? getDoc(doc(db, 'tenants', tenant, 'tickets', idx.ticketId)) : Promise.resolve(null)
    ]);
    const lines = linesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    const photos = (ticketSnap && ticketSnap.exists() && Array.isArray(ticketSnap.data().photos))
      ? ticketSnap.data().photos : [];
    const html = _myHoursDetailHtml(lines, photos, idx.rawId);
    ClockState.myTimesheetDetail[key] = html;
    // The row can be re-rendered (period nav) while this was in flight — re-look
    // it up rather than writing into a detached node.
    const live = document.getElementById('my-ts-detail-' + key);
    if (live) live.innerHTML = html;
  } catch (err) {
    console.warn('[Clock] My Hours detail load failed:', err?.message || err);
    const live = document.getElementById('my-ts-detail-' + key);
    if (live) {
      // Do NOT cache a failure, and release the claim so closing and reopening
      // the row retries. A driver on yard wifi gets a second chance.
      live.dataset.ddLoaded = '0';
      live.innerHTML = `<div style="font-size:0.75rem;color:#64748b;padding:6px 2px;">Couldn't load this shift's ticket — close and reopen to try again.</div>`;
    }
  }
}

function updateSelectedPersonStatus() {
  if (!ClockState.selectedPerson) return;
  if (isClockModalOpen()) return;
  // Don't interrupt PIN entry
  if (document.getElementById('clock-pin')?.matches(':focus')) return;

  _clockSyncModeLabels(); // TASK-9553 — Clock Out button label for this person's mode
  // TASK-4691 — was `!isFounder()`, a TENANT test that skipped the PIN for every
  // office session on trippin-trucking. Now a capability test + the device lock.
  // TASK-9941 F S2 — and the unlock is a server-checked grant for THIS person
  // (code / unverified), never a browser compare.
  const needsPin = !clockPunchAuthorization(ClockState.selectedPerson).ok;

  // TASK-9579 — the NEWEST open shift (was the first one the snapshot listed);
  // any older open shift is named under the status below, and Clock Out asks
  // which one to close (clock-double-tap).
  const { entry, others: _olderOpen } = _clockOpenShiftsFor(ClockState.selectedPerson);
  const statusContainer = document.getElementById('clock-status-container');
  const statusEl = document.getElementById('clock-status');
  const btnClockIn = document.getElementById('btn-clock-in');
  const btnClockOut = document.getElementById('btn-clock-out');
  const clockInFields = document.getElementById('clock-in-fields');
  const recordExpenseWrap = document.getElementById('clock-record-expense-wrap');

  if (!statusContainer || !statusEl) return;

  const pinEntry = document.getElementById('pin-entry');

  // Driver-expense entry point — show only after PIN passes (or founder),
  // and only when the carrier flipped the toggle ON. Hidden while PIN is
  // pending so we don't leak the feature pre-auth.
  // v1.15.495: dropped legacy BI-OR — gate is now strictly
  // `settings.driverExpenses.enabled === true`. Pre-flight scan confirmed
  // 0 tenants were relying on the BI-OR path.
  // TASK-528: ALSO show for a signed-in non-admin member with clock||billing
  // (the Clock self-service hub). That gate mirrors the deployed
  // createMemberExpense server gate; the driverExpenses.enabled tenant toggle
  // is the PIN-driver gate and does NOT apply to a signed-in office member.
  const expenseEnabled =
    window.AppState?.settings?.driverExpenses?.enabled === true ||
    memberCanRecordExpense();

  // If PIN required and not yet authenticated, gate everything behind PIN
  if (needsPin) {
    statusContainer.classList.add('hidden');
    btnClockIn?.classList.add('hidden');
    btnClockOut?.classList.add('hidden');
    clockInFields?.classList.add('hidden');
    recordExpenseWrap?.classList.add('hidden');
    pinEntry?.classList.remove('hidden');
    // TASK-4691 P2 — offer the lock override only when a gesture exists that
    // would actually grant it (see clockCanOfferOverride).
    document.getElementById('clock-pin-override')
      ?.classList.toggle('hidden', !clockCanOfferOverride());
    return;
  }

  if (expenseEnabled) {
    recordExpenseWrap?.classList.remove('hidden');
  } else {
    recordExpenseWrap?.classList.add('hidden');
  }

  if (entry) {
    const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    const duration = formatDuration(new Date() - clockIn);

    statusEl.innerHTML = `
      <div class="status-clocked-in">
        <span class="material-icons">timer</span>
        <div class="status-info">
          <div class="status-label">Clocked In</div>
          <div class="status-time">${clockIn.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} (${duration})</div>
          ${entry.customerName ? `<div class="status-job">${escapeHtml(entry.customerName)}${entry.jobTitle ? ' - ' + escapeHtml(entry.jobTitle) : ''}</div>` : '<div class="status-job">Shop time</div>'}
        </div>
      </div>
      ${_olderOpen.length ? `<div id="clock-status-multi-open" style="margin-top:6px;padding:6px 10px;border-radius:8px;background:rgba(245,158,11,0.12);color:#78350f;font-size:0.8rem;line-height:1.35;">
        <span class="material-icons" style="font-size:15px;vertical-align:-3px;margin-right:4px;color:#b45309;">warning</span>
        ${_olderOpen.length === 1 ? 'Another shift is' : _olderOpen.length + ' other shifts are'} still open (started ${escapeHtml(_olderOpen.map(_clockShiftStartLabel).join(', '))}). Clock Out will ask which one to close.
      </div>` : ''}
    `;
    statusContainer.classList.remove('hidden');
    btnClockIn?.classList.add('hidden');
    btnClockOut?.classList.remove('hidden');
    clockInFields?.classList.add('hidden');
    pinEntry?.classList.add('hidden');
  } else {
    statusEl.innerHTML = `
      <div class="status-clocked-out">
        <span class="material-icons">timer_off</span>
        <div class="status-info">
          <div class="status-label">Not Clocked In</div>
          <div class="status-time">Ready to clock in</div>
        </div>
      </div>
    `;
    statusContainer.classList.remove('hidden');
    btnClockIn?.classList.remove('hidden');
    btnClockOut?.classList.add('hidden');
    clockInFields?.classList.remove('hidden');
    // TASK-4691 follow-up — take the PIN box down once no PIN is required.
    // Reaching this line means the `needsPin` early-return above did NOT fire,
    // so the question is already settled: the person's own PIN was typed, they
    // are the signed-in member clocking themselves, or a workforce holder
    // unlocked the device. The clocked-IN branch above already hid it; this
    // branch never did, so a correct PIN left the box — and, after TASK-4691,
    // the "Manager override" button for an override ALREADY granted — sitting
    // under the live Clock In form. Found by a Chromium DOM run of the shipped
    // functions; the shipping lane's logic-only suite could not see it.
    // This hides a div. It is NOT a gate: clockIn() reads #clock-pin by id
    // regardless of visibility, and the clockEntries write arms (firestore.rules
    // :4127 create / :4171 update) still let any workforce holder write any
    // clockEntry with no PIN check at all.
    pinEntry?.classList.add('hidden');
    populateCustomerDropdown();
  }
}

// ============================================
// EVENT HANDLERS
// ============================================
function setupEventHandlers() {
  // TASK-360 — Self-clock surface: the hidden <select id="clock-person"> is
  // pre-seeded with the viewer's personId, but a programmatic value set does
  // not fire `onchange`. Without this, the customer/job dropdowns and the
  // Clock In button never appear because `onPersonSelect()` never runs.
  // Workforce admins still get the full dropdown (no auto-select).
  if (!hasWorkforceAccess()) {
    const selfId = getSelfPersonId();
    if (selfId) {
      // Defer to next tick so the DOM has settled before we fire selection.
      setTimeout(() => {
        const select = document.getElementById('clock-person');
        if (select && select.value === selfId) onPersonSelect();
      }, 0);
    }
  }
}

function onPersonSelect() {
  const personId = document.getElementById('clock-person')?.value;
  ClockState.selectedPerson = personId;
  ClockState.selectedCustomer = null;
  ClockState.authenticatedPersonId = null;  // clear on person change
  ClockState.codeGrant = null;              // TASK-9941 F S2 — a grant never follows a new pick
  _clockPinPanelReset();

  const actionsEl = document.getElementById('clock-actions');
  const pinEntry = document.getElementById('pin-entry');
  const clockInFields = document.getElementById('clock-in-fields');

  if (personId) {
    actionsEl?.classList.remove('hidden');
    updateSelectedPersonStatus();
  } else {
    actionsEl?.classList.add('hidden');
    pinEntry?.classList.add('hidden');
    clockInFields?.classList.add('hidden');
    document.getElementById('clock-status-container')?.classList.add('hidden');
  }

  // Clear PIN input and error on person change
  const pinInput = document.getElementById('clock-pin');
  if (pinInput) pinInput.value = '';
  const pinError = document.getElementById('pin-error');
  if (pinError) { pinError.textContent = ''; pinError.classList.add('hidden'); }

  // Re-render summary and history filtered by selected person
  renderRecentList();

  // TASK-586 — selected person changed → self-vs-admin status may have
  // changed → re-sync the inline equipment rows against the retained job
  // selection (the job dropdown is NOT rebuilt on person change).
  _clockRenderInlineEquipment(document.getElementById('clock-job')?.value || '');
  // TASK-9865 — the preselect is PER PERSON: with a customer already chosen
  // and no job picked yet, pick this person's crewed job; either way re-judge
  // the crew line. A job picked by hand is kept.
  _clockRejudgeJobForPerson();
}

function _clockRejudgeJobForPerson() {
  try {
    const customerId = document.getElementById('clock-customer')?.value;
    const select = document.getElementById('clock-job');
    if (!select || !customerId || customerId === '__shop__') return;
    const { jobs } = getRelevantJobs(customerId);
    const person = _clockPickerPerson();
    // Only an AUTOMATIC pick (or no pick) is re-decided for the new person; a
    // job someone chose by hand stays (dataset.autoPick is cleared on change).
    const auto = select.dataset.autoPick || '';
    const isAuto = !select.value || select.value === auto;
    if (isAuto && jobs.length > 1) {
      const pick = clockPickJobForPerson(jobs, ClockState.todaysJobDays, person, _clockNowMinutes());
      const next = pick.jobId || '';
      if (next !== select.value) {
        select.value = next;
        _clockRenderInlineEquipment(select.value || '');
      }
      select.dataset.autoPick = select.value || '';
    }
    _clockRenderCrewWarn('clock-job', 'clock-job-crew-warn', person, jobs);
  } catch (e) { console.warn('[Clock] crew re-judge skipped:', e); }
}

// TASK-9941 F S2 — the browser no longer compares the code (it does not know
// it). Typing only takes down a stale "didn't match"; submitClockPin() asks the
// server. A lock / offline notice stays up until the person or the outcome changes.
function onPinInput() {
  const pinError = document.getElementById('pin-error');
  if (pinError && !ClockState._pinFallbackReason) { pinError.textContent = ''; pinError.classList.add('hidden'); }
}

// TASK-4691 P2 — the manager override. Reuses the ONE existing access-PIN
// challenge (window.showPinChallenge, app.js) rather than building a second
// challenge path; toggleDeviceLock() itself is deliberately module-private per
// the TASK-763 COD Adversary note ("do NOT add window.toggleDeviceLock"), so we
// drive the same unlock through the sanctioned export and read the resulting
// lock state. On success the device is genuinely unlocked — the padlock, the
// sidebar pill and this override all move together, because they read the same
// state.
//
// This does NOT block or delay any clock write. It is offered only on the
// entry screen, before a punch exists.
function useClockPinOverride() {
  if (typeof window.showPinChallenge !== 'function') {
    window.showToast?.('Unlock is unavailable on this screen', 'error');
    return;
  }
  const targetId = ClockState.selectedPerson;
  const target = ClockState.people.find(p => p.id === targetId);
  window.showPinChallenge(null, (ok) => {
    if (!ok) return; // cancelled or failed — nothing changes, PIN box stays up
    // A NON-primary owner PIN leaves the device lock engaged by design
    // (TASK-719), and a kiosk PIN that doesn't carry `workforce` grants no
    // override. Both are correct — but say so, or the button looks broken.
    if (!clockPinOverrideActive()) {
      window.showToast?.('That PIN does not grant Workforce — enter the person\'s PIN instead', 'info');
      updateSelectedPersonStatus();
      return;
    }
    logClockAudit(ClockState.tenantId, 'CLOCK_PIN_OVERRIDE', {
      source: 'clock-module',
      personId: targetId || null,
      personName: target?.name || null,
      note: 'Device unlocked to clock another person without their PIN'
    });
    window.showToast?.('Override active — recorded in the activity log', 'success');
    updateSelectedPersonStatus();
  });
}

async function onCustomerSelect() {
  const customerId = document.getElementById('clock-customer')?.value;
  ClockState.selectedCustomer = customerId;

  const jobSelection = document.getElementById('job-selection');

  if (customerId && customerId !== '__shop__') {
    jobSelection?.classList.remove('hidden');
    // TASK-1417 — pull fresh jobs/jobDays before building the list, so a job
    // dispatched since this page loaded actually appears.
    await refreshJobCachesIfStale(ClockState.tenantId);
    // The user may have changed the customer while that was in flight; only
    // paint if this is still the selected one.
    if (document.getElementById('clock-customer')?.value !== customerId) return;
    populateJobDropdown(customerId); // re-syncs equipment rows internally (TASK-586)
  } else {
    jobSelection?.classList.add('hidden');
    // TASK-586 — shop / no customer → no job → clear any stale equipment rows.
    _clockRenderInlineEquipment('');
  }
}

function selectPerson(personId) {
  // TASK-360 — non-workforce viewers may only select themselves.
  // The "Currently Clocked In" list (the path that lets a kiosk admin
  // select another employee) is workforce-gated and not rendered for
  // self-only viewers, but in case of a race or deep-link, enforce it
  // here too. Other selections silently bounce to self.
  if (!hasWorkforceAccess()) {
    const selfId = getSelfPersonId();
    if (!selfId) return; // not linked → no clock-in possible
    if (personId !== selfId) {
      console.warn('[Clock] selectPerson blocked — viewer can only select self');
      personId = selfId;
    }
  }
  const select = document.getElementById('clock-person');
  if (select) {
    select.value = personId;
    onPersonSelect();
  }
}

// ============================================
// ADJUST CLOCK-IN TIME TOGGLE (every tenant — not founder-gated; TASK-932 item 9)
// ============================================
function toggleClockInTime() {
  const group = document.getElementById('clock-time-adjust-group');
  if (!group) return;
  const visible = group.style.display !== 'none';
  group.style.display = visible ? 'none' : '';
  if (!visible) {
    setSplitDateTimeValue('clock-in-time', toLocalDatetimeStr(new Date(), 'clock-in-time'));
  }
}

// ============================================
// CLOCK IN (with ticket creation)
// ============================================
async function clockIn() {
  const personId = ClockState.selectedPerson;
  if (!personId) return;

  const person = ClockState.people.find(p => p.id === personId);

  // TASK-4691 — validate the per-person clock PIN. Was `expectedPin &&
  // !isFounder()`: a TENANT test, so every office session on trippin-trucking
  // wrote a coworker's punch with no PIN at all. Now the same capability +
  // device-lock question the PIN box above asks, through the ONE shared helper.
  // TASK-9941 F S2 — and the answer is the server-checked grant for this
  // person (code / override / unverified), never a compare in the browser.
  const _codeAuth = clockPunchAuthorization(personId);
  const _pinRequired = clockPersonPinRequired(personId);
  if (!_codeAuth.ok) {
    window.showToast(`Enter ${person?.name ? person.name.split(/\s+/)[0] + '’s' : 'the'} PIN first`, 'error');
    // No re-render needed here: startClockUpdate() ticks
    // updateSelectedPersonStatus() every second while the clock tab is open,
    // so the PIN box is back on screen within a second of this refusal.
    return;
  }
  // Attribution: did this write lean on the override (someone OTHER than the
  // person, with no per-person PIN typed)? Lands on the CLOCK_IN row in
  // clockAuditLog — NOT on the clockEntries doc; putting an immutable
  // authorising-PIN field on the entry itself is TASK-4691 P4, deliberately not
  // in this slice. Say "activity log", never "on the entry", until P4 ships.
  //
  // 🔴 The third term is LOAD-BEARING, do not "simplify" it away. A COD lane
  // called it dead on the grounds that clockPersonPinRequired already
  // short-circuits on self — but _pinRequired is false for BOTH the self case
  // and the override case, so the self check is the only thing separating them.
  // Verified by running the real helpers: without it a member clocking
  // THEMSELVES is falsely stamped as having used a manager override.
  // TASK-9941 F S2 — `!!expectedPin &&` is gone: a person with no name +
  // phone code clocked in by someone else under the override is an override too.
  const _usedPinOverride = !_pinRequired
    && !_clockIsSelfSelection(personId) && _codeAuth.how === 'override';

  // TASK-258 v1.15.535 — Hard-stop client enforcement on expired credentials.
  // Gate: tenant.compliance.hardStopExpiredCredentials === true (Chris-controlled
  // via Overlord, default OFF). When ON + driver has expired CDL or MedCard →
  // blocking modal + HARD_STOP_BLOCK audit. clock-in does not bind to a truck
  // so AVI is not checked here (covered in dispatch saveAssignment).
  if (_hsIsHardStopEnabled()) {
    const expired = _hsCollectExpiredForPerson(person);
    if (expired.length > 0) {
      try {
        window.logAudit?.('compliance', 'HARD_STOP_BLOCK', {
          source: 'clock-module',
          attemptedAction: 'clock-in',
          personId,
          personName: person?.name || null,
          credentials: expired.map(e => e.kind),
          credentialDetail: expired.map(e => ({ kind: e.kind, daysOverdue: e.daysOverdue })),
          at: new Date().toISOString()
        });
      } catch (err) { console.warn('[Clock] HARD_STOP_BLOCK audit failed:', err); }
      const result = await _hsShowBlockingModal(person?.name, expired);
      // TASK-258 — admin override path. If a qualified admin chose to override
      // with a reason, audit-log the override and fall through to clock-in.
      // Otherwise (default Close, or non-admin) stay blocked.
      if (!result?.proceeded) {
        return;
      }
      try {
        window.logAudit?.('compliance', 'HARD_STOP_OVERRIDE', {
          source: 'clock-module',
          attemptedAction: 'clock-in',
          personId,
          personName: person?.name || null,
          credentials: expired.map(e => e.kind),
          credentialDetail: expired.map(e => ({ kind: e.kind, daysOverdue: e.daysOverdue })),
          overrideReason: result.reason,
          overrideBy: window.AppState?.user?.email || 'unknown',
          at: new Date().toISOString()
        });
      } catch (err) { console.warn('[Clock] HARD_STOP_OVERRIDE audit failed:', err); }
    }
  }

  // Track who authenticated via PIN (for edit permissions). TASK-9941 F S2 — not
  // for an unverified punch: nobody proved who they are.
  if (_codeAuth.how !== 'unverified') ClockState.authenticatedPersonId = personId;

  const customerId = document.getElementById('clock-customer')?.value;
  const jobId = document.getElementById('clock-job')?.value;
  const notes = document.getElementById('clock-notes')?.value;

  // Customer is required (unless shop)
  if (!customerId) {
    window.showToast('Please select a customer', 'error');
    return;
  }

  const isShop = customerId === '__shop__';
  let customer = null;
  let job = null;

  if (!isShop) {
    customer = ClockState.customers.find(c => c.id === customerId);
    if (jobId) {
      job = ClockState.allJobs.find(j => j.id === jobId);
    }
  }

  // Determine role and rate (async — checks contract rates)
  const btn = document.getElementById('btn-clock-in');
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Clocking In...';

  // clock-dst-all — an adjusted clock-in is COMPANY time: refuse an unreadable
  // zone / a nonexistent time, ask which one in the repeated fall-back hour.
  // Runs before the guard and every write, so a refusal leaves nothing behind.
  {
    const _adjStr = getSplitDateTimeValue('clock-in-time');
    if (_adjStr) {
      const _rt = await _clockResolveTimes([{ str: _adjStr, key: 'clock-in-time', label: 'Clock-in time' }], { inStr: _adjStr, inKey: 'clock-in-time', outNowMs: Date.now() });
      if (!_rt.ok) {
        window.showToast(_rt.message, 'warning', null, 9000);
        btn.disabled = false;
        btn.innerHTML = '<span class="material-icons">login</span> Clock In';
        return;
      }
    }
  }

  // clock-double-tap — one open shift per person. Runs BEFORE any ticket write,
  // so a refused tap leaves nothing behind. Kiosk / Workforce clock-ins of
  // someone else are refused the same way (close their open shift first).
  const _csg = window.ClockShiftGuard;
  const _csgEnd = _csg ? _csg.begin(personId) : () => {};
  if (!_csgEnd) {
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">login</span> Clock In';
    return;
  }
  const _newEntryRef = doc(collection(db, 'tenants', ClockState.tenantId, 'clockEntries'));
  let _csgClaim = null;
  if (_csg) {
    _csgClaim = await _csg.guardClockIn(_clockCsgDeps(), {
      tenantId: ClockState.tenantId, personId, personName: person?.name || '',
      self: _clockIsSelfSelection(personId), entryId: _newEntryRef.id,
      // stuck-shift-morning — a shift left open on an earlier day: ask for the
      // finish time, close it (closedLate), then carry on with this clock-in.
      resolveStale: true, surface: 'clock',
    });
    if (!_csgClaim.ok) {
      window.showToast(_csgClaim.message, 'warning', null, 9000);
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">login</span> Clock In';
      _csgEnd();
      try { updateSelectedPersonStatus(); } catch (_) { /* repaint is best-effort */ }
      return;
    }
  }
  let _ticketLineForCleanup = null;

  let _rr;
  try {
    _rr = await determineRoleAndRate(personId, jobId, customerId);
  } catch (_rrErr) {
    // Never leave this tab's in-flight mark or a 2-minute claim behind.
    _csgEnd();
    try { await _csgClaim?.release?.(); } catch (_) { /* best effort */ }
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">login</span> Clock In';
    throw _rrErr;
  }
  const { role, rate, roundToHalfHour, rateUnit, revenueCategory } = _rr;
  const _isPieceRate = !isTimeBasedUnit(rateUnit);

  try {
    // Use adjusted time if provided (founder only), else now
    const adjustedClockInStr = getSplitDateTimeValue('clock-in-time');
    const clockInTime = adjustedClockInStr
      ? Timestamp.fromDate(_tltDate(adjustedClockInStr, 'clock-in-time'))
      : Timestamp.now();

    // TASK-586/1221 — resolve THIS entry's assets ONCE, up front, so the same
    // set feeds BOTH the splitBy ticket resolver (below) AND the per-operator
    // equipment tie stamped on the clockEntry. Computing it here (before the
    // ticket resolve) is what lets a split job key its ticket on the operator's
    // rig. _entryAssetIds === exactly what gets written to entry.assetIds, so the
    // server (which resolves the split from before.assetIds) computes the SAME
    // key — client↔server parity by construction.
    const _tieSelfClockIn = equipmentTieEnabled() && !isShop && jobId && _clockIsSelfClockIn(personId);
    let _entryAssetIds = null; // null = legacy full-fill (no per-entry asset scoping)
    let _entryProvenance = null; // TASK-1547 — choice capture, stamped with assetIds
    if (_tieSelfClockIn) {
      const _tieJobDay = _clockResolveJobDayForEntry({ jobId, clockIn: clockInTime });
      const _tieDayAssetIds = _clockJobDayAssetIds(_tieJobDay);
      if (_tieDayAssetIds.size > 0) {
        const _tieChosen = _clockReadInlineAssetIds();
        // null = rows never rendered (defensive race) → fall back to
        // the rows' own default state. TASK-9763 — that default is the
        // pairing preselect (rule 5), so the race can't re-open the 2x2
        // over-claim; no pairing on the day = all, exactly as before.
        _entryAssetIds = (_tieChosen !== null)
          ? _tieChosen
          : Array.from(_clockCrewPairPreselect(_tieJobDay, Array.from(_tieDayAssetIds), personId).checked);
        // TASK-1547 — record the choice: what the inline rows rendered,
        // whether the person touched them, whether "No Equipment" produced an
        // empty pick. The defensive no-rows fallback never saw a UI selection
        // at all — its own source string ('kiosk_autotie') says so.
        const _eqEl = document.getElementById('clock-in-equipment');
        const _rendered = _eqEl ? Array.from(_eqEl.querySelectorAll('.clock-eqc-row')).map(r => r.dataset.assetId).filter(Boolean) : [];
        const _laborTap = _eqEl?.dataset?.eqLaborOnly === '1';
        const _touched = _laborTap || _eqEl?.dataset?.eqTouched === '1';
        _entryProvenance = _clockBuildAssetProvenance(
          (_tieChosen !== null) ? 'kiosk' : 'kiosk_autotie',
          {
            renderedAssetIds: _rendered,
            selectedAssetIds: _entryAssetIds,
            explicitLaborOnly: _laborTap && _entryAssetIds.length === 0,
            defaulted: (_tieChosen === null) ? true : !_touched
          }, _tieDayAssetIds);
      } else {
        // ON but the job has no equipment this day → explicit labor-only.
        _entryAssetIds = [];
        // TASK-1547 — renderedAssetIds:[] = "nothing to pick" (TASK-1539 rule
        // 6: fallback-eligible if the day gets equipment later).
        // TASK-1572 Slice 2 — if the fleet ask was on screen, its answer rides
        // as a provenance CLAIM (assetIds stays []). No ask → unchanged.
        const _fleetAsk = _clockReadFleetAsk();
        _entryProvenance = _fleetAsk
          ? _clockFleetAskProvenance('kiosk', _fleetAsk, _tieDayAssetIds)
          : _clockBuildAssetProvenance('kiosk', {
            renderedAssetIds: [], selectedAssetIds: [], explicitLaborOnly: false, defaulted: true
          }, _tieDayAssetIds);
      }
    }
    // Split context: use ONLY the entry's own assets (what the server will see).
    // On equipmentTie-OFF tenants the entry carries no per-operator assets, so a
    // vehicle-split degrades to a per-operator ticket (op: key) on BOTH sides —
    // honest: without the tie the system can't know which rig each operator ran.
    const _splitAssetIds = (_entryAssetIds != null) ? _entryAssetIds : [];

    // Find or create ticket
    let ticketId = null;
    let ticketLineId = null;

    // TASK-1175 made internalTimeOnly a CUSTOMER FLAG; `isShop` above still only
    // recognises the legacy '__shop__' sentinel it replaced. Ask the flag, so
    // yard/office time never enters the billable-ticket path at all rather than
    // entering it and being turned away deeper in with a null ticketId.
    // Deliberately does NOT fold into `isShop`: that also drives
    // `customerId: isShop ? null : ...` below, and nulling it would discard the
    // Shop-vs-Office distinction every existing internal-time entry carries.
    const isInternalTime = customer?.internalTimeOnly === true;
    if (!isShop && !isInternalTime && shouldBuildTickets()) {
      const _clockInDate = clockInTime?.toDate ? clockInTime.toDate() : new Date();
      const ticketResult = await findOrCreateTicket(
        isShop ? null : customerId,
        isShop ? null : customer?.name,
        jobId || null,
        job?.title || null,
        _clockInDate,
        undefined,
        undefined,
        { splitBy: job?.splitBy || 'combined', assetIds: _splitAssetIds, personId }
      );
      ticketId = ticketResult.ticketId;

      // Create ticket line for this employee
      const empLineData = {
        type: 'EMPLOYEE',
        clockEntryId: null, // will update after clock entry created
        personId,
        personName: person.name,
        role,
        fromTime: clockInTime,
        toTime: null,
        hours: 0,
        // TASK-9646 S5 — EMPLOYEE lines never carry a customer rate (TASK-1653;
        // zeroEmployeeLineMoney backstops) and the rules now refuse a client
        // rate from this arm; `rate` stays on the clock entry (payroll).
        rate: 0,
        rateUnit: rateUnit || 'Hourly',
        lineAmount: 0,
        revenueCategory: revenueCategory || '',
        notes: notes || null
      };
      if (_isPieceRate) empLineData.quantity = 0;
      // ticketId is legitimately null when the customer is internalTimeOnly
      // (Shop, Office) — findOrCreateTicket skipped ticket creation by design.
      // Everything downstream already handles a null ticketId/ticketLineId.
      ticketLineId = ticketId ? await createTicketLine(ticketId, empLineData) : null;
      if (ticketId && ticketLineId) _ticketLineForCleanup = { ticketId, ticketLineId };
    }

    // Create clock entry
    const entryData = {
      personId,
      personName: person.name,
      clockIn: clockInTime,
      clockOut: null,
      status: 'active',
      jobId: jobId || null,
      jobTitle: job?.title || null,
      customerId: isShop ? null : (customerId || null),
      customerName: isShop ? 'Shop' : (customer?.name || null),
      ticketId: ticketId || null,
      ticketLineId: ticketLineId || null,
      roundToHalfHour: roundToHalfHour || false,
      role,
      rate,
      rateUnit: rateUnit || 'Hourly',
      notes: notes || null,
      createdAt: Timestamp.now(),
      // TASK-9941 F S2 — how this punch was authorised (code / override /
      // unverified); nothing for a member clocking themselves.
      ...clockCodeStampFields(_codeAuth, 'in'),
    };

    // TASK-586 — per-operator equipment tie (mirrors jobboard.js
    // submitStandaloneClockIn → _jbFinalizeStandaloneClockIn, TASK-558/491 —
    // keep in sync). ONLY for SELF clock-ins on equipmentTie-ON tenants: the
    // person clocking THEMSELVES in confirms their equipment via the inline
    // rows and assetIds[] is written ATOMICALLY on the create (single tap —
    // no second sheet, no tap-and-not-clocked-in gap). source:'clock_spa'
    // tells onClockEntryAssetValidate this entry came through the confirm UI.
    // Kiosk/Workforce ADMIN clock-ins of OTHER people write neither field →
    // legacy full-fill attribution intact (dispatcher-confirmed default).
    // OFF tenants never enter this block — entryData is byte-identical.
    // (!isShop guard: when "Shop / No Customer" is selected the hidden
    // #clock-job select can retain a stale job value — never stamp equipment
    // data on a shop entry.)
    if (_tieSelfClockIn) {
      // _entryAssetIds was resolved up front (see above) — identical value,
      // just hoisted so the split resolver could reuse it. Behavior unchanged.
      entryData.source = 'clock_spa';
      entryData.assetIds = _entryAssetIds;
      // TASK-1547 — the choice record rides the same create (never without an
      // assetIds array; OFF tenants / admin clock-ins of others write neither).
      if (_entryProvenance) entryData.assetProvenance = _entryProvenance;
    }

    // clock-double-tap — written under the id the open-shift lock was claimed
    // for (firestore.rules clockLockAllowsCreate). A refusal here means another
    // device clocked this person in a moment ago.
    try {
      // TASK-9713 — late/offline or far-off-clock self clock-ins land marked for review.
      await (window.ClockShiftGuard?.writeClockIn ? window.ClockShiftGuard.writeClockIn(setDoc, _newEntryRef, entryData) : setDoc(_newEntryRef, entryData));
    } catch (_entryErr) {
      try { await _csgClaim?.release?.(); } catch (_) { /* best effort */ }
      if (_ticketLineForCleanup) {
        try { await deleteDoc(doc(db, 'tenants', ClockState.tenantId, 'tickets', _ticketLineForCleanup.ticketId, 'ticketLines', _ticketLineForCleanup.ticketLineId)); } catch (_) { /* best effort */ }
      }
      if (_csg && _csg.isDuplicateRefusal(_entryErr)) {
        const _open = await _csg.openShifts(_clockCsgDeps(), ClockState.tenantId, personId);
        if (_open && _open.length) {
          console.warn('[ClockShiftGuard] clock-in refused: already clocked in', _open[0].id);
          window.showToast(_csg.alreadyInMessage(_open[0], { self: _clockIsSelfSelection(personId), personName: person?.name || '' }), 'warning', null, 9000);
          const _e = new Error('already clocked in'); _e._userMessageShown = true; throw _e;
        }
        if (!_csgClaim?.locked) {
          console.warn('[ClockShiftGuard] clock-in refused: another clock-in is still saving');
          window.showToast(_csg.pendingMessage({ self: _clockIsSelfSelection(personId), personName: person?.name || '' }), 'warning', null, 9000);
          const _e = new Error('clock-in pending elsewhere'); _e._userMessageShown = true; throw _e;
        }
      }
      throw _entryErr;
    }
    const clockEntryRef = _newEntryRef;

    // Audit: clock-in
    logClockAudit(ClockState.tenantId, 'CLOCK_IN', {
      source: 'clock-module',
      clockEntryId: clockEntryRef.id,
      personId, personName: person.name,
      jobId: jobId || null, jobTitle: job?.title || null,
      customerId: customerId || null, customerName: customer?.name || null,
      ticketId, ticketLineId,
      rate, role, rateUnit: rateUnit || 'Hourly',
      // TASK-4691 — attribution. false = the person's own PIN was typed, or
      // they clocked themselves. true = an office session wrote someone else's
      // punch under the unlocked-device override. Client-computed, so it is
      // evidence of intent, NOT proof — a workforce holder can write any field
      // straight from the SDK (clockEntries create :4127 / update :4171). P4 adds the
      // server-side immutable version.
      pinOverride: _usedPinOverride,
      // TASK-9941 F S2 — 'self' | 'code' | 'override' | 'unverified' (+ why)
      codeCheck: _codeAuth.how,
      codeCheckReason: _codeAuth.reason || null,
    });
    _clockConsumeGrant(personId); // TASK-9941 F S2 — one punch per grant

    // GPS ping (fire-and-forget, gated by tenant setting captureGpsOnClock)
    capturePingIfEnabled('captureGpsOnClock', {
      eventType: 'clock_in',
      tenant: ClockState.tenantId,
      ticketId: ticketId || null,
      clockEntryId: clockEntryRef.id,
      driverId: personId,
      driverName: person.name
    });

    // Update ticketLine with clock entry ID + bump employeeCount
    if (ticketId && ticketLineId) {
      await updateDoc(doc(db, 'tenants', ClockState.tenantId, 'tickets', ticketId, 'ticketLines', ticketLineId), {
        clockEntryId: clockEntryRef.id
      });
      // TASK-4699 — NO `ticketEmailDirtyAt` on clock-IN, deliberately. Nothing
      // billable exists yet: this links the line to the entry and bumps a crew
      // counter at the START of a shift. The hours, lines and notes that would
      // actually make an emailed copy wrong all land at clock-OUT, which does
      // stamp — so nothing is lost, and a ticket does not read "edited" for a
      // shift that has not been worked. (Ambiguous case, called out in the
      // TASK-4699 report: on an already-emailed ticket a second driver clocking
      // in does change the crew count on the customer's copy before clock-out.)
      await updateDoc(doc(db, 'tenants', ClockState.tenantId, 'tickets', ticketId), {
        employeeCount: increment(1),
        updatedAt: Timestamp.now()
      });
    }

    window.showToast(`${person.name} clocked in`, 'success');

    // Reset form
    const pinEl = document.getElementById('clock-pin');
    const notesEl = document.getElementById('clock-notes');
    const customerEl = document.getElementById('clock-customer');
    if (pinEl) pinEl.value = '';
    if (notesEl) notesEl.value = '';
    if (customerEl) customerEl.value = '';
    ClockState.selectedCustomer = null;
    document.getElementById('job-selection')?.classList.add('hidden');
    // TASK-586 — clear the equipment confirm rows with the rest of the form.
    _clockRenderInlineEquipment('');
    // Reset time-adjust (founder only). TASK-497: the clock-in time field is a
    // split date-time widget (renderSplitDateTime 'clock-in-time') with no single
    // `timeInput` element — the old `if (timeInput) timeInput.value=''` threw a
    // ReferenceError on EVERY clock-in, caught below and surfaced as a bogus
    // "Error clocking in" toast right after the success toast. Clear via the split
    // helper (COD-approved fix): this ALSO closes a latent stale-time leak — clockIn
    // reads getSplitDateTimeValue('clock-in-time') unconditionally (:5802), so an
    // un-cleared founder-adjusted time would carry into the NEXT person's clock-in.
    const timeGroup = document.getElementById('clock-time-adjust-group');
    if (timeGroup) timeGroup.style.display = 'none';
    setSplitDateTimeValue('clock-in-time', '');
  } catch (error) {
    // clock-double-tap — the refusal already told the person what happened;
    // a second "Error clocking in" toast on top would contradict it.
    if (error && error._userMessageShown) return;
    // A failure before the entry was written must not hold the person's lock
    // for 2 minutes (release() refuses to drop a lock on an OPEN shift).
    try { await _csgClaim?.release?.(); } catch (_) { /* best effort */ }
    console.error('[Clock] Clock in error:', error);
    // 🔴 2026-08-18 — this used to show a bare "Error clocking in" and swallow the
    // real exception into the console. On a yard phone there IS no console, so a
    // driver could only report the generic string and nobody could diagnose it:
    // Steven Alandt hit it repeatedly and it cost a morning of guessing. The
    // catch wraps findOrCreateTicket + createTicketLine, which run BEFORE the
    // clockEntry is written, so a failure there loses the punch entirely — the
    // worst case, because the driver believes they are on the clock and they are
    // not.
    //
    // Two changes, both aimed at making the NEXT report self-diagnosing:
    //  1. show the real message in the toast, so a phone screenshot is enough;
    //  2. write a breadcrumb an admin can read without the driver doing anything.
    // Best-effort and non-throwing: a diagnostic must never become a second
    // failure on top of the one it is reporting.
    const _msg = (error && (error.message || error.code)) ? String(error.message || error.code) : 'Unknown error';
    window.showToast(`Error clocking in — ${_msg.slice(0, 140)}`, 'error');
    try {
      window.logAudit?.('clock', 'CLOCK_IN_FAILED', {
        personId: personId || null,
        personName: person?.name || null,
        jobId: jobId || null,
        // code + message only. No tokens, no PII beyond the person already named
        // on the entry they were trying to create.
        errorCode: (error && error.code) ? String(error.code).slice(0, 80) : null,
        errorMessage: _msg.slice(0, 300),
        ticketBuildEnabled: !!(window.AppState?.settings?.buildTicketsFromClock),
        userAgent: String(navigator.userAgent || '').slice(0, 180),
      });
    } catch (_e) { /* never let the breadcrumb throw over the real error */ }
  } finally {
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">login</span> Clock In';
    _csgEnd();
  }
}

// clock-double-tap — the Firestore calls window.ClockShiftGuard makes, bound
// to this module's own firebase imports (the guard imports nothing itself).
function _clockCsgDeps() {
  // stuck-shift-morning — updateDoc/Timestamp/addDoc close a stale open shift
  // (the ordinary clock-out update) when the clock-in finds one.
  return { db, doc, collection, query, where, getDocs, getDoc, runTransaction, serverTimestamp, updateDoc, addDoc, Timestamp };
}

async function determineRoleAndRate(personId, jobId, customerId) {
  const person = ClockState.people.find(p => p.id === personId);
  let role = 'NON_CDL';
  const _rateDebug = { personId, personName: person?.name, jobId, customerId, crewType: person?.crewType, role: person?.role };

  // 0. Check dispatcher lineItemSelections first (overrides all rate matching)
  if (jobId) {
    const jobDay = ClockState.todaysJobDays.find(jd => jd.jobId === jobId);
    const selected = jobDay?.lineItemSelections?.[`person:${personId}`];
    if (selected?.rate != null) {
      window.ddLog?.('[Rate] %c DISPATCH OVERRIDE', 'color:#22c55e;font-weight:bold', { ..._rateDebug, path: 'lineItemSelections', rate: selected.rate, rateUnit: selected.rateUnit });
      return {
        role: person?.crewType || role,
        rate: selected.rate,
        roundToHalfHour: selected.roundToHalfHour || false,
        rateUnit: selected.rateUnit || 'Hourly',
        lineItemId: selected.lineItemId || null,
        contractId: selected.contractId || null,
        revenueCategory: selected.revenueCategory || ''
      };
    }
  }

  // 1. Check crewType first (new system — maps to item codes for billing rates)
  //    Fall back to person.role with slug normalization for legacy records without crewType
  let effectiveCrewType = person?.crewType || null;
  if (!effectiveCrewType || !CREW_TYPE_ITEM_CODE_MAP[effectiveCrewType]) {
    // Normalize legacy role labels → slugs (e.g. "CDL Driver" → "cdl_driver")
    const raw = (person?.crewType || person?.role || '').toLowerCase().replace(/\s+/g, '_');
    const LABEL_TO_SLUG = { 'cdl_driver': 'cdl_driver', 'driver': 'cdl_driver', 'cdl': 'cdl_driver', 'laborer': 'laborer', 'foreman': 'foreman', 'operator': 'operator', 'swamper': 'swamper', 'supervisor': 'supervisor', 'mechanic': 'mechanic' };
    effectiveCrewType = LABEL_TO_SLUG[raw] || (CREW_TYPE_ITEM_CODE_MAP[raw] ? raw : null);
  }
  if (effectiveCrewType) {
    role = effectiveCrewType; // Use crew type slug as role

    // Try contract rate for this crew type's item code
    if (customerId && customerId !== '__shop__') {
      const itemCode = CREW_TYPE_ITEM_CODE_MAP[effectiveCrewType];
      const contractMatch = await lookupContractRate(customerId, 'EMPLOYEE', role);
      if (contractMatch) {
        window.ddLog?.('[Rate] %c CONTRACT MATCH', 'color:#3b82f6;font-weight:bold', { ..._rateDebug, path: 'contract-crewType', effectiveCrewType, itemCode, rate: contractMatch.rate, rateUnit: contractMatch.rateUnit });
        return { role, rate: contractMatch.rate, roundToHalfHour: contractMatch.roundToHalfHour, rateUnit: contractMatch.rateUnit || 'Hourly', revenueCategory: contractMatch.revenueCategory || '' };
      }

      // Try item code default rate
      const data = await loadContractRates(customerId);
      if (data?.itemCodes) {
        const matchedCode = data.itemCodes.find(ic => ic.code === itemCode && ic.billable !== false);
        if (matchedCode && matchedCode.defaultRate != null) {
          window.ddLog?.('[Rate] %c ITEM CODE DEFAULT', 'color:#8b5cf6;font-weight:bold', { ..._rateDebug, path: 'itemCode-default', effectiveCrewType, itemCode, rate: matchedCode.defaultRate });
          return { role, rate: matchedCode.defaultRate, roundToHalfHour: matchedCode.roundToHalfHour === true, rateUnit: matchedCode.defaultRateUnit || 'Hourly', revenueCategory: '' };
        }
      }
      window.ddLog?.('[Rate] No contract/itemCode match for customer', { customerId, effectiveCrewType, itemCode });
    }

    // Fallback: check item codes for default rate (without customer)
    const defaultRates = window.AppState?.settings?.defaultRates;
    if (defaultRates && typeof defaultRates === 'object') {
      const isCdl = ['cdl_driver', 'foreman', 'operator', 'supervisor'].includes(effectiveCrewType);
      const rate = isCdl ? (defaultRates.cdl ?? FALLBACK_RATES.cdl) : (defaultRates.nonCdl ?? FALLBACK_RATES.nonCdl);
      window.ddLog?.('[Rate] %c TENANT DEFAULT RATES', 'color:#f59e0b;font-weight:bold', { ..._rateDebug, path: 'defaultRates', effectiveCrewType, isCdl, rate });
      if (isCdl) {
        return { role, rate, roundToHalfHour: false, rateUnit: 'Hourly', revenueCategory: '' };
      }
      return { role, rate, roundToHalfHour: false, rateUnit: 'Hourly', revenueCategory: '' };
    }

    // Ultimate fallback for crew types
    const isCdlFinal = ['cdl_driver', 'foreman', 'operator', 'supervisor'].includes(effectiveCrewType);
    const fallbackRate = isCdlFinal ? FALLBACK_RATES.cdl : FALLBACK_RATES.nonCdl;
    window.ddWarn?.('[Rate] %c FALLBACK $0', 'color:#ef4444;font-weight:bold', { ..._rateDebug, path: 'FALLBACK_RATES', effectiveCrewType, isCdlFinal, rate: fallbackRate, hint: 'No contract, no defaultRates in settings — rate is $0' });
    if (isCdlFinal) {
      return { role, rate: FALLBACK_RATES.cdl, roundToHalfHour: false, rateUnit: 'Hourly', revenueCategory: '' };
    }
    return { role, rate: FALLBACK_RATES.nonCdl, roundToHalfHour: false, rateUnit: 'Hourly', revenueCategory: '' };
  }

  // 2. Legacy: Check dispatch assignment + roster role
  if (jobId) {
    const jobDay = ClockState.todaysJobDays.find(jd => jd.jobId === jobId);
    if (jobDay) {
      const personName = person?.name || '';
      const isDriver = (jobDay.drivers || []).some(d => d === personId || d === personName);
      if (isDriver) role = 'CDL';
      else if (person?.role) {
        const r = person.role.toLowerCase();
        if (r.includes('cdl') || r.includes('driver') || r === 'cdl') role = 'CDL';
      }
    }
  } else {
    if (person?.role) {
      const r = person.role.toLowerCase();
      if (r.includes('cdl') || r.includes('driver') || r === 'cdl') role = 'CDL';
    }
  }

  // Try contract rate lookup
  if (customerId && customerId !== '__shop__') {
    const contractMatch = await lookupContractRate(customerId, 'EMPLOYEE', role);
    if (contractMatch) {
      window.ddLog?.('[Rate] %c LEGACY CONTRACT', 'color:#3b82f6;font-weight:bold', { ..._rateDebug, path: 'legacy-contract', role, rate: contractMatch.rate });
      return { role, rate: contractMatch.rate, roundToHalfHour: contractMatch.roundToHalfHour, rateUnit: contractMatch.rateUnit || 'Hourly' };
    }
  }

  // Fallback to FALLBACK_RATES
  const legacyFallback = role === 'CDL' ? FALLBACK_RATES.cdl : FALLBACK_RATES.nonCdl;
  window.ddWarn?.('[Rate] %c LEGACY FALLBACK $0', 'color:#ef4444;font-weight:bold', { ..._rateDebug, path: 'legacy-FALLBACK', role, rate: legacyFallback, hint: 'No crewType mapping, no contract — rate is $0' });
  return { role, rate: legacyFallback, roundToHalfHour: false, rateUnit: 'Hourly' };
}

// ============================================
// OVERNIGHT TICKET DATE RANGE
// ============================================
// TASK-10182 — the SERVER owns the ticket field workDateEnd; the clockEntries trigger
// onClockEntryTicketDateRange (functions services/ticketDateRange.js) extends
// it from the entry's clock-out. firestore.rules pins workDateEnd to billers,
// so the old browser write (updateTicketDateRange) would be refused for every
// clock / jobboard / workforce login. Do not reintroduce a client writer.

// ============================================
// FIND OR CREATE TICKET
// ============================================
// Lock map prevents duplicate tickets when multiple clock-ins fire concurrently
const _ticketLocks = new Map();

// tenantIdOverride (6th arg) lets callers outside clock.js (e.g. jobboard.js
// on the driver PIN page) use this lock + dedup path even when ClockState
// hasn't been populated. Defaults to ClockState.tenantId when omitted.
//
// extraFields (7th arg, optional object) lets callers stamp their own
// fields onto the ticket doc at creation time (source='clock_out', notes,
// workPerformed, etc). Merged AFTER the defaults so callers can override
// source/jobName. Ignored when an existing ticket is matched — first
// writer wins on metadata; we don't overwrite an established ticket.
//
// Note on concurrency: the lock Map below is per-tab. Two tabs or two
// devices clocking the same driver into the same work within milliseconds
// can still race — the 4-tier dedup query is the only thing catching that
// case and a tight-enough window will evade it. Acceptable for current
// traffic (driver PIN flow is one-person one-device). If we see cross-tab
// duplicates in prod, migrate to deterministic doc IDs like
// `{tenant}_{workDate}_{customerId}_{jobId}` and rely on Firestore set()
// with merge:false for atomic-create semantics.
async function findOrCreateTicket(customerId, customerName, jobId, jobTitle, clockInDate, tenantIdOverride, extraFields, splitContext) {
  const tenant = tenantIdOverride || ClockState.tenantId;
  if (!tenant) {
    throw new Error('findOrCreateTicket: tenantId not set (pass tenantIdOverride or initialize Clock)');
  }

  // TASK-1221 — resolve the per-job splitBy key. combined/absent → null (exact
  // legacy behavior, zero change). A non-null key scopes dedup + the lock to a
  // single rig/operator so a split job forms one ticket PER key.
  const _splitBy = (splitContext && splitContext.splitBy) || 'combined';
  // TASK-1221 — ensure the trucks roster is loaded before classifying a split
  // key. clock.js loads ClockState.trucks (active==true) on Clock init, but the
  // JOBBOARD PIN path forms tickets through this same helper WITHOUT that init,
  // so an empty roster would degrade every vehicle split to an op: key here while
  // the server (fresh trucks read) resolves veh: — a client/server divergence
  // that duplicates tickets. One-shot fetch mirrors the server's active==true
  // criteria; cached on ClockState.trucks. Only runs for split jobs w/ no roster.
  if (_splitBy && _splitBy !== 'combined' && !(ClockState.trucks && ClockState.trucks.length)) {
    try {
      const _tsnap = await getDocs(query(collection(db, 'tenants', tenant, 'trucks'), where('active', '==', true)));
      ClockState.trucks = _tsnap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (_) { /* classifier will op:-fallback, matching an empty server roster */ }
  }
  const _splitKey = (_splitBy && _splitBy !== 'combined')
    ? resolveSplitKey(_splitBy, splitContext || {}, _clockBuildPowerUnitClassifier())
    : null;

  // Internal-time gate (added 2026-05-15 — TASK #8): customers flagged
  // internalTimeOnly (Shop / Office / etc.) get a clockEntry but NO ticket
  // or ticketLine. Universal across every caller of this helper. Caller may
  // pass `extraFields.skipInternalTime` for the fast path (avoids the
  // Firestore read); otherwise we look up the customer doc as a defensive
  // fallback so a forgetful caller can't bypass the rule.
  if (extraFields && extraFields.skipInternalTime === true) {
    return { ticketId: null, isNew: false, skippedInternalTime: true };
  }
  if (customerId && customerId !== '__shop__') {
    try {
      // TASK-9694 S6a — directory row (crew sessions cannot read `customers`;
      // a refused read here would fall through and BUILD a ticket for Shop time).
      const _cust = await _clockCustomerSource().getOne(tenant, customerId);
      if (_cust && _cust.internalTimeOnly === true) {
        return { ticketId: null, isNew: false, skippedInternalTime: true };
      }
    } catch (_) { /* fall through — better to over-create than block clock-in */ }
  }

  // Use actual clock-in date if provided (avoids overnight bug where new Date() = next day)
  const today = clockInDate instanceof Date ? clockInDate : new Date();
  const workDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`; // YYYY-MM-DD local

  // Serialize by customer+job+date(+splitKey) to prevent race-condition
  // duplicates. TASK-1221: the splitKey keeps two concurrent clock-ins on
  // DIFFERENT rigs of the same job from serializing into one another's ticket.
  const lockKey = `${customerId}|${jobId || ''}|${workDate}|${_splitKey || ''}`;
  while (_ticketLocks.has(lockKey)) {
    await _ticketLocks.get(lockKey);
  }

  let resolveLock;
  const lockPromise = new Promise(r => { resolveLock = r; });
  _ticketLocks.set(lockKey, lockPromise);

  try {
    return await _findOrCreateTicketInner(tenant, workDate, customerId, customerName, jobId, jobTitle, extraFields, _splitBy, _splitKey);
  } finally {
    _ticketLocks.delete(lockKey);
    resolveLock();
  }
}

// TASK-9390 — twin of billing.js _ticketJobTypeFromJob (both new-ticket paths
// must snapshot the same value, or a clock-built ticket and a generated one
// print different Type rows for the same job).
function _clockTicketJobTypeCode(job) {
  const JT = window.JobTypes;
  if (!job || !JT || JT.isDefault(job.type)) return null;
  return String(job.type);
}
function _clockTicketJobType(job) {
  const JT = window.JobTypes;
  if (!job || !JT || JT.isDefault(job.type)) return null;
  return JT.labelForJob(job, JT.list(window.AppState?.settings || {})) || null;
}

// TASK-9363 Slice 1 — the Tier 4 fuzzy job-NAME match, guarded. Twin of
// ticketGateway.js tier4NameMatch (server: attachManualEntryTicket, repoint,
// switchJob) — keep the two identical; tests/task9363-copy-repeat-run.unit.cjs
// pins parity.
//
// Tier 4 (same customer + exact title) is for a job that was deleted and
// recreated. A "Copy to New Job" keeps its source's title AND customer, so the
// unguarded tier put a copy's crew on its SOURCE job's ticket whenever both ran
// the same day. A candidate whose jobId names a DIFFERENT job that STILL EXISTS
// is therefore never a Tier 4 match. Existence = an authoritative tenant-scoped
// read (jobExists), never ClockState.allJobs — a cache miss is not a deletion.
// A thrown read or an undefined answer counts as EXISTS: no merge, new ticket.
async function _clockTier4NameMatch(candidates, jobId, customerId, normTitle, jobExists) {
  if (!normTitle) return null;
  const known = new Map();
  for (const t of (candidates || [])) {
    if (!t) continue;
    if (t.customerId !== customerId && t.originalCustomerId !== customerId) continue;
    const tName = (t.jobName || t.jobTitle || "").trim().toLowerCase();
    if (!tName || tName !== normTitle) continue;
    const other = t.jobId || null;
    if (other && other !== jobId) {
      if (!known.has(other)) {
        let exists = true;
        try { exists = (await jobExists(other)) !== false; } catch (_) { exists = true; }
        known.set(other, exists);
      }
      if (known.get(other)) continue; // a live, different job — its ticket, not ours
    }
    return t;
  }
  return null;
}

async function _findOrCreateTicketInner(tenant, workDate, customerId, customerName, jobId, jobTitle, extraFields, splitBy, splitKey) {
  // Tiered dedup: try progressively looser matches to avoid creating duplicates.
  // Excludes split tickets, invoiced/archived/deleted tickets.
  // Prefers tickets with active clock lines over empty manual tickets.
  let match = null;

  // TASK-1221 — when the resolved job is in a split mode (splitKey set) the
  // dedup is scoped to tickets carrying the SAME splitKey; a combined clock-in
  // never attaches to a split ticket (its own key would collapse rigs back
  // together). `splitFrom`/source:'split' is the UNRELATED manual-split feature.
  const _isSplit = !!(splitKey && splitBy && splitBy !== 'combined');

  // TASK-10107 — a SEALED, unbilled return estimate only; the client flag alone
  // exempts nothing. Twin of functions/src/shared/returnEstimate.js isReturnEstimate
  // (keep identical; tests/task10107-return-estimate-seal.unit.cjs runs both).
  const _isReturnEstimate = (t) => {
    if (!t || t.returnEstimate !== true) return false;
    const s = t.returnEstimateSeal;
    if (!s || typeof s !== 'object' || !t.jobId || s.jobId !== t.jobId || s.mirrorOf !== t.mirrorOf) return false;
    if (t.status !== 'draft' || t.invoiced === true || t.invoiceNumber || t.invoicedAt) return false;
    return !(Number(t.grandTotal) > 0);
  };
  const _isValidMatch = (data) => {
    if (data.splitFrom) return false; // manual-split tickets are never clock targets
    // TASK-842-A3 — the rental engine's draft tickets are server-owned slice
    // containers; a same-day clock-in must open its own ticket, never attach
    // EMPLOYEE lines onto the rental draft.
    if (data.rentalTicket === true) return false;
    // TASK-10107 — the $0 "return estimate" (mirror of a delivery ticket) is a
    // planning draft dated the expected return day; a clock-in that day must
    // open its own ticket, never attach pay lines onto the estimate. SEALED +
    // unbilled only (_isReturnEstimate above).
    if (_isReturnEstimate(data)) return false;
    const s = data.status;
    if (s === 'invoiced' || s === 'archived' || s === 'deleted' || s === 'locked' || s === 'sent') return false;
    // TASK-10026 — a ticket that already went to the customer is closed to the
    // crew (the server stamps driverEditLocked with this actor on send). A new
    // clock-in opens its own ticket instead of attaching hours the driver could
    // never put on the sent one.
    if (data.driverEditLocked === true && data.driverEditLockedBy === 'ticket-sent') return false;
    // TASK-1221 splitBy scoping — a split ticket is a VALID clock target (unlike
    // splitFrom), but only for the matching splitKey; combined clock-ins ignore
    // every split ticket so the two modes never cross-attach.
    if (_isSplit) { if (data.splitKey !== splitKey) return false; }
    else if (data.splitKey) { return false; }
    return true;
  };

  try {
    // Broad query: all draft/ready tickets for this workDate, filter client-side
    const dayQuery = query(
      collection(db, 'tenants', tenant, 'tickets'),
      where('workDate', '==', workDate),
      where('status', 'in', ['draft', 'ready'])
    );
    const daySnap = await getDocs(dayQuery);
    const candidates = daySnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(_isValidMatch);

    if (jobId) {
      // Tier 1: exact jobId + customerId match (current fields)
      match = candidates.find(t => t.jobId === jobId && t.customerId === customerId);

      // Tier 2: exact jobId match (customer may have been edited on the ticket)
      if (!match) match = candidates.find(t => t.jobId === jobId);

      // Tier 3: originalJobId match (job was deleted/recreated, but original alias still matches)
      if (!match) match = candidates.find(t => t.originalJobId === jobId);

      // Tier 4: fuzzy — same customer + similar job name (catches renamed/recreated jobs)
      // TASK-1221 — SKIP for split jobs: a fuzzy job-NAME match would merge two
      // different rigs (Steven's same-named-jobs workaround is exactly this). The
      // splitKey is the authoritative grouping; Tier 1–3 already scope to it via
      // _isValidMatch, so a fuzzy fallback here would defeat the split.
      // TASK-9363 — never a DIFFERENT job that still exists (a copy and its
      // source working the same day). Authoritative read, never the cache.
      if (!match && jobTitle && !_isSplit) {
        const normTitle = (jobTitle || '').trim().toLowerCase();
        match = await _clockTier4NameMatch(candidates, jobId, customerId, normTitle,
          async (otherJobId) => (await getDoc(doc(db, 'tenants', tenant, 'jobs', String(otherJobId)))).exists());
        if (match) console.log('[Clock] Fuzzy name match found ticket:', match.id, 'for job:', jobTitle);
      }
    } else {
      // No jobId: match by customer with no job
      match = candidates.find(t => (t.customerId === customerId || t.originalCustomerId === customerId) && !t.jobId);
    }
  } catch (error) {
    console.warn('[Clock] Ticket dedup query failed:', error.message);
    // Still proceed to create — better than blocking clock-in
  }

  if (match) {
    return { ticketId: match.id, isNew: false };
  }

  // Create new ticket
  // TASK-1106 — authoritative job resolve (money bug: PO drives the invoice).
  // ClockState.allJobs is THIS device's client cache; on a close-out device
  // (driver/kiosk = a different session than the dispatcher who set the PO)
  // it can be stale/partial or exclude the job, and the old unconditional
  // `job?.poNumber || null` silently wrote null even though the job doc had
  // the PO since creation (TITS/Loenbro ticket 100224). NEW-ticket path ONLY
  // (existing matches returned above are untouched): when jobId is set and
  // the cached job is missing OR its poNumber is null/undefined — NOT '' (an
  // empty string means a human deliberately cleared it; never override that)
  // — fetch the job doc and resolve poNumber + the sibling header fields
  // (location/wellName/wellNumber/vendorNumber) from it. One
  // conditional read per newly-created linked ticket. Fetch failure falls
  // back to the cached job — never block clock-out.
  // TASK-9585 S7b — same refetch when the cached job carries a CONFIRMED permit
  // cost (permitCostConfirmed): the PERMIT line below turns it into money, and
  // the cache can still hold a permit (or a confirmation) the office removed
  // minutes ago. The fresh doc decides whether the line is added.
  let job = jobId ? ClockState.allJobs.find(j => j.id === jobId) : null;
  if (jobId && (!job || job.poNumber === null || job.poNumber === undefined || job.permitCostConfirmed)) {
    // TASK-9585 S7c — the cached job still builds the ticket header when the
    // read finds no doc, throws, or (persistentLocalCache, offline) answers from
    // this device's cache, but its permitCostConfirmed is NOT billed: a yes that
    // could not be re-read from the server never becomes a PERMIT line here
    // (the office can add it). Copy, never mutate ClockState.allJobs.
    let _jobFresh = false;
    try {
      const jobSnap = await getDoc(doc(db, 'tenants', tenant, 'jobs', jobId));
      if (jobSnap.exists()) {
        job = { id: jobSnap.id, ...jobSnap.data() };
        _jobFresh = !jobSnap.metadata?.fromCache;
      }
    } catch (err) {
      console.warn('[Clock] Authoritative job fetch failed, using cached job:', err?.message || err);
    }
    if (!_jobFresh && job?.permitCostConfirmed) job = { ...job, permitCostConfirmed: null };
  }
  // TASK-1339 — NEVER-TRAP: this helper runs BEFORE the clock entry is written
  // on the clockIn() path, so a hard throw here would stop someone starting a
  // shift over a numbering read. If allocation fails (after its own retry) we
  // still create the ticket, but with NO number and a `ticketNoPending` flag —
  // a flagged, findable ticket beats both a blocked clock-in and the old
  // behavior (silently restarting the sequence at 1001, which collides with a
  // real ticket the moment TITS is at ~100,400).
  let ticketNo = null;
  let ticketNoPending = false;
  try {
    ticketNo = await getNextTicketNumber(tenant);
  } catch (err) {
    ticketNoPending = true;
    console.error('[Clock] Ticket number allocation failed — creating ticket unnumbered:', err?.message || err);
    window.logError?.(err, { module: 'clock', operation: 'TICKET_NUMBER_ALLOC', tenant });
    window.showToast?.('Ticket created without a number — dispatch will assign one.', 'warning');
  }

  const ticketData = {
    ...(ticketNo != null ? { ticketNo } : {}),
    ...(ticketNoPending ? { ticketNoPending: true } : {}),
    date: Timestamp.now(),
    workDate,
    status: 'draft',
    customerId: customerId || null,
    customerName: customerName || '',
    jobId: jobId || null,
    jobName: jobTitle || job?.title || null,
    location: job?.location || job?.address || null,
    wellName: job?.wellName || null,
    wellNumber: job?.wellNumber || null,
    poNumber: job?.poNumber || null,
    vendorNumber: job?.vendorNumber || null,
    // TASK-9390 — the job type LABEL snapshot, printed on the ticket/invoice
    // when AR/AP → Templates turns it on. Null for the default "Job" type; the
    // same rule billing.js generateTicketFromJob uses (_ticketJobTypeFromJob).
    jobType: _clockTicketJobType(job),
    jobTypeCode: _clockTicketJobTypeCode(job), // the slug, so a job move judges by code
    // TASK-9646 S4a — no `jobNotes` copy: every crew session reads every key
    // on this ticket; the job's internal note stays on the job (S6b: jobsPrivate).
    employees: [],
    grandTotal: 0,
    fastTrax: !!(job?.permitSettings?.usedFastTrax),
    source: 'clock',
    originalJobId: jobId || null,
    originalCustomerId: customerId || null,
    originalWorkDate: workDate,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    // Caller-supplied fields (source override, notes, workPerformed, etc).
    // Spread last so explicit callers win over defaults. See findOrCreateTicket
    // header comment for the contract.
    ...(extraFields && typeof extraFields === 'object' ? extraFields : {}),
    // TASK-1221 — split stamps LAST (authoritative; a caller's extraFields must
    // never override the resolved split identity). Absent for combined jobs.
    ...(_isSplit ? { splitBy, splitKey, splitLabel: _clockSplitLabel(splitKey) } : {}),
    // TASK-10006 S3b — who made it (firestore.rules pin it to the signed-in
    // uid): a ticket on a return job settles its rental pickup only when the
    // office or that day's crew made it. LAST, so no caller can override it.
    ...(auth?.currentUser?.uid ? { createdByUid: auth.currentUser.uid } : {}),
  };

  const ticketRef = await addDoc(collection(db, 'tenants', tenant, 'tickets'), ticketData);

  // Apply permit data from job if available. (The old "founder only" note here
  // named no gate in code; the AI read is offered only behind hasAiPack in the
  // job modal.) TASK-9585 S7 — the PERMIT line is priced ONLY from a cost an
  // office user said yes to (dispatch.js _offerPermitCostSuggestion writes
  // permitCostConfirmed). A bare `permitParsedCost` written by the old callable
  // with no yes is never copied onto a NEW ticket; nothing already billed is
  // rewritten.
  // The yes covers the files it was read from: a permit removed after the yes
  // takes the confirmation with it (paths: fullPath, or a legacy row's fullUrl).
  const _permitOnJob = new Set();
  for (const pm of (Array.isArray(job?.permits) ? job.permits : [])) {
    if (pm?.fullPath) _permitOnJob.add(String(pm.fullPath));
    const m = /\/o\/([^?]+)/.exec(String(pm?.fullUrl || ''));
    if (m) { try { _permitOnJob.add(decodeURIComponent(m[1])); } catch (_) { /* not a Storage URL */ } }
  }
  const _permitSrc = Array.isArray(job?.permitCostConfirmed?.sourceDoc) ? job.permitCostConfirmed.sourceDoc : [];
  const _permitConfirmedAmt = Number(job?.permitCostConfirmed?.amount);
  const _permitCost = (Number.isFinite(_permitConfirmedAmt) && _permitConfirmedAmt > 0 && job?.permitCostConfirmed?.byUid
    && _permitSrc.length > 0 && _permitSrc.every(p => _permitOnJob.has(String(p)))) ? _permitConfirmedAmt : 0;
  if (job && (_permitCost > 0 || job.permitSettings?.usedFastTrax)) {
    const updates = {};

    if (job.permitSettings?.usedFastTrax) {
      updates.fastTrax = true;
    }

    if (_permitCost > 0) {
      await addDoc(collection(db, 'tenants', tenant, 'tickets', ticketRef.id, 'ticketLines'), {
        type: 'PERMIT',
        description: job.permitParsedType ? `Load permit (${job.permitParsedType})` : 'Load permit',
        lineAmount: _permitCost,
        source: 'ai_parsed',
        jobId: jobId,
        createdAt: Timestamp.now()
      });
      // TASK-9646 S2 — no header grandTotal here: the PERMIT line write above
      // re-totals the ticket on the server (onTicketLineWrite), after the
      // permit-markup pass has priced it.
    }

    if (Object.keys(updates).length > 0) {
      updates.updatedAt = Timestamp.now();
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketRef.id), updates);
    }
  }

  return { ticketId: ticketRef.id, isNew: true };
}

// TASK-1339 — converged on billing.js's allocator (ONE owner). This used to be
// a second copy carrying the same two money bugs: a catch that swallowed any
// failure and returned the 1001 seed while TITS sits at ~100,400, and a query
// with no limit(1). Called through window.BillingModule, never an ES import —
// a ?v=-busted cross-import would be a separate module instance (Pitfall #1).
// Throws rather than inventing a number: a misnumbered ticket collides with a
// real one, and an unnumbered one goes invisible to every ticketNo-ordered
// report. Both are worse than a visible failure at clock-out.
async function getNextTicketNumber(tenant) {
  const alloc = window.BillingModule?.getNextTicketNumber;
  if (typeof alloc !== 'function') {
    throw new Error('Ticket numbering is unavailable (billing module not loaded)');
  }
  return alloc(tenant);
}

// TASK-9666/9667 — the Price Book item's own flags, carried onto the ticket line
// it creates. TWIN of billing.js _priceBookLineTicketFlags — keep identical
// (tests/task9667-per-item-invoice-hide.unit.cjs extracts both).
//   noCharge: the item is a deliberate "Show at $0" — Review prints N/C.
//   invoiceHidePending: the item's "Invoice" box is unticked. NOT a hide by
//     itself: the server (onTicketLineWrite → priceBookLineFlags) re-reads the
//     Price Book item and stamps includeOnInvoice:false only when the tenant
//     turned on per-item hiding. The member/driver rules arms may not write
//     includeOnInvoice:false, so writing it here would fail a close-out.
function _clockPriceBookLineFlags(li) {
  const out = {};
  if (!li) return out;
  if (li.noCharge === true && !(Number(li.rate) || 0)) out.noCharge = true;
  if (li.includeOnInvoice === false) out.invoiceHidePending = true;
  return out;
}

// TASK-10177 — the `includeOnInvoice` key on a close-out EQUIPMENT CREATE (all
// four lineData writers: confirmClockOut, confirmClockOutFromReview,
// applyReviewData, saveTicketFromForm). The row carries the Price Book item's
// Invoice box (lookupContractRate / the dispatcher pick). The old literal
// `eq.includeOnInvoice ?? true` wrote `false` for an unticked item, and the
// member/driver create arms of firestore.rules allow the key only absent or
// true — so a clock-only member's whole close-out was REFUSED. A non-biller now
// OMITS the key: the line is born `ratePending`, and the server rate-authority
// pass (ticketLineRateAuthority decideLineRate) stamps includeOnInvoice from the
// Price Book item it matches itself, never from the line. The two writers the
// rules already let author `false` keep writing it: a biller (window.canEditMoney,
// mirror of the canEditMoney arm) and a Workforce holder (the workforce arm has
// no includeOnInvoice pin). That matters for a $0 / inactive unticked item: the
// server takes its "unpriced" branch and does NOT stamp the key, and `false` is
// what keeps such a line off the invoice and out of the Unpriced-equipment
// Ready gate (equipmentLineUnpriced) — so their close-outs stay exactly as
// before (COD r1 Independent Reviewer). For a clock-only member / driver that
// $0 case now lands visible-at-$0 and badged for the biller, instead of the
// whole close-out being refused. A wrong mirror can only make this omit
// (always allowed) or fall back to the pre-fix shape.
// Workforce mirror (rules hasModule(t,'workforce')): a REAL members doc whose
// modules list holds 'workforce'. Deliberately NOT hasWorkforceAccess() (it also
// passes founder / demo / no-PIN kiosks the rules may not); a SYNTHETIC grant
// (compliance partner via ?client=) has no members doc for the rules to read
// (COD r2 Edge Case); a status mStOk refuses (trimmed, any case) is refused here
// too (COD r2 Code Reviewer) — the regex only ever omits MORE than the rules.
// No driver/public-mode test: the Clock view is unreachable in driver-PIN mode
// (app.js allowedViews) and those sessions carry no members-doc memberAccess.
function _clockEquipInvoiceField(eq) {
  if (!eq || eq.includeOnInvoice !== false) return { includeOnInvoice: true };
  const ma = window.AppState?.memberAccess;
  const wf = !!ma && !ma.synthetic && Array.isArray(ma.modules) && ma.modules.includes('workforce')
    && !/revoked|inactive|pending|suspended|removed/i.test(ma.status || '');
  return (wf || window.canEditMoney?.() === true) ? { includeOnInvoice: false } : {};
}

// ===========================================================================
// TASK-9665 — ONE way to record consumables: the customer's Price Book items
// ("Show on field ticket") at close-out, on EVERY close-out path — the direct
// save (confirmClockOut), the review screen (confirmClockOutFromReview /
// applyReviewData) and the form's own Save (saveTicketFromForm). Before this
// the qty boxes were LOADED only for `!isFounder()` and WRITTEN only by
// confirmClockOut, so a TITS office close-out (review path) never recorded a
// quantity at all. The Templates "Field Activity" Dumps/Water counts are not
// touched here (custom-section data, reconciled server-side).
//
// ONE line per Price Book item per clock session on the ticket
// (ticket × lineItemId × clockEntryId — the per-session key EQUIPMENT and
// EMPLOYEE lines already use, TASK-427). Re-opening Build Ticket, Apply then
// Confirm, Save then Confirm, a second tab: all UPDATE that line (or delete it
// when the box is cleared) — never a twin. A coworker's own session on the
// same ticket keeps its own line: two people who each used 4 bbl is 8 bbl.
//
// MONEY: the browser never decides the price. The line carries the item's
// rate for display plus the request marker `priceBookRatePending`; the server
// (onTicketLineWrite → priceBookLineFlags.resolvePriceBookRatesForTicket)
// re-reads the Price Book item under the TICKET's customer and rewrites
// rate/lineAmount from it. firestore.rules REQUIRES the marker on a FIELD_ITEM
// create from a clock-only member or a driver session, and pins rate /
// lineAmount / lineItemId on their updates — so a raw-SDK rate is overwritten,
// not trusted. `contractId` names the Price Book the person saw: the job day's
// book when it is Active (TASK-10190 — the same book dispatch prices the day's
// equipment from via lineItemSelections), else the FIRST Active contract
// (loadFieldTicketItems). Invoice column / hidden-line settings still read docs[0] (billing.js
// invoiceContractInfoFromDocs / index.js sendInvoiceEmail) — layout, not price; the
// line's own rate is the server's, from the line's contractId.
// ===========================================================================
function _clockReadFieldItemQtys() {
  const out = {};
  for (const li of (ClockState.fieldTicketLineItems || [])) {
    if (!li || !li.id) continue;
    const el = document.getElementById(`ft-qty-${li.id}`);
    if (!el) continue; // not rendered (appliesTo filter) → whatever is saved stays
    const n = parseFloat(el.value);
    out[li.id] = Number.isFinite(n) && n > 0 ? n : 0;
  }
  return out;
}

// Pure plan (unit-tested): which lines to create / update / remove.
//   items — the Price Book items shown at close-out
//   qtys  — { lineItemId: qty } read from the form; an item absent from the
//           map was not on screen and is left alone
//   saved — { lineItemId: { docId, quantity } } THIS session's lines on the ticket
function _clockFieldItemPlan(items, qtys, saved) {
  const plan = { create: [], update: [], remove: [] };
  const q = qtys || {};
  const s = saved || {};
  for (const li of (items || [])) {
    if (!li || !li.id || !Object.prototype.hasOwnProperty.call(q, li.id)) continue;
    const qty = Number(q[li.id]) || 0;
    const cur = s[li.id] || null;
    if (cur && cur.docId) {
      if (qty <= 0) plan.remove.push({ li, docId: cur.docId });
      else if (Math.abs((Number(cur.quantity) || 0) - qty) > 1e-6) plan.update.push({ li, docId: cur.docId, qty }); // boxes step 0.01
    } else if (qty > 0) {
      plan.create.push({ li, qty });
    }
  }
  return plan;
}

// This session's FIELD_ITEM lines already on the ticket → ClockState._fieldItemSaved
// and the qty boxes (so a re-opened Build Ticket shows what was recorded).
async function loadSavedFieldItemLines(ticketId, entry) {
  ClockState._fieldItemSaved = {};
  if (!ticketId || !entry?.id || !(ClockState.fieldTicketLineItems || []).length) return;
  const tenant = ClockState.tenantId;
  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'),
      where('type', '==', 'FIELD_ITEM')
    ));
    snap.docs.forEach(d => {
      const x = d.data() || {};
      if (!x.lineItemId || x.clockEntryId !== entry.id || x.derivedFromBinding) return;
      ClockState._fieldItemSaved[x.lineItemId] = { docId: d.id, quantity: Number(x.quantity) || 0 };
    });
    for (const [lineItemId, s] of Object.entries(ClockState._fieldItemSaved)) {
      const el = document.getElementById(`ft-qty-${lineItemId}`);
      if (el) el.value = s.quantity;
    }
  } catch (err) {
    console.warn('[Clock] Error loading saved field ticket items:', err);
  }
}

// The doc id of this session's line for an item is DETERMINISTIC (COD Code
// Reviewer, 2026-10-12): two tabs / a stale tab can only ever land on the SAME
// doc, so a twin line cannot exist by construction. Legacy addDoc ids are still
// found by loadSavedFieldItemLines' query.
function _clockFieldItemDocId(entry, li) {
  return `fi_${entry.id}_${li.id}`;
}

async function clockApplyFieldTicketItems(ticketId, entry, qtys) {
  if (!ticketId || !entry) return;
  const items = ClockState.fieldTicketLineItems || [];
  if (!items.length) return;
  const tenant = ClockState.tenantId;
  const saved = ClockState._fieldItemSaved || (ClockState._fieldItemSaved = {});
  const plan = _clockFieldItemPlan(items, qtys, saved);
  const lineRef = (id) => doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', id);
  // quantity + the marker only — rate/lineAmount are the server's (rules pin
  // them for non-billing writers; the server recomputes from the Price Book).
  const qtyPatch = (qty) => ({ quantity: qty, priceBookRatePending: true, updatedAt: Timestamp.now() });
  const writeQty = async (docId, qty) => updateDoc(lineRef(docId), qtyPatch(qty));
  // Round-2 COD (Domain 2026-10-12): every caller sits inside a close-out that
  // has ALREADY closed the shift. A write that fails here (offline — a
  // transaction cannot commit without a connection — a rules denial, or
  // contention) must never throw past the clock-out: log it, tell the person,
  // keep going. The next Build Ticket re-open re-reads the lines and retries.
  const failed = [];
  const guard = async (li, fn) => {
    try { await fn(); return true; }
    catch (e) {
      failed.push(li.description || li.id);
      console.error('[Clock] field item write failed:', li.id, e);
      window.logError?.(e, { module: 'clock', operation: 'FIELD_ITEM_WRITE', ticketId, lineItemId: li.id });
      return false;
    }
  };
  for (const { li, docId } of plan.remove) {
    if (await guard(li, () => deleteDoc(lineRef(docId)))) delete saved[li.id];
  }
  for (const { li, docId, qty } of plan.update) {
    if (await guard(li, () => writeQty(docId, qty))) saved[li.id].quantity = qty;
  }
  const fieldItemData = {};
  for (const { li, qty } of plan.create) {
    const lineId = _clockFieldItemDocId(entry, li);
    // Another tab of this same session may have written it already: update,
    // never a second create. ONE transaction (2nd COD Code Reviewer
    // 2026-10-12): a plain getDoc -> setDoc let two tabs both miss the read,
    // and the loser's setDoc became an update carrying money fields the
    // rules pin -- denied after the shift had already closed.
    // The line is BORN AT $0: rules refuse a member/driver FIELD_ITEM create
    // with any rate/lineAmount, and the server is the only writer of money.
    const wrote = await guard(li, () => runTransaction(db, async (tx) => {
      const existing = await tx.get(lineRef(lineId));
      if (existing.exists()) {
        if (Math.abs((Number(existing.data()?.quantity) || 0) - qty) > 1e-6) tx.update(lineRef(lineId), qtyPatch(qty));
        return;
      }
      tx.set(lineRef(lineId), {
      type: 'FIELD_ITEM',
      lineItemId: li.id,
      description: li.description,
      quantity: qty,
      rate: 0,                                    // server-priced (priceBookRatePending)
      rateUnit: li.rateUnit || 'Hourly',
      lineAmount: 0,
      category: li.category || 'Other',
      appliesTo: li.appliesTo || null,
      clockEntryId: entry.id || null,            // per-session key (TASK-427 shape)
      contractId: ClockState.contractId || null,  // the Price Book the person saw
      ...(li.itemCode ? { itemCode: String(li.itemCode) } : {}), // lets the derived-line duplicate guard see it
      priceBookRatePending: true,                 // server re-prices from the Price Book
      // TASK-9666/9667 — Price Book flags ride onto the line. Both are
      // ADDED ONLY when the item uses them, so every other line is
      // written exactly as before.
      ..._clockPriceBookLineFlags(li),
      createdAt: Timestamp.now()
      });
    }));
    if (!wrote) continue;
    saved[li.id] = { docId: lineId, quantity: qty };
    fieldItemData[li.id] = qty;
  }
  if (failed.length) {
    window.showToast?.(`Clocked out, but ${failed.length} field item${failed.length === 1 ? '' : 's'} did not save (${failed.join(', ')}). Reopen Build Ticket when you have signal.`, 'error');
  }
  const changed = plan.create.length + plan.update.length + plan.remove.length - failed.length;
  if (changed === 0 && Object.keys(saved).length === 0) return;
  // Field ticket metadata on the ticket doc. TASK-4596 — dot-paths, not a
  // wholesale `fieldTicketData: {...}` replace: this ticket can already carry
  // driverSignatureUrl/stampPhotoUrl/emailSentTo from an earlier session.
  // lineQuantities = this session's current quantities (the lines are the record).
  // 🔴 No `fieldTicketData.shiftNotes` here (COD Code Reviewer 2026-10-12): a
  // shift note is one PERSON's private record (TASK-9693) and the customer's
  // field-ticket email falls back to that field when workPerformed is empty —
  // the old writer never fired for a live tenant (0 of 1,056 tickets carry it).
  const lineQuantities = {};
  for (const [id, s] of Object.entries(saved)) lineQuantities[id] = s.quantity;
  const ftMeta = {
    'fieldTicketData.submittedBy': entry.personId || null,
    'fieldTicketData.submittedByName': entry.personName || '',
    'fieldTicketData.submittedAt': Timestamp.now(),
    'fieldTicketData.lineQuantities': lineQuantities,
    updatedAt: Timestamp.now()
  };
  await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), ftMeta);
  await _clockStampPriceBookHint(tenant, ticketId);
}

// TASK-10182 — fieldTicketData.contractId is the ticket's price-book hint
// (which of the customer's Active contracts prices its FIELD_ITEM / PPE lines:
// priceBookLineFlags _txFindPriceBookItem, ppeBilling findItemByCode,
// consumableStock). firestore.rules pins it to billers, so only a biller
// (window.canEditMoney) writes it, in its OWN write: a refusal must never take
// the close-out's signatures / quantities down with it. Without a hint every
// reader falls back to the first Active contract by document id, the same
// pick loadFieldTicketItems / getFieldTicketItems made for ClockState.contractId.
async function _clockStampPriceBookHint(tenant, ticketId) {
  const contractId = ClockState.contractId;
  if (!contractId || !ticketId) return false;
  if (!(typeof window.canEditMoney === 'function' && window.canEditMoney() === true)) return false;
  try {
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
      'fieldTicketData.contractId': contractId, updatedAt: Timestamp.now()
    });
    return true;
  } catch (e) {
    console.warn('[Clock] price-book hint not saved:', e?.code || e?.message);
    return false;
  }
}

// TASK-9646 S5 — an UPDATE from this (member / workforce / driver) arm never
// re-sends money: firestore.rules tlMoneyPinned refuses a `rate` change and a
// non-zero `lineAmount` without the S2 amountPending request. A DOWNTIME /
// STANDBY line a biller priced in Review therefore keeps its rate when the
// driver re-saves its window; the server re-derives the amount.
function _clockLineUpdatePayload(lineData) {
  const { rate: _rate, lineAmount: _lineAmount, ratePending: _rp, ...rest } = lineData || {};
  return { ...rest, amountPending: true };
}

async function createTicketLine(ticketId, lineData) {
  const tenant = ClockState.tenantId;
  // No ticket, no ticket line. findOrCreateTicket returns ticketId:null on
  // purpose for an internalTimeOnly customer (Shop/Office) — that is the
  // answer, not a failure. Passing it through built a 'tickets/null/ticketLines'
  // path, and the Firestore SDK throws "null is not an object (evaluating
  // 'n.indexOf')" when it parses a null path segment. That threw BEFORE the
  // clockEntry write, so the driver lost the punch while believing they were
  // clocked in. Return null and let the callers' existing `ticketId &&
  // ticketLineId` guards do their job.
  if (!ticketId) return null;
  lineData.createdAt = Timestamp.now();
  const lineRef = await addDoc(
    collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'),
    lineData
  );
  return lineRef.id;
}

// Returns Map of assetId → { docId, toTime } for equipment already on this ticket.
// SESSION-SCOPED when clockEntryId is passed: the map only includes equipment lines
// belonging to that same clock session (or legacy lines with no clockEntryId). This
// is deliberate and SURVIVES TASK-442 — the create/extend paths must only extend the
// CURRENT session's line; a 2nd session (new clockEntry, e.g. re-clock after lunch)
// finds no match → creates its OWN line with its OWN [from,to] window. The post-write
// dedup (deduplicateTicketLines / server runServerSideDedup) then consolidates all of
// an asset's per-session lines into ONE line whose hours are the INTERVAL-UNION of
// those windows (TASK-442). If we extended across sessions here instead, the merged
// span would be a naive bounding box (8am→5pm = 9h for an 8-12 + 1-5 handoff) and
// re-introduce a billing error — the per-session create + union-dedup split is correct.
async function getExistingEquipmentMap(ticketId, clockEntryId = null) {
  const tenant = ClockState.tenantId;
  try {
    const snap = await getDocs(
      query(
        collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'),
        where('type', '==', 'EQUIPMENT')
      )
    );
    const map = new Map();
    snap.docs.forEach(d => {
      const data = d.data();
      if (!data.assetId) return;
      // TASK-427 session scoping: when a clockEntryId is passed (every post-fix
      // clock-out path passes entry.id), the current session may ONLY extend/match
      // a line stamped with that SAME clockEntryId. A line from a different session
      // — or an un-stamped legacy line (Concern 2: don't let a new session silently
      // absorb a pre-fix null line and under-bill) — is excluded, so this session
      // creates its own line. Legacy null lines still merge among themselves via
      // deduplicateTicketLines for back-compat; they're just never extended here.
      if (clockEntryId) {
        if (data.clockEntryId !== clockEntryId) return;
      }
      map.set(data.assetId, { docId: d.id, toTime: data.toTime });
    });
    return map;
  } catch (err) {
    console.warn('[Clock] Error checking existing equipment:', err);
    return new Map();
  }
}
// TASK-442: interval-UNION of a group's [fromTime,toTime] windows, in hours.
// Overlapping windows (team-driving: two drivers, one truck, same hours) are counted
// ONCE; non-overlapping windows (sequential handoff / same-driver lunch) are summed;
// idle gaps between windows are NOT billed. Mirrors intervalUnionHours in
// functions/src/services/ticketLineDedup.js. Missing/invalid bounds are skipped so a
// malformed line can't NaN the result. Returns 0 when no line has a valid window.
function clockIntervalUnionHours(lines) {
  const ivs = lines.map(l => {
    const from = l.fromTime?.toDate?.() || (l.fromTime ? new Date(l.fromTime) : null);
    const to = l.toTime?.toDate?.() || (l.toTime ? new Date(l.toTime) : null);
    return { from: from ? from.getTime() : NaN, to: to ? to.getTime() : NaN };
  }).filter(iv => Number.isFinite(iv.from) && Number.isFinite(iv.to) && iv.to > iv.from)
    .sort((a, b) => a.from - b.from);
  if (ivs.length === 0) return 0;
  let totalMs = 0, curFrom = ivs[0].from, curTo = ivs[0].to;
  for (let i = 1; i < ivs.length; i++) {
    if (ivs[i].from <= curTo) { if (ivs[i].to > curTo) curTo = ivs[i].to; }
    else { totalMs += curTo - curFrom; curFrom = ivs[i].from; curTo = ivs[i].to; }
  }
  totalMs += curTo - curFrom;
  return parseFloat((totalMs / 3600000).toFixed(2));
}

// Post-write dedup: merge duplicate ticket lines.
// EQUIPMENT (TASK-442): consolidate per-ASSET (NOT per-session) — hours = interval-union
// of every session's window. Team-drive (two drivers/one truck, same window) bills the
// truck's real hours ONCE instead of 2×; sequential handoff / same-driver-lunch still
// bill the correct summed non-overlapping time; idle gaps stay unbilled. The rate guard
// stays so a different-rate split isn't merged.
// EMPLOYEE (TASK-427, UNCHANGED): keyed per personId + clockEntryId so distinct work
// sessions stay separate lines — each person's time is billed/paid individually.
async function deduplicateTicketLines(ticketId) {
  const tenant = ClockState.tenantId || window.AppState?.tenant;
  try {
    const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));

    // Group equipment by assetId + rate + rateUnit (per-asset union, NOT per-session)
    const eqByAsset = new Map();
    // Group employees by personId + clockEntryId (per-session, unchanged)
    const empByPerson = new Map();

    snap.docs.forEach(d => {
      const data = d.data();
      const entry = { docId: d.id, ...data };
      if (data.type === 'EQUIPMENT' && data.assetId) {
        // Per-asset key (NOT per-session). Includes rate + rateUnit + includeOnInvoice
        // so a different-rate or billable-vs-not split for the same asset isn't merged.
        const invoiceKey = data.includeOnInvoice === false ? '0' : '1';
        const k = `${data.assetId}|${data.rate || 0}|${data.rateUnit || ''}|${invoiceKey}`;
        if (!eqByAsset.has(k)) eqByAsset.set(k, []);
        eqByAsset.get(k).push(entry);
      }
      if (data.type === 'EMPLOYEE' && data.personId) {
        // Per-session key + rate/rateUnit guard (aligns with server findMergeGroups so
        // the client can't over-merge an intentional different-rate split that the
        // server would have preserved — COD Code Reviewer concern 1).
        const sessionKey = data.clockEntryId || '';
        const k = `${data.personId}|${sessionKey}|${data.rate || 0}|${data.rateUnit || ''}`;
        if (!empByPerson.has(k)) empByPerson.set(k, []);
        empByPerson.get(k).push(entry);
      }
    });

    let merged = 0;

    // Dedup equipment: keep line with most hours (or highest rate if tied)
    for (const [assetId, lines] of eqByAsset) {
      if (lines.length <= 1) continue;
      // Sort: prefer line with most hours, then highest rate
      lines.sort((a, b) => (b.hours || 0) - (a.hours || 0) || (b.rate || 0) - (a.rate || 0));
      const keep = lines[0];
      // Find widest time span across all dupes (for display fromTime/toTime)
      let earliestFrom = null, latestTo = null;
      for (const line of lines) {
        const from = line.fromTime?.toDate?.() || (line.fromTime ? new Date(line.fromTime) : null);
        const to = line.toTime?.toDate?.() || (line.toTime ? new Date(line.toTime) : null);
        if (from && (!earliestFrom || from < earliestFrom)) earliestFrom = from;
        if (to && (!latestTo || to > latestTo)) latestTo = to;
      }
      const perUnit = !isTimeBasedUnit(keep.rateUnit);
      // TASK-442: union hours (idle gaps unbilled, overlapping windows counted once).
      // Fall back to keeper's own hours only if no line had valid from/to bounds.
      const unionHrs = clockIntervalUnionHours(lines);
      const hours = perUnit ? 0 : (unionHrs > 0 ? unionHrs : parseFloat((keep.hours || 0).toFixed(2)));
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', keep.docId), {
        fromTime: earliestFrom ? Timestamp.fromDate(earliestFrom) : keep.fromTime,
        toTime: latestTo ? Timestamp.fromDate(latestTo) : keep.toTime,
        hours,
        // TASK-9646 S2 — a per-unit keeper is $0 until Review prices it (same as
        // the server dedup); a time-based keeper's amount is re-derived by the
        // SERVER from the stored hours × rate (`amountPending`).
        ...(perUnit ? { lineAmount: 0 } : { amountPending: true }),
        // TASK-1396 — mirror the SERVER dedup's marker (ticketLineDedup.js
        // `autoDedupMergedAt`). This keeper's window/hours now span sessions other
        // than the one named in its clockEntryId, and removeEquipment() reads this
        // to refuse a destructive delete of other operators' billable hours.
        autoDedupMergedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      // Delete dupes
      for (let i = 1; i < lines.length; i++) {
        await deleteDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', lines[i].docId));
        merged++;
      }
    }

    // Dedup employees: keep line with most hours
    for (const [personId, lines] of empByPerson) {
      if (lines.length <= 1) continue;
      lines.sort((a, b) => (b.hours || 0) - (a.hours || 0) || (b.rate || 0) - (a.rate || 0));
      const keep = lines[0];
      // Find widest time span
      let earliestFrom = null, latestTo = null;
      for (const line of lines) {
        const from = line.fromTime?.toDate?.() || (line.fromTime ? new Date(line.fromTime) : null);
        const to = line.toTime?.toDate?.() || (line.toTime ? new Date(line.toTime) : null);
        if (from && (!earliestFrom || from < earliestFrom)) earliestFrom = from;
        if (to && (!latestTo || to > latestTo)) latestTo = to;
      }
      const rawHrs = (earliestFrom && latestTo) ? (latestTo - earliestFrom) / 3600000 : keep.hours || 0;
      const hours = parseFloat(rawHrs.toFixed(2));
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', keep.docId), {
        fromTime: earliestFrom ? Timestamp.fromDate(earliestFrom) : keep.fromTime,
        toTime: latestTo ? Timestamp.fromDate(latestTo) : keep.toTime,
        hours,
        // TASK-9646 S2 / TASK-1653 — an EMPLOYEE line never carries customer
        // money; this used to mint hours × keep.rate on a person.
        lineAmount: 0,
        updatedAt: Timestamp.now()
      });
      for (let i = 1; i < lines.length; i++) {
        await deleteDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', lines[i].docId));
        merged++;
      }
    }

    if (merged > 0) {
      console.log(`[Clock] Dedup: merged ${merged} duplicate lines on ticket ${ticketId}`);
      // TASK-4699 — markDirty: a merge rewrites the keeper line's from/to window,
      // hours and lineAmount and DELETES the duplicates. Billed hours and the
      // total both move, so the emailed copy no longer matches. Only reached
      // when merged > 0, so a no-op dedup pass stamps nothing.
      await recalcTicketTotal(ticketId, true);
    }
  } catch (err) {
    console.warn('[Clock] Ticket line dedup error:', err);
  }
}
// Backward compat alias
async function deduplicateEquipmentLines(ticketId) { return deduplicateTicketLines(ticketId); }

// Backward-compat wrapper — returns Set of assetIds (TASK-427: session-scoped when
// clockEntryId is passed, so a re-clock session isn't blocked by a prior session's line)
async function getExistingEquipmentIds(ticketId, clockEntryId = null) {
  const map = await getExistingEquipmentMap(ticketId, clockEntryId);
  return new Set(map.keys());
}

// ============================================
// LOAD SAVED DOWNTIME/STANDBY ENTRIES FROM FIRESTORE
// ============================================
async function loadSavedTimeEntries(ticketId) {
  if (!ticketId) return;
  const tenant = ClockState.tenantId;
  try {
    const snap = await getDocs(
      query(
        collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'),
        where('type', 'in', ['DOWNTIME', 'STANDBY'])
      )
    );
    snap.docs.forEach(d => {
      const data = d.data();
      const fromTime = data.fromTime?.toDate?.() || null;
      const toTime = data.toTime?.toDate?.() || null;
      const entry = {
        startTime: fromTime ? toLocalDatetimeStr(fromTime) : '',
        endTime: toTime ? toLocalDatetimeStr(toTime) : '',
        notes: data.notes || '',
        _lineId: d.id,
        _saved: true,
        _dirty: false
      };
      // clock-dst-all — the stored instants, remembered on this row only.
      if (_tlt()) { if (fromTime) _tlt().stampRow(entry, 'startTime', fromTime); if (toTime) _tlt().stampRow(entry, 'endTime', toTime); }
      if (data.type === 'DOWNTIME') ClockState.clockOutDowntime.push(entry);
      if (data.type === 'STANDBY') ClockState.clockOutStandby.push(entry);
    });
  } catch (err) {
    console.warn('[Clock] Error loading saved time entries:', err);
  }
}

async function loadSavedEquipment(ticketId, clockIn) {
  const tenant = ClockState.tenantId;
  try {
    const snap = await getDocs(
      query(
        collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'),
        where('type', '==', 'EQUIPMENT')
      )
    );
    const existingAssetIds = new Set(ClockState.clockOutEquipment.map(e => e.assetId));
    snap.docs.forEach(d => {
      const data = d.data();
      // Skip if already in the list (from autoFillEquipment)
      if (data.assetId && existingAssetIds.has(data.assetId)) return;
      const fromTime = data.fromTime?.toDate?.() || clockIn;
      const toTime = data.toTime?.toDate?.() || new Date();
      ClockState.clockOutEquipment.push({
        assetId: data.assetId || null,
        assetName: data.description || data.assetName || 'Equipment',
        assetType: data.assetType || 'equipment',
        fleetNumber: data.fleetNumber || '',
        startTime: toLocalDatetimeStr(fromTime),
        endTime: toLocalDatetimeStr(toTime),
        rate: data.rate ?? 0,
        rateUnit: data.rateUnit || 'Hourly',
        roundToHalfHour: data.roundToHalfHour || false,
        lineItemId: data.lineItemId || null,
        contractId: data.contractId || null,
        includeOnInvoice: data.includeOnInvoice !== false,
        _lineId: d.id,
        // TASK-4559 — the note ALREADY on this line, so the box can render it
        // and so an untouched box is distinguishable from a deliberate clear.
        // This is the merge BASELINE (see clockResolveAssetNote): captured at
        // render time, never re-read from the doc at write time.
        _savedNote: typeof data.notes === 'string' ? data.notes : '',
        // TASK-1396 — provenance stamp. This query is deliberately NOT filtered by
        // clockEntryId (the TASK-442 per-asset dedup can leave the surviving keeper
        // owned by ANOTHER entry, so a strict filter would hide a line that legitimately
        // needs editing). Instead we carry the owner through and let
        // clockIsCrewEquipmentLine() render a coworker's row read-only — the same
        // treatment jobboard.js has had since TASK-1188.
        _clockEntryId: data.clockEntryId || null,
        // COD Code-Reviewer (TASK-1396, binding) — a TASK-442 dedup KEEPER can carry
        // THIS entry's clockEntryId while its window/hours are the interval-UNION of
        // several operators' sessions. It is not "yours" to hard-delete even though
        // it is yours to edit. Carry the merge marker so removeEquipment can refuse
        // to destroy other people's billable machine time.
        _mergedKeeper: !!data.autoDedupMergedAt,
        _saved: true,
        _dirty: false
      });
      // clock-dst-all — the stored instants, remembered on this row only.
      { const _r = ClockState.clockOutEquipment[ClockState.clockOutEquipment.length - 1]; if (_tlt() && _r) { _tlt().stampRow(_r, 'startTime', fromTime); _tlt().stampRow(_r, 'endTime', toTime); } }
    });
  } catch (err) {
    console.warn('[Clock] Error loading saved equipment:', err);
  }
}

// TASK-1396 — is this equipment row another crew member's, on a SHARED ticket?
// Ported from jobboard.js jbIsCrewEquipmentLine (TASK-1188); clock.js never got
// that treatment, and that asymmetry is exactly what trapped Steven Alandt
// clocked-in on 2026-08-06: loadSavedEquipment pulls EVERY equipment line on the
// ticket, and the shift-bounds validator then hard-blocked his clock-out on a
// coworker's perfectly correct truck window that simply started before his shift.
//
// A row RESTORED from Firestore (_saved) carrying a clockEntryId different from
// this close-out session's entry belongs to a coworker. It renders read-only,
// is EXCLUDED from shift-bounds validation, and never enters the write payload
// (which is also what stopped the foreign window from being cloned onto the
// current person's clockEntryId — the under-billing half of the same bug).
// Rows with no _clockEntryId (legacy / auto-filled / added this session) and rows
// matching the current session stay fully editable.
function clockCurrentEntryId() {
  return ClockState.clockOutEntry?.id || ClockState.reviewData?.entry?.id || null;
}

// ⚠️ TWIN — this is a deliberate PORT, not a shared helper (COD Blast flagged the
// fork). clock.js and jobboard.js cannot share one function without an `import`,
// which Pitfall #1 forbids across `?v=`-busted modules, and neither module owns the
// other's state (`ClockState.clockOutEntry` vs `JobBoardState._btEntryId`) or its
// restore marker (`_saved` vs `_restored` — clock.js accepts BOTH so the two stay
// interchangeable). ANY future change to crew-equipment semantics must be made in
// BOTH: jobboard.js `jbIsCrewEquipmentLine` / `jbCrewEquipmentOwnerLabel` and here.
function clockIsCrewEquipmentLine(eq) {
  if (!eq || (eq._saved !== true && eq._restored !== true) || !eq._clockEntryId) return false;
  return eq._clockEntryId !== clockCurrentEntryId();
}

// Attribution for a read-only crew row. The owning session's operator is only
// resolvable when their clock entry is visible — a clock-only member's
// clockEntries subscriptions are personId-scoped (TASK-452), so this usually
// falls back to the generic label.
function clockCrewEquipmentOwnerLabel(eq) {
  const pools = [ClockState.activeEntries || [], ClockState.recentEntries || []];
  for (const pool of pools) {
    const owner = pool.find(e => e.id === eq._clockEntryId);
    const name = (owner?.personName || '').trim();
    if (name) return `Crew equipment · added by ${name}'s shift`;
  }
  return 'Crew equipment';
}

// ============================================
// TASK-9207 — DISPLAY-ONLY OWNERSHIP OF A RESTORED LINE
// (the TWIN of jobboard.js TASK-9188 / 21b4ebc7 — change both or they diverge)
// ============================================
// THE DEFECT, and it is the same one on both surfaces. `clockIsCrewEquipmentLine`
// answers "is this line FROM ANOTHER SESSION?" — it never asked WHOSE. Re-clocking
// on the SAME ticket (the lunch case, TASK-427) leaves session 1's equipment lines
// on the ticket, `loadSavedEquipment` pulls EVERY equipment line back (deliberately
// unfiltered by clockEntryId — see its TASK-1396 comment), and every one of them
// carrying the PRIOR entry id renders as "Crew equipment" with a "Crew" lock chip.
// The operator's own morning truck is presented to him as somebody else's.
//
// `clockCrewEquipmentOwnerLabel` above cannot rescue it: it resolves the owner out
// of ClockState.activeEntries / recentEntries, and a COMPLETED earlier session is
// in neither pool (the clock-only subscriptions are personId-scoped AND
// status-scoped, TASK-452), so it falls through to the generic "Crew equipment".
//
// 🔴 WHY THIS IS A SECOND PREDICATE AND NOT A FIX TO THE FIRST — and the reason is
// STRONGER here than on the jobboard. Over there `jbIsCrewEquipmentLine` guards
// three mutation handlers. HERE `clockIsCrewEquipmentLine` additionally governs
// shift-bounds VALIDATION (:14267, :14307, :14453), the equipment PICKER's
// already-used set (:13678), the write payload and the three ticket-clone paths
// (:16093, :16385, :16704). Loosening it to "not mine" would re-open editing and
// deletion of a CLOSED segment's billed lines, re-arm the exact bounds trap that
// stranded Steven Alandt on 2026-08-06, and do it on two surfaces at once.
// Everything below is READ-ONLY: it changes what a row SAYS, never what may be
// done to it. A prior segment stays read-only, which is correct — it is closed.
//
// UNSTAMPED LINES KEEP THE JOBBOARD DEFAULT ("his"), NOT TASK-9195's ("crew"), and
// that disagreement between the two surfaces is deliberate. 9195 is My Hours — a
// READ-BACK weeks later where a wrong guess costs nothing but a caption, so it
// refuses to assume. This is a BUILDER: `clockIsCrewEquipmentLine` already returns
// false for a line with no `_clockEntryId`, so an unstamped line here is either
// auto-filled from dispatch moments ago, added by hand in this modal, or legacy
// pre-TASK-427 data on his own ticket — all of them his, all of them editable, and
// none of them ever reaching the code below (which only runs on lines the
// protective predicate ALREADY called crew). Telling him his freshly auto-filled
// truck belongs to the crew would be false on the screen where he is still working.
// NEVER TRAP THE DRIVER outranks a cautious caption.
//
// Resolution: one lazy `getDoc` per DISTINCT foreign clockEntryId. firestore.rules
// `isOwnClockEntry` lets a clock-only member or PIN driver read his OWN entry by id
// and NOT a coworker's, so a denial is itself the correct signal. Unresolved,
// denied, missing and someone-else's all stay "crew" — fail closed by construction.
// Values: 'me' | 'other'. Never cleared; a clockEntry's owner does not change.
//
// CACHE SCOPE — one difference from the twin, forced by this surface. The jobboard
// modal is always the driver's own; the classic clock-out modal is ALSO the
// workforce/kiosk surface (`clockOut` only delegates to the board for a self-only
// session, :12251), so the person whose shift is being closed is NOT necessarily
// the person holding the tablet. Ownership is therefore asked about the SUBJECT —
// `ClockState.clockOutEntry.personId` — while the key also carries the VIEWER,
// because a denial is viewer-dependent. Both in the key, so a PIN hand-off or a
// kiosk switching employees can never inherit the previous verdict.
const _clockLineOwnerCache = new Map();

// Whose shift is this modal closing? Attribution is about HIM, not about whoever
// is tapping the screen.
function _clockAttributionSubjectId() {
  return ClockState.clockOutEntry?.personId || ClockState.reviewData?.entry?.personId || null;
}

// Which PERSON is holding the device — null for a kiosk/owner who is not linked
// to a person record. This is the one used to decide whether the caption may say
// "Your", so it stays a bare personId.
function _clockAttributionViewerId() {
  return getSelfPersonId()
    || ClockState.authenticatedPersonId
    || window.AppState?.driverPersonId
    || null;
}

// The SESSION identity the cache is keyed on. COD Code Reviewer (TASK-9207): an
// owner who is not self-linked has NO personId, so every such viewer collapsed to
// one 'anon' bucket and could inherit another unlinked viewer's denial. Fall
// through to the auth uid before giving up, and put the tenant in the key too, so
// the identity a verdict is filed under is as specific as the session really is.
function _clockAttributionViewerKey() {
  const pid = _clockAttributionViewerId();
  if (pid) return 'p:' + pid;
  const uid = window.AppState?.user?.uid || null;
  return uid ? 'u:' + uid : 'anon';
}

function _clockLineOwnerKey(viewerKey, subjectId, entryId) {
  return `${ClockState.tenantId || 'no-tenant'}|${viewerKey || 'anon'}|${subjectId}|${entryId}`;
}

// TRUE only for a line the SUBJECT of this clock-out wrote during an EARLIER
// session on this ticket. Unresolved, unreadable or someone else's => false, i.e.
// it keeps the existing crew treatment. A `get` we never made and a `get` that was
// denied are the same answer: "not proven his".
function clockIsOwnEarlierShiftLine(eq) {
  if (!clockIsCrewEquipmentLine(eq)) return false;
  const subject = _clockAttributionSubjectId();
  if (!subject) return false;
  return _clockLineOwnerCache.get(
    _clockLineOwnerKey(_clockAttributionViewerKey(), subject, eq._clockEntryId)) === 'me';
}

// Attribution caption for a read-only row. A MERGED line (the TASK-442 per-asset
// dedup keeper, flagged `_mergedKeeper` at the restore site) says so plainly rather
// than claiming hours that may be the interval-union of two operators' windows.
function clockLineAttributionLabel(eq) {
  if (!clockIsOwnEarlierShiftLine(eq)) return clockCrewEquipmentOwnerLabel(eq);
  const subject = _clockAttributionSubjectId();
  const viewerIsSubject = !!subject && subject === _clockAttributionViewerId();
  let who = 'Your';
  if (!viewerIsSubject) {
    const name = (ClockState.clockOutEntry?.personName
      || ClockState.people?.find(p => p.id === subject)?.name || '').trim();
    who = name ? `${name}'s` : 'An';
  }
  return eq._mergedKeeper
    ? `${who} earlier shift · hours combined with the crew's`
    : `${who} earlier shift on this ticket`;
}

// One bounded pass, fired once the clock-out modal is painted. Fire-and-forget and
// TOTALLY swallowed: attribution is a caption, and NOTHING here may take down the
// equipment card, the bounds validator or the shift-note box the operator has to
// fill before he can leave. TASK-428 is the standing reminder of what happens when
// something on this path throws.
async function _clockResolveOwnEarlierShiftLines() {
  try {
    const subject = _clockAttributionSubjectId();
    const viewer = _clockAttributionViewerKey();
    const tenant = ClockState.tenantId;
    if (!subject || !tenant) return;
    const ids = [...new Set((ClockState.clockOutEquipment || [])
      .filter(eq => clockIsCrewEquipmentLine(eq))
      .map(eq => eq._clockEntryId))]
      .filter(id => id && !_clockLineOwnerCache.has(_clockLineOwnerKey(viewer, subject, id)));
    if (!ids.length) return;
    let foundOwn = false;
    for (const id of ids) {
      let verdict = 'other';   // fail closed — a denied read stays "crew"
      try {
        const snap = await getDoc(doc(db, 'tenants', tenant, 'clockEntries', id));
        if (snap.exists() && snap.data()?.personId === subject) verdict = 'me';
      } catch (err) {
        console.warn('[Clock] line owner read failed (staying crew):', id, err?.message);
      }
      _clockLineOwnerCache.set(_clockLineOwnerKey(viewer, subject, id), verdict);
      if (verdict === 'me') foundOwn = true;
    }
    if (!foundOwn) return;   // nothing the operator would see change
    refreshEquipmentList();
  } catch (err) {
    console.warn('[Clock] own-shift attribution pass failed:', err?.message);
  }
}

// COD Code Reviewer (TASK-9207), and it is a real hole: the Review & Confirm
// screen is a WHOLESALE `clock-out-body` innerHTML swap, so unlike the equipment
// list it cannot be repainted after the fact — doing so would destroy the review's
// own note box mid-edit, which is the TASK-1416 failure all over again. So the
// answer is to SETTLE attribution BEFORE the swap rather than repaint after it.
// Normally a cache hit and instant. Bounded, because the review is the last thing
// between a man and the end of his shift and a slow read must never hold it:
// after the budget the review opens anyway and the line simply stays "crew",
// which is the same fail-closed direction as everything else here.
const _CLOCK_ATTRIBUTION_REVIEW_BUDGET_MS = 1500;
async function _clockSettleAttributionForReview() {
  try {
    await Promise.race([
      _clockResolveOwnEarlierShiftLines(),
      new Promise(resolve => setTimeout(resolve, _CLOCK_ATTRIBUTION_REVIEW_BUDGET_MS)),
    ]);
  } catch (_) { /* a caption never blocks the review */ }
}

// ============================================
// TASK-4559 — the driver's per-asset WORK NOTE, on every close-out path
// ============================================
// WHAT WENT WRONG. The note the driver types into `#clock-asset-notes-{assetId}`
// was read in exactly ONE place per path: inside the CREATE branch, AFTER the
// `if (existing) { ...; continue; }` early-out. TASK-1635 (2026-08-19) made a
// tied truck's EQUIPMENT line materialize at clock-IN, so by clock-OUT the line
// already exists, the update branch runs, and the note the driver just typed was
// never read at all. Hours moved, the note vanished, nothing threw. Confirmed
// unrecoverable on TITS ticket #100530 (Cat 330 KSI, 8/20) — `notes: null`, no
// `internalNotes`, the driver had to retype it.
//
// The REVIEW path was worse than the ticket described: `showClockOutReview()`
// snapshots `equipment: [...ClockState.clockOutEquipment]` and then REPLACES the
// modal body (`bodyEl.innerHTML = buildReviewContent(...)`), destroying every
// note textarea before `confirmClockOutFromReview()` / `applyReviewData()` run.
// Those two wrote a hard-coded `notes: null` and never touched the DOM, so on
// the path TITS actually uses the note was dropped 100% of the time, on CREATE
// as well as on update. That is why the note is now CAPTURED into the equipment
// row on `oninput` (`_noteDraft`) — a value that survives the DOM swap — and the
// DOM is only a fallback read for the paths where the form is still mounted.
//
// 🔴 MERGE, NEVER CLOBBER. An empty box must not wipe a note a biller already
// corrected. `_savedNote` is the note the box was RENDERED with (loadSavedEquipment
// carries it off the stored line); the decision compares against THAT baseline,
// never against the line's current stored value — using "what's in Firestore now"
// as the baseline would turn an untouched empty box into a deliberate clear and
// erase the biller's correction.
//
// TASK-1636: CLEARING persists as `''`, never `null`. They mean different things
// downstream — `''` is "the driver deliberately emptied this", `null` is "nobody
// ever said". Only an explicitly emptied, previously-populated box writes `''`.
//
// PURE. Exported for tests/task4559-equipment-note-merge.unit.cjs.
//   typed    — the box's current value; null/undefined = the box was never
//              rendered for this asset (per-asset notes off, or a type that
//              doesn't get notes) → never touch the stored note.
//   baseline — what the box was rendered with (eq._savedNote), or ''/nullish
//              when this row has no stored note behind it.
// Returns { write, value }: `write:false` means OMIT `notes` from the payload.
function clockResolveAssetNote(typed, baseline) {
  if (typed === null || typed === undefined) return { write: false, value: '' };
  const cur = String(typed).trim();
  const base = (baseline === null || baseline === undefined) ? '' : String(baseline).trim();
  if (cur === base) return { write: false, value: cur };   // untouched → never clobber
  if (cur === '') return { write: true, value: '' };       // deliberate clear → '' not null
  return { write: true, value: cur };                      // typed or edited → persist
}

// Reads the note for ONE equipment row and returns the merge decision.
// DOM first (the form is mounted — this is the live value the driver sees), then
// the captured `_noteDraft` (the review screen, where the textarea is gone).
// Nothing typed and nothing captured → { write:false }, the stored note stands.
function clockReadAssetNote(eq) {
  if (!eq || !eq.assetId) return { write: false, value: '' };
  let typed = null;
  const el = (typeof document !== 'undefined')
    ? document.getElementById('clock-asset-notes-' + eq.assetId)
    : null;
  if (el) typed = el.value;
  else if (eq._noteDraft !== undefined) typed = eq._noteDraft;
  return clockResolveAssetNote(typed, eq._savedNote);
}

// `oninput` on the per-asset textarea. Parks the draft on the equipment ROW so
// it survives showClockOutReview()'s innerHTML swap. `_`-prefixed on purpose:
// it is client state, never part of a ticketLine write payload (every payload in
// this file is an explicit object literal, so it cannot leak by spread either).
function captureAssetNote(assetId, value) {
  for (const eq of (ClockState.clockOutEquipment || [])) {
    if (eq && eq.assetId === assetId) eq._noteDraft = value;
  }
}

// ============================================
// CLOCK OUT (enhanced modal)
// ============================================
// ============================================
// SELF-SERVICE CLOCK-OUT GETS ONE UI (2026-08-19)
// ============================================
// Two parallel clock-out modals exist: this file's (the Clock view) and
// jobboard.js's (the driver board; sticky safe-area footer, "Fix it" /
// "Done here" switching). Who sees which was an ENTRY-POINT accident:
//
//   - PIN drivers CANNOT reach the Clock view at all (the router hard-blocks
//     'clock' in driverModePin — app.js allowedViews). They always get the
//     board modal. No convergence needed there.
//   - SIGNED-IN members split: the app shell's personal clock action opens the
//     BOARD modal, while the Clock tile opens THIS file's modal. Same person,
//     two UIs, and they drifted — this file's confirm button said "Build
//     Ticket" and a real driver could not find the way out ("there is no
//     clock-out button on brians phone"; his entry's source:'clock_spa'
//     proves the Clock-view path).
//
// So: a SELF-ONLY session (TASK-360's "the person's way to clock in/out" —
// no workforce powers, clocking out their OWN linked person) delegates to the
// board modal. KIOSK AND ADMIN SURFACES ARE DELIBERATELY KEPT on this file's
// modal: hasWorkforceAccess() sessions clock out OTHER people, and the board
// modal is personId-locked — delegating a shared yard tablet would be wrong.
//
// 🔴 FAIL OPEN TO THE CLASSIC MODAL. Every guard below returns false rather
// than throwing or toasting, and clockOut() falls through to the flow that has
// worked for months. The failure mode of this feature must never be "driver
// cannot clock out" — a wrong-looking modal beats a trapped driver. Clock-out
// is a driver-trapping surface (TASK-428).
function _isSelfOnlyClockSession(personId) {
  const a = window.AppState || {};
  // Driver-shaped sessions (unreachable on this view today — the router blocks
  // 'clock' for PIN — but harmless and correct if that ever changes).
  if (a.driverModePin || a.driverMode) {
    return !!a.driverPersonId && a.driverPersonId === personId;
  }
  // Workforce/admin/kiosk: multi-person surface — never delegate.
  try { if (hasWorkforceAccess()) return false; } catch (_) { return false; }
  // Self-service member: only when clocking out THEIR OWN linked person.
  const selfPid = a.memberAccess && a.memberAccess.personId;
  return !!selfPid && selfPid === personId;
}

async function _delegateDriverClockOutToBoard(entry, personId) {
  try {
    const JB = window.JobBoardModule;
    if (!JB || typeof JB.clockOutFromBoardForPerson !== 'function'
           || typeof JB._hydrateState !== 'function') return false;

    // If the board already knows this entry (driver came from the board earlier
    // this session), do NOT re-hydrate — _hydrateState overwrites JobBoardState
    // wholesale, and the board's own jobs/jobDays are richer than ours.
    const jbs = JB._state || {};
    const jbHasEntry = Array.isArray(jbs.clockEntries) && jbs.clockEntries.some(e => e && e.id === entry.id);

    if (!jbHasEntry) {
      // COD Code Reviewer (❌, fixed): hydrating from ClockState.todaysJobDays
      // WITHOUT refreshing first would hand the board a jobDays snapshot frozen
      // at page load — on a session open since yesterday that resolves the
      // WRONG day's equipment or none, the exact TASK-1417 $0-ticket hazard
      // the classic path below already guards with this same call. Refresh
      // BEFORE hydrating, same order as the classic flow.
      try { await refreshJobCachesIfStale(ClockState.tenantId); } catch (e) {
        console.warn('[Clock] delegate: jobDay cache refresh failed (continuing with cached):', e.message);
      }
      // v1.15.2000 COD 🔴 — today's list alone has no day for a shift that
      // started yesterday; hand the board the shift's own day too (or none).
      await _clockEnsureEntryJobDays(entry);
      // Mirror dispatch.js openDriverClockOut's hydration, sourced from
      // ClockState (this view's own live subscriptions). ClockState has no
      // jobs cache, so fetch THIS entry's job doc — the modal header's
      // customer line and jbAutoFillEquipment's jobDay resolution read it,
      // and an equipment line that never lands is how a ticket comes out at
      // $0 (TASK-1417).
      let jobs = [];
      if (entry.jobId) {
        try {
          const jobSnap = await getDoc(doc(db, 'tenants', ClockState.tenantId, 'jobs', entry.jobId));
          if (jobSnap.exists()) jobs = [{ id: jobSnap.id, ...jobSnap.data() }];
        } catch (e) {
          console.warn('[Clock] delegate: job fetch failed (continuing — header degrades, entry still closable):', e.message);
        }
      }
      JB._hydrateState({
        tenantId: ClockState.tenantId,
        trucks: ClockState.trucks || [],
        trailers: ClockState.trailers || [],
        people: ClockState.people || [],
        customers: ClockState.customers || [],
        clockEntries: ClockState.activeEntries || [],
        jobs,
        jobDays: [...(ClockState.todaysJobDays || []), ...(ClockState.entryJobDays || [])],
      });
    }

    const person = (ClockState.people || []).find(p => p.id === personId);
    await JB.clockOutFromBoardForPerson(entry.id, entry.jobId || '', entry.jobDayId || '', {
      personId,
      personName: person?.name || entry.personName || '',
      shiftChosen: true, // clock-double-tap — clockOut() already asked which shift
    });
    return true;
  } catch (e) {
    // ANY failure → classic modal. Log loudly so drift is visible in console
    // audits, but the driver never sees this path fail.
    console.error('[Clock] driver clock-out delegation failed — falling back to classic modal:', e);
    return false;
  }
}

async function clockOut() {
  const personId = ClockState.selectedPerson;
  if (!personId) return;

  // TASK-9941 F S2 — clock-out had NO check of its own (exported on the module;
  // it leaned on the button only showing after the PIN box). Same helper as
  // clockIn. The grant is remembered for the sheet (ClockState.clockOutAuth)
  // and consumed when the clock-out is written, so a slow close-out is never
  // refused at Confirm.
  const _codeAuth = clockPunchAuthorization(personId);
  if (!_codeAuth.ok) {
    const _p = (ClockState.people || []).find(p => p.id === personId);
    window.showToast?.(`Enter ${_p?.name ? _p.name.split(/\s+/)[0] + '’s' : 'the'} PIN first`, 'error');
    try { updateSelectedPersonStatus(); } catch (_) { /* repaint is best-effort */ }
    return;
  }
  ClockState.clockOutAuth = { personId, how: _codeAuth.how, reason: _codeAuth.reason || null };

  // TASK-9579 — never whichever open shift the snapshot listed first.
  // clock-double-tap — and never silently the NEWEST either: after a double tap
  // the newest is usually the accidental one. With more than one open shift the
  // person (or Workforce, on the kiosk) is shown each one — start time + job —
  // and picks the one to close. Cancel closes nothing.
  const { entry: _newestOpen, others: _olderOpen } = _clockOpenShiftsFor(personId);
  if (!_newestOpen) return;
  let entry = _newestOpen;
  if (_olderOpen.length) {
    console.warn('[Clock] more than one open shift for this person — asking which to close:', [_newestOpen.id, ..._olderOpen.map(e => e.id)]);
    const _person = (ClockState.people || []).find(p => p.id === personId);
    const _picked = window.ClockShiftGuard
      ? await window.ClockShiftGuard.pickShiftToClose([_newestOpen, ..._olderOpen], {
          self: _clockIsSelfSelection(personId),
          personName: _person?.name || _newestOpen.personName || '',
        })
      : _newestOpen; // guard not loaded: never trap — fall back to the old behavior
    if (!_picked) return; // Cancel — nothing closed
    entry = _picked;
  }

  // Driver sessions in ticket mode get the board's modal — one UI everywhere.
  // shouldBuildTickets() is false in simple mode and for public access, so
  // those keep the classic Close Out untouched.
  if (shouldBuildTickets() && _isSelfOnlyClockSession(personId)) {
    if (await _delegateDriverClockOutToBoard(entry, personId)) return;
    // fall through — classic modal, exactly as before this change
  }

  ClockState.clockOutEntry = entry;
  ClockState.clockOutEquipment = [];
  ClockState.clockOutDowntime = [];
  ClockState.clockOutStandby = [];
  ClockState.clockOutPotholes = [];
  ClockState.clockOutPhotos = [];
  ClockState._photoTicketId = entry.ticketId || null; // TASK-1214
  ClockState._ticketPhotoLock = false;                // TASK-1214 — set by loadSavedFormData
  ClockState._ticketWorkTimes = null;
  // Reset deletion tracking arrays
  ClockState._deletedDowntimeIds = [];
  ClockState._deletedStandbyIds = [];
  ClockState._deletedEquipmentIds = []; // TASK-1396
  ClockState._boundsProblemsAcknowledged = 0; // TASK-1396/1399
  ClockState._reviewGateInFlight = false;
  // Reset save-from-form dedup flags
  ClockState._appliedEquipment = false;
  ClockState._appliedDowntime = false;
  ClockState._appliedStandby = false;
  ClockState._appliedFieldActivity = false;

  // Load saved ticket work times so form shows previously-saved values
  if (entry.ticketId) {
    try {
      const ticketSnap = await getDoc(doc(db, 'tenants', ClockState.tenantId, 'tickets', entry.ticketId));
      if (ticketSnap.exists()) {
        const td = ticketSnap.data();
        if (td.workStartTime || td.workStopTime) {
          ClockState._ticketWorkTimes = {
            workStartTime: td.workStartTime?.toDate?.() || null,
            workStopTime: td.workStopTime?.toDate?.() || null
          };
        }
      }
    } catch (e) {
      console.warn('[Clock] Failed to load ticket work times:', e.message);
    }
  }
  ClockState._appliedPotholes = false;
  ClockState._appliedNotes = false;
  ClockState._appliedPhotos = false;
  // Reset time validation flags
  ClockState._timeValidationFlags = {};
  ClockState._validating = false;
  // Reset field ticket state
  ClockState.fieldTicketLineItems = [];
  ClockState.fieldTicketSettings = null;
  ClockState.contractId = null;
  ClockState._fieldItemSaved = {}; // TASK-9665 — this session's FIELD_ITEM lines, by lineItemId

  const person = ClockState.people.find(p => p.id === personId);
  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
  const now = new Date();
  const duration = formatDuration(now - clockIn);
  const hours = ((now - clockIn) / 3600000).toFixed(2);

  // Clean up any duplicate lines from prior concurrent clock-outs before loading
  if (entry.ticketId) {
    await deduplicateTicketLines(entry.ticketId);
  }

  // TASK-1417 — the equipment auto-fill resolves this entry's jobDay out of
  // ClockState.todaysJobDays, which froze at page load. On a session open since
  // yesterday that means clocking out against the WRONG day's equipment, or
  // none at all — and an equipment line that never lands is exactly how a
  // ticket comes out at $0. Refresh before the fill, not just before the picker.
  await refreshJobCachesIfStale(ClockState.tenantId);

  // Auto-fill equipment from dispatch (async — checks contract rates)
  await autoFillEquipment(entry);

  // Load saved downtime/standby entries from Firestore
  await loadSavedTimeEntries(entry.ticketId);

  // Load the customer's "Show on field ticket" Price Book items. TASK-9665 —
  // was `!isFounder() && …`: a TENANT test that skipped the qty boxes for every
  // trippin-trucking office session, so a TITS close-out could never record a
  // quantity. Every account that has a customer gets them now (no TITS-special).
  if (entry.customerId && entry.customerId !== '__shop__') {
    // TASK-10188 — the Price Book chosen on this shift's job day decides the
    // Field Ticket Settings when it is Active; none chosen ⇒ strictest-wins.
    const _ftDay = _clockResolveJobDayForEntry(entry);
    await loadFieldTicketItems(entry.customerId, _ftDay?.selectedContractId || null);
  }

  // Load saved equipment from ticket (in addition to dispatch auto-fill)
  if (entry.ticketId) {
    await loadSavedEquipment(entry.ticketId, clockIn);
  }

  const bodyEl = document.getElementById('clock-out-body');
  if (bodyEl) {
    bodyEl.innerHTML = buildClockOutModalContent(entry, person, clockIn, now, hours, duration);
    // Init waypoint autocomplete on custom section waypoint fields
    _csInitWaypoints('clock');
  }

  // Render restored downtime/standby/equipment rows into the DOM
  if (shouldBuildTickets()) {
    refreshTimeEntryList('downtime');
    refreshTimeEntryList('standby');
    refreshEquipmentList();
    // TASK-9207 — resolve whether any restored "other session" equipment line is
    // actually an EARLIER segment of the person being clocked out (the lunch
    // re-clock), then repaint the caption. Deliberately NOT awaited: the modal must
    // be usable instantly, the rows are already correct as to editability and
    // validation, and only the attribution text changes. Self-contained try/catch
    // — see the function; nothing on the clock-out path may throw.
    _clockResolveOwnEarlierShiftLines();
  }

  _clockSyncModeLabels({ sheet: true }); // TASK-9553 — this person's mode
  const _coModal = document.getElementById('modal-clock-out');
  if (_coModal) _coModal.dataset.entryId = entry.id || ''; // clock-double-tap — which shift this sheet closes
  _coModal?.classList.remove('hidden');

  // Start live clock-out timer (ticket-building accounts have adj-clock-out input)
  if (shouldBuildTickets()) startClockOutTimer();

  // Restore saved form data (notes, field activity, photos) from Firestore
  if (shouldBuildTickets() && entry.ticketId) {
    await loadSavedFormData(entry.ticketId, entry);
  }
  // TASK-9665 — this session's recorded Price Book quantities back into the boxes
  // (a re-opened Build Ticket edits the same lines instead of adding twins).
  if (entry.ticketId) {
    await loadSavedFieldItemLines(entry.ticketId, entry);
  }

  // Init signature pads after modal is visible (needs DOM dimensions)
  if (!isFounder() && ClockState.fieldTicketSettings) {
    setTimeout(() => {
      if (ClockState.fieldTicketSettings.requireDriverSignature) initSignaturePad('driver');
      if (ClockState.fieldTicketSettings.requireCustomerSignature) initSignaturePad('customer');
    }, 150);
  }
}

// ============================================
// TASK-1397 — SHARED INVOICE NOTES (customer-facing). Option A.
//
// `ticket.notes` / `ticket.workPerformed` are ONE shared crew field (Chris,
// 2026-08-06: "shift notes are per operator, invoice (customer facing) notes
// should be shared"). Every person who opens Build Ticket gets the box
// prefilled with the same stored text, which made the old write paths unsafe
// for a multi-person crew in two ways, both landing on a customer invoice:
//
//   1. DOUBLE-PASTE. The clock-out paths appended the WHOLE box — prefill
//      included — back onto the stored value, so guy #2 wrote guy #1's
//      paragraph twice and guy #3 three times. 7 live TITS tickets shipped
//      to customers reading the same line twice.
//   2. SILENT CLOBBER. saveTicketFromForm / autoSaveOnClose (and the PIN
//      path in jobboard.js) overwrote the field from a textarea snapshotted
//      at modal-open, so whoever closed last erased what a coworker had
//      saved in between. Nothing threw; the line just vanished.
//
// Option A (Chris's call — Option B / per-author attribution is DROPPED, do
// not add author names) is ONE write path, shared by clock.js and
// jobboard.js through window.ClockModule (never a ?v= cross-import —
// Pitfall #1). Four properties:
//
//   * BASELINE — we remember the exact text the box was prefilled with, and
//     contribute only the DELTA this person actually typed. A prefill can
//     never be re-appended, so the double-paste is structurally impossible.
//   * TRANSACTION — the stored value is re-read inside runTransaction and
//     the delta merged onto CURRENT storage. Two crew members saving
//     seconds apart both survive: Firestore retries the loser's txn against
//     the winner's value instead of letting stale text overwrite it.
//   * DEDUPE — identical lines collapse, so the historical doubling heals on
//     the next save and no re-save can ever re-double a line.
//   * VISIBLE — when the merge folds in a line this person never saw, the
//     textarea is resynced and we toast it. A concurrent edit is never
//     silent; the "losing" writer is shown what they merged with.
//
// No schema change, no migration. Both `notes` and `workPerformed` are
// written together because the invoice renderers are split: billing.js reads
// `notes || workPerformed` in one place and `workPerformed` ONLY in three
// others, so letting the two copies drift changes what the customer sees.
//
// NOTE: this field is deliberately the OPPOSITE of shift notes, which stay
// per-operator and first-person (TASK-1327). Crew text merged here must
// never reach clockEntries.clockOutNotes / draftShiftNotes.
// ============================================

function invoiceNoteLines(text) {
  return String(text == null ? '' : text)
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(l => l.length > 0);
}

// Dedupe key: case- and whitespace-insensitive so "Hauled rig mix" and
// "hauled  rig mix" collapse to one line on a customer-facing document.
function invoiceNoteKey(line) {
  return String(line).toLowerCase().replace(/\s+/g, ' ').trim();
}

// Pure merge — no I/O, so it is unit-testable and identical for both surfaces.
//   stored   = what is in Firestore RIGHT NOW (read inside the transaction)
//   baseline = what this person's box was prefilled with at modal-open
//   current  = what this person's box says now
// Returns { text, added, dropped, foreign, wipeGuard, kept }.
// TASK-10036 (b) — `protectKeys` (optional Set of invoiceNoteKey values): lines
// the person took out of their box that they may NOT delete from the ticket
// (someone else on the crew wrote them and the person did not confirm). They
// stay on the ticket and are counted in `kept`. Omitted = the old contract.
function mergeInvoiceNoteText(storedText, baselineText, currentText, protectKeys) {
  const stored = invoiceNoteLines(storedText);
  const base = invoiceNoteLines(baselineText);
  const cur = invoiceNoteLines(currentText);

  const curKeys = new Set(cur.map(invoiceNoteKey));
  const baseKeys = new Set(base.map(invoiceNoteKey));

  // A person may delete a line they actually SAW (it was in their baseline
  // and they took it out). A TOTAL wipe is not honoured: on a phone that is
  // far likelier to be a fat-fingered select-all than a decision, and this
  // text prints on an invoice. Clearing the whole field stays a Billing-side
  // action, where the person doing it can see the compiled ticket.
  const wipeGuard = cur.length === 0 && base.length > 0;
  const removedKeys = new Set();
  let kept = 0;
  if (!wipeGuard) {
    for (const k of baseKeys) {
      if (curKeys.has(k)) continue;
      if (protectKeys && protectKeys.has(k)) { kept++; continue; }
      removedKeys.add(k);
    }
  }

  const out = [];
  const seen = new Set();
  let foreign = 0; // lines a DIFFERENT writer added while this box was open
  let dropped = 0;

  for (const line of stored) {
    const k = invoiceNoteKey(line);
    if (removedKeys.has(k)) { dropped++; continue; }
    if (seen.has(k)) continue; // heals historical double-paste
    seen.add(k);
    out.push(line);
    if (!baseKeys.has(k) && !curKeys.has(k)) foreign++;
  }
  let added = 0;
  for (const line of cur) {
    const k = invoiceNoteKey(line);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(line);
    added++;
  }
  return { text: out.join('\n'), added, dropped, foreign, wipeGuard, kept };
}

// TASK-10036 (b) — which invoice-note lines did THIS browser add to a ticket?
// Those the person may take back out freely; any OTHER prefilled line they
// delete is someone else's (or from an earlier session) and needs a confirm
// that names it. In memory only: after a reload every prefilled line counts as
// someone else's — the safe direction (asked, never silently dropped).
const _ownInvoiceNoteKeys = new Map(); // `${actor}|${ticketId}` -> Set(invoiceNoteKey)
// COD r1 (Code Reviewer) — keyed by WHO as well as the ticket: a shared yard
// tablet that switches drivers must not let the next one take back the last
// one's line unasked.
function _invoiceNoteOwnerKey(ticketId) {
  let who = 'anon';
  try {
    const st = (typeof window !== 'undefined' && window.AppState) || {};
    // COD r2 — a shared kiosk login switches PEOPLE without changing uid; the
    // PIN-verified person (ClockState.authenticatedPersonId) wins over the uid.
    const verified = (typeof ClockState !== 'undefined' && ClockState && ClockState.authenticatedPersonId) || null;
    who = st.driverPersonId || verified || st.user?.uid || 'anon';
  } catch (_) { /* hermetic test sandbox */ }
  return `${who}|${ticketId}`;
}

// Lines in `baseline` the person removed from their box, minus their own.
// Pure apart from the map read. Returns the ORIGINAL line text, in order.
function invoiceNoteForeignRemovals(ticketId, baselineText, currentText) {
  const cur = new Set(invoiceNoteLines(currentText).map(invoiceNoteKey));
  if (cur.size === 0) return []; // a total wipe is refused by the merge anyway
  const own = _ownInvoiceNoteKeys.get(_invoiceNoteOwnerKey(ticketId)) || new Set();
  const out = [];
  const seen = new Set();
  for (const line of invoiceNoteLines(baselineText)) {
    const k = invoiceNoteKey(line);
    if (cur.has(k) || own.has(k) || seen.has(k)) continue;
    seen.add(k);
    out.push(line);
  }
  return out;
}

function setInvoiceNotesBaseline(text) {
  ClockState._invoiceNotesBaseline = String(text == null ? '' : text);
}

function getInvoiceNotesBaseline() {
  return ClockState._invoiceNotesBaseline || '';
}

// TASK-10036 (b) — ask before a coworker's line comes off the ticket. Returns
// null (remove what the person removed) or the Set of keys to KEEP.
async function askInvoiceNoteRemovals(ticketId, baseline, currentText, opts = {}) {
  const foreign = invoiceNoteForeignRemovals(ticketId, baseline, currentText);
  if (foreign.length === 0) return null;
  let ok = false;
  if (opts.noPrompt !== true && opts.silent !== true && typeof window.showConfirm === 'function') {
    const one = foreign.length === 1;
    const list = foreign.map(l => `"${l.length > 90 ? l.slice(0, 90) + '…' : l}"`).join(', ');
    ok = await window.showConfirm(
      'Remove a crew note?',
      `You took ${one ? 'this line' : 'these lines'} out of the ticket notes: ${list}. Someone else on the crew may have written ${one ? 'it' : 'them'}, and the customer reads these notes. Remove ${one ? 'it' : 'them'} from the ticket?`,
      { confirmText: 'Remove', cancelText: 'Keep on ticket', danger: true, safeDefault: true }
    ) === true;
  }
  return ok ? null : new Set(foreign.map(invoiceNoteKey));
}

// THE one write path for ticket.notes / ticket.workPerformed. Callers pass
// the text their surface holds; everything else (re-read, merge, dedupe,
// baseline bookkeeping, concurrency toast) happens here.
//   opts.tenantId    — required for the PIN surface; defaults to ClockState
//   opts.ticketId    — required
//   opts.currentText — what the person's box says now
//   opts.baseline    — override the tracked baseline (jobboard passes its own)
//   opts.syncElId    — textarea id to resync with the merged result
//   opts.stateless   — true = do not touch ClockState's baseline (jobboard)
//   opts.silent      — true = suppress toasts
//   opts.surface     — TASK-10036: which screen wrote it (audit 'surface')
//   opts.noPrompt    — TASK-10036: never ask (auto-save on close); a coworker
//                      line the person deleted is then KEPT, never dropped
// Throws on write failure. Never swallow it: a lost invoice note is exactly
// the failure mode this task exists to kill.
async function saveInvoiceNotes(opts = {}) {
  const tenantId = opts.tenantId || ClockState.tenantId;
  const ticketId = opts.ticketId;
  if (!tenantId || !ticketId) return null;

  const baseline = (typeof opts.baseline === 'string') ? opts.baseline : getInvoiceNotesBaseline();
  const currentText = String(opts.currentText == null ? '' : opts.currentText);
  const ticketRef = doc(db, 'tenants', tenantId, 'tickets', ticketId);

  // TASK-10036 (b) — a prefilled line this person did not write and took out of
  // the box is removed only after a confirm that NAMES it. No confirm (auto-save,
  // silent caller, Cancel) = the line stays on the ticket. Billing can always
  // remove it, where the whole compiled ticket is on screen.
  // A caller that must not hold a dialog inside its own timer (jobboard's
  // jbAckOrQueue) asks first via askInvoiceNoteRemovals and passes the answer.
  const _protect = (opts.removalDecision !== undefined)
    ? opts.removalDecision
    : await askInvoiceNoteRemovals(ticketId, baseline, currentText, opts);
  // A uid / person id, never an email: driver sessions read ticket docs. The
  // server resolves a uid to an email for the audit row.
  const _who = (window.AppState?.driverPersonId ? `driver:${window.AppState.driverPersonId}` : null)
    || (window.AppState?.user?.uid && !window.AppState?.user?.isAnonymous ? `uid:${window.AppState.user.uid}` : null)
    || 'unknown';

  let merge = null;
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ticketRef);
    if (!snap.exists()) { merge = null; return; }
    const data = snap.data() || {};
    const stored = (typeof data.notes === 'string' && data.notes.trim())
      ? data.notes
      : (typeof data.workPerformed === 'string' ? data.workPerformed : '');
    merge = mergeInvoiceNoteText(stored, baseline, currentText, _protect);
    // Write when the text moved OR when the two denormalized copies have
    // drifted (older paths wrote `notes` without `workPerformed`, and three
    // invoice renderers read workPerformed ONLY).
    if (merge.text === stored && data.notes === data.workPerformed) {
      merge.skipped = true;
      return;
    }
    const _tkUpdate = {
      notes: merge.text,
      workPerformed: merge.text,
      updatedAt: Timestamp.now()
    };
    // TASK-4699 — invoice notes are the work description the customer reads, so
    // a change here makes an emailed copy wrong. Stamped ONLY when the TEXT
    // actually moved: the other reason this branch runs is the notes/
    // workPerformed drift repair above, which rewrites the same words into a
    // denormalized twin and changes nothing the customer would see. Stamping
    // that would flag a ticket stale for an internal cleanup — the exact noise
    // that makes the flag untrustworthy.
    if (merge.text !== stored) {
      _tkUpdate.ticketEmailDirtyAt = Timestamp.now();
      // TASK-10036 (a) — who + which screen, read by the server's
      // ticket_notes_changed audit row (trusted only when notesEditedAt moves).
      _tkUpdate.notesEditedBy = _who;
      _tkUpdate.notesEditedVia = String(opts.surface || 'clock_out');
      _tkUpdate.notesEditedAt = Timestamp.now();
    }
    tx.update(ticketRef, _tkUpdate);
  });

  if (!merge) return null;

  // TASK-10036 (b) — remember the lines this browser added, so the person can
  // take back their OWN line later without being asked.
  if (merge.added > 0) {
    const _own = _ownInvoiceNoteKeys.get(_invoiceNoteOwnerKey(ticketId)) || new Set();
    const _baseKeys = new Set(invoiceNoteLines(baseline).map(invoiceNoteKey));
    for (const l of invoiceNoteLines(currentText)) { const k = invoiceNoteKey(l); if (!_baseKeys.has(k)) _own.add(k); }
    _ownInvoiceNoteKeys.set(_invoiceNoteOwnerKey(ticketId), _own);
  }

  if (opts.stateless !== true) setInvoiceNotesBaseline(merge.text);

  // Resync the box so the person SEES the merged truth — but only if they
  // have not typed since we read it, otherwise we would stomp their edit.
  const el = opts.syncElId ? document.getElementById(opts.syncElId) : null;
  if (el && el.value.trim() === currentText.trim() && el.value !== merge.text) {
    el.value = merge.text;
    if (el.classList && el.classList.contains('textarea-auto-expand')) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  }

  if (!opts.silent) {
    // Plain English on purpose (COD 🎨 UX Lead): this reads as a helpful
    // sync to a tired operator on a phone, not as an error.
    if (merge.foreign > 0) {
      window.showToast?.(
        `Saved — combined with ${merge.foreign} note${merge.foreign === 1 ? '' : 's'} from the crew`,
        'info'
      );
    }
    if (merge.wipeGuard) {
      window.showToast?.('Invoice notes kept — your box was empty. Dispatch can clear them in Billing.', 'warning');
    }
    if (merge.kept > 0) {
      window.showToast?.(`Kept ${merge.kept} crew note line${merge.kept === 1 ? '' : 's'} on the ticket. Dispatch can remove ${merge.kept === 1 ? 'it' : 'them'} in Billing.`, 'info');
    }
  }
  return merge;
}

async function loadSavedFormData(ticketId, entry) {
  const tenant = ClockState.tenantId;
  try {
    const ticketSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId));
    if (!ticketSnap.exists()) return;
    const data = ticketSnap.data();

    // Restore ticket notes — SHARED crew field (TASK-1397). Snapshot exactly
    // what landed in the box as this person's baseline: every later write
    // contributes only the delta against it, so this prefill can never be
    // appended back onto the stored value.
    const ticketNotesEl = document.getElementById('clock-out-ticket-notes');
    const _sharedNotes = (typeof data.notes === 'string' && data.notes.trim())
      ? data.notes
      : (typeof data.workPerformed === 'string' ? data.workPerformed : '');
    if (ticketNotesEl && _sharedNotes) ticketNotesEl.value = _sharedNotes;
    setInvoiceNotesBaseline(ticketNotesEl ? ticketNotesEl.value : _sharedNotes);

    // TASK-1327 — shift notes are a PER-PERSON record. Never prefill this box
    // from the shared ticket-level `shiftNotes` field: it holds whoever saved
    // the ticket last, so the next person to clock out opened their box with a
    // coworker's note already in it and appended to it (92 of 174 multi-person
    // TITS tickets were cross-attributed this way). Restore ONLY from THIS
    // person's own clock entry, below.
    const shiftNotesEl = document.getElementById('clock-out-shift-notes');

    // Restore field activity — use != null to allow 0
    const dumpsEl = document.getElementById('field-dumps');
    if (dumpsEl && data.dumps != null) dumpsEl.value = data.dumps;
    const waterEl = document.getElementById('field-water-refills');
    if (waterEl && data.waterRefills != null) waterEl.value = data.waterRefills;

    // Restore this person's own shift notes — their saved draft, or a note
    // already recorded on their entry (clock-out / billing review).
    if (shiftNotesEl && !shiftNotesEl.value && entry) {
      const entrySnap = await getDoc(doc(db, 'tenants', tenant, 'clockEntries', entry.id));
      if (entrySnap.exists()) {
        const entryData = entrySnap.data();
        const own = entryData.draftShiftNotes || entryData.clockOutNotes || '';
        if (own) shiftNotesEl.value = own;
      }
    }

    // TASK-1214 — ticket lock state for the photo ✕ (hide it entirely on
    // locked tickets — never a disabled button; server rules are the real
    // boundary, this is defense-in-depth UI).
    ClockState._photoTicketId = ticketId;
    // TASK-4600 — widened to the display predicate with the other locks.
    ClockState._ticketPhotoLock = data.invoiced === true || !!data.invoiceNumber || !!data.invoicedAt || data.status === 'archived'
      || data.status === 'locked' || data.driverEditLocked === true;

    // Restore photos from ticket doc (show as saved thumbnails)
    if (data.photos && data.photos.length > 0) {
      for (const photo of data.photos) {
        // Only add if not already in the list (avoid dupes on re-save)
        const alreadyLoaded = ClockState.clockOutPhotos.some(p => p._url === photo.url);
        if (!alreadyLoaded) {
          ClockState.clockOutPhotos.push({
            file: { type: photo.url?.includes('.pdf') ? 'application/pdf' : 'image/jpeg', name: photo.name || 'photo' },
            preview: photo.url,
            name: photo.name || 'Saved photo',
            _url: photo.url,
            _restored: true,
            // TASK-1214 (COD binding) — keep the EXACT stored object: the
            // restored copy above drops uploadedAt, and arrayRemove needs the
            // stored element. Never mutate _originalPhoto.
            _originalPhoto: photo
          });
        }
      }
      refreshPhotoList();
    }
  } catch (err) {
    console.warn('[Clock] Error loading saved form data:', err);
  }
}

async function autoFillEquipment(entry) {
  ClockState._clockOutEquipDayMiss = null;
  if (!entry.jobId) return;
  // v1.15.2000 COD 🔴 — make the shift's own day resolvable (it may predate
  // today) before either fill path picks one.
  await _clockEnsureEntryJobDays(entry);
  // TASK-9579 — no job day on the shift's own date means nothing below may
  // fill a truck. Record it so the sheet SAYS so (renderEquipmentList) instead
  // of reading like dispatch assigned nothing; a fast submit would otherwise
  // send a ticket with no truck line (TASK-1417). Shown only while the sheet
  // has no equipment, so a tied rig filled flagged below, or a pick, clears it.
  ClockState._clockOutEquipDayMiss = _clockEquipDayMiss(entry);

  // TASK-586 — when per-operator equipment tracking is ON, route through the
  // operator-scoped fill (with the mid-week guard). When OFF (default), fall
  // through to the legacy all-assets fill — UNCHANGED. Mirrors jobboard.js
  // jbAutoFillEquipment → _jbAutoFillEquipmentTied routing (TASK-491) — keep
  // in sync.
  if (equipmentTieEnabled()) {
    await _clockAutoFillEquipmentTied(entry);
    // TASK-1572 Slice 2 — the driver's clock-in answer on an empty day (a
    // provenance claim, never assetIds). No-op for every entry without one.
    await _clockFillDriverClaimedAssets(entry);
    return;
  }
  // v1.15.2000 COD 🔴 — the SHIFT's day (clock-in tenant date), never the first
  // jobDay of the job that happens to be loaded. No day → fill nothing.
  const _legacyDay = _clockResolveJobDayForEntry(entry);
  if (!_legacyDay) return;
  return _clockAutoFillEquipmentAll(entry, null, _legacyDay);
}

// TASK-586 — per-operator clock-out fill (ON path only; OFF never reaches
// here). Mirrors jobboard.js _jbAutoFillEquipmentTied (TASK-491) — keep in
// sync. Fills ONLY the equipment THIS person tied to at clock-in
// (entry.assetIds ∩ the day's assets); rate/contract resolution is delegated
// to the legacy fill body via the allowedAssetIds filter so billing math is
// identical either way.
//
// MID-WEEK GUARD (same never-mix rule as the jobboard twin): if ANY visible
// clock entry on this jobDay lacks an assetIds field (created before the
// tenant turned the setting on, via a kiosk/Workforce ADMIN clock-in of
// another person, or via dispatch.js), the WHOLE day falls back to the
// LEGACY fill (all trucks+trailers+assets) so we never half-attribute a day.
//
// VISIBILITY SCOPE: visible set = ClockState.activeEntries + recentEntries —
// tenant-wide for workforce admins, personId-scoped for self-only viewers
// (TASK-452). Per the COD-reviewed note on the jobboard twin, own-entries
// visibility is sufficient: this fill only ever writes THIS entry's own
// equipment lines (keyed to entry.id); cross-crew full-day never-mix is
// enforced at the billing layer (TASK-495).
// TASK-1635 (💰) — mirrors jobboard.js `_jbFillTiedOffDayAssets` family; keep in
// sync. A clock-in-tied asset that isn't on the day's jobDay must STILL become
// an EQUIPMENT row (flagged `_offJobDay` → `_billingReviewFlag` at line write),
// never a silent drop. See the jobboard twin's comment for the full story.
function _clockResolveAnyAsset(id) {
  let asset = _resolveFleetRef(id, ClockState.trucks);
  let assetType = 'truck';
  if (!asset) { asset = _resolveFleetRef(id, ClockState.trailers); assetType = 'trailer'; }
  if (!asset) { asset = _resolveFleetRef(id, ClockState.assets); assetType = 'asset'; }
  return asset ? { asset, assetType } : null;
}

// Day refs may be doc ids OR unit/name strings (live jobDays mix both) —
// compare via both forms so an on-day asset is never false-flagged as off-day.
function _clockExpandedDayAssetIds(jobDay) {
  const raw = _clockJobDayAssetIds(jobDay);
  const out = new Set(raw);
  raw.forEach(r => {
    const hit = _clockResolveAnyAsset(r);
    if (hit && hit.asset.id != null) out.add(String(hit.asset.id));
  });
  return out;
}

function _clockTiedIdIsOnDay(id, expandedDayIds) {
  if (expandedDayIds.has(id)) return true;
  const hit = _clockResolveAnyAsset(id);
  return !!(hit && hit.asset.id != null && expandedDayIds.has(String(hit.asset.id)));
}

// The review flag an `_offJobDay` row carries onto its ticketLine — same
// single-flag convention as the server's equipmentLineEnrichment flags.
function _clockOffDayFlag() {
  return { reason: 'equipment_not_on_jobday', tiedAtClockIn: true, flaggedAt: Timestamp.now() };
}

// TASK-1674 — the SESSION-LOCAL marker a GUESSED equipment rate carries.
// `_equipRateMatchCore` could not find a price-book rule for this vehicle, so
// the number came from a description regex, a seeded item-code default, or a
// tie between several equally-matching lines. The line still bills — dropping
// the legacy-shaped tenants to $0 would block a biller mid-close — but it must
// never again look like a clean price-book hit. Returns null when the match was
// trustworthy.
//
// 🔴 This object is NOT written to Firestore. `validClientBillingReviewFlag()`
// in firestore.rules pins a CLIENT-authored `_billingReviewFlag` to exactly one
// shape ({reason:'equipment_not_on_jobday', tiedAtClockIn, flaggedAt}), and a
// clock-only member's close-out is evaluated by precisely that arm — a second
// reason string would deny the whole ticketLine write and trap a driver at
// clock-out. So the line carries a plain `rateResolvedBy` string, and the
// billing review banner re-derives the guess from the price book at render
// (billing.js `_equipRateGuessLines`). Persisting the richer flag from here
// needs a firestore.rules change first.
function _clockRateGuessFlag(match, vehicleType, assetName) {
  if (!match || match.rateGuessed !== true) return null;
  return {
    reason: 'equipment_rate_guessed',
    matchedVia: match.matchedVia || '',
    matchedDescription: match.matchedDescription || '',
    candidateCount: match.candidateCount || 0,
    vehicleType: vehicleType || '',
    assetName: assetName || '',
    rate: match.rate != null ? match.rate : null,
    flaggedAt: Timestamp.now()
  };
}

// Fill tied-but-off-day assets as flagged rows. Unlike the jobboard twin this
// surface (kiosk/admin/workforce) writes RESOLVED rates, so mirror the legacy
// fill's contract lookup; a no-match stays rate 0 and lands in the billing
// review badges exactly like any other unpriced line.
async function _clockFillTiedOffDayAssets(entry, offIds) {
  if (!Array.isArray(offIds) || offIds.length === 0) return;
  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
  const customerId = entry.customerId || null;
  if (customerId && customerId !== '__shop__') {
    await loadContractRates(customerId); // cached — same call the legacy fill makes
  }
  const seen = entry.ticketId
    ? await getExistingEquipmentIds(entry.ticketId, entry.id)
    : new Set();
  (ClockState.clockOutEquipment || []).forEach(e => { if (e.assetId) seen.add(e.assetId); });
  const added = [];
  for (const id of offIds) {
    const hit = _clockResolveAnyAsset(id);
    const assetId = hit ? (hit.asset.id || id) : id;
    if (seen.has(assetId) || seen.has(id)) continue;
    seen.add(assetId);
    const assetType = hit ? hit.assetType : 'truck';
    const assetName = hit
      ? ((window.getUnitDisplay ? window.getUnitDisplay(hit.asset) : '') || hit.asset.name || hit.asset.unit || 'Equipment')
      : ('Unit ' + id);
    const _vt = hit ? (hit.asset.type || hit.asset.category || '') : '';
    const contractMatch = customerId && customerId !== '__shop__'
      ? await lookupContractRate(customerId, 'EQUIPMENT', assetType, _vt, assetName)
      : null;
    ClockState.clockOutEquipment.push({
      assetId,
      assetName,
      assetType,
      fleetNumber: hit ? (hit.asset.unit || hit.asset.partNumber || '') : '',
      vehicleType: _vt,
      _rateGuess: _clockRateGuessFlag(contractMatch, _vt, assetName),
      rate: contractMatch?.rate ?? FALLBACK_RATES[assetType] ?? 0,
      rateUnit: contractMatch?.rateUnit || 'Hourly',
      roundToHalfHour: contractMatch ? contractMatch.roundToHalfHour === true : _clockNoMatchRounds(customerId), // TASK-9489
      includeOnInvoice: contractMatch?.includeOnInvoice !== false,
      revenueCategory: contractMatch?.revenueCategory || '',
      lineItemId: null,
      contractId: null,
      startTime: toLocalDatetimeStr(clockIn),
      endTime: toLocalDatetimeStr(new Date()),
      // TASK-4614 — auto-filled default window IS the clock window, so it tracks
      // the clock until the driver types a time or taps the chip off.
      _followsClock: true,
      // TASK-1635 — tied at clock-in, not on the jobDay: bill + flag, never drop.
      _offJobDay: true
    });
    added.push(assetName);
  }
  if (added.length > 0) {
    window.showToast?.(
      added.join(', ') + (added.length === 1 ? ' isn\'t' : ' aren\'t')
      + ' on this day\'s job — kept on the ticket from the clock-in and flagged for billing review.',
      'warning', null, 8000
    );
  }
}

async function _clockAutoFillEquipmentTied(entry) {
  const jobDay = _clockResolveJobDayForEntry(entry);
  const _tiedIds = (Array.isArray(entry.assetIds) ? entry.assetIds : []).map(String);
  if (!jobDay) {
    // TASK-1635 — no resolvable jobDay used to mean the tied rig vanished.
    await _clockFillTiedOffDayAssets(entry, _tiedIds);
    return;
  }

  // Entries on THIS day = the resolver's own tenant-day rule (jobDayId pins the
  // same-date tie), so grouping and day selection can never disagree.
  const visible = [...(ClockState.activeEntries || []), ...(ClockState.recentEntries || [])];
  const dayEntries = visible.filter(e => e.jobId === entry.jobId
    && _clockResolveJobDayForEntry({ jobId: e.jobId, clockIn: e.clockIn, jobDayId: jobDay.id }) === jobDay);
  // Defensive: always include the entry being clocked out so its own assetIds
  // count even if the subscription is still settling.
  if (!dayEntries.some(e => e.id === entry.id)) dayEntries.push(entry);

  // TASK-1539 — "missing" now includes empty-array entries whose [] means
  // "there was nothing to pick at clock-in" (decision table on the jobboard
  // twin _jbEntryCountsAsMissingAssetIds), so a jobDay that gets its equipment
  // AFTER a driver clocked in can still bill it via the legacy fallback.
  // SPLIT jobs keep the pre-1539 predicate exactly (rule 2 short-circuits any
  // [] promotion — TASK-1221 double-bill hazard; a job missing from the loaded
  // list resolves 'combined', matching jobboard.js _jbJobSplitBy).
  const _afJob = (ClockState.allJobs || []).find(j => j.id === entry.jobId);
  const _afSplitJob = ((_afJob && _afJob.splitBy) || 'combined') !== 'combined';
  const _afDayAssetIds = _clockJobDayAssetIds(jobDay);
  const anyMissingAssetIds = dayEntries.some(e => _clockEntryCountsAsMissingAssetIds(e, {
    splitJob: _afSplitJob,
    jobDayHasAssetsNow: _afDayAssetIds.size > 0
  }));

  // TASK-1635 — partition the tied assets ONCE: on-day fills clean, off-day
  // fills flagged. Both branches below append the off-day rows (the legacy
  // fallback only iterates the DAY's list, so a tied off-day rig was dropped
  // there too).
  const _expandedDayIds = _clockExpandedDayAssetIds(jobDay);
  const _offDayTiedIds = _tiedIds.filter(id => !_clockTiedIdIsOnDay(id, _expandedDayIds));

  if (anyMissingAssetIds) {
    // MID-WEEK FALLBACK — legacy fill, identical to pre-586 behavior, but
    // pinned to the SAME date-resolved jobDay (COD Code Reviewer 2026-06-12:
    // don't let the fill re-find a different day sharing this jobId).
    await _clockAutoFillEquipmentAll(entry, null, jobDay);
    await _clockFillTiedOffDayAssets(entry, _offDayTiedIds);
    return;
  }

  // PER-OPERATOR FILL: THIS entry's chosen assetIds; on-day ones fill clean
  // (matching via BOTH ref forms — _clockTiedIdIsOnDay), and the off-day
  // remainder is appended FLAGGED below instead of silently dropped (TASK-1635).
  const chosen = new Set();
  _tiedIds.forEach(id => {
    if (!_clockTiedIdIsOnDay(id, _expandedDayIds)) return;
    chosen.add(id);
    const hit = _clockResolveAnyAsset(id);
    if (hit && hit.asset.id != null) chosen.add(String(hit.asset.id));
  });
  await _clockAutoFillEquipmentAll(entry, chosen, jobDay);
  await _clockFillTiedOffDayAssets(entry, _offDayTiedIds);
}

// TASK-586 — renamed from autoFillEquipment (now the router above). Body is
// the original legacy fill, UNCHANGED except for two optional params:
//   • allowedAssetIds — null → fill ALL the day's assets (legacy,
//     byte-identical when equipmentTie is OFF); a Set → fill only assets
//     whose resolved doc id OR raw jobDay ref is in the set (per-operator).
//   • resolvedJobDay — the tied path passes its date-resolved jobDay so the
//     fill can't re-find a DIFFERENT day sharing the same jobId (COD Code
//     Reviewer 2026-06-12). Omitted on the legacy path → original first-match
//     lookup, byte-identical.
async function _clockAutoFillEquipmentAll(entry, allowedAssetIds, resolvedJobDay) {
  if (!entry.jobId) return;

  // v1.15.2000 COD 🔴 — no first-match fallback: an unresolved call resolves the
  // shift's own day or fills nothing.
  const jobDay = resolvedJobDay || _clockResolveJobDayForEntry(entry);
  if (!jobDay) return;

  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
  const customerId = entry.customerId || null;

  // Pre-load contract rates for this customer (caches internally)
  if (customerId && customerId !== '__shop__') {
    await loadContractRates(customerId);
  }

  // Line item selections from dispatch (overrides regex rate matching)
  const selections = jobDay.lineItemSelections || {};

  // Dedup: seed with equipment already on this ticket FOR THIS SESSION.
  // TASK-427: session-scoped (entry.id) so a re-clock (new clockEntry) still
  // auto-fills its own equipment instead of being blocked by session-1's lines.
  const seenAssetIds = entry.ticketId
    ? await getExistingEquipmentIds(entry.ticketId, entry.id)
    : new Set();

  // Add trucks
  for (const ref of (jobDay.trucks || [])) {
    const truck = _resolveFleetRef(ref, ClockState.trucks);
    // TASK-586 — per-operator scope: skip assets the operator didn't confirm.
    if (allowedAssetIds && truck && !allowedAssetIds.has(String(truck.id)) && !allowedAssetIds.has(String(ref))) continue;
    if (truck && !seenAssetIds.has(truck.id)) {
      seenAssetIds.add(truck.id);
      const selected = selections[`truck:${truck.id}`];
      let rate, roundToHalfHour = false, lineItemId = null, contractId = null, rateUnit = 'Hourly', includeOnInvoice = true, eqRevCat = '';
      const _tkName = window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.name || truck.unit || 'Truck');
      let _tkGuess = null;
      if (selected?.rate != null) {
        rate = selected.rate;
        roundToHalfHour = _clockSelectionRounds(selected, customerId); // TASK-9489
        lineItemId = selected.lineItemId || null;
        contractId = selected.contractId || null;
        rateUnit = selected.rateUnit || 'Hourly';
        includeOnInvoice = selected.includeOnInvoice !== false;
        eqRevCat = selected.revenueCategory || '';
      } else {
        const contractMatch = customerId ? await lookupContractRate(customerId, 'EQUIPMENT', 'truck', truck.type || '', _tkName) : null;
        rate = contractMatch?.rate ?? FALLBACK_RATES.truck;
        roundToHalfHour = contractMatch ? contractMatch.roundToHalfHour === true : _clockNoMatchRounds(customerId); // TASK-9489
        rateUnit = contractMatch?.rateUnit || 'Hourly';
        includeOnInvoice = contractMatch?.includeOnInvoice !== false;
        eqRevCat = contractMatch?.revenueCategory || '';
        _tkGuess = _clockRateGuessFlag(contractMatch, truck.type || '', _tkName);
      }
      ClockState.clockOutEquipment.push({
        assetId: truck.id,
        assetName: _tkName,
        assetType: 'truck',
        fleetNumber: truck.unit || '',
        vehicleType: truck.type || '',
        _rateGuess: _tkGuess,
        rate,
        rateUnit,
        roundToHalfHour,
        includeOnInvoice,
        revenueCategory: eqRevCat,
        lineItemId,
        contractId,
        startTime: toLocalDatetimeStr(clockIn),
        endTime: toLocalDatetimeStr(new Date()),
        // TASK-4614 — this end time is "whenever the sheet happened to open", not a
        // real shutdown time. It follows the clock window until the driver types a
        // time or taps the row's "Ends with shift" chip off.
        _followsClock: true
      });
    }
  }

  // Add trailers
  for (const ref of (jobDay.trailers || [])) {
    const trailer = _resolveFleetRef(ref, ClockState.trailers);
    // TASK-586 — per-operator scope: skip assets the operator didn't confirm.
    if (allowedAssetIds && trailer && !allowedAssetIds.has(String(trailer.id)) && !allowedAssetIds.has(String(ref))) continue;
    if (trailer && !seenAssetIds.has(trailer.id)) {
      seenAssetIds.add(trailer.id);
      const selected = selections[`trailer:${trailer.id}`];
      let rate, roundToHalfHour = false, lineItemId = null, contractId = null, rateUnit = 'Hourly', includeOnInvoice = true, eqRevCat = '';
      const _trName = window.getUnitDisplay ? window.getUnitDisplay(trailer) : (trailer.name || trailer.unit || 'Trailer');
      let _trGuess = null;
      if (selected?.rate != null) {
        rate = selected.rate;
        roundToHalfHour = _clockSelectionRounds(selected, customerId); // TASK-9489
        lineItemId = selected.lineItemId || null;
        contractId = selected.contractId || null;
        rateUnit = selected.rateUnit || 'Hourly';
        includeOnInvoice = selected.includeOnInvoice !== false;
        eqRevCat = selected.revenueCategory || '';
      } else {
        const contractMatch = customerId ? await lookupContractRate(customerId, 'EQUIPMENT', 'trailer', trailer.type || '', _trName) : null;
        rate = contractMatch?.rate ?? FALLBACK_RATES.trailer;
        roundToHalfHour = contractMatch ? contractMatch.roundToHalfHour === true : _clockNoMatchRounds(customerId); // TASK-9489
        rateUnit = contractMatch?.rateUnit || 'Hourly';
        includeOnInvoice = contractMatch?.includeOnInvoice !== false;
        eqRevCat = contractMatch?.revenueCategory || '';
        _trGuess = _clockRateGuessFlag(contractMatch, trailer.type || '', _trName);
      }
      ClockState.clockOutEquipment.push({
        assetId: trailer.id,
        assetName: _trName,
        assetType: 'trailer',
        fleetNumber: trailer.unit || '',
        vehicleType: trailer.type || '',
        _rateGuess: _trGuess,
        rate,
        rateUnit,
        roundToHalfHour,
        includeOnInvoice,
        revenueCategory: eqRevCat,
        lineItemId,
        contractId,
        startTime: toLocalDatetimeStr(clockIn),
        endTime: toLocalDatetimeStr(new Date()),
        // TASK-4614 — this end time is "whenever the sheet happened to open", not a
        // real shutdown time. It follows the clock window until the driver types a
        // time or taps the row's "Ends with shift" chip off.
        _followsClock: true
      });
    }
  }

  // Add assets
  for (const ref of (jobDay.assets || [])) {
    const asset = _resolveFleetRef(ref, ClockState.assets);
    // TASK-586 — per-operator scope: skip assets the operator didn't confirm.
    if (allowedAssetIds && asset && !allowedAssetIds.has(String(asset.id)) && !allowedAssetIds.has(String(ref))) continue;
    if (asset && !seenAssetIds.has(asset.id)) {
      seenAssetIds.add(asset.id);
      const selected = selections[`asset:${asset.id}`];
      let rate, roundToHalfHour = false, lineItemId = null, contractId = null, rateUnit = 'Hourly', includeOnInvoice = true, eqRevCat = '';
      const _asName = asset.name || 'Asset';
      const _asVt = asset.type || asset.category || '';
      let _asGuess = null;
      if (selected?.rate != null) {
        rate = selected.rate;
        roundToHalfHour = _clockSelectionRounds(selected, customerId); // TASK-9489
        lineItemId = selected.lineItemId || null;
        contractId = selected.contractId || null;
        rateUnit = selected.rateUnit || 'Hourly';
        includeOnInvoice = selected.includeOnInvoice !== false;
        eqRevCat = selected.revenueCategory || '';
      } else {
        // TASK-1674 — this call used to pass NO vehicleType at all, so an asset
        // could never reach the v2 appliesTo arm and always fell to the guess.
        const contractMatch = customerId ? await lookupContractRate(customerId, 'EQUIPMENT', 'asset', _asVt, _asName) : null;
        rate = contractMatch?.rate ?? FALLBACK_RATES.asset;
        roundToHalfHour = contractMatch ? contractMatch.roundToHalfHour === true : _clockNoMatchRounds(customerId); // TASK-9489
        rateUnit = contractMatch?.rateUnit || 'Hourly';
        includeOnInvoice = contractMatch?.includeOnInvoice !== false;
        eqRevCat = contractMatch?.revenueCategory || '';
        _asGuess = _clockRateGuessFlag(contractMatch, _asVt, _asName);
      }
      ClockState.clockOutEquipment.push({
        assetId: asset.id,
        assetName: _asName,
        assetType: 'asset',
        fleetNumber: asset.partNumber || '',
        vehicleType: _asVt,
        _rateGuess: _asGuess,
        rate,
        rateUnit,
        roundToHalfHour,
        includeOnInvoice,
        revenueCategory: eqRevCat,
        lineItemId,
        contractId,
        startTime: toLocalDatetimeStr(clockIn),
        endTime: toLocalDatetimeStr(new Date()),
        // TASK-4614 — this end time is "whenever the sheet happened to open", not a
        // real shutdown time. It follows the clock window until the driver types a
        // time or taps the row's "Ends with shift" chip off.
        _followsClock: true
      });
    }
  }
}

function buildClockOutModalContent(entry, person, clockIn, now, hours, duration) {
  const buildTickets = shouldBuildTickets();
  const isFounderAcct = isFounder();
  const clockInStr = toLocalDatetimeStr(clockIn, 'adj-clock-in');
  const clockOutStr = toLocalDatetimeStr(now, 'adj-clock-out');

  let html = `
    <!-- Summary -->
    <div class="clock-out-summary" style="margin-bottom:var(--spacing-lg);">
      <div class="summary-row"><span>Employee:</span><span><strong>${escapeHtml(person?.name || entry.personName)}</strong></span></div>
      <div class="summary-row"><span>Customer:</span><span>${escapeHtml(entry.customerName || 'Shop')}</span></div>
      ${entry.jobTitle ? `<div class="summary-row"><span>Job:</span><span>${escapeHtml(entry.jobTitle)}</span></div>` : ''}
      <div class="summary-row"><span>Duration:</span><span><strong>${hours} hrs</strong> (${duration})</span></div>
    </div>
  `;

  // Time Adjustment (ticket-building accounts, gated by COF template + driver permission)
  const _isDriverScope = !!(window.AppState?.driverMode || window.AppState?.driverModePin || window.AppState?.isPublicAccess);
  const _canAdjustTimes = !_isDriverScope || window.AppState?.settings?.allowDriverTimeEdit === true;
  if (buildTickets && isCOFSectionEnabled('timeclock') && _canAdjustTimes) {
    html += `
      <div class="clock-out-section" style="margin-bottom:var(--spacing-lg);">
        <h4 style="margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;">schedule</span> Time Adjustment
        </h4>
        <div style="display:flex;gap:var(--spacing-md);flex-wrap:wrap;">
          <div class="form-group" style="flex:1;min-width:200px;">
            <label style="font-size:0.8rem;">Clock In</label>
            ${renderSplitDateTime('adj-clock-in', clockInStr, 'window.ClockModule.pauseClockOutTimer(); window.ClockModule.recalcHours()')}
          </div>
          <div class="form-group" style="flex:1;min-width:200px;">
            <label style="font-size:0.8rem;">Clock Out</label>
            ${renderSplitDateTime('adj-clock-out', clockOutStr, 'window.ClockModule.markClockOutTyped(); window.ClockModule.recalcHours()')}
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
          <span style="font-size:0.85rem;color:var(--text-secondary);">Adjusted hours: <strong id="adj-hours">${hours}</strong></span>
          <button type="button" class="btn btn-secondary btn-sm" onclick="window.ClockModule.resetTimes()">Reset</button>
        </div>
      </div>
    `;
  }

  // Equipment (ticket-building accounts, gated by COF template)
  if (buildTickets && isCOFSectionEnabled('equipment')) {
    html += `
      <div class="clock-out-section" style="margin-bottom:var(--spacing-lg);">
        <h4 style="margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;">local_shipping</span> Equipment Used
        </h4>
        <div id="equipment-list">
          ${renderEquipmentList()}
        </div>
        <div style="margin-top:var(--spacing-sm);">
          <button type="button" class="btn btn-secondary btn-sm" onclick="window.ClockModule.showEquipmentPicker()">
            <span class="material-icons">add</span> Add Equipment
          </button>
          <div id="equipment-picker" class="hidden" style="margin-top:var(--spacing-sm);"></div>
        </div>

        <!-- Mileage Toggle -->
        <div style="display:flex;align-items:center;gap:var(--spacing-sm);margin-top:var(--spacing-md);">
          <input type="checkbox" id="mileage-toggle" onchange="window.ClockModule.toggleMileage()">
          <label for="mileage-toggle" style="cursor:pointer;margin:0;font-weight:600;">
            <span class="material-icons" style="font-size:18px;vertical-align:middle;">speed</span>
            Log Mileage
          </label>
        </div>
        <div id="mileage-fields" class="hidden" style="margin-top:var(--spacing-sm);display:flex;gap:var(--spacing-sm);flex-wrap:wrap;align-items:flex-end;">
          <div style="flex:1;min-width:120px;">
            <label style="font-size:0.75rem;color:var(--text-secondary);display:block;margin-bottom:2px;">Start Mileage</label>
            <input type="number" id="mileage-start" placeholder="e.g. 102450" style="width:100%;font-size:1rem;padding:8px;" min="0" step="1"
                   oninput="window.ClockModule.updateMileageTotal()">
          </div>
          <div style="flex:1;min-width:120px;">
            <label style="font-size:0.75rem;color:var(--text-secondary);display:block;margin-bottom:2px;">Stop Mileage</label>
            <input type="number" id="mileage-stop" placeholder="e.g. 102580" style="width:100%;font-size:1rem;padding:8px;" min="0" step="1"
                   oninput="window.ClockModule.updateMileageTotal()">
          </div>
          <div id="mileage-total" style="font-weight:700;font-size:1rem;min-width:80px;text-align:center;padding:8px 0;color:var(--brand-blue);"></div>
        </div>
      </div>
    `;

  }

  // Downtime (gated by COF template)
  if (buildTickets && isCOFSectionEnabled('downtime')) {
    html += `
      <div class="clock-out-section" style="margin-bottom:var(--spacing-lg);">
        <h4 style="margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;">pause_circle</span> Downtime
        </h4>
        <div id="downtime-list">
          ${ClockState.clockOutDowntime.length === 0 ? '<div class="empty-state-small" style="padding:var(--spacing-sm) 0;">No downtime entries</div>' : ''}
        </div>
        <button type="button" class="btn btn-secondary btn-sm" onclick="window.ClockModule.addDowntimeRow()" style="margin-top:var(--spacing-sm);">
          <span class="material-icons">add</span> Add Downtime
        </button>
      </div>
    `;
  }

  // Standby (gated by COF template)
  if (buildTickets && isCOFSectionEnabled('standby')) {
    html += `
      <div class="clock-out-section" style="margin-bottom:var(--spacing-lg);">
        <h4 style="margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;">hourglass_empty</span> Standby
        </h4>
        <div id="standby-list">
          ${ClockState.clockOutStandby.length === 0 ? '<div class="empty-state-small" style="padding:var(--spacing-sm) 0;">No standby entries</div>' : ''}
        </div>
        <button type="button" class="btn btn-secondary btn-sm" onclick="window.ClockModule.addStandbyRow()" style="margin-top:var(--spacing-sm);">
          <span class="material-icons">add</span> Add Standby
        </button>
      </div>
    `;
  }

  // Custom Ticket Sections (replaces hardcoded Field Activity / Line Info)
  if (buildTickets) {
    html += buildCustomSectionsHtml('clock');
  }

  // Start/Stop Time (ticket-building accounts, gated by COF template)
  if (buildTickets && isCOFSectionEnabled('startStopTimes')) {
    // Use saved ticket times if available, otherwise fall back to clock entry times
    let _ssClockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    let _ssClockOut = new Date();
    if (entry.ticketId && ClockState._ticketWorkTimes) {
      if (ClockState._ticketWorkTimes.workStartTime) _ssClockIn = ClockState._ticketWorkTimes.workStartTime;
      if (ClockState._ticketWorkTimes.workStopTime) _ssClockOut = ClockState._ticketWorkTimes.workStopTime;
    }
    // clock-dst-all — company zone, never the phone's (toLocalDatetimeStr).
    const _ssFmt = (d, key) => toLocalDatetimeStr(d, key);
    html += `
      <div class="clock-out-section" style="margin-bottom:var(--spacing-lg);">
        <h4 style="margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;">timer</span> Work Start / Stop
        </h4>
        <div style="display:flex;gap:var(--spacing-md);flex-wrap:wrap;">
          <div class="form-group" style="flex:1;min-width:180px;">
            <label style="font-size:0.8rem;">Start Time</label>
            ${renderSplitDateTime('work-start-time', _ssFmt(_ssClockIn, 'work-start-time'))}
          </div>
          <div class="form-group" style="flex:1;min-width:180px;">
            <label style="font-size:0.8rem;">Stop Time</label>
            ${renderSplitDateTime('work-stop-time', _ssFmt(_ssClockOut, 'work-stop-time'))}
          </div>
        </div>
        <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">
          Auto-filled from clock times. Adjust if billable work window differs.
        </div>
      </div>
    `;
  }

  // Notes — split shift/ticket notes for ticket-building accounts, simple for non-ticket
  // Gated by COF template 'notes' section
  if (buildTickets && isCOFSectionEnabled('notes')) {
    html += `
      <div class="form-group" style="margin-bottom:var(--spacing-sm);">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;">
          <label style="margin:0;">Shift Notes <span style="font-size:0.7rem;font-weight:400;color:var(--text-secondary);">(internal)</span></label>
          <span style="font-size:0.7rem;color:var(--accent-teal);font-weight:500;display:flex;align-items:center;gap:3px;">
            <span class="material-icons" style="font-size:14px;">auto_fix_high</span> Auto-formatted on review
          </span>
        </div>
        <textarea id="clock-out-shift-notes" rows="2" placeholder="${window.AppState?.settings?.requireShiftNotes === true ? 'REQUIRED — What did you do today?' : 'What did you do today?'}"
          oninput="window.ClockModule._cachedShiftNotes=this.value"></textarea>
      </div>
      <div style="margin-bottom:var(--spacing-sm);">
        <button type="button" class="btn btn-secondary btn-sm" id="btn-copy-ticket-notes" onclick="window.ClockModule.copyToTicketNotes()">
          Copy to Ticket Notes &rarr;
        </button>
      </div>
    `;

    // Per-asset notes (when enabled)
    if (isPerAssetNotesEnabled() && ClockState.clockOutEquipment.length > 0) {
      const noteableEquip = ClockState.clockOutEquipment.filter(eq => assetTypeGetsNotes(eq.assetType));
      if (noteableEquip.length > 0) {
        html += `
          <div class="clock-out-section" style="margin-bottom:var(--spacing-md);">
            <h4 style="margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
              <span class="material-icons" style="font-size:18px;">note_add</span> Equipment Notes
            </h4>
        `;
        for (const eq of noteableEquip) {
          const label = eq.assetName || eq.fleetNumber || eq.assetId;
          const _eqIdAttr = window.escAttr ? window.escAttr(eq.assetId) : escapeHtml(String(eq.assetId || ''));
          const typeTag = eq.assetType === 'trailer' ? ' (Trailer)' : eq.assetType === 'asset' ? ' (Asset)' : '';
          html += `
            <div style="margin-bottom:var(--spacing-sm);">
              <label style="font-size:0.8rem;font-weight:600;display:block;margin-bottom:2px;">${escapeHtml(label)}${typeTag}</label>
              <!-- 🔴 TASK-4559 (COD Code Reviewer ❌, blocking) — assetId is Firestore data, not a
                   JS-string-safe literal. It used to be interpolated RAW into both the id="" and
                   an inline handler's string argument: an apostrophe silently broke the handler
                   (so the note never captured, which is the very bug this task fixes), and a
                   crafted id was inline-script injection. escapeHtml is NOT attribute-safe — it
                   skips quotes — so the attribute uses window.escAttr, and the handler now reads
                   this.dataset.assetId instead of carrying the value inside quoted inline JS.
                   Same shape as roster.js's data-id/dataset pattern. getElementById still matches:
                   attribute ESCAPING is a source-text concern, the DOM id keeps the raw value. -->
              <textarea id="clock-asset-notes-${_eqIdAttr}" data-asset-id="${_eqIdAttr}" class="textarea-auto-expand sa-asset-note" rows="1" placeholder="What did ${window.escAttr ? window.escAttr(label) : escapeHtml(label)} do? Loads hauled, task, location..."
                oninput="this.style.height='auto';this.style.height=this.scrollHeight+'px';window.ClockModule.captureAssetNote(this.dataset.assetId, this.value)">${escapeHtml(eq._noteDraft !== undefined ? eq._noteDraft : (eq._savedNote || ''))}</textarea>
            </div>
          `;
        }
        html += `</div>`;
      }

      // Collapsible general ticket notes
      html += `
        <div class="review-details-card" style="margin-bottom:var(--spacing-lg);">
          <div class="review-details-header" onclick="this.parentElement.classList.toggle('collapsed')">
            <span><span class="material-icons">receipt_long</span> General Ticket Notes <span style="font-size:0.7rem;font-weight:400;color:var(--text-secondary);">(appears on invoice)</span></span>
            <span class="material-icons review-details-chevron">expand_less</span>
          </div>
          <div class="review-details-body">
            <textarea id="clock-out-ticket-notes" rows="2" placeholder="Notes for customer..."></textarea>
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:6px;line-height:1.4;">Shared with the whole crew — add your line, everyone's is kept.</div>
          </div>
        </div>
      `;
    } else {
      html += `
        <div class="form-group" style="margin-bottom:var(--spacing-lg);">
          <label>Ticket Notes <span style="font-size:0.7rem;font-weight:400;color:var(--text-secondary);">(appears on invoice)</span></label>
          <textarea id="clock-out-ticket-notes" rows="2" placeholder="Notes for customer..."></textarea>
          <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:6px;line-height:1.4;">Shared with the whole crew — add your line, everyone's is kept.</div>
        </div>
      `;
    }
  } else {
    html += `
      <div class="form-group">
        <label for="clock-out-notes">Clock Out Notes</label>
        <textarea id="clock-out-notes" rows="2" placeholder="Optional notes..."></textarea>
      </div>
    `;
  }

  // Signatures (non-founder, from fieldTicketSettings)
  const ftSettings = ClockState.fieldTicketSettings;
  if (!isFounderAcct && ftSettings && (ftSettings.requireDriverSignature || ftSettings.requireCustomerSignature)) {
    html += `
      <div class="clock-out-section" style="margin-bottom:var(--spacing-lg);">
        <h4 style="margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;">draw</span> Signatures
        </h4>
        ${ftSettings.requireDriverSignature ? `
          <div style="margin-bottom:var(--spacing-md);">
            <label style="font-size:0.85rem;font-weight:600;display:block;margin-bottom:4px;">Driver Signature</label>
            <div class="sig-canvas-wrap" id="sig-wrap-driver">
              <canvas id="sig-canvas-driver"></canvas>
              <span class="sig-placeholder">Sign here</span>
            </div>
            <button type="button" class="btn btn-secondary btn-sm" onclick="window.ClockModule.clearSignature('driver')" style="margin-top:4px;">Clear</button>
          </div>
        ` : ''}
        ${ftSettings.requireCustomerSignature ? `
          <div>
            <label style="font-size:0.85rem;font-weight:600;display:block;margin-bottom:4px;">Customer Signature</label>
            <div class="sig-canvas-wrap" id="sig-wrap-customer">
              <canvas id="sig-canvas-customer"></canvas>
              <span class="sig-placeholder">Customer signs here</span>
            </div>
            <div style="margin-top:4px;display:flex;gap:6px;">
              <button type="button" class="btn btn-secondary btn-sm" onclick="window.ClockModule.clearSignature('customer')">Clear</button>
              <button type="button" class="btn btn-secondary btn-sm" id="btn-sig-na-customer" onclick="window.ClockModule.markSignatureNA('customer')">Not Available</button>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  // Stamp / Photo Capture (non-founder, from fieldTicketSettings)
  if (!isFounderAcct && ftSettings?.requireStamp) {
    html += `
      <div class="clock-out-section" style="margin-bottom:var(--spacing-lg);">
        <h4 style="margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;">verified</span> Stamp / Proof Photo
        </h4>
        <div id="stamp-preview-area"></div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          <button type="button" class="btn btn-secondary btn-sm" onclick="document.getElementById('stamp-camera-input').click()">
            <span class="material-icons">photo_camera</span> Capture
          </button>
          <button type="button" class="btn btn-secondary btn-sm" onclick="document.getElementById('stamp-file-input').click()">
            <span class="material-icons">upload_file</span> Upload
          </button>
          <button type="button" class="btn btn-secondary btn-sm" id="btn-stamp-na" onclick="window.ClockModule.markStampNA()">Not Available</button>
        </div>
        <input type="file" id="stamp-camera-input" hidden accept="image/*" capture="environment" onchange="window.ClockModule.handleStampCapture(this.files)">
        <input type="file" id="stamp-file-input" hidden accept="image/*" onchange="window.ClockModule.handleStampCapture(this.files)">
      </div>
    `;
  }

  // Field Ticket Items (non-founder, from contract line items with includeOnFieldTicket)
  // Only show items whose appliesTo.kind matches what's actually dispatched
  if (ClockState.fieldTicketLineItems.length > 0) {
    // v1.15.2000 COD 🔴 — the shift's day decides which truck/trailer items show.
    const jobDay = entry.jobId ? _clockResolveJobDayForEntry(entry) : null;
    const dispatchedKinds = new Set();
    dispatchedKinds.add('person'); // person is always dispatched (they're clocking out)
    if (jobDay?.trucks?.length) dispatchedKinds.add('truck');
    if (jobDay?.trailers?.length) dispatchedKinds.add('trailer');
    if (jobDay?.assets?.length) dispatchedKinds.add('asset');

    const visibleItems = ClockState.fieldTicketLineItems.filter(li => {
      if (!li.appliesTo?.kind) return true; // no kind = always show
      return dispatchedKinds.has(li.appliesTo.kind);
    });

    if (visibleItems.length > 0) {
      html += `
        <div class="clock-out-section" style="margin-bottom:var(--spacing-lg);">
          <h4 style="margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
            <span class="material-icons" style="font-size:18px;">list_alt</span> Field Ticket Items
          </h4>
          <div id="ft-items-list">
            ${visibleItems.map(li => `
              <div class="ft-item-row">
                <span class="ft-item-label">${escapeHtml(li.description)}</span>
                <span class="ft-item-rate">${_clockPriceBookLineFlags(li).noCharge ? 'N/C' : (li.rate !== undefined ? `$${(li.rate || 0).toFixed(2)}/${escapeHtml(li.rateUnit || 'ea')}` : `per ${escapeHtml(li.rateUnit || 'ea')}`)}</span>
                <input type="number" id="ft-qty-${li.id}" min="0" step="0.01" value="0" placeholder="Qty">
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }
  }

  // Email Copy (non-founder, from fieldTicketSettings)
  if (!isFounderAcct && ftSettings?.requireEmailCopy) {
    html += `
      <div class="clock-out-section" style="margin-bottom:var(--spacing-lg);">
        <h4 style="margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;">email</span> Email Ticket Copy
        </h4>
        <div class="form-group" style="margin-bottom:0;">
          <input type="email" id="ft-email-to" class="form-control" value="${escapeHtml(ftSettings.defaultEmailRecipient || '')}" placeholder="Email address...">
        </div>
      </div>
    `;
  }

  // Photos & Documents (ticket-building accounts, gated by COF template)
  if (buildTickets && isCOFSectionEnabled('photos')) {
    html += `
      <div class="clock-out-section">
        <h4 style="margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;">photo_camera</span> Photos &amp; Documents<span id="photo-count-label" style="color:var(--text-secondary);font-weight:500;"></span>
        </h4>
        <div id="photo-thumbnails" style="margin-bottom:var(--spacing-sm);"></div>
        <button type="button" class="btn btn-secondary btn-sm" onclick="document.getElementById('photo-input').click()">
          <span class="material-icons">add_photo_alternate</span> Add Photos
        </button>
        <input type="file" id="photo-input" hidden multiple accept="image/*,.pdf"
               onchange="window.ClockModule.handlePhotoSelect(this.files)">
      </div>
    `;
  }

  // TASK-1483 — Personal Vehicle (payroll, NOT gated on buildTickets).
  if (pvSectionOn(person)) html += pvSectionHTML('clock', 'light', null);

  return html;
}

// ============================================
// SIGNATURE PAD FUNCTIONS
// ============================================
const _sigState = { driver: { drawing: false, ctx: null, signed: false, na: false }, customer: { drawing: false, ctx: null, signed: false, na: false } };

// TASK-891 — lazily register an arbitrary signature-pad role so other modules
// (HR + compliance-partner SPH e-sign) can reuse this exact pad via
// window.ClockModule.* without a third pad implementation (Pitfall #1). The two
// built-in field-ticket roles (driver/customer) are unchanged.
function _ensureSigRole(role) {
  if (!_sigState[role]) _sigState[role] = { drawing: false, ctx: null, signed: false, na: false };
  return _sigState[role];
}

function initSignaturePad(role) {
  _ensureSigRole(role);
  const canvas = document.getElementById(`sig-canvas-${role}`);
  if (!canvas) return;
  const wrap = document.getElementById(`sig-wrap-${role}`);
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = 120 * dpr;
  canvas.style.height = '120px';
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.strokeStyle = '#000';
  _sigState[role].ctx = ctx;
  _sigState[role].signed = false;
  _sigState[role].na = false;

  const getPos = (e) => {
    const r = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); _sigState[role].drawing = true; const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!_sigState[role].drawing) return; e.preventDefault(); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); _sigState[role].signed = true; wrap?.classList.add('signed'); };
  const end = () => { _sigState[role].drawing = false; };

  canvas.addEventListener('mousedown', start);
  canvas.addEventListener('mousemove', move);
  canvas.addEventListener('mouseup', end);
  canvas.addEventListener('mouseleave', end);
  canvas.addEventListener('touchstart', start, { passive: false });
  canvas.addEventListener('touchmove', move, { passive: false });
  canvas.addEventListener('touchend', end);
}

function clearSignature(role) {
  _ensureSigRole(role);
  const canvas = document.getElementById(`sig-canvas-${role}`);
  if (!canvas || !_sigState[role].ctx) return;
  const dpr = window.devicePixelRatio || 1;
  _sigState[role].ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
  _sigState[role].signed = false;
  _sigState[role].na = false;
  document.getElementById(`sig-wrap-${role}`)?.classList.remove('signed', 'na');
  const naBtn = document.getElementById(`btn-sig-na-${role}`);
  if (naBtn) naBtn.textContent = 'Not Available';
}

function markSignatureNA(role) {
  _ensureSigRole(role);
  _sigState[role].na = !_sigState[role].na;
  const wrap = document.getElementById(`sig-wrap-${role}`);
  const naBtn = document.getElementById(`btn-sig-na-${role}`);
  if (_sigState[role].na) {
    wrap?.classList.add('na');
    if (naBtn) naBtn.textContent = 'Undo N/A';
    const canvas = document.getElementById(`sig-canvas-${role}`);
    if (canvas && _sigState[role].ctx) {
      const dpr = window.devicePixelRatio || 1;
      _sigState[role].ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    }
    _sigState[role].signed = false;
  } else {
    wrap?.classList.remove('na');
    if (naBtn) naBtn.textContent = 'Not Available';
  }
}

function getSignatureDataUrl(role) {
  _ensureSigRole(role);
  if (_sigState[role].na) return 'N/A';
  if (!_sigState[role].signed) return null;
  const canvas = document.getElementById(`sig-canvas-${role}`);
  return canvas ? canvas.toDataURL('image/png') : null;
}

function dataUrlToBlob(dataUrl) {
  const parts = dataUrl.split(',');
  const mime = parts[0].match(/:(.*?);/)[1];
  const b64 = atob(parts[1]);
  const u8 = new Uint8Array(b64.length);
  for (let i = 0; i < b64.length; i++) u8[i] = b64.charCodeAt(i);
  return new Blob([u8], { type: mime });
}

// ============================================
// STAMP / PROOF PHOTO FUNCTIONS
// ============================================
function compressImage(file, maxWidth = 1200) {
  // TASK-1383 — read via the guarded shared reader so a failed read REJECTS
  // instead of hanging this promise forever (the old resolve-only wrapper
  // never settled on an iOS-WebView/HEIC read failure, silently eating the
  // photo). Image decode failure also rejects (was another silent hang).
  // 90s timeout (not the 30s default) — this is a driver-critical EVIDENTIARY
  // path on old phones in the yard: a slow-but-succeeding big-photo read being
  // aborted would LOSE the stamp where the old code merely took long
  // (integrated-gate Independent/Domain).
  return window.FileReadUtil.readAsDataURL(file, { timeoutMs: 90000 }).then((dataUrl) => new Promise((resolve, reject) => {
    const img = new Image();
    // TASK-9402 COD (integration r1) — clock-out now WAITS on this promise, so
    // it must settle on every path: a throw inside onload (null 2d context on a
    // memory-starved phone) or a toBlob that hands back null / never calls back
    // used to leave it pending and the Clock Out button stuck on "Converting".
    let settled = false;
    const done = (fn, v) => { if (!settled) { settled = true; clearTimeout(guard); fn(v); } };
    const guard = setTimeout(() => done(reject, new Error(`Could not read ${file?.name || 'that photo'} — please try a different photo.`)), 60000);
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = (maxWidth / w) * h; w = maxWidth; }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas unavailable');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          if (blob) done(resolve, blob);
          else done(reject, new Error(`Could not read ${file?.name || 'that photo'} — please try a different photo.`));
        }, 'image/jpeg', 0.8);
      } catch (_) {
        done(reject, new Error(`Could not read ${file?.name || 'that photo'} — please try a different photo.`));
      }
    };
    img.onerror = () => done(reject, new Error(`Could not read ${file?.name || 'that photo'} — please try a different photo.`));
    img.src = dataUrl;
  }));
}

// TASK-9402 — stamp photo prep (HEIC conversion + compression) runs at pick
// time; every stamp saver waits it out via settleStampCapture() /
// settlePhotoPrepBeforeSave BEFORE locking its button, or a Save pressed
// mid-conversion would save the ticket without the stamp.
const _stampPhotoPrep = createPhotoPrepTracker();
let _stampPickGen = 0;

function handleStampCapture(files) {
  if (!files || files.length === 0) return;
  const file = files[0];
  // TASK-9402 — an iPhone HEIC can't be drawn by Windows Chrome/Firefox, so
  // compressImage's canvas pass failed with "Could not read IMG_0412.HEIC —
  // try a different photo". Convert to JPEG first (same converter, same
  // message as every other photo screen), THEN compress.
  const gen = ++_stampPickGen;
  return _stampPhotoPrep.track(toDrawablePhoto(file).then(compressImage).then(blob => {
    // A later pick (or a reset for another ticket) superseded this one.
    if (gen !== _stampPickGen) return;
    ClockState._stampFile = blob;
    ClockState._stampNA = false;
    // Show preview
    const previewArea = document.getElementById('stamp-preview-area');
    if (previewArea) {
      const url = URL.createObjectURL(blob);
      previewArea.innerHTML = `<img src="${url}" class="stamp-preview" style="margin-bottom:8px;">`;
    }
    const naBtn = document.getElementById('btn-stamp-na');
    if (naBtn) naBtn.textContent = 'Not Available';
  }).catch((err) => {
    // TASK-1383 — read/decode failure used to hang silently (photo looked
    // attached but wasn't). Tell the driver so they can retake it.
    // Integrated-gate Code Reviewer: also CLEAR any prior staged stamp — a
    // failed REPLACEMENT must not leave the older stamp silently queued for
    // upload as if it were the new one.
    if (gen !== _stampPickGen) return;
    ClockState._stampFile = null;
    const previewArea = document.getElementById('stamp-preview-area');
    if (previewArea) previewArea.innerHTML = '';
    console.error('[Clock] Stamp photo read error:', err);
    // TASK-9402 — the converter's own message for a HEIC it couldn't convert.
    window.showToast?.(photoErrorMessage(err, err?.message || 'Could not read that photo — please try again.'), 'error');
  }));
}

/**
 * TASK-9402 — a promise that resolves once no stamp photo is still
 * converting/compressing, or null when nothing is in flight (so a caller
 * folding it into its own save tracker doesn't flash "Converting photo…").
 */
function settleStampCapture() {
  return _stampPhotoPrep.busy ? _stampPhotoPrep.settle() : null;
}

function markStampNA() {
  ClockState._stampNA = !ClockState._stampNA;
  const naBtn = document.getElementById('btn-stamp-na');
  const previewArea = document.getElementById('stamp-preview-area');
  if (ClockState._stampNA) {
    // TASK-9402 COD (integration r1) — a photo still converting must not land
    // after the driver chose N/A (it would clear N/A and save the photo).
    _stampPickGen++;
    ClockState._stampFile = null;
    if (previewArea) previewArea.innerHTML = '<span style="font-size:13px;color:var(--text-secondary);font-style:italic;">Stamp not available</span>';
    if (naBtn) naBtn.textContent = 'Undo N/A';
  } else {
    if (previewArea) previewArea.innerHTML = '';
    if (naBtn) naBtn.textContent = 'Not Available';
  }
}

// TASK-395-B: reset/read stamp capture state so the Job Board Build Ticket modal
// can reuse handleStampCapture/markStampNA (which write ClockState._stamp*) without
// bleed between modal opens and can read the captured blob for its own persist.
function resetStampCapture() {
  _stampPickGen++; // TASK-9402 — a conversion still running for the last ticket must not land on this one
  ClockState._stampFile = null;
  ClockState._stampNA = false;
}
function getStampCapture() {
  return { file: ClockState._stampFile || null, na: !!ClockState._stampNA };
}

function renderEquipmentList() {
  if (ClockState.clockOutEquipment.length === 0) {
    // TASK-9579 — a date miss is NOT "dispatch assigned nothing": say it.
    const _miss = ClockState._clockOutEquipDayMiss;
    if (_miss && (!_miss.entryId || _miss.entryId === ClockState.clockOutEntry?.id)) {
      return `<div id="clock-out-equip-daymiss" role="alert" style="padding:10px 12px;border:1px solid #f59e0b;background:rgba(245,158,11,0.12);border-radius:8px;color:#78350f;font-size:0.85rem;line-height:1.4;">
        <span class="material-icons" style="font-size:17px;vertical-align:-3px;margin-right:4px;color:#b45309;">warning</span>
        <strong>No truck found for this shift's day (${escapeHtml(_miss.label)}).</strong>
        No job day for this job was found on that date, so nothing was filled. Check the equipment before you submit — otherwise this ticket bills no truck.
      </div>`;
    }
    return '<div class="empty-state-small" style="padding:var(--spacing-sm) 0;">No equipment (auto-filled from dispatch)</div>';
  }

  return ClockState.clockOutEquipment.map((eq, idx) => {
    const hrs = calcRowHours(eq);
    // TASK-1396 — a coworker's restored line is READ-ONLY CONTEXT: no delete, no
    // editable times, no red "outside your shift" error (it isn't yours and it is
    // excluded from validation), clearly attributed. Mirrors jobboard.js TASK-1188.
    const isCrew = clockIsCrewEquipmentLine(eq);
    // TASK-9207 — DISPLAY ONLY, and the split is load-bearing: `isCrew` still
    // decides read-only, validation exclusion and the write payload (a closed
    // segment's billed line is not editable here whoever wrote it); `isOwnPrior`
    // only decides what the row SAYS about who wrote it. Never gate anything on it.
    const isOwnPrior = clockIsOwnEarlierShiftLine(eq);
    // OOB highlight parity with downtime/standby — equipment times auto-fill from
    // dispatch and can fall outside the shift after a clock adjustment (TASK-807).
    const startOOB = !isCrew && eq.startTime ? !isTimeWithinShift(eq.startTime, eq, 'startTime') : false;
    const endOOB = !isCrew && eq.endTime ? !isTimeWithinShift(eq.endTime, eq, 'endTime') : false;
    const endBeforeStart = !isCrew && (eq.startTime && eq.endTime) ? _tltRowPeek(eq, 'endTime') <= _tltRowPeek(eq, 'startTime') : false;
    const hasError = startOOB || endOOB || endBeforeStart;
    const errBorder = 'border:2px solid var(--danger);border-radius:4px;';
    const roStyle = isCrew ? 'padding:6px 4px;background:var(--bg-secondary,#f1f5f9);' : 'padding:6px 4px;';
    return `
      <div class="equipment-row" data-eq-idx="${idx}" style="display:flex;gap:var(--spacing-sm);align-items:center;padding:var(--spacing-sm) 0;border-bottom:1px solid var(--border-light);flex-wrap:wrap;${isCrew ? 'border-left:3px solid var(--border-medium,#94a3b8);padding-left:var(--spacing-sm);' : ''}">
        <div style="flex:1;min-width:120px;">
          <strong>${escapeHtml(eq.assetName)}</strong>
          ${eq.fleetNumber ? `<span style="color:var(--text-secondary);margin-left:4px;">#${escapeHtml(eq.fleetNumber)}</span>` : ''}
          ${isCrew ? `<div style="font-size:0.7rem;color:var(--text-secondary);margin-top:2px;display:flex;align-items:center;gap:3px;"><span class="material-icons" style="font-size:12px;">${isOwnPrior ? 'history' : 'groups'}</span>${escapeHtml(clockLineAttributionLabel(eq))}</div>` : ''}
        </div>
        ${isCrew
          ? renderSplitDateTime(`eq-${idx}-start`, eq.startTime, '', { style: roStyle, readonly: true })
          : renderSplitDateTime(`eq-${idx}-start`, eq.startTime, `window.ClockModule.updateEquipTime(${idx}, 'start', window.ClockModule.getSplitDateTimeValue('eq-${idx}-start'))`, { style: `padding:6px 4px;${startOOB ? errBorder : ''}` })}
        ${isCrew
          ? renderSplitDateTime(`eq-${idx}-end`, eq.endTime, '', { style: roStyle, readonly: true })
          : renderSplitDateTime(`eq-${idx}-end`, eq.endTime, `window.ClockModule.updateEquipTime(${idx}, 'end', window.ClockModule.getSplitDateTimeValue('eq-${idx}-end'))`, { style: `padding:6px 4px;${endOOB || endBeforeStart ? errBorder : ''}` })}
        <span style="font-weight:600;min-width:60px;text-align:right;${hasError ? 'color:var(--danger);' : ''}">${escapeHtml(_rowHoursText(eq))}</span>
        ${isCrew
          ? `<span title="${isOwnPrior ? 'From an earlier shift on this ticket — already closed out, read only' : 'Added by another crew member — read only'}" style="display:inline-flex;align-items:center;gap:3px;font-size:0.68rem;font-weight:600;color:var(--text-secondary);background:var(--bg-secondary,#e2e8f0);padding:3px 8px;border-radius:8px;"><span class="material-icons" style="font-size:13px;">lock</span>${isOwnPrior ? 'Earlier' : 'Crew'}</span>`
          : `${clockRenderFollowsClockChip(eq, idx)}
        <button type="button" class="btn-icon" onclick="window.ClockModule.removeEquipment(${idx})" title="Remove">
          <span class="material-icons">close</span>
        </button>`}
      </div>
      ${hasError ? `<div style="display:flex;align-items:center;gap:var(--spacing-sm);flex-wrap:wrap;padding:0 0 6px;">
        <span style="color:var(--danger);font-size:0.75rem;">${startOOB || endOOB ? 'Times must be within clock in/out range' : 'End time must be after start time'}</span>
        ${(startOOB || endOOB) ? `<button type="button" class="btn btn-secondary btn-sm" onclick="window.ClockModule.fitEntryToShift('equipment', ${idx})" style="font-size:0.72rem;padding:2px 8px;"><span class="material-icons" style="font-size:13px;">straighten</span> Fit to shift</button>` : ''}
      </div>` : ''}
    `;
  }).join('');
}

function showEquipmentPicker() {
  // TASK-1396 — only YOUR rows block re-adding an asset. A coworker's read-only
  // crew row must not lock you out of logging your own window on the same truck
  // (two operators, one machine): you add your own line, and the TASK-442 per-asset
  // interval-union dedup merges the windows without double-billing.
  const usedIds = new Set(
    ClockState.clockOutEquipment.filter(e => !clockIsCrewEquipmentLine(e)).map(e => e.assetId)
  );
  const trucks = ClockState.trucks.filter(t => t.active !== false && !usedIds.has(t.id));
  const trailers = ClockState.trailers.filter(t => t.active !== false && !usedIds.has(t.id));
  const assets = ClockState.assets.filter(a => a.active !== false && !usedIds.has(a.id));

  if (trucks.length === 0 && trailers.length === 0 && assets.length === 0) {
    window.showToast('All equipment already added', 'info');
    return;
  }

  const pickerEl = document.getElementById('equipment-picker');
  if (!pickerEl) return;

  let options = '<option value="">Select equipment...</option>';
  if (trucks.length > 0) {
    options += '<optgroup label="Vehicles">';
    trucks.forEach(t => options += `<option value="truck:${t.id}">${escapeHtml(t.name || t.unit)}${t.unit ? ' #' + escapeHtml(t.unit) : ''}</option>`);
    options += '</optgroup>';
  }
  if (trailers.length > 0) {
    options += '<optgroup label="Trailers">';
    trailers.forEach(t => options += `<option value="trailer:${t.id}">${escapeHtml(t.name || t.unit)}${t.unit ? ' #' + escapeHtml(t.unit) : ''}</option>`);
    options += '</optgroup>';
  }
  if (assets.length > 0) {
    options += '<optgroup label="Assets">';
    assets.forEach(a => options += `<option value="asset:${a.id}">${escapeHtml(a.name)}${a.partNumber ? ' #' + escapeHtml(a.partNumber) : ''}</option>`);
    options += '</optgroup>';
  }

  pickerEl.innerHTML = `
    <select class="form-control" onchange="window.ClockModule.selectEquipmentFromPicker(this.value)" style="width:100%;">
      ${options}
    </select>
  `;
  pickerEl.classList.remove('hidden');
}

function selectEquipmentFromPicker(value) {
  if (!value) return;
  const [type, id] = value.split(':');
  const entry = ClockState.clockOutEntry;
  if (!entry) return;
  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);

  let item = null;
  let assetName = 'Equipment';
  let fleetNumber = '';
  let rate = FALLBACK_RATES.asset;

  if (type === 'truck') {
    item = ClockState.trucks.find(t => t.id === id);
    assetName = (item && window.getUnitDisplay) ? window.getUnitDisplay(item) : (item?.name || item?.unit || 'Truck');
    fleetNumber = item?.unit || '';
    rate = FALLBACK_RATES.truck;
  } else if (type === 'trailer') {
    item = ClockState.trailers.find(t => t.id === id);
    assetName = (item && window.getUnitDisplay) ? window.getUnitDisplay(item) : (item?.name || item?.unit || 'Trailer');
    fleetNumber = item?.unit || '';
    rate = FALLBACK_RATES.trailer;
  } else if (type === 'asset') {
    item = ClockState.assets.find(a => a.id === id);
    assetName = item?.name || 'Asset';
    fleetNumber = item?.partNumber || '';
    rate = FALLBACK_RATES.asset;
  }

  if (!item) return;

  // Use adjusted clock times if available, otherwise original
  const adjInVal = getSplitDateTimeValue('adj-clock-in');
  const adjOutVal = getSplitDateTimeValue('adj-clock-out');
  const equipStart = adjInVal || toLocalDatetimeStr(clockIn);
  // 🔴 TASK-4614 — THIS IS THE LINE THAT COST $235 ON TITS #100545. With no
  // adjusted clock-out typed, `new Date()` here is the moment the driver PICKED
  // the unit, not the moment the shift ends. It stays as the seed value (the
  // driver may be closing out right now) but the row is marked `_followsClock` so
  // the submit-time sync moves it onto the real clock-out — unless the driver
  // types a window or taps the chip off, which is what an early drop-off does.
  const equipEnd = adjOutVal || toLocalDatetimeStr(new Date());

  ClockState.clockOutEquipment.push({
    assetId: item.id,
    assetName,
    assetType: type,
    fleetNumber,
    vehicleType: item.type || item.category || '',
    rate,
    startTime: equipStart,
    endTime: equipEnd,
    _followsClock: true
  });

  // Hide picker
  const pickerEl = document.getElementById('equipment-picker');
  if (pickerEl) {
    pickerEl.innerHTML = '';
    pickerEl.classList.add('hidden');
  }
  refreshEquipmentList();
}

function removeEquipment(idx) {
  const eq = ClockState.clockOutEquipment[idx];
  // TASK-1396 — a coworker's line can't be deleted from your close-out (jobboard
  // jbRemoveEquip parity). The button isn't rendered for crew rows; guard anyway
  // because window.ClockModule.removeEquipment is reachable from DevTools.
  if (!eq || clockIsCrewEquipmentLine(eq)) return;
  // TASK-1396 — track the delete so it actually persists. Before this, removing a
  // saved row only spliced client state: loadSavedEquipment re-loaded it on the next
  // open, so a bad row was un-removable (the self-perpetuating half of the trap).
  // Mirrors the downtime/standby _deleted*Ids pattern.
  //
  // COD Code-Reviewer (binding): EXCEPT a TASK-442 merge keeper. That line carries
  // your clockEntryId but its hours are the interval-union across every operator who
  // ran the machine — deleting it silently deletes their billable time. Drop it from
  // your view + your write payload, leave the document for a biller.
  if (eq._lineId && !eq._mergedKeeper) {
    if (!ClockState._deletedEquipmentIds) ClockState._deletedEquipmentIds = [];
    ClockState._deletedEquipmentIds.push(eq._lineId);
  } else if (eq._lineId && eq._mergedKeeper) {
    window.showToast?.(`${eq.assetName || 'That unit'} also carries other crew members' hours, so it stays on the ticket — dispatch will adjust it in review.`, 'info');
  }
  ClockState.clockOutEquipment.splice(idx, 1);
  refreshEquipmentList();
}

function updateEquipTime(idx, field, value) {
  // TASK-1396 — crew lines are read-only (jbUpdateEquipTime parity).
  if (clockIsCrewEquipmentLine(ClockState.clockOutEquipment[idx])) return;
  if (!isTimeWithinShift(value)) {
    window.showToast?.('Equipment times must be within clock in/out range', 'error');
    refreshEquipmentList();
    return;
  }
  if (field === 'start') ClockState.clockOutEquipment[idx].startTime = value;
  if (field === 'end') ClockState.clockOutEquipment[idx].endTime = value;
  const eq = ClockState.clockOutEquipment[idx];
  // TASK-4614 — the driver just took manual control of this window, so it must
  // never be clobbered by the submit-time re-sync (clockSyncClockTrackedEquipment).
  // jbUpdateEquipTime parity. Deliberately AFTER the isTimeWithinShift early-return
  // above: a REJECTED edit leaves the row's old window in place, so it must leave
  // the row's old flag in place too. Only accepted edits detach.
  const _wasFollowing = eq._followsClock === true;
  eq._followsClock = false;
  // Re-render (not the inline hours patch) when the chip has to change state, so
  // the driver sees the row stop tracking the clock the instant they type a time.
  if (_wasFollowing) {
    window.showToast?.(`${eq.assetName || 'That unit'} will bill the window you typed — it won't move when you clock out.`, 'info');
    refreshEquipmentList();
    return;
  }
  // Update hours display inline without re-rendering (avoids "sticky" input issue)
  const hrs = calcRowHours(eq);
  const row = document.querySelectorAll('.equipment-row')[idx];
  if (row) {
    const hoursSpan = row.querySelector('span[style*="font-weight:600"]');
    if (hoursSpan) hoursSpan.textContent = _rowHoursText(eq);
  }
}

function refreshEquipmentList() {
  const el = document.getElementById('equipment-list');
  if (el) el.innerHTML = renderEquipmentList();
}

function toggleMileage() {
  const checked = document.getElementById('mileage-toggle')?.checked;
  document.getElementById('mileage-fields')?.classList.toggle('hidden', !checked);
}

function updateMileageTotal() {
  const start = parseFloat(document.getElementById('mileage-start')?.value) || 0;
  const stop = parseFloat(document.getElementById('mileage-stop')?.value) || 0;
  const totalEl = document.getElementById('mileage-total');
  if (!totalEl) return;
  if (start > 0 && stop > 0 && stop >= start) {
    totalEl.textContent = `${(stop - start).toLocaleString()} mi`;
  } else {
    totalEl.textContent = '';
  }
}

function addDowntimeRow() {
  const entry = ClockState.clockOutEntry;
  if (!entry) return;
  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);

  ClockState.clockOutDowntime.push({
    startTime: toLocalDatetimeStr(clockIn),
    endTime: toLocalDatetimeStr(new Date()),
    notes: '',
    _lineId: null,
    _saved: false,
    _dirty: false
  });
  refreshTimeEntryList('downtime');
}

function addStandbyRow() {
  const entry = ClockState.clockOutEntry;
  if (!entry) return;
  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);

  ClockState.clockOutStandby.push({
    startTime: toLocalDatetimeStr(clockIn),
    endTime: toLocalDatetimeStr(new Date()),
    notes: '',
    _lineId: null,
    _saved: false,
    _dirty: false
  });
  refreshTimeEntryList('standby');
}

function removeTimeEntry(type, idx) {
  const list = type === 'downtime' ? ClockState.clockOutDowntime : ClockState.clockOutStandby;
  const entry = list[idx];
  if (entry?._lineId) {
    const key = type === 'downtime' ? '_deletedDowntimeIds' : '_deletedStandbyIds';
    if (!ClockState[key]) ClockState[key] = [];
    ClockState[key].push(entry._lineId);
  }
  list.splice(idx, 1);
  refreshTimeEntryList(type);
}

function updateTimeEntryField(type, idx, field, value) {
  const list = type === 'downtime' ? ClockState.clockOutDowntime : ClockState.clockOutStandby;
  if ((field === 'startTime' || field === 'endTime') && !isTimeWithinShift(value)) {
    window.showToast?.(`${type === 'downtime' ? 'Downtime' : 'Standby'} times must be within clock in/out range`, 'error');
    refreshTimeEntryList(type);
    return;
  }
  list[idx][field] = value;
  list[idx]._dirty = true;
  refreshTimeEntryList(type);
}

function refreshTimeEntryList(type) {
  const list = type === 'downtime' ? ClockState.clockOutDowntime : ClockState.clockOutStandby;
  const el = document.getElementById(`${type}-list`);
  if (!el) return;

  if (list.length === 0) {
    el.innerHTML = `<div class="empty-state-small" style="padding:var(--spacing-sm) 0;">No ${type} entries</div>`;
    return;
  }

  el.innerHTML = list.map((item, idx) => {
    const hrs = (item.startTime && item.endTime) ? calcRowHours(item) : 0;
    const startOOB = item.startTime ? !isTimeWithinShift(item.startTime, item, 'startTime') : false;
    const endOOB = item.endTime ? !isTimeWithinShift(item.endTime, item, 'endTime') : false;
    const endBeforeStart = (item.startTime && item.endTime) ? _tltRowPeek(item, 'endTime') <= _tltRowPeek(item, 'startTime') : false;
    const hasError = startOOB || endOOB || endBeforeStart;
    const errBorder = 'border:2px solid var(--danger);border-radius:4px;';
    const savedBadge = item._saved
      ? (item._dirty
        ? '<span style="font-size:0.7rem;color:var(--action-warning);display:flex;align-items:center;gap:2px;"><span class="material-icons" style="font-size:12px;">edit</span>Modified</span>'
        : '<span class="ss-text" style="font-size:0.7rem;display:flex;align-items:center;gap:2px;"><span class="material-icons" style="font-size:12px;">check_circle</span>Saved</span>')
      : '';
    return `
      <div style="display:flex;gap:var(--spacing-sm);align-items:center;padding:var(--spacing-sm) 0;border-bottom:1px solid var(--border-light);flex-wrap:wrap;">
        ${renderSplitDateTime(`te-${type}-${idx}-start`, item.startTime, `window.ClockModule.updateTimeEntryField('${type}', ${idx}, 'startTime', window.ClockModule.getSplitDateTimeValue('te-${type}-${idx}-start'))`, { style: `padding:6px 4px;${startOOB ? errBorder : ''}` })}
        ${renderSplitDateTime(`te-${type}-${idx}-end`, item.endTime || '', `window.ClockModule.updateTimeEntryField('${type}', ${idx}, 'endTime', window.ClockModule.getSplitDateTimeValue('te-${type}-${idx}-end'))`, { style: `padding:6px 4px;${endOOB || endBeforeStart ? errBorder : ''}` })}
        <span style="font-weight:600;min-width:50px;text-align:right;${hasError ? 'color:var(--danger);' : ''}">${(item.startTime && item.endTime) ? escapeHtml(_rowHoursText(item)) : '0.00h'}</span>
        <input type="text" value="${escapeHtml(item.notes)}" onchange="window.ClockModule.updateTimeEntryField('${type}', ${idx}, 'notes', this.value)" placeholder="Notes..." style="flex:1;min-width:100px;font-size:0.85rem;padding:4px;">
        ${savedBadge}
        <button type="button" class="btn-icon" onclick="window.ClockModule.removeTimeEntry('${type}', ${idx})" title="Remove">
          <span class="material-icons">close</span>
        </button>
      </div>
      ${hasError ? `<div style="display:flex;align-items:center;gap:var(--spacing-sm);flex-wrap:wrap;padding:0 0 4px;">
        <span style="color:var(--danger);font-size:0.75rem;">${startOOB || endOOB ? 'Times must be within clock in/out range' : 'End time must be after start time'}</span>
        ${(startOOB || endOOB) ? `<button type="button" class="btn btn-secondary btn-sm" onclick="window.ClockModule.fitEntryToShift('${type}', ${idx})" style="font-size:0.72rem;padding:2px 8px;"><span class="material-icons" style="font-size:13px;">straighten</span> Fit to shift</button>` : ''}
      </div>` : ''}
    `;
  }).join('');
}

// TASK-807 — one-tap "Fit to shift" for an out-of-range equipment/downtime/standby
// entry. Auto-filled-from-dispatch times can fall outside the shift after the driver
// shortens their clock; instead of a dead-end toast, clamp each endpoint independently
// into [bounds.min, bounds.max]. User-tapped, never silent — shows the before→after
// billable-hours change so the user sees the impact. Equipment hours are billable.
function fitEntryToShift(type, idx) {
  const bounds = getShiftBounds();
  if (!bounds) { window.showToast?.('Set clock in/out times first', 'error'); return; }
  const minMs = bounds.min.getTime();
  const maxMs = bounds.max.getTime();

  let item, label, name;
  if (type === 'equipment') {
    item = ClockState.clockOutEquipment[idx];
    label = 'Equipment';
    name = item?.assetName || 'Equipment';
  } else {
    const list = type === 'downtime' ? ClockState.clockOutDowntime : ClockState.clockOutStandby;
    item = list[idx];
    label = type === 'downtime' ? 'Downtime' : 'Standby';
    name = label;
  }
  if (!item) return;

  const beforeHrs = (item.startTime && item.endTime) ? calcRowHours(item) : 0;

  // Clamp BOTH endpoints into the range (not the naive start=max/end=min, which
  // breaks when end<min or start>max — COD Code-Reviewer fold).
  const clamp = (ms) => Math.min(maxMs, Math.max(minMs, ms));
  // clock-dst-all — strict company-zone reads: a fit computed from an unpicked
  // repeated-hour time would bake a guess into the row.
  const _fIn = getSplitDateTimeValue('adj-clock-in');
  const _fOut = getSplitDateTimeValue('adj-clock-out');
  const _fUnres = _clockRowUnresolved(item) || [[_fIn, 'adj-clock-in'], [_fOut, 'adj-clock-out']]
    .some(([v, k]) => v && !Number.isFinite(_tltMs(v, k)));
  if (_fUnres || !Number.isFinite(minMs) || !Number.isFinite(maxMs)) {
    window.showToast?.("One of these times happened twice (clocks fell back) or can't be read. Set it by hand — you'll pick which one when you save.", 'error');
    return;
  }
  let newStartMs = item.startTime ? clamp(_tltRowMs(item, 'startTime')) : minMs;
  let newEndMs = item.endTime ? clamp(_tltRowMs(item, 'endTime')) : maxMs;

  // If clamping collapsed the span (end<=start), flag the row instead of saving a
  // zero/negative span. Leave the original times so the red highlight stays.
  if (newEndMs <= newStartMs) {
    window.showToast?.(`${name} can't fit the shift — its window doesn't overlap your clock in/out. Adjust it manually or remove it.`, 'error');
    return;
  }

  // clock-dst-all — set from known instants, remembered on the row itself.
  if (_tlt()) { _tlt().stampRow(item, 'startTime', newStartMs); _tlt().stampRow(item, 'endTime', newEndMs); }
  else { item.startTime = toLocalDatetimeStr(new Date(newStartMs)); item.endTime = toLocalDatetimeStr(new Date(newEndMs)); }
  if (type !== 'equipment') item._dirty = true;
  // TASK-4614 — `_followsClock` is left EXACTLY as it was, on purpose. Fit-to-shift
  // clamps into [bounds.min, bounds.max], which is the same window
  // clockSyncClockTrackedEquipment writes, so on a still-tracking row the two agree
  // and the later sync is a no-op. On a row the driver already detached, leaving the
  // flag false is what keeps their typed window theirs — a one-tap range fix must
  // not silently re-attach the row to the clock. (This is the "confirm
  // fitEntryToShift is covered by whatever flag lands" item from the ticket:
  // covered by doing nothing, deliberately.)

  const afterHrs = calcRowHours(item);

  // Billing transparency — show before→after so a shrunk billable window is visible.
  if (Math.abs(afterHrs - beforeHrs) > 0.001) {
    window.showToast?.(`${name} adjusted ${beforeHrs.toFixed(2)}h → ${afterHrs.toFixed(2)}h to fit shift`, 'success');
  } else {
    window.showToast?.(`${name} fit to shift`, 'success');
  }

  if (type === 'equipment') refreshEquipmentList();
  else refreshTimeEntryList(type);
}

function recalcHours() {
  const adjIn = getSplitDateTimeValue('adj-clock-in');
  const adjOut = getSplitDateTimeValue('adj-clock-out');
  if (adjIn && adjOut) {
    const hrs = calcHoursBetween(adjIn, adjOut);
    const el = document.getElementById('adj-hours');
    // clock-dst-all — what's shown = what's saved: a repeated-hour time shows
    // both possible totals until the person picks on save.
    if (el) {
      const _T = _tlt();
      const _hb = _T ? _T.hoursBetween(adjIn, adjOut, { startKey: 'adj-clock-in', endKey: 'adj-clock-out' }) : null;
      if (_hb && !_hb.ok && _hb.ambiguous && _hb.options.length) {
        el.textContent = _hb.options.map(o => o.hours.toFixed(2)).join(' or ');
        el.title = _T.ambiguousPreviewText(_hb);
      } else if (_hb && !_hb.ok) {
        el.textContent = '—';
        el.title = _hb.error || '';
      } else {
        el.textContent = hrs.toFixed(2);
        el.title = '';
      }
    }
    // TASK-807 — the shift bounds just changed, so equipment/downtime/standby OOB
    // highlights must re-evaluate (a clock adjustment is what pushes them out of range).
    // ONLY on a manual clock adjustment (timer paused) — NOT the live 30s tick, which
    // would clobber an in-progress notes input mid-typing. Guarded by element presence
    // inside each refresh fn, so harmless if not rendered.
    if (ClockState.clockOutTimerPaused) {
      // TASK-4614 — the driver just moved their clock-out by hand, so clock-tracking
      // equipment follows it NOW rather than only at submit. Same move
      // jbApplyTimeEdit makes, and it turns "Ends with shift" into something the
      // driver watches happen instead of a promise. Only rows still tracking move.
      // clock-dst-all — only from times that resolve (company zone, no unpicked
      // repeated hour): the submit-time sync runs from the resolved final pair.
      const _sIn = _tltDate(adjIn, 'adj-clock-in');
      const _sOut = _tltDate(adjOut, 'adj-clock-out');
      if (!isNaN(_sIn.getTime()) && !isNaN(_sOut.getTime())) clockSyncClockTrackedEquipment(_sIn, _sOut);
      refreshEquipmentList();
      refreshTimeEntryList('downtime');
      refreshTimeEntryList('standby');
    }
    // Run async time validation (modal dialogs)
    validateTimeChanges(adjIn, adjOut, hrs);
  }
}

// Time validation — modal dialogs for suspicious time changes
async function validateTimeChanges(adjInStr, adjOutStr, hours) {
  if (!ClockState.clockOutEntry) return;
  if (ClockState._validating) return; // prevent re-entry while dialog is open
  if (!ClockState._timeValidationFlags) ClockState._timeValidationFlags = {};
  const flags = ClockState._timeValidationFlags;

  // clock-dst-all — company zone, strict: an unpicked repeated-hour time (or an
  // unreadable zone / nonexistent time) is not judged here — the save path asks
  // which one / refuses with the reason before anything is written.
  const adjIn = _tltDate(adjInStr, 'adj-clock-in');
  const adjOut = _tltDate(adjOutStr, 'adj-clock-out');
  if (isNaN(adjIn.getTime()) || isNaN(adjOut.getTime())) return;
  const now = new Date();
  const entry = ClockState.clockOutEntry;
  const origClockIn = entry._originalClockIn || (entry.clockIn?.toDate?.() || new Date(entry.clockIn));
  // Store original clock-in on first access so we always compare against the real original
  if (!entry._originalClockIn) entry._originalClockIn = origClockIn;

  ClockState._validating = true;
  try {
    // 1. Negative hours (hard block — end before start)
    if (adjOut <= adjIn) {
      await window.showConfirm?.('Invalid Times', 'Clock-out is before clock-in. Please fix the times.', {
        confirmText: 'OK', cancelText: 'Reset'
      });
      resetTimes();
      return;
    }

    // 2. Clock-out is in the future (> 1 min ahead)
    const futureKey = adjOutStr;
    if (!flags['future_' + futureKey] && (adjOut - now) > 60000) {
      const ok = await window.showConfirm?.('Future Clock-Out', 'Clock-out time is in the future. Are you sure?', {
        confirmText: 'Yes, keep it', cancelText: 'Reset'
      });
      if (ok) {
        flags['future_' + futureKey] = true;
      } else {
        resetTimes();
        return;
      }
    }

    // 3. Clock-in date changed to a different day
    const _T = _tlt();
    const origDay = _T ? _T.dateKey(origClockIn) : origClockIn.toDateString();
    const adjInDay = _T ? _T.dateKey(adjIn) : adjIn.toDateString();
    const dayKey = adjInStr;
    if (!flags['daychange_' + dayKey] && origDay !== adjInDay) {
      const ok = await window.showConfirm?.('Date Changed', 'Clock-in date was changed to a different day. Are you sure?', {
        confirmText: 'Yes, keep it', cancelText: 'Reset'
      });
      if (ok) {
        flags['daychange_' + dayKey] = true;
      } else {
        resetTimes();
        return;
      }
    }

    // 4. Shift > 24 hours
    const hoursKey = Math.floor(hours);
    if (!flags['long_' + hoursKey] && hours > 24) {
      const ok = await window.showConfirm?.('Long Shift', 'Shift is over 24 hours. Are you sure?', {
        confirmText: 'Yes, keep it', cancelText: 'Reset'
      });
      if (ok) {
        flags['long_' + hoursKey] = true;
      } else {
        resetTimes();
        return;
      }
    }
  } finally {
    ClockState._validating = false;
  }
}

function resetTimes() {
  const entry = ClockState.clockOutEntry;
  if (!entry) return;
  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
  const now = new Date();

  setSplitDateTimeValue('adj-clock-in', toLocalDatetimeStr(clockIn, 'adj-clock-in'));
  setSplitDateTimeValue('adj-clock-out', toLocalDatetimeStr(now, 'adj-clock-out'));
  recalcHours();

  // Resume live timer
  ClockState.clockOutTimerPaused = false;
  ClockState.clockOutTypedByHand = false; // TASK-4616
}

// ============================================
// LIVE CLOCK-OUT TIMER (founder only)
// ============================================
function startClockOutTimer() {
  stopClockOutTimer();
  ClockState.clockOutTimerPaused = false;
  ClockState.clockOutTypedByHand = false; // TASK-4616
  ClockState.clockOutTimerInterval = setInterval(() => {
    if (ClockState.clockOutTimerPaused) return;
    const outEl = document.getElementById('adj-clock-out-date');
    if (!outEl) return;
    const now = new Date();
    setSplitDateTimeValue('adj-clock-out', toLocalDatetimeStr(now, 'adj-clock-out'));
    recalcHours();
    // Update summary duration
    const entry = ClockState.clockOutEntry;
    if (entry) {
      const adjIn = getSplitDateTimeValue('adj-clock-in');
      const clockIn = adjIn ? _tltPeek(adjIn, 'adj-clock-in') : (entry.clockIn?.toDate?.() || new Date(entry.clockIn));
      const ms = now - clockIn;
      const hours = (ms / 3600000).toFixed(2);
      const duration = formatDuration(ms);
      const summaryEl = document.querySelector('.clock-out-summary .summary-row:last-child span:last-child');
      if (summaryEl) summaryEl.innerHTML = `<strong>${hours} hrs</strong> (${duration})`;
    }
  }, 30000);
}

function stopClockOutTimer() {
  if (ClockState.clockOutTimerInterval) {
    clearInterval(ClockState.clockOutTimerInterval);
    ClockState.clockOutTimerInterval = null;
  }
}

function pauseClockOutTimer() {
  ClockState.clockOutTimerPaused = true;
}

// TASK-4616 — the Clock OUT field's own change handler. Pauses the live timer like
// any time edit, and records that the clock-out is now the person's typed choice
// (a Clock IN edit also pauses the timer but leaves the clock-out un-chosen).
function markClockOutTyped() {
  ClockState.clockOutTimerPaused = true;
  ClockState.clockOutTypedByHand = true;
}

// clock-dst-all — company zone. Strict when both ends resolve (every save path
// resolves first, so a saved number is the resolved one); an unresolved
// repeated-hour end falls back to the FIRST instant — preview only. Displays
// of a row's hours use _hoursText (shows both options, never one silently).
function calcHoursBetween(startStr, endStr) {
  const start = _tltPeek(startStr);
  const end = _tltPeek(endStr);
  const h = (end - start) / 3600000;
  return Number.isFinite(h) ? Math.max(0, h) : 0;
}

// "8.50h", or "7.50 or 8.50h" when a time happened twice and is not picked yet,
// or "—" when it can't be read (unreadable company zone / nonexistent time).
function _hoursText(startStr, endStr) {
  const T = _tlt();
  if (!T || !startStr || !endStr) return calcHoursBetween(startStr, endStr).toFixed(2) + 'h';
  const hb = T.hoursBetween(startStr, endStr);
  if (hb.ok) return Math.max(0, hb.hours).toFixed(2) + 'h';
  if (hb.ambiguous && hb.options.length) return hb.options.map(o => o.hours.toFixed(2)).join(' or ') + 'h';
  return '—';
}

// Returns { min: Date, max: Date } for valid time range (uses adjusted times if present)
function getShiftBounds() {
  const entry = ClockState.clockOutEntry;
  if (!entry) return null;
  const adjIn = getSplitDateTimeValue('adj-clock-in');
  const adjOut = getSplitDateTimeValue('adj-clock-out');
  // clock-dst-all — company zone (preview bounds; peek for an unpicked repeated hour).
  const min = adjIn ? _tltPeek(adjIn, 'adj-clock-in') : (entry.clockIn?.toDate?.() || new Date(entry.clockIn));
  const max = adjOut ? _tltPeek(adjOut, 'adj-clock-out') : new Date();
  return { min, max };
}

function isTimeWithinShift(timeStr, row, field) {
  const bounds = getShiftBounds();
  if (!bounds) return true;
  const t = row && field ? _tltRowPeek(row, field) : _tltPeek(timeStr);
  if (isNaN(t.getTime()) || isNaN(bounds.min.getTime()) || isNaN(bounds.max.getTime())) return true; // unreadable: the save refuses it with the reason
  return t >= bounds.min && t <= bounds.max;
}

// =============================================================================
// TASK-4614 — EQUIPMENT MUST BILL THE SAME WINDOW THE PUNCH DOES.
//
// (Filed on the board as a second "TASK-4584". That number was already burned by
// an unrelated FCRA adverse-action change, commit be247026 — this work is the
// clock-billing one from commit 65b21f3e, re-allocated to TASK-4614.)
//
// THE LEAK. Every equipment row this module creates is auto-stamped
// `endTime: toLocalDatetimeStr(new Date())` at the moment the ROW is created —
// when the driver opens the clock-out sheet or picks a unit — not when the
// clock-out is SUBMITTED. The EMPLOYEE punch is written at submit. Keep working
// after building the sheet and the two drift apart, silently, in the UNDER-bill
// direction. TITS ticket #100545 (Loenbro drill, 8/22): equipment ended 8:41 PM,
// the punch ran to 9:52 PM — the truck billed 6.5h instead of 7.5h, $235 gone on
// one ticket. Detection signal for the rest: EMPLOYEE hours > EQUIPMENT hours on
// a one-man/one-truck ticket (TITS bills shop-to-shop, so equipment normally
// runs >= employee).
//
// THIS IS A PORT, NOT AN INVENTION. jobboard.js is the parallel close-out UI and
// had the identical bug; TASK-572 fixed it there with a `_followsClock` flag and
// `_jbSyncClockTrackedLines()` called as "final authority" at submit. Same
// mechanism, this module. Keep the two in step — they are the two halves of one
// close-out.
//
// 🔴 WHY THIS IS NOT A BLANKET RE-STAMP (COD Domain, binding). Drop-and-hook is
// normal work: a trailer left in the customer's yard hours before the driver's
// shift ends. On such a row the auto-stamp can be right BY COINCIDENCE and the
// driver rightly never edits it. Re-stamping on mere absence-of-edit would
// silently OVER-bill the customer — the same error class as the leak, pointed the
// other way. So the re-stamp is governed by an explicit, VISIBLE, two-way per-row
// flag, never by "nobody touched it":
//   • `_followsClock === true`  → the row renders a tappable "Ends with shift"
//     chip and its window follows the FINAL clock window.
//   • `_followsClock === false` → the row renders a "Set window" chip; its times
//     are the driver's and are NEVER touched here.
//   • Any manual time edit (updateEquipTime) drops it to false — jobboard parity.
//   • A row RESTORED from a previous modal session (loadSavedEquipment) never
//     carries the flag: a persisted window is somebody's decision, so on that row
//     following the clock is opt-IN via the chip. Same rule jobboard.js uses.
// The chip IS the signal that did not exist before, and it is the early-drop-off
// affordance: one tap freezes an auto-filled row at a typed window.
//
// 🔴 WHY EVERY CALL SITE PASSES `finalClockOut`, NEVER `new Date()` (COD Code
// Reviewer, binding). `showClockOutReview()` freezes `finalClockOut` at BUILD
// TICKET time and `confirmClockOutFromReview()` writes THAT value as the employee
// punch. If equipment re-stamped to `new Date()` at Confirm it would bill past the
// punch and bill a number the driver never saw — MOVING the mismatch instead of
// closing it. So the sync always takes the same finalClockIn/finalClockOut pair
// the employee punch is written from, and on the review path it runs BEFORE the
// snapshot so the review screen shows the exact windows that will be billed.
// (That the review path freezes BOTH sides early is its own question — a long
// dwell on the review screen shrinks employee AND equipment together, which is
// consistent but arguably still short. Filed separately; not fixed here.)
// =============================================================================
function clockSyncClockTrackedEquipment(inDate, outDate, list) {
  const rows = list || ClockState.clockOutEquipment || [];
  if (!(inDate instanceof Date) || !(outDate instanceof Date)) return 0;
  if (isNaN(inDate.getTime()) || isNaN(outDate.getTime())) return 0;
  if (outDate <= inDate) return 0;
  const T = _tlt();
  const startDT = toLocalDatetimeStr(inDate);
  const endDT = toLocalDatetimeStr(outDate);
  let moved = 0;
  rows.forEach(eq => {
    if (!eq || eq._followsClock !== true) return;
    // TASK-1396 parity — a coworker's read-only row is never ours to move.
    if (clockIsCrewEquipmentLine(eq)) return;
    const same = eq.startTime === startDT && eq.endTime === endDT;
    // clock-dst-all — set from the KNOWN clock instants and remembered on the
    // row, so a repeated-hour clock window re-reads as exactly these instants.
    if (T) { T.stampRow(eq, 'startTime', inDate); T.stampRow(eq, 'endTime', outDate); }
    else { eq.startTime = startDT; eq.endTime = endDT; }
    if (!same) moved++;
  });
  return moved;
}

// TASK-4618 — THE OTHER HALF OF THE SYNC: push the final clock-out onto equipment
// ticket lines that are ALREADY WRITTEN.
//
// WHAT THE FIRST PASS MISSED (integrated-diff COD, Code Reviewer — binding, and a
// real hole). `clockSyncClockTrackedEquipment` only moves the IN-MEMORY rows. Every
// equipment WRITE in this module sits behind `if (!_appliedEquipment)`, and that
// flag is set true by `saveTicketFromForm` and by `applyReviewData`. So on the route
// a driver most likely takes — build the sheet, hit **Save**, keep working, then
// clock out — the in-memory sync ran and was then DISCARDED, because the whole
// write block was skipped. The already-written line kept its Save-time end while
// the employee punch got the submit-time end: the original bug, intact, on the
// commonest path. Worse than intact, in fact — `recalcHours` showed the driver the
// CORRECTED longer hours while the database kept the short ones, so the number was
// wrong AND the screen confidently said otherwise.
//
// SCOPE, deliberately as narrow as it goes:
//   • `_followsClock === true` only — a row the driver typed or toggled off never moves.
//   • Crew rows (`clockIsCrewEquipmentLine`) never move — not ours to touch (TASK-1396).
//   • Time-based units only — a per-unit line's quantity is manual (TASK-4576).
//   • EXTEND ONLY. Never shrink a shared/merged TASK-442 interval-union window; that
//     window can carry another operator's hours.
//   • TIMES ONLY, no notes. On this branch the note was already written by the Save
//     or the Apply that set the flag, so staying out of it leaves TASK-4559's merge
//     rule the single owner of `notes`.
//   • Session-scoped via `getExistingEquipmentMap(ticketId, entryId)` (TASK-427), so
//     a re-clock never reaches session 1's line.
//
// Returns the PATCH or null so the write-side below and the note-merging branch in
// `confirmClockOutFromReview` share ONE copy of the arithmetic instead of drifting.
async function clockFollowsClockEndPatch(tenant, ticketId, eq, existing) {
  if (!eq || eq._followsClock !== true || !existing) return null;
  if (clockIsCrewEquipmentLine(eq)) return null;
  if (!isTimeBasedUnit(eq.rateUnit)) return null;
  const newEnd = _tltRowDate(eq, 'endTime');
  if (isNaN(newEnd.getTime())) return null;
  const existingEnd = existing.toTime?.toDate?.() || new Date(existing.toTime);
  if (!(newEnd > existingEnd)) return null;            // EXTEND ONLY
  const snap = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', existing.docId));
  const from = snap.data()?.fromTime?.toDate?.() || _tltRowDate(eq, 'startTime');
  const rawHrs = (newEnd - from) / 3600000;
  const extHours = clockEquipmentHours(rawHrs, false, eq.roundToHalfHour); // TASK-4576 — one rule
  // 🔴 COD Adversary + Code Reviewer, INDEPENDENTLY, both binding: the STORED rate
  // only — never fall back to `eq.rate`. The three older writers do
  // `snap.data()?.rate || eq.rate`, and on an UNPRICED line (`rate: 0`, waiting on
  // the server pricing enrichment) that `||` reaches into the in-memory row, which
  // is editable from devtools. A clock-only member could set
  // `ClockState.clockOutEquipment[i].rate` and have this write a chosen
  // `lineAmount` while `rate` stayed 0 — the exact state firestore.rules pins as
  // impossible on the DRIVER arm (TASK-1165) but does not pin on the bare-member
  // UPDATE arm this path is evaluated by.
  //
  // Adversary's suggested boundary was to skip the whole patch when the stored rate
  // is falsy. That is worse: it throws away the HOURS correction — the entire point
  // of this ticket — on every unpriced line. So the window is still corrected and
  // only the MONEY defers: rate 0 gives lineAmount 0, which is what the rules
  // invariant demands and what lets the server enrichment price the line later from
  // the now-correct hours. The stale `|| eq.rate` in the older writers is a
  // pre-existing exposure on the same surface — filed with TASK-4615, not widened here.
  //
  // TASK-9646 S2 — and now the money is not computed here AT ALL: the patch
  // stamps `amountPending` and the SERVER re-derives lineAmount from the STORED
  // hours × STORED rate (onTicketLineWrite → ticketTotalsReconcile), which also
  // closes the devtools-rate exposure above for good.
  const patch = {
    toTime: Timestamp.fromDate(newEnd),
    hours: extHours,
    amountPending: true,
  };
  // TASK-4576 — persist the rounding DECISION alongside the evidence, or billing.js's
  // next recalc walks the hours straight back down.
  if (eq.roundToHalfHour) {
    patch.roundedFrom = parseFloat(rawHrs.toFixed(2));
    patch.roundToHalfHour = true;
  }
  return patch;
}

// The `_appliedEquipment === true` writer. Its own try/catch on purpose: this runs
// AFTER the clock entry is already marked completed, and a billing correction may
// never be the thing that turns a finished clock-out into a failure. "Never trap a
// driver" outranks this write — a miss here is a line a biller can still fix; a
// throw here is a person standing in a yard (TASK-428/489, TASK-1399).
async function clockPushFollowsClockEnds(tenant, ticketId, entryId, rows, where) {
  if (!ticketId || !Array.isArray(rows) || rows.length === 0) return 0;
  let moved = 0;
  try {
    const existingEqMap = await getExistingEquipmentMap(ticketId, entryId);
    for (const eq of rows) {
      if (!eq || eq._followsClock !== true) continue;
      const existing = existingEqMap.get(eq.assetId);
      const patch = await clockFollowsClockEndPatch(tenant, ticketId, eq, existing);
      if (!patch) continue;
      patch.updatedAt = Timestamp.now();
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', existing.docId), patch);
      moved++;
    }
    if (moved > 0) console.log('[Clock] TASK-4618 (' + where + '): extended ' + moved + ' clock-tracking equipment line(s) to the final clock-out');
  } catch (err) {
    console.error('[Clock] TASK-4618 follows-clock end push failed (' + where + '):', err && (err.code || err.message));
    window.logError && window.logError(err, { module: 'clock', operation: 'FOLLOWS_CLOCK_END_PUSH', ticketId, where });
  }
  return moved;
}

// TASK-4614 — the VISIBLE half of `_followsClock`. Rendered on every editable
// equipment row so "this unit's window will move to my clock-out" is something
// the driver can SEE and reverse in one tap, instead of a silent default. Both
// states are rendered; there is no unlabelled row.
function clockRenderFollowsClockChip(eq, idx) {
  const on = eq._followsClock === true;
  // COD UX (binding): "Set window" was jargon. A driver reads these two states as
  // "same as my shift" vs "the time I typed", so say that.
  const label = on ? 'Ends with shift' : 'Custom time';
  const icon = on ? 'link' : 'link_off';
  const title = on
    ? 'This unit’s window follows your clock in/out. Tap if it was dropped or shut down early — its times will then stay exactly as typed.'
    : 'This unit’s window stays exactly as typed. Tap if it ran your whole shift and should follow your clock-out.';
  const style = on
    ? 'color:#166534;background:#dcfce7;border:1px solid #86efac;'
    : 'color:var(--text-secondary);background:var(--bg-secondary,#e2e8f0);border:1px solid var(--border-medium,#cbd5e1);';
  // escAttr, not escapeHtml — escapeHtml skips quotes and this is an attribute.
  const safeTitle = window.escAttr ? window.escAttr(title) : escapeHtml(title);
  // min-height 44px + 10px padding: this is a glove-on-a-tablet target, and it sits
  // right next to the Remove ✕ — an undersized chip here mis-taps into a delete.
  return `<button type="button" data-eq-follows="${on ? '1' : '0'}" onclick="window.ClockModule.toggleEquipFollowsClock(${idx})" title="${safeTitle}"
      style="display:inline-flex;align-items:center;justify-content:center;gap:4px;font-size:0.72rem;font-weight:600;padding:10px 12px;min-height:44px;border-radius:10px;cursor:pointer;white-space:nowrap;${style}">
      <span class="material-icons" style="font-size:15px;">${icon}</span>${label}</button>`;
}

// TASK-4614 — the driver's explicit call, both directions. Turning it ON snaps the
// row to the CURRENT clock window immediately so the hours shown are the hours the
// choice actually bills — a promise the driver can't see is how the original bug
// stayed invisible for months.
function toggleEquipFollowsClock(idx) {
  const eq = ClockState.clockOutEquipment[idx];
  if (!eq || clockIsCrewEquipmentLine(eq)) return;
  if (eq._followsClock === true) {
    eq._followsClock = false;
    window.showToast?.(`${eq.assetName || 'That unit'} will bill the window shown — it won't move when you clock out.`, 'info');
  } else {
    eq._followsClock = true;
    const bounds = getShiftBounds();
    if (bounds && bounds.max > bounds.min) {
      // clock-dst-all — copy the typed wall text when there is one (a format of a
      // previewed repeated-hour instant would bake in a guess); the save asks.
      eq.startTime = getSplitDateTimeValue('adj-clock-in') || toLocalDatetimeStr(bounds.min);
      eq.endTime = getSplitDateTimeValue('adj-clock-out') || toLocalDatetimeStr(bounds.max);
    }
    window.showToast?.(`${eq.assetName || 'That unit'} will follow your clock in/out — its end time moves with your clock-out.`, 'info');
  }
  refreshEquipmentList();
}

// =============================================================================
// TASK-1396 / TASK-1399 — SHIFT-BOUNDS PROBLEMS ARE A WARNING, NEVER A CAGE.
//
// History: confirmClockOut() and showClockOutReview() both `return`ed on the FIRST
// out-of-bounds downtime/standby/equipment row — BEFORE the CLOCK_OUT_ATTEMPT audit
// and before any Firestore write. Two real people were trapped on the clock by that
// return: Steven Alandt (2026-08-06, a coworker's truck row on a shared ticket, which
// he could neither edit into range nor delete) and Ethan Young (2026-08-06, real truck
// windows outside his re-clock window with allowDriverTimeEdit=false, so he could not
// widen the shift — he stayed clocked in overnight, 13.39h).
//
// The rule now (TASK-489 pattern, made law after TASK-428 stranded PIN crews 14–15h):
// a ticket-line problem may cost you a confirm dialog. It may never cost you your
// clock-out. Rows are written AS TYPED — never silently truncated ("flag, never
// truncate"); the server onTicketLineWrite trigger stamps _billingReviewFlag
// (equipment_outside_clock_window) so a biller reconciles the real hours.
//
// Crew rows (clockIsCrewEquipmentLine) are excluded entirely — a foreign row must
// never gate your own clock-out, and it isn't yours to fix.
// =============================================================================
function collectShiftBoundsProblems(finalClockIn, finalClockOut) {
  const all = [
    ...ClockState.clockOutDowntime.map((e, i) => ({ item: e, label: 'Downtime', type: 'downtime', idx: i })),
    ...ClockState.clockOutStandby.map((e, i) => ({ item: e, label: 'Standby', type: 'standby', idx: i })),
    ...ClockState.clockOutEquipment.map((e, i) => ({ item: e, label: 'Equipment', type: 'equipment', idx: i }))
  ];
  const problems = [];
  for (const row of all) {
    const te = row.item;
    if (row.type === 'equipment' && clockIsCrewEquipmentLine(te)) continue;
    if (!te.endTime || !te.startTime) continue; // partial entry (start only) — nothing to compare
    const teStart = _tltRowDate(te, 'startTime');
    const teEnd = _tltRowDate(te, 'endTime');
    if (!Number.isFinite(teStart.getTime()) || !Number.isFinite(teEnd.getTime())) continue;
    const name = te.assetName ? `${row.label} "${te.assetName}"` : row.label;
    if (teEnd < teStart) {
      problems.push({ ...row, name, kind: 'reversed' });
    } else if (teStart < finalClockIn || teStart > finalClockOut || teEnd < finalClockIn || teEnd > finalClockOut) {
      problems.push({ ...row, name, kind: 'oob' });
    }
  }
  return problems;
}

// Scroll + focus the first offending row (TASK-1399 item 5 — the toast could name a
// row that was off-screen, so "Bob — #10 is outside your shift" pointed at nothing).
function revealShiftBoundsProblem(problem) {
  if (!problem) return;
  try {
    const sel = problem.type === 'equipment'
      ? `#equipment-list .equipment-row[data-eq-idx="${problem.idx}"]`
      : `#te-${problem.type}-${problem.idx}-start-date`;
    const el = document.querySelector(sel);
    const row = el?.closest?.('.equipment-row') || el;
    row?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    if (problem.type === 'equipment') {
      document.getElementById(`eq-${problem.idx}-start-date`)?.focus?.();
    } else {
      el?.focus?.();
    }
  } catch (_) { /* purely cosmetic — never let it affect the clock-out */ }
}

// Returns true = proceed with the clock-out (write the rows as typed),
// false = the user chose to go back and fix the times.
async function confirmShiftBoundsProblems(problems) {
  const names = [...new Set(problems.map(p => p.name))];
  const shown = names.slice(0, 3).join(', ') + (names.length > 3 ? `, +${names.length - 3} more` : '');
  const reversed = problems.filter(p => p.kind === 'reversed');
  // Can this session widen its own clock window? A driver-PIN / public session on a
  // tenant with allowDriverTimeEdit=false CANNOT — for them "fix the times" is not an
  // available action at all, which is exactly how TASK-1399 became a dead end.
  const _isDriverScope = !!(window.AppState?.driverMode || window.AppState?.driverModePin || window.AppState?.isPublicAccess);
  const canAdjustTimes = !_isDriverScope || window.AppState?.settings?.allowDriverTimeEdit === true;

  const detail = reversed.length > 0 && reversed.length === problems.length
    ? `${shown} ends before it starts.`
    : `${shown} ${names.length === 1 ? 'is' : 'are'} outside your clock in/out window.`;
  const message = canAdjustTimes
    ? `${detail}\n\nYou can go back and adjust the times (or your clock in/out), or clock out now — your hours are saved either way and the equipment hours go to dispatch for review.`
    : `${detail}\n\nThese times are kept exactly as entered. Your hours are saved and dispatch will finalize the equipment hours.`;

  const proceed = await window.showConfirm?.(
    'Times outside your shift',
    message,
    {
      confirmText: canAdjustTimes ? 'Clock out anyway' : 'Save for review & clock out',
      cancelText: canAdjustTimes ? 'Go back and fix' : 'Go back',
      icon: 'schedule',
    }
  );
  return proceed === true;
}

// ============================================================================
// TASK-4577 — WHAT A CLOSE-OUT MAY DO WITH THE TICKET AN ENTRY POINTS AT
// ============================================================================
// There are FOUR close-out writers that write to `entry.ticketId`, not two.
// TWO of them (confirmClockOut, saveTicketFromForm) used to run this
// unconditionally, the moment they saw `status === 'deleted'`:
//
//   updateDoc(ticketRef, { status: data._prevStatus || 'draft', deletedAt: null,
//                          deletedBy: null, deletedByName: null, _prevStatus: null })
//
// It never asked WHO deleted the ticket, and it never cleared `deletedReason`.
// Two different actors soft-delete tickets and they mean OPPOSITE things:
//
//   billing.js deleteTicket()            -> writes `_prevStatus` (+ since TASK-10036 a
//                                           typed human `deletedReason`, never `repoint_*`).
//                                           A human deleted it, Undo is offered,
//                                           and a restore is the right answer.
//   ticketGateway repoint_to_shop /      -> writes `deletedReason: 'repoint_*'`, NO
//   repoint_emptied (TASK-4562)             `_prevStatus`, and ZEROES subtotal /
//                                           grandTotal / totalHours. A deliberate
//                                           correction: this ticket must not exist.
//
// So a coworker still clocked in on a repointed ticket RESURRECTED it at clock-out:
// #100539 would have come back as a `draft` still stamped `repoint_to_shop`, carrying
// the gateway's zeroed totals — silently undoing the correction and re-billing the
// customer for a job the repoint said never happened. 48 clock entries tenant-wide
// currently point at a `status:"deleted"` ticket, so this is the shape it meets.
//
// The OTHER TWO (confirmClockOutFromReview, applyReviewData) had no status check of
// any kind — not even the blind restore — so they wrote the hours straight onto a
// ticket that stays deleted, which is the ticket-fiasco failure class (work landing
// on a record filtered out of every board and AR view). 🔴 confirmClockOutFromReview
// is the path a ticket-building FOUNDER account takes: confirmClockOut returns early
// at showClockOutReview() for those tenants, so a guard added only to confirmClockOut
// would never have run on the tenant this incident came from. All four now share the
// one decision below.
//
// PURE by design (no Firestore, no DOM, no ClockState) so every branch is assertable
// without a network: tests/task4577-deleted-ticket-restore.unit.cjs.
//
// Returns { action, branch, restoreTo, clearDeletedReason }:
//   'proceed' — the ticket is open (draft/ready) and may take this shift's hours.
//   'restore' — un-delete it, then proceed.
//   'detach'  — do NOT touch the ticket; release the entry from it instead.
function clockDecideDeletedTicketAction(ticket) {
  // Unreachable from both callers (each is behind a `snap.exists()` guard) — kept so
  // a missing doc can never be read as a licence to write. Today a missing ticket
  // falls straight through to the line write, which throws and is caught; preserving
  // 'proceed' here keeps that byte-for-byte.
  if (!ticket || typeof ticket !== 'object') {
    return { action: 'proceed', branch: 'no-ticket-data', restoreTo: null, clearDeletedReason: false };
  }

  const status = typeof ticket.status === 'string' ? ticket.status : '';
  const deletedReason = typeof ticket.deletedReason === 'string' ? ticket.deletedReason : '';
  const prevStatus = typeof ticket._prevStatus === 'string' ? ticket._prevStatus : '';

  if (status === 'deleted') {
    // 🔴 A repoint stamp WINS over `_prevStatus` when a ticket somehow carries both.
    // The two markers disagree, and the disagreement is not symmetric: `_prevStatus`
    // only records what the ticket USED to be, while `repoint_*` is an explicit,
    // machine-written statement that this ticket should not exist. Resurrecting on
    // the weaker marker re-bills a customer silently and is not detectable from any
    // board; detaching leaves the hours on the entry, where the Ticket Cross-Check
    // already surfaces them as `job-no-ticket`. Fail toward NOT billing.
    if (deletedReason.indexOf('repoint_') === 0) {
      return { action: 'detach', branch: 'repoint-deleted', restoreTo: null, clearDeletedReason: false };
    }
    if (prevStatus) {
      // A human deleted it in Billing. Restore — but never INTO a finalized status:
      // "never append hours to a finalized ticket" is the 🔴 rule and outranks "keep
      // today's restore" where the two collide. billing.js refuses to delete
      // archived/locked/sent/invoiced tickets, so this is defence in depth, not a
      // live path; it exists because a fabricated 'draft' would be worse than honest
      // inaction (the TASK-1347 rawStatus lesson: a restore that invents a status is
      // how a `sent` ticket came back as an editable draft).
      if (prevStatus !== 'draft' && prevStatus !== 'ready') {
        return { action: 'detach', branch: 'deleted-from-finalized', restoreTo: null, clearDeletedReason: false };
      }
      return { action: 'restore', branch: 'human-deleted', restoreTo: prevStatus, clearDeletedReason: !!deletedReason };
    }
    // Neither marker — a legacy or unattributed delete. Restore as today, but a live
    // ticket may never carry a lying delete stamp, so clear the reason if one is set.
    return { action: 'restore', branch: 'deleted-unattributed', restoreTo: 'draft', clearDeletedReason: !!deletedReason };
  }

  // Open statuses take hours. '' is deliberate: pre-status legacy tickets proceed
  // exactly as they do today — detaching them would be an enormous blast radius for
  // no money benefit.
  if (status === 'draft' || status === 'ready' || status === '') {
    return { action: 'proceed', branch: 'open', restoreTo: null, clearDeletedReason: false };
  }

  // invoiced / archived / locked / sent / paid / anything else: FINALIZED. Appending
  // this shift's hours would mutate a record already sent to the customer or already
  // invoiced in QuickBooks (which is what `archived` means at TITS), and those hours
  // would never be billed. Release the entry instead.
  return { action: 'detach', branch: 'finalized', restoreTo: null, clearDeletedReason: false };
}

// Apply the decision above. ONE implementation, called from BOTH close-out writers,
// because these two blocks are twins and they have drifted before.
//
// Whichever branch fires, it writes an audit row. A silent resurrect and a silent
// re-mint are the same invisibility problem, and that invisibility is the whole
// reason TASK-4562 took a forensic session: `auditLog` had ZERO rows for the ticket
// that got destroyed. Routed through window.logAudit — TASK-1495 forbids a direct
// addDoc into auditLog (logAudit strips undefined and stamps `timestamp`, without
// which a row is invisible to every reader's range filter).
//
// Returns the decision, plus `detached` so the caller knows the entry no longer has
// a ticket. NOTE: the caller must treat `detached` as "stop writing to that ticket" —
// there is no mint-a-fresh-ticket path in either close-out flow (verified: the only
// two findOrCreateTicket callers are clock-IN and job-switch), so a detached entry
// keeps its hours and surfaces in the Ticket Cross-Check as `job-no-ticket`.
async function clockApplyDeletedTicketDecision(tenant, entry, ticketId, ticketData, source) {
  const decision = clockDecideDeletedTicketAction(ticketData);
  const ticketNo = ticketData?.ticketNo ?? null;

  if (decision.action === 'restore') {
    const restoreUpdate = {
      status: decision.restoreTo || 'draft',
      deletedAt: null,
      // TASK-1347 — clear the delete-actor stamp too, or this auto-restore leaves a
      // live, visible ticket permanently labelled "deleted by <whoever>".
      // billing.js undoDeleteTicket() clears the same pair; the two restore paths
      // must write the same image.
      deletedBy: null,
      deletedByName: null,
      _prevStatus: null,
      updatedAt: Timestamp.now()
    };
    // TASK-4577 — only ADD the field when there is actually a stamp to clear. The
    // firestore.rules ticket LIFECYCLE arm (invoiced/archived/locked pre-image) is a
    // `hasOnly([...])` allowlist that does NOT list `deletedReason`, so writing it
    // unconditionally would turn a restore that is allowed today into a hard denial
    // on exactly those tickets. Nothing writes a non-`repoint_` reason today, and a
    // `repoint_` reason never reaches this branch, so in practice the payload stays
    // identical to the shipped one. (Dispatcher: adding 'deletedReason' to that
    // allowlist would let this be unconditional — that rules file is not this
    // agent's to edit.)
    if (decision.clearDeletedReason) restoreUpdate.deletedReason = null;

    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), restoreUpdate);
    console.warn('[Clock] Ticket was deleted while clocked in — auto-restoring:', ticketId);
    window.logAudit?.('ticket', 'TICKET_AUTO_RESTORED_AT_CLOSEOUT', {
      ticketId,
      ticketNo,
      branch: decision.branch,
      restoredTo: decision.restoreTo || 'draft',
      clearedDeletedReason: !!decision.clearDeletedReason,
      clockEntryId: entry?.id ?? null,
      personId: entry?.personId ?? null,
      personName: entry?.personName ?? null,
      source: source || 'clock'
    });
    return { ...decision, detached: false };
  }

  if (decision.action === 'detach') {
    // Same shape the server writes when a repoint releases a stranded entry
    // (ticketGateway releaseStrandedEntries, TASK-4562) — one vocabulary, so the two
    // halves of this fix are greppable together.
    await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entry.id), {
      ticketId: null,
      ticketLineId: null,
      _orphanedFromTicketId: ticketId,
      _orphanedReason: decision.branch,
      updatedAt: Timestamp.now()
    });
    // Keep the in-memory entry in step so nothing downstream writes to the dead
    // ticket off a stale copy.
    if (entry) { entry.ticketId = null; entry.ticketLineId = null; }
    console.warn('[Clock] Ticket is not open for hours (' + decision.branch + ') — releasing entry:', ticketId);
    window.logAudit?.('ticket', 'TICKET_DETACHED_AT_CLOSEOUT', {
      ticketId,
      ticketNo,
      branch: decision.branch,
      ticketStatus: ticketData?.status ?? null,
      deletedReason: ticketData?.deletedReason ?? null,
      clockEntryId: entry?.id ?? null,
      personId: entry?.personId ?? null,
      personName: entry?.personName ?? null,
      source: source || 'clock',
      note: 'Hours stay on the clock entry. This ticket was NOT reopened, so the correction that removed it stands; the shift shows in Ticket Cross-Check as "Job, no ticket".'
    });
    return { ...decision, detached: true };
  }

  return { ...decision, detached: false };
}

// ============================================
// CONFIRM CLOCK OUT (with all enhancements)
// ============================================
async function confirmClockOut() {
  const entry = ClockState.clockOutEntry;
  if (!entry) {
    // TASK-420 — never a silent no-op. A null clockOutEntry means the modal
    // state was lost (the bug that ate Sean Wood's 6/1 clock-out). Tell the user
    // to reopen (which re-selects their entry) instead of tapping into the void,
    // and leave a trace so the next failure isn't invisible. NOT re-fetching here:
    // in kiosk mode the operator clocks out a SELECTED person, so guessing "the
    // viewer's active entry" risks clocking out the wrong shift (COD 2026-06-02).
    logClockAudit(ClockState.tenantId, 'CLOCK_OUT_FAIL', {
      source: 'clock-module', reason: 'no_clockOutEntry', online: navigator.onLine,
    });
    window.showToast?.('Couldn’t find your shift to clock out — please reopen it and try again.', 'error');
    return;
  }

  // Ticket-building founder accounts get a review screen before saving
  // Non-founder goes straight to save (no AI cleanup review step needed)
  if (shouldBuildTickets() && isFounder() && !ClockState.reviewMode) {
    return showClockOutReview();
  }

  // TASK-9402 — a stamp photo still converting must join this clock-out.
  // Before the button lock, per the settle-before-save pattern.
  const _closeGenBefore = _clockOutCloseGen;
  if (!(await settlePhotoPrepBeforeSave(_stampPhotoPrep, 'btn-confirm-clock-out'))) return;
  // COD r4 — the SAME shift must still be open after the wait (Cancel stays
  // live; clocking out a different person's entry would be the wrong shift).
  // Integration COD r1 — and the modal must not have been CLOSED during it:
  // closeModal awaits its notes autosave before it clears clockOutEntry, so the
  // entry check alone can't see a Cancel. _clockOutCloseGen bumps synchronously.
  if (ClockState.clockOutEntry !== entry || _clockOutCloseGen !== _closeGenBefore) return;

  const btn = document.getElementById('btn-confirm-clock-out');
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Clocking Out...';

  // clock-dst-all — the PUNCH first (COD 1.15.2009): only the adjusted clock in /
  // out can stop this clock-out. A repeated-hour clock time asks (Cancel = nothing
  // written); a typed time that can't be converted is refused; an untouched box
  // with an unreadable zone falls back to the stored clock-in / real now.
  // Ticket rows are resolved AFTER, best effort — they never block the punch.
  const _punch = await _clockResolvePunch(entry);
  if (!_punch.ok) {
    window.showToast?.(_punch.message, 'warning', null, 9000);
    btn.disabled = false;
    btn.innerHTML = `<span class="material-icons">logout</span> ${shouldBuildTickets() ? 'Build Ticket' : 'Close Out'}`;
    return;
  }
  let _ticketTimeProblems = [];
  try {
    const tenant = ClockState.tenantId;
    const buildTickets = shouldBuildTickets();

    // Determine final clock times
    let finalClockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    let finalClockOut = new Date();
    let adjustedClockIn = null;
    let adjustedClockOut = null;

    if (buildTickets) {
      const adjIn = getSplitDateTimeValue('adj-clock-in');
      const adjOut = getSplitDateTimeValue('adj-clock-out');
      if (adjIn && !_punch.keepIn) {
        const adjusted = _tltDate(adjIn, 'adj-clock-in');
        if (adjusted.getTime() !== finalClockIn.getTime()) {
          adjustedClockIn = Timestamp.fromDate(adjusted);
          finalClockIn = adjusted;
        }
      }
      if (adjOut && !_punch.nowOut) {
        finalClockOut = _tltDate(adjOut, 'adj-clock-out');
        adjustedClockOut = Timestamp.fromDate(finalClockOut);
      }
    }

    const totalHours = Math.max(0, parseFloat(((finalClockOut - finalClockIn) / 3600000).toFixed(2)));
    if (totalHours <= 0) {
      window.showToast('Clock Out must be after Clock In', 'error');
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">logout</span> Clock Out';
      return;
    }

    // TASK-4614 — FINAL AUTHORITY, before anything is written. Every equipment row
    // still tracking the clock takes the SAME finalClockIn/finalClockOut the
    // employee punch is written from below, so the truck can never bill a shorter
    // window than the person driving it. Rows the driver typed a window on, or
    // toggled off, carry `_followsClock:false` and are untouched. Runs before
    // collectShiftBoundsProblems so a synced row can't be flagged out-of-bounds
    // against the very window it was just set to.
    clockSyncClockTrackedEquipment(finalClockIn, finalClockOut);

    // clock-dst-all — ticket rows / work times AFTER the sync (rows that follow
    // the clock now carry the punch's own instants). Best effort: a row that
    // can't be read is left unsaved and the entry is flagged for review.
    if (buildTickets) {
      const _tt = await _clockResolveTicketTimesBestEffort();
      _ticketTimeProblems = _tt.problems || [];
      if (_punch.zoneProblem) _ticketTimeProblems.push(_punch.zoneProblem);
    }

    // TASK-1396/1399 — shift-bounds problems now CONFIRM, they never block.
    // (Was: `return` on the first bad row, before any write. That is the exact
    // line that trapped Steven Alandt and Ethan Young on the clock.)
    ClockState._boundsProblemsAcknowledged = 0; // re-evaluated fresh on every attempt
    const _boundsProblems = collectShiftBoundsProblems(finalClockIn, finalClockOut);
    if (_boundsProblems.length > 0) {
      const _proceed = await confirmShiftBoundsProblems(_boundsProblems);
      if (!_proceed) {
        revealShiftBoundsProblem(_boundsProblems[0]);
        btn.disabled = false;
        btn.innerHTML = `<span class="material-icons">logout</span> ${shouldBuildTickets() ? 'Build Ticket' : 'Close Out'}`;
        return;
      }
      ClockState._boundsProblemsAcknowledged = _boundsProblems.length;
    }

    let notes = buildTickets
      ? document.getElementById('clock-out-shift-notes')?.value
      : document.getElementById('clock-out-notes')?.value;

    // TASK-619 (Lacey) — require a shift note before clock-out when the tenant opts in.
    if (shiftNotesRequired() && !(notes || '').trim()) {
      window.showToast?.('Add a shift note to clock out — what did you do today?', 'error');
      btn.disabled = false;
      btn.innerHTML = `<span class="material-icons">logout</span> ${shouldBuildTickets() ? 'Build Ticket' : 'Close Out'}`;
      const focusEl = document.getElementById('clock-out-shift-notes') || document.getElementById('clock-out-notes');
      focusEl?.focus();
      return;
    }

    // TASK-1483 — personal-vehicle claim. Validated BEFORE any write: toggle on
    // with no/invalid miles bounces here (the driver can fix it or untick it),
    // so it can never produce a half-completed clock-out.
    const _pv = pvReadForm('clock');
    if (_pv && !_pv.ok) {
      window.showToast?.(_pv.error, 'error');
      btn.disabled = false;
      btn.innerHTML = `<span class="material-icons">logout</span> ${shouldBuildTickets() ? 'Build Ticket' : 'Close Out'}`;
      document.getElementById('clock-pv-miles')?.focus();
      return;
    }

    // TASK-395: signatures are NEVER-FORCE. Clock-out is never blocked on a missing
    // signature — a drawn pad still uploads below (~L7689). Rationale (Chris): forcing
    // = driver gets stuck = calls Chris; optional = the biller catches a missing sig
    // in normal billing review. `requireDriverSignature` now means "request", not "block".
    const ftSets = ClockState.fieldTicketSettings;

    // 1. Update clock entry
    const updateData = {
      clockOut: Timestamp.fromDate(finalClockOut),
      status: 'completed',
      totalHours,
      hours: totalHours, // keep both fields in sync — see fix log 2026-05-07
      clockOutNotes: notes || null,
      updatedAt: Timestamp.now(),
      qbSynced: false
    };
    if (adjustedClockIn) {
      updateData.adjustedClockIn = adjustedClockIn;
      updateData.clockIn = adjustedClockIn;
    }
    if (adjustedClockOut) updateData.adjustedClockOut = adjustedClockOut;
    Object.assign(updateData, _clockOutCodeStamp(entry)); // TASK-9941 F S2
    // clock-dst-all — ticket times that could not be saved: the punch still saves,
    // flagged for the office, with WHAT was not saved (typed text + reason).
    const _unsavedRows = buildTickets ? _clockUnsavedRowsSummary([['equipment', ClockState.clockOutEquipment], ['downtime', ClockState.clockOutDowntime], ['standby', ClockState.clockOutStandby]]) : [];
    if (_ticketTimeProblems.length || _unsavedRows.length) {
      updateData.needsReview = true;
      updateData.ticketTimesUnsaved = _clockUnsavedRecord(_unsavedRows, _ticketTimeProblems);
      if (!_ticketTimeProblems.length) _ticketTimeProblems.push('a ticket time could not be read');
      logClockAudit(tenant, 'CLOCK_OUT_LINE_FAIL', { source: 'clock-module', clockEntryId: entry.id, reason: 'ticket_times_unresolved', unsavedRows: _unsavedRows, reasons: _ticketTimeProblems.slice(0, 5) });
    }

    // Mileage (ticket-building accounts)
    if (buildTickets && document.getElementById('mileage-toggle')?.checked) {
      const mileStart = parseFloat(document.getElementById('mileage-start')?.value) || 0;
      const mileStop = parseFloat(document.getElementById('mileage-stop')?.value) || 0;
      if (mileStart > 0 && mileStop >= mileStart) {
        updateData.mileage = {
          start: mileStart,
          stop: mileStop,
          total: mileStop - mileStart
        };
      }
    }

    // TASK-420 — trace every clock-out write so a future silent failure is debuggable.
    logClockAudit(tenant, 'CLOCK_OUT_ATTEMPT', {
      source: 'clock-module', clockEntryId: entry.id, personId: entry.personId,
      online: navigator.onLine, totalHours,
    });
    // TASK-1483 — the claim rides the SAME write that completes the entry.
    await pvSafeUpdate(doc(db, 'tenants', tenant, 'clockEntries', entry.id), updateData, _pv ? _pv.fields : null, 'clock-module');

    // Audit: clock-out
    logClockAudit(tenant, 'CLOCK_OUT', {
      source: 'clock-module',
      clockEntryId: entry.id,
      personId: entry.personId, personName: entry.personName,
      jobId: entry.jobId, jobTitle: entry.jobTitle,
      totalHours,
      ticketId: entry.ticketId, ticketLineId: entry.ticketLineId,
      rate: entry.rate, role: entry.role,
      hadAdjustedTimes: !!(adjustedClockIn || adjustedClockOut),
      ..._clockOutCodeAudit(entry), // TASK-9941 F S2
    });
    _clockConsumeClockOutAuth(entry); // TASK-9941 F S2 — one punch per grant

    // GPS ping (fire-and-forget, gated by tenant setting captureGpsOnClock)
    capturePingIfEnabled('captureGpsOnClock', {
      eventType: 'clock_out',
      tenant,
      ticketId: entry.ticketId || null,
      clockEntryId: entry.id,
      driverId: entry.personId,
      driverName: entry.personName,
      extra: { totalHours }
    });

    // =========================================================================
    // TASK-1396/1399 — THE TICKET PHASE IS BEST-EFFORT. THE CLOCK ENTRY IS ALREADY
    // COMPLETED ABOVE. Nothing below may surface to the human as "you couldn't
    // clock out" (TASK-489 pattern, law since TASK-428 stranded PIN crews 14-15h).
    // A ticket/line failure degrades to: your hours are saved, the ticket needs
    // attention - and lands a CLOCK_OUT_LINE_FAIL row for dispatch to find.
    // =========================================================================
    let _ticketPhaseFailed = null;
    let _ticketDetached = false;
    try {
      // 1b. TASK-4577 — decide what this close-out may do with the ticket instead of
      // blind-restoring anything marked deleted. A repoint-deleted ticket is a
      // deliberate correction and must NOT come back; a finalized ticket must not
      // take these hours. On 'detach' the applier nulls entry.ticketId, so every
      // `if (entry.ticketId)` below skips of its own accord and nothing is written
      // to the dead ticket.
      if (entry.ticketId) {
        const ticketSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId));
        if (ticketSnap.exists()) {
          const _tDecision = await clockApplyDeletedTicketDecision(
            tenant, entry, entry.ticketId, ticketSnap.data(), 'confirmClockOut');
          _ticketDetached = !!_tDecision.detached;
        }
      }

      // 2. Update employee ticket line (never round — rounding is for equipment only)
      if (entry.ticketId && entry.ticketLineId) {
        try {
          const _entryPieceRate = !isTimeBasedUnit(entry.rateUnit);
          // TASK-9646 S2 / TASK-1653 — an EMPLOYEE line never carries customer
          // money. This used to write hours × entry.rate (the PAYROLL rate) and
          // rely on the server guard to zero it after the fact.
          const lineUpdate = {
            toTime: Timestamp.fromDate(finalClockOut),
            hours: totalHours,
            lineAmount: 0,
            updatedAt: Timestamp.now()
          };
          if (_entryPieceRate) lineUpdate.quantity = lineUpdate.quantity || 0;
          if (adjustedClockIn) lineUpdate.fromTime = adjustedClockIn;
          await updateDoc(
            doc(db, 'tenants', tenant, 'tickets', entry.ticketId, 'ticketLines', entry.ticketLineId),
            lineUpdate
          );
        } catch (lineErr) {
          console.warn('[Clock] Could not update ticket line on clock-out (may have been deleted):', lineErr.message);
        }
      }

      // 2b. Overnight day range (workDateEnd) is set by the server from this clock-out
      // (onClockEntryTicketDateRange, TASK-10182).

      // 3. Create equipment, downtime, standby, notes, photos ticket lines
      //    (skip if already saved via Save button — dedup flags on ClockState)
      if (buildTickets && entry.ticketId) {
        // TASK-1396 — persist equipment removals (runs regardless of _appliedEquipment;
        // a Save-then-remove-then-clock-out must still delete the line).
        for (const delId of (ClockState._deletedEquipmentIds || [])) {
          try {
            await deleteDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId, 'ticketLines', delId));
          } catch (delErr) {
            console.warn('[Clock] Could not delete removed equipment line:', delErr.message);
          }
        }
        ClockState._deletedEquipmentIds = [];
        // TASK-4618 — the Save route. `saveTicketFromForm` writes the equipment
        // lines and sets `_appliedEquipment`, so the block below is skipped and the
        // in-memory sync at the top of this function would be thrown away — the
        // already-written line keeps its Save-time end while the punch below gets
        // the submit-time end. That is the original bug on the commonest path.
        // Extend-only, `_followsClock` rows only, times only. See the helper.
        if (ClockState._appliedEquipment) {
          await clockPushFollowsClockEnds(
            tenant, entry.ticketId, entry.id, ClockState.clockOutEquipment, 'confirmClockOut:saved');
        }
        if (!ClockState._appliedEquipment) {
          // TASK-427: session-scoped — only extends THIS session's line. A new
          // session (re-clock) finds no match and creates its own equipment line.
          const existingEqMap = await getExistingEquipmentMap(entry.ticketId, entry.id);
          for (const eq of ClockState.clockOutEquipment) {
            if (_clockRowUnresolved(eq)) continue; // clock-dst-all — left for the office (flagged)
            // TASK-1396 — a coworker's restored row NEVER enters the write payload.
            // The session-scoped existing-map can't match it, so the old code created
            // a NEW line stamped with THIS person's clockEntryId while keeping the
            // foreign window: Swallow #14 billed 1.13h against a ~10h operator window
            // (~$2.5k under-billed on TITS ticket #100429 alone).
            if (clockIsCrewEquipmentLine(eq)) continue;
            const existing = existingEqMap.get(eq.assetId);
            if (existing) {
              // TASK-4559 — the note is decided BEFORE any of the time logic, and
              // OUTSIDE the end-time guard below. That guard is correct for the time
              // fields (never shrink a shared/merged window) but it used to gate the
              // ENTIRE write, so a re-save where the end time hadn't moved wrote
              // nothing at all — including the note the driver had just typed.
              const eqNote = clockReadAssetNote(eq);
              const eqUpd = {};
              // Per-unit items: quantity is manual, so no time extension — but a
              // note is still a note, so it is applied on this branch too.
              if (isTimeBasedUnit(eq.rateUnit)) {
                // Equipment already on ticket — extend end time if this clock-out is later
                const newEnd = _tltRowDate(eq, 'endTime');
                const existingEnd = existing.toTime?.toDate?.() || new Date(existing.toTime);
                if (newEnd > existingEnd) {
                  const existingFromSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId, 'ticketLines', existing.docId));
                  const existingFrom = existingFromSnap.data()?.fromTime?.toDate?.() || _tltRowDate(eq, 'startTime');
                  const rawHrs = (newEnd - existingFrom) / 3600000;
                  const extHours = clockEquipmentHours(rawHrs, false, eq.roundToHalfHour); // TASK-4576
                  eqUpd.toTime = Timestamp.fromDate(newEnd);
                  eqUpd.hours = extHours;
                  // TASK-9646 S2: server-priced
                  eqUpd.amountPending = true;
                  // TASK-4576 (the twin) — the re-save path recomputed rounded hours and
                  // persisted the DECISION nowhere, so a re-saved line read back as
                  // { hours: 7.5, roundedFrom: undefined, roundToHalfHour: undefined } and
                  // billing.js's next recalc walked it straight back down to 7.13. Same
                  // gated `if` as the CREATE branch — no field on a line that has none.
                  // `roundedFrom` tracks the NEW window; a stale one would make the
                  // Review rounding marker lie.
                  if (eq.roundToHalfHour) {
                    eqUpd.roundedFrom = parseFloat(rawHrs.toFixed(2));
                    eqUpd.roundToHalfHour = true;
                  }
                }
              }
              if (eqNote.write) eqUpd.notes = eqNote.value;
              if (Object.keys(eqUpd).length > 0) {
                eqUpd.updatedAt = Timestamp.now();
                await updateDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId, 'ticketLines', existing.docId), eqUpd);
              }
              continue;
            }
            const _perUnit = !isTimeBasedUnit(eq.rateUnit);
            const rawEqHours = calcRowHours(eq);
            const eqHours = clockEquipmentHours(rawEqHours, _perUnit, eq.roundToHalfHour); // TASK-4576 — one rule, four writers
            // TASK-4559 — same decision on the CREATE branch. A brand-new line has
            // no stored note to protect, so `write:false` (nothing typed) keeps the
            // pre-existing `notes: null`, and `''` still means a deliberate clear.
            const _eqNote = clockReadAssetNote(eq);
            const perAssetNote = _eqNote.write ? _eqNote.value : null;
            const lineData = {
              type: 'EQUIPMENT',
              assetId: eq.assetId,
              assetName: eq.assetName,
              assetType: eq.assetType,
              fleetNumber: eq.fleetNumber,
              vehicleType: eq.vehicleType || '',
              clockEntryId: entry.id || null, // TASK-427: per-session key
              fromTime: Timestamp.fromDate(_tltRowDate(eq, 'startTime')),
              toTime: Timestamp.fromDate(_tltRowDate(eq, 'endTime')),
              hours: eqHours,
              rate: 0, // TASK-9646 S5 — born $0; the SERVER prices it from the price book (ratePending)
              ratePending: true,
              rateUnit: eq.rateUnit || 'Hourly',
              ..._clockEquipInvoiceField(eq), // TASK-10177
              // TASK-9646 S2 — born $0; the SERVER derives hours × rate (amountPending).
              lineAmount: 0,
              ...(_perUnit ? {} : { amountPending: true }),
              revenueCategory: eq.revenueCategory || '',
              notes: perAssetNote
            };
            if (_perUnit) lineData.quantity = 0;
            // TASK-4564 part 1 — persist the rounding DECISION next to its evidence.
            // `roundedFrom` alone is only evidence; billing.js had to infer the
            // decision from it and could only repair the line the first time a biller
            // touched it. Recording it here means the decision is stored at the moment
            // it is made. Same `if` as `roundedFrom` on purpose — this adds no new
            // field to any line that doesn't already carry one, so it adds no new
            // firestore.rules surface (see the arm note at saveTicketFromForm).
            if (!_perUnit && eq.roundToHalfHour) {
              lineData.roundedFrom = parseFloat(rawEqHours.toFixed(2));
              lineData.roundToHalfHour = true;
            }
            if (eq._offJobDay) lineData._billingReviewFlag = _clockOffDayFlag(); // TASK-1635
            // TASK-1674 — a GUESSED rate never ships silently. It is stamped with a
            // plain `rateResolvedBy` string and NOT a `_billingReviewFlag`: firestore.rules
            // `validClientBillingReviewFlag()` pins a client-authored flag to the ONE
            // shape {reason:'equipment_not_on_jobday', tiedAtClockIn, flaggedAt}, and a
            // clock-only member's close-out goes through exactly that arm — a second
            // reason string here would DENY the whole write and trap a driver at
            // clock-out. The billing review banner derives the guess live from the price
            // book instead (_equipRateGuessLines), so the biller still sees it.
            if (eq._rateGuess && (Number(lineData.rate) || 0) > 0) {
              lineData.rateResolvedBy = 'fallback:unmatched-vehicle-type';
            }
            await createTicketLine(entry.ticketId, lineData);
          }
          // Post-write dedup: merge any duplicate equipment lines (race condition between concurrent clock-outs)
          await deduplicateEquipmentLines(entry.ticketId);
        }

        // 4. Downtime ticket lines — per-entry save/update/delete
        const downtimeDeleted = ClockState._deletedDowntimeIds || [];
        for (const delId of downtimeDeleted) {
          await deleteDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId, 'ticketLines', delId));
        }
        for (const dt of ClockState.clockOutDowntime) {
          if (_clockRowUnresolved(dt)) continue; // clock-dst-all — left for the office (flagged)
          const fromTime = dt.startTime ? Timestamp.fromDate(_tltRowDate(dt, 'startTime')) : null;
          const toTime = dt.endTime ? Timestamp.fromDate(_tltRowDate(dt, 'endTime')) : null;
          const dtHours = (dt.startTime && dt.endTime) ? calcRowHours(dt) : 0;
          const lineData = {
            type: 'DOWNTIME', fromTime, toTime,
            hours: parseFloat(dtHours.toFixed(2)), rate: 0, lineAmount: 0,
            notes: dt.notes || null
          };
          if (dt._lineId && dt._dirty) {
            await updateDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId, 'ticketLines', dt._lineId), _clockLineUpdatePayload(lineData));
          } else if (!dt._lineId) {
            await createTicketLine(entry.ticketId, lineData);
          }
        }
        ClockState._deletedDowntimeIds = [];

        // 5. Standby ticket lines — per-entry save/update/delete
        const standbyDeleted = ClockState._deletedStandbyIds || [];
        for (const delId of standbyDeleted) {
          await deleteDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId, 'ticketLines', delId));
        }
        for (const sb of ClockState.clockOutStandby) {
          if (_clockRowUnresolved(sb)) continue; // clock-dst-all — left for the office (flagged)
          const fromTime = sb.startTime ? Timestamp.fromDate(_tltRowDate(sb, 'startTime')) : null;
          const toTime = sb.endTime ? Timestamp.fromDate(_tltRowDate(sb, 'endTime')) : null;
          const sbHours = (sb.startTime && sb.endTime) ? calcRowHours(sb) : 0;
          const lineData = {
            type: 'STANDBY', fromTime, toTime,
            hours: parseFloat(sbHours.toFixed(2)), rate: 0, lineAmount: 0,
            notes: sb.notes || null
          };
          if (sb._lineId && sb._dirty) {
            await updateDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId, 'ticketLines', sb._lineId), _clockLineUpdatePayload(lineData));
          } else if (!sb._lineId) {
            await createTicketLine(entry.ticketId, lineData);
          }
        }
        ClockState._deletedStandbyIds = [];

        // 6. Custom ticket sections — TASK-4596: merge-save, not wholesale
        // replace. customSectionData is written WHOLE onto a ticket shared
        // with the crew, so a naive updateDoc used to delete a crew-mate's
        // row the moment this form had any section data of its own (see
        // _clockMergeSaveCustomSectionData above). A failure here throws up
        // into the surrounding catch (ticketErr) below — same as any other
        // step in this block — so the driver is still told "clocked out,
        // ticket needs attention" (never silent, never traps the clock-out).
        if (!ClockState._appliedCustomSections) {
          // Prefer the snapshot captured at review time (reviewData.customSectionData);
          // fall back to reading the live DOM if the form is still mounted.
          const customSectionData = (ClockState.reviewData?.customSectionData
            && Object.keys(ClockState.reviewData.customSectionData).length > 0)
            ? ClockState.reviewData.customSectionData
            : collectCustomSectionData('clock');
          await _clockMergeSaveCustomSectionData(tenant, entry.ticketId, customSectionData);
          ClockState._appliedCustomSections = true;
        }

        // 7. Line (pothole) ticket lines + per-line photos — founder only
        if (isFounder() && !ClockState._appliedPotholes) {
          for (const ph of ClockState.clockOutPotholes) {
            const tolStr = (ph.tolFeet > 0 || ph.tolInches > 0)
              ? `${ph.tolFeet}'${ph.tolInches}"`
              : '';
            const desc = [
              ph.type ? `Type: ${ph.type}` : '',
              tolStr ? `TOL: ${tolStr}` : '',
              ph.pipeSize ? `Pipe: ${ph.pipeSize}"` : ''
            ].filter(Boolean).join(', ');
            const lineId = await createTicketLine(entry.ticketId, {
              type: 'POTHOLE',
              description: desc,
              gpsLat: ph.gpsLat || null,
              gpsLng: ph.gpsLng || null,
              notes: ph.notes || null,
              lineAmount: 0
            });
            // Upload per-line photos (non-fatal)
            if (lineId && (ph.photos || []).length > 0) {
              try {
                await uploadPotholePhotos(entry.ticketId, lineId, ph.photos);
              } catch (phErr) {
                console.error('[Clock] Pothole photo upload failed:', phErr);
                window.logError?.(phErr, { module: 'clock', operation: 'POTHOLE_PHOTO_UPLOAD', ticketId: entry.ticketId, lineId });
                window.showToast?.('Pothole saved but photos failed to upload', 'error');
              }
            }
          }
        }

        // 8. Ticket notes → merged onto the CURRENT stored value (TASK-1397).
        // The old code appended the whole box (prefill included) onto a
        // stale read, which is what double-pasted a coworker's paragraph.
        // saveInvoiceNotes is idempotent, so it no longer needs the
        // _appliedNotes one-shot guard — a second call contributes nothing.
        await saveInvoiceNotes({
          tenantId: tenant,
          ticketId: entry.ticketId,
          currentText: document.getElementById('clock-out-ticket-notes')?.value || '',
          syncElId: 'clock-out-ticket-notes'
        });
        ClockState._appliedNotes = true;

        // 8b. Work start/stop times
        const _coWsVal = getSplitDateTimeValue('work-start-time');
        const _coWeVal = getSplitDateTimeValue('work-stop-time');
        if (_coWsVal || _coWeVal) {
          const _coTimeUpdate = { updatedAt: Timestamp.now() };
          if (_coWsVal && Number.isFinite(_tltMs(_coWsVal, 'work-start-time'))) _coTimeUpdate.workStartTime = Timestamp.fromDate(_tltDate(_coWsVal, 'work-start-time'));
          if (_coWeVal && Number.isFinite(_tltMs(_coWeVal, 'work-stop-time'))) _coTimeUpdate.workStopTime = Timestamp.fromDate(_tltDate(_coWeVal, 'work-stop-time'));
          await updateDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId), _coTimeUpdate);
        }

        // 9. Photos — upload to Storage, save URLs to ticket (non-fatal)
        if (!ClockState._appliedPhotos) {
          try {
            const _photoResult = await uploadClockOutPhotos(entry.ticketId);
            const _photoMsg = summarizePhotoFailures(_photoResult?.failures);
            if (_photoMsg) window.showToast?.(_photoMsg, 'error');
          } catch (photoErr) {
            console.error('[Clock] Clock-out photo upload failed:', photoErr);
            window.logError?.(photoErr, { module: 'clock', operation: 'CLOCKOUT_PHOTO_UPLOAD', ticketId: entry.ticketId });
            window.showToast?.(`Clocked out but photo save failed: ${photoErr?.code || photoErr?.message || 'unknown'}`, 'error');
          }
        }

        // 9.5. Field Ticket Items — TASK-9665: ONE upsert writer shared by every
        // close-out path (see clockApplyFieldTicketItems). Idempotent, so no
        // `_appliedFieldItems` guard: a prior Save/Apply left lines this call
        // updates, never duplicates.
        await clockApplyFieldTicketItems(entry.ticketId, entry, _clockReadFieldItemQtys());

        // 9.6. Upload signatures (non-founder) — non-fatal, clock-out still succeeds
        if (!isFounder() && ftSets) {
          try {
            const sigUpdates = {};

            if (ftSets.requireDriverSignature) {
              const driverDataUrl = getSignatureDataUrl('driver');
              if (driverDataUrl && driverDataUrl !== 'N/A') {
                const blob = dataUrlToBlob(driverDataUrl);
                const sigRef = ref(storage, `tenants/${tenant}/signatures/${entry.ticketId}-driver.png`);
                await uploadBytes(sigRef, blob, { contentType: 'image/png' });
                sigUpdates.driverSignatureUrl = await getDownloadURL(sigRef);
              }
            }
            if (ftSets.requireCustomerSignature) {
              const custDataUrl = getSignatureDataUrl('customer');
              if (custDataUrl === 'N/A') {
                sigUpdates.customerSignatureUnavailable = true;
              } else if (custDataUrl) {
                const blob = dataUrlToBlob(custDataUrl);
                const sigRef = ref(storage, `tenants/${tenant}/signatures/${entry.ticketId}-customer.png`);
                await uploadBytes(sigRef, blob, { contentType: 'image/png' });
                sigUpdates.customerSignatureUrl = await getDownloadURL(sigRef);
              }
            }
            if (Object.keys(sigUpdates).length > 0) {
              await updateDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId), {
                'fieldTicketData.driverSignatureUrl': sigUpdates.driverSignatureUrl || null,
                'fieldTicketData.customerSignatureUrl': sigUpdates.customerSignatureUrl || null,
                'fieldTicketData.customerSignatureUnavailable': sigUpdates.customerSignatureUnavailable || false,
                updatedAt: Timestamp.now()
              });
            }
          } catch (sigErr) {
            console.error('[Clock] Signature upload failed:', sigErr);
            window.logError?.(sigErr, { module: 'clock', operation: 'SIGNATURE_UPLOAD', ticketId: entry.ticketId });
            window.showToast?.('Clocked out but signature upload failed — notify admin', 'error');
          }
        }

        // 9.7. Upload stamp photo (non-founder) — non-fatal, clock-out still succeeds
        if (!isFounder() && ftSets?.requireStamp) {
          try {
            if (ClockState._stampFile) {
              const stampRef = ref(storage, `tenants/${tenant}/ticketStamps/${entry.ticketId}.jpg`);
              await uploadBytes(stampRef, ClockState._stampFile, { contentType: ClockState._stampFile?.type || 'image/jpeg' });
              const stampUrl = await getDownloadURL(stampRef);
              await updateDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId), {
                'fieldTicketData.stampPhotoUrl': stampUrl,
                updatedAt: Timestamp.now()
              });
            } else if (ClockState._stampNA) {
              await updateDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId), {
                'fieldTicketData.stampUnavailable': true,
                updatedAt: Timestamp.now()
              });
            }
          } catch (stampErr) {
            console.error('[Clock] Stamp photo upload failed:', stampErr);
            window.logError?.(stampErr, { module: 'clock', operation: 'STAMP_UPLOAD', ticketId: entry.ticketId });
            window.showToast?.('Clocked out but stamp photo failed to upload', 'error');
          }
        }

        // 10. Check if all clock entries for this ticket are completed → set READY
        await checkTicketReady(entry.ticketId);

        // 11. Recalculate ticket grand total
        // TASK-4699 — markDirty: this clock-out just wrote the driver's hours,
        // equipment/downtime/standby lines, work times and shift notes. All of
        // it is on the copy the customer was emailed.
        await recalcTicketTotal(entry.ticketId, true);

        // TASK-10026 — the email copy goes LAST. Sending closes the ticket to
        // the driver (the server stamps driverEditLocked on send), so the READY
        // status and total writes above must land first or a PIN session's
        // own close-out would be refused and read as "ticket needs attention".
        // 9.8. Send email copy (non-founder)
        if (!isFounder() && ftSets?.requireEmailCopy) {
          const emailTo = document.getElementById('ft-email-to')?.value?.trim();
          if (emailTo) {
            try {
              const sendFieldTicketEmail = httpsCallable(functions, 'sendFieldTicketEmail');
              // TASK-4713 — for a DRIVER-PIN session the server stamps
              // `fieldTicketData.emailSentTo` / `emailSentAt` itself (Admin SDK,
              // same batch as its dedup witness) and the driver arm of
              // firestore.rules DENIES a client write of it: a driver-writable
              // stamp let a pre-stamp make the customer's FIRST copy skip as
              // "already sent". So a PIN driver never writes it here.
              await sendFieldTicketEmail({ ticketId: entry.ticketId, emailTo });
              // COD round 4 (Code Reviewer ❌): a signed-in MEMBER takes the
              // server's OFFICE branch, which deliberately never touches
              // fieldTicketData (TASK-4662 S1). Without this write nothing
              // records the close-out copy, and the Job Board's `already`
              // pre-check would re-send it on the next Save. The member arm
              // allows it; this is the pre-4713 write, kept for members only.
              // Own try: a denial (mis-detected session) must not read as a
              // failed send.
              if (!window.AppState?.driverModePin) {
                try {
                  await updateDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId), {
                    'fieldTicketData.emailSentTo': emailTo,
                    'fieldTicketData.emailSentAt': Timestamp.now(),
                    updatedAt: Timestamp.now()
                  });
                } catch (stampErr) {
                  console.warn('[Clock] Field ticket sent-stamp not saved:', stampErr?.code || stampErr?.message);
                }
              }
            } catch (emailErr) {
              console.warn('[Clock] Field ticket email failed:', emailErr);
              // Non-blocking — ticket is still saved
            }
          }
        }
      }
    } catch (ticketErr) {
      // Never rethrow - the shift is closed; only the billing artifact is incomplete.
      console.error('[Clock] Ticket work failed AFTER the clock entry was completed:', ticketErr);
      _ticketPhaseFailed = ticketErr;
      logClockAudit(tenant, 'CLOCK_OUT_LINE_FAIL', {
        source: 'clock-module', clockEntryId: entry.id, personId: entry.personId,
        ticketId: entry.ticketId || null, online: navigator.onLine,
        message: ticketErr?.message || String(ticketErr),
      });
      window.logError?.(ticketErr, { module: 'clock', operation: 'CLOCK_OUT_TICKET_PHASE', ticketId: entry.ticketId });
    }

    stopClockOutTimer();
    // TASK-1396/1399 - honest, non-alarming messaging. "You are clocked out" is the
    // headline in BOTH branches; the ticket is a separate, recoverable concern.
    if (_ticketPhaseFailed) {
      window.showToast('You are clocked out and your hours are saved. The ticket needs attention - dispatch will finish it.', 'error');
    } else if (_ticketTimeProblems.length) {
      // clock-dst-all — the punch saved; the unreadable ticket times did not.
      window.showToast('You are clocked out and your hours are saved. Some ticket times were not saved (' + _ticketTimeProblems[0] + ') — the entry is flagged for the office to finish.', 'warning', null, 12000);
    } else if (_ticketDetached) {
      // TASK-4577 — same stance as the line above: "you are clocked out" is the
      // headline, the ticket is a separate recoverable concern. Never silent, and
      // never phrased as a clock-out failure (TASK-428 law).
      window.showToast('You are clocked out and your hours are saved. That ticket was removed by a dispatch correction, so the hours were not put back on it - dispatch will place them.', 'error');
    } else if (ClockState._boundsProblemsAcknowledged > 0) {
      window.showToast('Clocked out. Equipment times outside your shift were saved as entered and sent to dispatch for review.', 'success');
    } else {
      window.showToast('Clocked out successfully', 'success');
    }
    closeModal('clock-out');
    ClockState.clockOutEntry = null;
  } catch (error) {
    console.error('[Clock] Clock out error:', error);
    logClockAudit(ClockState.tenantId, 'CLOCK_OUT_FAIL', {
      source: 'clock-module', clockEntryId: entry?.id, reason: 'write_error',
      message: error?.message || String(error), online: navigator.onLine,
    });
    window.showToast('Error clocking out', 'error');
  } finally {
    btn.disabled = false;
    btn.innerHTML = `<span class="material-icons">logout</span> ${shouldBuildTickets() ? 'Build Ticket' : 'Close Out'}`;
  }
}

// ============================================
// CLOCK-OUT REVIEW SCREEN (founder only)
// ============================================

// TASK-4616 — the review screen FREEZES the clock-out time at Build Ticket, and
// Confirm writes that frozen value (employee punch AND equipment, TASK-4614/4618).
// Chris 2026-09-29 chose "warn", NOT "refresh at Confirm": the driver confirms the
// number he was shown, so Confirm must keep writing `finalClockOut` — silently
// writing a bigger number is its own wage-record defect. What changes is that a
// long dwell (AI cleanup, typing, photos, walking away) is no longer invisible:
//   1. after CLOCK_REVIEW_DWELL_WARN_MS the review shows a banner saying which
//      time Confirm will save and how to change it (Edit);
//   2. a CLOCK_OUT_REVIEW_DWELL row lands in clockAuditLog the first time it fires
//      ("make sure we have a listener for this … for tech support down the road");
//   3. the CLOCK_OUT (clock-review) row carries reviewOpenedAt + reviewDwellMinutes
//      on EVERY confirm, so support can see a dwell even when the banner never fired.
// Only a LIVE clock-out is measured. If the person typed a clock-out time (the live
// timer was paused), that time is their choice and dwell says nothing about it.
const CLOCK_REVIEW_DWELL_WARN_MS = 10 * 60 * 1000;
const CLOCK_REVIEW_DWELL_TICK_MS = 30 * 1000;

// Pure — unit-tested in tests/task4616-review-dwell-warning.unit.cjs.
function clockReviewDwell(data, nowMs) {
  const none = { warn: false, minutes: 0 };
  if (!data || data.clockOutWasLive !== true) return none;
  const outMs = data.finalClockOut instanceof Date ? data.finalClockOut.getTime() : NaN;
  if (!Number.isFinite(outMs) || !Number.isFinite(nowMs)) return none;
  const gap = nowMs - outMs;
  if (!(gap > 0)) return none;
  return { warn: gap >= CLOCK_REVIEW_DWELL_WARN_MS, minutes: Math.floor(gap / 60000) };
}

function _clockRenderReviewDwellWarning(data, dwell) {
  const el = document.getElementById('review-dwell-warning');
  if (!el) return;
  const outStr = data.finalClockOut.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  // Written into its own empty slot, never a repaint of the review body — that
  // body cannot be rebuilt without destroying the driver's edits (TASK-9207).
  el.innerHTML = `
    <div style="display:flex;gap:10px;align-items:flex-start;padding:12px 14px;margin-bottom:16px;border-radius:var(--radius-md);background:#FEF3C7;border-left:4px solid #D97706;color:#78350F;">
      <span class="material-icons" style="font-size:20px;color:#B45309;">schedule</span>
      <div style="font-size:0.9rem;line-height:1.4;">
        <strong>Clock-out time is ${escapeHtml(outStr)}</strong> — ${dwell.minutes} min ago.
        Confirm saves ${escapeHtml(outStr)}. To use a later time, tap Edit and change Clock Out.
        <div style="margin-top:8px;">
          <button type="button" class="btn btn-secondary btn-sm" onclick="window.ClockModule.backToForm()">
            <span class="material-icons" style="font-size:16px;">edit</span> Edit
          </button>
        </div>
      </div>
    </div>`;
}

function _clockReviewDwellTick() {
  const data = ClockState.reviewData;
  if (!ClockState.reviewMode || !data || !document.getElementById('btn-confirm-clock-out-review')) {
    stopClockReviewDwellWatch();
    return;
  }
  if (data._confirming) return; // Confirm tapped — the CLOCK_OUT row carries the dwell
  const dwell = clockReviewDwell(data, Date.now());
  if (!dwell.warn) return;
  _clockRenderReviewDwellWarning(data, dwell);
  data._dwellWarnShown = true;
  // One row per review. `_dwellWarnLogged` flips only on a SUCCESSFUL write, so an
  // offline/failed write is retried on the next tick (COD r1); `_dwellWarnLogging`
  // stops two ticks racing the same write.
  if (!data._dwellWarnLogged && !data._dwellWarnLogging) {
    data._dwellWarnLogging = true;
    logClockAudit(ClockState.tenantId, 'CLOCK_OUT_REVIEW_DWELL', {
      source: 'clock-review',
      clockEntryId: data.entry?.id || null,
      personId: data.entry?.personId || null,
      frozenClockOut: Timestamp.fromDate(data.finalClockOut),
      reviewOpenedAt: data.reviewOpenedAt ? Timestamp.fromDate(data.reviewOpenedAt) : null,
      dwellMinutes: dwell.minutes,
      thresholdMinutes: CLOCK_REVIEW_DWELL_WARN_MS / 60000,
      online: navigator.onLine,
    }).then((ok) => {
      data._dwellWarnLogging = false;
      if (ok === true) data._dwellWarnLogged = true;
    });
  }
}

function _clockReviewDwellOnVisible() {
  if (document.visibilityState === 'visible') _clockReviewDwellTick();
}

function startClockReviewDwellWatch() {
  stopClockReviewDwellWatch();
  ClockState.reviewDwellInterval = setInterval(_clockReviewDwellTick, CLOCK_REVIEW_DWELL_TICK_MS);
  // A phone with the screen off suspends timers; check the moment it comes back,
  // before the driver can reach Confirm.
  document.addEventListener('visibilitychange', _clockReviewDwellOnVisible);
}

function stopClockReviewDwellWatch() {
  if (ClockState.reviewDwellInterval) {
    clearInterval(ClockState.reviewDwellInterval);
    ClockState.reviewDwellInterval = null;
  }
  document.removeEventListener('visibilitychange', _clockReviewDwellOnVisible);
}

async function showClockOutReview() {
  const entry = ClockState.clockOutEntry;
  if (!entry) return;

  if (!shouldBuildTickets()) return;

  // clock-dst-all — the PUNCH only here (COD 1.15.2009): ticket rows are
  // resolved at Confirm, best effort, and never block the clock-out.
  const _punch = await _clockResolvePunch(entry);
  if (!_punch.ok) {
    window.showToast?.(_punch.message, 'warning', null, 9000);
    return;
  }

  // --- Run the same validation that confirmClockOut does ---
  let finalClockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
  let finalClockOut = new Date();
  let adjustedClockIn = null;
  let adjustedClockOut = null;

  const adjIn = getSplitDateTimeValue('adj-clock-in');
  const adjOut = getSplitDateTimeValue('adj-clock-out');
  if (adjIn && !_punch.keepIn) {
    const adjusted = _tltDate(adjIn, 'adj-clock-in');
    if (adjusted.getTime() !== finalClockIn.getTime()) {
      adjustedClockIn = adjusted;
      finalClockIn = adjusted;
    }
  }
  if (adjOut && !_punch.nowOut) {
    finalClockOut = _tltDate(adjOut, 'adj-clock-out');
    adjustedClockOut = finalClockOut;
  }
  // TASK-4616 — was this clock-out "now" (filled by the live timer, or no field) or
  // a time the person typed into Clock Out? Only the Clock Out field's own handler
  // sets clockOutTypedByHand — a Clock IN edit pauses the timer too, but the
  // clock-out it leaves behind is still un-chosen and still goes stale (COD r1).
  const clockOutWasLive = !adjOut || ClockState.clockOutTypedByHand !== true;

  const totalHours = Math.max(0, parseFloat(((finalClockOut - finalClockIn) / 3600000).toFixed(2)));
  if (totalHours <= 0) {
    window.showToast('Clock Out must be after Clock In', 'error');
    return;
  }

  // TASK-4614 — sync BEFORE the snapshot below, so the review table the driver
  // reads shows the exact equipment windows that will be billed. `finalClockOut`
  // is frozen right here and `confirmClockOutFromReview` writes THIS value as the
  // employee punch, so passing it (never `new Date()`) is what keeps the two
  // sides equal instead of just moving the mismatch to the other side.
  clockSyncClockTrackedEquipment(finalClockIn, finalClockOut);

  // TASK-1396/1399 — confirm, never block. This is the path TITS actually uses
  // (founder + build-tickets → review screen), and this `return` is where Steven
  // Alandt's clock-out died on a coworker's equipment row he could not fix.
  ClockState._boundsProblemsAcknowledged = 0; // re-evaluated fresh on every attempt
  const _boundsProblems = collectShiftBoundsProblems(finalClockIn, finalClockOut);
  if (_boundsProblems.length > 0) {
    // The Build Ticket button isn't disabled on this branch, so guard the await
    // window — a double-tap would otherwise open two confirms (the second
    // supersedes the first, which resolves false and bounces the user back).
    if (ClockState._reviewGateInFlight) return;
    ClockState._reviewGateInFlight = true;
    let _proceed = false;
    try {
      _proceed = await confirmShiftBoundsProblems(_boundsProblems);
    } finally {
      ClockState._reviewGateInFlight = false;
    }
    if (!_proceed) {
      revealShiftBoundsProblem(_boundsProblems[0]);
      return;
    }
    ClockState._boundsProblemsAcknowledged = _boundsProblems.length;
  }

  // --- Snapshot form data ---
  const person = ClockState.people.find(p => p.id === entry.personId);
  const shiftNotes = document.getElementById('clock-out-shift-notes')?.value || '';
  const ticketNotes = document.getElementById('clock-out-ticket-notes')?.value || '';
  const aiCleanup = true;
  const dumps = 0;
  const waterRefills = 0;

  // Collect custom section data before review (handles repeatable + single shapes)
  const customSectionData = collectCustomSectionData('clock');

  // Mileage
  let mileage = null;
  if (document.getElementById('mileage-toggle')?.checked) {
    const mileStart = parseFloat(document.getElementById('mileage-start')?.value) || 0;
    const mileStop = parseFloat(document.getElementById('mileage-stop')?.value) || 0;
    if (mileStart > 0 && mileStop >= mileStart) {
      mileage = { start: mileStart, stop: mileStop, total: mileStop - mileStart };
    }
  }

  // TASK-1483 — personal-vehicle claim: validate on the FORM (before the review
  // screen replaces it), carry the two fields through reviewData.
  const pvClaim = pvReadForm('clock');
  if (pvClaim && !pvClaim.ok) {
    window.showToast?.(pvClaim.error, 'error');
    document.getElementById('clock-pv-miles')?.focus();
    return;
  }

  ClockState.reviewData = {
    entry,
    person,
    pvFields: pvClaim ? pvClaim.fields : null,
    finalClockIn,
    finalClockOut,
    adjustedClockIn,
    adjustedClockOut,
    clockOutWasLive,          // TASK-4616
    reviewOpenedAt: new Date(), // TASK-4616 — when Build Ticket opened this review
    totalHours,
    shiftNotes,
    ticketNotes,
    aiCleanup,
    dumps,
    waterRefills,
    mileage,
    equipment: [...ClockState.clockOutEquipment],
    downtime: [...ClockState.clockOutDowntime],
    standby: [...ClockState.clockOutStandby],
    potholes: [...ClockState.clockOutPotholes],
    photos: [...ClockState.clockOutPhotos],
    customSectionData,
    // TASK-9665 — Price Book item quantities, read off the form BEFORE the
    // review screen replaces it (same reason as customSectionData).
    fieldItems: _clockReadFieldItemQtys()
  };

  ClockState.originalShiftNotes = shiftNotes;
  ClockState.cleanedShiftNotes = '';
  ClockState.reviewMode = true;

  // Stop live timer — snapshot data for review
  stopClockOutTimer();

  // TASK-9207 — settle "whose line is this" before the body swap, because the
  // review CANNOT be repainted afterwards without destroying it. See
  // _clockSettleAttributionForReview: cached and instant in the normal case,
  // hard-bounded so it can never hold up a man's clock-out.
  await _clockSettleAttributionForReview();

  // --- Swap modal content to review screen ---
  const bodyEl = document.getElementById('clock-out-body');
  if (bodyEl) bodyEl.innerHTML = buildReviewContent(ClockState.reviewData);

  // Update modal header
  const headerEl = document.querySelector('#modal-clock-out .modal-header h3');
  if (headerEl) headerEl.innerHTML = '<span class="material-icons" style="vertical-align:middle;margin-right:4px;">fact_check</span> Review & Confirm';

  // Update modal footer
  const footerEl = document.querySelector('#modal-clock-out .modal-footer');
  if (footerEl) {
    footerEl.innerHTML = `
      <button class="btn btn-secondary" onclick="window.ClockModule.backToForm()">
        <span class="material-icons">edit</span> Edit
      </button>
      <button class="btn btn-secondary" id="btn-apply-review" onclick="window.ClockModule.applyReviewData()">
        <span class="material-icons">save</span> Apply
      </button>
      ${shouldBuildTickets() ? `<button class="btn btn-secondary" onclick="window.ClockModule.switchTicketFromClockOut()" title="Switch to a different customer/job without clocking out"><span class="material-icons">swap_horiz</span> Switch Ticket</button>` : ''}
      <button class="btn btn-success" id="btn-confirm-clock-out-review" onclick="window.ClockModule.confirmClockOutFromReview()" disabled>
        <span class="material-icons">check_circle</span> Confirm Clock Out
      </button>
    `;
  }

  // TASK-4616 — watch for a long dwell on the frozen clock-out time.
  startClockReviewDwellWatch();

  // --- Fire AI cleanup async (requires Smart Tools) ---
  if (window.hasAiPack?.() && shiftNotes.trim()) {
    const notesContainer = document.getElementById('review-shift-notes-container');
    if (notesContainer) {
      notesContainer.innerHTML = `
        <div style="display:flex;align-items:center;gap:8px;padding:12px;color:var(--text-primary);">
          <span class="btn-spinner"></span>
          <span style="font-size:0.85rem;">Formatting notes...</span>
        </div>
      `;
    }

    const cleanup = httpsCallable(functions, 'cleanupShiftNotes');
    cleanup({ notes: shiftNotes }).then(result => {
      const cleaned = result.data?.cleaned || shiftNotes;
      ClockState.cleanedShiftNotes = cleaned;
      const container = document.getElementById('review-shift-notes-container');
      if (container) {
        container.innerHTML = `
          <textarea id="review-shift-notes-editable" rows="3" style="width:100%;font-size:0.9rem;padding:8px;border:1px solid var(--border-light);border-radius:var(--radius-sm);resize:vertical;">${escapeHtml(cleaned)}</textarea>
          <div style="display:flex;align-items:center;gap:8px;margin-top:6px;">
            <span style="font-size:0.7rem;padding:2px 8px;background:#E0F2FE;color:#0369A1;border-radius:10px;font-weight:600;">Formatted</span>
            <button type="button" class="btn btn-secondary btn-sm" onclick="window.ClockModule.revertShiftNotes()" id="btn-revert-notes" style="font-size:0.75rem;padding:2px 8px;">
              Revert to original
            </button>
          </div>
        `;
      }
    }).catch(err => {
      console.error('[Clock] AI cleanup failed:', err);
      const container = document.getElementById('review-shift-notes-container');
      if (container) {
        container.innerHTML = `
          <textarea id="review-shift-notes-editable" rows="3" style="width:100%;font-size:0.9rem;padding:8px;border:1px solid var(--border-light);border-radius:var(--radius-sm);resize:vertical;">${escapeHtml(shiftNotes)}</textarea>
          <div style="margin-top:4px;font-size:0.7rem;color:var(--text-secondary);">Auto-format unavailable — original notes shown</div>
        `;
      }
    });
  }
}

function buildReviewContent(data) {
  const { entry, person, finalClockIn, finalClockOut, totalHours, equipment, downtime, standby, potholes, photos, dumps, waterRefills, mileage, ticketNotes, shiftNotes, aiCleanup } = data;

  const fmtTime = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    return dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };
  // TASK-9189 — the END of a start/end pair, carrying a +1d marker when it lands
  // on a later local calendar day. Every pair below sits next to an hours total,
  // so a time-only end time contradicts the number beside it: a driver who
  // clocked in at 11 PM and out at 2 AM read "11:00 PM / 2:00 AM / 3.00 hrs" as
  // if the shift ran backwards. This is the DRIVER's own ticket-review screen,
  // and the times feeding it are editable, so the end is NOT necessarily "now".
  const fmtEndTime = (start, end) => window.Fmt.shiftSpan(start, end).outHtml;
  const fmtDate = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  // TASK-4616 — empty slot the dwell watch fills in place (no body repaint).
  let html = '<div id="review-dwell-warning" role="status" aria-live="polite"></div>';

  // --- Header Card ---
  html += `
    <div style="background:var(--bg-elevated);border-radius:var(--radius-md);padding:14px 16px;margin-bottom:16px;border-left:4px solid var(--brand-blue);">
      <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;">
        <div>
          <div style="font-weight:700;font-size:1.05rem;color:var(--text-primary);">${escapeHtml(entry.customerName || 'Shop')}</div>
          ${entry.jobTitle ? `<div style="font-size:0.85rem;color:var(--text-secondary);">${escapeHtml(entry.jobTitle)}</div>` : ''}
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-top:2px;">${fmtDate(finalClockIn)}</div>
        </div>
        <span style="font-size:0.7rem;padding:3px 10px;background:#FEF3C7;color:#92400E;border-radius:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;">Draft</span>
      </div>
    </div>
  `;

  // --- Employee Hours ---
  html += `
    <div style="margin-bottom:16px;">
      <h4 style="margin:0 0 8px 0;font-size:0.9rem;display:flex;align-items:center;gap:6px;color:var(--text-primary);">
        <span class="material-icons" style="font-size:18px;">person</span> Employee Hours
      </h4>
      <div style="overflow-x:auto;">
        <table style="width:100%;border-collapse:collapse;font-size:0.85rem;">
          <thead>
            <tr style="background:var(--bg-secondary);color:var(--text-primary);">
              <th style="text-align:left;padding:6px 10px;font-weight:600;">Name</th>
              <th style="text-align:left;padding:6px 10px;font-weight:600;">Clock In</th>
              <th style="text-align:left;padding:6px 10px;font-weight:600;">Clock Out</th>
              <th style="text-align:right;padding:6px 10px;font-weight:600;">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr style="background:#FEF3C7;color:var(--text-primary);">
              <td style="padding:6px 10px;font-weight:600;">${escapeHtml(person?.name || entry.personName)}</td>
              <td style="padding:6px 10px;">${fmtTime(finalClockIn)}</td>
              <td style="padding:6px 10px;">${fmtEndTime(finalClockIn, finalClockOut)}</td>
              <td style="padding:6px 10px;text-align:right;font-weight:700;">${totalHours} hrs</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `;

  // --- Equipment ---
  html += `
    <div style="margin-bottom:16px;">
      <h4 style="margin:0 0 8px 0;font-size:0.9rem;display:flex;align-items:center;gap:6px;color:var(--text-primary);">
        <span class="material-icons" style="font-size:18px;">local_shipping</span> Equipment
      </h4>
  `;
  if (equipment.length === 0) {
    html += `<div style="padding:8px 0;font-size:0.85rem;color:var(--text-secondary);font-style:italic;">No equipment used</div>`;
  } else {
    html += `
      <div style="overflow-x:auto;">
        <table style="width:100%;border-collapse:collapse;font-size:0.85rem;">
          <thead>
            <tr style="background:var(--bg-secondary);color:var(--text-primary);">
              <th style="text-align:left;padding:6px 10px;font-weight:600;">Name</th>
              <th style="text-align:left;padding:6px 10px;font-weight:600;">Start</th>
              <th style="text-align:left;padding:6px 10px;font-weight:600;">End</th>
              <th style="text-align:right;padding:6px 10px;font-weight:600;">Hours</th>
            </tr>
          </thead>
          <tbody>
    `;
    for (const eq of equipment) {
      const eqHrs = calcRowHours(eq);
      // TASK-1396 — the review screen is a "here's what you're about to submit"
      // summary. A crew row is NOT submitted (it's excluded from the write payload),
      // so label it, or the operator reads a coworker's 10h truck as his own.
      const _isCrew = clockIsCrewEquipmentLine(eq);
      // TASK-9207 — the SECOND display site of the same defect. "· crew (not
      // yours)" was printed over his OWN earlier segment on this ticket, on the
      // screen he reads immediately before signing. Still excluded from the write
      // payload and still dimmed — that part was never wrong — but named honestly.
      const _isOwnPrior = _isCrew && clockIsOwnEarlierShiftLine(eq);
      const _crewTag = _isOwnPrior ? '· earlier shift (already submitted)' : '· crew (not yours)';
      html += `
        <tr style="border-bottom:1px solid var(--border-light);color:var(--text-primary);${_isCrew ? 'opacity:0.75;' : ''}">
          <td style="padding:6px 10px;">${escapeHtml(eq.assetName)}${eq.fleetNumber ? ` <span style="color:var(--text-secondary);">#${escapeHtml(eq.fleetNumber)}</span>` : ''}${_isCrew ? ` <span style="font-size:0.7rem;font-weight:600;color:var(--text-secondary);">${escapeHtml(_crewTag)}</span>` : ''}</td>
          <td style="padding:6px 10px;">${fmtTime(_tltRowPeek(eq, 'startTime'))}</td>
          <td style="padding:6px 10px;">${fmtEndTime(_tltRowPeek(eq, 'startTime'), _tltRowPeek(eq, 'endTime'))}</td>
          <td style="padding:6px 10px;text-align:right;font-weight:600;">${eqHrs.toFixed(2)}h</td>
        </tr>
      `;
    }
    html += '</tbody></table></div>';
  }
  html += '</div>';

  // --- Summary Bar (Downtime + Standby) ---
  const totalDowntime = downtime.reduce((sum, d) => sum + calcRowHours(d), 0);
  const totalStandby = standby.reduce((sum, s) => sum + calcRowHours(s), 0);
  if (downtime.length > 0 || standby.length > 0) {
    html += `
      <div style="display:flex;gap:12px;margin-bottom:16px;flex-wrap:wrap;">
        ${downtime.length > 0 ? `
          <div style="flex:1;min-width:140px;background:var(--bg-elevated);border-radius:var(--radius-sm);padding:10px 14px;">
            <div style="font-size:0.75rem;color:var(--text-secondary);font-weight:600;text-transform:uppercase;">Downtime</div>
            <div style="font-size:1.1rem;font-weight:700;color:var(--text-primary);">${totalDowntime.toFixed(2)} hrs</div>
            <div style="font-size:0.75rem;color:var(--text-secondary);">${downtime.length} entr${downtime.length === 1 ? 'y' : 'ies'}</div>
          </div>
        ` : ''}
        ${standby.length > 0 ? `
          <div style="flex:1;min-width:140px;background:var(--bg-elevated);border-radius:var(--radius-sm);padding:10px 14px;">
            <div style="font-size:0.75rem;color:var(--text-secondary);font-weight:600;text-transform:uppercase;">Standby</div>
            <div style="font-size:1.1rem;font-weight:700;color:var(--text-primary);">${totalStandby.toFixed(2)} hrs</div>
            <div style="font-size:0.75rem;color:var(--text-secondary);">${standby.length} entr${standby.length === 1 ? 'y' : 'ies'}</div>
          </div>
        ` : ''}
      </div>
    `;
  }

  // --- Field Activity ---
  if (dumps > 0 || waterRefills > 0) {
    html += `
      <div style="margin-bottom:16px;">
        <h4 style="margin:0 0 8px 0;font-size:0.9rem;display:flex;align-items:center;gap:6px;color:var(--text-primary);">
          <span class="material-icons" style="font-size:18px;">construction</span> Field Activity
        </h4>
        <div style="display:flex;gap:16px;font-size:0.9rem;color:var(--text-primary);">
          ${dumps > 0 ? `<span>Dumps: <strong>${dumps}</strong></span>` : ''}
          ${waterRefills > 0 ? `<span>Water Refills: <strong>${waterRefills}</strong></span>` : ''}
        </div>
      </div>
    `;
  }

  // --- Field Ticket Items (TASK-9665) — what this close-out will record ---
  const _fiQtys = data.fieldItems || {};
  const _fiRows = (ClockState.fieldTicketLineItems || []).filter(li => li && (Number(_fiQtys[li.id]) || 0) > 0);
  if (_fiRows.length > 0) {
    html += `
      <div style="margin-bottom:16px;">
        <h4 style="margin:0 0 8px 0;font-size:0.9rem;display:flex;align-items:center;gap:6px;color:var(--text-primary);">
          <span class="material-icons" style="font-size:18px;">list_alt</span> Field Ticket Items
        </h4>
        <div style="overflow-x:auto;">
          <table style="width:100%;border-collapse:collapse;font-size:0.85rem;">
            <thead>
              <tr style="background:var(--bg-secondary);color:var(--text-primary);">
                <th style="text-align:left;padding:6px 10px;font-weight:600;">Item</th>
                <th style="text-align:right;padding:6px 10px;font-weight:600;">Qty</th>
                <th style="text-align:right;padding:6px 10px;font-weight:600;">Rate</th>
              </tr>
            </thead>
            <tbody>
              ${_fiRows.map(li => `
                <tr style="border-bottom:1px solid var(--border-color);">
                  <td style="padding:6px 10px;color:var(--text-primary);">${escapeHtml(li.description || '')}</td>
                  <td style="padding:6px 10px;text-align:right;color:var(--text-primary);">${Number(_fiQtys[li.id]) || 0}</td>
                  <td style="padding:6px 10px;text-align:right;color:var(--text-primary);">${_clockPriceBookLineFlags(li).noCharge ? 'N/C' : (li.rate !== undefined ? `$${(Number(li.rate) || 0).toFixed(2)}/${escapeHtml(li.rateUnit || 'ea')}` : `per ${escapeHtml(li.rateUnit || 'ea')}`)}</td>
                </tr>`).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  // --- Mileage ---
  if (mileage) {
    html += `
      <div style="margin-bottom:16px;">
        <h4 style="margin:0 0 8px 0;font-size:0.9rem;display:flex;align-items:center;gap:6px;color:var(--text-primary);">
          <span class="material-icons" style="font-size:18px;">speed</span> Mileage
        </h4>
        <div style="display:flex;gap:16px;font-size:0.9rem;color:var(--text-primary);">
          <span>Start: <strong>${mileage.start}</strong></span>
          <span>Stop: <strong>${mileage.stop}</strong></span>
          <span>Total: <strong>${mileage.total} mi</strong></span>
        </div>
      </div>
    `;
  }

  // --- Potholes ---
  if (potholes.length > 0) {
    html += `
      <div style="margin-bottom:16px;">
        <h4 style="margin:0 0 8px 0;font-size:0.9rem;display:flex;align-items:center;gap:6px;color:var(--text-primary);">
          <span class="material-icons" style="font-size:18px;">pin_drop</span> Line Information
        </h4>
        <div style="font-size:0.85rem;color:var(--text-primary);">${potholes.length} line${potholes.length === 1 ? '' : 's'} logged</div>
        <div style="margin-top:4px;">
    `;
    for (const ph of potholes) {
      const tolStr = (ph.tolFeet > 0 || ph.tolInches > 0) ? `TOL: ${ph.tolFeet}'${ph.tolInches}"` : '';
      const parts = [ph.type, tolStr, ph.pipeSize ? `Pipe: ${ph.pipeSize}"` : '', ph.notes].filter(Boolean);
      html += `<div style="font-size:0.8rem;color:var(--text-secondary);padding:2px 0;">${escapeHtml(parts.join(' | '))}`;
      if ((ph.photos || []).length > 0) {
        html += `<div style="display:flex;gap:4px;margin-top:4px;">`;
        for (const p of ph.photos) {
          html += p.preview
            ? `<div style="width:40px;height:40px;border-radius:4px;overflow:hidden;border:1px solid var(--border-light);"><img src="${p.preview}" style="width:100%;height:100%;object-fit:cover;"></div>`
            : `<div style="width:40px;height:40px;border-radius:4px;border:1px solid var(--border-light);display:flex;align-items:center;justify-content:center;background:var(--bg-elevated);"><span class="material-icons" style="font-size:16px;color:var(--text-secondary);">description</span></div>`;
        }
        html += `</div>`;
      }
      html += `</div>`;
    }
    html += '</div></div>';
  }

  // --- Custom Sections (TASK-848) ---
  // Read-only echo of the custom-section data the driver just entered, so they aren't
  // blind-confirming. Reuses the billing renderer via window.BillingModule (NEVER an ES
  // import — cross-importing ?v=-busted modules creates duplicate instances, pitfall #1).
  // No invoiceOnly filter: the review screen always shows everything, regardless of the
  // template's "Show on Customer Invoice" flag.
  if (data.customSectionData && Object.keys(data.customSectionData).length > 0) {
    const csHtml = window.BillingModule?.buildCustomSectionDataHtml?.(data.customSectionData, escapeHtml) || '';
    if (csHtml) {
      html += `<div style="margin-bottom:16px;">${csHtml}</div>`;
    }
  }

  // --- Ticket Notes ---
  if (ticketNotes.trim()) {
    html += `
      <div style="margin-bottom:16px;">
        <h4 style="margin:0 0 8px 0;font-size:0.9rem;display:flex;align-items:center;gap:6px;color:var(--text-primary);">
          <span class="material-icons" style="font-size:18px;">receipt_long</span> Ticket Notes
          <span style="font-size:0.7rem;font-weight:400;color:var(--text-secondary);">(appears on invoice)</span>
        </h4>
        <div style="background:#FEF3C7;border-radius:var(--radius-sm);padding:10px 14px;font-size:0.9rem;color:var(--text-primary);white-space:pre-wrap;">${escapeHtml(ticketNotes)}</div>
      </div>
    `;
  }

  // --- Shift Notes ---
  html += `<div style="margin-bottom:16px;">
    <h4 style="margin:0 0 8px 0;font-size:0.9rem;display:flex;align-items:center;gap:6px;color:var(--text-primary);">
      <span class="material-icons" style="font-size:18px;">notes</span> Shift Notes
      <span style="font-size:0.7rem;font-weight:400;color:var(--text-secondary);">(internal)</span>
    </h4>`;
  if (shiftNotes.trim()) {
    html += `
      <div style="margin-bottom:8px;">
        <div style="font-size:0.7rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;">You wrote:</div>
        <div style="background:var(--bg-secondary);border-radius:var(--radius-sm);padding:8px 12px;font-size:0.8rem;color:var(--text-secondary);white-space:pre-wrap;">${escapeHtml(shiftNotes)}</div>
      </div>
      <div id="review-shift-notes-container">
        ${window.hasAiPack?.() ? `
          <div style="display:flex;align-items:center;gap:8px;padding:12px;color:var(--text-primary);">
            <span class="btn-spinner"></span>
            <span style="font-size:0.85rem;">Formatting notes...</span>
          </div>
        ` : `
          <textarea id="review-shift-notes-editable" rows="3" style="width:100%;font-size:0.9rem;padding:8px;border:1px solid var(--border-light);border-radius:var(--radius-sm);resize:vertical;">${escapeHtml(shiftNotes)}</textarea>
        `}
      </div>`;
  } else {
    html += `<div id="review-shift-notes-container"><div style="padding:8px 0;font-size:0.85rem;color:var(--text-secondary);font-style:italic;">No shift notes</div></div>`;
  }
  html += '</div>';

  // --- Photos ---
  html += `
    <div style="margin-bottom:16px;">
      <h4 style="margin:0 0 8px 0;font-size:0.9rem;display:flex;align-items:center;gap:6px;color:var(--text-primary);">
        <span class="material-icons" style="font-size:18px;">photo_camera</span> Photos
      </h4>
  `;
  if (photos.length === 0) {
    html += `<div style="padding:8px 0;font-size:0.85rem;color:var(--text-secondary);font-style:italic;">No photos added</div>`;
  } else {
    html += '<div style="display:flex;flex-wrap:wrap;gap:6px;">';
    for (const photo of photos) {
      const isImage = photo.file?.type?.startsWith('image/');
      html += `
        <div style="width:64px;height:64px;border-radius:var(--radius-sm);overflow:hidden;border:1px solid var(--border-light);">
          ${isImage && photo.preview
            ? `<img src="${photo.preview}" style="width:100%;height:100%;object-fit:cover;">`
            : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:var(--bg-elevated);">
                 <span class="material-icons" style="font-size:24px;color:var(--text-secondary);">description</span>
               </div>`
          }
        </div>
      `;
    }
    html += '</div>';
  }
  html += '</div>';

  // --- Confirmation Checkbox ---
  html += `
    <div style="background:#F0FDF4;border:1px solid #BBF7D0;border-radius:var(--radius-md);padding:14px 16px;margin-top:8px;">
      <label style="display:flex;align-items:flex-start;gap:10px;cursor:pointer;margin:0;">
        <input type="checkbox" id="review-confirm-check" onchange="window.ClockModule.toggleReviewConfirm()" style="margin-top:3px;flex-shrink:0;">
        <span style="font-size:0.85rem;color:#166534;line-height:1.4;">
          I confirm the ticket information is accurate and complete. This will finalize the clock-out and create billing line items.
        </span>
      </label>
    </div>
  `;

  return html;
}

function backToForm() {
  ClockState.reviewMode = false;
  stopClockReviewDwellWatch(); // TASK-4616

  // Resume live timer
  startClockOutTimer();
  // TASK-4616 (COD r2) — a clock-out the person TYPED stays typed across Edit.
  // startClockOutTimer() un-pauses, and the restore below puts the typed time back
  // in the field, but the next 30 s tick would then overwrite it with "now" and the
  // typed flag would read as live. Keep the timer paused and the flag set instead.
  if (ClockState.reviewData && ClockState.reviewData.clockOutWasLive === false) {
    ClockState.clockOutTimerPaused = true;
    ClockState.clockOutTypedByHand = true;
  }

  const entry = ClockState.clockOutEntry;
  if (!entry) return;

  const data = ClockState.reviewData;
  if (!data) return;

  const person = data.person;
  const clockIn = data.finalClockIn;
  const now = data.finalClockOut;
  const hours = data.totalHours.toFixed(2);
  const duration = formatDuration(now - clockIn);

  // Rebuild form content
  const bodyEl = document.getElementById('clock-out-body');
  if (bodyEl) bodyEl.innerHTML = buildClockOutModalContent(entry, person, clockIn, now, hours, duration);

  // Restore header
  const headerEl = document.querySelector('#modal-clock-out .modal-header h3');
  if (headerEl) headerEl.textContent = 'Clock Out';

  // Restore footer
  const footerEl = document.querySelector('#modal-clock-out .modal-footer');
  if (footerEl) {
    footerEl.innerHTML = `
      <button class="btn btn-secondary" onclick="window.ClockModule.closeModal('clock-out')">Cancel</button>
      ${shouldBuildTickets() ? '<button class="btn btn-primary" id="btn-save-ticket-form" onclick="window.ClockModule.saveTicketFromForm()"><span class="material-icons">save</span> Save</button>' : ''}
      ${shouldBuildTickets() ? '<button class="btn btn-secondary" onclick="window.ClockModule.switchTicketFromClockOut()" title="Switch to a different customer/job without clocking out"><span class="material-icons">swap_horiz</span> Switch Ticket</button>' : ''}
      <button class="btn btn-danger" id="btn-confirm-clock-out" onclick="window.ClockModule.confirmClockOut()">
        <span class="material-icons">logout</span> Clock Out
      </button>
    `;
  }

  // TASK-1416 — REQUIRED companion to the buildClockOutModalContent() call above.
  // That builder emits `<div id="downtime-list">` / `<div id="standby-list">`
  // containing ONLY the "No … entries" placeholder, and deliberately emits NOTHING
  // when rows exist — the rows are painted by refreshTimeEntryList(). (Equipment is
  // the odd one out: its section inlines renderEquipmentList(), which is why
  // equipment survived this path and downtime/standby did not.) clockOut() pairs the
  // two calls at :10414; backToForm did not, so tapping "Edit" on Review & Confirm
  // repainted both sections as "No downtime entries" / "No standby entries" while
  // ClockState.clockOutDowntime/Standby still held the driver's rows.
  //
  // Display-only — the write path reads the STATE arrays, so nothing was ever lost
  // in Firestore. The harm is that the driver believes it was, re-enters the standby
  // he cannot see, and the ticket double-counts it (live on TITS: #1043, two
  // identical 2.5h DOWNTIME lines 48 min apart; #100308, an 11.9h notes-less
  // DOWNTIME stacked on a real 0.67h one).
  //
  // Runs AFTER the header/footer restore on purpose: if this ever throws, the modal
  // is still a coherent form rather than form-body-under-review-chrome — never trap
  // a driver mid-clock-out (TASK-428).
  // ⚠️ Any future caller of buildClockOutModalContent must repaint these too —
  // pinned by tests/clock-standby-repaint.unit.cjs.
  if (shouldBuildTickets()) {
    refreshTimeEntryList('downtime');
    refreshTimeEntryList('standby');
  }

  // Repopulate form fields from snapshot
  setTimeout(() => {
    // Time adjustments
    if (data.adjustedClockIn) setSplitDateTimeValue('adj-clock-in', toLocalDatetimeStr(data.adjustedClockIn, 'adj-clock-in'));
    if (data.adjustedClockOut) setSplitDateTimeValue('adj-clock-out', toLocalDatetimeStr(data.adjustedClockOut, 'adj-clock-out'));

    // Notes
    const shiftNotesEl = document.getElementById('clock-out-shift-notes');
    if (shiftNotesEl) shiftNotesEl.value = data.shiftNotes;
    const ticketNotesEl = document.getElementById('clock-out-ticket-notes');
    if (ticketNotesEl) ticketNotesEl.value = data.ticketNotes;

    // Field activity
    const dumpsEl = document.getElementById('field-dumps');
    if (dumpsEl) dumpsEl.value = data.dumps;
    const waterEl = document.getElementById('field-water-refills');
    if (waterEl) waterEl.value = data.waterRefills;

    // Field ticket items (TASK-9665) — the quantities typed before Review
    for (const [lineItemId, qty] of Object.entries(data.fieldItems || {})) {
      const qtyEl = document.getElementById(`ft-qty-${lineItemId}`);
      if (qtyEl) qtyEl.value = qty;
    }

    // Mileage
    if (data.mileage) {
      const mileToggle = document.getElementById('mileage-toggle');
      if (mileToggle) {
        mileToggle.checked = true;
        toggleMileage();
        setTimeout(() => {
          const startEl = document.getElementById('mileage-start');
          const stopEl = document.getElementById('mileage-stop');
          if (startEl) startEl.value = data.mileage.start;
          if (stopEl) stopEl.value = data.mileage.stop;
          updateMileageTotal();
        }, 50);
      }
    }
  }, 50);
}

async function confirmClockOutFromReview() {
  const data = ClockState.reviewData;
  if (!data) {
    // TASK-420 — non-silent: lost review state shouldn't vanish a clock-out.
    logClockAudit(ClockState.tenantId, 'CLOCK_OUT_FAIL', {
      source: 'clock-review', reason: 'no_reviewData', online: navigator.onLine,
    });
    window.showToast?.('Couldn’t save your clock-out — please reopen and try again.', 'error');
    return;
  }

  const btn = document.getElementById('btn-confirm-clock-out-review');
  if (!btn) return;
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Saving...';
  // TASK-4616 — measured at the tap, not after the writes. `_confirming` keeps the
  // dwell watch from firing a warning/audit row mid-save.
  data._confirming = true;
  const _confirmDwell = clockReviewDwell(data, Date.now());

  // clock-dst-all — the punch instants were resolved by showClockOutReview and
  // are frozen in reviewData. The ticket rows are resolved here, best effort
  // (COD 1.15.2009): a row that can't be read is left unsaved and the entry is
  // flagged — it never blocks the clock-out.
  let _ticketTimeProblems = [];
  try {
    const tenant = ClockState.tenantId;
    const entry = data.entry;
    const { finalClockIn, finalClockOut, adjustedClockIn, adjustedClockOut, totalHours } = data;

    // TASK-4614 — final authority at the real submit. `data.equipment` is a shallow
    // copy of the SAME row objects showClockOutReview already synced, so this is
    // normally a no-op; it stays because this function, not the review builder, is
    // the last thing that runs before the lines are written. Pinned to
    // `finalClockOut` — the same value written as the employee punch 20 lines down
    // — never `new Date()`, which would bill past the punch the driver reviewed.
    clockSyncClockTrackedEquipment(finalClockIn, finalClockOut, data.equipment);
    {
      const _tt = await _clockResolveTicketTimesBestEffort({ reusePicks: true });
      _ticketTimeProblems = _tt.problems || [];
    }

    // Read final shift notes from editable textarea (picks up AI + user edits)
    let notes = document.getElementById('review-shift-notes-editable')?.value ?? data.shiftNotes;

    // TASK-619 (Lacey) — require a shift note before clock-out when the tenant opts in.
    if (shiftNotesRequired() && !(notes || '').trim()) {
      window.showToast?.('Add a shift note to clock out — what did you do today?', 'error');
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">check_circle</span> Confirm Clock Out';
      data._confirming = false; // TASK-4616 — still on the review; resume the watch
      document.getElementById('review-shift-notes-editable')?.focus();
      return;
    }

    // 1. Update clock entry
    const updateData = {
      clockOut: Timestamp.fromDate(finalClockOut),
      status: 'completed',
      totalHours,
      hours: totalHours, // keep both fields in sync — see fix log 2026-05-07
      clockOutNotes: notes || null,
      updatedAt: Timestamp.now(),
      qbSynced: false
    };
    if (adjustedClockIn) {
      updateData.adjustedClockIn = Timestamp.fromDate(adjustedClockIn);
      updateData.clockIn = Timestamp.fromDate(adjustedClockIn);
    }
    if (adjustedClockOut) updateData.adjustedClockOut = Timestamp.fromDate(adjustedClockOut);
    Object.assign(updateData, _clockOutCodeStamp(entry)); // TASK-9941 F S2
    // clock-dst-all — ticket times left for the office, with what was not saved.
    const _unsavedRowsR = _clockUnsavedRowsSummary([['equipment', data.equipment], ['downtime', data.downtime], ['standby', data.standby]]);
    if (_ticketTimeProblems.length || _unsavedRowsR.length) {
      updateData.needsReview = true;
      updateData.ticketTimesUnsaved = _clockUnsavedRecord(_unsavedRowsR, _ticketTimeProblems);
      if (!_ticketTimeProblems.length) _ticketTimeProblems.push('a ticket time could not be read');
      logClockAudit(tenant, 'CLOCK_OUT_LINE_FAIL', { source: 'clock-review', clockEntryId: entry.id, reason: 'ticket_times_unresolved', unsavedRows: _unsavedRowsR, reasons: _ticketTimeProblems.slice(0, 5) });
    }

    // Mileage
    if (data.mileage) {
      updateData.mileage = data.mileage;
    }

    logClockAudit(tenant, 'CLOCK_OUT_ATTEMPT', {
      source: 'clock-review', clockEntryId: entry.id, personId: entry.personId,
      online: navigator.onLine, totalHours,
    });
    // TASK-1483 — claim captured on the form (showClockOutReview), same write.
    await pvSafeUpdate(doc(db, 'tenants', tenant, 'clockEntries', entry.id), updateData, data.pvFields || null, 'clock-review');

    // Audit: clock-out from review
    logClockAudit(tenant, 'CLOCK_OUT', {
      source: 'clock-review',
      clockEntryId: entry.id,
      personId: entry.personId, personName: entry.personName,
      jobId: entry.jobId, jobTitle: entry.jobTitle,
      totalHours,
      ticketId: entry.ticketId, ticketLineId: entry.ticketLineId,
      rate: entry.rate, role: entry.role,
      hadAdjustedTimes: !!(adjustedClockIn || adjustedClockOut),
      // TASK-4616 — tech-support trail for the frozen clock-out. reviewDwellMinutes
      // is null when the clock-out was typed (not live), so it is never mistaken
      // for unpaid time.
      reviewOpenedAt: data.reviewOpenedAt ? Timestamp.fromDate(data.reviewOpenedAt) : null,
      clockOutWasLive: data.clockOutWasLive === true,
      reviewDwellMinutes: data.clockOutWasLive === true ? _confirmDwell.minutes : null,
      reviewDwellWarned: !!data._dwellWarnShown,
      ..._clockOutCodeAudit(entry), // TASK-9941 F S2
    });
    _clockConsumeClockOutAuth(entry); // TASK-9941 F S2 — one punch per grant

    // =========================================================================
    // TASK-1396/1399 — THE TICKET PHASE IS BEST-EFFORT. THE CLOCK ENTRY IS ALREADY
    // COMPLETED ABOVE. Nothing below may surface to the human as "you couldn't
    // clock out" (TASK-489 pattern, law since TASK-428 stranded PIN crews 14-15h).
    // A ticket/line failure degrades to: your hours are saved, the ticket needs
    // attention - and lands a CLOCK_OUT_LINE_FAIL row for dispatch to find.
    // =========================================================================
    let _ticketPhaseFailed = null;
    let _ticketDetached = false;
    try {
      // 1b. TASK-4577 — this path had NO deleted-ticket handling at all, and it is
      // the one a ticket-building FOUNDER account takes (confirmClockOut returns at
      // showClockOutReview() for those tenants, so its guard never runs here). It
      // therefore wrote hours straight onto a status:'deleted' ticket. Same shared
      // decision as the other three close-out writers.
      if (entry.ticketId) {
        const _reviewTicketSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId));
        if (_reviewTicketSnap.exists()) {
          const _tDecision = await clockApplyDeletedTicketDecision(
            tenant, entry, entry.ticketId, _reviewTicketSnap.data(), 'confirmClockOutFromReview');
          _ticketDetached = !!_tDecision.detached;
        }
      }

      // 2. Update employee ticket line (never round — rounding is for equipment only)
      if (entry.ticketId && entry.ticketLineId) {
        try {
          const _entryPieceRate2 = !isTimeBasedUnit(entry.rateUnit);
          // TASK-9646 S2 / TASK-1653 — EMPLOYEE lines never carry customer money.
          const lineUpdate = {
            toTime: Timestamp.fromDate(finalClockOut),
            hours: totalHours,
            lineAmount: 0,
            updatedAt: Timestamp.now()
          };
          if (_entryPieceRate2) lineUpdate.quantity = lineUpdate.quantity || 0;
          if (adjustedClockIn) lineUpdate.fromTime = Timestamp.fromDate(adjustedClockIn);
          await updateDoc(
            doc(db, 'tenants', tenant, 'tickets', entry.ticketId, 'ticketLines', entry.ticketLineId),
            lineUpdate
          );
        } catch (lineErr) {
          console.warn('[Clock] Could not update ticket line on clock-out (may have been deleted):', lineErr.message);
        }
      }

      // 2b. Overnight day range (workDateEnd) is set by the server from this clock-out
      // (onClockEntryTicketDateRange, TASK-10182).

      // 3-9: Skip ticket line creation if already applied via "Apply" button
      if (entry.ticketId) {
        // TASK-1396 — persist equipment removals made before entering review.
        for (const delId of (ClockState._deletedEquipmentIds || [])) {
          try {
            await deleteDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId, 'ticketLines', delId));
          } catch (delErr) {
            console.warn('[Clock] Could not delete removed equipment line:', delErr.message);
          }
        }
        ClockState._deletedEquipmentIds = [];
        // TASK-4618 — the Apply route. `applyReviewData` writes the lines and sets
        // `data._appliedEquipment`, correct AS OF Apply; the driver then keeps
        // working and Confirms later, so the punch moves and the line would not.
        // Same extend-only push as the non-review path.
        if (data._appliedEquipment) {
          await clockPushFollowsClockEnds(
            tenant, entry.ticketId, entry.id, data.equipment, 'confirmClockOutFromReview:applied');
        }
        if (!data._appliedEquipment) {
          // TASK-427: session-scoped — a re-clock session isn't blocked by a prior
          // session's line; it creates its own (different clockEntryId).
          // TASK-4559 — the MAP, not the id Set. This path used to `continue` on an
          // existing line and write nothing, so on the review flow (the one TITS
          // actually uses) a note typed on a truck tied at clock-IN was dropped
          // every single time. The times are still deliberately left alone here —
          // this branch only carries the note across.
          const existingEqMap = await getExistingEquipmentMap(entry.ticketId, entry.id);
          for (const eq of data.equipment) {
            if (_clockRowUnresolved(eq)) continue; // clock-dst-all — left for the office (flagged)
            if (clockIsCrewEquipmentLine(eq)) continue; // TASK-1396 — never clone a coworker's row
            const _existingEq = existingEqMap.get(eq.assetId);
            if (_existingEq) { // already on ticket (this session)
              const _note = clockReadAssetNote(eq);
              const _upd = {};
              // TASK-4618 — this branch has always deliberately left times alone,
              // and that stays true for every row the driver owns. The one carve-out
              // is a `_followsClock` row, whose window is BY DEFINITION the clock's:
              // it reaches here on Save-in-form → Build Ticket → Confirm, where
              // `data._appliedEquipment` is undefined (reviewData is a fresh object
              // and never copies ClockState's flag) so the applied-path push above
              // does not fire. Same helper, so the arithmetic exists once; merged
              // into `_upd` rather than written separately so it rides along with
              // TASK-4559's note decision in ONE updateDoc.
              Object.assign(_upd, await clockFollowsClockEndPatch(tenant, entry.ticketId, eq, _existingEq) || {});
              if (_note.write) _upd.notes = _note.value;
              if (Object.keys(_upd).length > 0) {
                _upd.updatedAt = Timestamp.now();
                await updateDoc(
                  doc(db, 'tenants', tenant, 'tickets', entry.ticketId, 'ticketLines', _existingEq.docId),
                  _upd
                );
              }
              continue;
            }
            const _pu = !isTimeBasedUnit(eq.rateUnit);
            const rawEqHours = calcRowHours(eq);
            const eqHours = clockEquipmentHours(rawEqHours, _pu, eq.roundToHalfHour); // TASK-4576 — one rule, four writers
            const eqLineData = {
              type: 'EQUIPMENT',
              assetId: eq.assetId,
              assetName: eq.assetName,
              assetType: eq.assetType,
              fleetNumber: eq.fleetNumber,
              vehicleType: eq.vehicleType || '',
              clockEntryId: entry.id || null, // TASK-427: per-session key
              fromTime: Timestamp.fromDate(_tltRowDate(eq, 'startTime')),
              toTime: Timestamp.fromDate(_tltRowDate(eq, 'endTime')),
              hours: eqHours,
              rate: 0, // TASK-9646 S5 — born $0; the SERVER prices it from the price book (ratePending)
              ratePending: true,
              rateUnit: eq.rateUnit || 'Hourly',
              ..._clockEquipInvoiceField(eq), // TASK-10177
              // TASK-9646 S2 — born $0; the SERVER derives hours × rate (amountPending).
              lineAmount: 0,
              ...(_pu ? {} : { amountPending: true }),
              revenueCategory: eq.revenueCategory || '',
              // TASK-4559 — was a hard-coded `null`. The review screen has already
              // replaced the form DOM by the time this runs, so the value comes from
              // the `_noteDraft` captured on the row at `oninput`.
              notes: (() => { const n = clockReadAssetNote(eq); return n.write ? n.value : null; })()
            };
            if (_pu) eqLineData.quantity = 0;
            // TASK-4564 part 1 — persist the decision alongside the evidence.
            if (!_pu && eq.roundToHalfHour) {
              eqLineData.roundedFrom = parseFloat(rawEqHours.toFixed(2));
              eqLineData.roundToHalfHour = true;
            }
            if (eq._offJobDay) eqLineData._billingReviewFlag = _clockOffDayFlag(); // TASK-1635
            // TASK-1674 — guessed rate stamped as a plain string only (see the write
            // site above: firestore.rules pins client-authored _billingReviewFlag shapes).
            if (eq._rateGuess && (Number(eqLineData.rate) || 0) > 0) {
              eqLineData.rateResolvedBy = 'fallback:unmatched-vehicle-type';
            }
            await createTicketLine(entry.ticketId, eqLineData);
          }
          await deduplicateEquipmentLines(entry.ticketId);
        }

        if (!data._appliedDowntime) {
          for (const dt of data.downtime) {
            if (_clockRowUnresolved(dt)) continue; // clock-dst-all — left for the office (flagged)
            if (dt._lineId) continue; // already saved to Firestore
            const dtHours = (dt.startTime && dt.endTime) ? calcRowHours(dt) : 0;
            await createTicketLine(entry.ticketId, {
              type: 'DOWNTIME',
              fromTime: dt.startTime ? Timestamp.fromDate(_tltRowDate(dt, 'startTime')) : null,
              toTime: dt.endTime ? Timestamp.fromDate(_tltRowDate(dt, 'endTime')) : null,
              hours: parseFloat(dtHours.toFixed(2)),
              rate: 0,
              lineAmount: 0,
              notes: dt.notes || null
            });
          }
        }

        if (!data._appliedStandby) {
          for (const sb of data.standby) {
            if (_clockRowUnresolved(sb)) continue; // clock-dst-all — left for the office (flagged)
            if (sb._lineId) continue; // already saved to Firestore
            const sbHours = (sb.startTime && sb.endTime) ? calcRowHours(sb) : 0;
            await createTicketLine(entry.ticketId, {
              type: 'STANDBY',
              fromTime: sb.startTime ? Timestamp.fromDate(_tltRowDate(sb, 'startTime')) : null,
              toTime: sb.endTime ? Timestamp.fromDate(_tltRowDate(sb, 'endTime')) : null,
              hours: parseFloat(sbHours.toFixed(2)),
              rate: 0,
              lineAmount: 0,
              notes: sb.notes || null
            });
          }
        }

        if (!data._appliedFieldActivity && (data.dumps > 0 || data.waterRefills > 0)) {
          const fieldUpdates = { updatedAt: Timestamp.now() };
          if (data.dumps > 0) fieldUpdates.dumps = increment(data.dumps);
          if (data.waterRefills > 0) fieldUpdates.waterRefills = increment(data.waterRefills);
          await updateDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId), fieldUpdates);
        }

        if (!data._appliedPotholes) {
          for (const ph of data.potholes) {
            const tolStr = (ph.tolFeet > 0 || ph.tolInches > 0)
              ? `${ph.tolFeet}'${ph.tolInches}"`
              : '';
            const desc = [
              ph.type ? `Type: ${ph.type}` : '',
              tolStr ? `TOL: ${tolStr}` : '',
              ph.pipeSize ? `Pipe: ${ph.pipeSize}"` : ''
            ].filter(Boolean).join(', ');
            const lineId = await createTicketLine(entry.ticketId, {
              type: 'POTHOLE',
              description: desc,
              gpsLat: ph.gpsLat || null,
              gpsLng: ph.gpsLng || null,
              notes: ph.notes || null,
              lineAmount: 0
            });
            if (lineId && (ph.photos || []).length > 0) {
              try {
                await uploadPotholePhotos(entry.ticketId, lineId, ph.photos);
              } catch (phErr) {
                console.error('[Clock] Pothole photo upload failed:', phErr);
                window.logError?.(phErr, { module: 'clock', operation: 'POTHOLE_PHOTO_UPLOAD', ticketId: entry.ticketId, lineId });
                window.showToast?.('Pothole saved but photos failed to upload', 'error');
              }
            }
          }
        }

        // TASK-1397 — shared invoice notes, merged not appended. `data`
        // carries the review-screen snapshot of the box; the baseline is
        // still the modal-open prefill, so only the typed delta lands.
        await saveInvoiceNotes({
          tenantId: tenant,
          ticketId: entry.ticketId,
          currentText: data.ticketNotes || '',
          syncElId: 'clock-out-ticket-notes'
        });
        data._appliedNotes = true;

        if (!data._appliedPhotos) {
          const _photoResult = await uploadClockOutPhotos(entry.ticketId);
          const _photoMsg = summarizePhotoFailures(_photoResult?.failures);
          if (_photoMsg) window.showToast?.(_photoMsg, 'error');
        }

        // 9b. Work start/stop times
        const _crWsVal = getSplitDateTimeValue('work-start-time');
        const _crWeVal = getSplitDateTimeValue('work-stop-time');
        if (_crWsVal || _crWeVal) {
          const _crTimeUpdate = { updatedAt: Timestamp.now() };
          if (_crWsVal && Number.isFinite(_tltMs(_crWsVal, 'work-start-time'))) _crTimeUpdate.workStartTime = Timestamp.fromDate(_tltDate(_crWsVal, 'work-start-time'));
          if (_crWeVal && Number.isFinite(_tltMs(_crWeVal, 'work-stop-time'))) _crTimeUpdate.workStopTime = Timestamp.fromDate(_tltDate(_crWeVal, 'work-stop-time'));
          await updateDoc(doc(db, 'tenants', tenant, 'tickets', entry.ticketId), _crTimeUpdate);
        }

        // 9c. Custom ticket sections — TASK-4596: this review path never
        // referenced customSectionData at all. The review screen is read-only
        // for custom sections (buildReviewContent only renders a summary via
        // BillingModule.buildCustomSectionDataHtml — there are no live inputs
        // left to re-collect), so the data used here is the snapshot taken at
        // review time (showClockOutReview → data.customSectionData). A driver
        // who reviewed and tapped "Confirm Clock Out" directly — no prior tap
        // of the separate "Apply" button — used to lose every custom-section
        // entry silently while still getting a "Clocked out successfully"
        // toast. Merge-save (not wholesale) for the same shared-ticket reason
        // as every other writer here.
        if (data.customSectionData && Object.keys(data.customSectionData).length > 0) {
          await _clockMergeSaveCustomSectionData(tenant, entry.ticketId, data.customSectionData);
        }

        // 9d. Field Ticket Items — TASK-9665. This review path (the one a
        // ticket-building office account takes) never wrote them; the snapshot
        // taken in showClockOutReview is the record. Upsert, so an earlier
        // Apply tap left lines this call updates, never duplicates.
        await clockApplyFieldTicketItems(entry.ticketId, entry, data.fieldItems || {});

        // 10. Check if all clock entries completed → set READY
        await checkTicketReady(entry.ticketId);

        // 11. Recalculate ticket grand total
        // TASK-4699 — markDirty: this clock-out just wrote the driver's hours,
        // equipment/downtime/standby lines, work times and shift notes. All of
        // it is on the copy the customer was emailed.
        await recalcTicketTotal(entry.ticketId, true);
      }
    } catch (ticketErr) {
      // Never rethrow - the shift is closed; only the billing artifact is incomplete.
      console.error('[Clock] Ticket work failed AFTER the clock entry was completed:', ticketErr);
      _ticketPhaseFailed = ticketErr;
      logClockAudit(tenant, 'CLOCK_OUT_LINE_FAIL', {
        source: 'clock-review', clockEntryId: entry.id, personId: entry.personId,
        ticketId: entry.ticketId || null, online: navigator.onLine,
        message: ticketErr?.message || String(ticketErr),
      });
      window.logError?.(ticketErr, { module: 'clock', operation: 'CLOCK_OUT_TICKET_PHASE', ticketId: entry.ticketId });
    }

    stopClockOutTimer();
    // TASK-1396/1399 - honest, non-alarming messaging. "You are clocked out" is the
    // headline in BOTH branches; the ticket is a separate, recoverable concern.
    if (_ticketPhaseFailed) {
      window.showToast('You are clocked out and your hours are saved. The ticket needs attention - dispatch will finish it.', 'error');
    } else if (_ticketTimeProblems.length) {
      // clock-dst-all — the punch saved; the unreadable ticket times did not.
      window.showToast('You are clocked out and your hours are saved. Some ticket times were not saved (' + _ticketTimeProblems[0] + ') — the entry is flagged for the office to finish.', 'warning', null, 12000);
    } else if (_ticketDetached) {
      // TASK-4577 — see the twin in confirmClockOut.
      window.showToast('You are clocked out and your hours are saved. That ticket was removed by a dispatch correction, so the hours were not put back on it - dispatch will place them.', 'error');
    } else if (ClockState._boundsProblemsAcknowledged > 0) {
      window.showToast('Clocked out. Equipment times outside your shift were saved as entered and sent to dispatch for review.', 'success');
    } else {
      window.showToast('Clocked out successfully', 'success');
    }
    closeModal('clock-out');
    ClockState.clockOutEntry = null;
    ClockState.reviewMode = false;
    ClockState.reviewData = null;
  } catch (error) {
    console.error('[Clock] Clock out error:', error);
    logClockAudit(ClockState.tenantId, 'CLOCK_OUT_FAIL', {
      source: 'clock-review', clockEntryId: data?.entry?.id, reason: 'write_error',
      message: error?.message || String(error), online: navigator.onLine,
    });
    window.showToast('Error clocking out', 'error');
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">check_circle</span> Confirm Clock Out';
    data._confirming = false; // TASK-4616
  }
}

// ============================================
// APPLY REVIEW DATA (save ticket data without clocking out)
// ============================================
async function applyReviewData() {
  // TASK-4596 (integrated-diff COD) — see the two toasts below: the custom-section
  // failure toast used to be followed unconditionally by a green 'Ticket data
  // saved'. Two toasts saying opposite things is how a driver learns to believe
  // the green one, which is the failure this entire day of work has been about.
  let _csdApplyFailed = false;
  const data = ClockState.reviewData;
  if (!data) return;
  if (!data.entry?.ticketId) {
    // TASK-804 — same null-ticket dead-end as saveTicketFromForm.
    window.showToast?.('This shift was clocked in without a billable customer, so no ticket exists yet — set a customer on the shift and save, and we’ll create the ticket.', 'error');
    return;
  }

  const btn = document.getElementById('btn-apply-review');
  if (!btn) return;
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Saving...';

  // clock-dst-all — every typed time this save writes is COMPANY time: refuse an
  // unreadable zone / a nonexistent time, ask which one in the repeated
  // fall-back hour. Before any write — a refusal writes nothing.
  {
    const _rt = await _clockResolveFormTimes();
    if (!_rt.ok) {
      window.showToast?.(_rt.message, 'warning', null, 9000);
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">save</span> Apply';
      return;
    }
  }
  try {
    const tenant = ClockState.tenantId;
    const ticketId = data.entry.ticketId;

    // 0. TASK-4577 — same shared decision as the three other close-out writers.
    // This one also had no status check, so Apply wrote equipment/notes/photos onto
    // a repoint-deleted or already-invoiced ticket. If the ticket is not open for
    // hours the entry is released and the Apply STOPS — writing to a dead ticket is
    // worse than not writing at all, because the hours then look filed.
    const _applyTicketCheck = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId));
    if (_applyTicketCheck.exists()) {
      const _tDecision = await clockApplyDeletedTicketDecision(
        tenant, data.entry, ticketId, _applyTicketCheck.data(), 'applyReviewData');
      if (_tDecision.detached) {
        window.showToast?.('That ticket was removed by a dispatch correction, so nothing was saved onto it. Your hours are safe on the shift — dispatch will place them.', 'error');
        btn.disabled = false;
        btn.innerHTML = '<span class="material-icons">save</span> Apply';
        return;
      }
    }

    // 0b. TASK-1396 — persist equipment removals made in the form before review.
    for (const delId of (ClockState._deletedEquipmentIds || [])) {
      try {
        await deleteDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', delId));
      } catch (delErr) {
        console.warn('[Clock] Could not delete removed equipment line:', delErr.message);
      }
    }
    ClockState._deletedEquipmentIds = [];

    // TASK-4614 — Apply writes the same lines Confirm would, so it needs the same
    // final-authority sync, against the same reviewed finalClockIn/finalClockOut.
    // Without it, hitting Apply then Confirm sets `_appliedEquipment` and the
    // Confirm-side sync is skipped entirely — the leak would survive on that route.
    clockSyncClockTrackedEquipment(data.finalClockIn, data.finalClockOut, data.equipment);

    // 1. Equipment ticket lines (if not already applied) — dedup against existing lines
    if (!data._appliedEquipment && data.equipment.length > 0) {
      // TASK-427: session-scoped extend — only this session's line is extended;
      // a re-clock session creates its own line (different clockEntryId).
      const existingEqMap = await getExistingEquipmentMap(ticketId, data.entry?.id);
      for (const eq of data.equipment) {
        if (clockIsCrewEquipmentLine(eq)) continue; // TASK-1396 — never clone a coworker's row
        const existing = existingEqMap.get(eq.assetId);
        if (existing) {
          // TASK-4559 — note decided first, outside the end-time guard below and
          // outside the per-unit early-out, so neither can swallow it.
          const eqNote = clockReadAssetNote(eq);
          const eqUpd = {};
          if (isTimeBasedUnit(eq.rateUnit)) { // per-unit: no time extension, quantity is manual
            // Equipment already on ticket — extend end time if this is later
            const newEnd = _tltRowDate(eq, 'endTime');
            const existingEnd = existing.toTime?.toDate?.() || new Date(existing.toTime);
            if (newEnd > existingEnd) {
              const existingFromSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', existing.docId));
              const existingFrom = existingFromSnap.data()?.fromTime?.toDate?.() || _tltRowDate(eq, 'startTime');
              const rawHrs = (newEnd - existingFrom) / 3600000;
              const extHours = clockEquipmentHours(rawHrs, false, eq.roundToHalfHour); // TASK-4576
              eqUpd.toTime = Timestamp.fromDate(newEnd);
              eqUpd.hours = extHours;
              // TASK-9646 S2: server-priced
              eqUpd.amountPending = true;
              // TASK-4576 (the twin) — see the same block in confirmClockOut.
              if (eq.roundToHalfHour) {
                eqUpd.roundedFrom = parseFloat(rawHrs.toFixed(2));
                eqUpd.roundToHalfHour = true;
              }
            }
          }
          if (eqNote.write) eqUpd.notes = eqNote.value;
          if (Object.keys(eqUpd).length > 0) {
            eqUpd.updatedAt = Timestamp.now();
            await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', existing.docId), eqUpd);
          }
          continue;
        }
        const _pu = !isTimeBasedUnit(eq.rateUnit);
        // TASK-4576 — this line used to be `_pu ? 0 : parseFloat(calcHoursBetween(...).toFixed(2))`:
        // the ONLY close-out writer that never rounded. Same times, different bill,
        // depending on which button the dispatcher clicked.
        const rawEqHours = calcRowHours(eq);
        const eqHours = clockEquipmentHours(rawEqHours, _pu, eq.roundToHalfHour); // TASK-4576 — one rule, four writers
        const eqLine = {
          type: 'EQUIPMENT',
          assetId: eq.assetId,
          assetName: eq.assetName,
          assetType: eq.assetType,
          fleetNumber: eq.fleetNumber,
          vehicleType: eq.vehicleType || '',
          clockEntryId: data.entry?.id || null, // TASK-427: per-session key
          fromTime: Timestamp.fromDate(_tltRowDate(eq, 'startTime')),
          toTime: Timestamp.fromDate(_tltRowDate(eq, 'endTime')),
          hours: eqHours,
          rate: 0, // TASK-9646 S5 — born $0; the SERVER prices it from the price book (ratePending)
          ratePending: true,
          rateUnit: eq.rateUnit || 'Hourly',
          ..._clockEquipInvoiceField(eq), // TASK-10177
          // TASK-9646 S2 — born $0; the SERVER derives hours × rate (amountPending).
          lineAmount: 0,
          ...(_pu ? {} : { amountPending: true }),
          revenueCategory: eq.revenueCategory || '',
          // TASK-4559 — was a hard-coded `null`; the review DOM is already gone by
          // the time Apply runs, so this reads the captured `_noteDraft`.
          notes: (() => { const n = clockReadAssetNote(eq); return n.write ? n.value : null; })()
        };
        if (_pu) eqLine.quantity = 0;
        // TASK-4576 (+ the TASK-4564 part 1 write this writer never got, because it
        // did no rounding to record). SAME `if` and same shape as the other three
        // writers on purpose: gated on (!perUnit && eq.roundToHalfHour), so it adds
        // no field to any line that carries neither today and therefore no new
        // firestore.rules denial surface. The rounding pins are DRIVER-ARM ONLY
        // (firestore.rules ~4312 create / ~4477 update) and driver-PIN mode never
        // gains the 'clock' view (app.js allowedViews), so this write is evaluated
        // by the unpinned bare-member arm — it cannot trap a driver at clock-out.
        if (!_pu && eq.roundToHalfHour) {
          eqLine.roundedFrom = parseFloat(rawEqHours.toFixed(2));
          eqLine.roundToHalfHour = true;
        }
        if (eq._offJobDay) eqLine._billingReviewFlag = _clockOffDayFlag(); // TASK-1635
        // TASK-1674 — guessed rate stamped as a plain string only (see the write
        // site above: firestore.rules pins client-authored _billingReviewFlag shapes).
        if (eq._rateGuess && (Number(eqLine.rate) || 0) > 0) {
          eqLine.rateResolvedBy = 'fallback:unmatched-vehicle-type';
        }
        await createTicketLine(ticketId, eqLine);
      }
      data._appliedEquipment = true;
    }

    // 2. Downtime ticket lines
    if (!data._appliedDowntime && data.downtime.length > 0) {
      for (const dt of data.downtime) {
        if (dt._lineId) continue; // already saved to Firestore
        const dtHours = (dt.startTime && dt.endTime) ? calcRowHours(dt) : 0;
        await createTicketLine(ticketId, {
          type: 'DOWNTIME',
          fromTime: dt.startTime ? Timestamp.fromDate(_tltRowDate(dt, 'startTime')) : null,
          toTime: dt.endTime ? Timestamp.fromDate(_tltRowDate(dt, 'endTime')) : null,
          hours: parseFloat(dtHours.toFixed(2)),
          rate: 0,
          lineAmount: 0,
          notes: dt.notes || null
        });
      }
      data._appliedDowntime = true;
    }

    // 3. Standby ticket lines
    if (!data._appliedStandby && data.standby.length > 0) {
      for (const sb of data.standby) {
        if (sb._lineId) continue; // already saved to Firestore
        const sbHours = (sb.startTime && sb.endTime) ? calcRowHours(sb) : 0;
        await createTicketLine(ticketId, {
          type: 'STANDBY',
          fromTime: sb.startTime ? Timestamp.fromDate(_tltRowDate(sb, 'startTime')) : null,
          toTime: sb.endTime ? Timestamp.fromDate(_tltRowDate(sb, 'endTime')) : null,
          hours: parseFloat(sbHours.toFixed(2)),
          rate: 0,
          lineAmount: 0,
          notes: sb.notes || null
        });
      }
      data._appliedStandby = true;
    }

    // 4. Field activity (dumps, water refills)
    if (!data._appliedFieldActivity && (data.dumps > 0 || data.waterRefills > 0)) {
      const fieldUpdates = { updatedAt: Timestamp.now() };
      if (data.dumps > 0) fieldUpdates.dumps = increment(data.dumps);
      if (data.waterRefills > 0) fieldUpdates.waterRefills = increment(data.waterRefills);
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), fieldUpdates);
      data._appliedFieldActivity = true;
    }

    // 4b. Field Ticket Items — TASK-9665 (upsert; Confirm afterwards re-runs it as a no-op)
    await clockApplyFieldTicketItems(ticketId, data.entry, data.fieldItems || {});

    // 5. Pothole/line ticket lines + per-line photos
    if (!data._appliedPotholes && data.potholes.length > 0) {
      for (const ph of data.potholes) {
        const tolStr = (ph.tolFeet > 0 || ph.tolInches > 0)
          ? `${ph.tolFeet}'${ph.tolInches}"`
          : '';
        const desc = [
          ph.type ? `Type: ${ph.type}` : '',
          tolStr ? `TOL: ${tolStr}` : '',
          ph.pipeSize ? `Pipe: ${ph.pipeSize}"` : ''
        ].filter(Boolean).join(', ');
        const lineId = await createTicketLine(ticketId, {
          type: 'POTHOLE',
          description: desc,
          gpsLat: ph.gpsLat || null,
          gpsLng: ph.gpsLng || null,
          notes: ph.notes || null,
          lineAmount: 0
        });
        if (lineId && (ph.photos || []).length > 0) {
          try {
            await uploadPotholePhotos(ticketId, lineId, ph.photos);
          } catch (phErr) {
            console.error('[Clock] Pothole photo upload failed:', phErr);
            window.logError?.(phErr, { module: 'clock', operation: 'POTHOLE_PHOTO_UPLOAD', ticketId, lineId });
            window.showToast?.('Pothole saved but photos failed to upload', 'error');
          }
        }
      }
      data._appliedPotholes = true;
    }

    // 6. Ticket notes — shared crew field, merged not appended (TASK-1397).
    await saveInvoiceNotes({
      tenantId: tenant,
      ticketId,
      currentText: data.ticketNotes || '',
      syncElId: 'clock-out-ticket-notes'
    });
    data._appliedNotes = true;

    // 7. Photos
    if (!data._appliedPhotos && data.photos.length > 0) {
      const _photoResult = await uploadClockOutPhotos(ticketId);
      const _photoMsg = summarizePhotoFailures(_photoResult?.failures);
      if (_photoMsg) window.showToast?.(_photoMsg, 'error');
      data._appliedPhotos = true;
    }

    // 7b. Work start/stop times
    const _arWsVal = getSplitDateTimeValue('work-start-time');
    const _arWeVal = getSplitDateTimeValue('work-stop-time');
    if (_arWsVal || _arWeVal) {
      const _arTimeUpdate = { updatedAt: Timestamp.now() };
      if (_arWsVal) _arTimeUpdate.workStartTime = Timestamp.fromDate(_tltDate(_arWsVal, 'work-start-time'));
      if (_arWeVal) _arTimeUpdate.workStopTime = Timestamp.fromDate(_tltDate(_arWeVal, 'work-stop-time'));
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), _arTimeUpdate);
    }

    // 7c. Custom section data — TASK-4596: merge-save (see confirmClockOut's
    // twin) so Apply can't wipe a crew-mate's row on this shared ticket.
    // Wrapped locally (unlike the two clock-out writers) because Apply is a
    // checkpoint-save button: recalcTicketTotal and the clock-in time-adjust
    // persistence below still need to run even if this one write fails, and
    // the unconditional "Ticket data saved" success toast further down would
    // otherwise misreport a failure here as a clean save.
    if (data.customSectionData && Object.keys(data.customSectionData).length > 0) {
      try {
        await _clockMergeSaveCustomSectionData(tenant, ticketId, data.customSectionData);
      } catch (csdErr) {
        console.error('[Clock] Custom section merge-save failed:', csdErr);
        window.logError?.(csdErr, { module: 'clock', operation: 'CSD_MERGE_SAVE', ticketId });
        window.showToast?.('Ticket data saved, but custom section entries failed to save — try Apply again.', 'error');
        _csdApplyFailed = true;
      }
    }

    // 8. Recalc ticket total
    // TASK-4699 — markDirty: applyReviewData writes equipment/downtime/standby
    // lines, pothole lines + their photos, and the dumps/water-refill counts.
    await recalcTicketTotal(ticketId, true);

    // 9. Persist time adjustment to clock entry (if changed)
    const adjInVal = getSplitDateTimeValue('adj-clock-in');
    if (adjInVal && data.entry) {
      const adjClockIn = _tltDate(adjInVal, 'adj-clock-in');
      const origClockIn = data.entry.clockIn?.toDate?.() || new Date(data.entry.clockIn);
      // Only update if changed (more than 30 seconds difference)
      if (Math.abs(adjClockIn - origClockIn) > 30000) {
        const entryId = data.entry.id;
        const adjTimestamp = Timestamp.fromDate(adjClockIn);
        const beforeSnap = { ...data.entry };
        await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entryId), {
          clockIn: adjTimestamp,
          adjustedClockIn: adjTimestamp
        });
        logClockEntryChange(tenant, entryId, beforeSnap, { ...beforeSnap, clockIn: adjTimestamp, adjustedClockIn: adjTimestamp }, 'edit', 'review-shift-clockin-adjust');
        // Update local state so status banner reflects new time
        data.entry.clockIn = adjTimestamp;
        const localEntry = ClockState.activeEntries.find(e => e.id === entryId);
        if (localEntry) localEntry.clockIn = adjTimestamp;
        // Also update the EMPLOYEE ticket line fromTime
        if (data.entry.ticketLineId) {
          await updateDoc(
            doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', data.entry.ticketLineId),
            { fromTime: adjTimestamp, updatedAt: Timestamp.now() }
          );
        }
      }
    }

    // Refresh status displays
    renderActiveList();
    updateSelectedPersonStatus();

    data._dataApplied = true;
    if (!_csdApplyFailed) window.showToast?.('Ticket data saved', 'success');

    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">check</span> Applied';
    btn.classList.remove('btn-secondary');
    btn.classList.add('btn-success');
    setTimeout(() => {
      btn.innerHTML = '<span class="material-icons">save</span> Apply';
      btn.classList.remove('btn-success');
      btn.classList.add('btn-secondary');
    }, 2000);
  } catch (error) {
    console.error('[Clock] Apply review data error:', error);
    window.showToast?.('Error saving ticket data', 'error');
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">save</span> Apply';
  }
}

// ============================================
// SAVE TICKET DATA FROM MAIN FORM (no review screen needed)
// ============================================
async function saveTicketFromForm() {
  const entry = ClockState.clockOutEntry;
  if (!entry?.ticketId) {
    // TASK-804 — a shift clocked in with no billable customer has ticketId:null.
    // Tell the user how to fix it instead of the cryptic dead-end.
    window.showToast?.('This shift was clocked in without a billable customer, so no ticket exists yet — set a customer on the shift and save, and we’ll create the ticket.', 'error');
    return;
  }

  const btn = document.getElementById('btn-save-ticket-form');
  if (!btn) return;
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Saving...';

  // clock-dst-all — every typed time this save writes is COMPANY time: refuse an
  // unreadable zone / a nonexistent time, ask which one in the repeated
  // fall-back hour. Before any write — a refusal writes nothing.
  {
    const _rt = await _clockResolveFormTimes();
    if (!_rt.ok) {
      window.showToast?.(_rt.message, 'warning', null, 9000);
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">save</span> Save';
      return;
    }
  }
  try {
    const tenant = ClockState.tenantId;
    const ticketId = entry.ticketId;

    // 0. TASK-4577 — same shared decision as the other three close-out writers (this
    // block and confirmClockOut's were the two blind auto-restores). If the ticket is
    // not open for hours the entry is released and the Save STOPS rather than writing
    // equipment/notes onto a dead or finalized ticket.
    const _ticketCheck = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId));
    if (_ticketCheck.exists()) {
      const _tDecision = await clockApplyDeletedTicketDecision(
        tenant, entry, ticketId, _ticketCheck.data(), 'saveTicketFromForm');
      if (_tDecision.detached) {
        window.showToast?.('That ticket was removed by a dispatch correction, so nothing was saved onto it. Your hours are safe on the shift — dispatch will place them.', 'error');
        btn.disabled = false;
        btn.innerHTML = '<span class="material-icons">save</span> Save';
        return;
      }
    }

    // 0b. TASK-1396 — persist equipment removals. This is the Save path Steve was
    // using while blocked; without this, removing a row was purely cosmetic and
    // loadSavedEquipment brought it straight back.
    const equipmentDeleted = ClockState._deletedEquipmentIds || [];
    for (const delId of equipmentDeleted) {
      try {
        await deleteDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', delId));
      } catch (delErr) {
        console.warn('[Clock] Could not delete removed equipment line:', delErr.message);
      }
    }
    ClockState._deletedEquipmentIds = [];

    // 1. Equipment ticket lines (dedup + extend end time for shared equipment)
    if (ClockState.clockOutEquipment.length > 0) {
      // TASK-427: session-scoped — extend/match only THIS session's line so a
      // re-clock (2nd session) gets its own equipment line.
      const existingEqMap = await getExistingEquipmentMap(ticketId, entry?.id);
      for (const eq of ClockState.clockOutEquipment) {
        if (clockIsCrewEquipmentLine(eq)) continue; // TASK-1396 — never clone a coworker's row
        const existing = existingEqMap.get(eq.assetId);
        if (existing) {
          // TASK-4559 — this is the re-save path, so it is exactly where an empty
          // box must NOT clobber a note a biller already corrected. Decided before
          // the per-unit early-out so a per-unit row's note is carried too. NOTE:
          // this writer has never had an end-time guard — its times update
          // unconditionally ("handles re-save after edits").
          const eqNote = clockReadAssetNote(eq);
          const eqUpd = {};
          if (isTimeBasedUnit(eq.rateUnit)) { // per-unit: quantity is manual, no time update
            // Update times to match current form values (handles re-save after edits)
            const newStart = _tltRowDate(eq, 'startTime');
            const newEnd = _tltRowDate(eq, 'endTime');
            // TASK-9646 S2 — the line read that fed the client-side rate is gone.
            const rawHrs = (newEnd - newStart) / 3600000;
            const updHours = clockEquipmentHours(rawHrs, false, eq.roundToHalfHour); // TASK-4576
            eqUpd.fromTime = Timestamp.fromDate(newStart);
            eqUpd.toTime = Timestamp.fromDate(newEnd);
            eqUpd.hours = updHours;
            // TASK-9646 S2: server-priced
            eqUpd.amountPending = true;
            // TASK-4576 (the twin) — see the same block in confirmClockOut.
            if (eq.roundToHalfHour) {
              eqUpd.roundedFrom = parseFloat(rawHrs.toFixed(2));
              eqUpd.roundToHalfHour = true;
            }
          }
          if (eqNote.write) eqUpd.notes = eqNote.value;
          if (Object.keys(eqUpd).length > 0) {
            eqUpd.updatedAt = Timestamp.now();
            await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', existing.docId), eqUpd);
          }
          continue;
        }
        const _pu = !isTimeBasedUnit(eq.rateUnit);
        const rawEqHours = calcRowHours(eq);
        const eqHours = clockEquipmentHours(rawEqHours, _pu, eq.roundToHalfHour); // TASK-4576 — one rule, four writers
        // TASK-4559 — same merge decision as the update branch above.
        const _eqNote = clockReadAssetNote(eq);
        const perAssetNote = _eqNote.write ? _eqNote.value : null;
        const eqLine = {
          type: 'EQUIPMENT',
          assetId: eq.assetId,
          assetName: eq.assetName,
          assetType: eq.assetType,
          fleetNumber: eq.fleetNumber,
          vehicleType: eq.vehicleType || '',
          clockEntryId: entry?.id || null, // TASK-427: per-session key
          fromTime: Timestamp.fromDate(_tltRowDate(eq, 'startTime')),
          toTime: Timestamp.fromDate(_tltRowDate(eq, 'endTime')),
          hours: eqHours,
          rate: 0, // TASK-9646 S5 — born $0; the SERVER prices it from the price book (ratePending)
          ratePending: true,
          rateUnit: eq.rateUnit || 'Hourly',
          ..._clockEquipInvoiceField(eq), // TASK-10177
          // TASK-9646 S2 — born $0; the SERVER derives hours × rate (amountPending).
          lineAmount: 0,
          ...(_pu ? {} : { amountPending: true }),
          revenueCategory: eq.revenueCategory || '',
          notes: perAssetNote
        };
        if (_pu) eqLine.quantity = 0;
        // TASK-4564 part 1 — persist the decision alongside the evidence. Safe on the
        // rules side: clock.js close-out is evaluated by the bare-MEMBER create arm
        // (driver-PIN mode never gets the `clock` view — app.js allowedViews), and
        // that arm carries no rounding pin. The driver-arm pin's absence check
        // already covers `roundedFrom`, which this same `if` has written since
        // TASK-1410 — so this adds ZERO new denial surface anywhere.
        if (!_pu && eq.roundToHalfHour) {
          eqLine.roundedFrom = parseFloat(rawEqHours.toFixed(2));
          eqLine.roundToHalfHour = true;
        }
        if (eq._offJobDay) eqLine._billingReviewFlag = _clockOffDayFlag(); // TASK-1635
        // TASK-1674 — guessed rate stamped as a plain string only (see the write
        // site above: firestore.rules pins client-authored _billingReviewFlag shapes).
        if (eq._rateGuess && (Number(eqLine.rate) || 0) > 0) {
          eqLine.rateResolvedBy = 'fallback:unmatched-vehicle-type';
        }
        await createTicketLine(ticketId, eqLine);
      }
      await deduplicateEquipmentLines(ticketId);
      ClockState._appliedEquipment = true;
    }

    // 2. Downtime ticket lines — per-entry save/update/delete
    const downtimeDeleted = ClockState._deletedDowntimeIds || [];
    for (const delId of downtimeDeleted) {
      await deleteDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', delId));
    }
    for (const dt of ClockState.clockOutDowntime) {
      const fromTime = dt.startTime ? Timestamp.fromDate(_tltRowDate(dt, 'startTime')) : null;
      const toTime = dt.endTime ? Timestamp.fromDate(_tltRowDate(dt, 'endTime')) : null;
      const dtHours = (dt.startTime && dt.endTime) ? calcRowHours(dt) : 0;
      const lineData = {
        type: 'DOWNTIME', fromTime, toTime,
        hours: parseFloat(dtHours.toFixed(2)), rate: 0, lineAmount: 0,
        notes: dt.notes || null
      };
      if (dt._lineId && dt._dirty) {
        await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', dt._lineId), _clockLineUpdatePayload(lineData));
        dt._dirty = false;
      } else if (!dt._lineId) {
        const newId = await createTicketLine(ticketId, lineData);
        dt._lineId = newId;
        dt._saved = true;
        dt._dirty = false;
      }
    }
    ClockState._deletedDowntimeIds = [];

    // 3. Standby ticket lines — per-entry save/update/delete
    const standbyDeleted = ClockState._deletedStandbyIds || [];
    for (const delId of standbyDeleted) {
      await deleteDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', delId));
    }
    for (const sb of ClockState.clockOutStandby) {
      const fromTime = sb.startTime ? Timestamp.fromDate(_tltRowDate(sb, 'startTime')) : null;
      const toTime = sb.endTime ? Timestamp.fromDate(_tltRowDate(sb, 'endTime')) : null;
      const sbHours = (sb.startTime && sb.endTime) ? calcRowHours(sb) : 0;
      const lineData = {
        type: 'STANDBY', fromTime, toTime,
        hours: parseFloat(sbHours.toFixed(2)), rate: 0, lineAmount: 0,
        notes: sb.notes || null
      };
      if (sb._lineId && sb._dirty) {
        await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', sb._lineId), _clockLineUpdatePayload(lineData));
        sb._dirty = false;
      } else if (!sb._lineId) {
        const newId = await createTicketLine(ticketId, lineData);
        sb._lineId = newId;
        sb._saved = true;
        sb._dirty = false;
      }
    }
    ClockState._deletedStandbyIds = [];

    // 4. Field activity (dumps, water refills) — always save (even 0) so reopening Build Ticket restores values
    const dumps = parseInt(document.getElementById('field-dumps')?.value) || 0;
    const waterRefills = parseInt(document.getElementById('field-water-refills')?.value) || 0;
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
      dumps, waterRefills, updatedAt: Timestamp.now()
    });

    // 4a. Field Ticket Items — TASK-9665: Save records the Price Book quantities
    // too (upsert; a later Confirm updates the same lines).
    await clockApplyFieldTicketItems(ticketId, entry, _clockReadFieldItemQtys());

    // 4b. Save shift notes — to THIS person's clock entry ONLY. 🔴 TASK-9693:
    // never to the shared ticket doc. A ticket is one crew-wide doc every
    // driver-PIN session can read, so a ticket-level copy published each
    // operator's note to the whole crew (and the last saver overwrote the
    // previous one). The per-person draft is what restores the box on reopen
    // (TASK-1327); clock-out commits it to clockOutNotes.
    const shiftNotes = document.getElementById('clock-out-shift-notes')?.value || '';
    if (entry?.id) {
      await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entry.id), {
        draftShiftNotes: shiftNotes, updatedAt: Timestamp.now()
      });
    }

    // 4c. Save work start/stop times to ticket doc
    const _wsVal = getSplitDateTimeValue('work-start-time');
    const _weVal = getSplitDateTimeValue('work-stop-time');
    if (_wsVal || _weVal) {
      const timeUpdate = { updatedAt: Timestamp.now() };
      if (_wsVal) timeUpdate.workStartTime = Timestamp.fromDate(_tltDate(_wsVal, 'work-start-time'));
      if (_weVal) timeUpdate.workStopTime = Timestamp.fromDate(_tltDate(_weVal, 'work-stop-time'));
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), timeUpdate);
    }

    // 5. Pothole/line ticket lines + per-line photos
    if (!ClockState._appliedPotholes && ClockState.clockOutPotholes.length > 0) {
      for (const ph of ClockState.clockOutPotholes) {
        const tolStr = (ph.tolFeet > 0 || ph.tolInches > 0)
          ? `${ph.tolFeet}'${ph.tolInches}"`
          : '';
        const desc = [
          ph.type ? `Type: ${ph.type}` : '',
          tolStr ? `TOL: ${tolStr}` : '',
          ph.pipeSize ? `Pipe: ${ph.pipeSize}"` : ''
        ].filter(Boolean).join(', ');
        const lineId = await createTicketLine(ticketId, {
          type: 'POTHOLE',
          description: desc,
          gpsLat: ph.gpsLat || null,
          gpsLng: ph.gpsLng || null,
          notes: ph.notes || null,
          lineAmount: 0
        });
        if (lineId && (ph.photos || []).length > 0) {
          try {
            await uploadPotholePhotos(ticketId, lineId, ph.photos);
          } catch (phErr) {
            console.error('[Clock] Pothole photo upload failed:', phErr);
            window.logError?.(phErr, { module: 'clock', operation: 'POTHOLE_PHOTO_UPLOAD', ticketId, lineId });
            window.showToast?.('Pothole saved but photos failed to upload', 'error');
          }
        }
      }
      ClockState._appliedPotholes = true;
    }

    // 6. Ticket notes — shared crew field (TASK-1397). The old branch here
    // was the clobber: once _appliedNotes was set it blind-overwrote the
    // whole field from this modal's textarea, erasing anything a coworker
    // had saved in the meantime. Now every save merges the typed delta onto
    // the CURRENT stored value inside a transaction, so re-saving is safe
    // and a concurrent writer cannot be silently erased.
    await saveInvoiceNotes({
      tenantId: tenant,
      ticketId,
      currentText: document.getElementById('clock-out-ticket-notes')?.value || '',
      syncElId: 'clock-out-ticket-notes'
    });
    ClockState._appliedNotes = true;

    // 7. Photos (non-fatal — ticket data already saved)
    if (!ClockState._appliedPhotos && ClockState.clockOutPhotos.length > 0) {
      try {
        const _photoResult = await uploadClockOutPhotos(ticketId);
        const _photoMsg = summarizePhotoFailures(_photoResult?.failures);
        if (_photoMsg) window.showToast?.(_photoMsg, 'error');
      } catch (photoErr) {
        console.error('[Clock] Clock-out photo upload failed:', photoErr);
        window.logError?.(photoErr, { module: 'clock', operation: 'CLOCKOUT_PHOTO_UPLOAD', ticketId });
        window.showToast?.(`Ticket saved but photo save failed: ${photoErr?.code || photoErr?.message || 'unknown'}`, 'error');
      }
      ClockState._appliedPhotos = true;
    }

    // 8. Persist clock-in time adjustment (if changed)
    const adjInVal = getSplitDateTimeValue('adj-clock-in');
    if (adjInVal && entry) {
      const adjClockIn = _tltDate(adjInVal, 'adj-clock-in');
      const origClockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
      if (Math.abs(adjClockIn - origClockIn) > 30000) {
        const adjTimestamp = Timestamp.fromDate(adjClockIn);
        await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entry.id), {
          clockIn: adjTimestamp,
          adjustedClockIn: adjTimestamp
        });
        entry.clockIn = adjTimestamp;
        const localEntry = ClockState.activeEntries.find(e => e.id === entry.id);
        if (localEntry) localEntry.clockIn = adjTimestamp;
        if (entry.ticketLineId) {
          await updateDoc(
            doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', entry.ticketLineId),
            { fromTime: adjTimestamp, updatedAt: Timestamp.now() }
          );
        }
      }
    }

    // 9. Recalc ticket total
    // TASK-4699 — markDirty: this is the driver's Build Ticket save — lines,
    // field activity, shift notes, work start/stop, pothole photos.
    await recalcTicketTotal(ticketId, true);

    // Refresh status displays
    renderActiveList();
    updateSelectedPersonStatus();
    // Re-render time entry lists to update saved badges
    refreshTimeEntryList('downtime');
    refreshTimeEntryList('standby');

    window.showToast?.('Ticket data saved', 'success');

    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">check</span> Saved';
    btn.classList.remove('btn-primary');
    btn.classList.add('btn-success');
    setTimeout(() => {
      btn.innerHTML = '<span class="material-icons">save</span> Save';
      btn.classList.remove('btn-success');
      btn.classList.add('btn-primary');
    }, 2000);
  } catch (error) {
    console.error('[Clock] Save ticket from form error:', error);
    window.showToast?.('Error saving ticket data', 'error');
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">save</span> Save';
  }
}

function toggleReviewConfirm() {
  const checked = document.getElementById('review-confirm-check')?.checked;
  const btn = document.getElementById('btn-confirm-clock-out-review');
  if (btn) btn.disabled = !checked;
}

function revertShiftNotes() {
  const textarea = document.getElementById('review-shift-notes-editable');
  if (textarea) textarea.value = ClockState.originalShiftNotes;
  // Hide AI badge and revert button
  const revertBtn = document.getElementById('btn-revert-notes');
  if (revertBtn) revertBtn.style.display = 'none';
  const container = document.getElementById('review-shift-notes-container');
  const badge = container?.querySelector('span[style*="Formatted"]');
  if (badge) badge.textContent = 'Original';
}

async function checkTicketReady(ticketId) {
  // Ticket stays draft until biller explicitly marks it ready
  // (Previously auto-promoted to 'ready' when last person clocked out)
}

// TASK-4699 — "the customer is holding an out-of-date copy" is STAMPED, NEVER
// derived from `updatedAt`. toggleDriverEditLock() bumps updatedAt without
// changing one thing a customer can see, so an `updatedAt > sentAt` comparison
// false-flags every lock toggle. Do not "simplify" this to a comparison.
//
// `markDirty` is OPT-IN per call site, and that is the whole design: the caller
// is the only thing that knows whether the write it just made changed what the
// customer SEES. recalcTicketTotal() itself is also reached from read-only heal
// paths (billing.js viewTicket, gateway recalc) that change nothing, so a stamp
// applied unconditionally here would light the flag on a ticket nobody touched —
// noise, and a flag Lacey stops trusting is worse than no flag at all.
//
// Stamped UNCONDITIONALLY when opted in, with NO read of the ticket first. That
// is deliberate on the driver lane: a gating read is an extra round trip on the
// exact path TASK-1642's 11-minute hang died on. It costs nothing to stamp a
// never-sent ticket — _ticketDeliveryState() (billing.js) only reads dirtyAt
// when `ticketSendLastAt` exists, and appendTicketSendLog (functions) clears the
// field with the Admin SDK on every outbound send. So a pre-send stamp is
// invisible and then wiped.
//
// It rides the header write this function ALREADY makes — no new write, no new
// failure mode, and the stamp cannot land without the content change or vice
// versa. The enclosing try/catch here already swallows, so a stamp can never
// fail a driver's save (TASK-428).
async function recalcTicketTotal(ticketId, markDirty = false) {
  // Fallback to AppState — these helpers are exported and called from
  // billing.js viewTicket before Clock module's onTenantReady fires, so
  // ClockState.tenantId can be null at first call. Crashed prod 2026-05-07
  // 1:06 PM with "Cannot read properties of null (reading 'indexOf')" from
  // Firestore's collection() ResourcePath.fromString.
  const tenant = ClockState.tenantId || window.AppState?.tenant;
  if (!tenant) {
    console.warn('[recalcTicketTotal] no tenant — skipping');
    return;
  }
  try {
    // TASK-1666 converged this writer's money on billing.js's tax-aware helper;
    // TASK-9646 S2 then moved the money to the SERVER entirely (see below), so
    // only the EMPLOYEE hours / crew count are read here.
    const linesSnap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
    const lines = linesSnap.docs.map(d => d.data());
    let totalHours = 0;
    const employeeIds = new Set();
    for (const line of lines) {
      if (line.type === 'EMPLOYEE') {
        totalHours += line.hours || 0;
        if (line.personId) employeeIds.add(line.personId);
      }
    }
    const update = {
      totalHours: parseFloat(totalHours.toFixed(2)),
      employeeCount: employeeIds.size,
      updatedAt: Timestamp.now()
    };
    // TASK-9646 S2 — NO MONEY HERE. grandTotal / subtotal / taxAmount /
    // taxableSubtotal are computed on the SERVER on every ticketLine write
    // (onTicketLineWrite → ticketTotalsReconcile, the same computeTicketTotals
    // the invoice uses), and a header write that still carries them is
    // re-derived by onTicketHeaderTotals. This writer used to compute them from
    // the line amounts it could read; once rates leave non-billing sessions
    // that would silently write $0. Hours / crew count are not money and stay.
    // TASK-4699 — see the header comment. Opt-in, rides this existing write.
    if (markDirty) update.ticketEmailDirtyAt = Timestamp.now();
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), update);
  } catch (error) {
    console.error('[Clock] Error recalculating ticket total:', error);
  }
}

// ============================================
// WORKFORCE ADJUSTMENTS
// ============================================
const ADJ_CATEGORIES = {
  'per-diem': ['Per-Diem'],
  // TASK-950: 'Health Insurance' removed as a preset — it duplicates the dedicated
  // Health Insurance field (person.hrBCBS, Edit Employment Info) and the payroll CSV
  // exports BOTH as separate columns, so entering it in both places double-withholds.
  // hrBCBS is the canonical health-insurance field; 'Custom' still covers any tenant
  // that wants it as a flexible per-period deduction instead.
  'deduction': ['Fuel', 'Child Support', 'Car Payment', 'Tool Purchase', 'Uniform', 'Phone', 'Loan Repayment', 'Custom'],
  'bonus': ['Safety Bonus', 'Completion Bonus', 'Referral Bonus', 'Holiday Bonus', 'Performance Bonus', 'Custom'],
  'reimbursement': ['Fuel', 'Travel', 'Mileage', 'Meals', 'Lodging', 'Tools/Equipment', 'Custom']
};

function updateAdjCategoryDropdown() {
  const type = document.getElementById('adj-type')?.value;
  const catWrap = document.getElementById('adj-category-wrap');
  const customWrap = document.getElementById('adj-custom-wrap');
  if (!catWrap) return;

  const cats = ADJ_CATEGORIES[type] || [];
  if (cats.length <= 1) {
    // Per-diem — no category needed
    catWrap.style.display = 'none';
    if (customWrap) customWrap.style.display = 'none';
    updateAdjPerDiemWarning(); // TASK-4628 — type just became per-diem
    return;
  }
  catWrap.style.display = '';
  const sel = document.getElementById('adj-category');
  if (sel) {
    sel.innerHTML = cats.map(c => `<option value="${c}">${c}</option>`).join('');
  }
  if (customWrap) customWrap.style.display = 'none';
  updateAdjPerDiemWarning(); // TASK-4628 — type left per-diem; clear the warning
}

function onAdjCustomerChange() {
  const custId = document.getElementById('adj-customer')?.value;
  const jobWrap = document.getElementById('adj-job-wrap');
  const jobSelect = document.getElementById('adj-job');
  if (!jobWrap || !jobSelect) return;
  if (!custId) {
    jobWrap.style.display = 'none';
    jobSelect.innerHTML = '<option value="">None</option>';
    return;
  }
  // TASK-1082 — relevance-scored + capped, archived excluded (was: every
  // non-archived job dumped, incl. long-stale ones).
  const relevant = getRelevantAdminJobs(custId, null);
  if (relevant.length === 0) {
    jobWrap.style.display = 'none';
    jobSelect.innerHTML = '<option value="">None</option>';
    return;
  }
  jobWrap.style.display = '';
  jobSelect.innerHTML = buildRelevantJobOptions(custId, null, 'None');
}

function onAdjCategoryChange() {
  const val = document.getElementById('adj-category')?.value;
  const customWrap = document.getElementById('adj-custom-wrap');
  if (customWrap) customWrap.style.display = val === 'Custom' ? '' : 'none';
}

async function showAddAdjustment() {
  // TASK-360 — workforce admin surface.
  if (!_assertWorkforceAccess('showAddAdjustment')) return;
  // TASK-4637 — payAdjustments create is admin/billing per firestore.rules; the
  // button is hidden for everyone else, but this is exported on
  // window.ClockModule and reachable from a console. Refuse by name rather than
  // opening a form whose Save can only fail.
  if (!_clockCanCreatePayAdjustment()) {
    console.warn('[Clock] showAddAdjustment blocked — payAdjustments create is admin/billing-only (firestore.rules).');
    window.showToast?.('Adding a pay adjustment needs the Billing module (not view only) — ask the owner.', 'error');
    return;
  }
  const personOptions = '<option value="">Select Employee</option>' + ClockState.people
    .filter(p => p.active !== false && !p.terminated)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    .map(p => `<option value="${p.id}">${escapeHtml(p.name)}</option>`)
    .join('');

  const content = document.getElementById('workforce-content');
  if (!content) return;

  const existingForm = document.getElementById('adj-form-inline');
  if (existingForm) { existingForm.remove(); return; }

  const formDiv = document.createElement('div');
  formDiv.id = 'adj-form-inline';
  formDiv.style.cssText = 'background:var(--bg-elevated);border-radius:var(--radius-md);padding:var(--spacing-md);margin-bottom:var(--spacing-md);border:1px solid var(--border-light);';
  // P0 2026-10-06 — customers only (vendors share the collection).
  // TASK-9548 slice 3 — Shop/Office under "Internal — not billed", same as every
  // other Workforce customer picker (_clockCustomerOptionsHtml drops vendors).
  const custOptions = '<option value="">None (general)</option>' + _clockCustomerOptionsHtml(ClockState.customers || []);
  const custHint = _clockCustomerHintHtml(ClockState.customers || []);

  formDiv.innerHTML = `
    <div style="display:flex;gap:var(--spacing-sm);flex-wrap:wrap;align-items:flex-end;">
      <div class="form-group" style="flex:1;min-width:150px;margin:0;">
        <label style="font-size:0.8rem;">Employee</label>
        <select id="adj-person" class="form-control" onchange="window.ClockModule.updateAdjPerDiemWarning()">${personOptions}</select>
      </div>
      <div class="form-group" style="width:140px;margin:0;">
        <label style="font-size:0.8rem;">Type</label>
        <select id="adj-type" class="form-control" onchange="window.ClockModule.updateAdjCategoryDropdown()">
          <option value="per-diem">Per-Diem</option>
          <option value="bonus">Bonus</option>
          <option value="deduction">Deduction</option>
          <option value="reimbursement">Reimbursement</option>
        </select>
      </div>
      <div class="form-group" id="adj-category-wrap" style="width:160px;margin:0;display:none;">
        <label style="font-size:0.8rem;">Category</label>
        <select id="adj-category" class="form-control" onchange="window.ClockModule.onAdjCategoryChange()">
        </select>
      </div>
      <div class="form-group" id="adj-custom-wrap" style="flex:1;min-width:120px;margin:0;display:none;">
        <label style="font-size:0.8rem;">Custom Category</label>
        <input type="text" id="adj-custom-category" class="form-control" placeholder="Enter category name">
      </div>
      <div class="form-group" style="width:100px;margin:0;">
        <label style="font-size:0.8rem;">Amount ($)</label>
        <input type="number" id="adj-amount" class="form-control" min="0" step="0.01" placeholder="0.00">
      </div>
      <div class="form-group" id="adj-customer-wrap" style="flex:1;min-width:150px;margin:0;">
        <label style="font-size:0.8rem;">Customer <span style="font-size:0.7rem;color:var(--text-secondary);">(optional)</span></label>
        <select id="adj-customer" class="form-control" onchange="window.ClockModule.onAdjCustomerChange()">${custOptions}</select>
        ${custHint}
      </div>
      <div class="form-group" id="adj-job-wrap" style="flex:1;min-width:140px;margin:0;display:none;">
        <label style="font-size:0.8rem;">Job <span style="font-size:0.7rem;color:var(--text-secondary);">(optional)</span></label>
        <select id="adj-job" class="form-control"><option value="">None</option></select>
      </div>
      <div class="form-group" style="flex:1;min-width:120px;margin:0;">
        <label style="font-size:0.8rem;">Notes</label>
        <input type="text" id="adj-notes" class="form-control" placeholder="Optional">
      </div>
      <div class="form-group" style="margin:0;display:flex;align-items:center;gap:6px;padding-bottom:2px;">
        <label style="font-size:0.8rem;display:flex;align-items:center;gap:4px;cursor:pointer;white-space:nowrap;">
          <input type="checkbox" id="adj-recurring"> <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">repeat</span> Recurring
        </label>
      </div>
      <button class="btn btn-primary btn-sm" onclick="window.ClockModule.saveAdjustment()" style="height:36px;">Save</button>
    </div>
    <!-- TASK-4628 — filled in by updateAdjPerDiemWarning() when the chosen
         employee already has an HR per-diem rate. This is the cheapest place to
         stop the conflict: before the row exists. -->
    <div id="adj-perdiem-warning" style="display:none;"></div>
  `;
  content.insertBefore(formDiv, content.firstChild);
  updateAdjCategoryDropdown();
}

// TASK-4628 — a manual per-diem row for someone who already has an HR per-diem
// RATE will never pay (TASK-939 precedence: rate wins, manual is fallback-only).
// Say so on the form, at the moment the person + type are chosen, rather than
// letting them save a row that quietly does nothing for months.
function updateAdjPerDiemWarning() {
  const box = document.getElementById('adj-perdiem-warning');
  if (!box) return;
  const hit = _adjPerDiemConflictFromForm();
  if (!hit) { box.style.display = 'none'; box.innerHTML = ''; return; }
  box.style.display = '';
  box.innerHTML = `
    <div style="display:flex;gap:8px;align-items:flex-start;background:rgba(217,119,6,0.08);border:1px solid rgba(217,119,6,0.4);border-radius:8px;padding:8px 10px;margin-top:var(--spacing-sm);">
      <span class="material-icons" style="color:#B45309;font-size:18px;">report_problem</span>
      <div style="flex:1;min-width:0;font-size:0.8rem;color:var(--text-primary);line-height:1.4;">
        <strong>This per-diem row will not pay.</strong>
        ${escapeHtml(hit.name)} already has a per-diem rate of $${hit.rate.toFixed(2)}/day in HR &rarr; Edit Employment Info.
        The rate takes precedence &mdash; per diem is paid as $${hit.rate.toFixed(2)} &times; each day of 8h or more, and manual per-diem rows are ignored while a rate is set.
        Clear the HR rate first if you want this amount to be what pays.
      </div>
    </div>`;
}

// Shared by the live form warning and the save-time confirm so they can never
// disagree. Returns { name, rate } or null.
function _adjPerDiemConflictFromForm() {
  const type = document.getElementById('adj-type')?.value;
  if (String(type || '').toLowerCase() !== 'per-diem') return null;
  const personId = document.getElementById('adj-person')?.value;
  if (!personId) return null;
  const person = getPersonForReport(personId);
  if (!person) return null;
  // Any positive amount triggers the same precedence — probe with 1 so the
  // warning shows before an amount has been typed.
  const hit = perDiemManualSuperseded(person, 1);
  return hit ? { name: person.name || 'This employee', rate: hit.rate } : null;
}

async function saveAdjustment() {
  // TASK-360 — workforce admin surface (pay adjustments / per-diem / etc.).
  if (!_assertWorkforceAccess('saveAdjustment')) return;
  // TASK-4637 — mirror the rules' create arm here too (admin/billing, and never
  // on a compliance-only tenant). Without this the write reaches Firestore and
  // comes back as a generic "Error saving adjustment" that names nothing.
  if (!_clockCanCreatePayAdjustment()) {
    console.warn('[Clock] saveAdjustment blocked — payAdjustments create is admin/billing-only (firestore.rules).');
    window.showToast?.('Adding a pay adjustment needs the Billing module (not view only) — ask the owner.', 'error');
    return;
  }
  const personId = document.getElementById('adj-person')?.value;
  const type = document.getElementById('adj-type')?.value;
  const amount = parseFloat(document.getElementById('adj-amount')?.value || '0');
  const notes = document.getElementById('adj-notes')?.value?.trim();
  const categorySelect = document.getElementById('adj-category')?.value;
  const customCategory = document.getElementById('adj-custom-category')?.value?.trim();
  const category = type === 'per-diem' ? 'Per-Diem'
    : categorySelect === 'Custom' ? (customCategory || 'Custom')
    : (categorySelect || null);

  if (!personId || amount <= 0) {
    window.showToast('Please fill in employee and amount', 'error');
    return;
  }

  // TASK-4628 — last stop before a row that will never pay gets written. The
  // inline warning above may have been scrolled past; this one cannot be.
  // Saving anyway is allowed (the office may be about to clear the HR rate) —
  // it just can no longer happen by accident.
  const _pdConflict = _adjPerDiemConflictFromForm();
  if (_pdConflict && !confirm(
    `${_pdConflict.name} already has a per-diem RATE of $${_pdConflict.rate.toFixed(2)}/day in HR.\n\n`
    + `While that rate is set it takes precedence: per diem pays $${_pdConflict.rate.toFixed(2)} x each day of 8h or more, and this manual $${amount.toFixed(2)} row will pay NOTHING.\n\n`
    + 'Save it anyway?'
  )) return;

  const customerId = document.getElementById('adj-customer')?.value || null;
  const jobId = document.getElementById('adj-job')?.value || null;
  const customerName = customerId ? (ClockState.customers.find(c => c.id === customerId)?.name || null) : null;
  const jobTitle = jobId ? ((ClockState.allJobs || []).find(j => j.id === jobId)?.title || null) : null;
  const recurring = document.getElementById('adj-recurring')?.checked || false;

  const person = ClockState.people.find(p => p.id === personId);
  try {
    const adjDoc = {
      personId,
      personName: person?.name || 'Unknown',
      type,
      category: category || null,
      amount,
      recurring,
      date: Timestamp.now(),
      notes: notes || null,
      createdAt: Timestamp.now(),
      createdBy: window.AppState?.user?.email || 'unknown'
    };
    if (customerId) { adjDoc.customerId = customerId; adjDoc.customerName = customerName; }
    if (jobId) { adjDoc.jobId = jobId; adjDoc.jobTitle = jobTitle; }
    const adjRef = await addDoc(collection(db, 'tenants', ClockState.tenantId, 'payAdjustments'), adjDoc);

    // Sync to costEntries for BI if tied to a customer/job
    if (customerId && type !== 'deduction') {
      const biCategory = type === 'per-diem' ? 'per_diem' : 'labor';
      await setDoc(doc(db, 'tenants', ClockState.tenantId, 'costEntries', `payadj_${adjRef.id}`), {
        source: 'payroll_adjustment',
        payAdjustmentId: adjRef.id,
        category: biCategory,
        amount,
        date: Timestamp.now(),
        customerId,
        customerName: customerName || null,
        jobId: jobId || null,
        jobName: jobTitle || null,
        description: `${category || capitalizeFirst(type)}: ${person?.name || 'Unknown'}`,
        notes: notes || null,
        createdAt: Timestamp.now(),
        createdBy: window.AppState?.user?.email || 'unknown'
      });
    }

    window.showToast('Adjustment saved', 'success');
    document.getElementById('adj-form-inline')?.remove();
    // Re-render adjustments tab
    const content = document.getElementById('workforce-content');
    if (content) content.innerHTML = getWorkforceAdjustmentsHTML();
  } catch (error) {
    console.error('[Clock] Error saving adjustment:', error);
    window.showToast('Error saving adjustment', 'error');
  }
}

// ============================================
// TIME OFF FUNCTIONS (preserved from original)
// ============================================
function updateTimeOffPersonDropdown() {
  const select = document.getElementById('timeoff-person');
  if (!select) return;
  const currentValue = select.value;
  select.innerHTML = '<option value="">Select employee...</option>' +
    ClockState.people.map(p => `<option value="${p.id}">${escapeHtml(p.name)}</option>`).join('');
  if (currentValue) select.value = currentValue;
}

function switchTimeOffFilter(filter) {
  ClockState.timeoffFilter = filter;
  document.querySelectorAll('.timeoff-filters .filter-btn[data-filter]').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.filter === filter);
  });
  renderTimeOffRequestsList();
}

// TASK-10121 — Clock -> Time Off grouping (Lacey, TITS: "not sorted and
// organized by employee"). Default 'employee'; the viewer's explicit choice is
// remembered per tenant in localStorage (a per-viewer convenience only — no
// earlier key existed, so nobody is pinned to the old flat list).
function _clockTimeOffGroupKey() {
  return `dd.clock.timeOffGroup.${ClockState.tenantId || 'default'}`;
}
function _clockTimeOffGroup() {
  // Cached per tenant: a company switch re-reads that company's saved choice (COD Code Reviewer).
  const tid = ClockState.tenantId || 'default';
  if (ClockState.timeoffGroupTenant === tid && (ClockState.timeoffGroup === 'employee' || ClockState.timeoffGroup === 'none')) return ClockState.timeoffGroup;
  let g = null;
  try { g = localStorage.getItem(_clockTimeOffGroupKey()); } catch (_) { g = null; }
  ClockState.timeoffGroup = (g === 'none') ? 'none' : 'employee';
  ClockState.timeoffGroupTenant = tid;
  return ClockState.timeoffGroup;
}
function setTimeOffGroup(g) {
  ClockState.timeoffGroup = (g === 'none') ? 'none' : 'employee';
  ClockState.timeoffGroupTenant = ClockState.tenantId || 'default';
  try { localStorage.setItem(_clockTimeOffGroupKey(), ClockState.timeoffGroup); } catch (_) {}
  document.querySelectorAll('.timeoff-group-filters .filter-btn[data-group]').forEach(btn => {
    const on = btn.dataset.group === ClockState.timeoffGroup;
    btn.classList.toggle('active', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  renderTimeOffRequestsList();
}

function renderTimeOffRequestsList() {
  const container = document.getElementById('timeoff-list');
  if (!container) return;

  let filteredRequests = ClockState.timeoffRequests;
  if (ClockState.timeoffFilter !== 'all') {
    filteredRequests = filteredRequests.filter(r => r.status === ClockState.timeoffFilter);
  }

  const countEl = document.getElementById('timeoff-requests-count');
  if (countEl) {
    countEl.textContent = `${filteredRequests.length} request${filteredRequests.length !== 1 ? 's' : ''}`;
  }

  if (filteredRequests.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">📅</div>
        <div class="empty-state-title">No ${ClockState.timeoffFilter} requests</div>
        <div class="empty-state-text">
          ${ClockState.timeoffFilter === 'pending' ? 'No time-off requests are waiting for approval.' : 'No requests found with this status.'}
        </div>
      </div>
    `;
    return;
  }

  const personOf = r => ClockState.people.find(p => p.id === r.personId) || null;
  // TASK-10121 — grouped by employee (name order, pending first in each block)
  // through the shared window.TimeOffGroup rule hr.js uses. It only re-orders
  // the rows already in ClockState.timeoffRequests — a self-scoped viewer's
  // snapshot holds only their own requests, so nothing is widened. One person
  // in view (always true for self-scope) → no header, same flat list as before.
  const TG = window.TimeOffGroup;
  const groups = (TG && _clockTimeOffGroup() === 'employee')
    ? TG.byEmployee(filteredRequests, { nameOf: r => personOf(r)?.name || r.personName || r.employeeName || 'Unknown' })
    : null;
  if (groups && groups.length > 1) {
    container.innerHTML = groups.map(g => `
      <div class="timeoff-group" data-person-key="${escapeHtml(g.key)}" style="display:flex;flex-direction:column;gap:var(--spacing-sm);">
        <div class="timeoff-group-label" style="font-weight:600;font-size:0.8rem;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin-top:var(--spacing-xs);">
          ${escapeHtml(g.name)} &middot; ${g.items.length}${g.pendingCount && ClockState.timeoffFilter !== 'pending' ? ` <span style="text-transform:none;letter-spacing:0;">(${g.pendingCount} pending)</span>` : ''}
        </div>
        ${g.items.map(_clockTimeOffCardHtml).join('')}
      </div>`).join('');
    return;
  }
  const flat = groups ? groups[0].items : filteredRequests;
  container.innerHTML = flat.map(_clockTimeOffCardHtml).join('');
}

function _clockTimeOffCardHtml(request) {
    const person = ClockState.people.find(p => p.id === request.personId);
    const days = calculateDays(request.startDate, request.endDate);
    return `
      <div class="timeoff-card status-${request.status}" data-id="${request.id}">
        <div class="timeoff-card-header">
          <div class="timeoff-card-person">
            <div class="person-avatar" style="background: ${person?.color || '#3B82F6'}">${getInitials(person?.name || request.personName)}</div>
            <div class="person-info">
              <div class="person-name">${escapeHtml(person?.name || request.personName || 'Unknown')}</div>
              <div class="person-role">${escapeHtml(person?.role || '')}</div>
            </div>
          </div>
          <span class="badge badge-${request.status}">${capitalizeFirst(request.status)}</span>
        </div>
        <div class="timeoff-card-body">
          <div class="timeoff-dates">
            <span class="material-icons">date_range</span>
            <span>${formatDate(request.startDate)} - ${formatDate(request.endDate)}</span>
            <span class="timeoff-days">(${days} day${days !== 1 ? 's' : ''})</span>
          </div>
          <div class="timeoff-type">
            <span class="material-icons">${getTypeIcon(request.type)}</span>
            <span>${formatType(request.type)}</span>
          </div>
          ${request.reason ? `<div class="timeoff-reason">${escapeHtml(truncate(request.reason, 100))}</div>` : ''}
        </div>
        <div class="timeoff-card-actions">
          ${request.status === 'pending' ? `
            <button class="btn btn-sm btn-primary" onclick="window.ClockModule.reviewTimeOffRequest('${request.id}')">
              <span class="material-icons">visibility</span> Review
            </button>
            ${isOwnTimeOffRequest(request) ? `<button class="btn-icon" onclick="window.ClockModule.deleteTimeOffRequest('${request.id}')" title="Withdraw" aria-label="Withdraw request">
              <span class="material-icons">close</span>
            </button>` : ''}
          ` : `
            <button class="btn btn-sm btn-secondary" onclick="window.ClockModule.viewTimeOffRequest('${request.id}')">
              <span class="material-icons">visibility</span> View
            </button>
          `}
        </div>
      </div>
    `;
}

// TASK-528 — Can the viewer file time off on OTHER people's behalf?
// Privileged tier mirrors the timeOff firestore.rules create rule's
// "create for anyone, any status" branch EXACTLY: isDispatcherOrHigher
// (owner/admin/dispatcher role) OR an HR-module holder (payroll clerk filing
// for a driver on the road — Domain/Regulatory COD ask 2026-06-09). Everyone
// else (clock-/timeoff-only staff, driver PIN) is self-scoped.
//
// NOTE: we check the dispatcher ROLE here, NOT hasWorkforceAccess() — a
// dispatcher without the `workforce` module is still privileged by the rule
// (isDispatcherOrHigher), and gating the UI on hasWorkforceAccess() would
// wrongly self-scope them (COD Code Reviewer 2026-06-09). hasWorkforceAccess()
// returning true (founder/demo/no-PIN-account) is a superset that also passes.
function canRequestTimeOffForOthers() {
  if (hasWorkforceAccess()) return true;
  if (memberHasModule('hr')) return true;
  const ma = getActiveMemberAccess();
  return !!ma && ['owner', 'admin', 'dispatcher'].includes(ma.role);
}

// ============================================
// MY TIME OFF — upcoming + past history under the request form
// (Chris 2026-07-13 — ported from timeoff.js toggleHistory/renderHistory so
// users see historic + future accepted time off wherever they request it)
// ============================================
function toggleTimeOffHistory() {
  document.getElementById('clock-timeoff-history-section')?.classList.toggle('open');
}

function renderTimeOffHistory() {
  const body = document.getElementById('clock-timeoff-history-body');
  if (!body) return;

  // Resolve "current person" — explicit dropdown selection wins so privileged
  // viewers opening on behalf of someone get the right history; falls back to
  // the signed-in member's linked personId (the same self the lock in
  // showNewTimeOffRequest pins the picker to).
  const personId = document.getElementById('timeoff-person')?.value || getSelfPersonId() || '';

  if (!personId) {
    body.innerHTML = `
      <div style="text-align:center;padding:var(--spacing-sm);color:var(--text-secondary);font-size:0.85rem;">
        Select an employee to see their time off.
      </div>
    `;
    return;
  }

  // ClockState.timeoffRequests is the live tenant-wide snapshot (startDate
  // desc, Dates already coerced). Filter client-side — no second listener.
  // Partition into Upcoming (approved, startDate >= today, ascending) and
  // Past (approved+denied, capped at 10) — same split as timeoff.js.
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const personRequests = ClockState.timeoffRequests
    .filter(r => r.personId === personId)
    .slice(0, 15);

  const upcoming = personRequests
    .filter(r => r.status === 'approved' && r.startDate >= today)
    // Order ascending for upcoming (next first), since the snapshot is desc.
    .slice()
    .sort((a, b) => a.startDate - b.startDate);

  const past = personRequests
    .filter(r => (r.status === 'approved' || r.status === 'denied') && r.startDate < today)
    .slice(0, 10);

  body.innerHTML = `
    <div class="timeoff-history-group">
      <div class="timeoff-history-heading">Upcoming</div>
      ${upcoming.length === 0
        ? `<div class="timeoff-history-empty">No upcoming time off yet.</div>`
        : upcoming.map(renderTimeOffHistoryRow).join('')}
    </div>
    <div class="timeoff-history-group" style="margin-top:var(--spacing-sm);">
      <div class="timeoff-history-heading">Past</div>
      ${past.length === 0
        ? `<div class="timeoff-history-empty">No past requests.</div>`
        : past.map(renderTimeOffHistoryRow).join('')}
    </div>
  `;
}

function renderTimeOffHistoryRow(req) {
  const days = calculateDays(req.startDate, req.endDate);
  const isSameDay = req.startDate && req.endDate &&
    req.startDate.getFullYear?.() === req.endDate.getFullYear?.() &&
    req.startDate.getMonth?.() === req.endDate.getMonth?.() &&
    req.startDate.getDate?.() === req.endDate.getDate?.();
  const dateLabel = isSameDay
    ? formatDate(req.startDate)
    : `${formatDate(req.startDate)} – ${formatDate(req.endDate)}`;
  const hrs = req.hours || days * 8;
  return `
    <div class="timeoff-history-row">
      <div class="timeoff-history-main">
        <div class="timeoff-history-type">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">${getTypeIcon(req.type)}</span>
          ${escapeHtml(formatType(req.type))}
        </div>
        <div class="timeoff-history-dates">${escapeHtml(dateLabel)} · ${hrs} hr${hrs !== 1 ? 's' : ''}</div>
      </div>
      <span class="badge badge-${escapeHtml(req.status)}">${escapeHtml(capitalizeFirst(req.status))}</span>
    </div>
  `;
}

function showNewTimeOffRequest() {
  document.getElementById('modal-timeoff-title').textContent = 'Request Time Off';
  document.getElementById('form-timeoff-request')?.reset();
  document.getElementById('timeoff-request-id').value = '';
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const startEl = document.getElementById('timeoff-start');
  const endEl = document.getElementById('timeoff-end');
  if (startEl) startEl.value = formatDateInput(tomorrow);
  if (endEl) endEl.value = formatDateInput(tomorrow);
  updateTimeOffPersonDropdown();

  // TASK-528 self-scope: a non-privileged member can only request their OWN
  // time off. Default AND lock the person picker to their linked personId.
  // Mirrors the clock self-vs-workforce split (hasWorkforceAccess) and the
  // tightened timeOff create rule (self-create gated on personId == own
  // linked person). Privileged viewers (workforce / HR clerk) keep the full
  // picker so they can file on a coworker's behalf.
  const personSelect = document.getElementById('timeoff-person');
  if (personSelect) {
    if (!canRequestTimeOffForOthers()) {
      const selfId = getSelfPersonId();
      if (selfId && Array.from(personSelect.options).some(o => o.value === selfId)) {
        personSelect.value = selfId;
      }
      // Lock to self. A disabled <select> won't submit its value, so we keep
      // it enabled-but-readonly-feeling: block changes via pointer/keys and a
      // change handler that snaps back. (saveTimeOffRequest also re-asserts.)
      personSelect.dataset.lockedSelf = selfId || '';
      personSelect.setAttribute('aria-readonly', 'true');
      personSelect.style.pointerEvents = 'none';
      personSelect.style.background = 'var(--bg-subtle, #F8FAFC)';
      personSelect.onchange = () => {
        const lock = personSelect.dataset.lockedSelf || '';
        if (lock) personSelect.value = lock;
        renderTimeOffHistory();
      };
    } else {
      // Privileged viewer — ensure no stale lock from a prior open.
      delete personSelect.dataset.lockedSelf;
      personSelect.removeAttribute('aria-readonly');
      personSelect.style.pointerEvents = '';
      personSelect.style.background = '';
      // Re-render the My Time Off history when they pick a different person
      // (idempotent — replaces handler on every open).
      personSelect.onchange = () => renderTimeOffHistory();
    }
  }

  // My Time Off history (Chris 2026-07-13, ported from timeoff.js
  // showNewRequest): default the section open on desktop, collapsed on
  // mobile, then render for the resolved person.
  const historySection = document.getElementById('clock-timeoff-history-section');
  if (historySection) {
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    historySection.classList.toggle('open', !isMobile);
  }
  renderTimeOffHistory();

  document.getElementById('modal-timeoff-request')?.classList.remove('hidden');
}

async function saveTimeOffRequest(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;
  // End-before-start guard (TITS 2026-09-23). firestore.rules refuses it too;
  // this is the plain message before the write.
  {
    const _s = document.getElementById('timeoff-start')?.value;
    const _en = document.getElementById('timeoff-end')?.value;
    if (_s && _en && _en < _s) { window.showToast?.('End date cannot be before start date', 'error'); return; }
  }
  const id = document.getElementById('timeoff-request-id')?.value;
  let personId = document.getElementById('timeoff-person')?.value;

  // TASK-528 self-scope enforcement (defense in depth — the UI lock in
  // showNewTimeOffRequest is bypassable via DevTools). A non-privileged member
  // may ONLY request their OWN time off. Force personId to their linked person
  // and refuse if it doesn't match / they aren't linked. Privileged viewers
  // (workforce / HR clerk) skip this and may file for anyone. The firestore
  // rule is the real backstop (self-create requires personId == own linked
  // person + status:'pending'); this is the friendly client-side guard.
  if (!canRequestTimeOffForOthers()) {
    const selfId = getSelfPersonId();
    if (!selfId) {
      window.showToast?.('Your login isn\'t linked to a roster person yet — ask an admin.', 'error');
      return;
    }
    if (personId && personId !== selfId) {
      window.showToast?.('You can only request your own time off', 'error');
      return;
    }
    personId = selfId;
  }

  const person = ClockState.people.find(p => p.id === personId);
  if (!person) { window.showToast?.('Please select an employee', 'error'); return; }

  // TASK-4720 — every save goes through saveTimeOffRequest: the server refuses
  // a request overlapping this person's pending/approved one ("You already have
  // time off requested for <dates>. Withdraw that request first.") and lets only
  // the requester edit their own pending request. No client write rule exists.
  const payload = {
    tenantId: window.AppState?.tenant || 'demo',
    personId,
    startDate: document.getElementById('timeoff-start').value,
    endDate: document.getElementById('timeoff-end').value,
    type: document.getElementById('timeoff-type')?.value,
    reason: document.getElementById('timeoff-reason')?.value.trim(),
    source: 'clock',
  };

  try {
    const fn = httpsCallable(functions, 'saveTimeOffRequest');
    if (id) {
      await fn({ ...payload, action: 'update', requestId: id });
      window.showToast?.('Request updated', 'success');
    } else {
      await fn({ ...payload, action: 'create' });
      window.showToast?.('Request submitted', 'success');
    }
    closeModal('timeoff-request');
  } catch (error) {
    console.error('[Clock] Error saving time-off request:', error);
    window.showToast?.(error?.message || 'Error submitting request', 'error');
  }
}

function viewTimeOffRequest(id) {
  const request = ClockState.timeoffRequests.find(r => r.id === id);
  if (!request) return;
  const person = ClockState.people.find(p => p.id === request.personId);
  const days = calculateDays(request.startDate, request.endDate);

  const content = document.getElementById('timeoff-review-content');
  content.innerHTML = `
    <div class="review-header">
      <div class="person-avatar large" style="background: ${person?.color || '#3B82F6'}">${getInitials(person?.name || request.personName)}</div>
      <div class="review-person-info">
        <div class="review-person-name">${escapeHtml(person?.name || request.personName)}</div>
        <div class="review-person-role">${escapeHtml(person?.role || '')}</div>
      </div>
    </div>
    <div class="review-details">
      <div class="detail-row"><span class="detail-label">Status</span><span class="badge badge-${request.status}">${capitalizeFirst(request.status)}</span></div>
      <div class="detail-row"><span class="detail-label">Type</span><span class="detail-value">${formatType(request.type)}</span></div>
      <div class="detail-row"><span class="detail-label">Dates</span><span class="detail-value">${formatDate(request.startDate)} - ${formatDate(request.endDate)}</span></div>
      <div class="detail-row"><span class="detail-label">Duration</span><span class="detail-value">${days} day${days !== 1 ? 's' : ''}</span></div>
      ${request.reason ? `<div class="detail-row full"><span class="detail-label">Reason</span><p class="detail-value">${escapeHtml(request.reason)}</p></div>` : ''}
      ${request.reviewedBy ? `<div class="detail-row"><span class="detail-label">Reviewed By</span><span class="detail-value">${escapeHtml(request.reviewedByName || request.reviewedBy)}</span></div>` : ''}
      ${request.reviewNote ? `<div class="detail-row full"><span class="detail-label">${request.status === 'denied' ? 'Denial Reason' : 'Manager Note'}</span><p class="detail-value">${escapeHtml(request.reviewNote)}</p></div>` : ''}
    </div>
  `;
  document.getElementById('timeoff-review-actions').innerHTML = `
    <button class="btn btn-secondary" onclick="window.ClockModule.closeModal('timeoff-review')">Close</button>
  `;
  document.getElementById('modal-timeoff-review')?.classList.remove('hidden');
}

function reviewTimeOffRequest(id) {
  const request = ClockState.timeoffRequests.find(r => r.id === id);
  if (!request) return;
  ClockState.selectedRequest = request;
  const person = ClockState.people.find(p => p.id === request.personId);
  const days = calculateDays(request.startDate, request.endDate);

  const content = document.getElementById('timeoff-review-content');
  content.innerHTML = `
    <div class="review-header">
      <div class="person-avatar large" style="background: ${person?.color || '#3B82F6'}">${getInitials(person?.name || request.personName)}</div>
      <div class="review-person-info">
        <div class="review-person-name">${escapeHtml(person?.name || request.personName)}</div>
        <div class="review-person-role">${escapeHtml(person?.role || '')}</div>
      </div>
    </div>
    <div class="review-details">
      <div class="detail-row"><span class="detail-label">Type</span><span class="detail-value">${formatType(request.type)}</span></div>
      <div class="detail-row"><span class="detail-label">Dates</span><span class="detail-value">${formatDate(request.startDate)} - ${formatDate(request.endDate)}</span></div>
      <div class="detail-row"><span class="detail-label">Duration</span><span class="detail-value">${days} day${days !== 1 ? 's' : ''}</span></div>
      ${request.reason ? `<div class="detail-row full"><span class="detail-label">Reason</span><p class="detail-value">${escapeHtml(request.reason)}</p></div>` : ''}
    </div>
    <div class="review-notes">
      <label for="timeoff-review-note">Manager Notes (optional)</label>
      <textarea id="timeoff-review-note" rows="2" maxlength="300" placeholder="Optional — texted to the employee with the decision. Keep it schedule-related (no medical detail)."></textarea>
    </div>
  `;
  document.getElementById('timeoff-review-actions').innerHTML = `
    <button class="btn btn-secondary" onclick="window.ClockModule.closeModal('timeoff-review')">Cancel</button>
    <button class="btn btn-danger" onclick="window.ClockModule.denyTimeOffRequest('${id}')"><span class="material-icons">close</span> Deny</button>
    <button class="btn btn-primary" onclick="window.ClockModule.approveTimeOffRequest('${id}')"><span class="material-icons">check</span> Approve</button>
  `;
  document.getElementById('modal-timeoff-review')?.classList.remove('hidden');
}

// TASK-1136 — approve/deny now flow through the adminManageTimeOff callable
// (role-gated + self-approval-blocked + server-side audit; decision SMS —
// including the deny reason — rides the onTimeOffUpdate trigger). Raw
// updateDoc paths and the client logAudit duplicates are DELETED; the server
// _writeAudit row is authoritative. The modal's existing "Manager Notes" input
// is the unified `reviewNote` field — on deny it doubles as the reason texted
// to the requester. DEMO PARITY: anon demo users have no users/{uid} doc so
// the callable can't resolve their tenant; rules leave demo open — keep the
// pre-1136 raw write for demo ONLY.
async function _clockDecideTimeOff(id, action, note) {
  const tenant = window.AppState?.tenant || 'demo';
  if (tenant === 'demo') {
    await updateDoc(doc(db, 'tenants', tenant, 'timeOff', id), {
      status: action === 'approve' ? 'approved' : 'denied',
      reviewedAt: Timestamp.now(),
      reviewedBy: window.AppState?.user?.email || 'Manager',
      // TASK-1129 — demo parity for the attribution stamp (server stamps it
      // for real tenants via adminManageTimeOff).
      reviewedByName: (window.AppState?.memberAccess?.displayName || window.AppState?.user?.displayName
        || (window.AppState?.user?.email || '').split('@')[0] || null),
      reviewNote: note || '', updatedAt: Timestamp.now()
    });
    return;
  }
  const fn = httpsCallable(functions, 'adminManageTimeOff');
  await fn({ requestId: id, action, reason: note || '' });
}

async function approveTimeOffRequest(id) {
  const note = (document.getElementById('timeoff-review-note')?.value || '').trim().slice(0, 300);
  try {
    await _clockDecideTimeOff(id, 'approve', note);
    window.showToast?.('Request approved', 'success');
    closeModal('timeoff-review');
  } catch (error) {
    console.error('[Clock] Error approving request:', error);
    window.showToast?.(error?.message || 'Error approving request', 'error');
  }
}

async function denyTimeOffRequest(id) {
  const note = (document.getElementById('timeoff-review-note')?.value || '').trim().slice(0, 300);
  try {
    await _clockDecideTimeOff(id, 'deny', note);
    window.showToast?.('Request denied', 'success');
    closeModal('timeoff-review');
  } catch (error) {
    console.error('[Clock] Error denying request:', error);
    window.showToast?.(error?.message || 'Error denying request', 'error');
  }
}

// TASK-4720 — is this request the viewer's own (they may edit / withdraw it
// while pending)? Same identity the server checks: the viewer's linked person,
// or the login that filed it. Office users approve or reject everyone else's.
function isOwnTimeOffRequest(request) {
  if (!request) return false;
  if ((window.AppState?.tenant || 'demo') === 'demo') return true; // demo is open (server + rules)
  const self = getSelfPersonId();
  const uid = window.AppState?.user?.uid || null;
  return (!!self && request.personId === self) || (!!uid && request.submittedByUid === uid);
}

function deleteTimeOffRequest(id) {
  const request = ClockState.timeoffRequests.find(r => r.id === id);
  if (!request || request.status !== 'pending') return;
  const confirmBtn = document.getElementById('btn-confirm-delete-timeoff');
  if (confirmBtn) {
    confirmBtn.onclick = async () => {
      try {
        // TASK-4720 — withdraw = delete, requester only, server-side.
        const fn = httpsCallable(functions, 'saveTimeOffRequest');
        await fn({ tenantId: window.AppState?.tenant || 'demo', action: 'withdraw', requestId: id });
        window.showToast?.('Request withdrawn', 'success');
        closeModal('timeoff-delete');
      } catch (error) {
        console.error('[Clock] Error withdrawing request:', error);
        window.showToast?.(error?.message || 'Error withdrawing request', 'error');
      }
    };
  }
  document.getElementById('modal-timeoff-delete')?.classList.remove('hidden');
}

// ============================================
// SWITCH JOB (mid-shift)
// ============================================
function showSwitchJobModal(entryId) {
  // Reset flag unless explicitly set by switchTicketFromClockOut
  if (!ClockState._switchFromClockOut) ClockState._switchFromClockOut = false;
  const entry = ClockState.activeEntries.find(e => e.id === entryId);
  if (!entry) { showToast('Clock entry not found', 'error'); return; }

  // TASK-360 — Switch Job on another person's entry is a workforce surface.
  // The "Currently Clocked In" list (which exposes this button) is hidden
  // for non-workforce viewers, but enforce here too as a defense for the
  // case where a clock-out flow Switch Ticket button triggers this on the
  // viewer's OWN entry (that case is allowed — they're operating on self).
  if (!hasWorkforceAccess()) {
    const selfId = getSelfPersonId();
    if (!selfId || entry.personId !== selfId) {
      console.warn('[Clock] Switch Job blocked — viewer can only modify own entry');
      showToast('You can only switch your own job. Ask an admin to switch others.', 'error');
      return;
    }
  }

  // TASK-9941 F S2 — a switch closes this shift and opens another: a punch.
  // From the clock-out sheet it rides the grant that opened the sheet; from the
  // Currently Clocked In list it needs this person's PIN (or the override) first.
  if (!_clockSwitchAuth(entry).ok) {
    selectPerson(entry.personId);
    showToast(`Enter ${entry.personName ? String(entry.personName).split(/\s+/)[0] + '’s' : 'their'} PIN first, then switch the job.`, 'info');
    return;
  }

  ClockState.switchJobEntryId = entryId;

  const person = ClockState.people.find(p => p.id === entry.personId);
  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
  const elapsed = formatDuration(new Date() - clockIn);
  const hours = ((new Date() - clockIn) / 3600000).toFixed(2);

  // Build customer dropdown — internalTimeOnly (Shop/Office) grouped first under
  // "Internal — not billed" (TASK-1175 pin, TASK-9548 label)
  const custOptions = _clockCustomerOptionsHtml(ClockState.customers, { selectedId: entry.customerId });

  const bodyEl = document.getElementById('switch-job-body');
  if (!bodyEl) return;

  bodyEl.innerHTML = `
    <!-- Current segment summary -->
    <div style="padding:14px;background:#fef2f2;border:1px solid #fecaca;border-radius:var(--radius-md);text-align:center;margin-bottom:16px;">
      <div style="font-size:0.75rem;text-transform:uppercase;letter-spacing:0.5px;color:#dc2626;font-weight:600;margin-bottom:4px;">Finalizing Current Segment</div>
      <div style="font-size:0.9rem;font-weight:700;color:#0F172A;">${escapeHtml(person?.name || entry.personName)}</div>
      <div style="font-size:1.3rem;font-weight:800;color:#b91c1c;">${hours} hrs (${elapsed})</div>
      <div style="font-size:0.8rem;color:#64748b;margin-top:2px;">${escapeHtml(entry.customerName || 'Shop')}${entry.jobTitle ? ' - ' + escapeHtml(entry.jobTitle) : ''}</div>
      <button class="btn btn-sm" style="margin-top:8px;background:#fff;border:1px solid #e2e8f0;color:#3B82F6;font-weight:600;font-size:0.75rem;" onclick="window.ClockModule.toggleSwitchTicketReview('${entry.ticketId || ''}')">
        <span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px;">receipt_long</span> Review Ticket
      </button>
      <div id="switch-ticket-review" style="display:none;margin-top:10px;text-align:left;"></div>
    </div>

    <!-- Shift notes for this segment -->
    <div style="margin-bottom:16px;">
      <label style="font-weight:600;font-size:0.8rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-secondary);display:flex;align-items:center;gap:6px;margin-bottom:6px;">
        <span class="material-icons" style="font-size:16px;">notes</span> Shift Notes (this segment)
      </label>
      <textarea id="switch-shift-notes" rows="2" class="form-control" placeholder="${window.AppState?.settings?.requireShiftNotes === true ? 'REQUIRED — What did you do today?' : 'What did you do today?'}"></textarea>
    </div>

    <!-- New job selection -->
    <div style="border-top:1px solid var(--border-color);padding-top:16px;">
      <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);margin-bottom:12px;">Switch To:</div>
      <div style="margin-bottom:12px;">
        <label style="font-weight:600;font-size:0.8rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-secondary);margin-bottom:6px;display:block;">Customer</label>
        <select id="switch-customer" class="form-control" onchange="window.ClockModule.onSwitchCustomerChange()">
          <option value="">Choose customer...</option>
          ${custOptions}
        </select>
        ${_clockCustomerHintHtml(ClockState.customers)}
      </div>
      <div id="switch-job-group" style="display:none;">
        <label style="font-weight:600;font-size:0.8rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-secondary);margin-bottom:6px;display:block;">Job</label>
        <select id="switch-job" class="form-control" onchange="window.ClockModule.onSwitchJobChange()">
          <option value="">No specific job</option>
        </select>
        <div id="switch-job-crew-warn" style="display:none;margin-top:8px;"></div>
      </div>
    </div>
  `;

  document.getElementById('modal-switch-job')?.classList.remove('hidden');

  // TASK-4563 (A) — the driver already typed his note in Build Ticket; carry it
  // into the switch box instead of showing an EMPTY box that is then REQUIRED
  // (confirmSwitchJob -> shiftNotesRequired). Retyping reads as 'the app lost my
  // note' and is a real source of the 'notes did not save' reports. Seeded via
  // .value AFTER render, never interpolated into the markup (COD Code Reviewer:
  // textarea escaping). Same PERSON, same SEGMENT being closed, so this does NOT
  // violate the first-person shift-note standard; only the clock-out launch path
  // seeds — the active-list launch stays empty.
  if (ClockState._switchFromClockOut) {
    const _sw = document.getElementById('switch-shift-notes');
    const _co = document.getElementById('clock-out-shift-notes');
    if (_sw && _co && !_sw.value) _sw.value = _co.value || '';
  }
}

async function onSwitchCustomerChange() {
  const custId = document.getElementById('switch-customer')?.value;
  const jobGroup = document.getElementById('switch-job-group');
  const jobSelect = document.getElementById('switch-job');
  if (!custId || custId === '__shop__') {
    jobGroup.style.display = 'none';
    return;
  }

  // TASK-1417 — same freeze as the clock-in picker (COD Code Reviewer): this
  // read getRelevantJobs() straight off the init-time cache.
  await refreshJobCachesIfStale(ClockState.tenantId);
  if (document.getElementById('switch-customer')?.value !== custId) return;

  const { jobs: filtered, groups } = getRelevantJobs(custId);

  jobSelect.innerHTML = _clockJobOptionsHtml(groups);
  jobGroup.style.display = filtered.length > 0 ? 'block' : 'none';

  // TASK-9865 — preselect the job this person is crewed on today (same table
  // as the clock-in picker) and show the advisory crew line.
  const entry = ClockState.activeEntries.find(e => e.id === ClockState.switchJobEntryId);
  const person = entry ? { id: entry.personId, name: entry.personName || null } : _clockPickerPerson();
  const pick = clockPickJobForPerson(filtered, ClockState.todaysJobDays, person, _clockNowMinutes());
  if (pick.jobId) jobSelect.value = pick.jobId;
  _clockRenderCrewWarn('switch-job', 'switch-job-crew-warn', person, filtered);
}

// TASK-9865 — the Switch Job picker changed by hand: re-judge the crew line.
function onSwitchJobChange() {
  try {
    const custId = document.getElementById('switch-customer')?.value;
    const jobs = (custId && custId !== '__shop__') ? getRelevantJobs(custId).jobs : [];
    const entry = ClockState.activeEntries.find(e => e.id === ClockState.switchJobEntryId);
    const person = entry ? { id: entry.personId, name: entry.personName || null } : _clockPickerPerson();
    _clockRenderCrewWarn('switch-job', 'switch-job-crew-warn', person, jobs);
  } catch (e) { console.warn('[Clock] crew warning skipped:', e); }
}

async function toggleSwitchTicketReview(ticketId) {
  const container = document.getElementById('switch-ticket-review');
  if (!container) return;

  // Toggle off if already visible
  if (container.style.display !== 'none') {
    container.style.display = 'none';
    return;
  }

  container.style.display = 'block';

  if (!ticketId) {
    container.innerHTML = '<div style="padding:8px;font-size:0.75rem;color:#64748b;font-style:italic;">No ticket linked (Shop time).</div>';
    return;
  }

  container.innerHTML = '<div style="text-align:center;padding:8px;"><span class="btn-spinner"></span> Loading...</div>';

  try {
    const tenant = ClockState.tenantId;
    const linesSnap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
    // TASK-9440 — the fuel surcharge is office billing, never shown to the crew.
    const lines = linesSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(l => l.type !== 'FUEL_SURCHARGE');

    if (lines.length === 0) {
      container.innerHTML = '<div style="padding:8px;font-size:0.75rem;color:#64748b;font-style:italic;">No line items on this ticket yet.</div>';
      return;
    }

    // Group by type
    const grouped = {};
    lines.forEach(l => {
      const type = l.type || 'OTHER';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(l);
    });

    const typeLabels = { EMPLOYEE: 'Employee Time', EQUIPMENT: 'Equipment', DOWNTIME: 'Downtime', STANDBY: 'Standby', POTHOLE: 'Pothole/Line', PERMIT: 'Load Permit' };
    const typeIcons = { EMPLOYEE: 'person', EQUIPMENT: 'precision_manufacturing', DOWNTIME: 'pause_circle', STANDBY: 'hourglass_empty', POTHOLE: 'location_on', PERMIT: 'description' };

    // TASK-9646 S4a — amounts come from the server (`driverTicketPrices`: the
    // Billing "Show prices on driver tickets" switch + caller on the ticket, or
    // a money reader = billing/bi/owner), never off the line docs. A clock-only
    // member opens this on their own shift; a workforce/dispatch user without
    // billing/bi now sees hours only unless the switch is on and they are on
    // the ticket (2nd COD: a deliberate change — money = billing/bi modules).
    let _prices = null;
    let _pricesFailed = false;
    try {
      const _pr = await httpsCallable(functions, 'driverTicketPrices')({ ticketId });
      _prices = _pr && _pr.data && _pr.data.enabled === true ? (_pr.data.lines || {}) : null;
    } catch (e) {
      _prices = null;
      // integ/2100 COD r1 rule: a failed call is NOT "prices off" — say so when
      // the company shows prices (a refusal = not on the ticket = no note).
      _pricesFailed = window.AppState?.settings?.showPricesOnDriverTickets === true
        && !/permission-denied/.test(String(e?.code || ''));
    }

    let html = '<div style="font-size:0.75rem;border-top:1px solid #e2e8f0;padding-top:8px;">';
    if (_pricesFailed) html += '<div style="padding:0 0 6px;color:var(--text-secondary);">Prices could not be checked just now — reopen the review to try again.</div>';
    for (const [type, items] of Object.entries(grouped)) {
      const label = typeLabels[type] || type;
      const icon = typeIcons[type] || 'receipt';
      html += `<div style="margin-bottom:6px;">
        <div style="font-weight:700;color:#334155;display:flex;align-items:center;gap:4px;margin-bottom:2px;">
          <span class="material-icons" style="font-size:14px;color:#64748b;">${icon}</span> ${escapeHtml(label)} (${items.length})
        </div>`;
      items.forEach(item => {
        const hrs = item.hours ? `${item.hours}h` : '';
        const _p = _prices && _prices[item.id];
        const amt = _p && !_p.pending && Number(_p.lineAmount) ? `$${Number(_p.lineAmount).toFixed(2)}` : '';
        const name = item.assetName || item.personName || item.description || '';
        html += `<div style="padding:2px 0 2px 18px;color:#475569;">${escapeHtml(name)}${hrs ? ' — ' + hrs : ''}${amt ? ' — ' + amt : ''}</div>`;
      });
      html += '</div>';
    }
    html += '</div>';
    container.innerHTML = html;
  } catch (error) {
    console.error('[Clock] Error loading ticket for review:', error);
    container.innerHTML = '<div style="padding:8px;font-size:0.75rem;color:#dc2626;">Error loading ticket</div>';
  }
}

async function confirmSwitchJob() {
  const entryId = ClockState.switchJobEntryId;
  const entry = ClockState.activeEntries.find(e => e.id === entryId);
  if (!entry) { showToast('Clock entry not found', 'error'); return; }

  const newCustomerId = document.getElementById('switch-customer')?.value;
  if (!newCustomerId) { showToast('Select a customer', 'error'); return; }
  const _switchAuth = _clockSwitchAuth(entry); // TASK-9941 F S2
  if (!_switchAuth.ok) { showToast('Enter the PIN first, then switch the job.', 'error'); return; }

  const newJobId = document.getElementById('switch-job')?.value || null;
  const shiftNotes = document.getElementById('switch-shift-notes')?.value?.trim() || '';
  // TASK-619 (Lacey) — a job switch closes out the current segment (a clock-out),
  // so honor the require-shift-note setting here too.
  if (shiftNotesRequired() && !shiftNotes) {
    showToast('Add a shift note before switching jobs — what did you do on this one?', 'error');
    document.getElementById('switch-shift-notes')?.focus();
    return;
  }
  const isShop = newCustomerId === '__shop__';
  const newCustomer = isShop ? { id: '__shop__', name: 'Shop' } : ClockState.customers.find(c => c.id === newCustomerId);
  const newJob = newJobId ? ClockState.allJobs.find(j => j.id === newJobId) : null;
  const tenant = ClockState.tenantId;

  const btn = document.getElementById('btn-confirm-switch-job');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Switching...'; }

  // clock-double-tap — a switch closes THIS shift and opens one new one. If the
  // person has ANOTHER open shift too, the new segment would be refused by the
  // one-open-shift rule after this one was already closed, so stop here, before
  // any write, and say which shift is in the way.
  const _csg = window.ClockShiftGuard;
  if (_csg) {
    const _open = await _csg.openShifts(_clockCsgDeps(), tenant, entry.personId);
    const _others = (_open || []).filter(e => e.id !== entryId);
    if (_others.length) {
      const _o = _others[0];
      const _who = _clockIsSelfSelection(entry.personId) ? 'You also have' : `${entry.personName || 'This person'} also has`;
      showToast(`${_who} an open shift since ${_csg.startLabel(_o)} (${_csg.whereLabel(_o)}). Close that shift first, then switch.`, 'warning', null, 9000);
      if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">swap_horiz</span> Switch'; }
      return;
    }
  }
  const _switchNewEntryRef = doc(collection(db, 'tenants', tenant, 'clockEntries'));

  try {
    const now = new Date();
    const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    const totalHours = Math.max(0, (now - clockIn) / 3600000);
    const roundedHours = parseFloat(totalHours.toFixed(2));
    // === STEP 1: Finalize old segment ===

    const useGateway = !!window.USE_TICKET_GATEWAY || !!window.AppState?.settings?.useTicketGateway;
    // TASK-9989 item 1 (COD r2) — the server finalized the shift but left a
    // CLOSED ticket alone (invoiced / locked); the client must not then stamp
    // its date range or recalc + mark it dirty below.
    let _swOldTicketClosed = false;
    if (useGateway) {
      try {
        // Gateway handles 1A (clockEntry) + 1B (ticketLine cascade) atomically.
        const gwPayload = { entryId, clockOutNotes: shiftNotes };
        const gwScope = 'jobSwitch:' + entryId;
        const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: GW_CLIENT_TIMEOUT_MS });
        const _swRes = await fn({ op: 'jobSwitch', tenantId: tenant, payload: gwPayload, opKey: _gwOpKey(gwScope, gwPayload) });
        _swOldTicketClosed = _swRes?.data?.updated?.ticketLineSkipped === 'closedBook';
        _gwOpDone(gwScope);
      } catch (gwErr) {
        // TASK-1160 — a timeout is NOT evidence the gateway is broken: the
        // server may STILL complete after we gave up. Falling back to the
        // legacy raw-write path here is exactly what double-finalizes the
        // segment. Keep the gateway on, tell the user plainly, let them retry
        // (the opKey above dedups the retry server-side).
        if (_gwIsDeadlineExceeded(gwErr)) {
          showToast('That took too long — the switch may still have gone through. Wait a few seconds, check your shift, then retry.', 'warning');
          gwErr._userMessageShown = true;
          throw gwErr;
        }
        // TASK-9989 item 1 (COD r1) — a deliberate SERVER REFUSAL is not an
        // outage: permission-denied (not allowed), failed-precondition (shift
        // already closed / the same switch still in flight), invalid-argument /
        // not-found (entry not active / gone). Say it, keep the gateway on —
        // falling back here would put the rest of the visit's edits on the
        // direct path (no closed-book check), as the edit catch already avoids.
        const _swCode = String(gwErr?.code || gwErr?.details?.code || '');
        if (/permission-denied|failed-precondition|invalid-argument|not-found/i.test(_swCode)) {
          showToast(gwErr?.message || 'The switch was not saved — refresh and check the shift.', 'error');
          gwErr._userMessageShown = true;
          throw gwErr;
        }
        _clockGatewayFallback('jobSwitch', gwErr);
        // Re-throw so the user sees an error and retries via legacy path.
        throw gwErr;
      }
    } else {
      // Legacy direct-write path (driver/offline-safe; remains as backup until
      // the gateway has bake time on TITS).

      // TASK-1160 — active-status precondition on the legacy finalize: this path
      // is reached on retry AFTER a gateway failure, when the gateway call (or a
      // concurrent device) may already have finalized this segment. Re-read
      // fresh and bail instead of re-finalizing (duplicate hours/money). Mirrors
      // the server-side in-txn recheck (ticketGateway jobSwitch/switchJob).
      const freshSnap = await getDoc(doc(db, 'tenants', tenant, 'clockEntries', entryId));
      if (!freshSnap.exists()) {
        showToast('This shift no longer exists — refresh and check the entry.', 'error');
        const e = new Error('entry missing'); e._userMessageShown = true; throw e;
      }
      if ((freshSnap.data() || {}).status !== 'active') {
        showToast('This shift was already closed — no changes made. Refresh to see it.', 'warning');
        closeModal('switch-job');
        const e = new Error('already closed'); e._userMessageShown = true; e._alreadyClosed = true; throw e;
      }

      // 1a. Update old clockEntry → completed with job_switch reason
      const jobSwitchUpdate = {
        clockOut: Timestamp.now(),
        status: 'completed',
        completionReason: 'job_switch',
        totalHours: roundedHours,
        hours: roundedHours, // keep both fields in sync
        clockOutNotes: shiftNotes
      };
      await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entryId), jobSwitchUpdate);
      logClockEntryChange(tenant, entryId, { ...entry }, { ...entry, ...jobSwitchUpdate }, 'edit', 'job-switch');

      // 1b. Update old EMPLOYEE ticketLine (never round — rounding is for equipment only)
      if (entry.ticketId && entry.ticketLineId) {
        try {
          // TASK-9646 S2 / TASK-1653 — EMPLOYEE lines never carry customer money
          // (this used to price the person at hours × the payroll rate).
          const lineUpdate = { toTime: Timestamp.now(), hours: roundedHours, lineAmount: 0, updatedAt: Timestamp.now() };
          await updateDoc(
            doc(db, 'tenants', tenant, 'tickets', entry.ticketId, 'ticketLines', entry.ticketLineId),
            lineUpdate
          );
        } catch (lineErr) {
          console.warn('[Clock] Could not update old ticket line (may have been deleted):', lineErr.message);
        }
      }
    }

    // TASK-9941 F S2 (COD r1 Code Reviewer) — the switch CLOSES this segment:
    // stamp how that clock-out was authorised too (gateway or legacy path both
    // finished above). Best-effort — never fails the switch.
    {
      const _outStamp = clockCodeStampFields(_switchAuth, 'out');
      if (Object.keys(_outStamp).length) {
        try { await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entryId), _outStamp); }
        catch (_e) { console.warn('[Clock] switch clock-out stamp skipped:', _e?.code || _e?.message); }
      }
    }

    // 1b2. Overnight on job switch: the server extends workDateEnd from the
    // closed segment's clock-out (onClockEntryTicketDateRange, TASK-10182).

    // Ticket stays draft until biller explicitly marks it ready

    // 1d. Recalc old ticket total
    if (entry.ticketId && !_swOldTicketClosed) {
      // TASK-4699 — markDirty on the OLD ticket only. The switch closed out this
      // person's EMPLOYEE line on it (hours land, the total moves), so the copy
      // the customer holds for THAT ticket is now behind. The NEW ticket is
      // findOrCreate'd and takes no stamp — see the classification note there.
      await recalcTicketTotal(entry.ticketId, true);
    }

    // === STEP 2: Start new segment ===
    const personId = entry.personId;
    const person = ClockState.people.find(p => p.id === personId);
    const { role, rate, roundToHalfHour: newRoundHalf, rateUnit: newRateUnit, revenueCategory: newRevCat } = await determineRoleAndRate(personId, newJobId, isShop ? null : newCustomerId);

    let newTicketId = null;
    let newTicketLineId = null;

    if (!isShop && newCustomerId && shouldBuildTickets()) {
      // TASK-1221 — resolve the new job's split ticket from the switching entry's
      // own assets (carried across the switch) + operator, so a switch INTO a
      // split job lands on that rig's ticket (not a merged one).
      const ticketResult = await findOrCreateTicket(
        newCustomerId, newCustomer?.name, newJobId, newJob?.title || newJob?.name || null,
        undefined, undefined, undefined,
        { splitBy: newJob?.splitBy || 'combined', assetIds: Array.isArray(entry.assetIds) ? entry.assetIds : [], personId }
      );
      newTicketId = ticketResult.ticketId;

      const _reassignLineData = {
        type: 'EMPLOYEE', clockEntryId: null, personId, personName: person?.name || '', role,
        fromTime: Timestamp.now(), toTime: null, hours: 0, rate: 0 /* TASK-9646 S5: man-hour lines carry no rate */, rateUnit: newRateUnit || 'Hourly', lineAmount: 0,
        revenueCategory: newRevCat || '', notes: null
      };
      if (!isTimeBasedUnit(newRateUnit)) _reassignLineData.quantity = 0;
      newTicketLineId = await createTicketLine(newTicketId, _reassignLineData);

      // Ensure ticket is draft + bump employeeCount
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', newTicketId), {
        status: 'draft',
        employeeCount: increment(1),
        updatedAt: Timestamp.now()
      });
    }

    // 2c. Create new clockEntry
    // clock-double-tap — point the person's open-shift lock at the new segment
    // first (the old one is closed now, so the lock is free). A claim that
    // cannot be taken is not fatal here: the old shift is already closed, and
    // the rules decide on the create below.
    if (_csg) {
      const _c = await _csg.claim(_clockCsgDeps(), tenant, personId, _switchNewEntryRef.id);
      if (!_c.ok) console.warn('[ClockShiftGuard] switch: lock held elsewhere, writing the new segment anyway', _c.held);
    }
    const newClockEntryRef = _switchNewEntryRef;
    // TASK-9713 — same write as a clock-in (the old segment is already closed).
    const _switchEntryData = {
      personId, personName: person?.name || '',
      customerId: isShop ? null : (newCustomerId || ''),
      customerName: isShop ? 'Shop' : (newCustomer?.name || ''),
      jobId: newJobId || null, jobTitle: newJob?.title || newJob?.name || '',
      notes: '', clockIn: Timestamp.now(), status: 'active', source: 'main_app',
      ticketId: newTicketId || null, ticketLineId: newTicketLineId || null,
      roundToHalfHour: newRoundHalf || false, role, rate, rateUnit: newRateUnit || 'Hourly',
      previousEntryId: entryId,
      ...clockCodeStampFields(_switchAuth, 'in'), // TASK-9941 F S2
    };
    await (window.ClockShiftGuard?.writeClockIn ? window.ClockShiftGuard.writeClockIn(setDoc, newClockEntryRef, _switchEntryData) : setDoc(newClockEntryRef, _switchEntryData));
    _clockConsumeGrant(entry.personId); // TASK-9941 F S2 — one punch per grant
    if (ClockState.clockOutAuth && ClockState.clockOutAuth.personId === entry.personId) ClockState.clockOutAuth = null;

    // 2d. Link ticketLine back to clockEntry
    if (newTicketId && newTicketLineId) {
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', newTicketId, 'ticketLines', newTicketLineId), {
        clockEntryId: newClockEntryRef.id
      });
    }

    closeModal('switch-job');
    // Also close clock-out modal if switch was triggered from there
    if (ClockState._switchFromClockOut) {
      // TASK-4563 (B) — the switch already wrote this segment's note to the ENTRY
      // (clockOutNotes, per-operator). closeModal('clock-out') runs autoSaveOnClose,
      // which blind-writes the entry draftShiftNotes from the STALE
      // #clock-out-shift-notes box onto the OLD ticket the switch just finalized
      // (ClockState.clockOutEntry is never updated here) — an empty box therefore
      // ERASES the note. Mark the entry consumed so the auto-save skips ONLY those
      // two writes. 🔴 COD Adversary: do NOT early-return the whole auto-save —
      // dumps/waterRefills and the MERGED invoice notes are legitimate pre-switch
      // edits and must still fire.
      ClockState._switchConsumedEntryId = entry.id;   // COD r2: keyed to THIS entry, never a bare global boolean
      closeModal('clock-out');
      ClockState._switchFromClockOut = false;
    }
    showToast(`Switched to ${isShop ? 'Shop' : escapeHtml(newCustomer?.name || 'new job')}`, 'success');
  } catch (err) {
    console.error('[Clock] Switch job error:', err);
    // TASK-1160 — timeout / already-closed already showed a specific message.
    if (!err?._userMessageShown) showToast('Error switching job', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">swap_horiz</span> Switch'; }
  }
}

function switchTicketFromClockOut() {
  const entry = ClockState.clockOutEntry;
  if (!entry) { showToast('No active clock entry', 'error'); return; }
  ClockState._switchFromClockOut = true;
  showSwitchJobModal(entry.id);
}

// ============================================
// MODAL HELPERS
// ============================================
async function autoSaveOnClose() {
  const entry = ClockState.clockOutEntry;
  // TASK-4563 (B) — one-shot, consumed HERE so it can never leak past the early
  // return below (COD r2 Code Reviewer) and only ever applies to the entry the
  // switch actually closed (COD r2 Adversary: shared PIN kiosks mean a concurrent
  // close on ANOTHER entry must not inherit this suppression).
  const _consumedEntryId = ClockState._switchConsumedEntryId || null;
  ClockState._switchConsumedEntryId = null;
  const _switchConsumed = !!_consumedEntryId && _consumedEntryId === entry?.id;
  if (!entry?.ticketId || !shouldBuildTickets()) return;
  const tenant = ClockState.tenantId;
  const ticketId = entry.ticketId;
  try {
    const updates = { updatedAt: Timestamp.now() };

    // Shift notes — saved ONLY as the per-person draft on this person's own
    // clock entry (TASK-1327), below. 🔴 TASK-9693: never onto the shared
    // ticket doc (every driver-PIN session can read it). TASK-4563 (B): when a
    // job switch already persisted this segment's note to the entry, the stale
    // box must not overwrite it — gated on _switchConsumed below.
    const shiftNotes = document.getElementById('clock-out-shift-notes')?.value || '';

    // TASK-1397 — ticket notes are NOT part of this blind update any more.
    // This path used to overwrite the shared crew field from a textarea
    // snapshotted at modal-open, so simply closing the modal erased a
    // coworker's line. It now goes through the merging write path below,
    // with its own try/catch: an auto-save is exactly where a lost invoice
    // note would otherwise go unnoticed, so a failure gets a toast.
    const _invoiceNotesText = document.getElementById('clock-out-ticket-notes')?.value || '';

    // Save field activity
    const dumps = parseInt(document.getElementById('field-dumps')?.value) || 0;
    const waterRefills = parseInt(document.getElementById('field-water-refills')?.value) || 0;
    updates.dumps = dumps;
    updates.waterRefills = waterRefills;

    // TASK-4699 — this function runs on EVERY clock-out modal close, edited or
    // not, and blind-writes the form back. An unconditional stamp here would
    // flag a ticket "edited since sent" because someone opened and closed the
    // modal — noise, and noise is what makes Lacey stop believing the flag.
    // So it is gated on a REAL change: the shift-notes box against the
    // per-person draft it was restored from (TASK-1327). Skipped entirely when
    // a job switch already consumed this segment's note, because the entry
    // draft write below is skipped on that branch.
    //
    // KNOWN, NARROW GAP, stated rather than papered over: dumps / waterRefills
    // changed and then closed WITHOUT pressing Save are not covered here — the
    // stored values are not in memory to compare against, and a read to get
    // them is a round trip on the driver's close path. Pressing Save (the
    // normal flow, saveTicketFromForm) covers both fields correctly.
    if (!_switchConsumed && shiftNotes !== (entry.draftShiftNotes || '')) {
      updates.ticketEmailDirtyAt = Timestamp.now();
    }

    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), updates);
    if (entry?.id && !_switchConsumed) {
      await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entry.id), {
        draftShiftNotes: shiftNotes, updatedAt: Timestamp.now()
      });
    }
    // TASK-1397 — merge the shared invoice notes (never overwrite).
    try {
      await saveInvoiceNotes({
        tenantId: tenant,
        ticketId,
        currentText: _invoiceNotesText,
        syncElId: 'clock-out-ticket-notes',
        surface: 'clock_autosave',
        noPrompt: true // TASK-10036 — the modal is closing: keep, never ask
      });
    } catch (noteErr) {
      console.error('[Clock] Invoice-notes auto-save failed:', noteErr);
      window.logError?.(noteErr, { module: 'clock', operation: 'INVOICE_NOTES_AUTOSAVE', ticketId });
      window.showToast?.('Invoice notes may not have saved — reopen the ticket and check', 'error');
    }
    console.log('[Clock] Auto-saved on close:', ticketId);
  } catch (err) {
    console.warn('[Clock] Auto-save on close failed:', err);
  }
}

// TASK-9402 integration COD r1 — bumped SYNCHRONOUSLY when the clock-out modal
// closes, so a confirmClockOut waiting on a photo conversion sees the Cancel.
let _clockOutCloseGen = 0;

async function closeModal(type) {
  if (type === 'clock-out') _clockOutCloseGen++;
  if (type === 'clock-out') stopClockReviewDwellWatch(); // TASK-4616 — sync, before the autosave await
  document.getElementById(`modal-${type}`)?.classList.add('hidden');
  if (type === 'timeoff-review') ClockState.selectedRequest = null;
  if (type === 'clock-out') {
    // Auto-save notes and field activity on close (even without clicking Save)
    await autoSaveOnClose();
    stopClockOutTimer();
    ClockState.clockOutEntry = null;
    ClockState.clockOutPotholes = [];
    ClockState.clockOutPhotos = [];
    ClockState.reviewMode = false;
    ClockState.reviewData = null;
    // Clear deletion tracking arrays
    ClockState._deletedDowntimeIds = [];
    ClockState._deletedStandbyIds = [];
    ClockState._deletedEquipmentIds = []; // TASK-1396
    ClockState._boundsProblemsAcknowledged = 0; // TASK-1396/1399
    ClockState._reviewGateInFlight = false;
    // Clear save-from-form dedup flags
    ClockState._appliedEquipment = false;
    ClockState._appliedDowntime = false;
    ClockState._appliedStandby = false;
    ClockState._appliedFieldActivity = false;
    ClockState._appliedPotholes = false;
    ClockState._appliedNotes = false;
    ClockState._invoiceNotesBaseline = ''; // TASK-1397 — never carry one ticket's baseline onto the next
    ClockState._appliedPhotos = false;
    ClockState.fieldTicketLineItems = [];
    ClockState.fieldTicketSettings = null;
    ClockState.contractId = null;
    ClockState._fieldItemSaved = {}; // TASK-9665
    // TASK-9402 COD r5 — resetStampCapture also bumps the pick generation, so
    // a stamp still converting for THIS ticket can't land on the next one.
    resetStampCapture();
    // Clear time validation flags
    ClockState._timeValidationFlags = {};
    // Restore header in case closed from review screen
    const closeOutLabel = shouldBuildTickets() ? 'Build Ticket' : 'Close Out';
    const headerEl = document.querySelector('#modal-clock-out .modal-header h3');
    if (headerEl) headerEl.textContent = closeOutLabel;
    // Restore footer to default
    const footerEl = document.querySelector('#modal-clock-out .modal-footer');
    if (footerEl) {
      footerEl.innerHTML = `
        <button class="btn btn-secondary" onclick="window.ClockModule.closeModal('clock-out')">Cancel</button>
        ${shouldBuildTickets() ? '<button class="btn btn-primary" id="btn-save-ticket-form" onclick="window.ClockModule.saveTicketFromForm()"><span class="material-icons">save</span> Save</button>' : ''}
        ${shouldBuildTickets() ? '<button class="btn btn-secondary" onclick="window.ClockModule.switchTicketFromClockOut()" title="Switch to a different customer/job without clocking out"><span class="material-icons">swap_horiz</span> Switch Ticket</button>' : ''}
        <button class="btn btn-danger" id="btn-confirm-clock-out" onclick="window.ClockModule.confirmClockOut()">
          <span class="material-icons">logout</span> ${closeOutLabel}
        </button>
      `;
    }
  }
}

// ============================================
// UTILITIES
// ============================================
// clock-dst-all — every typed clock time is COMPANY time, read through
// window.TenantLocalTime (utils/tenantLocalTime.js, read at CALL time per
// Pitfall #1). toLocalDatetimeStr fills a box from a stored instant in the
// company zone (and remembers which repeated-hour instant it was, per `key`);
// _tltDate / _tltMs read a box back (STRICT: NaN for an unresolved
// repeated-hour time — run _clockResolveTimes first on every save path);
// _tltPeek is for previews / red borders only.
function _tlt() { return window.TenantLocalTime || null; }
function toLocalDatetimeStr(d, key) {
  const T = _tlt();
  if (T) return T.formatLocal(d, key ? { key } : undefined);
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
function _tltMs(str, key) {
  const T = _tlt();
  return T ? T.toMs(str, key ? { key } : undefined) : NaN;
}
function _tltDate(str, key) { return new Date(_tltMs(str, key)); }
function _tltPeek(str, key) {
  const T = _tlt();
  return new Date(T ? T.peekMs(str, key ? { key } : undefined) : NaN);
}
// COD 1.15.2009 — a ticket ROW's time is read through the ROW (its own answer:
// stamped from a known instant, or its own pick), never a shared cache.
function _tltRowMs(row, field) { const T = _tlt(); return T && row ? T.rowMs(row, field) : NaN; }
function _tltRowDate(row, field) { return new Date(_tltRowMs(row, field)); }
function _tltRowPeek(row, field) { const T = _tlt(); return new Date(T && row ? T.rowPeekMs(row, field) : NaN); }
// A row's hours: strict when both ends resolve (what a save writes), else the
// preview fallback (first instant) — saves skip / refuse unresolved rows first.
function calcRowHours(row) {
  if (!row) return 0;
  const a = _tltRowMs(row, 'startTime'); const b = _tltRowMs(row, 'endTime');
  if (Number.isFinite(a) && Number.isFinite(b)) return Math.max(0, (b - a) / 3600000);
  const h = (_tltRowPeek(row, 'endTime') - _tltRowPeek(row, 'startTime')) / 3600000;
  return Number.isFinite(h) ? Math.max(0, h) : 0;
}
function _rowHoursText(row) {
  const T = _tlt();
  if (!T || !row || !row.startTime || !row.endTime) return calcRowHours(row).toFixed(2) + 'h';
  const hb = T.hoursBetween(row.startTime, row.endTime, { row });
  if (hb.ok) return Math.max(0, hb.hours).toFixed(2) + 'h';
  if (hb.ambiguous && hb.options.length) return hb.options.map(o => o.hours.toFixed(2)).join(' or ') + 'h';
  return '—';
}
// A row with a time that does not resolve (never-picked repeated hour, a gap,
// an unreadable zone). Punch paths SKIP such rows (left for the office) —
// a ticket row never keeps a person on the clock.
function _clockRowUnresolved(row) {
  if (!row) return false;
  return (!!row.startTime && !Number.isFinite(_tltRowMs(row, 'startTime')))
    || (!!row.endTime && !Number.isFinite(_tltRowMs(row, 'endTime')));
}
// COD 1.15.2009 — what a punch path could NOT write, kept ON the clock entry
// (ticketTimesUnsaved) and in the CLOCK_OUT_LINE_FAIL audit row, so the office
// can finish the ticket: the rows' typed text, never a guessed instant.
function _clockUnsavedRowsSummary(lists) {
  const out = [];
  (lists || []).forEach(([type, rows]) => (rows || []).forEach((r) => {
    if (!r || (type === 'equipment' && clockIsCrewEquipmentLine(r))) return;
    if (!_clockRowUnresolved(r)) return;
    out.push({
      type, name: String(r.assetName || '').slice(0, 60) || null,
      start: String(r.startTime || '').slice(0, 16), end: String(r.endTime || '').slice(0, 16),
      notes: String(r.notes || '').slice(0, 120) || null,
    });
  }));
  return out.slice(0, 20);
}
function _clockUnsavedRecord(rows, problems) {
  return { rows: rows || [], reasons: (problems || []).map(x => String(x).slice(0, 200)).slice(0, 5), at: Timestamp.now() };
}

// Resolve every wall time a save will write: refuses an unreadable company
// zone / a time that never happened; asks which one for a repeated-hour time
// (showing the hours each gives). items: [{ str, key | row+field, label }].
// pair: optional { inStr, inKey, outStr, outKey } — describe() shows the shift
// length per choice. opts: passed to resolveAll (reusePicks / noPrompt).
// → { ok:true } | { ok:false, message, code, cancelled }
async function _clockResolveTimes(items, pair, opts) {
  const T = _tlt();
  const list = (items || []).filter(it => it && it.str);
  if (!list.length) return { ok: true };
  if (!T) return { ok: false, code: 'not-loaded', message: "Clock time tools didn't load. Refresh and try again." };
  const describe = (it, ms) => {
    if (it.row) {
      // A row: the hours this row gives with that choice, when its other end resolves.
      const otherField = it.field === 'startTime' ? 'endTime' : 'startTime';
      const o = T.rowMs(it.row, otherField);
      if (!Number.isFinite(o)) return '';
      const h = (it.field === 'startTime' ? o - ms : ms - o) / 3600000;
      return h >= 0 ? ` — ${T.hm(h)}` : '';
    }
    if (!pair) return '';
    // The other end only counts when it is itself unambiguous (else no hours —
    // never a number built on a guessed instant). Out may be "now" (outNowMs).
    const other = (str, key) => { if (!str) return NaN; const r = T.read(str, { key }); return r.ok ? r.ms : NaN; };
    let a = NaN, b = NaN;
    if (it.key === pair.inKey && it.str === pair.inStr) { a = ms; b = pair.outStr ? other(pair.outStr, pair.outKey) : (pair.outNowMs || NaN); }
    else if (it.key === pair.outKey && it.str === pair.outStr) { b = ms; a = other(pair.inStr, pair.inKey); }
    if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return '';
    return ` — ${T.hm((b - a) / 3600000)} shift`;
  };
  const r = await T.resolveAll(list, { describe, ...(opts || {}) });
  return r.ok ? { ok: true } : { ok: false, message: r.message, code: r.code, cancelled: !!r.cancelled };
}

// The PUNCH: the adjusted clock in / out boxes (ticket-building accounts only —
// the plain close-out writes "now").
function _clockPunchTimeItems() {
  if (!shouldBuildTickets()) return [];
  const items = [];
  const adjIn = getSplitDateTimeValue('adj-clock-in');
  const adjOut = getSplitDateTimeValue('adj-clock-out');
  if (adjIn) items.push({ str: adjIn, key: 'adj-clock-in', label: 'Clock in' });
  if (adjOut) items.push({ str: adjOut, key: 'adj-clock-out', label: 'Clock out' });
  return items;
}
// The TICKET: work start/stop and this person's equipment / downtime / standby
// rows (crew rows are read-only and never written). Each row's start and end
// is its own item, read through the row.
function _clockTicketTimeItems() {
  if (!shouldBuildTickets()) return [];
  const items = [];
  const ws = getSplitDateTimeValue('work-start-time');
  const we = getSplitDateTimeValue('work-stop-time');
  if (ws) items.push({ str: ws, key: 'work-start-time', label: 'Work start' });
  if (we) items.push({ str: we, key: 'work-stop-time', label: 'Work stop' });
  (ClockState.clockOutEquipment || []).forEach((eq) => {
    if (!eq || clockIsCrewEquipmentLine(eq)) return;
    const nm = eq.assetName || 'Equipment';
    if (eq.startTime) items.push({ str: eq.startTime, row: eq, field: 'startTime', label: nm + ' start' });
    if (eq.endTime) items.push({ str: eq.endTime, row: eq, field: 'endTime', label: nm + ' end' });
  });
  [['Downtime', ClockState.clockOutDowntime], ['Standby', ClockState.clockOutStandby]].forEach(([lbl, list]) => {
    (list || []).forEach((it) => {
      if (!it) return;
      if (it.startTime) items.push({ str: it.startTime, row: it, field: 'startTime', label: lbl + ' start' });
      if (it.endTime) items.push({ str: it.endTime, row: it, field: 'endTime', label: lbl + ' end' });
    });
  });
  return items;
}
function _clockFormTimeItems() { return [..._clockPunchTimeItems(), ..._clockTicketTimeItems()]; }
function _clockPunchPair() {
  const adjIn = getSplitDateTimeValue('adj-clock-in');
  const adjOut = getSplitDateTimeValue('adj-clock-out');
  return { inStr: adjIn, inKey: 'adj-clock-in', outStr: adjOut, outKey: 'adj-clock-out', outNowMs: adjOut ? null : Date.now() };
}
// Ticket-only saves (Save, Apply): every time strictly — a refusal stops THAT
// save (nobody is clocked in or out by it).
async function _clockResolveFormTimes(opts) {
  return _clockResolveTimes(_clockFormTimeItems(), _clockPunchPair(), opts);
}

// COD 1.15.2009 #1 — the clock-out PUNCH. Resolves only the adjusted clock in /
// out. A repeated-hour time still asks (Cancel = nothing written). When the
// company zone can't be read (or settings never loaded): a box the person did
// NOT touch is not a typed time — clock-in stays the stored clock-in and
// clock-out is the real current time ("now"), so the punch saves; only a box
// the person TYPED is refused, because it can't be converted safely.
// → { ok:true, keepIn, nowOut } | { ok:false, message, cancelled }
async function _clockResolvePunch(entry, opts) {
  const items = _clockPunchTimeItems();
  if (!items.length) return { ok: true, keepIn: false, nowOut: false };
  const r = await _clockResolveTimes(items, _clockPunchPair(), opts);
  if (r.ok) return { ok: true, keepIn: false, nowOut: false };
  if (r.cancelled || (r.code !== 'zone' && r.code !== 'not-loaded')) return r;
  const T = _tlt();
  const adjIn = getSplitDateTimeValue('adj-clock-in');
  const storedIn = entry && (entry.clockIn?.toDate?.() || (entry.clockIn ? new Date(entry.clockIn) : null));
  const inUntouched = !adjIn || (storedIn && T && T.formatLocal(storedIn, { noRemember: true }) === adjIn);
  const outUntouched = ClockState.clockOutTimerPaused !== true;
  if (inUntouched && outUntouched) return { ok: true, keepIn: true, nowOut: true, zoneProblem: r.message };
  return { ok: false, message: r.message + ' (A typed clock time can only be saved once the company time zone is readable.)' };
}

// The ticket rows / work times on a PUNCH path: best effort, never blocking.
// Rows that fail (gap, unreadable zone, a repeated time not picked) are left
// unresolved — the punch path skips writing them and flags the entry for review.
// → { problems: [message], cancelled }
async function _clockResolveTicketTimesBestEffort(opts) {
  const T = _tlt();
  const items = _clockTicketTimeItems();
  if (!items.length) return { problems: [] };
  if (!T) return { problems: ["Clock time tools didn't load — ticket times were not saved."] };
  const problems = [];
  const askable = [];
  for (const it of items) {
    const rd = T.read(it.str, it.row ? { row: it.row, field: it.field, label: it.label } : { key: it.key, label: it.label, picksOnlyIfReuse: true, reusePicks: !!(opts && opts.reusePicks) });
    if (rd.ok) continue;
    if (rd.code === 'ambiguous') askable.push(it);
    else problems.push(rd.error);
  }
  let cancelled = false;
  if (askable.length) {
    const r = await _clockResolveTimes(askable, _clockPunchPair(), opts);
    if (!r.ok) { cancelled = !!r.cancelled; problems.push(r.cancelled ? 'A ticket time that happened twice was not picked.' : r.message); }
  }
  return { problems, cancelled };
}

// Split date+time inputs avoid iOS 17+ datetime-local picker bug where the
// nested time scroll-wheel overlay covers the OK button. Native <input type="date">
// and <input type="time"> each use a simpler picker that doesn't trigger this.
function renderSplitDateTime(id, datetimeLocalStr, onchangeHandler = '', opts = {}) {
  const [date, time] = String(datetimeLocalStr || '').split('T');
  const cls = opts.className ? ` class="${opts.className}"` : '';
  const style = opts.style ? ` style="${opts.style}"` : '';
  const disabled = opts.disabled ? ' disabled' : '';
  // TASK-1396 — `readonly` (vs `disabled`) keeps the value legible/selectable while
  // refusing edits; used for another crew member's equipment window.
  const readonly = opts.readonly ? ' readonly' : '';
  const placeholder = opts.placeholder ? ` placeholder="${opts.placeholder}"` : '';
  const onchangeAttr = onchangeHandler ? ` onchange="${onchangeHandler}"` : '';
  return `
    <div class="datetime-split" data-datetime-id="${id}" style="display:flex;gap:4px;flex-wrap:wrap;">
      <input type="date" id="${id}-date"${cls}${style} value="${date || ''}"${onchangeAttr}${disabled}${readonly}${placeholder}>
      <input type="time" id="${id}-time"${cls}${style} value="${time ? time.slice(0, 5) : ''}"${onchangeAttr}${disabled}${readonly}${placeholder}>
    </div>
  `;
}

function getSplitDateTimeValue(id) {
  const dateEl = document.getElementById(`${id}-date`);
  const timeEl = document.getElementById(`${id}-time`);
  const date = dateEl?.value;
  const time = timeEl?.value;
  if (!date) return '';
  return `${date}T${time || '00:00'}`;
}

function setSplitDateTimeValue(id, datetimeLocalStr) {
  const [date, time] = String(datetimeLocalStr || '').split('T');
  const dateEl = document.getElementById(`${id}-date`);
  const timeEl = document.getElementById(`${id}-time`);
  if (dateEl) dateEl.value = date || '';
  if (timeEl) timeEl.value = time ? time.slice(0, 5) : '';
}

function formatDuration(ms) {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

// TASK-862 — delegates to shared window.escapeHtml (sharedFormat.js); escapes the
// identical set (&<>"', '->&#39;). Hoisted wrapper, window lookup deferred to call time.
function escapeHtml(str) {
  return window.escapeHtml(str);
}

function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatDate(date) {
  if (!date) return '-';
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatDateInput(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function truncate(str, maxLength) {
  if (!str) return '';
  return str.length > maxLength ? str.slice(0, maxLength) + '...' : str;
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

// TASK-191 Phase 3 — render an "out of zone" warning pill on a shift-review card
// when a clock-in OR clock-out landed outside the applied geofence. Display/audit
// only — never blocks anything. Returns '' when in-zone or not-checked (no flag).
// The flag is only ever set when geofence was enabled AND coords were captured,
// so a missing/false flag = in-zone or no-GPS = no pill (correct).
function buildGeofencePillHTML(entry) {
  if (!entry) return '';
  const outIn = entry.outOfGeofence === true;
  const outOut = entry.clockOutOfGeofence === true;
  if (!outIn && !outOut) return '';
  // Use the distance for whichever side is flagged (separate in/out fields so we
  // never show the wrong number). When both are flagged, show the larger one.
  const inFt = outIn && typeof entry.geofenceDistanceFt === 'number' ? entry.geofenceDistanceFt : null;
  const outFt = outOut && typeof entry.clockOutGeofenceDistanceFt === 'number' ? entry.clockOutGeofenceDistanceFt : null;
  const distFt = (inFt != null && outFt != null) ? Math.max(inFt, outFt) : (outFt != null ? outFt : inFt);
  const distLabel = distFt != null ? ` — ${distFt.toLocaleString()}ft` : '';
  const which = (outIn && outOut) ? 'in & out' : (outOut ? 'out' : 'in');
  return `<div class="wf-geofence-pill" title="Clock-${which} was outside the job geofence (advisory only — the clock-in/out still went through)" style="display:inline-flex;align-items:center;gap:3px;margin-top:4px;padding:2px 8px;border-radius:10px;background:rgba(245,158,11,0.14);color:#B45309;font-size:0.72rem;font-weight:600;line-height:1.4;">📍 out of zone${distLabel}</div>`;
}

function calculateDays(start, end) {
  const startDate = start instanceof Date ? start : new Date(start);
  const endDate = end instanceof Date ? end : new Date(end);
  return Math.ceil(Math.abs(endDate - startDate) / 86400000) + 1;
}

function getTypeIcon(type) {
  return { vacation: 'beach_access', sick: 'healing', personal: 'person', bereavement: 'favorite', jury_duty: 'gavel', other: 'event' }[type] || 'event';
}

function formatType(type) {
  return { vacation: 'Vacation', sick: 'Sick Leave', personal: 'Personal Day', bereavement: 'Bereavement', jury_duty: 'Jury Duty', other: 'Other' }[type] || type;
}

function startClockUpdate() {
  // Clear any existing interval to prevent stacking
  if (ClockState.clockUpdateInterval) {
    clearInterval(ClockState.clockUpdateInterval);
    ClockState.clockUpdateInterval = null;
  }
  const updateClock = () => {
    const el = document.getElementById('current-time');
    if (el) {
      el.textContent = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' });
    }
    if (ClockState.activeTab === 'clock') {
      renderActiveList();
      updateSelectedPersonStatus();
    }
  };
  updateClock();
  ClockState.clockUpdateInterval = setInterval(updateClock, 1000);
}

// ============================================
// POTHOLE FUNCTIONS (founder only)
// ============================================
function addPothole() {
  ClockState.clockOutPotholes.push({
    type: '',
    tolFeet: 0,
    tolInches: 0,
    pipeSize: '',
    gpsLat: null,
    gpsLng: null,
    notes: '',
    photos: []
  });
  refreshPotholeList();
}

function removePothole(idx) {
  ClockState.clockOutPotholes.splice(idx, 1);
  refreshPotholeList();
}

function updatePotholeField(idx, field, value) {
  ClockState.clockOutPotholes[idx][field] = value;
}

function grabPotholeGPS(idx) {
  if (!navigator.geolocation) {
    window.showToast('GPS not supported in this browser', 'error');
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      ClockState.clockOutPotholes[idx].gpsLat = pos.coords.latitude;
      ClockState.clockOutPotholes[idx].gpsLng = pos.coords.longitude;
      refreshPotholeList();
      window.showToast('GPS captured', 'success');
    },
    (err) => window.showToast('GPS unavailable: ' + err.message, 'error'),
    { enableHighAccuracy: true, timeout: 10000 }
  );
}

function refreshPotholeList() {
  const el = document.getElementById('pothole-list');
  if (!el) return;

  if (ClockState.clockOutPotholes.length === 0) {
    el.innerHTML = '<div class="empty-state-small" style="padding:var(--spacing-sm) 0;">No line entries</div>';
    return;
  }

  el.innerHTML = ClockState.clockOutPotholes.map((ph, idx) => {
    const hasGPS = ph.gpsLat !== null && ph.gpsLng !== null;
    const photos = ph.photos || [];
    return `
      <div style="background:var(--bg-elevated);border-radius:var(--radius-md);padding:var(--spacing-sm);margin-bottom:var(--spacing-sm);">
        <div style="display:flex;gap:var(--spacing-sm);flex-wrap:wrap;align-items:center;margin-bottom:var(--spacing-xs);">
          <div style="flex:1;min-width:120px;">
            <label style="font-size:0.7rem;color:var(--text-secondary);display:block;">Type of Line</label>
            <input type="text" value="${escapeHtml(ph.type)}" placeholder="e.g. poly, metal"
                   style="width:100%;padding:4px;font-size:0.85rem;"
                   onchange="window.ClockModule.updatePotholeField(${idx},'type',this.value)">
          </div>
          <button type="button" class="btn-icon" onclick="window.ClockModule.removePothole(${idx})" title="Remove" style="align-self:flex-end;">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div style="display:flex;gap:var(--spacing-sm);flex-wrap:wrap;align-items:flex-end;margin-bottom:var(--spacing-xs);">
          <div style="min-width:60px;">
            <label style="font-size:0.7rem;color:var(--text-secondary);display:block;">TOL (ft)</label>
            <input type="number" value="${ph.tolFeet}" min="0" style="width:60px;padding:4px;font-size:0.85rem;"
                   onchange="window.ClockModule.updatePotholeField(${idx},'tolFeet',parseInt(this.value)||0)">
          </div>
          <div style="min-width:70px;">
            <label style="font-size:0.7rem;color:var(--text-secondary);display:block;">TOL (in)</label>
            <select onchange="window.ClockModule.updatePotholeField(${idx},'tolInches',parseFloat(this.value)||0)" style="padding:4px;font-size:0.85rem;">
              <option value="0" ${ph.tolInches === 0 ? 'selected' : ''}>0</option>
              <option value="3" ${ph.tolInches === 3 ? 'selected' : ''}>3</option>
              <option value="6" ${ph.tolInches === 6 ? 'selected' : ''}>6</option>
              <option value="9" ${ph.tolInches === 9 ? 'selected' : ''}>9</option>
            </select>
          </div>
          <div style="min-width:70px;">
            <label style="font-size:0.7rem;color:var(--text-secondary);display:block;">Pipe (in)</label>
            <input type="text" value="${escapeHtml(ph.pipeSize)}" style="width:70px;padding:4px;font-size:0.85rem;" placeholder="e.g. 8"
                   onchange="window.ClockModule.updatePotholeField(${idx},'pipeSize',this.value)">
          </div>
          <button type="button" class="btn btn-sm ${hasGPS ? 'btn-success' : 'btn-secondary'}" onclick="window.ClockModule.grabPotholeGPS(${idx})" style="white-space:nowrap;">
            <span class="material-icons" style="font-size:16px;">my_location</span>
            ${hasGPS ? `${ph.gpsLat.toFixed(4)}, ${ph.gpsLng.toFixed(4)}` : 'Grab GPS'}
          </button>
        </div>
        <input type="text" value="${escapeHtml(ph.notes)}" placeholder="Notes..." style="width:100%;padding:4px;font-size:0.85rem;margin-bottom:var(--spacing-xs);"
               onchange="window.ClockModule.updatePotholeField(${idx},'notes',this.value)">
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-top:4px;">
          ${photos.map((p, pi) => `
            <div style="position:relative;width:60px;height:60px;border-radius:var(--radius-sm);overflow:hidden;border:1px solid var(--border-light);flex-shrink:0;">
              ${p.preview ? `<img src="${p.preview}" style="width:100%;height:100%;object-fit:cover;">` : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:var(--bg-elevated);"><span class="material-icons" style="font-size:20px;color:var(--text-secondary);">description</span></div>`}
              <button type="button" onclick="window.ClockModule.removePotholePhoto(${idx},${pi})"
                      style="position:absolute;top:1px;right:1px;background:rgba(0,0,0,0.6);color:#fff;border:none;border-radius:50%;width:18px;height:18px;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;">
                <span class="material-icons" style="font-size:12px;">close</span>
              </button>
            </div>
          `).join('')}
          <label style="width:60px;height:60px;border:2px dashed var(--border-light);border-radius:var(--radius-sm);display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;">
            <span class="material-icons" style="font-size:20px;color:var(--text-secondary);">add_a_photo</span>
            <span style="font-size:0.55rem;color:var(--text-secondary);">Photo</span>
            <input type="file" hidden accept="image/*" capture="environment" multiple
                   onchange="window.ClockModule.handlePotholePhotoSelect(${idx}, this.files); this.value='';">
          </label>
        </div>
      </div>
    `;
  }).join('');
}

// ============================================
// PER-LINE PHOTO FUNCTIONS (founder only)
// ============================================
function handlePotholePhotoSelect(potholeIdx, files) {
  if (!files || files.length === 0) return;
  const ph = ClockState.clockOutPotholes[potholeIdx];
  if (!ph) return;
  if (!ph.photos) ph.photos = [];

  Array.from(files).forEach(file => {
    if (file.type.startsWith('image/')) {
      // TASK-1383 — guarded shared reader. On a failed read the photo used to
      // be silently DROPPED (resolve-only reader, no onerror); now it falls
      // back to the no-preview path so the file still attaches + uploads.
      window.FileReadUtil.readAsDataURL(file).then((dataUrl) => {
        ph.photos.push({ file, preview: dataUrl, name: file.name });
        refreshPotholeList();
      }).catch(() => {
        ph.photos.push({ file, preview: null, name: file.name });
        refreshPotholeList();
      });
    } else {
      ph.photos.push({ file, preview: null, name: file.name });
      refreshPotholeList();
    }
  });
}

function removePotholePhoto(potholeIdx, photoIdx) {
  const ph = ClockState.clockOutPotholes[potholeIdx];
  if (!ph || !ph.photos) return;
  ph.photos.splice(photoIdx, 1);
  refreshPotholeList();
}

// TASK-9958 — who is attaching photos at clock-out, from the session: the auth
// uid, plus the PIN driver's server-validated name/personId, or the signed-in
// member's. Never typed. null = no uid (store nothing rather than a guess).
function _clockFileUploader() {
  const A = window.AppState || {};
  const uid = auth?.currentUser?.uid || A.user?.uid || '';
  const pin = !!(A.driverModePin || A.driverMode);
  const ma = A.memberAccess || null;
  const name = (pin ? A.driverPersonName : '') || ma?.displayName || ma?.personName
    || A.user?.displayName || A.user?.email || A.driverPersonName || '';
  const personId = (pin ? A.driverPersonId : '') || ma?.personId || '';
  return buildTicketDocUploader({ uid, name, personId });
}

// TASK-9958 — one auditLog FILE_ADDED row per photo now on the ticket, after
// the write that attached it. Fire-and-forget.
function _clockAuditPhotosAdded(target, ticketId, ticketLineId, rows) {
  try {
    const uid = auth?.currentUser?.uid || window.AppState?.user?.uid || '';
    for (const r of (rows || [])) {
      if (!r) continue;
      window.logAudit?.('ticket', 'FILE_ADDED', fileAddedAuditPayload({
        entry: { name: r.name, fullPath: r.path || null }, target, via: 'clock_out', ticketId, ticketLineId, uid,
      }));
    }
  } catch (_) { /* best-effort */ }
}

async function uploadPotholePhotos(ticketId, ticketLineId, photos) {
  if (!photos || photos.length === 0) return;
  const tenant = ClockState.tenantId;
  const uploaded = [];
  const _uploader = _clockFileUploader(); // TASK-9958
  const _paths = [];

  for (const photo of photos) {
    try {
      const timestamp = Date.now();
      const safeName = photo.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `tickets/${tenant}/${ticketId}/potholes/${ticketLineId}/${timestamp}_${safeName}`;
      const storageRef = ref(storage, path);
      // TASK-9397 — never store a raw HEIC: convert, then JPEG-compress it
      // (Safari hands back the HEIC itself; processPhoto re-encodes it).
      let body = photo.file;
      if (await detectHeic(photo.file)) {
        body = await processPhoto(await toDrawablePhoto(photo.file), { maxDimension: 1600, quality: 0.8, maxSizeKB: 2000 });
      }
      await uploadBytes(storageRef, body, { contentType: body?.type || 'image/jpeg', customMetadata: fileUploadCustomMetadata(_uploader?.uid, 'clock_out') });
      const url = await getDownloadURL(storageRef);
      uploaded.push({ url, name: photo.name, uploadedAt: new Date().toISOString(), uploadedVia: 'clock_out', ...(_uploader ? { uploadedBy: _uploader } : {}) }); // TASK-9958
      _paths.push({ name: photo.name, path });
    } catch (err) {
      console.error('[Clock] Pothole photo upload error:', err?.cause || err);
      if (err?.userMessage) window.showToast?.(`${photo.name}: ${err.userMessage}`, 'error');
    }
  }

  if (uploaded.length > 0) {
    await updateDoc(
      doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', ticketLineId),
      { photos: uploaded, updatedAt: Timestamp.now() }
    );
    _clockAuditPhotosAdded('ticket_line_photos', ticketId, ticketLineId, _paths); // TASK-9958
  }
}

// ============================================
// PHOTO FUNCTIONS (founder only)
// ============================================
function handlePhotoSelect(files) {
  if (!files || files.length === 0) return;

  const selected = Array.from(files);
  const addedCount = selected.length;

  // If the ticket was already saved this session, a newly-added photo would NOT
  // upload (saveTicketFromForm sets _appliedPhotos=true after upload). Reset so
  // anything added after a save still uploads on the next save / clock-out.
  ClockState._appliedPhotos = false;

  let pending = 0;
  selected.forEach(file => {
    if (file.type.startsWith('image/')) {
      pending++;
      // TASK-1383 — guarded shared reader (adds abort/null-result/sync-throw/
      // timeout settlement the old onerror-only wrapper missed, so `pending`
      // can never wedge and _onPhotosAdded always fires).
      window.FileReadUtil.readAsDataURL(file).then((dataUrl) => {
        // _new flags a just-attached (staged, not yet uploaded) photo for the badge.
        ClockState.clockOutPhotos.push({ file, preview: dataUrl, name: file.name, _new: true });
        refreshPhotoList();
        pending--;
        if (pending === 0) _onPhotosAdded(addedCount);
      }).catch(() => {
        // Integrated-gate Code Reviewer: a failed PREVIEW read must not DROP a
        // driver's evidentiary photo while the counter still says "added" —
        // stage it preview-less (the raw file uploads fine; DVIR/HOS record
        // integrity beats a thumbnail).
        ClockState.clockOutPhotos.push({ file, preview: null, name: file.name, _new: true });
        refreshPhotoList();
        pending--;
        if (pending === 0) _onPhotosAdded(addedCount);
      });
    } else {
      // PDF or other doc — no preview
      ClockState.clockOutPhotos.push({ file, preview: null, name: file.name, _new: true });
      refreshPhotoList();
    }
  });

  // PDFs (no async read) confirm immediately; images confirm when all readers settle.
  if (pending === 0) _onPhotosAdded(addedCount);

  // Clear input so same file can be re-selected
  const input = document.getElementById('photo-input');
  if (input) input.value = '';
}

// In-modal confirmation after photos are staged (NOT uploaded — upload happens
// at clock-out / ticket save). Keeps the user in the same modal and makes the
// added photo obvious.
function _onPhotosAdded(addedCount) {
  if (addedCount > 0) {
    window.showToast?.(
      addedCount === 1 ? 'Photo added' : `${addedCount} photos added`,
      'success'
    );
  }
  // Scroll the most-recent thumbnail into view inside the modal body so it's
  // visible after the picker closes (esp. on phones). Scoped to the modal
  // scroll container, block:'nearest' to avoid yanking the viewport.
  const thumbs = document.getElementById('photo-thumbnails');
  const last = thumbs?.lastElementChild;
  if (last && typeof last.scrollIntoView === 'function') {
    try { last.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (_) { /* no-op */ }
  }
}

async function removePhoto(idx) {
  const photo = ClockState.clockOutPhotos[idx];
  if (!photo) return;
  if (photo._restored) {
    // TASK-1214 — already uploaded: the old splice-only path left the photo on
    // the ticket doc (it reappeared on reopen + rendered on the customer PDF).
    // Route through the shared helper (confirm + arrayRemove + audit receipt).
    const res = await window.TicketPhotos?.removeUploaded({
      tenantId: ClockState.tenantId,
      ticketId: ClockState._photoTicketId || ClockState.clockOutEntry?.ticketId || null,
      photo: photo._originalPhoto || { url: photo._url || photo.url, name: photo.name },
      source: 'clock-kiosk',
      personId: ClockState.clockOutEntry?.personId || null,
      personName: ClockState.clockOutEntry?.personName || null,
      respectDriverLock: true
    });
    if (!res?.ok) return;
    // Splice by identity — the index may have shifted during the await.
    const i = ClockState.clockOutPhotos.indexOf(photo);
    if (i !== -1) ClockState.clockOutPhotos.splice(i, 1);
  } else {
    // Staged-only (never uploaded) — local removal is complete removal.
    ClockState.clockOutPhotos.splice(idx, 1);
  }
  refreshPhotoList();
}

// TASK-1220 — tap a thumbnail → full-size lightbox (PDFs keep a new tab;
// staged PDFs have no url/preview, so they stay non-clickable).
function showPhotoAt(idx) {
  const photo = ClockState.clockOutPhotos[idx];
  if (!photo) return;
  const isImage = photo._restored
    ? !photo.name?.toLowerCase().endsWith('.pdf')
    : photo.file?.type?.startsWith('image/');
  const url = photo._restored ? (photo._url || photo.url) : photo.preview;
  if (!url) return;
  // TASK-9855 — saved files open through the Storage SDK by path.
  const fileRef = photo._originalPhoto || url;
  if (isImage) {
    // A local preview (this session's pick) needs no fetch.
    if (/^(data|blob):/i.test(String(photo.preview || ''))) { window.PhotoLightbox?.show(photo.preview); return; }
    window.StorageFile.blobUrl(fileRef).then(u => window.PhotoLightbox?.show(u))
      .catch(() => window.showToast?.('Could not open the photo', 'error'));
  } else if (photo._restored) window.StorageFile.open(fileRef);
}

function refreshPhotoList() {
  const el = document.getElementById('photo-thumbnails');
  const countLabel = document.getElementById('photo-count-label');

  // Header count = ALL staged + restored photos.
  if (countLabel) {
    const n = ClockState.clockOutPhotos.length;
    countLabel.textContent = n > 0 ? ` (${n})` : '';
  }

  if (!el) return;

  if (ClockState.clockOutPhotos.length === 0) {
    el.innerHTML = '';
    return;
  }

  el.innerHTML = ClockState.clockOutPhotos.map((photo, idx) => {
    const isImage = photo._restored
      ? !photo.name?.toLowerCase().endsWith('.pdf')
      : photo.file?.type?.startsWith('image/');
    // "Saved" = already uploaded (_restored). "Attached" = staged this session (_new),
    // not yet uploaded — distinct copy so we don't imply a save that hasn't happened.
    let badge = '';
    if (photo._restored) {
      badge = '<div style="position:absolute;bottom:2px;left:2px;background:rgba(0,0,0,0.6);color:#4ade80;border-radius:4px;padding:0 3px;font-size:0.5rem;display:flex;align-items:center;gap:2px;"><span class="material-icons" style="font-size:10px;">check_circle</span>Saved</div>';
    } else if (photo._new) {
      badge = '<div style="position:absolute;bottom:2px;left:2px;background:rgba(0,0,0,0.6);color:#4ade80;border-radius:4px;padding:0 3px;font-size:0.5rem;display:flex;align-items:center;gap:2px;"><span class="material-icons" style="font-size:10px;">check</span>Attached</div>';
    }
    const savedBadge = badge;
    // TASK-1220 — image tiles (and saved PDFs) are tappable: images open the
    // in-app lightbox, PDFs a new tab. TASK-1214 — the ✕ is hidden ENTIRELY on
    // locked tickets for uploaded photos (never a disabled button), and its
    // hit target is ≥44px with a smaller visible circle.
    const viewable = photo._restored ? true : (isImage && !!photo.preview);
    const hideRemove = photo._restored && ClockState._ticketPhotoLock;
    return `
      <div ${viewable ? `onclick="window.ClockModule.showPhotoAt(${idx})"` : ''} style="position:relative;display:inline-block;width:80px;height:80px;margin:0 6px 6px 0;border-radius:var(--radius-sm);overflow:hidden;border:1px solid var(--border-light);vertical-align:top;${viewable ? 'cursor:pointer;' : ''}">
        ${isImage && photo.preview
          // COD (Code Reviewer) — escape Firestore-sourced URLs in the attr;
          // staged previews are local FileReader data URIs (base64, no markup
          // chars) — skip escaping those to avoid churning multi-MB strings.
          // TASK-9855 — a SAVED photo loads through the Storage SDK by path
          // (rules-checked), never the stored token URL; a local preview
          // (data:/blob: from this session's pick) is used as-is.
          ? (/^(data|blob):/i.test(String(photo.preview))
              ? `<img src="${photo.preview}" style="width:100%;height:100%;object-fit:cover;">`
              : `<img ${window.StorageFile.imgAttrs(photo._originalPhoto || photo.preview)} style="width:100%;height:100%;object-fit:cover;">`)
          : `<div style="width:100%;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;background:var(--bg-elevated);">
               <span class="material-icons" style="font-size:28px;color:var(--text-secondary);">description</span>
               <span style="font-size:0.55rem;color:var(--text-secondary);text-align:center;padding:0 2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%;">${escapeHtml(photo.name)}</span>
             </div>`
        }
        ${savedBadge}
        ${hideRemove ? '' : `<button type="button" aria-label="Delete photo" onclick="event.stopPropagation();window.ClockModule.removePhoto(${idx})"
                style="position:absolute;top:0;right:0;width:44px;height:44px;background:transparent;color:#fff;border:none;cursor:pointer;padding:0;display:flex;align-items:flex-start;justify-content:flex-end;">
          <span style="margin:2px 2px 0 0;background:rgba(0,0,0,0.6);border-radius:50%;width:22px;height:22px;display:flex;align-items:center;justify-content:center;"><span class="material-icons" style="font-size:14px;">close</span></span>
        </button>`}
      </div>
    `;
  }).join('');
}

function summarizePhotoFailures(failures) {
  if (!failures || failures.length === 0) return null;
  const byCode = {};
  for (const f of failures) {
    const code = f.code || 'unknown';
    byCode[code] = (byCode[code] || 0) + 1;
  }
  const codeList = Object.entries(byCode).map(([code, n]) => `${n}×${code}`).join(', ');
  const n = failures.length;
  return `${n} photo${n > 1 ? 's' : ''} failed: ${codeList}. Ticket saved.`;
}

async function uploadClockOutPhotos(ticketId) {
  if (ClockState.clockOutPhotos.length === 0) return { uploaded: 0, failures: [] };

  const tenant = ClockState.tenantId;
  const photos = [];
  const failures = [];
  // Staged photos uploaded to Storage this pass, paired with their url. We mark them
  // _restored ONLY after the ticket-doc arrayUnion succeeds (below) — otherwise a
  // Storage-ok / updateDoc-fail would flag them uploaded but leave them unattached.
  const uploadedStaged = [];
  // TASK-9958 (COD r1 🔴) — declared HERE, in this function's own scope. It was
  // only declared in uploadPotholePhotos, so `_uploader?.uid` below threw a
  // ReferenceError inside the per-photo try and EVERY clock-out photo was lost.
  const _uploader = _clockFileUploader();

  for (const photo of ClockState.clockOutPhotos) {
    // Skip already-uploaded photos (restored from Firestore)
    if (photo._restored) continue;
    const fileMeta = {
      name: photo.name,
      type: photo.file?.type || '(no type)',
      size: photo.file?.size ?? null,
    };
    try {
      const timestamp = Date.now();
      const safeName = photo.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const isPdf = photo.file.type === 'application/pdf' || photo.name.toLowerCase().endsWith('.pdf');

      let uploadBlob = photo.file;
      // Compress images before upload (skip PDFs).
      // TASK-9397 — a HEIC used to fail compression on Windows Chrome/Firefox
      // and fall through to "uploading raw": a ticket photo only Safari can
      // show. It is converted first; a failed conversion lands in `failures`
      // (the driver is told) and raw HEIC is never uploaded.
      if (!isPdf) {
        const isHeic = await detectHeic(photo.file);
        const drawable = isHeic ? await toDrawablePhoto(photo.file) : photo.file;
        if (isHeic || photo.file.type.startsWith('image/')) {
          try {
            uploadBlob = await processPhoto(drawable, { maxDimension: 1600, quality: 0.8, maxSizeKB: 2000 });
          } catch (compressErr) {
            if (isHeic && drawable === photo.file) throw compressErr;
            uploadBlob = drawable;
            console.warn('[Clock] Photo compression failed, uploading uncompressed:', compressErr);
          }
        }
      }

      const path = `tickets/${tenant}/${ticketId}/${timestamp}_${safeName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, uploadBlob, { contentType: uploadBlob.type || 'image/jpeg', customMetadata: fileUploadCustomMetadata(_uploader?.uid, 'clock_out') });
      const url = await getDownloadURL(storageRef);
      const photoObj = { url, name: photo.name, uploadedAt: new Date().toISOString(), uploadedVia: 'clock_out' }; // TASK-9958 — how
      if (_uploader) photoObj.uploadedBy = _uploader; // TASK-9958 — who
      photos.push(photoObj);
      uploadedStaged.push({ photo, url, photoObj, path });
    } catch (err) {
      const reason = {
        ...fileMeta,
        code: err?.code || err?.name || 'unknown',
        message: err?.message || String(err),
      };
      console.error('[Clock] Photo upload error:', reason, err);
      window.logError?.(err, { module: 'clock', operation: 'CLOCKOUT_PHOTO_UPLOAD_ITEM', ticketId, ...reason });
      failures.push(reason);
    }
  }

  if (photos.length > 0) {
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
      photos: arrayUnion(...photos),
      // TASK-4699 — ticket photos are attached to the customer's copy. Inside
      // the length > 0 guard, so a pass that uploaded nothing stamps nothing.
      ticketEmailDirtyAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    _clockAuditPhotosAdded('ticket_photos', ticketId, undefined, uploadedStaged.map(u => ({ name: u.photoObj.name, path: u.path }))); // TASK-9958
    // Now that the ticket doc has the photos, mark these staged photos uploaded so a
    // later save (after _appliedPhotos was reset by a new add — TASK-806) does NOT
    // re-upload and duplicate them. _restored is the existing "already uploaded, skip"
    // sentinel; flipping it also flips the tile badge from "Attached" → "Saved". If the
    // updateDoc above threw, we never reach here, so a retry re-uploads + re-attaches.
    for (const { photo, url, photoObj } of uploadedStaged) {
      photo._restored = true;
      photo._new = false;
      photo.url = url;
      photo._url = url;              // keep the restore-dedup key consistent
      photo._originalPhoto = photoObj; // TASK-1214 — EXACT stored element for arrayRemove
    }
    ClockState._photoTicketId = ticketId; // TASK-1214 — removal target for this session's uploads
  }

  return { uploaded: photos.length, failures };
}

// ============================================
// NOTES HELPER
// ============================================
// TASK-1397 — "Copy to ticket notes" rewrites THIS person's contribution to a
// SHARED field. Since the write path now contributes only the delta against
// the modal-open baseline, a plain `el.value = cleaned` would read as a
// deliberate deletion of every crew line already in the box. So compose:
// baseline lines first, this person's text after, deduped. Composing from the
// BASELINE (not from the box's current contents) is what makes pressing the
// button twice idempotent — a second AI cleanup replaces the first rather
// than stacking on top of it.
function applyToTicketNotesBox(el, text) {
  if (!el) return;
  const seen = new Set();
  const out = [];
  for (const line of invoiceNoteLines(getInvoiceNotesBaseline()).concat(invoiceNoteLines(text))) {
    const k = invoiceNoteKey(line);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(line);
  }
  el.value = out.join('\n');
}

async function copyToTicketNotes() {
  const shiftNotes = document.getElementById('clock-out-shift-notes')?.value || '';
  const ticketNotesEl = document.getElementById('clock-out-ticket-notes');
  if (!ticketNotesEl) return;

  const useAI = !!window.hasAiPack?.();

  if (useAI && shiftNotes.trim()) {
    const btn = document.getElementById('btn-copy-ticket-notes');
    const origHTML = btn?.innerHTML;
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<span class="btn-spinner"></span> Cleaning up...';
    }
    try {
      const cleanup = httpsCallable(functions, 'cleanupShiftNotes');
      const result = await cleanup({ notes: shiftNotes });
      applyToTicketNotesBox(ticketNotesEl, result.data.cleaned || shiftNotes);
      window.showToast('Notes cleaned up & copied', 'success');
    } catch (err) {
      console.error('[Clock] AI cleanup failed:', err);
      applyToTicketNotesBox(ticketNotesEl, shiftNotes);
      window.showToast('Auto-format failed — copied raw notes', 'error');
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = origHTML;
      }
    }
  } else {
    applyToTicketNotesBox(ticketNotesEl, shiftNotes);
    window.showToast('Copied to ticket notes', 'success');
  }
}

// ============================================
// EDIT / DELETE CLOCK ENTRIES (Workforce or Self)
// ============================================

// TASK-10034 (8) — the delete warnings (final check / invoiced ticket) live in the
// LAZY views/clockFinalCheck.js (perf budget); loaded only when a delete is confirmed.
let _finalCheckMod = null;
async function clockEntryDeleteWarnings(entryId, personId) {
  if (!_finalCheckMod) _finalCheckMod = await __ddChunkWrap(import('./clockFinalCheck.js?v=10034001'));
  return _finalCheckMod.clockEntryDeleteWarnings({ ClockState, db, doc, getDoc, P: _pph(), cfg: _pphCfg() }, entryId, personId);
}

// TASK-10034 (8) — the one delete confirm: the plain question, or, when there
// is something to warn about, a centered dialog that says it. Warn, never block.
async function confirmClockEntryDelete(entryId, personId) {
  let lines = [];
  try { lines = await clockEntryDeleteWarnings(entryId, personId); } catch (_) { lines = []; }
  if (!lines.length || typeof window.showConfirm !== 'function') {
    return confirm(['Delete this clock entry? This cannot be undone.'].concat(lines).join('\n\n'));
  }
  return !!(await window.showConfirm('Delete this clock entry?', lines.join('  ') + '  This cannot be undone.',
    { confirmText: 'Delete anyway', cancelText: 'Keep it', danger: true }));
}

async function deleteClockEntry(entryId, personId, skipConfirm = false) {
  // TASK-360 — clock entry delete: workforce/admin can delete anyone's
  // entry. Clock-only users can only delete their OWN entry (find it in
  // activeEntries/recent/history first, compare personId to selfPersonId).
  // (Legacy `if (!isFounder() && !shouldBuildTickets()) return;` removed
  // 2026-05-26 — was a stale tier gate that blocked workforce admins on
  // tenants without ticket-building. The workforce / self check below is
  // the correct + complete permission boundary.)
  if (!hasWorkforceAccess()) {
    const selfId = getSelfPersonId();
    const lookup = (ClockState.activeEntries || []).concat(ClockState.recentEntries || [], ClockState.historyEntries || []).find(e => e.id === entryId);
    if (!selfId || !lookup || lookup.personId !== selfId) {
      console.warn('[Clock] deleteClockEntry blocked — viewer can only delete own entry');
      try { window.showToast?.('You can only delete your own clock entries.', 'error'); } catch (_) {}
      return;
    }
  }
  if (!skipConfirm && !(await confirmClockEntryDelete(entryId, personId))) return; // TASK-10034 (8)
  // TASK-9443 — soft pay-period lock: deleting a paid shift is the biggest silent change.
  if (!(await _payrollLockProceed(entryId, 'Deleting this shift'))) return;

  const tenant = ClockState.tenantId;
  if (!tenant || !entryId) {
    window.showToast('Error deleting entry — missing data', 'error');
    return;
  }
  // Block deletion of currently active (clocked-in) entries
  const activeEntry = ClockState.activeEntries.find(e => e.id === entryId);
  if (activeEntry && !activeEntry.clockOut) {
    window.showToast('Cannot delete an active clock entry — clock out first', 'error');
    return;
  }
  try {
    // Audit log: snapshot the entry before deleting
    const entrySnap = await getDoc(doc(db, 'tenants', tenant, 'clockEntries', entryId));
    const entryData = entrySnap.exists() ? entrySnap.data() : null;
    if (entryData) {
      await logClockAudit(tenant, 'DELETE', {
        source: 'clock-module',
        clockEntryId: entryId,
        personId: entryData.personId || personId,
        personName: entryData.personName || null,
        entrySnapshot: entryData
      });
      // CLOCK_ENTRY_DELETE — irreversible payroll mutation. Compute red flags
      // (hoursLost, hadTicket, hadCoTime, entryAge) so payroll disputes can be
      // reconstructed without restoring the snapshot manually.
      try {
        const ci = entryData.clockIn?.toDate?.() || (entryData.clockIn ? new Date(entryData.clockIn) : null);
        const co = entryData.clockOut?.toDate?.() || (entryData.clockOut ? new Date(entryData.clockOut) : null);
        const hoursLost = (ci && co) ? Math.max(0, (co - ci) / 3600000) : 0;
        const createdAt = entryData.createdAt?.toDate?.() || (entryData.createdAt ? new Date(entryData.createdAt) : null);
        const entryAgeDays = createdAt ? Math.floor((Date.now() - createdAt.getTime()) / 86400000) : null;
        window.logAudit?.('clock', 'CLOCK_ENTRY_DELETE', {
          source: 'admin',
          entryId,
          personId: entryData.personId || personId,
          personName: entryData.personName || null,
          jobId: entryData.jobId || null,
          ticketId: entryData.ticketId || null,
          redFlags: {
            deletedBy: window.AppState?.user?.email || 'unknown',
            hadTicket: !!entryData.ticketId,
            hadCoTime: !!co,
            hoursLost: Number(hoursLost.toFixed(2)),
            entryAgeDays
          }
        });
      } catch (auditErr) { console.warn('[Clock] CLOCK_ENTRY_DELETE audit failed:', auditErr); }
    }
    await deleteDoc(doc(db, 'tenants', tenant, 'clockEntries', entryId));
  } catch (err) {
    console.error('[Clock] Error deleting clock entry:', err);
    window.showToast('Error deleting entry: ' + (err.code || err.message || 'unknown'), 'error');
    return;
  }
  // Delete succeeded — update local state and refresh UI
  ClockState.recentEntries = ClockState.recentEntries.filter(e => e.id !== entryId);
  ClockState.activeEntries = ClockState.activeEntries.filter(e => e.id !== entryId);
  ClockState.historyEntries = ClockState.historyEntries.filter(e => e.id !== entryId);
  for (const key of Object.keys(ClockState.weeklyCache || {})) {
    ClockState.weeklyCache[key] = ClockState.weeklyCache[key].filter(e => e.id !== entryId);
  }
  window.showToast('Clock entry deleted', 'success');
  try {
    const content = document.getElementById('workforce-content');
    if (content) content.innerHTML = getWorkforceContentHTML();
    if (personId) showEmployeeDetail(personId);
  } catch (uiErr) {
    console.error('[Clock] Error refreshing UI after delete:', uiErr);
  }
}

async function deleteClockEntryFromModal(entryId, personId) {
  // Legacy founder/ticket-tier gate removed 2026-05-26. deleteClockEntry
  // performs the actual workforce/self permission check.
  if (!(await confirmClockEntryDelete(entryId, personId))) return; // TASK-10034 (8)
  const modal = document.getElementById('modal-edit-clockentry');
  if (modal) modal.classList.add('hidden');
  await deleteClockEntry(entryId, personId, true);
}

function editClockEntry(entryId) {
  // Find the entry in active or recent lists
  const entry = [...ClockState.activeEntries, ...ClockState.recentEntries].find(e => e.id === entryId);
  // Also check weekly cache
  let found = entry;
  if (!found) {
    for (const entries of Object.values(ClockState.weeklyCache || {})) {
      found = entries.find(e => e.id === entryId);
      if (found) break;
    }
  }
  if (!found) {
    window.showToast('Entry not found', 'error');
    return;
  }

  // Permission check via canEditEntry (admins can edit all, drivers with allowDriverTimeEdit can edit their own)
  if (!canEditEntry(found)) return;

  const cin = found.clockIn?.toDate?.() || new Date(found.clockIn);
  const cout = found.clockOut?.toDate?.() ? found.clockOut.toDate() : null;
  // clock-dst-all — company zone, never the phone's (and remembers which
  // repeated-hour instant a stored time is, so an unchanged box keeps it).
  const cinStr = toLocalDatetimeStr(cin, 'edit-clock-in');
  const coutStr = toLocalDatetimeStr(cout || null, 'edit-clock-out'); // TASK-9971: an empty clock-out forgets the last entry's slot

  // Build edit modal
  let modal = document.getElementById('modal-edit-clockentry');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-edit-clockentry';
    modal.className = 'modal';
    document.body.appendChild(modal);
  }

  // Build customer dropdown — TASK-1175: internal-time customers pinned first
  // (same as the clock-in / switch pickers) + tagged for parity; TASK-9548
  // groups them under a labeled "Internal — not billed" optgroup (Mike couldn't
  // find Shop here because nothing said why it wasn't under S).
  // integ/2112 (COD r2): the entry's own customer is ALWAYS an option. If the
  // customer list has not landed (or no longer lists it), the select would
  // otherwise fall to "no customer" and a save would re-file the shift as Shop.
  const _editCustList = (found.customerId && found.customerId !== '__shop__'
    && !(ClockState.customers || []).some(c => c.id === found.customerId))
    ? [...(ClockState.customers || []), { id: found.customerId, name: found.customerName || 'Current customer' }]
    : ClockState.customers;
  const editCustOptions = _clockCustomerOptionsHtml(_editCustList, {
    selectedId: found.customerId,
    optionAttrs: c => (c.internalTimeOnly === true ? ' data-internal-time="1"' : ''),
  });

  // Build job dropdown for current customer — TASK-1082 relevance-scored + capped,
  // with the entry's current job always pinned (found.jobId) so an old shift keeps
  // its job. Includes the "No specific job" placeholder itself.
  const editJobOptions = found.customerId
    ? buildRelevantJobOptions(found.customerId, found.jobId, 'No specific job', found.jobTitle || found.jobName || null)
    : '<option value="">No specific job</option>';

  modal.innerHTML = `
    <div class="modal-content" style="max-width:420px;">
      <div class="modal-header">
        <h3>Edit Clock Entry</h3>
        <button class="btn-icon" onclick="document.getElementById('modal-edit-clockentry').classList.add('hidden')">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="display:flex;flex-direction:column;gap:var(--spacing-md);">
        <div style="font-weight:600;color:var(--text-primary);">${escapeHtml(found.personName || '')}</div>
        ${/* TASK-9983 — say WHICH entry this is before anything is changed */''}
        <div id="edit-clock-current" style="font-size:0.82rem;color:var(--text-secondary);margin-top:-6px;">Editing: ${escapeHtml([found.customerName || 'No customer', found.jobTitle || found.jobName].filter(Boolean).join(' · '))} · ${cout ? `${(Math.round(Number(found.totalHours || 0) * 100) / 100).toFixed(2)} h` : 'still clocked in'}</div>
        ${_clockPendingWarnHtml(entryId)}
        ${(() => {
          // TASK-592 — equipment tied at clock-in, DISPLAY-ONLY. Verified
          // 2026-06-12: NO assetIds edit path exists anywhere — TASK-491 only
          // built the clock-in capture; saveClockEntryEdit + the
          // mutateTicketAndClockEntry gateway payload carry customer/job/
          // times/notes only (Switch Job doesn't rewrite assetIds either).
          // Correcting a mis-tap = clock out and back in, not this modal
          // (building an edit flow is out of scope for 592).
          // Same gates as the panel chips: OFF tenants and
          // kiosk/legacy entries (no assetIds field) render nothing.
          const _equipHtml = _clockEquipChipsHtml(found, null, { muted: true });
          if (!_equipHtml) return '';
          return `
        <div class="form-group" style="margin:0;">
          <label>Equipment at clock-in</label>
          ${_equipHtml}
          <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">Recorded when the driver clocked in — can't be edited here. To fix a wrong pick, clock out and back in.</div>
        </div>`;
        })()}
        <div class="form-group">
          <label>Customer</label>
          <select id="edit-clock-customer" class="form-control" onchange="window.ClockModule.onEditClockCustomerChange()">
            ${(!found.customerId || found.customerId === '__shop__')
              ? `<option value="${escapeHtml(found.customerId || '')}" selected>No Customer (legacy shop time)</option>`
              : ''}
            ${editCustOptions}
          </select>
          ${_clockCustomerHintHtml(ClockState.customers)}
        </div>
        <div class="form-group" id="edit-clock-job-group" style="${found.customerId ? '' : 'display:none;'}">
          <label>Job</label>
          <select id="edit-clock-job" class="form-control">
            ${editJobOptions}
          </select>
        </div>
        <div class="form-group">
          <label>Clock In</label>
          ${renderSplitDateTime('edit-clock-in', cinStr, 'window.ClockModule.updateEditHoursPreview()', { className: 'form-control' })}
        </div>
        <div class="form-group">
          <label>Clock Out</label>
          ${renderSplitDateTime('edit-clock-out', coutStr, 'window.ClockModule.updateEditHoursPreview()', { className: 'form-control', disabled: !cout && !isClockAdmin(), placeholder: !cout ? 'Still active — set time to clock out' : '' })}
        </div>
        <div id="edit-clock-hours-preview" style="font-size:0.85rem;color:var(--text-secondary);padding:4px 0;"></div>
        <div class="form-group">
          <label>Notes</label>
          <input type="text" id="edit-clock-notes" class="form-control" value="${
            /* TASK-4621 — corrections land in clockOutNotes (gated editClockNotes
               op), so seed from it first or a corrected entry would reopen
               showing the stale pre-correction notes text. clockOutNotes||notes
               is the established clock-module pattern. */
            escapeHtml(found.clockOutNotes || found.notes || '')}">
        </div>
        ${/* TASK-1483 — admin correction of the personal-vehicle claim */''}
        ${pvAdminSectionOn(getPersonForReport(found.personId), found) ? pvAdminSectionHTML('adm', found) : ''}
      </div>
      <div class="modal-footer" style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;">
        ${(() => {
          // TASK-441 (C) — align the Delete gate with what deleteClockEntry()
          // actually permits. Previously the button was shown via isClockAdmin()
          // but the action requires hasWorkforceAccess() (or the entry being the
          // viewer's own) — so a clock-admin-but-not-workforce user could click
          // Delete on someone else's entry and get a silent no-op (the doc stayed,
          // no CLOCK_ENTRY_DELETE audit). Now we only show Delete when the action
          // will succeed: workforce admins for anyone, or anyone for their OWN entry.
          const _selfId = getSelfPersonId();
          const _isOwnEntry = !!_selfId && found.personId === _selfId;
          const _canDelete = isClockAdmin() && (hasWorkforceAccess() || _isOwnEntry);
          return _canDelete ? `<button class="btn btn-secondary btn-sm" onclick="window.ClockModule.deleteClockEntryFromModal('${entryId}','${found.personId || ''}')" style="color:var(--status-danger);border-color:var(--status-danger);margin-right:auto;padding:6px 10px;font-size:0.82rem;">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">delete</span> Delete
        </button>` : '';
        })()}
        ${_tkBtn(found) ? `<button class="btn btn-secondary btn-sm" onclick="window.ClockModule.openTicketFromClock('${_safeTicketId(found.ticketId)}','${_safeTicketId(found.id)}')" style="color:var(--brand-blue);border-color:var(--brand-blue);padding:6px 10px;font-size:0.82rem;">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">receipt_long</span> Ticket
        </button>` : ''}
        ${(() => {
          // "Clock Out Now" button: active entry + admin. Sets clockOut=now and saves.
          // Skipped if stale (>24h) — the "Close (0 hrs)" path is the right tool there.
          const isActive = !cout;
          const isStale = isActive && cin && (Date.now() - cin.getTime()) > 24 * 60 * 60 * 1000;
          if (!isActive || isStale || !isClockAdmin()) return '';
          return `<button class="btn btn-primary btn-sm" onclick="window.ClockModule.clockOutNowFromModal('${entryId}')" style="background:var(--status-success);border-color:var(--status-success);padding:6px 10px;font-size:0.82rem;" title="Clock out at the current time">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;">logout</span> Clock Out Now
          </button>`;
        })()}
        ${(() => {
          // "Close stale" button: only when entry is still active (no clockOut)
          // AND clockIn is older than 24h. Bypasses overlap check by zero-houring
          // the entry. Admin-only (the modal already gates on canEditEntry).
          const isStale = !cout && cin && (Date.now() - cin.getTime()) > 24 * 60 * 60 * 1000;
          if (!isStale) return '';
          return `<button class="btn btn-secondary btn-sm" onclick="window.ClockModule.closeStaleClockEntry('${entryId}')" style="color:#a16207;border-color:#a16207;padding:6px 10px;font-size:0.82rem;" title="Stale active entry — close at zero hours, no overlap check">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;">restart_alt</span> Close (0 hrs)
          </button>`;
        })()}
        <button class="btn btn-secondary btn-sm" onclick="window.ClockModule.viewClockEntryHistory('${entryId}')" style="padding:6px 10px;font-size:0.82rem;" title="See every edit to this clock entry">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">history</span> History
        </button>
        <button class="btn btn-secondary btn-sm" onclick="document.getElementById('modal-edit-clockentry').classList.add('hidden')" style="padding:6px 12px;font-size:0.82rem;">Cancel</button>
        <button id="btn-save-clockentry-edit" class="btn btn-primary btn-sm" onclick="window.ClockModule.saveClockEntryEdit('${entryId}')" style="padding:6px 14px;font-size:0.82rem;">Save</button>
      </div>
    </div>
  `;
  modal.dataset.entryId = entryId;
  modal.dataset.personId = found.personId || '';
  modal.classList.remove('hidden');
  updateEditHoursPreview();
}

function updateEditHoursPreview() {
  const cinVal = getSplitDateTimeValue('edit-clock-in');
  const coutVal = getSplitDateTimeValue('edit-clock-out');
  const preview = document.getElementById('edit-clock-hours-preview');
  if (!preview) return;
  if (!cinVal || !coutVal) { preview.textContent = ''; return; }
  // clock-dst-all — COMPANY zone, through the same helper the save uses, so the
  // hours shown are the hours saved. A repeated-hour time shows both totals.
  const _T = _tlt();
  if (_T) {
    const _hb = _T.hoursBetween(cinVal, coutVal, { startKey: 'edit-clock-in', endKey: 'edit-clock-out' });
    if (!_hb.ok) {
      preview.innerHTML = _hb.ambiguous
        ? `<span style="color:var(--status-warning,#B45309);font-weight:600;">${escapeHtml(_T.ambiguousPreviewText(_hb))}</span>`
        : `<span style="color:var(--status-danger);">${escapeHtml(_hb.error || 'That time could not be read.')}</span>`;
      return;
    }
  }
  const cin = _tltDate(cinVal, 'edit-clock-in');
  const cout = _tltDate(coutVal, 'edit-clock-out');
  const hrs = (cout - cin) / 3600000;
  if (!Number.isFinite(hrs)) { preview.textContent = ''; return; }
  if (hrs <= 0) {
    preview.innerHTML = '<span style="color:var(--status-danger);">Clock Out must be after Clock In</span>';
    return;
  }
  let msg = hrs > 24
    ? `<span style="color:var(--status-danger);font-weight:600;">${hrs.toFixed(2)} hours — check your dates!</span>`
    : `${hrs.toFixed(2)} hours`;
  // Overlap check in preview
  const modal = document.getElementById('modal-edit-clockentry');
  const personId = modal?.dataset?.personId;
  const entryId = modal?.dataset?.entryId;
  if (personId) {
    const overlap = checkClockOverlap(personId, cin, cout, entryId);
    if (overlap) {
      msg += `<br><span style="color:var(--status-danger);font-weight:600;">⚠ ${escapeHtml(overlap)}</span>`;
    }
  }
  preview.innerHTML = msg;
}

// TASK-9988 S1 — Billing's "Clock <name> out first" lands here (billing.js
// _openWorkforceClockOut, via window.ClockModule — never a ?v= import). It opens
// the EXISTING Workforce Edit Clock Entry modal for that entry, where the office
// sets the time the person actually stopped. No new clock-out path: same modal,
// same save (saveClockEntryEdit → gateway), same audit.
async function openEntryForClockOut(entryId) {
  if (!entryId) return false;
  if (!hasWorkforceAccess()) {
    window.showToast?.('Clocking someone else out needs Workforce access — ask whoever runs payroll to do it.', 'error');
    return false;
  }
  const alreadyOut = (e) => {
    window.showToast?.(`${e.personName || 'They'} ${e.personName ? 'is' : 'are'} already clocked out — go back to Billing and try again.`, 'info');
    return false;
  };
  // integ/2112 (TASK-9988 x TASK-9983): the edit modal builds its customer
  // select from ClockState.customers, and that listener lands AFTER the
  // active-entries one on a cold Clock view (Billing -> "Clock out first"). Opened
  // too early, the select has no option for the entry's customer, shows "no
  // customer", and Clock Out Now would save the shift as Shop (9983's wrong-entry
  // guard warns, but "Save anyway" would still mis-file billable time). Wait,
  // bounded, for the entry's customer to be loadable before opening.
  const customerReady = (e) => !e.customerId || e.customerId === '__shop__'
    || (ClockState.customers || []).some(c => c.id === e.customerId);
  const waitForCustomer = async (e) => {
    for (let j = 0; j < 40 && !customerReady(e); j++) await new Promise(r => setTimeout(r, 150));
  };
  // The Clock view's live lists arrive a moment after navigation.
  for (let i = 0; i < 40; i++) {
    const found = [...(ClockState.activeEntries || []), ...(ClockState.recentEntries || [])].find(e => e.id === entryId);
    if (found) {
      if (found.clockOut) return alreadyOut(found);
      await waitForCustomer(found);
      editClockEntry(entryId);
      return true;
    }
    await new Promise(r => setTimeout(r, 150));
  }
  // Not in the lists yet (cold view / slow phone): read the entry itself and
  // only say "no longer open" after a confirmed read (independent review).
  try {
    const snap = await getDoc(doc(db, 'tenants', ClockState.tenantId, 'clockEntries', entryId));
    if (snap.exists()) {
      const entry = { id: snap.id, ...snap.data() };
      if (entry.clockOut) return alreadyOut(entry);
      if (!(ClockState.activeEntries || []).some(e => e.id === entryId)) {
        ClockState.activeEntries = [...(ClockState.activeEntries || []), entry];
      }
      await waitForCustomer(entry);
      editClockEntry(entryId);
      return true;
    }
  } catch (err) {
    console.warn('[Clock] openEntryForClockOut read failed', err);
    window.showToast?.('Could not open that clock-in — check your connection, then open Workforce and clock them out there.', 'error');
    return false;
  }
  window.showToast?.('That clock-in no longer exists — go back to Billing and try again.', 'info');
  return false;
}

async function clockOutNowFromModal(entryId) {
  // Fill clock-out with current time, then save. Admin-only path for
  // closing an active entry from the Workforce edit modal.
  if (!isClockAdmin()) return;
  setSplitDateTimeValue('edit-clock-out', toLocalDatetimeStr(new Date(), 'edit-clock-out'));
  updateEditHoursPreview();
  await saveClockEntryEdit(entryId);
}

function onEditClockCustomerChange() {
  const custId = document.getElementById('edit-clock-customer')?.value;
  const jobGroup = document.getElementById('edit-clock-job-group');
  const jobSelect = document.getElementById('edit-clock-job');
  if (!custId) {
    if (jobGroup) jobGroup.style.display = 'none';
    return;
  }
  // TASK-1082 — relevance-scored + capped, archived excluded (was: every
  // non-finished job dumped). Customer just changed, so there's no prior job to pin.
  const relevant = getRelevantAdminJobs(custId, null);
  if (jobSelect) {
    jobSelect.innerHTML = buildRelevantJobOptions(custId, null);
  }
  if (jobGroup) jobGroup.style.display = relevant.length > 0 ? '' : 'none';
}

// TASK-1364 — the receipt button on the Clock/Workforce cards. Silent no-op
// since it shipped (12da01dd, 2026-03-04).
//
// ROOT CAUSE: this used to call window.BillingModule.openReviewTicket() in
// place. But #modal-review-ticket and #modal-view-ticket exist ONLY inside
// getBillingHTML(), which initBilling() writes into DOM.mainContent — and
// loadViewContent() (app.js) wipes DOM.mainContent.innerHTML on EVERY
// navigation. While the user is on Clock/Workforce those nodes are simply not
// in the document, and every DOM write inside openReviewTicket is null-guarded
// or optional-chained, so nothing threw, nothing rendered, and the catch below
// never fired. The click was swallowed whole.
//
// FIX: navigate to Billing FIRST, then open the modal. We hand the ticket off
// through window._clockTicketDeepLink and initBilling() consumes it once the
// view is mounted and subscribed — the same handoff shape the BI module already
// uses (window._biDeepLink). Going through the deep link instead of calling
// straight into BillingModule after navigateToView() matters because the nav
// gate can defer: a PIN challenge or a still-resolving member-access read
// returns before anything is mounted, and a direct call in that window would
// be the same silent no-op with extra steps.
//
// NOT hoisting the modals to document.body (the other obvious fix): that
// renders the markup but leaves BillingState.jobs/customers empty, which drops
// job-permit + job-photo bridging AND lets _resolveFallbackPoNumber() skip the
// parent-job PO and persist the customer contract PO onto a live ticket.
// TASK-9411 — Jess (TITS office, 2026-09-28): "a back button so when I exit the
// ticket I don't have to go back through the process of getting to the
// employee's time clock again." openTicketFromClock MUST leave this view
// (TASK-1364 — the ticket modals only exist inside Billing), so what comes back
// is a snapshot: which view + tab + sub-tab, the Weekly Hours week, and — when
// the employee timesheet modal is up — whose, which pay period, and how far down
// she had scrolled. billing.js hands it back as window._clockReturnRestore when
// the ticket is closed; initClock replays it. Plain data only (no DOM refs).
function _captureClockReturnContext() {
  try {
    const wfModal = document.getElementById('modal-workforce');
    const adjSec = document.getElementById('employee-adjustments-section');
    // The workforce modal also hosts the Add Hours picker, and detailPersonId is
    // never cleared — so "the timesheet is up" is proven by the timesheet's own
    // marker, not by the state field.
    const detailOpen = !!(wfModal && !wfModal.classList.contains('hidden') && ClockState.detailPersonId
      && adjSec && adjSec.dataset.personId === ClockState.detailPersonId);
    const person = detailOpen
      ? (ClockState.people.find(p => p.id === ClockState.detailPersonId)
        || ClockState._historicalPeopleCache?.get?.(ClockState.detailPersonId) || null)
      : null;
    const main = document.getElementById('main-content');
    return {
      tenantId: ClockState.tenantId,
      view: window.AppState?.currentView === 'workforce' ? 'workforce' : 'clock',
      activeTab: ClockState.activeTab,
      workforceTab: ClockState.workforceTab,
      workforceWeekStart: ClockState.workforceWeekStart ? ClockState.workforceWeekStart.getTime() : null,
      detailPersonId: detailOpen ? ClockState.detailPersonId : null,
      personName: person?.name || null,
      detailPayPeriodOffset: detailOpen ? (ClockState.detailPayPeriodOffset || 0) : 0,
      detailScrollTop: detailOpen ? (document.getElementById('workforce-modal-body')?.scrollTop || 0) : 0,
      pageScrollTop: main ? (main.scrollTop || 0) : 0,
      windowScrollY: window.scrollY || 0,
    };
  } catch (e) {
    console.warn('[Clock] TASK-9411 could not capture return context:', e);
    return null;
  }
}

// Pure: is a restore hand-off usable for THIS clock mount? Stale (>20s since the
// ticket closed), wrong tenant, or malformed → ignore it.
function _isUsableClockReturn(r, tenantId, nowMs) {
  if (!r || typeof r !== 'object') return false;
  if (!r.tenantId || r.tenantId !== tenantId) return false;
  // 20s: a hop the nav gate refused (cancelled PIN) must not reopen the
  // timesheet on some unrelated Clock visit later (COD round 2).
  if (!Number.isFinite(r.at) || nowMs - r.at > 20000 || nowMs < r.at - 5000) return false;
  return true;
}

// Called from initClock. State that the tab renders read (sub-tab, week) is set
// SYNCHRONOUSLY so app.js's own deferred switchTab('workforce') paints the right
// thing; the timesheet modal + scroll are replayed once that tab is up.
function _applyClockReturnRestore() {
  const r = window._clockReturnRestore;
  if (!r) return;
  delete window._clockReturnRestore;
  if (!_isUsableClockReturn(r, ClockState.tenantId, Date.now())) return;
  if (r.activeTab === 'workforce' && !hasWorkforceAccess()) return;

  if (typeof r.workforceTab === 'string') ClockState.workforceTab = r.workforceTab;
  if (Number.isFinite(r.workforceWeekStart)) ClockState.workforceWeekStart = new Date(r.workforceWeekStart);

  const started = Date.now();
  const finish = async () => {
    try {
      // Wait for the tab the user was on (app.js switches to workforce ~300ms
      // after mount for #/workforce). The clock view with the workforce TAB
      // selected has nobody to do that for us, so do it ourselves.
      if (r.activeTab === 'workforce' && ClockState.activeTab !== 'workforce') {
        if (r.view === 'workforce' && Date.now() - started < 2000) { setTimeout(finish, 50); return; }
        switchTab('workforce');
      }
      if (r.activeTab === 'workforce' && ClockState.workforceWeekStart && !isCurrentWeek(ClockState.workforceWeekStart)) {
        const key = _weekCacheKey(ClockState.workforceWeekStart);
        if (!ClockState.weeklyCacheFullyLoaded[key]) {
          await loadWeekData(ClockState.workforceWeekStart);
          const content = document.getElementById('workforce-content');
          if (content && ClockState.activeTab === 'workforce') {
            content.innerHTML = getWorkforceContentHTML();
            loadAdjustmentFlags();
          }
        }
      }
      const main = document.getElementById('main-content');
      if (main && r.pageScrollTop > 0) main.scrollTop = r.pageScrollTop;
      if (r.windowScrollY > 0) window.scrollTo(0, r.windowScrollY);
      if (r.detailPersonId) {
        ClockState.detailPayPeriodOffset = Math.min(0, Number(r.detailPayPeriodOffset) || 0);
        await showEmployeeDetail(r.detailPersonId, false);
        const body = document.getElementById('workforce-modal-body');
        if (body && r.detailScrollTop > 0) {
          // One frame for layout so the scroll height exists before we set it.
          requestAnimationFrame(() => { body.scrollTop = r.detailScrollTop; });
        }
      }
    } catch (e) {
      console.warn('[Clock] TASK-9411 return restore failed:', e);
    }
  };
  setTimeout(finish, 0);
}

// TASK-9220 — the sidebar PERSONAL "Clock In / Out" tile (app.js
// PersonalActions.togglePersonalClock) used to run a second, stripped-down clock:
// a job-less, customer-less punch with no ticket, and a label that never said
// Build Ticket. It now hands off to THIS view instead — one clock. The tile
// leaves window._clockPersonalHandoff = { tenantId, personId, at } and navigates
// here; initClock replays it: select the signed-in person, then either open
// their close-out (Build Ticket in ticket mode — clockOut() picks the same modal
// the Clock tile's own button does) or leave them on the clock-in form with the
// customer/job picker. Plain data only, consumed once.
//
// Pure: is a hand-off usable for THIS clock mount? Wrong tenant, not the
// signed-in person, malformed or stale (>30s — a navigation the gate bounced must
// not fire on some unrelated Clock visit later) → ignore it.
function _isUsablePersonalClockHandoff(h, tenantId, selfPersonId, nowMs) {
  if (!h || typeof h !== 'object') return false;
  if (!h.tenantId || h.tenantId !== tenantId) return false;
  if (!h.personId || !selfPersonId || h.personId !== selfPersonId) return false;
  if (!Number.isFinite(h.at) || nowMs - h.at > 30000 || nowMs < h.at - 5000) return false;
  return true;
}

function _applyPersonalClockHandoff() {
  const h = window._clockPersonalHandoff;
  if (!h) return;
  delete window._clockPersonalHandoff;
  const selfId = getSelfPersonId();
  if (!_isUsablePersonalClockHandoff(h, ClockState.tenantId, selfId, Date.now())) return;
  const tenantId = ClockState.tenantId;
  const mount = ClockState._mountSeq;
  const started = Date.now();
  const step = () => {
    // The user moved on (another view, another tenant, a remount from a second
    // tap, a different login) — drop it quietly. Re-checked on EVERY step, not
    // just before the wait (COD Code Reviewer).
    if (ClockState.tenantId !== tenantId || ClockState._mountSeq !== mount) return;
    if (getSelfPersonId() !== selfId) return;
    if (window.AppState?.tenant && window.AppState.tenant !== tenantId) return;
    const select = document.getElementById('clock-person');
    if (!select) return;
    const hasSelf = Array.from(select.options || []).some(o => o.value === selfId);
    if (!ClockState._entriesLoaded || !hasSelf) {
      if (Date.now() - started < 10000) { setTimeout(step, 100); return; }
      // Never silent: a worker who thinks they tapped "clock in" must be told
      // nothing happened yet (COD Domain).
      window.showToast?.(hasSelf
        ? 'Your clock status is still loading. Check below before you leave.'
        : 'Pick your name, then your customer and job, and tap Clock In.', 'warning');
      return;
    }
    // A failed read is UNKNOWN, not "not clocked in" (COD Code Reviewer).
    if (ClockState._entriesFailed) {
      window.showToast?.('Could not load your clock status. Refresh and try again.', 'error');
      return;
    }
    if (select.value !== selfId || ClockState.selectedPerson !== selfId) selectPerson(selfId);
    const { entry } = _clockOpenShiftsFor(selfId);
    if (entry) {
      // Clocked in → the Clock tile's own Build Ticket / Close Out button.
      Promise.resolve(clockOut()).catch(e => console.error('[Clock] personal hand-off clock-out failed:', e));
      return;
    }
    // Not clocked in → the clock-in form (customer + job picker) is up. Say so:
    // the tap itself recorded no time (COD Domain).
    const target = document.getElementById('clock-actions') || select;
    try { target.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (_) { /* old browsers */ }
    window.showToast?.('Not clocked in yet. Pick your customer and job, then tap Clock In.', 'info');
  };
  setTimeout(step, 0);
}

function openTicketFromClock(ticketId, entryId) {
  try {
    if (!ClockState.tenantId) { showToast('No tenant loaded', 'error'); return; }
    // Belt to the render-time gate: every call site is already wrapped in
    // hasBillingAccess(), but an onclick string is trivially replayable and
    // navigateToView('billing') would otherwise strand a clock-only member on a
    // "no access" screen with their clock view already destroyed.
    if (!hasBillingAccess()) {
      // TASK-1377(a) — no billing: only the viewer's OWN entry, read-only, in a
      // clock-owned modal (never Billing). Ownership is re-derived here from
      // loaded entries — the onclick string carries only a ticket id.
      // The TAPPED entry (by id — COD r1: two of one person's shifts can share a
      // combined ticket, and its hours/crew tagging must be the row's), looked
      // up wherever editClockEntry looks (a self-linked Workforce-without-
      // Billing user taps from weeklyCache rows). In this branch
      // hasBillingAccess() is false, so _tkBtn(x) IS the own-entry test.
      const e = [...ClockState.activeEntries, ...ClockState.recentEntries, ...Object.values(ClockState.weeklyCache || {}).flat()]
        .find(x => x.id === entryId && x.ticketId === ticketId && _tkBtn(x));
      const tenant = window.AppState?.tenant; // tenant from AppState, never the entry
      // The edit modal stays open underneath: the viewer stacks on top (it
      // re-appends itself last), so Close lands back on the shift.
      if (e && tenant) {
        __ddChunkWrap(import('./clockTicketView.js?v=1377001'))
          .then(m => m.openOwnTicket(db, tenant, ticketId, e, _myHoursDetailHtml))
          .catch(() => showToast('Could not open ticket', 'error'));
      } else showToast('Billing access is required to open tickets', 'warning');
      return;
    }
    if (typeof window.navigateToView !== 'function') {
      showToast('Billing module not loaded', 'error');
      return;
    }
    // TASK-9411 — remember exactly where she was (employee, pay period, week,
    // tab, scroll) BEFORE the billing view wipes it, so closing the ticket can
    // put her straight back instead of making her re-navigate to the timesheet.
    const returnTo = _captureClockReturnContext();
    // Close the clock edit modal if open — the billing view is about to replace
    // everything underneath it.
    document.getElementById('modal-edit-clockentry')?.classList.add('hidden');
    // Timestamped so a navigation the gate bounced (trial wall, cancelled PIN
    // challenge) can't leave a landmine that pops a modal on some unrelated
    // Billing visit later — initBilling drops hand-offs older than 30s.
    window._clockTicketDeepLink = { ticketId, at: Date.now(), returnTo };
    window.navigateToView('billing');
  } catch (err) {
    console.error('[Clock] openTicketFromClock error:', err);
    delete window._clockTicketDeepLink;
    showToast('Could not open ticket', 'error');
  }
}

// Overlap detection — block entries that cross over another entry for the same person
function checkClockOverlap(personId, newIn, newOut, excludeEntryId) {
  const allEntries = [
    ...ClockState.activeEntries,
    ...ClockState.recentEntries,
    ...Object.values(ClockState.weeklyCache || {}).flat()
  ];
  // Dedupe by id
  const seen = new Set();
  const personEntries = allEntries.filter(e => {
    if (seen.has(e.id)) return false;
    seen.add(e.id);
    return e.personId === personId && e.id !== excludeEntryId;
  });

  for (const entry of personEntries) {
    const eIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
    // For active entries (no clock-out), treat as open-ended up to now
    const eOut = entry.clockOut
      ? (entry.clockOut?.toDate?.() || new Date(entry.clockOut))
      : new Date();

    // Two ranges overlap if: newIn < eOut AND newOut > eIn
    const checkOut = newOut || new Date(); // if no clock-out, use now
    if (newIn < eOut && checkOut > eIn) {
      const eInStr = eIn.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      const eOutStr = entry.clockOut
        ? eOut.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
        : 'still active';
      return `Overlaps with existing entry: ${eInStr} – ${eOutStr}${entry.customerName ? ' (' + entry.customerName + ')' : ''}`;
    }
  }
  return null;
}

// Close a stale active clock entry at zero hours. Bypasses the overlap check
// because by definition the stale entry was forgotten and admin needs an out
// when it overlaps with the person's later (legitimate) shifts. Admin-only —
// the edit modal already gated on canEditEntry, and this is a footer button
// only shown when entry has clockIn>24h ago AND no clockOut yet.
async function closeStaleClockEntry(entryId) {
  const found = [...ClockState.activeEntries, ...ClockState.recentEntries].find(e => e.id === entryId)
    || Object.values(ClockState.weeklyCache || {}).flat().find(e => e.id === entryId);
  if (!found) {
    window.showToast?.('Entry not found', 'error');
    return;
  }
  const personLabel = found.personName || 'this entry';
  const clockIn = found.clockIn?.toDate?.() || new Date(found.clockIn);
  const dayLabel = clockIn.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const ok = window.confirm(
    `Close ${personLabel}'s stale clock-in from ${dayLabel} at zero hours?\n\n` +
    `This bypasses the overlap check so the entry frees up. Use only when the entry was forgotten and not part of an actual shift. Re-enter real hours via Workforce → Hours if needed.`
  );
  if (!ok) return;

  const tenant = ClockState.tenantId;
  const updates = {
    clockOut: Timestamp.fromDate(clockIn), // zero-hour close
    status: 'completed',
    totalHours: 0,
    hours: 0, // keep both fields in sync
    closedByAdmin: true,
    adminCloseReason: 'stale-active-entry-close',
    updatedAt: Timestamp.now()
  };
  try {
    await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entryId), updates);
    logClockEntryChange(tenant, entryId, { ...found }, { ...found, ...updates }, 'edit', 'admin-close-stale');
    logClockAudit(tenant, 'CLOCK_OUT', {
      source: 'clock-module-stale-close',
      clockEntryId: entryId,
      personId: found.personId, personName: found.personName,
      totalHours: 0,
      origClockIn: clockIn,
      note: 'Stale active entry zero-hour close (admin override of overlap check).'
    });
    // Update local state so UI reflects immediately
    found.clockOut = updates.clockOut;
    found.status = 'completed';
    found.totalHours = 0;
    document.getElementById('modal-edit-clockentry')?.classList.add('hidden');
    if (typeof renderClock === 'function') renderClock();
    window.showToast?.(`${personLabel}'s stale entry closed.`, 'success');
  } catch (err) {
    console.error('[Clock] closeStaleClockEntry failed:', err);
    window.showToast?.('Error closing stale entry: ' + (err.message || 'Unknown'), 'error');
  }
}

/**
 * Phase 2 gateway path for dispatcher edit. Reads the form, normalizes the payload,
 * calls the mutateTicketAndClockEntry callable. Falls back to the legacy direct-write
 * path on any error (graceful degradation during rollout).
 */
// ============================================
// TASK-9983 — invoiced-shift pay fork + wrong-entry guard (Workforce edit)
// ============================================
// Pay and billing are separate records (Chris 2026-10-20). When the server
// refuses a Workforce time edit because the shift's ticket is closed, and says
// the editor may save PAY only (details.payOnlyAllowed), ONE dialog asks where
// the difference belongs BEFORE anything saves:
//   • added hours  — already billed (invoice covers it) / this customer, the
//     invoice was short (billing flag) / somewhere else: an internal-time place
//     (the tenant's internalTimeOnly customers — CRM owns that flag; the clock-in
//     picker and Internal-Time Pay read the same one) or another customer's job,
//     added as ITS OWN entry through the normal manualEntry op (decision A: it
//     never widens the invoiced shift).
//   • removed hours — no, pay only / yes, flag the invoice.
// Hours below the original punch also need a reason code (server-enforced;
// details.needsReduceReason / reduceNeeded). The server re-checks everything.

const PAY_REDUCE_REASONS = [
  ['forgot_clock_out', 'Forgot to clock out'],
  ['wrong_job', 'Wrong job'],
  ['duplicate', 'Duplicate'],
  ['other', 'Other'],
];
const _PF_SLOP_MS = 30 * 1000;

function _pfMs(v) {
  if (v == null) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : null;
}
function _pfH(n) { return (Math.round(Number(n || 0) * 100) / 100).toFixed(2); }

// The extra time as ONE separate window: only when the new window is the old
// one extended on exactly one end (nothing trimmed). Otherwise not offered.
function _payForkSegment(bIn, bOut, nIn, nOut) {
  if (![bIn, bOut, nIn, nOut].every(Number.isFinite) || nOut <= nIn) return { ok: false, reason: 'The new times could not be read.' };
  const early = nIn < bIn - _PF_SLOP_MS;
  const late = nOut > bOut + _PF_SLOP_MS;
  const trims = nIn > bIn + _PF_SLOP_MS || nOut < bOut - _PF_SLOP_MS;
  if (trims || early === late) {
    return { ok: false, reason: 'Only when the shift is longer on one end — the extra time then becomes its own entry.' };
  }
  return early ? { ok: true, startMs: nIn, endMs: bIn } : { ok: true, startMs: bOut, endMs: nOut };
}

function _pfNorm(s) { return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim(); }

// Customer step. Internal-time places first (the tenant's own — never a
// hardcoded "Shop" when they have set some up), then customers this person
// worked for that day, then every other active customer. `query` filters by name.
function _payForkCustomerGroups(customers, workedIds, query) {
  const BC = typeof globalThis !== 'undefined' ? globalThis.BillableCustomer : null;
  const list = (customers || []).filter(c => c && c.id && c.active !== false && !window.PickerGroups.isInactiveCustomer(c) && String(c.status || '').trim().toLowerCase() !== 'archived'); // TASK-10030 — case-blind
  const isInt = c => c.internalTimeOnly === true;
  const internal = list.filter(isInt);
  let others = list.filter(c => !isInt(c));
  if (BC && typeof BC.billableCustomers === 'function') others = BC.billableCustomers(others); // vendors are never a job destination
  const q = _pfNorm(query);
  const hit = c => !q || _pfNorm(c.name || c.company).includes(q);
  const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true, sensitivity: 'base' });
  const worked = others.filter(c => workedIds && workedIds.has(c.id)).filter(hit).sort(byName);
  const rest = others.filter(c => !(workedIds && workedIds.has(c.id))).filter(hit).sort(byName);
  const noInternalSetUp = internal.length === 0;
  const internalItems = noInternalSetUp
    ? ((!q || 'shop / not billable'.includes(q)) ? [{ id: '__shop__', name: 'Shop / not billable', internalTimeOnly: true, _fallback: true }] : [])
    : internal.filter(hit).sort(byName);
  return { internal: internalItems, worked, rest, noInternalSetUp };
}

function _payForkCustomerOptionsHtml(groups, selectedId) {
  const PG = window.PickerGroups;
  const sel = selectedId == null ? '' : String(selectedId);
  const it = c => ({ value: c.id, label: c.name || c.company || 'Customer', selected: sel !== '' && String(c.id) === sel, attrs: c.internalTimeOnly === true ? ' data-internal-time="1"' : '' });
  return PG.groupedOptionsHtml([
    { label: '', items: [{ value: '', label: 'Pick where the hours belong…', selected: sel === '' }] },
    { label: PG.INTERNAL_LABEL, items: groups.internal.map(it) },
    { label: 'Worked for that day', items: groups.worked.map(it) },
    { label: PG.CUSTOMERS_LABEL, items: groups.rest.map(it) },
  ], { alwaysGroup: true });
}

// Job step for a customer: that day's jobs first (the ones this person was on
// the crew for marked and first), then the customer's other active jobs.
function _payForkJobGroups(customerId, jobs, dayJobDays, person, query, excludeJobId) {
  const list = (jobs || []).filter(j => j && j.id && j.customerId === customerId && j.status !== 'archived' && j.status !== 'finished' && j.id !== excludeJobId);
  const days = (dayJobDays || []).filter(Boolean);
  const dayIds = new Set(days.map(d => d.jobId));
  const pid = person && person.id, pname = person && person.name;
  const crewed = j => days.some(d => d.jobId === j.id && clockPersonOnJobDay(d, pid, pname));
  const q = _pfNorm(query);
  const hit = j => !q || _pfNorm(j.title || j.name).includes(q) || _pfNorm(j.jobNumber).includes(q);
  const byTitle = (a, b) => String(a.title || a.name || '').localeCompare(String(b.title || b.name || ''), undefined, { numeric: true, sensitivity: 'base' });
  const thatDay = list.filter(j => dayIds.has(j.id)).filter(hit)
    .map(j => ({ job: j, crewed: crewed(j) }))
    .sort((a, b) => (Number(b.crewed) - Number(a.crewed)) || byTitle(a.job, b.job));
  const other = list.filter(j => !dayIds.has(j.id)).filter(hit).sort(byTitle).map(j => ({ job: j, crewed: false }));
  return { thatDay, other };
}

function _payForkJobOptionsHtml(groups, selectedId) {
  const PG = window.PickerGroups;
  const sel = selectedId == null ? '' : String(selectedId);
  const it = r => ({ value: r.job.id, label: (r.crewed ? '★ ' : '') + (r.job.title || r.job.name || 'Untitled job') + (r.crewed ? ' (on the crew)' : ''), selected: sel !== '' && String(r.job.id) === sel });
  return PG.groupedOptionsHtml([
    { label: '', items: [{ value: '', label: 'Pick a job…', selected: sel === '' }] },
    { label: 'That day', items: groups.thatDay.map(it) },
    { label: 'Other active jobs', items: groups.other.map(it) },
  ], { alwaysGroup: true });
}

// Customers this person worked for that day: their own entries + the jobs they
// were on the crew for that day.
function _payForkWorkedCustomerIds(personEntries, dayJobDays, jobs, person) {
  const ids = new Set();
  (personEntries || []).forEach(e => { if (e && e.customerId) ids.add(e.customerId); });
  const pid = person && person.id, pname = person && person.name;
  (dayJobDays || []).forEach(d => {
    if (!d || !clockPersonOnJobDay(d, pid, pname)) return;
    const j = (jobs || []).find(x => x && x.id === d.jobId);
    if (j && j.customerId) ids.add(j.customerId);
  });
  return ids;
}

// Job days of one calendar date (the SHIFT's day, not today). Same widened
// window + calendar-date filter as loadTodaysJobDays.
async function _loadJobDaysForDate(tenantId, dateStr) {
  try {
    const mid = new Date(dateStr + 'T12:00:00');
    const from = new Date(mid); from.setDate(from.getDate() - 2);
    const to = new Date(mid); to.setDate(to.getDate() + 2);
    const snap = await getDocs(query(
      collection(db, 'tenants', tenantId, 'jobDays'),
      where('date', '>=', Timestamp.fromDate(from)),
      where('date', '<', Timestamp.fromDate(to))
    ));
    return snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(jd => {
      const d = jd.date?.toDate ? jd.date.toDate() : new Date(jd.date);
      return d.toISOString().split('T')[0] === dateStr || d.toLocaleDateString('en-CA') === dateStr;
    });
  } catch (e) {
    console.warn('[Clock] TASK-9983 job days for', dateStr, 'failed:', e?.message);
    return [];
  }
}

function _pfPersonEntriesOn(personId, dateStr, excludeId) {
  const seen = new Set();
  return [...ClockState.activeEntries, ...ClockState.recentEntries, ...Object.values(ClockState.weeklyCache || {}).flat()]
    .filter(e => {
      if (!e || seen.has(e.id)) return false;
      seen.add(e.id);
      if (e.personId !== personId || e.id === excludeId) return false;
      const ms = _pfMs(e.clockIn);
      return ms != null && _clockFormatDateISO(new Date(ms)) === dateStr;
    });
}

// Wrong-entry guard. Plain sentences, or [] when nothing looks off:
//   • the edit changes the entry's customer/job ("This changes Brian Reid's
//     9.06 h Pronghorn · Rig move entry into a 2.77 h Shop entry.")
//   • the new window duplicates or overlaps another entry of the same person.
function _wrongEntryWarnings(before, next, others) {
  const out = [];
  if (!before || !next) return out;
  const who = before.personName || 'this person';
  const label = (cust, job) => [cust || 'No customer', job].filter(Boolean).join(' · ');
  const bH = before.totalHours != null ? Number(before.totalHours) : null;
  const custChanged = String(before.customerId || '') !== String(next.customerId || '');
  const jobChanged = String(before.jobId || '') !== String(next.jobId || '');
  if (custChanged || jobChanged) {
    out.push(`This changes ${who}'s ${bH != null ? _pfH(bH) + ' h ' : ''}${label(before.customerName, before.jobTitle)} entry into a ${next.hours != null ? _pfH(next.hours) + ' h ' : ''}${label(next.customerName, next.jobTitle)} entry.`);
  }
  const nIn = next.inMs, nOut = next.outMs;
  if (Number.isFinite(nIn) && Number.isFinite(nOut)) {
    for (const o of (others || [])) {
      const oIn = _pfMs(o.clockIn), oOut = o.clockOut ? _pfMs(o.clockOut) : Date.now();
      if (!Number.isFinite(oIn) || !Number.isFinite(oOut)) continue;
      const t = ms => new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      const what = `${who}'s ${t(oIn)}–${o.clockOut ? t(oOut) : 'now'} ${label(o.customerName, o.jobTitle)} entry`;
      if (Math.abs(oIn - nIn) <= 60000 && Math.abs(oOut - nOut) <= 60000) out.push(`This makes it a duplicate of ${what}.`);
      else if (nIn < oOut && nOut > oIn) out.push(`This overlaps ${what}.`);
    }
  }
  return out;
}

async function _wrongEntryGuardOk(entryId) {
  try {
    const found = [...ClockState.activeEntries, ...ClockState.recentEntries].find(e => e.id === entryId)
      || Object.values(ClockState.weeklyCache || {}).flat().find(e => e.id === entryId);
    if (!found) return true;
    const cinVal = getSplitDateTimeValue('edit-clock-in');
    const coutVal = getSplitDateTimeValue('edit-clock-out');
    const inMs = cinVal ? _tltDate(cinVal, 'edit-clock-in').getTime() : NaN;
    const outMs = coutVal ? _tltDate(coutVal, 'edit-clock-out').getTime() : NaN;
    const custId = document.getElementById('edit-clock-customer')?.value || null;
    const jobId = document.getElementById('edit-clock-job')?.value || null;
    const cust = custId ? ClockState.customers.find(c => c.id === custId) : null;
    const job = jobId ? ClockState.allJobs.find(j => j.id === jobId) : null;
    const next = {
      customerId: custId, jobId,
      customerName: cust ? cust.name : (custId ? found.customerName : 'Shop'),
      jobTitle: job ? (job.title || job.name) : (jobId && jobId === found.jobId ? found.jobTitle : null),
      inMs, outMs, hours: Number.isFinite(inMs) && Number.isFinite(outMs) ? (outMs - inMs) / 3600000 : null,
    };
    // A legacy shop entry carries customerId '' / '__shop__' / null — all "no customer".
    if ((!found.customerId || found.customerId === '__shop__') && (!custId || custId === '__shop__')) next.customerId = found.customerId;
    const dayStr = Number.isFinite(inMs) ? _clockFormatDateISO(new Date(inMs)) : null;
    const others = dayStr ? [
      ..._pfPersonEntriesOn(found.personId, dayStr, entryId),
      // the previous day too — an overnight shift
      ..._pfPersonEntriesOn(found.personId, _clockFormatDateISO(new Date(inMs - 86400000)), entryId),
    ] : [];
    const warns = _wrongEntryWarnings(found, next, others);
    if (!warns.length) return true;
    const msg = warns.join(' ') + ' Is this the right entry?';
    return typeof window.showConfirm === 'function'
      ? await window.showConfirm('Check this is the right entry', msg, { confirmText: 'Save anyway', cancelText: 'Go back', icon: 'warning', iconColor: '#F59E0B', safeDefault: true })
      : confirm(msg);
  } catch (e) {
    console.warn('[Clock] wrong-entry guard failed open:', e?.message);
    return true; // a guard that cannot run never traps the edit; the server gates still apply
  }
}

function _pfLockLine(details) {
  const lk = details.lock || {};
  const tno = details.ticketNo != null ? `Ticket #${details.ticketNo}` : 'This shift’s ticket';
  let s = `${tno} is ${lk.label || 'closed'}`;
  if (lk.qboDocNumber) s += ` · in QuickBooks as #${lk.qboDocNumber}`;
  if (lk.paid) s += ' · marked paid';
  return s + '.';
}

function _pfReasonHtml(details) {
  const need = details.needsReduceReason === true || details.reduceNeeded === true;
  if (!need) return '';
  return `
    <div class="form-group" style="margin:0;">
      <label for="pf-reason">Why are the hours going below the original punch (${_pfH(details.baselineHours)} h)?</label>
      <select id="pf-reason" class="form-control">
        <option value="">Pick a reason…</option>
        ${PAY_REDUCE_REASONS.map(([v, l]) => `<option value="${v}">${escapeHtml(l)}</option>`).join('')}
      </select>
      <input type="text" id="pf-reason-note" class="form-control" maxlength="300" placeholder="Note (required for Other)" style="margin-top:6px;">
    </div>`;
}

function _pfReadReason(details) {
  const need = details.needsReduceReason === true || details.reduceNeeded === true;
  if (!need) return { ok: true, reason: null };
  const code = document.getElementById('pf-reason')?.value || '';
  const note = (document.getElementById('pf-reason-note')?.value || '').trim();
  if (!code) return { ok: false, msg: 'Pick why the hours are going down.' };
  if (code === 'other' && note.length < 3) return { ok: false, msg: 'Say in a few words why the hours are going down.' };
  return { ok: true, reason: { code, note } };
}

// The reason-only dialog (an open ticket, hours below the original punch).
function _openReduceReasonDialog(personName, details) {
  return new Promise((resolve) => {
    document.getElementById('modal-pay-fork')?.remove();
    const m = document.createElement('div');
    m.id = 'modal-pay-fork';
    m.className = 'modal';
    m.innerHTML = `
      <div class="modal-content" style="max-width:440px;">
        <div class="modal-header"><h3>Hours going down</h3></div>
        <div class="modal-body" style="display:flex;flex-direction:column;gap:var(--spacing-md);">
          <div style="color:var(--text-primary);">${escapeHtml(personName || 'This person')}: ${_pfH(details.baselineHours)} h punched → ${_pfH(details.newHours)} h.</div>
          ${_pfReasonHtml({ ...details, needsReduceReason: true })}
          <div id="pf-error" style="color:var(--status-danger);font-size:0.85rem;"></div>
        </div>
        <div class="modal-footer" style="display:flex;gap:8px;justify-content:flex-end;">
          <button class="btn btn-secondary btn-sm" id="pf-cancel">Cancel</button>
          <button class="btn btn-primary btn-sm" id="pf-save">Save</button>
        </div>
      </div>`;
    document.body.appendChild(m);
    m.classList.remove('hidden');
    const done = (v) => { m.remove(); resolve(v); };
    m.querySelector('#pf-cancel').onclick = () => done(null);
    m.querySelector('#pf-save').onclick = () => {
      const r = _pfReadReason({ needsReduceReason: true });
      if (!r.ok) { m.querySelector('#pf-error').textContent = r.msg; return; }
      done({ action: 'reason', reduceReason: r.reason });
    };
  });
}

// The one-step fork. Resolves { action:'payOnly', payAnswer, reduceReason }
// | { action:'elsewhere', customerId, customerName, jobId, jobTitle, internal, seg, dateStr }
// | null (cancelled — nothing saved).
async function _openPayForkDialog(ctx) {
  const { entry, details, newInMs, newOutMs } = ctx;
  const beforeH = Number(details.beforeHours != null ? details.beforeHours : entry.totalHours || 0);
  const afterH = Number(details.newHours != null ? details.newHours : 0);
  const delta = Math.round((afterH - beforeH) * 100) / 100;
  const added = delta > 0;
  const who = entry.personName || 'This person';
  const seg = _payForkSegment(_pfMs(entry.clockIn), _pfMs(entry.clockOut), newInMs, newOutMs);
  const dateStr = _clockFormatDateISO(new Date(_pfMs(entry.clockIn) || newInMs));
  const person = _clockPickerPerson(entry.personId) || { id: entry.personId, name: entry.personName };
  const dayJobDays = added && seg.ok ? await _loadJobDaysForDate(ClockState.tenantId, dateStr) : [];
  const worked = _payForkWorkedCustomerIds(_pfPersonEntriesOn(entry.personId, dateStr, null), dayJobDays, ClockState.allJobs, person);
  const custGroups = _payForkCustomerGroups(ClockState.customers, worked, '');
  const segH = seg.ok ? _pfH((seg.endMs - seg.startMs) / 3600000) : null;
  const opt = (v, l, sub, disabled) => `
    <label class="pf-opt" style="display:flex;gap:10px;align-items:flex-start;padding:10px 12px;border:1px solid var(--border-light);border-radius:8px;cursor:${disabled ? 'not-allowed' : 'pointer'};opacity:${disabled ? 0.55 : 1};min-height:44px;">
      <input type="radio" name="pf-answer" value="${v}" ${disabled ? 'disabled' : ''} style="margin-top:3px;">
      <span><span style="font-weight:600;color:var(--text-primary);">${escapeHtml(l)}</span>${sub ? `<br><span style="font-size:0.8rem;color:var(--text-secondary);">${escapeHtml(sub)}</span>` : ''}</span>
    </label>`;
  const options = added ? [
    // 2nd review ⚪ — say "invoice" only when there IS one; a ticket locked by
    // the office (not invoiced) gets the ticket wording.
    (details.lock && details.lock.kind === 'invoiced')
      ? opt('covered', 'Already billed — the invoice covers it', 'Pay only. The ticket and invoice stay as they are.')
      : opt('covered', 'Already on the ticket — it covers it', 'Pay only. The ticket stays as it is.'),
    opt('invoiceShort', 'This customer — the invoice was short', 'Pay changes now; billing gets a flag to correct the invoice.'),
    opt('elsewhere', seg.ok ? `Somewhere else — add the extra ${segH} h as their own entry` : 'Somewhere else — add the extra hours as their own entry',
      seg.ok ? 'Shop / a yard, or another customer’s job. This shift stays as it is.' : seg.reason, !seg.ok),
  ] : [
    opt('payOnly', 'No — pay only', 'The customer keeps what was billed.'),
    opt('invoiceWrong', 'Yes — flag the invoice for billing', 'Billing gets a flag to correct the invoice.'),
  ];
  // Where internal places are managed: CRM's "internal time" customer flag
  // (not Roster → Facilities — facilities are not a clock destination).
  const internalHint = custGroups.noInternalSetUp
    ? '<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">To list a yard or shop here, mark its customer as internal time in CRM.</div>'
    : '';
  return new Promise((resolve) => {
    document.getElementById('modal-pay-fork')?.remove();
    const m = document.createElement('div');
    m.id = 'modal-pay-fork';
    m.className = 'modal';
    m.innerHTML = `
      <div class="modal-content" style="max-width:480px;max-height:100vh;max-height:100dvh;overflow:auto;">
        <div class="modal-header"><h3>${escapeHtml(added ? 'Where do the extra hours belong?' : 'Does the invoice need correcting?')}</h3></div>
        <div class="modal-body" style="display:flex;flex-direction:column;gap:var(--spacing-md);">
          <div style="font-size:0.85rem;color:var(--text-secondary);">${escapeHtml(_pfLockLine(details))} The ticket and invoice won’t change.</div>
          <div style="color:var(--text-primary);font-weight:600;">${escapeHtml(who)}: ${_pfH(beforeH)} h → ${_pfH(afterH)} h (${delta > 0 ? '+' : ''}${_pfH(delta)} h)</div>
          <div style="display:flex;flex-direction:column;gap:8px;" role="radiogroup">${options.join('')}</div>
          <div id="pf-elsewhere" style="display:none;flex-direction:column;gap:8px;">
            <div class="form-group" style="margin:0;">
              <label for="pf-cust">Customer or internal place</label>
              <input type="search" id="pf-cust-search" class="form-control" placeholder="Search customers..." autocomplete="off" style="margin-bottom:6px;">
              <select id="pf-cust" class="form-control">${_payForkCustomerOptionsHtml(custGroups, '')}</select>
              ${internalHint}
            </div>
            <div class="form-group" id="pf-job-group" style="margin:0;display:none;">
              <label for="pf-job">Job</label>
              <input type="search" id="pf-job-search" class="form-control" placeholder="Search jobs..." autocomplete="off" style="margin-bottom:6px;">
              <select id="pf-job" class="form-control"><option value="">Pick a job…</option></select>
            </div>
          </div>
          ${_pfReasonHtml(details)}
          <div style="font-size:0.75rem;color:var(--text-secondary);">If this pay period was already sent to payroll, the payroll service won’t pull the change by itself.</div>
          <div id="pf-error" style="color:var(--status-danger);font-size:0.85rem;"></div>
        </div>
        <div class="modal-footer" style="display:flex;gap:8px;justify-content:flex-end;">
          <button class="btn btn-secondary btn-sm" id="pf-cancel">Cancel</button>
          <button class="btn btn-primary btn-sm" id="pf-save">Save</button>
        </div>
      </div>`;
    document.body.appendChild(m);
    m.classList.remove('hidden');
    const $ = s => m.querySelector(s);
    const done = (v) => { m.remove(); resolve(v); };
    const custSel = $('#pf-cust');
    const jobSel = $('#pf-job');
    const refreshJobs = () => {
      const custId = custSel ? custSel.value : '';
      const isInternal = custId === '__shop__' || custGroups.internal.some(x => x.id === custId) ||
        (ClockState.customers || []).some(x => x.id === custId && x.internalTimeOnly === true);
      $('#pf-job-group').style.display = (!custId || isInternal) ? 'none' : '';
      if (!custId || isInternal) return;
      jobSel.innerHTML = _payForkJobOptionsHtml(_payForkJobGroups(custId, ClockState.allJobs, dayJobDays, person, $('#pf-job-search').value, null), jobSel.value);
    };
    m.querySelectorAll('input[name="pf-answer"]').forEach(r => r.addEventListener('change', () => {
      const pick = m.querySelector('input[name="pf-answer"]:checked');
      $('#pf-elsewhere').style.display = pick && pick.value === 'elsewhere' ? 'flex' : 'none';
    }));
    $('#pf-cust-search').addEventListener('input', (ev) => {
      const keep = custSel.value;
      custSel.innerHTML = _payForkCustomerOptionsHtml(_payForkCustomerGroups(ClockState.customers, worked, ev.target.value), keep);
      refreshJobs();
    });
    custSel.addEventListener('change', () => { $('#pf-job-search').value = ''; jobSel.value = ''; refreshJobs(); });
    $('#pf-job-search').addEventListener('input', refreshJobs);
    $('#pf-cancel').onclick = () => done(null);
    $('#pf-save').onclick = () => {
      const err = $('#pf-error');
      const pick = m.querySelector('input[name="pf-answer"]:checked');
      if (!pick) { err.textContent = added ? 'Pick where the extra hours belong.' : 'Pick whether the invoice needs correcting.'; return; }
      if (pick.value === 'elsewhere') {
        const custId = custSel.value;
        if (!custId) { err.textContent = 'Pick a customer or an internal place.'; return; }
        const c = (ClockState.customers || []).find(x => x.id === custId) || custGroups.internal.find(x => x.id === custId);
        const internal = custId === '__shop__' || !!(c && c.internalTimeOnly === true);
        const jobId = internal ? null : (jobSel.value || '');
        if (!internal && !jobId) { err.textContent = 'Pick the job the extra hours were on.'; return; }
        const job = jobId ? (ClockState.allJobs || []).find(j => j.id === jobId) : null;
        done({
          // COD diff r1 (Edge Case): the new entry's work day is the day its
          // OWN window starts (an overnight tail belongs to the next day's ticket).
          action: 'elsewhere', internal, seg, dateStr: seg.ok ? _clockFormatDateISO(new Date(seg.startMs)) : dateStr,
          customerId: custId, customerName: custId === '__shop__' ? 'Shop' : (c ? c.name : null),
          jobId: jobId || null, jobTitle: job ? (job.title || job.name || null) : null,
        });
        return;
      }
      const r = _pfReadReason(details);
      if (!r.ok) { err.textContent = r.msg; return; }
      done({ action: 'payOnly', payAnswer: pick.value, reduceReason: r.reason });
    };
  });
}

// The manualEntry payload for "somewhere else" — the SAME shape the Add Hours
// form sends (saveManualClockEntry gwPayload), so payroll, Internal-Time Pay,
// BI and the ticket attach treat it like any manual punch; plus payFork so
// billing's list shows it. Pure (unit-tested against the real form's shape).
function _payForkManualPayload(entry, person, pick) {
  const p = person || { id: entry.personId, name: entry.personName };
  return {
    personId: entry.personId,
    personName: p.name || entry.personName || null,
    customerId: pick.customerId || null,
    customerName: pick.customerName || null,
    jobId: pick.jobId || null,
    jobTitle: pick.jobTitle || null,
    clockIn: new Date(pick.seg.startMs).toISOString(),
    clockOut: new Date(pick.seg.endMs).toISOString(),
    notes: `Extra time from the ${pick.dateStr} shift correction`,
    rate: p.hrHourlyRate || 0,
    role: p.crewType || p.role || (p.roles && p.roles[0]) || 'NON_CDL',
    workDate: pick.dateStr,
    payFork: { sourceEntryId: entry.id },
  };
}

async function _payForkAddElsewhere(entry, pick) {
  // COD diff r1 (Edge Case): the extra window must not land on another of the
  // person's entries (a later shop punch already covering it = double pay).
  const _ov = checkClockOverlap(entry.personId, new Date(pick.seg.startMs), new Date(pick.seg.endMs), entry.id);
  if (_ov) throw new Error(`${_ov} — the extra hours would be paid twice. Fix that entry first.`);
  const person = getPersonForReport(entry.personId);
  const gwPayload = _payForkManualPayload(entry, person, pick);
  const scope = 'payFork:' + entry.id;
  const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: GW_CLIENT_TIMEOUT_MS });
  const res = (await fn({ op: 'manualEntry', tenantId: ClockState.tenantId, payload: gwPayload, opKey: _gwOpKey(scope, gwPayload) }))?.data || null;
  _gwOpDone(scope);
  const u = res?.updated || {};
  const hrs = _pfH((pick.seg.endMs - pick.seg.startMs) / 3600000);
  const where = pick.internal ? (pick.customerName || 'Shop') : [pick.customerName, pick.jobTitle].filter(Boolean).join(' — ');
  if (u.needsTicketDecision) {
    await _manualEntryLockedDayPrompt(u, { customerId: pick.customerId, customerName: pick.customerName, jobId: pick.jobId, jobTitle: pick.jobTitle, dateVal: pick.dateStr });
  } else if (!pick.internal && u.attachError && !u.ticketId) {
    // integ/2108.1 COD r2 (Code Reviewer): same warning as Add Hours — the
    // hours are saved and paid, but they are not on that job's ticket yet.
    window.showToast?.(`Added ${hrs} h to ${where} as its own entry, but attaching them to a ticket failed — they are NOT on a ticket yet. Build it from Billing.`, 'warning');
  } else if (pick.internal) {
    window.showToast?.(`Added ${hrs} h to ${where} as its own entry. The invoiced shift was not changed.`, 'success');
  } else {
    window.showToast?.(`Added ${hrs} h to ${where} as its own entry. Billing will see it to check that job’s ticket.`, 'success');
  }
  return res;
}

// Called from the gateway save's failed-precondition branch. Returns
//   { retry: {...extra payload} }  → re-run the save with these fields
//   { handled: true, result }      → the "somewhere else" entry was added
//   null                           → nothing to offer (caller shows the toast)
//   { cancelled: true }            → the user backed out; nothing saved
async function _payForkOnRefusal(entryId, payload, details, noteChanged) {
  if (!details || typeof details !== 'object') return null;
  const entry = [...ClockState.activeEntries, ...ClockState.recentEntries].find(e => e.id === entryId)
    || Object.values(ClockState.weeklyCache || {}).flat().find(e => e.id === entryId) || null;
  if (details.closedBook === true && details.payOnlyAllowed === true) {
    if (noteChanged) {
      window.showToast?.('Save the hours first, without the note — this ticket’s shift note is locked.', 'error');
      return { cancelled: true };
    }
    if (!entry) return null;
    const pick = await _openPayForkDialog({
      entry, details,
      newInMs: new Date(payload.clockIn).getTime(),
      newOutMs: payload.clockOut ? new Date(payload.clockOut).getTime() : NaN,
    });
    if (!pick) return { cancelled: true };
    if (pick.action === 'elsewhere') {
      // 2nd review 🟡 — a refusal here (future time, overlap, vendor, deleted
      // job) must be SAID; the dialog is already closed. Nothing was saved.
      try {
        const result = await _payForkAddElsewhere(entry, pick);
        return { handled: true, result };
      } catch (fe) {
        console.error('[Clock] TASK-9983 extra-hours entry failed:', fe);
        // COD diff r1 (Independent): a timeout is NOT "nothing saved" — the
        // server may still finish. Say so; the same dialog answer replays the
        // same request key, so a retry cannot add the hours twice.
        if (_gwIsDeadlineExceeded(fe)) {
          window.showToast?.('That took too long — the extra hours may still have been added. Check the person’s day before trying again.', 'warning');
        } else {
          window.showToast?.('The extra hours were NOT added: ' + (fe?.message || 'save failed — try again.') + ' The shift was not changed.', 'error');
        }
        return { cancelled: true };
      }
    }
    return { retry: { payOnly: true, payAnswer: pick.payAnswer, ...(pick.reduceReason ? { reduceReason: pick.reduceReason } : {}) } };
  }
  if (details.needsReduceReason === true && !details.closedBook) {
    const pick = await _openReduceReasonDialog(entry?.personName, details);
    if (!pick) return { cancelled: true };
    return { retry: { reduceReason: pick.reduceReason } };
  }
  return null;
}

async function saveClockEntryEditViaGateway(entryId, extra) {
  // TASK-9983 — `extra` carries the answers of the pay-fork / reason dialogs
  // (payOnly, payAnswer, reduceReason) on a re-run after a server refusal.
  const _extra = (extra && typeof extra === 'object') ? extra : {};
  const cinVal = getSplitDateTimeValue('edit-clock-in');
  const coutVal = getSplitDateTimeValue('edit-clock-out');
  const notesVal = document.getElementById('edit-clock-notes')?.value?.trim() || '';
  const newCustId = document.getElementById('edit-clock-customer')?.value || null;
  const newJobId = document.getElementById('edit-clock-job')?.value || null;
  if (!cinVal) {
    window.showToast('Clock In time is required', 'error');
    return;
  }
  const newCustomer = newCustId ? ClockState.customers.find(c => c.id === newCustId) : null;
  const newJob = newJobId ? ClockState.allJobs.find(j => j.id === newJobId) : null;

  // TASK-4621 — mechanism (b), COD BINDING 1: a changed Notes box routes through
  // the gated editClockNotes op (below, after the main save), NEVER through
  // editClockEntry's `notes` field — a notes-only editClockEntry payload has
  // timesChanging=false and skips the closed-book gate entirely, which would let
  // a staffer rewrite a shift note on an already-INVOICED ticket (the lock
  // TASK-1287 exists to enforce). Baseline = what seeded the box
  // (clockOutNotes || notes, see editClockEntry modal render).
  const _noteEntry = [...ClockState.activeEntries, ...ClockState.recentEntries].find(e => e.id === entryId)
    || Object.values(ClockState.weeklyCache || {}).flat().find(e => e.id === entryId) || null;
  const _noteBaseline = String(_noteEntry?.clockOutNotes || _noteEntry?.notes || '').trim();
  const _noteChanged = notesVal !== _noteBaseline;
  // editClockNotes REQUIRES a ticket binding. When the entry has no ticket and
  // this save cannot mint one (no billable customer picked), keep the legacy
  // `notes` write inside the editClockEntry payload so a staffer correcting a
  // ticketless (Shop/internal) entry is never dead-ended — there is no ticket
  // surface to render on and no closed book to bypass in that case.
  const _notesInPayload = !_noteEntry?.ticketId && !newCustId;

  const payload = {
    entryId,
    // clock-dst-all — company-zone instants (resolved in _saveClockEntryEditInner).
    clockIn: _tltDate(cinVal, 'edit-clock-in').toISOString(),
    clockOut: coutVal ? _tltDate(coutVal, 'edit-clock-out').toISOString() : null,
    ...(_notesInPayload ? { notes: notesVal } : {}),
    customerId: newCustId,
    customerName: newCustomer ? newCustomer.name : (newCustId ? null : 'Shop'),
    jobId: newJobId,
    // TASK-9548 slice 2 (COD Code Reviewer) — a FINISHED job is never in
    // ClockState.allJobs, so an unchanged job used to save as jobTitle:null.
    // Same id as the entry already carries → keep the title it carries.
    jobTitle: newJob ? (newJob.title || newJob.name || null) : _keptJobTitle(newJobId, _noteEntry),
    // TASK-9983 — dialog answers on a re-run (never _depth).
    ...(_extra.payOnly === true ? { payOnly: true, payAnswer: _extra.payAnswer } : {}),
    ...(_extra.reduceReason ? { reduceReason: _extra.reduceReason } : {}),
  };

  try {
    const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: GW_CLIENT_TIMEOUT_MS });
    const _gwScope = 'edit:' + entryId;
    const result = await fn({
      op: 'editClockEntry',
      tenantId: ClockState.tenantId,
      payload,
      opKey: _gwOpKey(_gwScope, payload), // TASK-1160 — payload-hashed: identical retry replays, changed values execute fresh
    });
    if (!result?.data?.ok) throw new Error('Gateway returned not-ok');
    _gwOpDone(_gwScope);
    // TASK-804 fold (a): reflect the server-resolved ticket linkage + canonical
    // customer/job back into local state so a 2nd in-session save doesn't see
    // ticketId:null and re-mint (the gateway minted/moved on the customer
    // change). Mirrors updateLocalEntry in the legacy path.
    const u = result.data.updated || {};
    const applyServerResult = (entry) => {
      if (entry.id !== entryId) return;
      entry.clockIn = Timestamp.fromDate(new Date(payload.clockIn));
      // TASK-4621 — notes ride the payload only on the ticketless path; when
      // routed via editClockNotes instead, don't clobber local notes with
      // undefined (clockOutNotes is mirrored after that call succeeds).
      if (payload.notes !== undefined) entry.notes = payload.notes;
      if (payload.clockOut) {
        entry.clockOut = Timestamp.fromDate(new Date(payload.clockOut));
        if (u.totalHours != null) { entry.totalHours = u.totalHours; entry.hours = u.totalHours; }
        entry.status = 'completed';
      }
      // Prefer the server-resolved canonical values (repoint validates + names);
      // fall back to the submitted payload for the non-reconcile path.
      entry.customerId = (u.customerId !== undefined ? u.customerId : payload.customerId) || null;
      entry.customerName = (u.customerName !== undefined ? u.customerName : payload.customerName) || null;
      entry.jobId = (u.jobId !== undefined ? u.jobId : payload.jobId) || null;
      entry.jobTitle = (u.jobTitle !== undefined ? u.jobTitle : payload.jobTitle) || null;
      if (u.ticketId !== undefined) entry.ticketId = u.ticketId || null;
      if (u.ticketLineId !== undefined) entry.ticketLineId = u.ticketLineId || null;
    };
    ClockState.recentEntries.forEach(applyServerResult);
    ClockState.activeEntries.forEach(applyServerResult);
    for (const entries of Object.values(ClockState.weeklyCache || {})) entries.forEach(applyServerResult);

    // TASK-4621 — persist the changed shift note through the gated editClockNotes
    // op. Runs AFTER the main save so a ticket minted by THIS save (u.ticketId)
    // carries the note too. A refusal here (invoiced/archived/locked ticket, or
    // no permission) lands as a VISIBLE toast below — the times save above stays
    // good, and the note stays untouched in the database; never a silent no-op.
    let _noteSaveError = null;
    if (_noteChanged && !_notesInPayload) {
      const _effTicketId = (u.ticketId !== undefined ? u.ticketId : _noteEntry?.ticketId) || null;
      const _applyNoteLocal = (mutator) => {
        ClockState.recentEntries.forEach(mutator);
        ClockState.activeEntries.forEach(mutator);
        for (const entries of Object.values(ClockState.weeklyCache || {})) entries.forEach(mutator);
      };
      if (_effTicketId) {
        try {
          const nres = await fn({
            op: 'editClockNotes',
            tenantId: ClockState.tenantId,
            payload: { entryId, ticketId: _effTicketId, clockOutNotes: notesVal }
          });
          if (!nres?.data?.ok) throw new Error('Shift note save returned not-ok');
          _applyNoteLocal((entry) => { if (entry.id === entryId) entry.clockOutNotes = notesVal; });
        } catch (ne) {
          _noteSaveError = ne;
        }
      } else {
        // The expected mint didn't land a ticket (attach failed) — fall back to
        // the legacy `notes` write so the staffer's text is never dropped. No
        // ticket exists, so there is no closed book to bypass here.
        try {
          await updateDoc(doc(db, 'tenants', ClockState.tenantId, 'clockEntries', entryId), {
            notes: notesVal, updatedAt: Timestamp.now()
          });
          _applyNoteLocal((entry) => { if (entry.id === entryId) entry.notes = notesVal; });
        } catch (ne) {
          _noteSaveError = ne;
        }
      }
    }
    if (_noteSaveError) {
      console.error('[Clock] Shift note save failed after entry save:', _noteSaveError);
      window.showToast?.('Times saved, but the shift note was NOT: ' +
        (_noteSaveError?.message || 'save failed — reopen the entry and try again.'), 'error');
    } else if (u.payChange) {
      // TASK-9983 — say exactly what changed and what did not.
      const pc = u.payChange;
      window.showToast?.(`Pay saved: ${pc.deltaHours > 0 ? '+' : ''}${_pfH(pc.deltaHours)} h. The ticket and invoice were not changed.` +
        (pc.state === 'needsBilling' ? ' Billing has a flag to check the invoice.' : ''), 'success');
    } else {
      window.showToast?.('Saved', 'success');
    }
    // TASK-1654 — the gateway cascaded the new window onto this entry's
    // EQUIPMENT lines. Surface what MOVED (money changed — the human net for a
    // wrong cascade) and any line it deliberately did NOT touch (hand-adjusted
    // window/hours) so the biller reviews it instead of trusting a silently-
    // frozen number.
    if (Array.isArray(u.equipmentLinesUpdated) && u.equipmentLinesUpdated.length > 0) {
      const moved = u.equipmentLinesUpdated
        .map(l => `${l.assetName || l.assetId || 'equipment'} ${l.beforeHours ?? '?'}h → ${l.hours}h`)
        .join(', ');
      window.showToast?.(`Equipment re-billed to the new times: ${moved}`, 'info');
    }
    if (Array.isArray(u.equipmentLinesSkipped) && u.equipmentLinesSkipped.length > 0) {
      const names = u.equipmentLinesSkipped.map(s => s.assetName || s.assetId || 'a line').join(', ');
      window.showToast?.(`Heads up: ${u.equipmentLinesSkipped.length} equipment line(s) kept their own window (${names}) — review the ticket if they should follow the new times.`, 'warning');
    }
    document.getElementById('modal-edit-clockentry')?.classList.add('hidden');
    // Re-render so the corrected ticket linkage (Ticket button, pills) shows.
    const content = document.getElementById('workforce-content');
    if (content) content.innerHTML = getWorkforceContentHTML();
    renderActiveList();
    updateSelectedPersonStatus();

    // TASK-1177: refresh the open employee-detail panel after a gateway save —
    // mirrors the legacy path's fold (saveClockEntryEdit ~L13781). The panel's
    // rows come from its OWN pay-period Firestore query (in NO cache), so
    // patching the caches + re-rendering workforce-content isn't enough; without
    // this the open panel keeps showing stale pre-edit rows + totals.
    const editedEntry = [...ClockState.activeEntries, ...ClockState.recentEntries].find(e => e.id === entryId);
    if (!editedEntry) {
      let foundInCache = false;
      for (const entries of Object.values(ClockState.weeklyCache || {})) {
        const found = entries.find(e => e.id === entryId);
        if (found) { foundInCache = true; showEmployeeDetail(found.personId, false); break; }
      }
      // Fallback: entry lives only in the detail panel's own pay-period query, so
      // it's in NO local cache — refresh by the open panel's personId. Only while
      // the workforce modal is actually open (detailPersonId is never cleared).
      if (!foundInCache && ClockState.detailPersonId &&
          !document.getElementById('modal-workforce')?.classList.contains('hidden')) {
        showEmployeeDetail(ClockState.detailPersonId, false);
      }
    } else {
      showEmployeeDetail(editedEntry.personId, false);
    }
    return result.data;
  } catch (e) {
    console.warn('[Clock] Gateway edit failed, falling back to direct write:', e?.message);
    // A permission-denied is an AUTHZ rejection (e.g. a clock-only member tried
    // to mint), NOT a transient gateway failure — surface it and do NOT fall
    // back to the legacy direct-write (which can't mint anyway). For other
    // errors, disable the gateway for the session so the user can retry legacy.
    const code = e?.code || e?.details?.code || '';
    if (/permission-denied/i.test(String(code)) || /permission-denied/i.test(String(e?.message))) {
      window.showToast?.(e?.message || 'You don’t have permission to change this entry’s customer.', 'error');
      throw e;
    }
    // TASK-1654 — failed-precondition is a deliberate SERVER REFUSAL (closed
    // books: the ticket is invoiced/archived/locked, so an hours correction
    // must go through the Billing unlock flow first). Surface it and do NOT
    // disable the gateway — falling back to the legacy direct write would
    // silently mutate the very books the server just refused to touch.
    if (/failed-precondition/i.test(String(code))) {
      // TASK-9983 — the server says what it will accept instead: a pay-only
      // save on a closed ticket, or the same edit with a reason code. Ask
      // once, then re-run. Nothing was saved by the refused call.
      const _det = e?.details && typeof e.details === 'object' ? e.details : null;
      if (_det && (_extra._depth || 0) < 2) {
        const _fork = await _payForkOnRefusal(entryId, payload, _det, _noteChanged && !_notesInPayload);
        if (_fork && _fork.retry) {
          return saveClockEntryEditViaGateway(entryId, { ..._extra, ..._fork.retry, _depth: (_extra._depth || 0) + 1 });
        }
        if (_fork && _fork.handled) {
          document.getElementById('modal-edit-clockentry')?.classList.add('hidden');
          const content = document.getElementById('workforce-content');
          if (content) content.innerHTML = getWorkforceContentHTML();
          renderActiveList();
          if (_noteEntry?.personId) showEmployeeDetail(_noteEntry.personId, false);
          // undefined on purpose: THIS entry was not saved (the extra hours went
          // to a new entry), so saveClockEntryEdit must not apply this form's
          // follow-up writes (the admin vehicle claim) to it.
          return undefined;
        }
        if (_fork && _fork.cancelled) { e._userMessageShown = true; return undefined; }
      }
      window.showToast?.(e?.message || 'This entry’s ticket is closed — unlock it in Billing before correcting times.', 'error');
      e._userMessageShown = true;
      throw e;
    }
    // TASK-1160 — deadline-exceeded means WE gave up while the server may still
    // complete. Do NOT flip to the legacy raw-write path (that's the
    // double-apply race) — keep the gateway on and let the user retry; the
    // opKey dedups an identical retry server-side.
    if (_gwIsDeadlineExceeded(e)) {
      window.showToast?.('That took too long — your edit may still have saved. Check the entry, then retry if it didn’t.', 'warning');
      e._userMessageShown = true;
      throw e;
    }
    // TASK-9983 (2nd review 🔴) — a re-run carrying dialog answers (pay-only /
    // reason) that the server refuses is a REFUSAL, never an outage: the legacy
    // direct write has no closed-book check and would change pay on the
    // invoiced shift with no record. Say it and keep the gateway on.
    if ((_extra._depth || 0) > 0 || _extra.payOnly === true || _extra.reduceReason) {
      window.showToast?.(e?.message || 'Nothing was saved — check the times and try again.', 'error');
      e._userMessageShown = true;
      throw e;
    }
    // TASK-9989 item 1 (COD r1) — the server's own validation (invalid-argument:
    // bad times, zoneless time) or a vanished entry (not-found) is a refusal
    // too: the direct path would write the same thing with no closed-book check.
    if (/invalid-argument|not-found/i.test(String(code))) {
      window.showToast?.(e?.message || 'Nothing was saved — check the times and try again.', 'error');
      e._userMessageShown = true;
      throw e;
    }
    _clockGatewayFallback('editClockEntry', e); // disable for rest of session — let user retry via legacy
    window.showToast?.('Couldn’t save through the server — tap Save once more.', 'warning');
    throw e;
  }
}

// TASK-9443 — SOFT pay-period lock. Before an edit/delete of a shift that falls
// in a period whose payroll was already exported, say so and let the editor
// cancel. true = proceed. Fail-open on EVERYTHING (no module, lookup error,
// entry not found): the lock informs, it never traps. The change itself is
// listed in the next Payroll Prep by the snapshot diff, whichever path wrote it.
async function _payrollLockProceed(entryId, what, newClockInVal) {
  try {
    const PL = window.PayrollRunLock;
    if (!PL || !entryId) return true;
    const runs = await PL.refresh(false);
    // No exported runs → nothing to warn about, but ONLY when the lookup actually
    // worked. An unknown lock state (lookup error, claim mismatch after a company
    // switch) goes on to confirmEdit, which still warns (fail SAFE, re-review COD).
    if ((!runs || !runs.length) && (typeof PL.lookupOk !== 'function' || PL.lookupOk())) return true;
    const pool = [...(ClockState.activeEntries || []), ...(ClockState.recentEntries || []), ...(ClockState.historyEntries || []),
      ...Object.values(ClockState.weeklyCache || {}).flat()];
    let e = pool.find(x => x && x.id === entryId) || null;
    if (!e && ClockState.tenantId) {
      const snap = await getDoc(doc(db, 'tenants', ClockState.tenantId, 'clockEntries', entryId));
      e = snap.exists() ? snap.data() : null;
    }
    const ci = e && (e.clockIn?.toDate?.() || (e.clockIn ? new Date(e.clockIn) : null));
    const ms = ci ? ci.getTime() : NaN;
    if (!Number.isFinite(ms)) return true;
    if (!(await PL.confirmEdit(ms, { what }))) return false;
    // The edit can also MOVE the shift into an exported period (new clock-in) — ask once for that period too.
    const nms = newClockInVal ? _tltPeek(newClockInVal, 'edit-clock-in').getTime() : NaN; // TASK-9971 company zone, not the phone's
    const from = PL.findRunFor(runs, ms), to = Number.isFinite(nms) ? PL.findRunFor(runs, nms) : null;
    if (to && (!from || from.key !== to.key)) return await PL.confirmEdit(nms, { what: what + ' (it moves the shift into this period)' });
    return true;
  } catch (_) { return true; }
}

async function saveClockEntryEdit(entryId) {
  // TASK-804 fold (c): disable + spinner the Save button for the duration so a
  // double-click can't fire two saves (a double-mint on the customer-change
  // path). Re-enabled in the finally below. Guard against re-entry too.
  const _saveBtn = document.getElementById('btn-save-clockentry-edit');
  if (_saveBtn) {
    if (_saveBtn.dataset.saving === '1') return; // already in flight
    _saveBtn.dataset.saving = '1';
    _saveBtn.disabled = true;
    _saveBtn.dataset.label = _saveBtn.innerHTML;
    _saveBtn.innerHTML = '<span class="btn-spinner"></span> Saving...';
  }
  try {
    // TASK-9867 — a Claude request is waiting on this shift: saving by hand
    // makes it fail at approve (fingerprint guard). Ask once, offer the reject.
    {
      const _pend = _clockPendingFor(entryId);
      if (_pend) {
        const _who = _pend.mine ? 'you' : (_pend.requester || 'someone else');
        const _ok = typeof window.showConfirm === 'function'
          ? await window.showConfirm('A Claude request is waiting on this shift', `${_clockPendingKindText(_pend)} — asked by ${_who}. If you save this edit, that request will fail when it is approved.${_pend.canReject ? ' You can reject it from the warning above instead.' : ''}`, { confirmText: 'Save anyway', cancelText: 'Cancel', danger: false, safeDefault: true })
          : confirm('A Claude request is waiting on this shift. Save anyway?');
        if (!_ok) return;
      }
    }
    // TASK-9443 — soft pay-period lock: warn when this shift's payroll was already exported.
    if (!(await _payrollLockProceed(entryId, 'This edit', getSplitDateTimeValue('edit-clock-in')))) return;
    // TASK-9983 — wrong-entry guard: say plainly when this edit turns the
    // entry into a different job/customer or lands on another of the person's
    // entries (Brian 10/14: a 9.06 h haul re-timed onto a 2.77 h shop window).
    if (!(await _wrongEntryGuardOk(entryId))) return;
    // TASK-1483 — read + validate the admin vehicle claim BEFORE the main save
    // (the inner save re-renders / closes the modal). Separate write after,
    // because the edit gateway op does not carry these fields.
    const _pvAdm = pvReadForm('adm');
    if (_pvAdm && !_pvAdm.ok) {
      window.showToast?.(_pvAdm.error, 'error');
      document.getElementById('adm-pv-miles')?.focus();
      return;
    }
    const _pvBefore = _pvAdm ? { ...(_pvFindEntry(entryId) || {}) } : null;
    const _res = await _saveClockEntryEditInner(entryId);
    // Only after the main edit SAVED (legacy returns true, gateway returns its
    // ok payload; a refused/cancelled/failed edit returns undefined or throws)
    // — never change the miles or clear a review behind a failed edit.
    if (_pvAdm && _res) await pvAdminSave(entryId, _pvAdm.fields, _pvBefore);
    return _res;
  } finally {
    if (_saveBtn) {
      _saveBtn.dataset.saving = '0';
      _saveBtn.disabled = false;
      if (_saveBtn.dataset.label) _saveBtn.innerHTML = _saveBtn.dataset.label;
    }
  }
}

async function _saveClockEntryEditInner(entryId) {
  // Phase 2 of the Clock & Billing refactor: when the gateway feature flag is on,
  // route this dispatcher edit through the transactional Cloud Function instead
  // of writing Firestore directly. TASK-9989 item 1: the flag is ON for every
  // company (initClock → _clockGatewayDefaultOn); the direct write below is the
  // demo sandbox + the logged per-session fallback only. Driver clockOut paths
  // intentionally NOT routed through the gateway (need Firestore offline support).
  // clock-dst-all — the typed clock in/out are COMPANY time. Resolve both BEFORE
  // either write path (gateway or direct): refuse an unreadable zone / a time
  // that never happened, ask which one in the repeated fall-back hour (showing
  // the shift length each gives). A refusal writes nothing.
  {
    const _ci = getSplitDateTimeValue('edit-clock-in');
    const _co = getSplitDateTimeValue('edit-clock-out');
    const _rt = await _clockResolveTimes([
      { str: _ci, key: 'edit-clock-in', label: 'Clock in' },
      { str: _co, key: 'edit-clock-out', label: 'Clock out' },
    ], { inStr: _ci, inKey: 'edit-clock-in', outStr: _co, outKey: 'edit-clock-out' });
    if (!_rt.ok) {
      window.showToast(_rt.message, 'warning', null, 9000);
      return;
    }
  }
  const useGateway = !!window.USE_TICKET_GATEWAY || !!window.AppState?.settings?.useTicketGateway;
  if (useGateway) {
    return saveClockEntryEditViaGateway(entryId);
  }

  const cinVal = getSplitDateTimeValue('edit-clock-in');
  const coutVal = getSplitDateTimeValue('edit-clock-out');
  const notesVal = document.getElementById('edit-clock-notes')?.value?.trim() || '';
  const newCustId = document.getElementById('edit-clock-customer')?.value || null;
  const newJobId = document.getElementById('edit-clock-job')?.value || null;

  if (!cinVal) {
    window.showToast('Clock In time is required', 'error');
    return;
  }

  // Resolve customer/job names
  const newCustomer = newCustId ? ClockState.customers.find(c => c.id === newCustId) : null;
  const newJob = newJobId ? ClockState.allJobs.find(j => j.id === newJobId) : null;

  // TASK-4621 — mechanism (b), COD BINDING 1: on a TICKET-BOUND entry a changed
  // Notes box goes through the gated editClockNotes op (after the main write
  // below), not this raw `notes` write — the raw write has no closed-book check,
  // so it would let a staffer rewrite a shift note on an invoiced ticket. A
  // ticketless entry (nothing to render on, no book to bypass) and the demo
  // sandbox (no server gateway) keep the legacy `notes` write so nobody is
  // dead-ended. Baseline = what seeded the box (clockOutNotes || notes).
  const _noteEntry = [...ClockState.activeEntries, ...ClockState.recentEntries].find(e => e.id === entryId)
    || Object.values(ClockState.weeklyCache || {}).flat().find(e => e.id === entryId) || null;
  const _noteBaseline = String(_noteEntry?.clockOutNotes || _noteEntry?.notes || '').trim();
  const _noteChanged = notesVal !== _noteBaseline;
  const _isDemoTenant = ClockState.tenantId === 'demo' || ClockState.tenantId === 'demo-tenant';
  const _routeNoteViaGateway = _noteChanged && !!_noteEntry?.ticketId && !_isDemoTenant;

  const newClockIn = _tltDate(cinVal, 'edit-clock-in'); // clock-dst-all — company zone
  const updates = {
    clockIn: Timestamp.fromDate(newClockIn),
    ...(_routeNoteViaGateway ? {} : { notes: notesVal }),
    customerId: newCustId,
    customerName: newCustomer ? newCustomer.name : (newCustId ? null : 'Shop'),
    jobId: newJobId,
    // TASK-9548 slice 2 — see saveClockEntryEditViaGateway: keep the stored title
    // when the (finished, uncached) job is unchanged.
    jobTitle: newJob ? (newJob.title || newJob.name || null) : _keptJobTitle(newJobId, _noteEntry)
  };

  // Guard: clock-in cannot be in the future
  if (newClockIn > new Date()) {
    window.showToast('Clock In cannot be in the future', 'error');
    return;
  }

  if (coutVal) {
    const newClockOut = _tltDate(coutVal, 'edit-clock-out');
    if (newClockOut <= newClockIn) {
      window.showToast('Clock Out must be after Clock In', 'error');
      return;
    }
    // Guard: clock-out cannot be in the future
    if (newClockOut > new Date()) {
      window.showToast('Clock Out cannot be in the future', 'error');
      return;
    }
    const totalHours = (newClockOut - newClockIn) / 3600000;
    if (totalHours <= 0) {
      window.showToast('Clock Out must be after Clock In', 'error');
      return;
    }
    // Guard: flag unreasonably long shifts (>24h)
    if (totalHours > 24) {
      const confirmLong = confirm(`This entry is ${totalHours.toFixed(1)} hours — are you sure the dates are correct?`);
      if (!confirmLong) return;
    }
    updates.clockOut = Timestamp.fromDate(newClockOut);
    updates.totalHours = Math.round(totalHours * 100) / 100;
    // Keep `hours` in sync with `totalHours` — they're parallel fields.
    // Skipping this is the bug that produced Javaun's 9.84 vs 16.58 mismatch.
    updates.hours = updates.totalHours;
    // Transition active → completed: subscriptions filter on status, so we must set it
    updates.status = 'completed';
  } else if (!coutVal && document.getElementById('edit-clock-out-date')?.disabled === false) {
    // Clock-out field exists but was cleared — don't recalc
  } else {
    // Clock-in changed but clock-out not editable (still active) or unchanged —
    // recalculate totalHours if the entry already has a clockOut
    const existingEntry = [...ClockState.activeEntries, ...ClockState.recentEntries].find(e => e.id === entryId)
      || Object.values(ClockState.weeklyCache || {}).flat().find(e => e.id === entryId);
    if (existingEntry?.clockOut) {
      const existingClockOut = existingEntry.clockOut?.toDate?.() || new Date(existingEntry.clockOut);
      if (existingClockOut > newClockIn) {
        updates.totalHours = Math.round(((existingClockOut - newClockIn) / 3600000) * 100) / 100;
        // Keep `hours` in sync with `totalHours` (same bug class as above).
        updates.hours = updates.totalHours;
      }
    }
  }

  // Overlap check — find the original entry to get personId
  const origForOverlap = [...ClockState.activeEntries, ...ClockState.recentEntries].find(e => e.id === entryId)
    || Object.values(ClockState.weeklyCache || {}).flat().find(e => e.id === entryId);
  if (origForOverlap) {
    const effectiveOut = coutVal ? _tltDate(coutVal, 'edit-clock-out') : (origForOverlap.clockOut?.toDate?.() || new Date(origForOverlap.clockOut));
    const overlap = checkClockOverlap(origForOverlap.personId, newClockIn, effectiveOut, entryId);
    if (overlap) {
      window.showToast(overlap, 'error');
      return;
    }
  }

  const tenant = ClockState.tenantId;
  try {
    // Capture before-snapshot for audit log
    const beforeSnap = [...ClockState.activeEntries, ...ClockState.recentEntries].find(e => e.id === entryId)
      || Object.values(ClockState.weeklyCache || {}).flat().find(e => e.id === entryId);
    await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entryId), updates);

    // Audit-log the edit (Phase 1: clockEntryAuditLog) — fire-and-forget, won't block UI
    logClockEntryChange(tenant, entryId, beforeSnap, { ...beforeSnap, ...updates }, 'edit', 'dispatcher-edit-modal');

    // Sync ticket line hours if linked
    const origEntry = [...ClockState.activeEntries, ...ClockState.recentEntries].find(e => e.id === entryId)
      || Object.values(ClockState.weeklyCache || {}).flat().find(e => e.id === entryId);
    if (origEntry?.ticketId && updates.totalHours !== undefined) {
      try {
        const linesSnap = await getDocs(
          query(
            collection(db, 'tenants', tenant, 'tickets', origEntry.ticketId, 'ticketLines'),
            where('type', '==', 'EMPLOYEE'),
            where('personId', '==', origEntry.personId)
          )
        );
        for (const lineDoc of linesSnap.docs) {
          await updateDoc(doc(db, 'tenants', tenant, 'tickets', origEntry.ticketId, 'ticketLines', lineDoc.id), {
            hours: updates.totalHours,
            // TASK-1653 (COD 2026-08-19): EMPLOYEE lines NEVER carry customer
            // money — "we don't bill people on tickets, just equipment." This
            // legacy fallback used to recompute `hours × stored rate`, re-
            // minting a dollar figure on any line whose rate had been violated
            // non-zero. Pin to 0 like the gateway (EMPLOYEE_LINE_RATE/_AMOUNT)
            // and pubSaveEditShift already do; zeroEmployeeLineMoney backstops.
            // Canonical field is `lineAmount` (matches every other writer +
            // ticketLineDedup.js). Was writing `total` (orphan field) long ago.
            // TASK-9646 S5 — `rate` is no longer sent: the rules pin it on this
            // arm (unchanged only), and an EMPLOYEE line is born at 0 anyway.
            lineAmount: 0,
          });
        }
      } catch (syncErr) {
        console.error('[Clock] Error syncing ticket line hours:', syncErr);
      }
    }

    // TASK-4621 — a ticket-bound note change goes through the gated
    // editClockNotes op (unconditional invoiced/archived/locked check + ticket
    // binding + Edited provenance). Best-effort AFTER the entry write: a refusal
    // surfaces as a visible toast below, never a silent no-op, and the
    // time/customer save above stands either way.
    let _noteSaveError = null;
    let _noteSaved = false;
    if (_routeNoteViaGateway) {
      try {
        const _noteFn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: GW_CLIENT_TIMEOUT_MS });
        const nres = await _noteFn({
          op: 'editClockNotes',
          tenantId: ClockState.tenantId,
          payload: { entryId, ticketId: _noteEntry.ticketId, clockOutNotes: notesVal }
        });
        if (!nres?.data?.ok) throw new Error('Shift note save returned not-ok');
        _noteSaved = true;
      } catch (ne) {
        _noteSaveError = ne;
        console.error('[Clock] Shift note save failed after entry save:', ne);
      }
    }

    // Update local state arrays so UI reflects changes immediately
    const updateLocalEntry = (entry) => {
      if (entry.id !== entryId) return;
      entry.clockIn = updates.clockIn;
      if (updates.notes !== undefined) entry.notes = updates.notes;
      if (_noteSaved) entry.clockOutNotes = notesVal;
      entry.customerId = updates.customerId;
      entry.customerName = updates.customerName;
      entry.jobId = updates.jobId;
      entry.jobTitle = updates.jobTitle;
      if (updates.clockOut) {
        entry.clockOut = updates.clockOut;
        entry.totalHours = updates.totalHours;
      }
    };
    ClockState.recentEntries.forEach(updateLocalEntry);
    ClockState.activeEntries.forEach(updateLocalEntry);
    for (const entries of Object.values(ClockState.weeklyCache || {})) {
      entries.forEach(updateLocalEntry);
    }

    if (_noteSaveError) {
      window.showToast('Times saved, but the shift note was NOT: ' +
        (_noteSaveError?.message || 'save failed — reopen the entry and try again.'), 'error');
    } else {
      window.showToast('Clock entry updated', 'success');
    }
    document.getElementById('modal-edit-clockentry')?.classList.add('hidden');

    // Re-render the Hours tab (stat cards, lists, weekly table)
    const content = document.getElementById('workforce-content');
    if (content) content.innerHTML = getWorkforceContentHTML();

    // Re-render active list and selected person status with updated clock-in time
    renderActiveList();
    updateSelectedPersonStatus();

    // Refresh the employee detail modal with updated data
    const editedEntry = [...ClockState.activeEntries, ...ClockState.recentEntries].find(e => e.id === entryId);
    if (!editedEntry) {
      let foundInCache = false;
      for (const entries of Object.values(ClockState.weeklyCache || {})) {
        const found = entries.find(e => e.id === entryId);
        if (found) { foundInCache = true; showEmployeeDetail(found.personId, false); break; }
      }
      // Fallback: entry was loaded only by the detail panel's own Firestore
      // query (pay-period range), so it's in NO local cache — without this the
      // open panel keeps showing stale data after a save. Only refresh while
      // the workforce modal is actually open (detailPersonId is never cleared).
      if (!foundInCache && ClockState.detailPersonId &&
          !document.getElementById('modal-workforce')?.classList.contains('hidden')) {
        showEmployeeDetail(ClockState.detailPersonId, false);
      }
    } else {
      showEmployeeDetail(editedEntry.personId, false);
    }
    // TASK-1483 (COD round 3) — an explicit success result: the caller writes
    // the personal-vehicle claim ONLY when the main edit actually saved.
    return true;
  } catch (err) {
    console.error('[Clock] Error updating clock entry:', err);
    window.showToast('Error updating entry', 'error');
  }
}

// ============================================
// ADD HOURS — PERSON PICKER (includes inactive/terminated)
// ============================================

async function showAddHoursPersonPicker() {
  // TASK-360 — workforce admin: add hours for anyone (including
  // terminated/inactive). Workforce-only.
  if (!_assertWorkforceAccess('showAddHoursPersonPicker')) return;
  const modalBody = document.getElementById('workforce-modal-body');
  if (!modalBody) return;

  // Show loading in workforce modal
  document.getElementById('modal-workforce').classList.remove('hidden');
  modalBody.innerHTML = `<div style="text-align:center;padding:var(--spacing-xl);"><span class="btn-spinner"></span> Loading roster...</div>`;

  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', ClockState.tenantId, 'people'),
      orderBy('name')
    ));
    const allPeople = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const active = allPeople.filter(p => p.active !== false);
    const inactive = allPeople.filter(p => p.active === false);

    const renderRow = (p) => {
      const initials = (p.name || '??').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
      const role = p.crewType || p.role || p.roles?.[0] || '';
      return `
        <div onclick="window.ClockModule.showEmployeeDetail('${p.id}')" style="display:flex;align-items:center;gap:10px;padding:10px 12px;cursor:pointer;border-radius:var(--radius-sm);transition:background 0.15s;" onmouseenter="this.style.background='var(--bg-secondary)'" onmouseleave="this.style.background='transparent'">
          <span class="wf-table-avatar" style="background:${p.color || '#64748b'};width:32px;height:32px;font-size:0.7rem;">${initials}</span>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:500;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(p.name || 'Unknown')}</div>
            ${role ? `<div style="font-size:0.75rem;color:var(--text-secondary);">${escapeHtml(role)}</div>` : ''}
          </div>
          <span style="font-size:0.7rem;padding:2px 8px;border-radius:10px;font-weight:500;${p.active !== false ? 'background:#DCFCE7;color:#166534;' : 'background:#FEE2E2;color:#991B1B;'}">${p.active !== false ? 'Active' : 'Inactive'}</span>
        </div>`;
    };

    modalBody.innerHTML = `
      <div style="margin-bottom:var(--spacing-md);">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-sm);">
          <h3 style="margin:0;color:var(--text-primary);">Add Hours — Select Employee</h3>
          <button class="btn btn-secondary btn-sm" onclick="window.ClockModule.switchWorkforceTab('hours')" style="font-size:0.78rem;">Back to Hours</button>
        </div>
        <input type="search" id="add-hours-search" placeholder="Search employees..." autocomplete="off"
          oninput="window.ClockModule._filterAddHoursPicker(this.value)"
          style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.9rem;margin-bottom:var(--spacing-sm);" />
      </div>
      <div id="add-hours-list" style="max-height:55vh;overflow-y:auto;">
        ${active.length ? `<div style="font-size:0.7rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;padding:4px 12px;">Active (${active.length})</div>` : ''}
        ${active.map(renderRow).join('')}
        ${inactive.length ? `<div style="font-size:0.7rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;padding:12px 12px 4px;border-top:1px solid var(--border-light);margin-top:4px;">Inactive / Terminated (${inactive.length})</div>` : ''}
        ${inactive.map(renderRow).join('')}
      </div>
    `;

    // Store for filtering
    ClockState._addHoursAllPeople = allPeople;
    ClockState._addHoursRenderRow = renderRow;
  } catch (err) {
    console.error('[Clock] Error loading people for add-hours:', err);
    modalBody.innerHTML = `<div style="text-align:center;padding:var(--spacing-xl);color:var(--status-danger);">Error loading roster</div>`;
  }
}

function _filterAddHoursPicker(term) {
  const list = document.getElementById('add-hours-list');
  if (!list || !ClockState._addHoursAllPeople) return;
  const q = (term || '').toLowerCase().trim();
  const all = ClockState._addHoursAllPeople;
  const renderRow = ClockState._addHoursRenderRow;
  const filtered = q ? all.filter(p => (p.name || '').toLowerCase().includes(q)) : all;
  const active = filtered.filter(p => p.active !== false);
  const inactive = filtered.filter(p => p.active === false);
  list.innerHTML =
    (active.length ? `<div style="font-size:0.7rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;padding:4px 12px;">Active (${active.length})</div>` : '') +
    active.map(renderRow).join('') +
    (inactive.length ? `<div style="font-size:0.7rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;padding:12px 12px 4px;border-top:1px solid var(--border-light);margin-top:4px;">Inactive / Terminated (${inactive.length})</div>` : '') +
    inactive.map(renderRow).join('');
  if (!filtered.length) {
    list.innerHTML = `<div style="text-align:center;padding:var(--spacing-lg);color:var(--text-secondary);">No matches</div>`;
  }
}

// ============================================
// WORKFORCE MANUAL ENTRY & PER-DIEM
// ============================================

function showAddClockEntryForm(personId) {
  // TASK-364 — inactive folks live in _historicalPeopleCache (populated by
  // showEmployeeDetail when admin clicks on them). Use merged lookup so
  // Add Entry flow works for terminated/inactive employees.
  const person = getPersonForReport(personId);
  if (!person) return;

  // clock-dst-all — the COMPANY's today (was the UTC date: after 7 PM Central it
  // defaulted to tomorrow and the save tripped "Cannot create entries in the future").
  const today = (_tlt() && _tlt().zone()) ? _tlt().todayKey() : new Date().toISOString().split('T')[0];
  // TASK-1411 — carry the tenant's OWN internal-time flag onto the option so the
  // form can tell billable work from internal without hardcoding any customer
  // name. Every tenant marks their own internal customers (TASK-1175); nothing
  // here knows or cares that TITS calls theirs "Shop" and "Office".
  // TASK-9548 — grouped like every other clock customer picker.
  // TASK-10250 (COD) — the Reports filter is only a default: a NEW entry never
  // starts on an Inactive / merged-away customer, so that filter is not kept.
  const _addCust = (ClockState.customers || []).find(c => c && c.id === ClockState.reportsCustomer);
  const customerOptions = _clockCustomerOptionsHtml(ClockState.customers, {
    selectedId: _addCust && window.PickerGroups.isInactiveCustomer(_addCust) ? null : ClockState.reportsCustomer,
    optionAttrs: c => ` data-internal-time="${c.internalTimeOnly === true ? '1' : '0'}"`,
  });

  let formDiv = document.getElementById('add-entry-form-container');
  if (!formDiv) {
    formDiv = document.createElement('div');
    formDiv.id = 'add-entry-form-container';
    const modalBody = document.getElementById('workforce-modal-body');
    if (modalBody) modalBody.insertBefore(formDiv, modalBody.firstChild);
  }

  formDiv.innerHTML = `
    <div style="background:var(--bg-secondary);border-radius:var(--radius-md);padding:var(--spacing-md);margin-bottom:var(--spacing-md);border:1px solid var(--brand-blue);">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-sm);">
        <strong style="color:var(--text-primary);font-size:0.9rem;">New Clock Entry</strong>
        <button class="btn-icon" onclick="document.getElementById('add-entry-form-container').innerHTML=''" style="color:var(--text-secondary);"><span class="material-icons" style="font-size:18px;">close</span></button>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
        <div class="form-group" style="margin-bottom:0;">
          <label style="font-size:0.78rem;color:var(--text-secondary);">Date</label>
          <input type="date" id="add-entry-date" class="form-control" value="${today}" style="font-size:0.85rem;">
        </div>
        <div class="form-group" style="margin-bottom:0;">
          <label style="font-size:0.78rem;color:var(--text-secondary);">Customer</label>
          <select id="add-entry-customer" class="form-control" style="font-size:0.85rem;" onchange="window.ClockModule._filterAddEntryJobs(); window.ClockModule._syncAddEntryNotesRequirement();">
            <option value="">Select...</option>
            ${customerOptions}
          </select>
          ${_clockCustomerHintHtml(ClockState.customers)}
        </div>
        <div class="form-group" style="margin-bottom:0;grid-column:1/-1;">
          <label style="font-size:0.78rem;color:var(--text-secondary);">Job</label>
          <select id="add-entry-job" class="form-control" style="font-size:0.85rem;">
            <option value="">Select...</option>
          </select>
        </div>
        <div class="form-group" style="margin-bottom:0;">
          <label style="font-size:0.78rem;color:var(--text-secondary);">Clock In</label>
          <input type="time" id="add-entry-clockin" class="form-control" value="07:00">
        </div>
        <div class="form-group" style="margin-bottom:0;">
          <label style="font-size:0.78rem;color:var(--text-secondary);">Clock Out</label>
          <input type="time" id="add-entry-clockout" class="form-control" value="17:00">
        </div>
        <div class="form-group" style="margin-bottom:0;grid-column:1/-1;">
          <label style="font-size:0.78rem;color:var(--text-secondary);" id="add-entry-notes-label">Notes</label>
          <input type="text" id="add-entry-notes" class="form-control" placeholder="Optional" style="font-size:0.85rem;">
          <div id="add-entry-notes-hint" style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;"></div>
        </div>
        ${/* TASK-1483 — people who never punch still get a mileage log */''}
        ${pvAdminSectionOn(person, null) ? pvAdminSectionHTML('addpv', null) : ''}
      </div>
      <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:var(--spacing-sm);">
        <button class="btn btn-secondary btn-sm" onclick="document.getElementById('add-entry-form-container').innerHTML=''">Cancel</button>
        <button class="btn btn-primary btn-sm" id="btn-save-manual-entry" onclick="window.ClockModule.saveManualClockEntry('${personId}')">Save Entry</button>
      </div>
    </div>
  `;

  // TASK-1411 (COD Code-Reviewer ❌) — the form can open with a customer ALREADY
  // preselected from ClockState.reportsCustomer, and nothing ran after the
  // innerHTML write. So the label stayed "Notes / Optional" and the hint stayed
  // blank in exactly the case the feature exists for, and the job dropdown sat
  // empty. Sync both on mount, not only on change.
  setTimeout(() => {
    try { _filterAddEntryJobs(); } catch (_) { /* job list is best-effort */ }
    try { _syncAddEntryNotesRequirement(); } catch (_) { /* label is best-effort */ }
  }, 0);
}

// TASK-1411 — is the customer selected on the manual-entry form BILLABLE?
// Reads the tenant's OWN `internalTimeOnly` flag off the selected option
// (TASK-1175 model: internal time goes to a real customer and the flag decides
// tickets-or-not). No customer selected = not billable. Deliberately knows
// nothing about any customer NAME — every tenant marks their own internal work,
// so this behaves correctly for a company that calls it "Yard" or "Overhead".
function _addEntryIsBillable() {
  const sel = document.getElementById('add-entry-customer');
  if (!sel || !sel.value) return false;
  // Source of truth is the customer record, NOT the rendered option — a rebuilt
  // or stale DOM can drop the data attribute, and COD Architect was right that
  // guessing from a missing attribute is the wrong failure mode. Order: real
  // data → dataset → unknown means NOT billable, so we never hard-block a save
  // on an assumption (losing a payroll entry is worse than a missing note).
  const cust = (ClockState.customers || []).find(c => c.id === sel.value);
  if (cust) return cust.internalTimeOnly !== true;
  const flag = sel.options[sel.selectedIndex]?.dataset?.internalTime;
  if (flag === '1') return false;
  if (flag === '0') return true;
  return false;
}

// Keeps the Notes field honest about what it is: on billable work this text
// becomes the ticket a customer is billed from, so it is required — the same
// standard a real clock-out already holds drivers to via `requireShiftNotes`.
// The person keying a manual entry usually was NOT there, which is exactly why
// the description matters most on this path.
function _syncAddEntryNotesRequirement() {
  const billable = _addEntryIsBillable();
  const label = document.getElementById('add-entry-notes-label');
  const input = document.getElementById('add-entry-notes');
  const hint = document.getElementById('add-entry-notes-hint');
  if (!label || !input) return;
  const sel = document.getElementById('add-entry-customer');
  const custName = sel?.value ? (sel.options[sel.selectedIndex]?.text || 'this customer') : '';
  if (billable) {
    label.innerHTML = 'What was done? <span style="color:var(--danger);">*</span>';
    input.placeholder = 'e.g. Hauled 3 loads of crushed concrete to the SWD';
    input.setAttribute('aria-required', 'true');
    if (hint) hint.innerHTML = `<span class="material-icons" style="font-size:12px;vertical-align:middle;">receipt_long</span> This becomes the ticket ${custName ? 'billed to ' + escapeHtml(custName) : 'for this work'} — required.`;
  } else {
    label.textContent = 'Notes';
    input.placeholder = 'Optional';
    input.removeAttribute('aria-required');
    if (hint) hint.textContent = sel?.value ? 'Internal time — no ticket is created, so a note is optional.' : 'Pick a customer to see whether this bills.';
  }
}

function _filterAddEntryJobs() {
  const customerId = document.getElementById('add-entry-customer')?.value;
  const jobSelect = document.getElementById('add-entry-job');
  if (!jobSelect) return;
  // TASK-1082 — relevance-scored + capped once a customer is picked (was: every
  // job for the customer, or ALL jobs when none picked). No prior job to pin.
  jobSelect.innerHTML = customerId
    ? buildRelevantJobOptions(customerId, null, 'Select...')
    : '<option value="">Select...</option>';
}

async function saveManualClockEntry(personId) {
  // TASK-360 — workforce admin: add an arbitrary clock entry for any person.
  // Even self-edit goes through the regular clock-in flow, not this admin
  // path. Workforce-only.
  if (!_assertWorkforceAccess('saveManualClockEntry')) return;
  // TASK-364 — fall back to historical cache for inactive folks.
  const person = getPersonForReport(personId);
  if (!person) return;

  const dateVal = document.getElementById('add-entry-date')?.value;
  const customerId = document.getElementById('add-entry-customer')?.value || null;
  const jobId = document.getElementById('add-entry-job')?.value || null;
  const clockInTime = document.getElementById('add-entry-clockin')?.value;
  const clockOutTime = document.getElementById('add-entry-clockout')?.value;
  const notes = document.getElementById('add-entry-notes')?.value?.trim() || '';

  if (!dateVal || !clockInTime || !clockOutTime) {
    window.showToast('Date, Clock In, and Clock Out are required', 'error');
    return;
  }

  // TASK-1483 — admin-entered personal-vehicle claim (validated before any write).
  const _pvAdd = pvReadForm('addpv');
  if (_pvAdd && !_pvAdd.ok) {
    window.showToast(_pvAdd.error, 'error');
    document.getElementById('addpv-pv-miles')?.focus();
    return;
  }
  const _pvAddFields = _pvAdd && _pvAdd.fields.personalVehicleUsed ? _pvAdd.fields : null;

  // TASK-1411 — a manual entry against a BILLABLE customer must say what was
  // done. Since TASK-1390 this entry auto-creates or joins a ticket, so a blank
  // note produces a billable ticket nobody can describe: TITS lost a 3-hour
  // ND State SWD haul on 2026-08-03 exactly this way — time recorded, work not,
  // and by the time billing noticed, only the driver knew what he had hauled.
  // Internal customers (the tenant's own `internalTimeOnly` flag) skip this —
  // they never produce a ticket, so there is nothing to describe.
  if (!notes && _addEntryIsBillable()) {
    window.showToast('Describe the work — this entry creates a billable ticket', 'error');
    const el = document.getElementById('add-entry-notes');
    if (el) { el.style.borderColor = 'var(--danger)'; el.focus(); el.scrollIntoView({ block: 'center' }); }
    return;
  }

  // clock-dst-all — the typed date + times are COMPANY time: refuse an
  // unreadable zone / a time that never happened, ask which one in the repeated
  // fall-back hour (with the hours each gives). Nothing is written on a refusal.
  const _inStr = `${dateVal}T${clockInTime}`;
  const _outStr = `${dateVal}T${clockOutTime}`;
  const _rt = await _clockResolveTimes([
    { str: _inStr, key: 'add-entry-clockin', label: 'Clock in' },
    { str: _outStr, key: 'add-entry-clockout', label: 'Clock out' },
  ], { inStr: _inStr, inKey: 'add-entry-clockin', outStr: _outStr, outKey: 'add-entry-clockout' });
  if (!_rt.ok) {
    window.showToast(_rt.message, 'warning', null, 9000);
    return;
  }
  const clockIn = _tltDate(_inStr, 'add-entry-clockin');
  const clockOut = _tltDate(_outStr, 'add-entry-clockout');
  if (isNaN(clockIn.getTime()) || isNaN(clockOut.getTime())) {
    window.showToast("Those times couldn't be read. Check the date and times.", 'error');
    return;
  }
  if (clockOut <= clockIn) {
    window.showToast('Clock Out must be after Clock In', 'error');
    return;
  }
  // Guard: no future dates
  if (clockIn > new Date() || clockOut > new Date()) {
    window.showToast('Cannot create entries in the future', 'error');
    return;
  }

  const totalHours = Math.round(((clockOut - clockIn) / 3600000) * 100) / 100;
  // Guard: flag unreasonably long shifts (>18h for same-day manual entry)
  if (totalHours > 18) {
    const confirmLong = confirm(`This entry is ${totalHours.toFixed(1)} hours — are you sure?`);
    if (!confirmLong) return;
  }
  // Overlap check — block if this entry crosses another for the same person
  const overlap = checkClockOverlap(personId, clockIn, clockOut, null);
  if (overlap) {
    window.showToast(overlap, 'error');
    return;
  }
  // TASK-9443 slice 2 — soft pay-period lock: a backdated shift added to an
  // exported period is warned about (office only; nothing is refused).
  try {
    const PL = window.PayrollRunLock;
    if (PL && !(await PL.confirmEdit(clockIn.getTime(), { what: 'Adding this shift' }))) return;
  } catch (_) { /* soft lock: never blocks */ }

  const customer = customerId ? ClockState.customers.find(c => c.id === customerId) : null;
  const job = jobId ? ClockState.allJobs.find(j => j.id === jobId) : null;
  const role = person.crewType || person.role || (person.roles && person.roles[0]) || 'NON_CDL';
  const rate = person.hrHourlyRate || 0;

  const btn = document.getElementById('btn-save-manual-entry');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const entryData = {
      personId,
      personName: person.name,
      customerId: customerId || null,
      customerName: customer?.name || null,
      jobId: jobId || null,
      jobTitle: job?.title || null,
      clockIn: Timestamp.fromDate(clockIn),
      clockOut: Timestamp.fromDate(clockOut),
      status: 'completed',
      totalHours,
      hours: totalHours, // keep both fields in sync
      notes: notes || null,
      rate,
      role,
      createdAt: Timestamp.now(),
      source: 'workforce-manual'
    };

    // TASK-1036 — manual COMPLETED entries are now SERVER-written, ALWAYS (no
    // feature flag): firestore.rules denies status:'completed' on the raw
    // self-create arm (a clock-only member's one-shot fabricated completed
    // entry with a chosen ticketId/jobId was one TASK-1027 backfill trigger
    // away from a minted EMPLOYEE pay line). The gateway op validates +
    // audit-logs + enforces workforce/billing/demo-only server-side
    // (ticketGateway.js manualEntry — no self arm; everyone who can reach
    // this UI is workforce-class per hasWorkforceAccess, or demo).
    // Direct-write fallback kept for gateway outages: it still passes rules
    // for workforce/admin/dispatcher/billing (their create arms are
    // untouched). NEVER fall back on permission-denied — that's the gateway
    // REFUSING (not failing); retrying raw would just double-error.
    const gwPayload = {
      personId,
      personName: person.name,
      customerId: customerId || null,
      customerName: customer?.name || null,
      jobId: jobId || null,
      jobTitle: job?.title || null,
      clockIn: clockIn.toISOString(),
      clockOut: clockOut.toISOString(),
      notes: notes || null,
      rate,
      role,
      // TASK-1390 — the form's date IS the local work day; the server uses it to
      // pick which day's ticket the hours land on (validated ±48h of clockIn).
      workDate: dateVal,
    };
    const gwScope = 'manualEntry:' + personId;
    let gwResult = null;
    let fellBack = false;
    try {
      const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: GW_CLIENT_TIMEOUT_MS });
      // TASK-1160 — opKey: an identical retry after a timeout replays server-side
      // instead of add()'ing a duplicate completed entry (non-idempotent create).
      gwResult = (await fn({ op: 'manualEntry', tenantId: ClockState.tenantId, payload: gwPayload, opKey: _gwOpKey(gwScope, gwPayload) }))?.data || null;
      _gwOpDone(gwScope);
    } catch (gwErr) {
      if (gwErr?.code === 'functions/permission-denied' || gwErr?.code === 'permission-denied') throw gwErr;
      // TASK-1160 — on deadline-exceeded the server may STILL create the entry;
      // the legacy addDoc fallback would then be a guaranteed duplicate. Don't
      // fall back — tell the user to check + retry (opKey dedups the retry).
      if (_gwIsDeadlineExceeded(gwErr)) {
        window.showToast('That took too long — the entry may still have saved. Check the list in a few seconds, then retry if it didn’t appear.', 'warning');
        gwErr._userMessageShown = true;
        throw gwErr;
      }
      // TASK-1390 — invalid-argument / failed-precondition mean the gateway
      // REFUSED (stale customer, mismatched job, validation) — not an outage.
      // Direct-writing here would save the exact data the server just rejected.
      if (gwErr?.code === 'functions/invalid-argument' || gwErr?.code === 'functions/failed-precondition' ||
          gwErr?.code === 'invalid-argument' || gwErr?.code === 'failed-precondition') {
        window.showToast(gwErr?.message || 'Could not save the entry', 'error');
        gwErr._userMessageShown = true;
        throw gwErr;
      }
      console.warn('[Clock] Gateway manualEntry failed, falling back to direct write:', gwErr?.message);
      await addDoc(collection(db, 'tenants', ClockState.tenantId, 'clockEntries'), _pvAddFields ? {
        ...entryData, ..._pvAddFields,
        personalVehicleEnteredBy: { source: 'admin', by: window.AppState?.user?.email || 'unknown', at: Timestamp.now() },
      } : entryData);
      fellBack = true;
    }
    // TASK-1483 — the gateway op does not carry the claim; stamp it on the new
    // entry as an admin-entered follow-up write (audited in pvAdminSave).
    if (_pvAddFields && !fellBack) {
      const _newId = gwResult?.updated?.entryId || null;
      if (_newId) await pvAdminSave(_newId, _pvAddFields, { id: _newId, personId, personName: person.name });
      else window.showToast(`Hours saved, but the ${pvName()} miles could not be attached. Open the entry and add them.`, 'warning');
    }

    // Add to local state for immediate UI update
    const _gwUpd = gwResult?.updated || {};
    const newEntry = { ...entryData, id: _gwUpd.entryId || ('temp-' + Date.now()), ...(_gwUpd.ticketId ? { ticketId: _gwUpd.ticketId } : {}) };
    ClockState.recentEntries.push(newEntry);

    // Also add to weekly cache
    const weekKey = getWeekKey(clockIn);
    if (weekKey && ClockState.weeklyCache[weekKey]) {
      ClockState.weeklyCache[weekKey].push(newEntry);
    }

    // TASK-1390 — say what happened to the TICKET, not just the entry. 41 TITS
    // manual entries went unbilled precisely because the admin got no signal.
    if (fellBack) {
      // Direct-write path = hours saved, NO ticket. Surface it loudly instead
      // of the old silent degrade (the entry re-orphans on this path).
      window.showToast('Saved — but the billing service was unreachable, so these hours are NOT on a ticket. Build the ticket from Billing, or re-save later.', 'warning');
    } else if (_gwUpd.needsTicketDecision) {
      await _manualEntryLockedDayPrompt(_gwUpd, { customerId, customerName: customer?.name || null, jobId, jobTitle: job?.title || null, dateVal });
    } else if (_gwUpd.ticketId) {
      const tno = _gwUpd.ticketNo != null ? ' #' + _gwUpd.ticketNo : '';
      window.showToast(`Saved ${totalHours.toFixed(2)} hrs → ${_gwUpd.ticketCreated ? 'created Ticket' + tno + ' for ' + dateVal : 'attached to Ticket' + tno}`, 'success');
    } else if (_gwUpd.attachError) {
      window.showToast('Hours saved, but attaching them to a ticket failed — they are NOT on a ticket yet. Build it from Billing.', 'warning');
    } else {
      // Shop / internal customer — correctly non-billable, same as before.
      window.showToast('Manual entry added', 'success');
    }

    // Refresh UI
    const content = document.getElementById('workforce-content');
    if (content) content.innerHTML = getWorkforceContentHTML();
    showEmployeeDetail(personId, false);
  } catch (err) {
    console.error('[Clock] Error saving manual entry:', err);
    // TASK-1160 — the timeout branch already showed a specific message.
    if (!err?._userMessageShown) window.showToast('Error saving entry', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = 'Save Entry'; }
  }
}

// TASK-1390 — the one genuine human fork on a manual entry: the target day's
// ticket is already invoiced/archived/locked (a backdated entry can land on an
// already-billed day — the case PIN clock-out never hits). The HOURS are saved
// either way (payroll never blocks); this asks what to do about BILLING them:
// mint a fresh supplemental draft for that day (via the hardened `repoint` op —
// same customer/job, and since locked tickets are never dedup candidates it
// creates a new draft, TASK-804 mint semantics), or deliberately leave them
// unbilled. Never silently mutates the locked ticket, never silently skips.
async function _manualEntryLockedDayPrompt(gwUpd, ctx) {
  const lt = gwUpd.lockedTicket || {};
  const tno = lt.ticketNo != null ? `Ticket #${lt.ticketNo}` : 'A ticket';
  const inv = lt.invoiceNumber ? ` (${lt.invoiceNumber})` : '';
  const msg = `${tno} for this job on ${ctx.dateVal} is already ${lt.status || 'locked'}${inv}, so these hours were NOT added to it. The hours themselves are saved. Add them to a NEW ticket for ${ctx.dateVal}, or leave them unbilled?`;
  let addToNew = false;
  if (typeof window.showConfirm === 'function') {
    addToNew = await window.showConfirm('That day is already billed', msg,
      { confirmText: 'Add to New Ticket', cancelText: 'Leave Unbilled', icon: 'receipt_long', iconColor: '#F59E0B' });
  } else {
    addToNew = confirm(msg);
  }
  if (!addToNew) {
    // TASK-9989 — Leave Unbilled is recorded for billing (server-only
    // invoicedPayChanges record + audit row): it shows on Billing's "Pay
    // changed after invoicing" list and on the billed ticket's Review, where
    // billing bills it on a new ticket or dismisses it. Changes no ticket.
    let told = false;
    try {
      const luPayload = { entryId: gwUpd.entryId, workDate: ctx.dateVal };
      const luScope = 'leaveUnbilled:' + gwUpd.entryId;
      const fnLu = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: GW_CLIENT_TIMEOUT_MS });
      const luRes = (await fnLu({ op: 'leaveManualEntryUnbilled', tenantId: ClockState.tenantId, payload: luPayload, opKey: _gwOpKey(luScope, luPayload) }))?.data;
      _gwOpDone(luScope);
      told = !!luRes?.payChangeId;
    } catch (e) {
      console.warn('[Clock] TASK-9989 leave-unbilled record failed:', e?.message);
    }
    window.showToast(told
      ? 'Hours saved — NOT on any ticket. Billing will see them on its "Pay changed after invoicing" list.'
      : 'Hours saved — NOT on any ticket, and billing could not be told. Tell billing, or add them to a ticket from Billing.', 'warning', null, 9000);
    return;
  }
  try {
    // workDate: COD Code Reviewer (real-diff round) — without it, repoint would
    // derive the supplemental draft's day from clockIn in the SERVER tz (the
    // entry has no source ticket), which can drift a backdated/evening entry to
    // the wrong day. The form's date is the day the admin means.
    const rpPayload = { entryId: gwUpd.entryId, customerId: ctx.customerId, customerName: ctx.customerName, jobId: ctx.jobId, jobTitle: ctx.jobTitle, workDate: ctx.dateVal };
    const rpScope = 'repoint:' + gwUpd.entryId;
    const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: GW_CLIENT_TIMEOUT_MS });
    const res = (await fn({ op: 'repoint', tenantId: ClockState.tenantId, payload: rpPayload, opKey: _gwOpKey(rpScope, rpPayload) }))?.data;
    _gwOpDone(rpScope);
    if (res?.updated?.ticketId) {
      window.showToast(`Hours added to a new draft ticket for ${ctx.dateVal}`, 'success');
    } else {
      window.showToast('Hours saved — attach them to a ticket from Billing.', 'warning');
    }
  } catch (e) {
    console.error('[Clock] TASK-1390 supplemental ticket failed:', e);
    window.showToast('Could not create the new ticket — the hours are saved; attach them from Billing.', 'error');
  }
}

function showAddPerDiemForm(personId) {
  // TASK-364 — fall back to historical cache for inactive folks.
  const person = getPersonForReport(personId);
  if (!person) return;

  const today = new Date().toISOString().split('T')[0];
  const defaultAmount = person.hrPerDiem || '';

  const section = document.getElementById('employee-perdiem-section');
  if (!section) return;

  // Insert form at top of perdiem section
  let formDiv = document.getElementById('add-perdiem-form-container');
  if (!formDiv) {
    formDiv = document.createElement('div');
    formDiv.id = 'add-perdiem-form-container';
    section.insertBefore(formDiv, section.firstChild);
  }

  formDiv.innerHTML = `
    <div style="background:var(--bg-secondary);border-radius:var(--radius-md);padding:var(--spacing-md);margin-top:var(--spacing-md);border:1px solid var(--accent-teal);">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-sm);">
        <strong style="color:var(--text-primary);font-size:0.9rem;">Add Per-Diem</strong>
        <button class="btn-icon" onclick="document.getElementById('add-perdiem-form-container').innerHTML=''" style="color:var(--text-secondary);"><span class="material-icons" style="font-size:18px;">close</span></button>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
        <div class="form-group" style="margin-bottom:0;">
          <label style="font-size:0.78rem;color:var(--text-secondary);">Amount ($)</label>
          <input type="number" id="add-perdiem-amount" class="form-control" step="0.01" min="0" value="${defaultAmount}" placeholder="0.00" style="font-size:0.85rem;">
        </div>
        <div class="form-group" style="margin-bottom:0;">
          <label style="font-size:0.78rem;color:var(--text-secondary);">Date</label>
          <input type="date" id="add-perdiem-date" class="form-control" value="${today}" style="font-size:0.85rem;">
        </div>
        <div class="form-group" style="margin-bottom:0;grid-column:1/-1;">
          <label style="font-size:0.78rem;color:var(--text-secondary);">Notes</label>
          <input type="text" id="add-perdiem-notes" class="form-control" placeholder="e.g. overnight, travel day" style="font-size:0.85rem;">
        </div>
      </div>
      <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:var(--spacing-sm);">
        <button class="btn btn-secondary btn-sm" onclick="document.getElementById('add-perdiem-form-container').innerHTML=''">Cancel</button>
        <button class="btn btn-primary btn-sm" id="btn-save-perdiem" onclick="window.ClockModule.savePerDiemEntry('${personId}')">Save</button>
      </div>
    </div>
  `;
}

async function savePerDiemEntry(personId) {
  // TASK-360 — workforce admin: per-diem entry on behalf of someone. Workforce-only.
  if (!_assertWorkforceAccess('savePerDiemEntry')) return;
  // TASK-9491 — per diem is pay: firestore.rules perDiemEntries create is
  // canEditMoney (owner via ownerId, or billing and not view-only).
  if (typeof window.canEditMoney === 'function' && window.canEditMoney() === false) {
    window.showToast?.(window.MONEY_VIEW_ONLY_MSG || 'View only — ask the owner to make this change.', 'warning');
    return;
  }
  // TASK-364 — fall back to historical cache for inactive folks.
  const person = getPersonForReport(personId);
  if (!person) return;

  const amount = parseFloat(document.getElementById('add-perdiem-amount')?.value);
  const date = document.getElementById('add-perdiem-date')?.value;
  const notes = document.getElementById('add-perdiem-notes')?.value?.trim() || '';

  if (!amount || amount <= 0) {
    window.showToast('Per-diem amount is required', 'error');
    return;
  }
  if (!date) {
    window.showToast('Date is required', 'error');
    return;
  }

  const btn = document.getElementById('btn-save-perdiem');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    await addDoc(collection(db, 'tenants', ClockState.tenantId, 'perDiemEntries'), {
      personId,
      personName: person.name,
      amount,
      date,
      notes: notes || null,
      createdAt: Timestamp.now()
    });

    window.showToast('Per-diem added', 'success');
    showEmployeeDetail(personId); // refresh modal including perdiem section
  } catch (err) {
    console.error('[Clock] Error saving per-diem:', err);
    window.showToast('Error saving per-diem', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = 'Save'; }
  }
}

async function deletePerDiemEntry(entryId, personId) {
  // TASK-360 — workforce admin: delete per-diem for any person.
  if (!_assertWorkforceAccess('deletePerDiemEntry')) return;
  if (!confirm('Delete this per-diem entry?')) return;
  try {
    await deleteDoc(doc(db, 'tenants', ClockState.tenantId, 'perDiemEntries', entryId));
    window.showToast('Per-diem deleted', 'success');
    showEmployeeDetail(personId);
  } catch (err) {
    console.error('[Clock] Error deleting per-diem:', err);
    window.showToast('Error deleting per-diem', 'error');
  }
}

async function loadPerDiemForWeek(personId, monday, sunday) {
  try {
    const mondayStr = monday.toISOString().split('T')[0];
    const sundayStr = sunday.toISOString().split('T')[0];
    const snap = await getDocs(
      query(
        collection(db, 'tenants', ClockState.tenantId, 'perDiemEntries'),
        where('personId', '==', personId),
        where('date', '>=', mondayStr),
        where('date', '<=', sundayStr)
      )
    );
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[Clock] Error loading per-diem:', err);
    return [];
  }
}

// ============================================
// DRIVER EXPENSE ENTRY POINT (general / no job context)
// ============================================
//
// Reuses the existing JobBoardModule.openExpenseModal flow. The modal is
// shared between this clock-screen entry (general expenses) and the Build
// Ticket "+ Expense" badge in jobboard.js (job-context expenses). Same code
// path; different pre-fill.
async function openRecordExpense() {
  if (!ClockState.tenantId) {
    window.showToast?.('Loading carrier — try again in a moment', 'error');
    return;
  }
  // Require an authenticated person (PIN passed) for driver-scope clients.
  const personId = ClockState.authenticatedPersonId || ClockState.selectedPerson;
  const person = personId ? ClockState.people.find(p => p.id === personId) : null;
  if (!person && !isFounder()) {
    window.showToast?.('Select your name first', 'error');
    return;
  }

  // Pre-fetch cost allocations — clock module doesn't subscribe to this
  // collection, so JobBoardState.costAllocations is empty when the modal
  // opens from the clock screen. Mirrors dispatch.js openDriverRecordExpense.
  let costAllocations = [];
  try {
    const allocSnap = await getDocs(collection(db, 'tenants', ClockState.tenantId, 'costAllocations'));
    costAllocations = allocSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(a => a.active !== false)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || String(a.label || '').localeCompare(String(b.label || '')));
  } catch (e) {
    console.warn('[Clock] openRecordExpense: cost allocations fetch failed', e?.message);
  }

  // Hydrate JobBoardModule state so its modal has the data it needs.
  if (window.JobBoardModule?._hydrateState) {
    window.JobBoardModule._hydrateState({
      tenantId: ClockState.tenantId,
      trucks: ClockState.trucks,
      trailers: ClockState.trailers,
      people: ClockState.people,
      customers: ClockState.customers,
      clockEntries: ClockState.activeEntries,
      jobs: ClockState.allJobs || [],
      jobDays: ClockState.todaysJobDays || [],
      costAllocations,
    });
  }

  // Set driver-scope hints so the modal uses the driver callable path.
  // Founders editing on a real clock screen don't hit this — they use the
  // admin path inside openExpenseModal.
  const drivingAsDriver = !isFounder();
  if (drivingAsDriver && person) {
    window.AppState = window.AppState || {};
    window.AppState.driverPersonId = person.id;
    window.AppState.driverPersonName = person.name || '';
    // TASK-9941 F S2 — the name + phone code is no longer put in
    // AppState.driverPin: the browser does not hold anyone's code, and the
    // driver callables accept only a private PIN anyway (_validateDriverContext
    // 'custom'), so the old value could never authorise anything.
    try { delete window.AppState.driverPin; } catch (_) { window.AppState.driverPin = null; }
  }

  // No job/entry — general expense.
  window.JobBoardModule?.openExpenseModal?.(null, null, null, {
    tenantId: ClockState.tenantId,
    clockEntries: ClockState.activeEntries,
    trucks: ClockState.trucks,
    jobs: ClockState.allJobs || [],
    jobDays: ClockState.todaysJobDays || [],
    driverId: person?.id || null,
    driverName: person?.name || '',
    driverExpenseSettings: window.AppState?.settings?.driverExpenses || null,
    costAllocations,
  });
}

// TASK-528 — Open the Time Off request form from the Clock hub.
//
// Implementation note: the standalone TimeOffModule modal (timeoff.js
// `#modal-request`) is NOT in the DOM while the Clock screen is mounted — each
// top-level view replaces DOM.mainContent (app.js renderView). The Clock module
// already has its OWN identical time-off tab + modal (`#modal-timeoff-request`,
// showNewTimeOffRequest / saveTimeOffRequest, same `tenants/{tid}/timeOff`
// collection). So the self-service hub stays entirely on the Clock screen: we
// switch to the built-in time-off tab and open its modal — no navigation to the
// separate Time Off module. showNewTimeOffRequest self-scopes + locks the
// person picker for non-workforce viewers (TASK-528).
function openRequestTimeOff() {
  try {
    // Ensure the time-off tab DOM (incl. #modal-timeoff-request) is mounted.
    if (ClockState.activeTab !== 'timeoff') {
      switchTab('timeoff');
    }
    showNewTimeOffRequest();
  } catch (e) {
    console.error('[Clock] openRequestTimeOff failed:', e);
    window.showToast?.('Could not open the time-off form', 'error');
  }
}

// ============================================
// TASK-1483 — WORKFORCE → CLAIMS: the driver-claimed-amounts review surface.
// Built keyed on CLAIM TYPE, not as a truck-miles list (Chris 2026-08-11: "we
// don't build just for TITS"): per diem, standby, downtime, piece rate and
// vehicle miles are all driver-reported at clock-out and reach payroll with
// nobody checking. This ships with the Personal Vehicle rows populated; the row
// shape ({ type, date, person, quantity, unit, rate, amount, customer, job })
// already holds the other types when they are wired in.
// Home = Workforce (the module that owns clock data, TASK-360) so review can
// happen MID-WEEK, not only at payroll close.
// Approve/deny is a TENANT SETTING (settings.personalVehicle.requireApproval),
// default OFF = visibility only. When on, payroll counts approved rows only.
// The settings card (enable, basis, rate, flat, effective date) is owner/admin
// only and saves through the setPersonalVehicleSettings callable — the rate is
// typed by the admin, never hardcoded, never fetched from a gov API.
// ============================================
const CLAIM_TYPES = [
  { key: 'vehicle', label: 'Personal vehicle', unit: 'mi' },
];

// The vehicle claim type shows under the tenant's own pay-type name.
function _claimTypeLabel(t) {
  return t && t.key === 'vehicle' ? Object.assign({}, t, { label: pvName() }) : t;
}

function _claimsDefaults() {
  if (ClockState.claims) return ClockState.claims;
  const pp = getPayPeriodDates(0) || {};
  ClockState.claims = {
    start: pp.start ? _fmtLocalYmd(pp.start) : _fmtLocalYmd(new Date(Date.now() - 13 * 86400000)),
    end: pp.end ? _fmtLocalYmd(pp.end) : _fmtLocalYmd(new Date()),
    person: '', type: '', rows: [], loading: false, loaded: false, error: null, seq: 0,
  };
  return ClockState.claims;
}

async function loadClaimsData() {
  const c = _claimsDefaults();
  const seq = ++c.seq;
  c.loading = true; c.error = null;
  _rerenderClaims();
  try {
    const start = new Date(c.start + 'T00:00:00');
    const end = new Date(c.end + 'T23:59:59.999');
    const snap = await getDocs(query(
      collection(db, 'tenants', ClockState.tenantId, 'clockEntries'),
      where('clockIn', '>=', Timestamp.fromDate(start)),
      where('clockIn', '<=', Timestamp.fromDate(end)),
      orderBy('clockIn', 'asc')
    ));
    if (seq !== c.seq) return; // superseded
    const entries = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const VR = window.VehicleReimb;
    // Day hours for the "≥ N hours" trigger — the same TASK-939 rule payroll
    // uses (addDateHours over COMPLETED entries, clock-in local date).
    const byPid = {};
    entries.forEach(e => {
      if (!e.personId || e.status !== 'completed') return;
      const b = byPid[e.personId] || (byPid[e.personId] = { name: e.personName || '', dateHours: {} });
      addDateHours(b.dateHours, e.clockIn, e.totalHours || 0);
    });
    const rows = VR ? VR.computeClaims(entries, pvPolicy(),
      Object.assign({ includeOpen: true, isEligible: pvEligibleFn(getPersonForReport) }, pvDayOpts(byPid))) : [];
    c.rows = rows.map(r => ({ type: 'vehicle', ...r }));
    // The miles each entry showed when this list loaded — a review is written
    // against THESE, never against whatever the doc holds at click time.
    c.seenMiles = {};
    entries.forEach(e => { c.seenMiles[e.id] = e.personalVehicleUsed === true ? (e.personalVehicleMiles ?? null) : null; });
    // TASK-9439 — and the amount each entry's row would pay: a decision is
    // stamped with it, and the engine voids it if that amount later changes.
    c.seenAmount = {};
    c.rows.forEach(r => (r.entryIds || []).forEach(id => {
      if (typeof r.reviewAmount === 'number') c.seenAmount[id] = r.reviewAmount;
    }));
    c.loaded = true;
  } catch (err) {
    if (seq !== c.seq) return;
    console.error('[Clock] claims load failed:', err);
    c.error = err?.message || 'Could not load claims';
  }
  c.loading = false;
  _rerenderClaims();
}

function _rerenderClaims() {
  if (ClockState.workforceTab !== 'claims') return;
  const content = document.getElementById('workforce-content');
  if (content) content.innerHTML = getClaimsTabHTML();
}

function setClaimsFilter(key, value) {
  const c = _claimsDefaults();
  if (key === 'start' || key === 'end') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return;
    c[key] = value;
    loadClaimsData();
    return;
  }
  if (key === 'person' || key === 'type') c[key] = String(value || '');
  _rerenderClaims();
}

function _pvSettingsCardHTML() {
  // 1.15.1972 — mirror setPersonalVehicleSettings exactly: owner (ownerId) or the
  // workforce module. Same rule as the internal-time pay editor.
  if (!_canEditInternalPay()) return '';
  const VR = window.VehicleReimb;
  const pol = pvPolicy() || { enabled: false, requireApproval: false, history: [] };
  const cur = VR ? VR.currentPolicy(pol) : null;
  const today = _fmtLocalYmd(new Date());
  const hist = (pol.history || []).slice().reverse().slice(0, 6).map(h => `
    <div style="padding:2px 0;">From <strong>${escapeHtml(h.effectiveFrom)}</strong>:
      ${h.basis === 'flat_per_day'
        ? `$${Number(h.flatPerDay).toFixed(2)}/day flat at ${(h.ratePerMile * 100).toFixed(1)}¢/mi — ${h.excessTaxable ? 'part the miles don’t cover paid as wages' : 'paid in full as a reimbursement'}; a day qualifies on ${h.qualify?.mode === 'hours' ? `≥ ${h.qualify.minHours}h worked` : h.qualify?.mode === 'both' ? `miles and ≥ ${h.qualify.minHours}h worked` : 'miles entered'}`
        : `${(h.ratePerMile * 100).toFixed(1)}¢ per mile`}
      <span style="color:var(--text-secondary);">— set by ${escapeHtml(h.setBy || 'unknown')}</span></div>`).join('');
  return `
    <div class="card" style="padding:14px;margin-bottom:14px;border-left:4px solid #0F766E;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
        <span class="material-icons" style="color:#0F766E;">directions_car</span>
        <strong>${escapeHtml(pol.payTypeName || pvName())}</strong>
        <span style="font-size:0.75rem;color:var(--text-secondary);">(owner / admin)</span>
      </div>
      <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:10px;">
        Pays people who use their <strong>own</strong> vehicle for work. Separate from per diem. Only people with
        <em>Personal-vehicle reimbursement</em> ticked on their HR Employment info see it at clock-out.
        Type your rate — it is never filled in for you (the IRS business rate changes every January).
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:10px;align-items:end;">
        <div><label style="font-size:0.75rem;color:var(--text-secondary);" for="pvs-name">Pay type name (what your crew sees)</label>
          <input type="text" id="pvs-name" class="form-control" maxlength="40" placeholder="Vehicle Reimbursement" value="${escapeHtml(pol.payTypeName || '')}"></div>
        <label style="display:flex;gap:6px;align-items:center;font-weight:600;"><input type="checkbox" id="pvs-enabled" ${pol.enabled ? 'checked' : ''}> Turned on</label>
        <div style="font-size:0.78rem;color:var(--text-secondary);">Approval: set under <strong>Approval before payroll</strong> below.</div>
        <div><label style="font-size:0.75rem;color:var(--text-secondary);">Pay basis</label>
          <select id="pvs-basis" class="form-control" onchange="['pvs-flat-wrap','pvs-excess-wrap','pvs-qualify-wrap','pvs-minhours-wrap'].forEach(id=>{const el=document.getElementById(id);if(el)el.style.display=this.value==='flat_per_day'?'':'none';})">
            <option value="per_mile" ${cur?.basis !== 'flat_per_day' ? 'selected' : ''}>Per mile driven</option>
            <option value="flat_per_day" ${cur?.basis === 'flat_per_day' ? 'selected' : ''}>Flat amount per day used</option>
          </select></div>
        <div><label style="font-size:0.75rem;color:var(--text-secondary);">Rate ($ per mile)</label>
          <input type="number" id="pvs-rate" class="form-control" step="0.001" min="0" max="2" placeholder="e.g. 0.725" value="${cur ? cur.ratePerMile : ''}"></div>
        <div id="pvs-flat-wrap" style="${cur?.basis === 'flat_per_day' ? '' : 'display:none;'}"><label style="font-size:0.75rem;color:var(--text-secondary);">Flat ($ per day)</label>
          <input type="number" id="pvs-flat" class="form-control" step="0.01" min="0" max="500" placeholder="e.g. 35" value="${cur && cur.flatPerDay != null ? cur.flatPerDay : ''}"></div>
        <label id="pvs-excess-wrap" style="display:${cur?.basis === 'flat_per_day' ? 'flex' : 'none'};gap:6px;align-items:center;font-weight:600;font-size:0.85rem;"
          title="Off (default): the whole flat amount is exported as a reimbursement. On: the part miles × rate doesn't cover is exported as wages (Gusto Bonus column). Your call — confirm with your payroll provider.">
          <input type="checkbox" id="pvs-excess-taxable" ${cur?.excessTaxable ? 'checked' : ''}> Treat the part the miles don't cover as taxable wages
          <span style="display:block;font-weight:400;font-size:0.72rem;color:var(--text-secondary);">Off (default): the whole flat amount is exported as a reimbursement. On: the part miles × rate doesn't cover is exported as wages (Gusto Bonus). Your call — confirm with your payroll provider.</span></label>
        <div id="pvs-qualify-wrap" style="${cur?.basis === 'flat_per_day' ? '' : 'display:none;'}"><label style="font-size:0.75rem;color:var(--text-secondary);">A day qualifies when…</label>
          <select id="pvs-qualify-mode" class="form-control" onchange="const w=document.getElementById('pvs-minhours-wrap');if(w)w.style.display=this.value==='miles'?'none':'';">
            <option value="miles" ${(cur?.qualify?.mode || 'miles') === 'miles' ? 'selected' : ''}>Miles are entered that day</option>
            <option value="hours" ${cur?.qualify?.mode === 'hours' ? 'selected' : ''}>They work at least N hours</option>
            <option value="both" ${cur?.qualify?.mode === 'both' ? 'selected' : ''}>Both: miles entered AND at least N hours</option>
          </select></div>
        <div id="pvs-minhours-wrap" style="${cur?.basis === 'flat_per_day' && (cur?.qualify?.mode || 'miles') !== 'miles' ? '' : 'display:none;'}"><label style="font-size:0.75rem;color:var(--text-secondary);">N = hours worked that day</label>
          <input type="number" id="pvs-min-hours" class="form-control" step="0.25" min="0.25" max="24" value="${cur?.qualify?.minHours ?? 8}"></div>
        <div><label style="font-size:0.75rem;color:var(--text-secondary);">Rate effective from</label>
          <input type="date" id="pvs-effective" class="form-control" value="${today}"></div>
        <div><button class="btn btn-primary btn-sm" id="pvs-save-btn" onclick="window.ClockModule.savePersonalVehicleSettings()">Save</button></div>
      </div>
      <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:8px;">
        A new rate applies from its effective date forward; earlier days keep the rate they were worked at.
        With a flat daily amount, Payroll Prep always shows the part the miles don't cover (miles × rate below the flat).
        Whether that part is paid as wages is <strong>your setting</strong> above (off by default = the whole flat is exported as a reimbursement), effective from the date you pick.
        DispatchDeck does not decide tax treatment — confirm yours with your payroll provider. The IRS publishes a standard business mileage rate each year.
        If a day qualifies on <strong>hours worked</strong> alone, no miles back that day's payment — tell your payroll provider how you pay it.
      </div>
      ${hist ? `<div style="font-size:0.78rem;margin-top:8px;"><strong>Rate history</strong>${hist}</div>` : ''}
    </div>`;
}

async function savePersonalVehicleSettings() {
  if (ClockState._pvsSaving) return;
  const enabled = document.getElementById('pvs-enabled')?.checked === true;
  // TASK-9439 — requireApproval is no longer sent from here: approval lives on
  // the effective-dated "Approval before payroll" card (setPayrollApprovalSettings).
  // Omitted = the callable keeps the stored value.
  const basis = document.getElementById('pvs-basis')?.value || 'per_mile';
  const rateRaw = document.getElementById('pvs-rate')?.value;
  const flatRaw = document.getElementById('pvs-flat')?.value;
  const effectiveFrom = document.getElementById('pvs-effective')?.value || '';
  const VR = window.VehicleReimb;
  // Pay type name: sent only when it changed (the callable audits old -> new).
  const nameRaw = String(document.getElementById('pvs-name')?.value || '');
  const nameClean = VR ? VR.cleanPayTypeName(nameRaw) : nameRaw.trim();
  if (nameRaw.trim().length > 40) { window.showToast?.('Pay type name: 40 characters or fewer', 'error'); return; }
  const nameCur = VR ? VR.payTypeNameOf(window.AppState?.settings || {}) : '';
  const payTypeName = (nameClean || (VR ? VR.DEFAULT_PAY_TYPE_NAME : '')) === nameCur ? undefined : (nameClean || null);
  // COD (tax-switch delta, Edge Case): compare with the row in effect on the
  // CHOSEN date — comparing with today's row silently skipped a future-dated
  // change whose values matched today's.
  const cur = VR ? (/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom) ? VR.resolvePolicy(pvPolicy(), effectiveFrom) : VR.currentPolicy(pvPolicy())) : null;
  const ratePerMile = parseFloat(rateRaw);
  const flatPerDay = basis === 'flat_per_day' ? parseFloat(flatRaw) : null;
  const excessTaxable = basis === 'flat_per_day' && document.getElementById('pvs-excess-taxable')?.checked === true;
  const qMode = document.getElementById('pvs-qualify-mode')?.value || 'miles';
  const qMin = parseFloat(document.getElementById('pvs-min-hours')?.value);
  const qualify = basis === 'flat_per_day' ? { mode: qMode, minHours: qMin } : null;
  // Send a rate row only when the rate/basis actually changed (or none exists).
  let rate = null;
  const rateTyped = String(rateRaw || '').trim() !== '';
  if (rateTyped) {
    if (!(ratePerMile > 0 && ratePerMile <= 2)) { window.showToast?.('Rate must be a dollar amount per mile, like 0.725', 'error'); return; }
    if (basis === 'flat_per_day' && !(flatPerDay > 0 && flatPerDay <= 500)) { window.showToast?.('Enter the flat daily amount (up to $500)', 'error'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)) { window.showToast?.('Pick the date the rate takes effect', 'error'); return; }
    const changed = !cur || cur.basis !== basis || Math.abs(cur.ratePerMile - ratePerMile) > 1e-9
      || (basis === 'flat_per_day' && Math.abs((cur.flatPerDay || 0) - flatPerDay) > 1e-9)
      || (basis === 'flat_per_day' && (cur.excessTaxable === true) !== excessTaxable)
      || (basis === 'flat_per_day' && ((cur.qualify?.mode || 'miles') !== qMode || Math.abs((cur.qualify?.minHours ?? 8) - qMin) > 1e-9));
    if (basis === 'flat_per_day' && !(qMin > 0 && qMin <= 24)) { window.showToast?.('Hours worked must be more than 0 and at most 24', 'error'); return; }
    if (changed) {
      rate = { basis, ratePerMile, flatPerDay, excessTaxable, qualify, effectiveFrom };
      if (effectiveFrom < _fmtLocalYmd(new Date())) {
        const ok = await (window.showConfirm?.('Back-date this rate?',
          `This rate will apply to days from ${effectiveFrom} on — including pay periods that may already be closed. Continue?`,
          { confirmText: 'Back-date it', cancelText: 'Cancel', icon: 'warning' }) ?? false);
        if (!ok) return;
        rate.allowBackdate = true; // the callable refuses a silent back-date
      }
    }
  } else if (enabled && !cur) {
    window.showToast?.('Type your mileage rate before turning this on', 'error'); return;
  }
  ClockState._pvsSaving = true;
  const btn = document.getElementById('pvs-save-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    const fn = httpsCallable(functions, 'setPersonalVehicleSettings');
    const res = await fn({ tenantId: ClockState.tenantId, enabled, rate, ...(payTypeName !== undefined ? { payTypeName } : {}) });
    const pv = res?.data?.personalVehicle;
    if (pv && window.AppState) {
      window.AppState.settings = window.AppState.settings || {};
      window.AppState.settings.personalVehicle = pv;
    }
    window.showToast?.(res?.data?.noop ? 'No changes to save' : `${pvName()} settings saved`, 'success');
    if (ClockState.claims) loadClaimsData(); else _rerenderClaims();
  } catch (err) {
    console.error('[Clock] personal-vehicle settings save failed:', err);
    window.showToast?.(err?.message || 'Could not save settings', 'error');
  } finally {
    ClockState._pvsSaving = false;
    const b = document.getElementById('pvs-save-btn');
    if (b) { b.disabled = false; b.textContent = 'Save'; }
  }
}

// ============================================
// TASK-9439 — APPROVAL BEFORE PAYROLL. Generalises 1483's "Require approval":
// the tenant ticks which driver-reported amounts must be approved before they
// pay (effective-dated, so a flip never re-cuts a closed period) and whether
// Payroll Prep BLOCKS the export while any is pending (on) or only WARNS (off).
// Same gate as the mileage card + the setPayrollApprovalSettings callable:
// the owner (tenants.ownerId) or the workforce module — never a role.
// Only types that are self-reported AND reach the payroll file are listed
// (vehicleReimb.js APPROVAL_TYPES); the others are named with the reason.
// ============================================
function _payrollApprovalCardHTML() {
  if (!_canEditInternalPay()) return '';
  const VR = window.VehicleReimb;
  if (!VR || !VR.approvalRequiredOn) return '';
  const pol = pvPolicy();
  const ap = (pol && pol.approval) || { blockExport: false, history: [] };
  const today = _fmtLocalYmd(new Date());
  const req = VR.approvalRequiredOn(pol, today, 'vehicle');
  // A 1483 tenant (un-dated requireApproval ON, no dated row yet): miles
  // claims are gated, hours-only days are not (never re-cut). Extending it to
  // hours days is an explicit, dated opt-in — never a side effect of Save.
  const legacyOnly = req && !((ap.history || []).length);
  const typeName = (t) => (t === 'vehicle' ? pvName() : t);
  const hist = (ap.history || []).slice().reverse().slice(0, 6).map(h => `
    <div style="padding:2px 0;">From <strong>${escapeHtml(h.effectiveFrom)}</strong>:
      ${h.types.length ? `${h.types.map(t => escapeHtml(typeName(t))).join(', ')} needs approval` : 'nothing needs approval'}
      <span style="color:var(--text-secondary);">— set by ${escapeHtml(h.setBy || 'unknown')}</span></div>`).join('');
  return `
    <div class="card" style="padding:14px;margin-bottom:14px;border-left:4px solid #D97706;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
        <span class="material-icons" style="color:#D97706;">fact_check</span>
        <strong>Approval before payroll</strong>
        <span style="font-size:0.75rem;color:var(--text-secondary);">(owner / workforce)</span>
      </div>
      <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:10px;">
        Pick which amounts your people report themselves that must be approved here before they are paid.
        An unapproved or denied item is never paid. The Claims list won't let you approve your own claim.
      </div>
      <div style="display:flex;flex-direction:column;gap:8px;">
        <label style="display:flex;gap:6px;align-items:flex-start;font-weight:600;">
          <input type="checkbox" id="pa-type-vehicle" data-painted="${req ? '1' : '0'}" ${req ? 'checked' : ''} style="margin-top:3px;">
          <span>${escapeHtml(pvName())}
            <span style="display:block;font-weight:400;font-size:0.72rem;color:var(--text-secondary);">Miles claims and flat daily amounts, including days that earn the flat on hours worked alone.</span></span></label>
        ${legacyOnly ? `<label style="display:flex;gap:6px;align-items:flex-start;font-size:0.8rem;margin-left:22px;">
          <input type="checkbox" id="pa-extend-hours" style="margin-top:3px;">
          <span>Also require approval for days that earn the flat on hours worked alone, from the date below
            <span style="display:block;font-size:0.72rem;color:var(--text-secondary);">Your earlier "Require approval" setting covers miles claims only.</span></span></label>` : ''}
        <div style="font-size:0.72rem;color:var(--text-secondary);">
          Not listed: standby, downtime and piece rate are billed to your customer, not paid through payroll.
          Per diem is figured from hours worked. Out-of-pocket expenses are approved in AP.
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:end;">
          <div><label style="font-size:0.75rem;color:var(--text-secondary);" for="pa-effective">A change to the list applies to work from</label>
            <input type="date" id="pa-effective" class="form-control" value="${today}" onchange="window.ClockModule.paEffectiveChanged(this.value)"></div>
        </div>
        <label style="display:flex;gap:6px;align-items:flex-start;font-weight:600;">
          <input type="checkbox" id="pa-block" ${ap.blockExport ? 'checked' : ''} style="margin-top:3px;">
          <span>Block the payroll export until everything is approved
            <span style="display:block;font-weight:400;font-size:0.72rem;color:var(--text-secondary);">On: Payroll Prep can't copy or download the file while any ticked item in the period is waiting. Off: the export works and Payroll Prep warns you with the count. Only the account owner can turn this off or un-tick a type.</span></span></label>
        <div><button class="btn btn-primary btn-sm" id="pa-save-btn" onclick="window.ClockModule.savePayrollApprovalSettings()">Save</button></div>
      </div>
      ${hist ? `<div style="font-size:0.78rem;margin-top:8px;"><strong>History</strong>${hist}</div>` : ''}
    </div>`;
}

async function savePayrollApprovalSettings() {
  if (ClockState._paSaving) return;
  const VR = window.VehicleReimb;
  if (!VR || !VR.approvalRequiredOn) return;
  const pol = pvPolicy();
  const blockExport = document.getElementById('pa-block')?.checked === true;
  const eff = document.getElementById('pa-effective')?.value || '';
  const types = VR.APPROVAL_TYPES.filter(t => document.getElementById('pa-type-' + t)?.checked === true);
  const payload = { tenantId: ClockState.tenantId, blockExport };
  // The list is effective-dated: send it only when it differs from what is in
  // effect on the chosen date (the callable appends a history row + audits).
  if (!/^\d{4}-\d{2}-\d{2}$/.test(eff)) { window.showToast?.('Pick the date the change applies from', 'error'); return; }
  // COD r2/r3: send the list ONLY when the user changed a box from what it
  // was painted with (painted = in effect on the chosen date, incl. the 1483
  // baseline; the date picker repaints — paEffectiveChanged). So Save never
  // changes the list as a side effect, moving the date alone never cancels a
  // scheduled row, and un-ticking always sticks. A 1483 tenant extends
  // approval to hours-only days only via the explicit "also … hours" box.
  const touched = VR.APPROVAL_TYPES.some(t => {
    const el = document.getElementById('pa-type-' + t);
    return !!el && (el.checked === true) !== (el.getAttribute?.('data-painted') === '1');
  });
  const extendHours = document.getElementById('pa-extend-hours')?.checked === true && types.indexOf('vehicle') !== -1;
  if (touched || extendHours) {
    payload.types = types;
    payload.effectiveFrom = eff;
    if (eff < _fmtLocalYmd(new Date())) {
      const ok = await (window.showConfirm?.('Apply to earlier work?',
        `This changes which claims from ${eff} on need approval — including pay periods that may already be closed. Continue?`,
        { confirmText: 'Apply from that date', cancelText: 'Cancel', icon: 'warning' }) ?? false);
      if (!ok) return;
      payload.allowBackdate = true; // the callable refuses a silent back-date
    }
  }
  ClockState._paSaving = true;
  const btn = document.getElementById('pa-save-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    const fn = httpsCallable(functions, 'setPayrollApprovalSettings');
    const res = await fn(payload);
    const pa = res?.data?.payrollApproval;
    if (pa && window.AppState) {
      window.AppState.settings = window.AppState.settings || {};
      window.AppState.settings.payrollApproval = pa;
    }
    window.showToast?.(res?.data?.noop ? 'No changes to save' : 'Approval settings saved', 'success');
    if (ClockState.claims) loadClaimsData(); else _rerenderClaims();
  } catch (err) {
    console.error('[Clock] payroll approval settings save failed:', err);
    window.showToast?.(err?.message || 'Could not save approval settings', 'error');
  } finally {
    ClockState._paSaving = false;
    const b = document.getElementById('pa-save-btn');
    if (b) { b.disabled = false; b.textContent = 'Save'; }
  }
}

// TASK-9439 (COD r3) — the date picker repaints the boxes with what is in
// effect ON that date, so the boxes always show the policy being edited.
function paEffectiveChanged(ymd) {
  const VR = window.VehicleReimb;
  if (!VR || !/^\d{4}-\d{2}-\d{2}$/.test(String(ymd || ''))) return;
  const pol = pvPolicy();
  VR.APPROVAL_TYPES.forEach(t => {
    const el = document.getElementById('pa-type-' + t);
    if (!el) return;
    const on = VR.approvalRequiredOn(pol, ymd, t);
    el.checked = on;
    el.setAttribute('data-painted', on ? '1' : '0');
  });
}

// TASK-9439 — Payroll Prep's "approve" link: open Workforce → Claims on that
// person + day. Payroll Prep is a Workforce sub-tab, so this is a tab switch.
function openClaimsFor(pid, ymd) {
  if (!hasWorkforceAccess()) {
    window.showToast?.('Approving claims needs the Workforce module.', 'error');
    return;
  }
  const c = _claimsDefaults();
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(ymd || ''))) { c.start = ymd; c.end = ymd; }
  c.person = String(pid || '');
  c.type = '';
  c.rows = []; c.loaded = false; c.error = null;
  if (!document.getElementById('workforce-content')) {
    window.showToast?.('Open Workforce → Claims to approve it.', 'error');
    return;
  }
  switchWorkforceTab('claims'); // getClaimsTabHTML loads the filtered range
}

function getClaimsTabHTML() {
  const c = _claimsDefaults();
  if (!c.loaded && !c.loading && !c.error) setTimeout(loadClaimsData, 0);
  const pol = pvPolicy() || { requireApproval: false };
  // TASK-9439 — approval is per work date now (effective-dated); the header
  // speaks for today, each row carries its own needsApproval.
  const apToday = window.VehicleReimb?.approvalRequiredOn ? window.VehicleReimb.approvalRequiredOn(pol, _fmtLocalYmd(new Date())) : !!pol.requireApproval;
  const money = (n) => '$' + (Number(n) || 0).toFixed(2);
  const people = [...new Map(c.rows.map(r => [r.personId, r.personName])).entries()]
    .sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  const rows = c.rows.filter(r => (!c.person || r.personId === c.person) && (!c.type || r.type === c.type));
  const statusPill = (r) => {
    const map = {
      approved: ['#059669', 'Approved'], denied: ['#DC2626', 'Denied'],
      pending: ['#D97706', 'Needs review'], open: ['#64748B', 'Counted'],
      ineligible: ['#DC2626', 'Not enrolled — not paid'],
      hours: ['#64748B', 'Hours day (no miles)'],
    };
    const [col, lbl] = map[r.status] || map.open;
    // TASK-1483 — the day trigger's verdict, in words (flat_per_day only).
    const why = r.qualifyReason ? `<div style="font-size:0.7rem;color:${r.qualifies === false ? '#B45309' : 'var(--text-secondary)'};">${escapeHtml(r.qualifyReason)}</div>` : '';
    const hoursTag = r.hoursOnly && r.status !== 'hours' ? ' <span style="font-size:0.7rem;color:var(--text-secondary);">(hours day, no miles)</span>' : '';
    // TASK-9451 — an open shift blocks the decision; say what to do about it.
    const openTag = !r.open ? '' : (r.needsApproval && r.status === 'pending')
      ? ' <span style="font-size:0.7rem;color:#B45309;">(clock-out still open — close the shift in Workforce → Hours, then approve)</span>'
      : ' <span style="font-size:0.7rem;color:var(--text-secondary);">(shift still open)</span>';
    return `<span style="font-size:0.72rem;font-weight:700;color:${col};">${lbl}</span>${hoursTag}${openTag}${why}`;
  };
  c._visible = rows;
  const rowsHTML = rows.map((r, idx) => {
    const t = _claimTypeLabel(CLAIM_TYPES.find(x => x.key === r.type) || { label: r.type, unit: '' });
    const rateTxt = r.noRate ? '<span style="color:#DC2626;">no rate for this date</span>'
      : r.basis === 'flat_per_day' ? `${money(r.flatPerDay)}/day · ${(r.ratePerMile * 100).toFixed(1)}¢/mi`
        : `${(r.ratePerMile * 100).toFixed(1)}¢/mi`;
    // COD (TASK-1483): approve/deny only a SETTLED claim — a completed entry is
    // frozen for the driver by rules, so the approved miles are the miles paid.
    // TASK-9439 — rows that need approval ON THEIR DATE, including an
    // hours-only flat day (its decision is written on that day's entries).
    // A row that pays nothing anyway (not qualified / no rate) has nothing to approve.
    const actions = (r.needsApproval && !r.open && r.status !== 'ineligible' && r.qualifies !== false && !r.noRate) ? `
        <button class="btn btn-sm btn-secondary" style="padding:2px 8px;font-size:0.72rem;" onclick="window.ClockModule.reviewVehicleClaimRow(${idx}, 'approved')">Approve</button>
        <button class="btn btn-sm btn-secondary" style="padding:2px 8px;font-size:0.72rem;color:#DC2626;" onclick="window.ClockModule.reviewVehicleClaimRow(${idx}, 'denied')">Deny</button>` : '';
    return `<tr>
      <td>${escapeHtml(r.ymd)}</td>
      <td>${escapeHtml(r.personName || '')}</td>
      <td>${escapeHtml(t.label)}${r.adminEntered ? ' <span style="font-size:0.68rem;color:#7C3AED;font-weight:700;">ADMIN-ENTERED</span>' : ''}</td>
      <td style="text-align:right;">${Number(r.miles).toFixed(1)} ${t.unit}</td>
      <td>${rateTxt}</td>
      <td style="text-align:right;">${money(r.amount)}${r.taxableExcess > 0.004 ? `<div style="font-size:0.7rem;color:#B45309;">${money(r.taxableExcess)} paid as wages</div>` : (r.uncovered > 0.004 ? `<div style="font-size:0.7rem;color:var(--text-secondary);" title="Part of the flat that miles × rate doesn't cover — information only; paid as a reimbursement per your setting">${money(r.uncovered)} not covered by miles</div>` : '')}</td>
      <td>${escapeHtml(r.customerName || '')}</td>
      <td>${escapeHtml(r.jobTitle || '')}</td>
      <td>${statusPill(r)}${actions ? `<div style="display:flex;gap:4px;margin-top:4px;">${actions}</div>` : ''}</td>
    </tr>`;
  }).join('');
  const totals = rows.filter(r => r.payable && !r.noRate).reduce((a, r) => {
    a.miles += r.miles; a.amount += r.amount; a.excess += r.taxableExcess; return a;
  }, { miles: 0, amount: 0, excess: 0 });
  return `
    ${_pvSettingsCardHTML()}
    ${_payrollApprovalCardHTML()}
    <div class="card" style="padding:14px;">
      <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:end;margin-bottom:10px;">
        <div><strong>Driver-claimed amounts</strong>
          <div style="font-size:0.78rem;color:var(--text-secondary);">What people reported at clock-out that turns into pay. ${apToday ? 'Approval is ON — payroll counts approved claims only.' : 'Visibility only — every claim counts. Tick it under "Approval before payroll" above to gate them.'}</div></div>
        <div style="margin-left:auto;display:flex;flex-wrap:wrap;gap:8px;align-items:end;">
          <div><label style="font-size:0.72rem;color:var(--text-secondary);display:block;">From</label><input type="date" class="form-control" value="${c.start}" onchange="window.ClockModule.setClaimsFilter('start', this.value)"></div>
          <div><label style="font-size:0.72rem;color:var(--text-secondary);display:block;">To</label><input type="date" class="form-control" value="${c.end}" onchange="window.ClockModule.setClaimsFilter('end', this.value)"></div>
          <div><label style="font-size:0.72rem;color:var(--text-secondary);display:block;">Person</label>
            <select class="form-control" onchange="window.ClockModule.setClaimsFilter('person', this.value)">
              <option value="">Everyone</option>
              ${people.map(([id, name]) => `<option value="${escapeHtml(String(id))}" ${c.person === id ? 'selected' : ''}>${escapeHtml(name || '')}</option>`).join('')}
            </select></div>
          <div><label style="font-size:0.72rem;color:var(--text-secondary);display:block;">Type</label>
            <select class="form-control" onchange="window.ClockModule.setClaimsFilter('type', this.value)">
              <option value="">All types</option>
              ${CLAIM_TYPES.map(_claimTypeLabel).map(t => `<option value="${t.key}" ${c.type === t.key ? 'selected' : ''}>${escapeHtml(t.label)}</option>`).join('')}
            </select></div>
        </div>
      </div>
      ${c.loading ? '<div style="padding:16px;color:var(--text-secondary);">Loading…</div>'
        : c.error ? `<div style="padding:16px;color:#DC2626;">${escapeHtml(c.error)}</div>`
        : !rows.length ? '<div style="padding:16px;color:var(--text-secondary);">No claims in this range.</div>'
        : `<div style="overflow-x:auto;"><table class="data-table" style="width:100%;font-size:0.82rem;">
            <thead><tr><th>Date</th><th>Person</th><th>Type</th><th style="text-align:right;">Qty</th><th>Rate</th><th style="text-align:right;">Amount</th><th>Customer</th><th>Job</th><th>Status</th></tr></thead>
            <tbody>${rowsHTML}</tbody>
            <tfoot><tr><td colspan="3"><strong>Counted toward pay</strong></td><td style="text-align:right;"><strong>${totals.miles.toFixed(1)} mi</strong></td><td></td>
              <td style="text-align:right;"><strong>${money(totals.amount)}</strong>${totals.excess > 0.004 ? `<div style="font-size:0.7rem;color:#B45309;">${money(totals.excess)} paid as wages</div>` : ''}</td><td colspan="3"></td></tr></tfoot>
          </table></div>`}
    </div>`;
}

// Approve / deny — workforce-class only (rules: the self arm can never write
// personalVehicleReview). One row may cover several entries (flat_per_day
// groups a person's day), so the decision is applied to every entry in it.
// Audited old → new per entry in clockAuditLog.
function reviewVehicleClaimRow(idx, status) {
  const r = (_claimsDefaults()._visible || [])[Number(idx)];
  if (!r) return;
  if (r.hoursOnly) return reviewVehicleHoursDay(r, status); // TASK-9439
  return reviewVehicleClaim(r.entryIds || [], status);
}

async function reviewVehicleClaim(entryIds, status) {
  if (!hasWorkforceAccess()) return;
  if (status !== 'approved' && status !== 'denied') return;
  let ids = Array.isArray(entryIds) ? entryIds.filter(x => typeof x === 'string' && x) : [];
  if (!ids.length || ClockState._pvReviewBusy) return;
  const c = _claimsDefaults();
  // COD Adversary (TASK-1483): nobody approves their OWN claim. TASK-9450:
  // the reviewVehicleClaim callable enforces it for everyone but the owner
  // (tenants.ownerId; logged) and needs owner or the `workforce` module;
  // this is the early, clear message.
  const _selfPid = getSelfPersonId();
  const _row = c.rows.find(r => (r.entryIds || []).includes(ids[0]));
  if (_selfPid && _row && _row.personId === _selfPid && !_clockIsOwner()) {
    window.showToast?.('You can’t review your own claim — ask another manager.', 'error');
    return;
  }
  if (_row && (_row.open || _row.status === 'ineligible')) return;
  // TASK-9459 — a held day (approved shift + unsettled shift): the decision lands on the UNSETTLED shifts only,
  // so Deny never claws back a shift that was already approved (approved still pays; denied is settled).
  if (_row && _row.dayHeld && Array.isArray(_row.heldIds) && _row.heldIds.length) ids = ids.filter(i => _row.heldIds.includes(i));
  // TASK-9451 — the decision is for the person-day this list showed. Without
  // the row we cannot tell what that was, so refuse rather than guess.
  if (!_row || !_row.personId || !_row.ymd || !window.VehicleReimb || typeof window.VehicleReimb.ymdOf !== 'function') {
    window.showToast?.('This claim is no longer in the list — reload and review it again.', 'error');
    loadClaimsData();
    return;
  }
  ClockState._pvReviewBusy = true;
  const seen = c.seenMiles || {};
  const seenAmt = c.seenAmount || {};
  // TASK-9450 — the review is written by the reviewVehicleClaim callable, never
  // the client (rules only let a client CLEAR personalVehicleReview). The
  // server re-checks every shift (same person, completed, same day, the miles
  // this list showed), recomputes the amount from the policy in effect that
  // day, stamps who/when, refuses self-review (owner excepted, logged) and
  // audits old -> new. One changed shift writes nothing.
  const entries = ids.map(id => ({ id, miles: seen[id] === undefined ? null : seen[id], amount: typeof seenAmt[id] === 'number' ? seenAmt[id] : null }));
  await _callReviewVehicleClaim({ tenantId: ClockState.tenantId, status, kind: 'claim', personId: _row.personId, ymd: _row.ymd, entries },
    'This claim changed since the list loaded (miles, day or shift) — reloaded. Review it again.');
}

// TASK-9450 — shared caller for both review kinds: toasts, busy flag, reload.
async function _callReviewVehicleClaim(payload, staleMsg) {
  try {
    const r = await httpsCallable(functions, 'reviewVehicleClaim')(payload);
    const res = (r && r.data) || {};
    if (res.stale) window.showToast?.(staleMsg, 'error');
    else window.showToast?.(payload.status === 'approved' ? 'Approved' : 'Denied', 'success');
  } catch (err) {
    console.error('[Clock] vehicle claim review failed:', err);
    const msg = err && err.code === 'functions/permission-denied' && err.message ? err.message : 'Could not save the review';
    window.showToast?.(msg, 'error');
  } finally {
    ClockState._pvReviewBusy = false;
    loadClaimsData();
  }
}

// TASK-9439 — approve / deny an hours-only flat day (approval required on its
// date). It has no miles claim, so the decision is written on EVERY completed
// entry of that person-day — the same personalVehicleReview field, the same
// rules guard (workforce module, never your own entry), bound to each entry's
// miles (0 without a claim) and the day's flat. The engine reads the day as
// approved only when every entry carries the approval, so a shift added later
// sends it back for review. Audited per entry.
async function reviewVehicleHoursDay(r, status) {
  if (!hasWorkforceAccess()) return;
  if (status !== 'approved' && status !== 'denied') return;
  const VR = window.VehicleReimb;
  const ids = Array.isArray(r?.entryIds) ? r.entryIds.filter(x => typeof x === 'string' && x) : [];
  if (!VR || !ids.length || ClockState._pvReviewBusy) return;
  if (typeof r.reviewAmount !== 'number') return;
  const _selfPid = getSelfPersonId();
  if (_selfPid && r.personId === _selfPid && !_clockIsOwner()) {
    window.showToast?.('You can’t review your own claim — ask another manager.', 'error');
    return;
  }
  const c = _claimsDefaults();
  const seen = c.seenMiles || {};
  // A shift this list never saw cannot be vouched for — reload instead.
  if (ids.some(id => !(id in seen))) {
    window.showToast?.('That day changed since this list loaded — reloaded. Review it again.', 'error');
    loadClaimsData();
    return;
  }
  ClockState._pvReviewBusy = true;
  // TASK-9450 — server-written (see reviewVehicleClaim above).
  const entries = ids.map(id => ({ id, miles: seen[id], amount: r.reviewAmount }));
  await _callReviewVehicleClaim({ tenantId: ClockState.tenantId, status, kind: 'hoursDay', personId: r.personId, ymd: r.ymd, entries },
    'That day changed since this list loaded — reloaded. Review it again.');
}

// ============================================
// EXPOSE TO WINDOW
// ============================================
window.ClockModule = {
  // TASK-9489 — Billing Settings clears this after the rounding switch flips,
  // so a clock-out does not price off line choices cached before a reset.
  clearContractRateCache: () => { ClockState.contractRateCache = {}; },
  // TASK-1397 — THE shared invoice-notes write path. jobboard.js (driver-PIN
  // surface) calls these through window.ClockModule, NEVER a ?v= import:
  // two busters = two module instances = two baselines = the bug again.
  saveInvoiceNotes,
  askInvoiceNoteRemovals, // TASK-10036 (b) — jobboard asks before its ack timer
  mergeInvoiceNoteText,
  invoiceNoteLines,
  // Split datetime helpers (exposed for row-based onchange handlers)
  getSplitDateTimeValue,
  setSplitDateTimeValue,
  onPersonSelect,
  onCustomerSelect,
  onPinInput,
  // TASK-9941 F S2 — server-checked PIN box, unverified fallback, tablet PIN setup
  submitClockPin,
  clockContinueUnverified,
  clockStartPinSetup,
  clockSendPinSetupCode,
  clockFinishPinSetup,
  clockCancelPinSetup,
  clockPunchAuthorization,
  clockCodeOutcome,
  clockCodeStampFields,
  useClockPinOverride, // TASK-4691 P2 — manager override button in the PIN block
  selectPerson,
  // TASK-586 — per-operator equipment confirm at self-clock-in (equipmentTie)
  onClockJobChange,
  clockToggleEquipRow,
  clockEquipSwapPicked, // TASK-9763 S2 — "In a different truck?" at clock-in
  clockInLaborOnly,
  clockFleetAskChanged, // TASK-1572 Slice 2 — empty-day "which truck?" select
  clockFleetAskLaborOnly, // TASK-1572 Slice 2 — its labor-only out
  clockIn,
  clockOut,
  confirmClockOut,
  closeModal,
  switchTab,
  // Driver-expense entry point — general (no job context)
  openRecordExpense,
  // TASK-528 — Request Time Off shortcut (opens TimeOffModule modal)
  openRequestTimeOff,
  // Clock-out modal helpers
  recalcHours,
  resetTimes,
  saveTicketFromForm,
  showEquipmentPicker,
  selectEquipmentFromPicker,
  removeEquipment,
  updateEquipTime,
  toggleEquipFollowsClock, // TASK-4614 — the "Ends with shift" chip on each equipment row
  toggleMileage,
  updateMileageTotal,
  addDowntimeRow,
  addStandbyRow,
  removeTimeEntry,
  updateTimeEntryField,
  fitEntryToShift,
  // Clock-out timer
  pauseClockOutTimer,
  markClockOutTyped, // TASK-4616
  // Pothole / Line Info
  addPothole,
  _csGrabGPS,
  _csAddInstance,
  _csRemoveInstance,
  removePothole,
  updatePotholeField,
  grabPotholeGPS,
  handlePotholePhotoSelect,
  removePotholePhoto,
  // Signatures & Stamps (field ticket v2)
  clearSignature,
  markSignatureNA,
  // TASK-395: exposed so the Job Board Build Ticket modal reuses the SAME pad
  // implementation (no third signature-pad impl) — Pitfall #1 (call via
  // window.ClockModule.*, never import with a cache-buster).
  initSignaturePad,
  getSignatureDataUrl,
  dataUrlToBlob,
  handleStampCapture,
  markStampNA,
  resetStampCapture, // TASK-395-B (Job Board builder reuse)
  getStampCapture,   // TASK-395-B
  settleStampCapture, // TASK-9402 — Job Board Build Ticket waits out stamp conversion before saving
  // Photos
  handlePhotoSelect,
  removePhoto,
  showPhotoAt, // TASK-1220 — thumbnail → lightbox
  // Notes
  copyToTicketNotes,
  // Workforce - Hours
  switchWorkforceTab,
  navigateWorkforceWeek,
  filterWorkforceSearch,
  showEmployeeDetail,
  showAddHoursPersonPicker,
  _filterAddHoursPicker,
  showAddClockEntryForm,
  saveManualClockEntry,
  _filterAddEntryJobs,
  _syncAddEntryNotesRequirement, // TASK-1411 — billable manual entries must describe the work
  showAddPerDiemForm,
  savePerDiemEntry,
  deletePerDiemEntry,
  // Workforce - Reports
  loadReportsData,
  switchReportsPeriod,
  navigatePayPeriod,
  navigateDetailPayPeriod,
  toggleTimesheetSort,
  savePayPeriodSettings,
  saveInternalPaySettings, onInternalOtMethodChange, removeStaleInternalPay, // TASK-9388
  // TASK-1182 — always-reachable pay-period settings panel + live preview
  togglePayPeriodSettings,
  updatePayPeriodPreview,
  switchReportsEmployee,
  switchReportsCustomer,
  setReportsCustomDate,
  applyReportsCustom,
  toggleReportsExpand,
  // TASK-915 — ⚠️ Needs Review badge → "Confirm with Employee" SMS
  confirmHoursWithEmployee,
  printWorkforceReport,
  toggleExportMenu,
  toggleReportsShowNotes,
  toggleReportsShowDailyDetail,
  toggleReportsShowLaborDetail,
  exportPayrollCSV,
  exportDetailedCSV,
  exportAdjustmentsCSV,
  // Workforce - Adjustments
  showAddAdjustment,
  updateAdjCategoryDropdown,
  updateAdjPerDiemWarning, // TASK-4628 — onchange from the Add Adjustment form
  onAdjCategoryChange,
  onAdjCustomerChange,
  saveAdjustment,
  deleteAdjustment,
  switchAdjustmentsPeriod,
  // Time Off functions
  switchTimeOffFilter,
  setTimeOffGroup,
  showNewTimeOffRequest,
  toggleTimeOffHistory,
  saveTimeOffRequest,
  viewTimeOffRequest,
  reviewTimeOffRequest,
  approveTimeOffRequest,
  denyTimeOffRequest,
  deleteTimeOffRequest,
  // Clock entry edit/delete (founder only)
  editClockEntry,
  updateEditHoursPreview,
  onEditClockCustomerChange,
  openTicketFromClock,
  deleteClockEntry,
  deleteClockEntryFromModal,
  saveClockEntryEdit,
  viewClockEntryHistory,
  // TASK-1575 — exposed so the Job Board driver Build Ticket flow (jobboard.js)
  // writes a clockEntryAuditLog row for its own clock-in/out adjustments through
  // the SAME single implementation (Pitfall #1: window.* global, NEVER a ?v=
  // cross-import). It has its own internal stripUndef + try/catch swallow, so a
  // caller never needs to guard against undefined fields or a failed write.
  logClockEntryChange,
  clockOutNowFromModal,
  openEntryForClockOut, // TASK-9988 S1 — Billing's "Clock them out first"
  closeStaleClockEntry,
  // Switch Job (mid-shift)
  showSwitchJobModal,
  confirmSwitchJob,
  onSwitchCustomerChange,
  onSwitchJobChange,     // TASK-9865
  clockPickCrewJob,      // TASK-9865 — one-tap "Switch to J-1248" under a clock-side picker
  // TASK-9867 — move several finished shifts in one action + "a Claude fix is waiting" chip
  toggleMovePick,
  clearMovePick,
  openMoveEntriesModal,
  closeMoveEntriesModal,
  onMoveEntriesCustomerChange,
  submitMoveEntries,
  rejectPendingClockRequest,
  // TASK-9865 — the clock-in preselect decision table + labels, read by
  // jobboard.js through window.ClockModule (never a ?v= import, Pitfall #1).
  clockPersonOnJobDay,
  clockJobDayStart,
  clockWallMinutes,
  clockFormatWallTime,
  clockJobNumberLabel,
  clockTodaysDayFor,
  clockJobPickLabel,
  clockJobShortLabel,
  clockPickJobForPerson,
  clockCrewWarning,
  clockCrewWarningHtml,
  toggleSwitchTicketReview,
  switchTicketFromClockOut,
  // Clock-out review (founder only)
  showClockOutReview,
  backToForm,
  toggleReviewConfirm,
  revertShiftNotes,
  confirmClockOutFromReview,
  applyReviewData,
  // TASK-4576 — the ONE close-out equipment hours rule, exported so the four
  // writers provably share it and tests/task4576-*.unit.cjs can assert it
  // offline. window.* per Pitfall #1, never a ?v= cross-module import.
  clockEquipmentHours,
  // Clock history
  loadMoreHistory,
  // TASK-451 — driver read-only "My Timesheet" (self-only, ~12mo, lazy per-period)
  showMyTimesheet,
  navigateMyTimesheet,
  // TASK-9195 — "What I did": lazy per-row read of the shift's persisted
  // ticketLines. Takes a DOM-safe entry KEY (not a ticket id) and re-resolves
  // SELF internally, so the TASK-451 no-personId-argument rule still holds.
  loadMyHoursDetail,
  // TASK-602 — current open pay-period boundary for the public driver timesheet
  // edit/delete gate (app.js). Reads already-loaded settings.payPeriodType/
  // payPeriodAnchor — ZERO Firestore reads. Exposed on window (Pitfall #1: app.js
  // must NOT cross-import clock.js with a ?v= cache-buster). getPayPeriodDates(0)
  // = current open period {start, end} as local Dates.
  getPayPeriodDates,
  // TASK-1169 — Payroll Prep (payroll.js) single-source helpers, exposed per
  // Pitfall #1 (window global, NEVER a ?v= cross-import). OT numbers MUST come
  // from computeCrossPeriodOT/computePerWeekOT + isPayrollExempt — re-deriving
  // OT anywhere else is forbidden (the 83h 3h-vs-5h OT bug; see the comment at
  // computePerWeekOT). escapeCSV carries the TASK-453 formula-injection guard.
  isPayrollExempt,
  payrollExemptKind,
  computePerWeekOT,
  computeCrossPeriodOT,
  buildPayrollPrepData,
  getPayPeriodDates, // TASK-9443 — payroll.js maps an exported run back to its pay-period offset
  escapeCSV,
  downloadCSV,
  // TASK-1483 — personal-vehicle claim: shared section/reader for jobboard.js
  // (driver PIN) + the Workforce → Claims review surface + its settings card.
  pvToggle,
  pvSectionOn,
  pvSectionHTML,
  pvName, // TASK-1483 — tenant pay-type name (raw text; escape on render)
  pvReadForm,
  setClaimsFilter,
  loadClaimsData,
  reviewVehicleClaim,
  reviewVehicleClaimRow,
  savePersonalVehicleSettings,
  // TASK-9439 — approval before payroll: settings card, hours-day review, and
  // Payroll Prep's per-item "approve" link (payroll.js → window.ClockModule).
  savePayrollApprovalSettings,
  paEffectiveChanged,
  reviewVehicleHoursDay,
  openClaimsFor,
  getPayPeriodType,
  // Adjust clock-in time
  toggleClockInTime,
  // TASK-4559 — the per-asset note textarea's `oninput` handler. MUST stay
  // exported: the draft it parks on the equipment row is the only copy that
  // survives showClockOutReview()'s innerHTML swap, so the review + Apply paths
  // have nothing to write without it.
  captureAssetNote,
  // Pure merge decision, exported for the unit net (never called from markup).
  clockResolveAssetNote,
  // TASK-4577 — pure decision: may this close-out write to the ticket the entry
  // points at, must it un-delete it first, or must it be released from it?
  // Exported for the unit net (never called from markup).
  clockDecideDeletedTicketAction,
  // Ticket line dedup (also used by billing module)
  deduplicateTicketLines,
  // Shared ticket creation (used by dispatch.js PIN clock-in + switch job)
  findOrCreateTicket,
  createTicketLine,
  getNextTicketNumber,
  // Exceptions tab (TASK-1442) + the legacy health/audit data tools it hosts
  runTimecardExceptions,
  buildTimecardExceptions,          // pure rules — exported for the E2E net
  getExceptionsTabHTML,
  getExceptionsResultsHTML,
  switchExceptionsPeriod,
  navExceptionsPayPeriod,
  setExceptionsCustomRange,
  setExceptionsTypeFilter,
  openExceptionEntry,
  runTicketHealthCheck,
  loadAuditLog,
  // TASK-916 — All Activity filter handlers
  setAuditDateFilter,
  applyAuditCustomRange,
  setAuditActorFilter,
  // TASK-441 — exposed so the E2E regression net can drive the REAL render
  // paths (build the weekly table + report HTML against injected ClockState)
  // and assert the "Needs Review" oversized-shift flag without spinning up a
  // full multi-employee seeded tenant. Harmless in prod (pure render helpers).
  buildWeeklyHoursTable,
  buildReportsResults,
  getOversizedShiftThreshold,
  // TASK-9794 — clock-in review: shared decision + one-click Accept (payroll.js).
  entryNeedsReview,
  entryReviewReasons,
  acceptClockEntryReview,
  clockReviewSnapshotKey, // TASK-9794 — Accept precondition (unit-tested)
  // Test-only bridge: ClockState is a module-private const, so the E2E spec
  // can't seed it from the page. This shallow-merges fields into the REAL
  // ClockState the render functions read. No-op in normal use.
  __seedClockStateForTest(patch) { if (patch && typeof patch === 'object') Object.assign(ClockState, patch); }
};

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
